package com.adoptu.frontend

import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import org.w3c.dom.*
import kotlin.js.Promise
import kotlin.js.json

private val englishTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Login",
    "register" to "Register",
    "logout" to "Close session",
    "profile" to "Profile",
    "myPets" to "My Pets",
    "addPet" to "Add Pet",
    "save" to "Save",
    "cancel" to "Cancel",
    "search" to "Search",
    "shelters" to "Shelters",
    "photographers" to "Photographers",
    "temporalHomes" to "Temporal Homes",
    "findTemporalHomes" to "Find Temporal Homes",
    "sterilizationLocations" to "Sterilization Locations",
    "donate" to "Donate",
    "countryLabel" to "Country",
    "selectCountryToSearch" to "Select a country",
    "selectCountryFirst" to "Select a country to enable these filters",
    "state" to "State",
    "city" to "City",
    "zipCode" to "ZIP",
    "neighborhood" to "Neighborhood",
    "name" to "Name",
    "email" to "Email",
    "phone" to "Phone",
    "address" to "Address",
    "description" to "Description",
    "animalShelters" to "Animal Shelters",
    "sheltersDescription" to "Find animal shelters where you can find pets to adopt or leave them by prior agreement with the shelter",
    "photographerDescription" to "Professional photographers offering pet photo sessions",
    "sterilizationLocationsDescription" to "Find places where you can take animals to be sterilized.",
    "privacyPolicy" to "Privacy Policy",
    "privacyLastUpdated" to "Last updated: April 2025",
    "privacyHighlight" to "At Adopt-U, we believe in minimal data collection. We only collect your email address to enable communication between adopters and rescuers. We do not use your information for advertising, and we will never share your data with third parties for marketing purposes.",
    "privacyInfoCollectedH2" to "Information We Collect",
    "privacyAccountRegH3" to "Account Registration",
    "privacyAccountRegIntro" to "To create an account on Adopt-U, we collect the following information:",
    "privacyEmailLabel" to "Email address: ",
    "privacyEmailDesc" to "Required for account verification, password recovery, and communication between adopters and rescuers",
    "privacyDisplayNameLabel" to "Display name: ",
    "privacyDisplayNameDesc" to "A name you choose to be identified by on the platform",
    "privacyLangPrefLabel" to "Language preference: ",
    "privacyLangPrefDesc" to "To provide the platform in your preferred language",
    "privacyNoFullNameEtc" to "We do not collect your full name, phone number, physical address, identification documents, payment information, or any other personal data beyond what is listed above.",
    "privacyOptionalProfileH3" to "Optional Profile Information",
    "privacyOptionalProfileIntro" to "Depending on your role, you may optionally provide:",
    "privacyAdopterLabel" to "As an Adopter: ",
    "privacyAdopterDesc" to "No additional information is required or collected",
    "privacyRescuerLabel" to "As a Rescuer: ",
    "privacyRescuerDesc" to "Location information for pets you have available for adoption",
    "privacyPhotographerLabel" to "As a Photographer: ",
    "privacyPhotographerDesc" to "Service location and contact preferences",
    "privacyTemporalHomeLabel" to "As a Temporal Home: ",
    "privacyTemporalHomeDesc" to "Location where you can provide temporary housing",
    "privacyLocationUsage" to "All location information is used solely to match adopters with nearby rescuers and service providers.",
    "privacyPetInfoH3" to "Pet Information",
    "privacyPetInfoIntro" to "Rescuers may post information about pets, including:",
    "privacyPetPhotos" to "Photos of the pet",
    "privacyPetDescBreedAge" to "Description, breed, age, and characteristics",
    "privacyPetHealth" to "Health and vaccination status",
    "privacyPetLocation" to "Location where the pet is available",
    "privacyPetInfoNotVerified" to "This information is provided by rescuers and is not independently verified by Adopt-U.",
    "privacyHowWeUseH2" to "How We Use Your Information",
    "privacyPrimaryPurposeH3" to "Primary Purpose: Facilitating Adoption Connections",
    "privacyEmailUsedFor" to "Your email address is used exclusively for the following purposes:",
    "privacyUseVerification" to "Sending you a verification link when you create an account",
    "privacyUseRescuerContact" to "Allowing rescuers to contact you about pets you're interested in",
    "privacyUseContactRescuer" to "Allowing you to contact rescuers about their listed pets",
    "privacyUseNotifications" to "Sending you important account and security notifications",
    "privacyOnceConnected" to "Once you connect with another user through our platform, all further communication is conducted directly between you and that party. We do not monitor, store, or have access to the content of your communications.",
    "privacyNeverDoH3" to "What We Will Never Do",
    "privacyCommitIntro" to "We solemnly commit to the following:",
    "privacyNoAdsLabel" to "No advertising: ",
    "privacyNoAdsDesc" to "We will never display targeted or non-targeted advertisements based on your activity",
    "privacyNoMarketingLabel" to "No marketing: ",
    "privacyNoMarketingDesc" to "We will never share your email with third parties for marketing or promotional purposes",
    "privacyNoSellingLabel" to "No data selling: ",
    "privacyNoSellingDesc" to "We will never sell, rent, or otherwise transfer your personal information to any third party",
    "privacyNoProfilingLabel" to "No profiling: ",
    "privacyNoProfilingDesc" to "We will not use your data to build profiles for any purpose beyond basic account management",
    "privacyNoTrackingLabel" to "No tracking: ",
    "privacyNoTrackingDesc" to "We do not use tracking pixels, cookies for advertising, or any similar tracking technologies",
    "privacyDataStorageH2" to "Data Storage and Security",
    "privacyDataStoredSecurely" to "Your data is stored securely using industry-standard encryption. We employ appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.",
    "privacyDataRetention" to "We retain your account information for as long as your account remains active. You may request deletion of your account and associated data at any time by contacting us.",
    "privacyYourRightsH2" to "Your Rights",
    "privacyRightsIntro" to "You have the following rights regarding your personal data:",
    "privacyRightAccess" to "Access: You may request a copy of all personal data we hold about you",
    "privacyRightCorrection" to "Correction: You may request that we correct any inaccurate information",
    "privacyRightDeletion" to "Deletion: You may request that we delete your account and all associated data",
    "privacyRightPortability" to "Portability: You may request that we provide your data in a commonly used format",
    "privacyExerciseRights" to "To exercise any of these rights, please contact us at adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Cookies",
    "privacyCookiesText" to "Adopt-U uses minimal cookies necessary for platform functionality, including session management and language preferences. We do not use advertising cookies or tracking cookies.",
    "privacyThirdPartyH2" to "Third-Party Services",
    "privacyThirdPartyIntro" to "We use the following third-party services, which may process your data:",
    "privacyAws" to "AWS (Amazon Web Services): For data storage and email delivery. Their privacy policies apply to their processing of your data.",
    "privacyWebauthn" to "WebAuthn/FIDO2: For secure passkey authentication. This involves cryptographic operations in your browser.",
    "privacyThirdPartySelected" to "We have carefully selected service providers who share our commitment to data privacy.",
    "privacyChildrensH2" to "Children's Privacy",
    "privacyChildrensText" to "Adopt-U is not intended for users under the age of 18. We do not knowingly collect information from minors. If you believe a minor has created an account, please contact us to have it removed.",
    "privacyChangesH2" to "Changes to This Policy",
    "privacyChangesText" to "We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. We will notify users of significant changes via email or prominent notice on the platform.",
    "privacyContactH2" to "Contact Us",
    "privacyContactText" to "If you have any questions or concerns about this Privacy Policy, or to exercise your data rights, please contact us at adopt-u@adopt-u.org.",
    "termsConditions" to "Terms and Conditions",
    "installApp" to "Install App",
    "termsAcceptanceH2" to "Acceptance of Terms",
    "termsAcceptanceText" to "By accessing and using Adopt-U, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our platform.",
    "termsNatureH2" to "Nature of Our Service",
    "termsNatureText1" to "Adopt-U is a connection platform that facilitates contact between pet rescuers and potential adopters. We are not a pet store, broker, shelter, or adoption agency. We do not own, custody, or intermediately handle any pets listed on our platform.",
    "termsNatureText2" to "Our role is strictly limited to providing a communication channel between parties. We do not participate in adoption negotiations, contracts, or the physical transfer of animals.",
    "termsUserRespH2" to "User Responsibilities",
    "termsForRescuersH3" to "For Rescuers:",
    "termsRescuerResp1" to "Provide accurate, truthful information about pets in your care",
    "termsRescuerResp2" to "Ensure pets are healthy, vaccinated, and appropriately vetted before listing",
    "termsRescuerResp3" to "Conduct responsible screening of potential adopters",
    "termsRescuerResp4" to "Arrange safe transfer of pets to their new homes",
    "termsRescuerResp5" to "Accept responsibility for the accuracy of all information provided",
    "termsRescuerResp6" to "Comply with all local laws and regulations regarding pet adoption",
    "termsRescuerResp7" to "Never use the platform for commercial pet sales or breeding purposes",
    "termsForAdoptersH3" to "For Adopters:",
    "termsAdopterResp1" to "Provide accurate information about your living situation and experience with pets",
    "termsAdopterResp2" to "Be prepared to demonstrate your ability to provide proper care",
    "termsAdopterResp3" to "Understand that rescinding a pet's adoption causes significant stress to the animal",
    "termsAdopterResp4" to "Respect the rescuer's right to decline your adoption request",
    "termsPetListingsH2" to "Pet Listings and Information",
    "termsPetListingsText1" to "Adopt-U does not verify, endorse, or guarantee the accuracy of information provided in pet listings. Information including but not limited to breed, age, health status, temperament, and vaccination records is provided by rescuers and has not been independently verified.",
    "termsPetListingsText2" to "Photos and descriptions are provided by rescuers. While we prohibit deliberate misrepresentation, we cannot guarantee that all listings accurately represent the current condition of the animal.",
    "termsPetListingsText3" to "Users are encouraged to ask questions, request veterinary records, and meet pets before finalizing any adoption arrangement.",
    "termsAdoptionProcessH2" to "Adoption Process",
    "termsAdoptionProcessIntro" to "Once a rescuer and adopter agree to proceed with an adoption:",
    "termsAdoptionStep1" to "All arrangements, including transfer date, location, and any associated costs, are negotiated directly between the parties",
    "termsAdoptionStep2" to "Adopt-U does not facilitate or guarantee any payment transactions",
    "termsAdoptionStep3" to "The platform does not provide adoption contracts; any contractual arrangements are the sole responsibility of the parties involved",
    "termsAdoptionStep4" to "We strongly recommend documenting all agreements in writing",
    "termsLiabilityH2" to "Limitation of Liability",
    "termsLiabilityIntro" to "Adopt-U shall not be held liable for:",
    "termsLiability1" to "Any disputes, disagreements, or conflicts between rescuers and adopters",
    "termsLiability2" to "The accuracy of information provided in pet listings",
    "termsLiability3" to "The health, behavior, or condition of any animal before, during, or after adoption",
    "termsLiability4" to "Any financial losses, damages, or injuries arising from use of the platform",
    "termsLiability5" to "The outcome of any adoption, including but not limited to returns, health issues, or compatibility problems",
    "termsLiability6" to "Any actions taken by users outside of the platform after contact has been established",
    "termsLiabilityAck" to "Users acknowledge that adopting a pet is a significant commitment and that they assume full responsibility for their decision to adopt.",
    "termsProhibitedH2" to "Prohibited Activities",
    "termsProhibitedIntro" to "The following activities are strictly prohibited on Adopt-U:",
    "termsProhibited1" to "Commercial pet sales, breeding operations, or puppy mills",
    "termsProhibited2" to "Listing pets you do not directly care for",
    "termsProhibited3" to "False or misleading information in listings or profiles",
    "termsProhibited4" to "Harassment, discrimination, or inappropriate contact with other users",
    "termsProhibited5" to "Using the platform to collect personal information for purposes unrelated to pet adoption",
    "termsProhibited6" to "Any illegal activities or violations of animal welfare laws",
    "termsAccountTerminationH2" to "Account Termination",
    "termsAccountTerminationText" to "Adopt-U reserves the right to suspend or terminate accounts that violate these terms, engage in prohibited activities, or bring the platform into disrepute. We may also remove listings that contain false, misleading, or inappropriate content.",
    "termsModificationsH2" to "Modifications to Terms",
    "termsModificationsText" to "We reserve the right to modify these terms at any time. Continued use of the platform after changes constitutes acceptance of the modified terms. We will notify users of significant changes via email or platform announcements.",
    "termsContactText" to "If you have any questions about these Terms and Conditions, please contact us at adopt-u@adopt-u.org.",
    "language" to "Language",
    "displayName" to "Display Name",
    "saveProfile" to "Save Profile",
    "accountSettings" to "Account Settings",
    "yourRoles" to "Your Roles:",
    "resources" to "Resources",
    "loginWithPasskey" to "Login with Passkey",
    "signInWithPasskey" to "Sign in with Passkey",
    "resendVerificationEmail" to "Resend Verification Email",
    "usesFido" to "Uses FIDO2 / WebAuthn for secure passwordless authentication.",
    "loginWithMagicLink" to "Login with Email Link",
    "magicLinkDesc" to "We'll send you a login link to your email (valid for 5 minutes).",
    "sendMagicLink" to "Send Login Link",
    "loginWithPassword" to "Login with Password",
    "signIn" to "Sign In",
    "forgotPassword" to "Forgot Password?",
    "registerNewAccount" to "Create Account",
    "accountDetails" to "Account Details",
    "registrationMethod" to "Registration Method",
    "selectAdditionalRoles" to "Select additional roles:",
    "adoptPet" to "Adopt a pet",
    "publishPets" to "Publish pets for adoption",
    "offerPhotography" to "Offer photography services",
    "photographerSettings" to "Photographer Settings",
    "sessionFee" to "Session Fee",
    "provideTemporaryHome" to "Provide temporary home for pets",
    "passkeyNoPassword" to "Passkeys are more secure than passwords and work across all your devices.",
    "registerWithPasskey" to "Register with Passkey",
    "passkeySettings" to "Passkey Settings",
    "password" to "Password",
    "confirmPassword" to "Confirm Password",
    "passwordRequirements" to "Password must be at least 8 characters and contain uppercase, lowercase, number, and symbol",
    "securityInfo" to "Security Information",
    "passkeyMostSecure" to "Passkey: Most secure, recommended",
    "passkeyBrowserCompat" to "Passkey may not work on all browsers",
    "passwordLeastSecure" to "Password: Less secure, not recommended",
    "selectLoginMethods" to "Select one or more login methods:",
    "registerPasskeyOption" to "Passkey (most secure, works on all your devices)",
    "registerPasswordOption" to "Password (less secure, use as backup only)",
    "howPasskeysWorkTitle" to "How does a passkey work?",
    "howPasskeysWorkIntro" to "A passkey replaces your password with your device's built-in security — fingerprint, face recognition, or screen lock PIN.",
    "howPasskeysWorkSecurity" to "It's stored only on your device, can't be phished or leaked in a data breach, and syncs automatically across your devices.",
    "howPasskeysWorkBackup" to "You can add a password too, but a passkey alone is enough to sign in — no password needed.",
    "gotIt" to "Got it",
    "alreadyHaveAccount" to "Already have an account?",
    "all" to "All",
    "filterDogs" to "Dogs",
    "filterCats" to "Cats",
    "filterBirds" to "Birds",
    "filterFish" to "Fish",
    "filterAllSexOption" to "All Sex",
    "filterMaleOption" to "♂ Male",
    "filterFemaleOption" to "♀ Female",
    "age" to "Age",
    "rescued" to "Rescued",
    "years" to "years",
    "months" to "months",
    "year" to "year",
    "month" to "month",
    "loginToAdopt" to "Log in to request adoption",
    "requiredMarker" to "(required)",
    "pageNotFoundTitle" to "Page not found",
    "pageNotFoundHint" to "The link may be old or mistyped. The pets are still here.",
    "browsePets" to "Browse pets",
    "requestsFromRescuers" to "Requests from Rescuers",
    "signingOut" to "Signing out...",
    "adopter" to "Adopter",
    "country" to "Country",
    "zip" to "ZIP",
    "viewPublic" to "View public page",
    "banUser" to "Ban User",
    "banReason" to "Reason (optional)",
    "myPhotoRequests" to "My Photo Session Requests",
    "noPhotoRequestsYet" to "You haven't requested a photo session yet.",
    "photoRequestsReceived" to "Photo Session Requests Received",
    "noPhotoRequestsReceived" to "No photo session requests yet.",
    "photoStatusPending" to "Pending",
    "photoStatusApproved" to "Approved",
    "photoStatusRejected" to "Rejected",
    "photoStatusCancelled" to "Cancelled",
    "photoStatusCompleted" to "Completed",
    "cancelRequest" to "Cancel request",
    "registerConsentPrefix" to "By creating an account you accept our",
    "registerConsentTerms" to "Terms and Conditions",
    "registerConsentAnd" to "and",
    "registerConsentPrivacy" to "Privacy Policy",
    "petStatusAvailable" to "Available",
    "petStatusAdopted" to "Adopted",
    "petStatusDisabled" to "Disabled",
    "petStatusPending" to "Pending",
    "addEditLocation" to "Add/Edit Location",
    "addLocation" to "Add Location",
    "manage" to "Manage",
    "adminPanel" to "Admin Panel",
    "adminSterilizationLocations" to "Manage Sterilization Locations",
    "adopterRequired" to "Adopter (required)",
    "adoptionFee" to "Adoption Fee",
    "adoptionRequests" to "Adoption Requests",
    "ageMonths" to "Age (months)",
    "ageYears" to "Age (years)",
    "alias" to "Alias",
    "bird" to "Bird",
    "breed" to "Breed",
    "medicalEventsOverview" to "Medical Events Overview",
    "back" to "Back",
    "cat" to "Cat",
    "color" to "Color",
    "compatibility" to "Compatibility",
    "currentPassword" to "Current Password",
    "dog" to "Dog",
    "emailChange" to "Change Email",
    "emailChangeDesc" to "Update your email address. A verification link will be sent to the new email.",
    "energyLevel" to "Energy Level",
    "female" to "Female",
    "findTemporalHome" to "Find Temporal Home",
    "fish" to "Fish",
    "goodWithCats" to "Good with cats",
    "goodWithDogs" to "Good with dogs",
    "goodWithKids" to "Good with kids",
    "high" to "High",
    "houseTrained" to "House trained",
    "large" to "Large",
    "low" to "Low",
    "male" to "Male",
    "managePets" to "Manage Pets",
    "managePetsBtn" to "Add or Edit Pets",
    "managePetsDescription" to "Manage all pet pages. Add or remove pets.",
    "manageShelters" to "Manage Shelters",
    "manageSheltersBtn" to "Add or Edit Shelters",
    "manageSheltersDescription" to "Manage all shelter pages. Add or remove shelters.",
    "manageUsers" to "Manage Users",
    "medical" to "Medical",
    "medium" to "Medium",
    "backToSearch" to "Back to search",
    "contactThisHome" to "Contact This Temporal Home",
    "loginAsRescuerToContact" to "Log in as a rescuer to send a request to this temporal home.",
    "memberSince" to "Member since",
    "microchipId" to "Microchip ID",
    "microchipped" to "Microchipped",
    "myTemporalHome" to "My Temporal Home",
    "newEmail" to "New Email",
    "noTemporalHomes" to "No temporal homes found.",
    "requestSentToHome" to "Your request has been sent!",
    "sendRequestBtn" to "Send Request",
    "temporalHomeNotFound" to "This temporal home could not be found.",
    "viewDetails" to "View Details",
    "rescuerDirectory" to "Rescuers",
    "noRescuersFound" to "No rescuers found.",
    "availablePetsCount" to "Available pets",
    "availablePets" to "Available Pets",
    "noAvailablePetsForRescuer" to "This rescuer has no available pets right now.",
    "rescuerNotFound" to "This rescuer could not be found.",
    "errorLoadingResults" to "Error loading results.",
    "volunteerForThisRescuer" to "Volunteer for This Rescuer",
    "volunteerExplanation" to "Approved volunteers can suggest edits to this rescuer's pet listings.",
    "applyToVolunteerBtn" to "Apply to Volunteer",
    "loginToVolunteer" to "Log in to apply to volunteer for this rescuer.",
    "volunteerApplicationSent" to "Your volunteer application has been sent!",
    "myVolunteering" to "My Volunteering",
    "noVolunteerApplicationsYet" to "You haven't applied to volunteer for any rescuer yet.",
    "howToVolunteerHint" to "To apply, open a rescuer's page from the directory and tap \"Apply to Volunteer\".",
    "volunteerStatusPending" to "Pending",
    "volunteerStatusActive" to "Active",
    "volunteerStatusRejected" to "Rejected",
    "volunteerApplications" to "Volunteer Applications",
    "noVolunteerApplications" to "No volunteer applications.",
    "petEditSuggestions" to "Pet Edit Suggestions",
    "noPetEditSuggestions" to "No pending edit suggestions.",
    "suggestedByLabel" to "suggested by",
    "suggestAnEditTitle" to "Suggest an Edit",
    "suggestAnEditExplanation" to "As an approved volunteer, you can suggest changes to this listing. The rescuer will review before they go live.",
    "suggestEditBtn" to "Send Suggestion",
    "editSuggestionSent" to "Your suggestion has been sent to the rescuer for review!",
    "sponsorThisPetTitle" to "Sponsor This Pet",
    "sponsorExplanation" to "Can't adopt but want to help with expenses? Offer money or in-kind support and connect directly with the rescuer.",
    "sponsorThisRescuerTitle" to "Sponsor This Rescuer",
    "sponsorRescuerExplanation" to "Support this rescuer's general costs with money or in-kind help.",
    "sponsorTypeLabel" to "What can you offer?",
    "sponsorTypeMoney" to "Money",
    "sponsorTypeInKind" to "In-kind (food, supplies, etc.)",
    "amountLabel" to "Amount (monthly)",
    "currencyLabel" to "Currency",
    "inKindDescriptionLabel" to "Describe what you can offer",
    "messageLabel" to "Message",
    "sendOfferBtn" to "Send Offer",
    "sponsorOfferSent" to "Your offer has been sent! The rescuer will reach out to arrange the details.",
    "sponsorshipOffersReceived" to "Sponsorship Offers",
    "noSponsorshipOffers" to "No sponsorship offers yet.",
    "generalFundLabel" to "General fund",
    "offeringLabel" to "Offering",
    "sponsorshipStatusSent" to "New",
    "sponsorshipStatusRead" to "Read",
    "markAsReadBtn" to "Mark as Read",
    "mySponsorshipOffers" to "My Sponsorship Offers",
    "noSponsorshipOffersYet" to "You haven't sent any sponsorship offers yet.",
    "howToSponsorHint" to "To sponsor, open a rescuer's page (general support) or a pet's page (that pet's costs) and send an offer.",
    "browsePetsLink" to "Browse pets",
    "yourMessage" to "Your message",
    "newPassword" to "New Password",
    "passkeyDesc" to "Add additional passkeys to your account for easy login across devices.",
    "passkeyName" to "Passkey Name",
    "pers" to "People",
    "petFoodDescription" to "Learn which foods are safe, harmful, or toxic for your pets",
    "petFoodGuide" to "Pet Food Guide",
    "foodInformation" to "Food Information",
    "petsForAdoption" to "Pets for Adoption",
    "chooseCountryPrompt" to "Find your new best friend",
    "chooseCountryPromptHint" to "Choose a country above to browse pets available for adoption near you.",
    "noPetsFound" to "No pets found",
    "noPetsFoundHint" to "Try a different filter or check back soon — new pets are added regularly.",
    "loadingMorePets" to "Loading more pets…",
    "photos" to "Photos (max 12)",
    "profilePhotographerRole" to "Photographer - Offer photography services",
    "profileRescuerRole" to "Rescuer - Publish pets for adoption",
    "profileTemporalHomeRole" to "Temporal Home - Provide temporary home for pets",
    "provideShelter" to "Shelter - Manage an animal shelter",
    "provideSterilization" to "Sterilization Service - Provide sterilization services",
    "requestEmailChange" to "Request Email Change",
    "rescueDate" to "Rescue Date",
    "rescueLocation" to "Rescue Location",
    "savePassword" to "Save Password",
    "selectEnergy" to "Select energy",
    "selectSize" to "Select size",
    "sex" to "Sex",
    "shelterSettings" to "Shelter Settings",
    "size" to "Size",
    "small" to "Small",
    "specialNeeds" to "Special Needs",
    "sterilizationSettings" to "Sterilization Settings",
    "sterilized" to "Sterilized",
    "temporalHomeSettings" to "Temporal Home Settings",
    "temperament" to "Temperament",
    "type" to "Type *",
    "urgentAdoption" to "Urgent adoption needed",
    "vaccinations" to "Vaccinations",
    "vetDisclaimer" to "Consult your veterinarian before making dietary changes.",
    "weight" to "Weight (kg)",
    "weightLabel" to "Weight",
    "website" to "Website",
    "securitySettings" to "Security Settings",
    "emailVerifiedTitle" to "Email Verified Successfully!",
    "emailVerifiedMessage" to "Your email has been verified. Your registration is now complete.",
    "redirectingIn" to "Redirecting to the main page in ",
    "canAlsoLogin" to "You can also ",
    "loginNow" to "Login Now",
    "emailVerificationFailed" to "Verification Failed",
    "invalidOrExpiredToken" to "The verification link is invalid or has expired.",
    "tryRegisteringAgain" to "Please try registering again.",
    "verificationEmailResent" to "Verification email was expired. A new verification email has been sent. Please check your inbox.",
    "emailVerificationSent" to "A verification email has been sent. Please check your inbox before logging in.",
    "emailNotVerifiedLogin" to "Please verify your email before logging in. Check your inbox for the verification link.",
    "invalidOrExpiredMagicLink" to "Invalid or expired magic link. Please request a new one.",
    "invalidToken" to "Invalid token.",
    "accountBanned" to "Your account has been banned.",
    "loginError" to "Login error occurred.",
    "profileUpdated" to "Profile updated!",
    "displayNameRequired" to "Display name cannot be empty",
    "saving" to "Saving...",
    "passwordSet" to "Password is set",
    "noPassword" to "No password set",
    "passwordChangeSuccess" to "Password changed successfully!",
    "passwordChangeFailed" to "Failed to change password",
    "passwordSetSuccess" to "Password set successfully!",
    "passwordSetFailed" to "Failed to set password",
    "passwordsDoNotMatch" to "Passwords do not match",
    "currentPasswordRequired" to "Current password is required",
    "emailChangeSent" to "Verification email sent to your new email address!",
    "emailChangeFailed" to "Failed to request email change",
    "emailRequired" to "Email is required",
    "sendingEllipsis" to "Sending...",
    "resetLinkSent" to "Password reset link sent! Check your email.",
    "magicLinkSent" to "Login link sent! Check your email.",
    "magicLinkSendFailed" to "Failed to send login link",
    "failedSendResetLink" to "Failed to send reset link.",
    "invalidOrMissingToken" to "Invalid or missing token.",
    "passwordTooShort" to "Password must be at least 8 characters.",
    "passwordNeedUppercase" to "Password needs at least 1 uppercase letter.",
    "passwordNeedLowercase" to "Password needs at least 1 lowercase letter.",
    "passwordNeedNumber" to "Password needs at least 1 number.",
    "passwordNeedSymbol" to "Password needs at least 1 symbol.",
    "resettingPassword" to "Resetting password...",
    "passwordResetSuccess" to "Password reset successfully! You can now login.",
    "failedResetPassword" to "Failed to reset password.",
    "loginSuccessRedirecting" to "Login successful! Redirecting...",
    "loginFailedExpiredLink" to "Login failed. The link may be invalid or expired.",
    "loginFailed" to "Login failed.",
    "emailChangedSuccess" to "Email changed successfully!",
    "failedChangeEmailExpired" to "Failed to change email. The link may be invalid or expired.",
    "failedChangeEmail" to "Failed to change email.",
    "emailVerifiedSuccess" to "Email verified successfully!",
    "failedVerifyEmailExpired" to "Failed to verify email. The link may be invalid or expired.",
    "failedVerifyEmail" to "Failed to verify email.",
    "dropImagesHint" to "Drop images here or click to browse",
    "forgotPasswordTitle" to "Forgot Password",
    "forgotPasswordIntro" to "Enter your email and we'll send you a link to reset your password.",
    "sendResetLink" to "Send Reset Link",
    "backToLogin" to "Back to Login",
    "resetPasswordTitle" to "Reset Password",
    "resetPasswordIntro" to "Enter your new password below.",
    "emailLinkLoginTitle" to "Email Link Login",
    "verifying" to "Verifying...",
    "emailChangeTitle" to "Email Change",
    "verifyContactEmailTitle" to "Verify Contact Email",
    "status" to "Status",
    "yes" to "Yes",
    "no" to "No",
    "noDescription" to "No description.",
    "urgentBadge" to "URGENT - Needs home soon!",
    "needsNewHome" to "This pet urgently needs a new home",
    "promotedReasonLabel" to "Reason",
    "selectReason" to "Select a reason",
    "promotedReasonMoving" to "Owner is moving",
    "promotedReasonComplaints" to "Complaints / can't keep more pets",
    "promotedReasonPetConflict" to "Conflict with another pet",
    "promotedReasonOther" to "Other",
    "promotedReasonDetailLabel" to "Additional details (optional)",
    "promotedReasonRequired" to "Please select a reason",
    "needsNewHomeBadge" to "Needs new home",
    "housingType" to "Housing type",
    "preferNotToSay" to "Prefer not to say",
    "houseHousing" to "House",
    "apartmentHousing" to "Apartment",
    "hasYard" to "Has a yard",
    "hasOtherPets" to "Other pets at home",
    "adoptionExperience" to "Adoption experience",
    "firstTimeAdopter" to "First-time adopter",
    "experiencedAdopter" to "Experienced adopter",
    "noAdoptionRequests" to "No adoption requests",
    "noMessage" to "No message",
    "markUnderReview" to "Mark Under Review",
    "approve" to "Approve",
    "reject" to "Reject",
    "reviewNoteLabel" to "Private review note",
    "saveNote" to "Save Note",
    "adoptionStatusPending" to "Pending",
    "adoptionStatusUnderReview" to "Under Review",
    "adoptionStatusApproved" to "Approved",
    "adoptionStatusRejected" to "Not Approved",
    "myAdoptionRequests" to "My Adoption Requests",
    "noAdoptionRequestsYet" to "You haven't requested to adopt any pets yet.",
    "medicalHistory" to "Medical History",
    "medicalSchedule" to "Vaccination & Deworming Schedule",
    "recordType" to "Type",
    "vaccination" to "Vaccination",
    "deworming" to "Deworming",
    "recordName" to "Name",
    "dateGiven" to "Date given",
    "nextDueDate" to "Next due (optional)",
    "notesOptional" to "Notes (optional)",
    "addRecord" to "Add Record",
    "noMedicalRecords" to "No vaccination or deworming records yet.",
    "nextDueLabel" to "Next due",
    "givenLabel" to "Given",
    "overdue" to "Overdue",
    "dueSoon" to "Due soon",
    "upToDate" to "Up to date",
    "recordNameRequired" to "Name is required",
    "dateGivenRequired" to "Date given is required",
    "failedToAddRecord" to "Failed to add record",
    "confirmDeleteRecord" to "Delete this record?",
    "maxCapacity" to "Max pets at once (optional)",
    "placePetHere" to "Place a Pet Here",
    "noAvailablePetsToPlace" to "You have no available pets to place right now.",
    "selectPet" to "Select pet",
    "startPlacementBtn" to "Start Placement",
    "placementStarted" to "Foster placement started!",
    "petsCurrentlyWithMe" to "Pets Currently With Me",
    "noPetsCurrentlyFostered" to "No pets are currently placed with you.",
    "sinceLabel" to "since",
    "fosterPlacement" to "Foster Placement",
    "endPlacement" to "End Placement",
    "currentlyWithLabel" to "Currently with",
    "notCurrentlyFostered" to "Not currently in foster care.",
    "confirmEndPlacement" to "End this foster placement? The pet will be recorded as back with you.",
    "aTemporalHome" to "a temporal home",
    "viewsLabel" to "Views",
    "inquiriesLabel" to "Inquiries",
    "conversionLabel" to "Conversion",
    "messageOptional" to "Message (optional)",
    "requestAdoption" to "Request Adoption",
    "editPet" to "Edit Pet",
    "adoptionRequestSubmitted" to "Adoption request submitted!",
    "failedSubmitRequest" to "Failed to submit request",
    "noPhotosYet" to "No photos yet.",
    "rabbit" to "Rabbit",
    "safeFoods" to "Safe Foods",
    "harmfulFoods" to "Harmful Foods",
    "cannotEatFoods" to "Cannot Eat",
    "legendSafe" to "Safe",
    "legendHarmful" to "Harmful",
    "legendToxic" to "Toxic",
    "food_dog_safe_chicken_name" to "Chicken",
    "food_dog_safe_chicken_detail" to "Cooked, boneless",
    "food_dog_safe_chicken_desc" to "Lean protein, easy to digest",
    "food_dog_safe_rice_name" to "Rice",
    "food_dog_safe_rice_detail" to "White or brown",
    "food_dog_safe_rice_desc" to "Good source of carbohydrates",
    "food_dog_safe_carrots_name" to "Carrots",
    "food_dog_safe_carrots_detail" to "Raw or cooked",
    "food_dog_safe_carrots_desc" to "Low in calories, high in fiber",
    "food_dog_safe_apples_name" to "Apples",
    "food_dog_safe_apples_detail" to "Without seeds",
    "food_dog_safe_apples_desc" to "Rich in vitamins",
    "food_dog_safe_peanutbutter_name" to "Peanut Butter",
    "food_dog_safe_peanutbutter_detail" to "Unsalted, no xylitol",
    "food_dog_safe_peanutbutter_desc" to "Good protein source",
    "food_dog_harmful_grapes_name" to "Grapes",
    "food_dog_harmful_grapes_detail" to "All varieties",
    "food_dog_harmful_grapes_desc" to "Can cause kidney failure",
    "food_dog_harmful_onions_name" to "Onions",
    "food_dog_harmful_onions_detail" to "All forms",
    "food_dog_harmful_onions_desc" to "Damages red blood cells",
    "food_dog_harmful_chocolate_name" to "Chocolate",
    "food_dog_harmful_chocolate_detail" to "All types",
    "food_dog_harmful_chocolate_desc" to "Toxic to dogs",
    "food_dog_harmful_garlic_name" to "Garlic",
    "food_dog_harmful_garlic_detail" to "All forms",
    "food_dog_harmful_garlic_desc" to "Causes anemia",
    "food_dog_cannot_xylitol_name" to "Xylitol",
    "food_dog_cannot_xylitol_detail" to "Artificial sweetener",
    "food_dog_cannot_xylitol_desc" to "Highly toxic",
    "food_dog_cannot_macadamia_name" to "Macadamia Nuts",
    "food_dog_cannot_macadamia_detail" to "All forms",
    "food_dog_cannot_macadamia_desc" to "Causes weakness",
    "food_dog_cannot_avocado_name" to "Avocado",
    "food_dog_cannot_avocado_detail" to "Pit, skin, all",
    "food_dog_cannot_avocado_desc" to "Contains persin",
    "food_dog_cannot_alcohol_name" to "Alcohol",
    "food_dog_cannot_alcohol_detail" to "All forms",
    "food_dog_cannot_alcohol_desc" to "Highly toxic",
    "food_cat_safe_cookedfish_name" to "Cooked Fish",
    "food_cat_safe_cookedfish_detail" to "Without bones",
    "food_cat_safe_cookedfish_desc" to "High in protein",
    "food_cat_safe_chicken_name" to "Chicken",
    "food_cat_safe_chicken_detail" to "Cooked, plain",
    "food_cat_safe_chicken_desc" to "Good protein source",
    "food_cat_safe_pumpkin_name" to "Pumpkin",
    "food_cat_safe_pumpkin_detail" to "Plain, cooked",
    "food_cat_safe_pumpkin_desc" to "Helps digestion",
    "food_cat_safe_eggs_name" to "Eggs",
    "food_cat_safe_eggs_detail" to "Cooked",
    "food_cat_safe_eggs_desc" to "Complete protein",
    "food_cat_harmful_raweggs_name" to "Raw Eggs",
    "food_cat_harmful_raweggs_detail" to "With avidin",
    "food_cat_harmful_raweggs_desc" to "Interferes with biotin",
    "food_cat_harmful_rawfish_name" to "Raw Fish",
    "food_cat_harmful_rawfish_detail" to "Contains thiaminase",
    "food_cat_harmful_rawfish_desc" to "Breaks down B vitamins",
    "food_cat_harmful_dogfood_name" to "Dog Food",
    "food_cat_harmful_dogfood_detail" to "Any",
    "food_cat_harmful_dogfood_desc" to "Lacks taurine",
    "food_cat_harmful_milk_name" to "Milk",
    "food_cat_harmful_milk_detail" to "Most cats",
    "food_cat_harmful_milk_desc" to "Lactose intolerance",
    "food_cat_cannot_chocolate_name" to "Chocolate",
    "food_cat_cannot_chocolate_detail" to "All types",
    "food_cat_cannot_chocolate_desc" to "Theobromine toxic",
    "food_cat_cannot_onionsgarlic_name" to "Onions/Garlic",
    "food_cat_cannot_onionsgarlic_detail" to "All forms",
    "food_cat_cannot_onionsgarlic_desc" to "Damages RBC",
    "food_cat_cannot_grapesraisins_name" to "Grapes/Raisins",
    "food_cat_cannot_grapesraisins_detail" to "All varieties",
    "food_cat_cannot_grapesraisins_desc" to "Kidney damage",
    "food_cat_cannot_alcohol_name" to "Alcohol",
    "food_cat_cannot_alcohol_detail" to "All forms",
    "food_cat_cannot_alcohol_desc" to "Very toxic",
    "food_bird_safe_seeds_name" to "Seeds",
    "food_bird_safe_seeds_detail" to "Variety mix",
    "food_bird_safe_seeds_desc" to "Good fat source",
    "food_bird_safe_fruits_name" to "Fruits",
    "food_bird_safe_fruits_detail" to "Most varieties",
    "food_bird_safe_fruits_desc" to "Vitamins and minerals",
    "food_bird_safe_vegetables_name" to "Vegetables",
    "food_bird_safe_vegetables_detail" to "Leafy greens",
    "food_bird_safe_vegetables_desc" to "Essential nutrients",
    "food_bird_safe_pellets_name" to "Pellets",
    "food_bird_safe_pellets_detail" to "Quality formulated",
    "food_bird_safe_pellets_desc" to "Complete nutrition",
    "food_bird_harmful_avocado_name" to "Avocado",
    "food_bird_harmful_avocado_detail" to "All parts",
    "food_bird_harmful_avocado_desc" to "Persin toxic",
    "food_bird_harmful_fruitpits_name" to "Fruit Pits",
    "food_bird_harmful_fruitpits_detail" to "Apple, peach",
    "food_bird_harmful_fruitpits_desc" to "Cyanide traces",
    "food_bird_harmful_salt_name" to "Salt",
    "food_bird_harmful_salt_detail" to "Any form",
    "food_bird_harmful_salt_desc" to "Toxic to birds",
    "food_bird_harmful_caffeine_name" to "Caffeine",
    "food_bird_harmful_caffeine_detail" to "Coffee, tea",
    "food_bird_harmful_caffeine_desc" to "Heart issues",
    "food_bird_cannot_chocolate_name" to "Chocolate",
    "food_bird_cannot_chocolate_detail" to "All types",
    "food_bird_cannot_chocolate_desc" to "Theobromine toxic",
    "food_bird_cannot_onions_name" to "Onions",
    "food_bird_cannot_onions_detail" to "All forms",
    "food_bird_cannot_onions_desc" to "Damages RBC",
    "food_bird_cannot_garlic_name" to "Garlic",
    "food_bird_cannot_garlic_detail" to "All forms",
    "food_bird_cannot_garlic_desc" to "Toxic",
    "food_bird_cannot_mushrooms_name" to "Mushrooms",
    "food_bird_cannot_mushrooms_detail" to "Wild varieties",
    "food_bird_cannot_mushrooms_desc" to "Potential toxins",
    "food_fish_safe_flakes_name" to "Flakes",
    "food_fish_safe_flakes_detail" to "Quality brands",
    "food_fish_safe_flakes_desc" to "Complete fish food",
    "food_fish_safe_pellets_name" to "Pellets",
    "food_fish_safe_pellets_detail" to "Floating/sinking",
    "food_fish_safe_pellets_desc" to "Balanced nutrition",
    "food_fish_safe_frozenfood_name" to "Frozen Food",
    "food_fish_safe_frozenfood_detail" to "Bloodworms, brine",
    "food_fish_safe_frozenfood_desc" to "Protein source",
    "food_fish_safe_vegetables_name" to "Vegetables",
    "food_fish_safe_vegetables_detail" to "Blanched zucchini",
    "food_fish_safe_vegetables_desc" to "Fiber source",
    "food_fish_harmful_bread_name" to "Bread",
    "food_fish_harmful_bread_detail" to "Any type",
    "food_fish_harmful_bread_desc" to "No nutrition, fills up",
    "food_fish_harmful_humanfood_name" to "Human Food",
    "food_fish_harmful_humanfood_detail" to "Cooked meals",
    "food_fish_harmful_humanfood_desc" to "Wrong nutrition",
    "food_fish_harmful_livefeed_name" to "Live Feed",
    "food_fish_harmful_livefeed_detail" to "Wild caught insects",
    "food_fish_harmful_livefeed_desc" to "Potential parasites",
    "food_fish_cannot_landinsect_name" to "Land Insect",
    "food_fish_cannot_landinsect_detail" to "Crawling bugs",
    "food_fish_cannot_landinsect_desc" to "Not natural food",
    "food_fish_cannot_mammalmeat_name" to "Mammal Meat",
    "food_fish_cannot_mammalmeat_detail" to "Chicken, beef",
    "food_fish_cannot_mammalmeat_desc" to "Wrong for most fish",
    "food_fish_cannot_dairy_name" to "Dairy",
    "food_fish_cannot_dairy_detail" to "Any",
    "food_fish_cannot_dairy_desc" to "Cannot digest",
    "food_fish_cannot_breadcrumbs_name" to "Breadcrumbs",
    "food_fish_cannot_breadcrumbs_detail" to "Many contain garlic",
    "food_fish_cannot_breadcrumbs_desc" to "Harmful additives",
    "food_rabbit_safe_hay_name" to "Hay",
    "food_rabbit_safe_hay_detail" to "Timothy grass",
    "food_rabbit_safe_hay_desc" to "80% of diet",
    "food_rabbit_safe_leafygreens_name" to "Leafy Greens",
    "food_rabbit_safe_leafygreens_detail" to "Romaine, cilantro",
    "food_rabbit_safe_leafygreens_desc" to "Daily vitamins",
    "food_rabbit_safe_pellets_name" to "Pellets",
    "food_rabbit_safe_pellets_detail" to "Timothy-based",
    "food_rabbit_safe_pellets_desc" to "Balanced nutrition",
    "food_rabbit_safe_carrots_name" to "Carrots",
    "food_rabbit_safe_carrots_detail" to "In moderation",
    "food_rabbit_safe_carrots_desc" to "Treat",
    "food_rabbit_harmful_icebergletttuce_name" to "Iceberg Lettuce",
    "food_rabbit_harmful_icebergletttuce_detail" to "No nutrition",
    "food_rabbit_harmful_icebergletttuce_desc" to "Can cause GI stasis",
    "food_rabbit_harmful_beans_name" to "Beans",
    "food_rabbit_harmful_beans_detail" to "All legumes",
    "food_rabbit_harmful_beans_desc" to "Gas, digestive issues",
    "food_rabbit_harmful_corn_name" to "Corn",
    "food_rabbit_harmful_corn_detail" to "Any form",
    "food_rabbit_harmful_corn_desc" to "Cannot digest",
    "food_rabbit_harmful_potatoes_name" to "Potatoes",
    "food_rabbit_harmful_potatoes_detail" to "Any form",
    "food_rabbit_harmful_potatoes_desc" to "Wrong nutrients",
    "food_rabbit_cannot_chocolate_name" to "Chocolate",
    "food_rabbit_cannot_chocolate_detail" to "All types",
    "food_rabbit_cannot_chocolate_desc" to "Theobromine toxic",
    "food_rabbit_cannot_onions_name" to "Onions",
    "food_rabbit_cannot_onions_detail" to "All forms",
    "food_rabbit_cannot_onions_desc" to "Very toxic",
    "food_rabbit_cannot_garlic_name" to "Garlic",
    "food_rabbit_cannot_garlic_detail" to "All forms",
    "food_rabbit_cannot_garlic_desc" to "Toxic",
    "food_rabbit_cannot_avocado_name" to "Avocado",
    "food_rabbit_cannot_avocado_detail" to "All parts",
    "food_rabbit_cannot_avocado_desc" to "Contains persin",
    "primary" to "Primary",
    "passkeyRegisteredSuccess" to "Passkey registered successfully!",
    "passkeyRegistrationFailed" to "Passkey registration failed",
    "passkeyNameRequired" to "Passkey name is required",
    "registeringPasskey" to "Registering passkey...",
    "passkeyRegistered" to "Passkey registered",
    "noPasskey" to "No passkey registered",
    "noPets" to "No pets yet.",
    "photographerCountryStateRequired" to "Please fill in country and state for photographer services",
        "temporalHomeAliasCountryCityRequired" to "Please fill in alias, country and city for temporal home",
        "shelterNameCountryCityAddressRequired" to "Please fill in name, country, city and address for shelter",
        "sterilizationNameCountryCityAddressRequired" to "Please fill in name, country, city and address for sterilization location",
        "country.afghanistan" to "Afghanistan",
        "country.albania" to "Albania",
        "country.algeria" to "Algeria",
        "country.argentina" to "Argentina",
        "country.armenia" to "Armenia",
        "country.australia" to "Australia",
        "country.austria" to "Austria",
        "country.azerbaijan" to "Azerbaijan",
        "country.bangladesh" to "Bangladesh",
        "country.belarus" to "Belarus",
        "country.belgium" to "Belgium",
        "country.bolivia" to "Bolivia",
        "country.bosnia" to "Bosnia and Herzegovina",
        "country.brazil" to "Brazil",
        "country.bulgaria" to "Bulgaria",
        "country.cambodia" to "Cambodia",
        "country.cameroon" to "Cameroon",
        "country.canada" to "Canada",
        "country.chile" to "Chile",
        "country.china" to "China",
        "country.colombia" to "Colombia",
        "country.costaRica" to "Costa Rica",
        "country.croatia" to "Croatia",
        "country.cuba" to "Cuba",
        "country.czechia" to "Czech Republic",
        "country.denmark" to "Denmark",
        "country.dominicanRepublic" to "Dominican Republic",
        "country.ecuador" to "Ecuador",
        "country.egypt" to "Egypt",
        "country.elSalvador" to "El Salvador",
        "country.estonia" to "Estonia",
        "country.ethiopia" to "Ethiopia",
        "country.finland" to "Finland",
        "country.france" to "France",
        "country.georgia" to "Georgia",
        "country.germany" to "Germany",
        "country.ghana" to "Ghana",
        "country.greece" to "Greece",
        "country.guatemala" to "Guatemala",
        "country.haiti" to "Haiti",
        "country.honduras" to "Honduras",
        "country.hungary" to "Hungary",
        "country.iceland" to "Iceland",
        "country.india" to "India",
        "country.indonesia" to "Indonesia",
        "country.iran" to "Iran",
        "country.iraq" to "Iraq",
        "country.ireland" to "Ireland",
        "country.israel" to "Israel",
        "country.italy" to "Italy",
        "country.jamaica" to "Jamaica",
        "country.japan" to "Japan",
        "country.jordan" to "Jordan",
        "country.kazakhstan" to "Kazakhstan",
        "country.kenya" to "Kenya",
        "country.kuwait" to "Kuwait",
        "country.latvia" to "Latvia",
        "country.lebanon" to "Lebanon",
        "country.libya" to "Libya",
        "country.lithuania" to "Lithuania",
        "country.luxembourg" to "Luxembourg",
        "country.malaysia" to "Malaysia",
        "country.mexico" to "Mexico",
        "country.moldova" to "Moldova",
        "country.mongolia" to "Mongolia",
        "country.montenegro" to "Montenegro",
        "country.morocco" to "Morocco",
        "country.myanmar" to "Myanmar",
        "country.nepal" to "Nepal",
        "country.netherlands" to "Netherlands",
        "country.newZealand" to "New Zealand",
        "country.nicaragua" to "Nicaragua",
        "country.nigeria" to "Nigeria",
        "country.northKorea" to "North Korea",
        "country.norway" to "Norway",
        "country.pakistan" to "Pakistan",
        "country.panama" to "Panama",
        "country.paraguay" to "Paraguay",
        "country.peru" to "Peru",
        "country.philippines" to "Philippines",
        "country.poland" to "Poland",
        "country.portugal" to "Portugal",
        "country.puertoRico" to "Puerto Rico",
        "country.qatar" to "Qatar",
        "country.romania" to "Romania",
        "country.russia" to "Russia",
        "country.saudiArabia" to "Saudi Arabia",
        "country.serbia" to "Serbia",
        "country.singapore" to "Singapore",
        "country.slovakia" to "Slovakia",
        "country.slovenia" to "Slovenia",
        "country.southAfrica" to "South Africa",
        "country.southKorea" to "South Korea",
        "country.spain" to "Spain",
        "country.sriLanka" to "Sri Lanka",
        "country.sudan" to "Sudan",
        "country.sweden" to "Sweden",
        "country.switzerland" to "Switzerland",
        "country.syria" to "Syria",
        "country.taiwan" to "Taiwan",
        "country.thailand" to "Thailand",
        "country.tunisia" to "Tunisia",
        "country.turkey" to "Turkey",
        "country.ukraine" to "Ukraine",
        "country.uae" to "United Arab Emirates",
        "country.uk" to "United Kingdom",
        "country.usa" to "United States",
        "country.uruguay" to "Uruguay",
        "country.uzbekistan" to "Uzbekistan",
        "country.venezuela" to "Venezuela",
        "country.vietnam" to "Vietnam",
        "country.yemen" to "Yemen",
        "loading" to "Loading...",
        "countryRequired" to "Please select a country to search",
        "noSheltersFound" to "No shelters found yet. Check back later!",
        "visitWebsite" to "Visit Website",
        "fiscalId" to "Fiscal ID",
        "donationInformation" to "Donation Information",
        "bankName" to "Bank",
        "accountHolder" to "Account Holder",
        "accountNumber" to "Account Number",
        "currency" to "Currency",
        "errorLoadingShelters" to "Error loading shelters",
        "pleaseSelectCountry" to "Please select a country",
        "actions" to "Actions",
        "addShelter" to "Add Shelter",
        "addEditShelter" to "Add / Edit Shelter",
        "allStates" to "All States",
        "confirmDelete" to "Are you sure you want to delete this location?",
        "confirmDeleteShelter" to "Are you sure you want to delete this shelter?",
        "confirmUnbanUser" to "Unban this user?",
        "confirmResetUserCredentials" to "This will invalidate {email}'s current password and passkeys and email them a reset link. Continue?",
        "confirmDeactivateUser" to "Deactivate this user? They can be reactivated later.",
        "confirmDeactivatePet" to "Deactivate this pet? It will be hidden from public listings and can be reactivated later.",
        "confirmBlockRescuer" to "Block this rescuer from sending you more requests?",
        "confirmDeletePet" to "Delete this pet?",
        "confirmDeleteStorage" to "Delete this storage?",
        "contact" to "Contact",
        "delete" to "Delete",
        "edit" to "Edit",
        "errorDeletingShelter" to "Error deleting shelter",
        "errorLoadingLocations" to "Error loading locations",
        "errorLoadingShelter" to "Error loading shelter",
        "errorSaving" to "Error saving",
        "errorSavingShelter" to "Error saving shelter",
        "location" to "Location",
        "noLocationsFound" to "No locations found",
        "selectCountry" to "Select a country",
        "selectCountryToFilter" to "Please select a country to filter",
        "shelterAdded" to "Shelter added successfully",
        "shelterDeleted" to "Shelter deleted successfully",
        "shelterUpdated" to "Shelter updated successfully",
        "updateShelter" to "Update Shelter",
        "errorLoadingPhotographers" to "Error loading photographers",
        "noPhotographersAvailable" to "No photographers available",
        "requestPhotoSession" to "Request Photo Session",
        "selectPhotographer" to "Select",
        "photographersSelected" to "{count} selected",
        "requestSelectedPhotographers" to "Request selected photographers",
        "clearSelection" to "Clear selection",
        "requestToMultiple" to "Request to",
        "messageRequired" to "Please write a message for the photographers.",
        "requestsSentSuccessfully" to "Requests sent successfully",
        "message" to "Message",
        "sendRequest" to "Send Request",
        "requestSentSuccessfully" to "Request sent successfully!",
        "requestTo" to "Request to",
        "enterMessage" to "Enter a message for {name}...",
        "reportUrgent" to "Urgent",
        "topRescuers" to "Top Rescuers",
        "urgentRescuerSettings" to "Urgent Rescuer Settings",
        "urgentRescueAlerts" to "Urgent Rescue Alerts",
        "reportUrgentTitle" to "Report a Pet in Danger",
        "reportUrgentIntro" to "If you've found an injured, abused, starving, or abandoned pet that needs immediate help, tell us where and what's wrong - we'll alert nearby urgent rescuers right away.",
        "dangerType" to "What's wrong?",
        "dangerInjured" to "Injured",
        "dangerAbused" to "Being abused / mistreated",
        "dangerStarving" to "Starving",
        "dangerTooYoung" to "Too young to survive alone",
        "dangerOther" to "Other emergency",
        "useMyLocation" to "Use my current location",
        "confirmMyLocation" to "Confirm my current location",
        "locationPermissionHint" to "Your browser will ask for permission to share your location; it's only used to page you about reports near you.",
        "phoneOptional" to "Your phone (optional)",
        "submitReport" to "Submit Report",
        "urgentRescuerSettingsTitle" to "Urgent Rescuer Settings",
        "urgentRescuerSettingsIntro" to "Get paged when a pet in danger is reported near your coverage area. First to accept takes the case.",
        "urgentRescuerActive" to "I'm available to respond to urgent reports",
        "coverageAreaMode" to "Coverage area",
        "coverageArea" to "Coverage area",
        "coverageAreaHint" to "Confirm your current location, drag the pin, or type an address below - the map and the fields stay in sync.",
        "useMyLocationAndRadius" to "Use my location + a radius",
        "pickAZone" to "Pick a country/state/city instead",
        "radiusKm" to "Radius (km)",
        "urgentRescueAlertsTitle" to "Urgent Rescue Alerts",
        "urgentRescuerLeaderboardTitle" to "Top Urgent Rescuers (last 12 months)",
        "locating" to "Locating...",
        "geolocationUnsupported" to "Geolocation is not supported by your browser",
        "locationCaptured" to "Location captured",
        "locationDenied" to "Couldn't get your location - please allow location access or enter a city instead",
        "street" to "Street",
        "exteriorNumber" to "Exterior number",
        "referenceNotes" to "Reference notes (e.g. house color, nearby landmark)",
        "concreteAddressHint" to "Add a street and number if you can - this is what lets a rescuer take a taxi or open a maps app straight to the spot.",
        "descriptionRequired" to "Please describe the situation",
        "locationRequired" to "Please provide a location (use your current location or enter a country and city)",
        "submittingEllipsis" to "Submitting...",
        "reportSubmitted" to "Report submitted. Thank you - nearby rescuers are being alerted now.",
        "reportSubmitFailed" to "Failed to submit report",
        "phoneRequired" to "Please enter a phone number",
        "savingEllipsis" to "Saving...",
        "saved" to "Saved",
        "saveFailed" to "Failed to save",
        "noUrgentPages" to "No pending urgent alerts right now",
        "accept" to "Accept",
        "alreadyAccepted" to "Someone else already accepted this report",
        "noLeaderboardData" to "No accepted rescues in the last 12 months yet",
        "rescueAcceptedTitle" to "Rescue Accepted",
        "rescueAcceptedMessage" to "Thank you - you're on it. Head to the reported location as soon as you can.",
        "rescueAcceptFailedTitle" to "Couldn't Accept",
        "rescueAcceptFailedMessage" to "This report was already accepted by another rescuer, or the link is invalid or expired.",
        "share" to "Share",
        "shareViaFacebook" to "Share on Facebook",
        "shareViaX" to "Share on X",
        "shareViaWhatsapp" to "Share on WhatsApp",
        "shareViaEmail" to "Share via Email",
        "copyLink" to "Copy Link",
        "linkCopied" to "Link copied!",
        "reportLostFoundTitle" to "Report a Lost or Found Pet",
        "reportLostFoundIntro" to "Lost your pet, or found a stray? File a report and we'll check it against other reports nearby.",
        "browseLostFound" to "Browse existing lost & found reports",
        "kindLost" to "I lost my pet",
        "kindFound" to "I found a pet",
        "petType" to "Type of pet",
        "petTypeUnknown" to "Not sure",
        "lostFoundBrowseTitle" to "Lost & Found Pets",
        "showing" to "Showing",
        "kindLostPlural" to "Lost pets",
        "kindFoundPlural" to "Found pets",
        "reportResolvedTitle" to "Report Resolved",
        "reportResolvedMessage" to "Thanks for the update - this report is now closed.",
        "reportResolveFailedTitle" to "Couldn't Resolve",
        "reportResolveFailedMessage" to "This report was already resolved, or the link is invalid or expired.",
        "reportLostFound" to "Lost/\u200BFound",
        "noLostFoundReportsFound" to "No lost & found reports found",
        "errorLoadingReports" to "Error loading reports",
        "yourEmail" to "Your email",
        "contactReporter" to "Contact reporter",
        "contactFormIncomplete" to "Please enter your email and a message",
        "contactSent" to "Message sent",
        "contactFailed" to "Failed to send message",
        "lostFoundReportSubmitted" to "Report submitted. Thank you - we'll check it against other reports nearby.",
        "video" to "Video (optional)",
        "removeVideo" to "Remove video",
        "saveThisSearch" to "Save this search",
        "savedSearchSaved" to "Search saved! We'll email you when a new match is listed.",
        "savedSearchFailed" to "Failed to save search",
        "favorite" to "Favorite",
        "addToFavorites" to "Add to Favorites",
        "removeFromFavorites" to "Remove from Favorites",
        "myFavorites" to "My Favorites",
        "noFavoritesYet" to "No favorites yet",
        "mySavedSearches" to "My Saved Searches",
        "noSavedSearchesYet" to "No saved searches yet",
        "donationPromptText" to "Thank you for helping a pet today. If you can, consider a small donation to keep Adopt-U free for everyone.",
        "donationPromptCta" to "Donate",
        "dismiss" to "Dismiss",
    )

    private val spanishTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Iniciar sesión",
    "register" to "Registrarse",
    "logout" to "Cerrar sesión",
    "profile" to "Perfil",
    "myPets" to "Mis mascotas",
    "addPet" to "Agregar mascota",
    "save" to "Guardar",
    "cancel" to "Cancelar",
    "search" to "Buscar",
    "shelters" to "Refugios",
    "photographers" to "Fotógrafos",
    "temporalHomes" to "Casas temporales",
    "findTemporalHomes" to "Buscar casas temporales",
    "sterilizationLocations" to "Lugares de esterilización",
    "donate" to "Donar",
    "countryLabel" to "País",
    "selectCountryToSearch" to "Selecciona un país",
    "selectCountryFirst" to "Selecciona un país para activar estos filtros",
    "state" to "Estado",
    "city" to "Ciudad",
    "zipCode" to "Código postal",
    "neighborhood" to "Barrio",
    "name" to "Nombre",
    "email" to "Correo electrónico",
    "phone" to "Teléfono",
    "address" to "Dirección",
    "description" to "Descripción",
    "animalShelters" to "Refugios de animales",
    "sheltersDescription" to "Encuentra refugios de animales donde puedes encontrar mascotas para adoptar o dejarlas previo acuerdo con el refugio",
    "photographerDescription" to "Fotógrafos profesionales ofreciendo sesiones de fotos de mascotas",
    "sterilizationLocationsDescription" to "Encuentra lugares donde puedes llevar animales para esterilizar.",
    "privacyPolicy" to "Política de privacidad",
    "privacyLastUpdated" to "Última actualización: abril de 2025",
    "privacyHighlight" to "En Adopt-U, creemos en la recopilación mínima de datos. Solo recopilamos tu dirección de correo electrónico para permitir la comunicación entre adoptantes y rescatistas. No usamos tu información con fines publicitarios y nunca compartiremos tus datos con terceros con fines de marketing.",
    "privacyInfoCollectedH2" to "Información que recopilamos",
    "privacyAccountRegH3" to "Registro de cuenta",
    "privacyAccountRegIntro" to "Para crear una cuenta en Adopt-U, recopilamos la siguiente información:",
    "privacyEmailLabel" to "Dirección de correo electrónico: ",
    "privacyEmailDesc" to "Necesaria para la verificación de la cuenta, la recuperación de contraseña y la comunicación entre adoptantes y rescatistas",
    "privacyDisplayNameLabel" to "Nombre para mostrar: ",
    "privacyDisplayNameDesc" to "Un nombre que eliges para ser identificado en la plataforma",
    "privacyLangPrefLabel" to "Preferencia de idioma: ",
    "privacyLangPrefDesc" to "Para ofrecer la plataforma en tu idioma preferido",
    "privacyNoFullNameEtc" to "No recopilamos tu nombre completo, número de teléfono, dirección física, documentos de identificación, información de pago ni ningún otro dato personal más allá de lo indicado anteriormente.",
    "privacyOptionalProfileH3" to "Información de perfil opcional",
    "privacyOptionalProfileIntro" to "Según tu rol, puedes proporcionar opcionalmente:",
    "privacyAdopterLabel" to "Como Adoptante: ",
    "privacyAdopterDesc" to "No se requiere ni se recopila información adicional",
    "privacyRescuerLabel" to "Como Rescatista: ",
    "privacyRescuerDesc" to "Información de ubicación de las mascotas que tienes disponibles para adopción",
    "privacyPhotographerLabel" to "Como Fotógrafo: ",
    "privacyPhotographerDesc" to "Ubicación del servicio y preferencias de contacto",
    "privacyTemporalHomeLabel" to "Como Hogar Temporal: ",
    "privacyTemporalHomeDesc" to "Ubicación donde puedes brindar alojamiento temporal",
    "privacyLocationUsage" to "Toda la información de ubicación se utiliza únicamente para conectar a los adoptantes con rescatistas y proveedores de servicios cercanos.",
    "privacyPetInfoH3" to "Información de la mascota",
    "privacyPetInfoIntro" to "Los rescatistas pueden publicar información sobre las mascotas, incluyendo:",
    "privacyPetPhotos" to "Fotos de la mascota",
    "privacyPetDescBreedAge" to "Descripción, raza, edad y características",
    "privacyPetHealth" to "Estado de salud y vacunación",
    "privacyPetLocation" to "Ubicación donde la mascota está disponible",
    "privacyPetInfoNotVerified" to "Esta información es proporcionada por los rescatistas y no es verificada de forma independiente por Adopt-U.",
    "privacyHowWeUseH2" to "Cómo usamos tu información",
    "privacyPrimaryPurposeH3" to "Propósito principal: Facilitar conexiones de adopción",
    "privacyEmailUsedFor" to "Tu dirección de correo electrónico se utiliza exclusivamente para los siguientes fines:",
    "privacyUseVerification" to "Enviarte un enlace de verificación al crear una cuenta",
    "privacyUseRescuerContact" to "Permitir que los rescatistas te contacten sobre mascotas que te interesan",
    "privacyUseContactRescuer" to "Permitirte contactar a los rescatistas sobre las mascotas que publican",
    "privacyUseNotifications" to "Enviarte notificaciones importantes sobre tu cuenta y seguridad",
    "privacyOnceConnected" to "Una vez que te conectas con otro usuario a través de nuestra plataforma, toda comunicación posterior se realiza directamente entre ustedes. No monitoreamos, almacenamos ni tenemos acceso al contenido de tus comunicaciones.",
    "privacyNeverDoH3" to "Lo que nunca haremos",
    "privacyCommitIntro" to "Nos comprometemos solemnemente a lo siguiente:",
    "privacyNoAdsLabel" to "Sin publicidad: ",
    "privacyNoAdsDesc" to "Nunca mostraremos anuncios dirigidos o no dirigidos basados en tu actividad",
    "privacyNoMarketingLabel" to "Sin marketing: ",
    "privacyNoMarketingDesc" to "Nunca compartiremos tu correo electrónico con terceros con fines de marketing o promocionales",
    "privacyNoSellingLabel" to "Sin venta de datos: ",
    "privacyNoSellingDesc" to "Nunca venderemos, alquilaremos ni transferiremos de otro modo tu información personal a terceros",
    "privacyNoProfilingLabel" to "Sin perfilado: ",
    "privacyNoProfilingDesc" to "No usaremos tus datos para crear perfiles con fines distintos a la gestión básica de la cuenta",
    "privacyNoTrackingLabel" to "Sin rastreo: ",
    "privacyNoTrackingDesc" to "No utilizamos píxeles de seguimiento, cookies publicitarias ni tecnologías de rastreo similares",
    "privacyDataStorageH2" to "Almacenamiento y seguridad de datos",
    "privacyDataStoredSecurely" to "Tus datos se almacenan de forma segura utilizando cifrado estándar de la industria. Empleamos medidas técnicas y organizativas adecuadas para proteger tu información personal contra el acceso, alteración, divulgación o destrucción no autorizados.",
    "privacyDataRetention" to "Conservamos la información de tu cuenta mientras esta permanezca activa. Puedes solicitar la eliminación de tu cuenta y los datos asociados en cualquier momento contactándonos.",
    "privacyYourRightsH2" to "Tus derechos",
    "privacyRightsIntro" to "Tienes los siguientes derechos sobre tus datos personales:",
    "privacyRightAccess" to "Acceso: Puedes solicitar una copia de todos los datos personales que tenemos sobre ti",
    "privacyRightCorrection" to "Corrección: Puedes solicitar que corrijamos cualquier información inexacta",
    "privacyRightDeletion" to "Eliminación: Puedes solicitar que eliminemos tu cuenta y todos los datos asociados",
    "privacyRightPortability" to "Portabilidad: Puedes solicitar que te proporcionemos tus datos en un formato de uso común",
    "privacyExerciseRights" to "Para ejercer cualquiera de estos derechos, contáctanos en adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Cookies",
    "privacyCookiesText" to "Adopt-U utiliza cookies mínimas necesarias para el funcionamiento de la plataforma, incluyendo la gestión de sesiones y las preferencias de idioma. No utilizamos cookies publicitarias ni de rastreo.",
    "privacyThirdPartyH2" to "Servicios de terceros",
    "privacyThirdPartyIntro" to "Utilizamos los siguientes servicios de terceros, que pueden procesar tus datos:",
    "privacyAws" to "AWS (Amazon Web Services): Para almacenamiento de datos y envío de correos electrónicos. Sus políticas de privacidad aplican al procesamiento de tus datos.",
    "privacyWebauthn" to "WebAuthn/FIDO2: Para autenticación segura mediante passkey. Esto implica operaciones criptográficas en tu navegador.",
    "privacyThirdPartySelected" to "Hemos seleccionado cuidadosamente proveedores de servicios que comparten nuestro compromiso con la privacidad de los datos.",
    "privacyChildrensH2" to "Privacidad de menores",
    "privacyChildrensText" to "Adopt-U no está destinado a usuarios menores de 18 años. No recopilamos conscientemente información de menores. Si crees que un menor ha creado una cuenta, contáctanos para que sea eliminada.",
    "privacyChangesH2" to "Cambios a esta política",
    "privacyChangesText" to "Podemos actualizar esta Política de Privacidad periódicamente para reflejar cambios en nuestras prácticas o requisitos legales. Notificaremos a los usuarios sobre cambios significativos por correo electrónico o mediante un aviso destacado en la plataforma.",
    "privacyContactH2" to "Contáctanos",
    "privacyContactText" to "Si tienes alguna pregunta o inquietud sobre esta Política de Privacidad, o deseas ejercer tus derechos sobre tus datos, contáctanos en adopt-u@adopt-u.org.",
    "termsConditions" to "Términos y condiciones",
    "installApp" to "Instalar App",
    "termsAcceptanceH2" to "Aceptación de los términos",
    "termsAcceptanceText" to "Al acceder y utilizar Adopt-U, aceptas y te comprometes a cumplir los términos y disposiciones de este acuerdo. Si no estás de acuerdo con estos términos, no utilices nuestra plataforma.",
    "termsNatureH2" to "Naturaleza de nuestro servicio",
    "termsNatureText1" to "Adopt-U es una plataforma de conexión que facilita el contacto entre rescatistas de mascotas y posibles adoptantes. No somos una tienda de mascotas, intermediarios, refugio ni agencia de adopción. No poseemos, custodiamos ni manejamos directamente ninguna mascota publicada en nuestra plataforma.",
    "termsNatureText2" to "Nuestro papel se limita estrictamente a proporcionar un canal de comunicación entre las partes. No participamos en las negociaciones de adopción, contratos ni en la entrega física de los animales.",
    "termsUserRespH2" to "Responsabilidades del usuario",
    "termsForRescuersH3" to "Para Rescatistas:",
    "termsRescuerResp1" to "Proporcionar información precisa y veraz sobre las mascotas a tu cuidado",
    "termsRescuerResp2" to "Asegurarte de que las mascotas estén sanas, vacunadas y adecuadamente revisadas antes de publicarlas",
    "termsRescuerResp3" to "Realizar una evaluación responsable de los posibles adoptantes",
    "termsRescuerResp4" to "Organizar el traslado seguro de las mascotas a sus nuevos hogares",
    "termsRescuerResp5" to "Aceptar la responsabilidad por la exactitud de toda la información proporcionada",
    "termsRescuerResp6" to "Cumplir con todas las leyes y regulaciones locales relativas a la adopción de mascotas",
    "termsRescuerResp7" to "Nunca utilizar la plataforma para la venta comercial de mascotas o fines de cría",
    "termsForAdoptersH3" to "Para Adoptantes:",
    "termsAdopterResp1" to "Proporcionar información precisa sobre tu situación de vivienda y experiencia con mascotas",
    "termsAdopterResp2" to "Estar preparado para demostrar tu capacidad de brindar los cuidados adecuados",
    "termsAdopterResp3" to "Comprender que cancelar la adopción de una mascota causa un estrés significativo al animal",
    "termsAdopterResp4" to "Respetar el derecho del rescatista a rechazar tu solicitud de adopción",
    "termsPetListingsH2" to "Publicaciones e información de mascotas",
    "termsPetListingsText1" to "Adopt-U no verifica, avala ni garantiza la exactitud de la información proporcionada en las publicaciones de mascotas. La información, incluyendo entre otros la raza, edad, estado de salud, temperamento y registros de vacunación, es proporcionada por los rescatistas y no ha sido verificada de forma independiente.",
    "termsPetListingsText2" to "Las fotos y descripciones son proporcionadas por los rescatistas. Si bien prohibimos la tergiversación deliberada, no podemos garantizar que todas las publicaciones representen con precisión el estado actual del animal.",
    "termsPetListingsText3" to "Se recomienda a los usuarios hacer preguntas, solicitar registros veterinarios y conocer a las mascotas antes de finalizar cualquier acuerdo de adopción.",
    "termsAdoptionProcessH2" to "Proceso de adopción",
    "termsAdoptionProcessIntro" to "Una vez que un rescatista y un adoptante acuerdan proceder con una adopción:",
    "termsAdoptionStep1" to "Todos los arreglos, incluyendo la fecha de traslado, ubicación y cualquier costo asociado, se negocian directamente entre las partes",
    "termsAdoptionStep2" to "Adopt-U no facilita ni garantiza ninguna transacción de pago",
    "termsAdoptionStep3" to "La plataforma no proporciona contratos de adopción; cualquier acuerdo contractual es responsabilidad exclusiva de las partes involucradas",
    "termsAdoptionStep4" to "Recomendamos encarecidamente documentar todos los acuerdos por escrito",
    "termsLiabilityH2" to "Limitación de responsabilidad",
    "termsLiabilityIntro" to "Adopt-U no será responsable de:",
    "termsLiability1" to "Cualquier disputa, desacuerdo o conflicto entre rescatistas y adoptantes",
    "termsLiability2" to "La exactitud de la información proporcionada en las publicaciones de mascotas",
    "termsLiability3" to "La salud, el comportamiento o el estado de cualquier animal antes, durante o después de la adopción",
    "termsLiability4" to "Cualquier pérdida financiera, daño o lesión que surja del uso de la plataforma",
    "termsLiability5" to "El resultado de cualquier adopción, incluyendo entre otros devoluciones, problemas de salud o de compatibilidad",
    "termsLiability6" to "Cualquier acción realizada por los usuarios fuera de la plataforma después de haberse establecido contacto",
    "termsLiabilityAck" to "Los usuarios reconocen que adoptar una mascota es un compromiso significativo y que asumen toda la responsabilidad de su decisión de adoptar.",
    "termsProhibitedH2" to "Actividades prohibidas",
    "termsProhibitedIntro" to "Las siguientes actividades están estrictamente prohibidas en Adopt-U:",
    "termsProhibited1" to "Venta comercial de mascotas, operaciones de cría o criaderos intensivos",
    "termsProhibited2" to "Publicar mascotas de las que no te encargas directamente",
    "termsProhibited3" to "Información falsa o engañosa en publicaciones o perfiles",
    "termsProhibited4" to "Acoso, discriminación o contacto inapropiado con otros usuarios",
    "termsProhibited5" to "Usar la plataforma para recopilar información personal con fines no relacionados con la adopción de mascotas",
    "termsProhibited6" to "Cualquier actividad ilegal o violación de las leyes de bienestar animal",
    "termsAccountTerminationH2" to "Cancelación de cuenta",
    "termsAccountTerminationText" to "Adopt-U se reserva el derecho de suspender o cancelar cuentas que violen estos términos, participen en actividades prohibidas o dañen la reputación de la plataforma. También podemos eliminar publicaciones que contengan contenido falso, engañoso o inapropiado.",
    "termsModificationsH2" to "Modificaciones a los términos",
    "termsModificationsText" to "Nos reservamos el derecho de modificar estos términos en cualquier momento. El uso continuado de la plataforma después de los cambios constituye la aceptación de los términos modificados. Notificaremos a los usuarios sobre cambios significativos por correo electrónico o mediante anuncios en la plataforma.",
    "termsContactText" to "Si tienes alguna pregunta sobre estos Términos y Condiciones, contáctanos en adopt-u@adopt-u.org.",
    "language" to "Idioma",
    "displayName" to "Nombre para mostrar",
    "saveProfile" to "Guardar perfil",
    "accountSettings" to "Configuración de cuenta",
    "yourRoles" to "Tus roles:",
    "resources" to "Recursos",
    "loginWithPasskey" to "Iniciar sesión con Passkey",
    "signInWithPasskey" to "Iniciar sesión con Passkey",
    "resendVerificationEmail" to "Reenviar correo de verificación",
    "usesFido" to "Usa FIDO2 / WebAuthn para autenticación segura sin contraseña.",
    "loginWithMagicLink" to "Iniciar sesión con enlace de correo",
    "magicLinkDesc" to "Te enviaremos un enlace de inicio de sesión a tu correo (válido por 5 minutos).",
    "sendMagicLink" to "Enviar enlace de inicio",
    "loginWithPassword" to "Iniciar sesión con contraseña",
    "signIn" to "Iniciar sesión",
    "forgotPassword" to "¿Olvidaste la contraseña?",
    "registerNewAccount" to "Crear cuenta",
    "accountDetails" to "Detalles de la cuenta",
    "registrationMethod" to "Método de registro",
    "selectAdditionalRoles" to "Selecciona roles adicionales:",
    "adoptPet" to "Adoptar una mascota",
    "publishPets" to "Publicar mascotas para adopción",
    "offerPhotography" to "Ofrecer servicios de fotografía",
    "photographerSettings" to "Configuración de fotógrafo",
    "sessionFee" to "Tarifa por sesión",
    "provideTemporaryHome" to "Proveer casa temporal para mascotas",
    "passkeyNoPassword" to "Las passkeys son más seguras que las contraseñas y funcionan en todos tus dispositivos.",
    "registerWithPasskey" to "Registrarse con Passkey",
    "passkeySettings" to "Configuración de passkey",
    "password" to "Contraseña",
    "confirmPassword" to "Confirmar contraseña",
    "passwordRequirements" to "La contraseña debe tener al menos 8 caracteres y contener mayúsculas, minúsculas, números y símbolos",
    "securityInfo" to "Información de seguridad",
    "passkeyMostSecure" to "Passkey: Más seguro, recomendado",
    "passkeyBrowserCompat" to "Passkey puede no funcionar en todos los navegadores",
    "passwordLeastSecure" to "Contraseña: Menos segura, no recomendada",
    "selectLoginMethods" to "Selecciona uno o más métodos de inicio de sesión:",
    "registerPasskeyOption" to "Passkey (más seguro, funciona en todos tus dispositivos)",
    "registerPasswordOption" to "Contraseña (menos seguro, úsala solo como respaldo)",
    "howPasskeysWorkTitle" to "¿Cómo funciona una passkey?",
    "howPasskeysWorkIntro" to "Una passkey reemplaza tu contraseña con la seguridad integrada de tu dispositivo: huella digital, reconocimiento facial o PIN de bloqueo de pantalla.",
    "howPasskeysWorkSecurity" to "Se guarda solo en tu dispositivo, no puede robarse mediante phishing ni filtrarse en una brecha de datos, y se sincroniza automáticamente entre tus dispositivos.",
    "howPasskeysWorkBackup" to "También puedes agregar una contraseña, pero con la passkey basta para iniciar sesión, sin necesidad de contraseña.",
    "gotIt" to "Entendido",
    "alreadyHaveAccount" to "¿Ya tienes una cuenta?",
    "all" to "Todos",
    "filterDogs" to "Perros",
    "filterCats" to "Gatos",
    "filterBirds" to "Aves",
    "filterFish" to "Peces",
    "filterAllSexOption" to "Todos los sexos",
    "filterMaleOption" to "♂ Macho",
    "filterFemaleOption" to "♀ Hembra",
    "age" to "Edad",
    "rescued" to "Rescatado",
    "years" to "años",
    "months" to "meses",
    "year" to "año",
    "month" to "mes",
    "loginToAdopt" to "Inicia sesión para solicitar la adopción",
    "requiredMarker" to "(requerido)",
    "pageNotFoundTitle" to "Página no encontrada",
    "pageNotFoundHint" to "El enlace puede ser antiguo o estar mal escrito. Las mascotas siguen aquí.",
    "browsePets" to "Ver mascotas",
    "requestsFromRescuers" to "Solicitudes de rescatistas",
    "signingOut" to "Cerrando sesión...",
    "adopter" to "Adoptante",
    "country" to "País",
    "zip" to "Código postal",
    "viewPublic" to "Ver página pública",
    "banUser" to "Suspender usuario",
    "banReason" to "Motivo (opcional)",
    "myPhotoRequests" to "Mis solicitudes de sesión de fotos",
    "noPhotoRequestsYet" to "Aún no has solicitado una sesión de fotos.",
    "photoRequestsReceived" to "Solicitudes de sesión recibidas",
    "noPhotoRequestsReceived" to "Aún no hay solicitudes de sesión.",
    "photoStatusPending" to "Pendiente",
    "photoStatusApproved" to "Aprobada",
    "photoStatusRejected" to "Rechazada",
    "photoStatusCancelled" to "Cancelada",
    "photoStatusCompleted" to "Completada",
    "cancelRequest" to "Cancelar solicitud",
    "registerConsentPrefix" to "Al crear una cuenta aceptas nuestros",
    "registerConsentTerms" to "Términos y condiciones",
    "registerConsentAnd" to "y la",
    "registerConsentPrivacy" to "Política de privacidad",
    "petStatusAvailable" to "Disponible",
    "petStatusAdopted" to "Adoptado",
    "petStatusDisabled" to "Deshabilitado",
    "petStatusPending" to "Pendiente",
    "addEditLocation" to "Agregar/Editar ubicación",
    "addLocation" to "Agregar ubicación",
    "manage" to "Gestionar",
    "adminPanel" to "Panel de administración",
    "adminSterilizationLocations" to "Gestionar lugares de esterilización",
    "adopterRequired" to "Adoptante (requerido)",
    "adoptionFee" to "Tarifa de adopción",
    "adoptionRequests" to "Solicitudes de adopción",
    "ageMonths" to "Edad (meses)",
    "ageYears" to "Edad (años)",
    "alias" to "Alias",
    "bird" to "Pájaro",
    "breed" to "Raza",
    "medicalEventsOverview" to "Resumen de eventos médicos",
    "back" to "Atrás",
    "cat" to "Gato",
    "color" to "Color",
    "compatibility" to "Compatibilidad",
    "currentPassword" to "Contraseña actual",
    "dog" to "Perro",
    "emailChange" to "Cambiar correo electrónico",
    "emailChangeDesc" to "Actualiza tu dirección de correo. Se enviará un enlace de verificación a tu nuevo correo.",
    "energyLevel" to "Nivel de energía",
    "female" to "Hembra",
    "findTemporalHome" to "Encontrar casa temporal",
    "fish" to "Pez",
    "goodWithCats" to "Bueno con gatos",
    "goodWithDogs" to "Bueno con perros",
    "goodWithKids" to "Bueno con niños",
    "high" to "Alto",
    "houseTrained" to "Entrenado en casa",
    "large" to "Grande",
    "low" to "Bajo",
    "male" to "Macho",
    "managePets" to "Gestionar mascotas",
    "managePetsBtn" to "Agregar o editar mascotas",
    "managePetsDescription" to "Gestiona todas las páginas de mascotas. Agrega o elimina mascotas.",
    "manageShelters" to "Gestionar refugios",
    "manageSheltersBtn" to "Agregar o editar refugios",
    "manageSheltersDescription" to "Gestiona todas las páginas de refugios. Agrega o elimina refugios.",
    "manageUsers" to "Gestionar usuarios",
    "medical" to "Médico",
    "medium" to "Mediano",
    "backToSearch" to "Volver a la búsqueda",
    "contactThisHome" to "Contactar esta casa temporal",
    "loginAsRescuerToContact" to "Inicia sesión como rescatista para enviar una solicitud a esta casa temporal.",
    "memberSince" to "Miembro desde",
    "microchipId" to "ID de microchip",
    "microchipped" to "Con microchip",
    "myTemporalHome" to "Mi casa temporal",
    "newEmail" to "Nuevo correo",
    "noTemporalHomes" to "No se encontraron casas temporales.",
    "requestSentToHome" to "¡Tu solicitud ha sido enviada!",
    "sendRequestBtn" to "Enviar solicitud",
    "temporalHomeNotFound" to "No se pudo encontrar esta casa temporal.",
    "viewDetails" to "Ver detalles",
    "rescuerDirectory" to "Rescatistas",
    "noRescuersFound" to "No se encontraron rescatistas.",
    "availablePetsCount" to "Mascotas disponibles",
    "availablePets" to "Mascotas disponibles",
    "noAvailablePetsForRescuer" to "Este rescatista no tiene mascotas disponibles en este momento.",
    "rescuerNotFound" to "No se pudo encontrar a este rescatista.",
    "errorLoadingResults" to "Error al cargar los resultados.",
    "volunteerForThisRescuer" to "Ser voluntario para este rescatista",
    "volunteerExplanation" to "Los voluntarios aprobados pueden sugerir ediciones a las publicaciones de mascotas de este rescatista.",
    "applyToVolunteerBtn" to "Postularme como voluntario",
    "loginToVolunteer" to "Inicia sesión para postularte como voluntario de este rescatista.",
    "volunteerApplicationSent" to "¡Tu solicitud de voluntariado ha sido enviada!",
    "myVolunteering" to "Mi Voluntariado",
    "noVolunteerApplicationsYet" to "Aún no te has postulado como voluntario con ningún rescatista.",
    "howToVolunteerHint" to "Para postularte, abre la página de un rescatista desde el directorio y pulsa \"Postularme como voluntario\".",
    "volunteerStatusPending" to "Pendiente",
    "volunteerStatusActive" to "Activo",
    "volunteerStatusRejected" to "Rechazado",
    "volunteerApplications" to "Solicitudes de Voluntariado",
    "noVolunteerApplications" to "No hay solicitudes de voluntariado.",
    "petEditSuggestions" to "Sugerencias de Edición",
    "noPetEditSuggestions" to "No hay sugerencias de edición pendientes.",
    "suggestedByLabel" to "sugerido por",
    "suggestAnEditTitle" to "Sugerir una Edición",
    "suggestAnEditExplanation" to "Como voluntario aprobado, puedes sugerir cambios a esta publicación. El rescatista revisará antes de que se publiquen.",
    "suggestEditBtn" to "Enviar Sugerencia",
    "editSuggestionSent" to "¡Tu sugerencia ha sido enviada al rescatista para su revisión!",
    "sponsorThisPetTitle" to "Apadrina a Esta Mascota",
    "sponsorExplanation" to "¿No puedes adoptar pero quieres ayudar con los gastos? Ofrece dinero o apoyo en especie y conecta directamente con el rescatista.",
    "sponsorThisRescuerTitle" to "Apadrina a Este Rescatista",
    "sponsorRescuerExplanation" to "Apoya los gastos generales de este rescatista con dinero o ayuda en especie.",
    "sponsorTypeLabel" to "¿Qué puedes ofrecer?",
    "sponsorTypeMoney" to "Dinero",
    "sponsorTypeInKind" to "En especie (alimento, insumos, etc.)",
    "amountLabel" to "Monto (mensual)",
    "currencyLabel" to "Moneda",
    "inKindDescriptionLabel" to "Describe lo que puedes ofrecer",
    "messageLabel" to "Mensaje",
    "sendOfferBtn" to "Enviar Oferta",
    "sponsorOfferSent" to "¡Tu oferta ha sido enviada! El rescatista se pondrá en contacto para coordinar los detalles.",
    "sponsorshipOffersReceived" to "Ofertas de Apadrinamiento",
    "noSponsorshipOffers" to "Aún no hay ofertas de apadrinamiento.",
    "generalFundLabel" to "Fondo general",
    "offeringLabel" to "Ofreciendo",
    "sponsorshipStatusSent" to "Nuevo",
    "sponsorshipStatusRead" to "Leído",
    "markAsReadBtn" to "Marcar como leído",
    "mySponsorshipOffers" to "Mis Ofertas de Apadrinamiento",
    "noSponsorshipOffersYet" to "Aún no has enviado ninguna oferta de apadrinamiento.",
    "howToSponsorHint" to "Para apadrinar, abre la página de un rescatista (apoyo general) o la de una mascota (gastos de esa mascota) y envía una oferta.",
    "browsePetsLink" to "Ver mascotas",
    "yourMessage" to "Tu mensaje",
    "newPassword" to "Nueva contraseña",
    "passkeyDesc" to "Agrega passkeys adicionales a tu cuenta para iniciar sesión fácilmente en todos tus dispositivos.",
    "passkeyName" to "Nombre de passkey",
    "pers" to "Personas",
    "petFoodDescription" to "Aprende qué alimentos son seguros, dañinos o tóxicos para tus mascotas",
    "petFoodGuide" to "Guía de alimentos para mascotas",
    "foodInformation" to "Información de alimentos",
    "petsForAdoption" to "Mascotas para adopción",
    "chooseCountryPrompt" to "Encuentra a tu nuevo mejor amigo",
    "chooseCountryPromptHint" to "Elige un país arriba para ver las mascotas disponibles para adopción cerca de ti.",
    "noPetsFound" to "No se encontraron mascotas",
    "noPetsFoundHint" to "Prueba otro filtro o vuelve pronto — añadimos mascotas nuevas con regularidad.",
    "loadingMorePets" to "Cargando más mascotas…",
    "photos" to "Fotos (máx 12)",
    "profilePhotographerRole" to "Fotógrafo - Ofrece servicios de fotografía",
    "profileRescuerRole" to "Rescatista - Publica mascotas para adopción",
    "profileTemporalHomeRole" to "Casa temporal - Provee casa temporal para mascotas",
    "provideShelter" to "Refugio - Gestiona un refugio de animales",
    "provideSterilization" to "Servicio de esterilización - Provee servicios de esterilización",
    "requestEmailChange" to "Solicitar cambio de correo",
    "rescueDate" to "Fecha de rescate",
    "rescueLocation" to "Lugar de rescate",
    "savePassword" to "Guardar contraseña",
    "selectEnergy" to "Selecciona energía",
    "selectSize" to "Selecciona tamaño",
    "sex" to "Sexo",
    "shelterSettings" to "Configuración de refugio",
    "size" to "Tamaño",
    "small" to "Pequeño",
    "specialNeeds" to "Necesidades especiales",
    "sterilizationSettings" to "Configuración de esterilización",
    "sterilized" to "Esterilizado",
    "temporalHomeSettings" to "Configuración de casa temporal",
    "temperament" to "Temperamento",
    "type" to "Tipo *",
    "urgentAdoption" to "Adopción urgente",
    "vaccinations" to "Vacunas",
    "vetDisclaimer" to "Consulta a tu veterinario antes de hacer cambios en la dieta.",
    "weight" to "Peso (kg)",
    "weightLabel" to "Peso",
    "website" to "Sitio web",
    "securitySettings" to "Configuración de seguridad",
    "emailVerifiedTitle" to "¡Correo verificado exitosamente!",
    "emailVerifiedMessage" to "Tu correo ha sido verificado. Tu registro está completo.",
    "redirectingIn" to "Redirigiendo a la página principal en ",
    "canAlsoLogin" to "También puedes ",
    "loginNow" to "iniciar sesión ahora",
    "emailVerificationFailed" to "Verificación fallida",
    "invalidOrExpiredToken" to "El enlace de verificación no es válido o ha expirado.",
    "tryRegisteringAgain" to "Por favor, intenta registrarte nuevamente.",
    "verificationEmailResent" to "El correo de verificación ha expirado. Se ha enviado un nuevo correo de verificación. Por favor revisa tu bandeja de entrada.",
    "emailVerificationSent" to "Se ha enviado un correo de verificación. Por favor revisa tu bandeja de entrada antes de iniciar sesión.",
    "emailNotVerifiedLogin" to "Por favor verifica tu correo antes de iniciar sesión. Revisa tu bandeja de entrada para el enlace de verificación.",
    "invalidOrExpiredMagicLink" to "Enlace de inicio de sesión inválido o expirado. Por favor solicita uno nuevo.",
    "invalidToken" to "Token inválido.",
    "accountBanned" to "Tu cuenta ha sido baneada.",
    "loginError" to "Ocurrió un error de inicio de sesión.",
    "profileUpdated" to "¡Perfil actualizado!",
    "displayNameRequired" to "El nombre para mostrar no puede estar vacío",
    "saving" to "Guardando...",
    "passwordSet" to "Contraseña establecida",
    "noPassword" to "Sin contraseña",
    "passwordChangeSuccess" to "¡Contraseña cambiada exitosamente!",
    "passwordChangeFailed" to "Error al cambiar contraseña",
    "passwordSetSuccess" to "¡Contraseña establecida exitosamente!",
    "passwordSetFailed" to "Error al establecer contraseña",
    "passwordsDoNotMatch" to "Las contraseñas no coinciden",
    "currentPasswordRequired" to "Se requiere contraseña actual",
    "emailChangeSent" to "¡Correo de verificación enviado a tu nueva dirección!",
    "emailChangeFailed" to "Error al solicitar cambio de correo",
    "emailRequired" to "Se requiere correo electrónico",
    "sendingEllipsis" to "Enviando...",
    "resetLinkSent" to "¡Enlace de restablecimiento enviado! Revisa tu correo.",
    "magicLinkSent" to "¡Enlace de inicio de sesión enviado! Revisa tu correo.",
    "magicLinkSendFailed" to "Error al enviar el enlace de inicio de sesión",
    "failedSendResetLink" to "Error al enviar el enlace de restablecimiento.",
    "invalidOrMissingToken" to "Token inválido o faltante.",
    "passwordTooShort" to "La contraseña debe tener al menos 8 caracteres.",
    "passwordNeedUppercase" to "La contraseña necesita al menos 1 letra mayúscula.",
    "passwordNeedLowercase" to "La contraseña necesita al menos 1 letra minúscula.",
    "passwordNeedNumber" to "La contraseña necesita al menos 1 número.",
    "passwordNeedSymbol" to "La contraseña necesita al menos 1 símbolo.",
    "resettingPassword" to "Restableciendo contraseña...",
    "passwordResetSuccess" to "¡Contraseña restablecida exitosamente! Ya puedes iniciar sesión.",
    "failedResetPassword" to "Error al restablecer la contraseña.",
    "loginSuccessRedirecting" to "¡Inicio de sesión exitoso! Redirigiendo...",
    "loginFailedExpiredLink" to "Error al iniciar sesión. El enlace puede ser inválido o haber expirado.",
    "loginFailed" to "Error al iniciar sesión.",
    "emailChangedSuccess" to "¡Correo electrónico cambiado exitosamente!",
    "failedChangeEmailExpired" to "Error al cambiar el correo. El enlace puede ser inválido o haber expirado.",
    "failedChangeEmail" to "Error al cambiar el correo electrónico.",
    "emailVerifiedSuccess" to "¡Correo electrónico verificado exitosamente!",
    "failedVerifyEmailExpired" to "Error al verificar el correo. El enlace puede ser inválido o haber expirado.",
    "failedVerifyEmail" to "Error al verificar el correo electrónico.",
    "dropImagesHint" to "Arrastra imágenes aquí o haz clic para explorar",
    "forgotPasswordTitle" to "Contraseña olvidada",
    "forgotPasswordIntro" to "Ingresa tu correo electrónico y te enviaremos un enlace para restablecer tu contraseña.",
    "sendResetLink" to "Enviar enlace de restablecimiento",
    "backToLogin" to "Volver al inicio de sesión",
    "resetPasswordTitle" to "Restablecer contraseña",
    "resetPasswordIntro" to "Ingresa tu nueva contraseña a continuación.",
    "emailLinkLoginTitle" to "Inicio de sesión por enlace",
    "verifying" to "Verificando...",
    "emailChangeTitle" to "Cambio de correo electrónico",
    "verifyContactEmailTitle" to "Verificar correo de contacto",
    "status" to "Estado",
    "yes" to "Sí",
    "no" to "No",
    "noDescription" to "Sin descripción.",
    "urgentBadge" to "URGENTE - ¡Necesita un hogar pronto!",
    "needsNewHome" to "Esta mascota necesita urgentemente un nuevo hogar",
    "promotedReasonLabel" to "Motivo",
    "selectReason" to "Selecciona un motivo",
    "promotedReasonMoving" to "El dueño se muda",
    "promotedReasonComplaints" to "Quejas / no puede tener más mascotas",
    "promotedReasonPetConflict" to "Conflicto con otra mascota",
    "promotedReasonOther" to "Otro",
    "promotedReasonDetailLabel" to "Detalles adicionales (opcional)",
    "promotedReasonRequired" to "Por favor selecciona un motivo",
    "needsNewHomeBadge" to "Necesita nuevo hogar",
    "housingType" to "Tipo de vivienda",
    "preferNotToSay" to "Prefiero no decirlo",
    "houseHousing" to "Casa",
    "apartmentHousing" to "Apartamento",
    "hasYard" to "Tiene patio",
    "hasOtherPets" to "Otras mascotas en casa",
    "adoptionExperience" to "Experiencia con adopciones",
    "firstTimeAdopter" to "Primera vez adoptando",
    "experiencedAdopter" to "Adoptante con experiencia",
    "noAdoptionRequests" to "No hay solicitudes de adopción",
    "noMessage" to "Sin mensaje",
    "markUnderReview" to "Marcar en revisión",
    "approve" to "Aprobar",
    "reject" to "Rechazar",
    "reviewNoteLabel" to "Nota privada de revisión",
    "saveNote" to "Guardar nota",
    "adoptionStatusPending" to "Pendiente",
    "adoptionStatusUnderReview" to "En revisión",
    "adoptionStatusApproved" to "Aprobada",
    "adoptionStatusRejected" to "No aprobada",
    "myAdoptionRequests" to "Mis solicitudes de adopción",
    "noAdoptionRequestsYet" to "Todavía no has solicitado adoptar ninguna mascota.",
    "medicalHistory" to "Historial médico",
    "medicalSchedule" to "Calendario de vacunas y desparasitación",
    "recordType" to "Tipo",
    "vaccination" to "Vacuna",
    "deworming" to "Desparasitación",
    "recordName" to "Nombre",
    "dateGiven" to "Fecha aplicada",
    "nextDueDate" to "Próxima dosis (opcional)",
    "notesOptional" to "Notas (opcional)",
    "addRecord" to "Agregar registro",
    "noMedicalRecords" to "Aún no hay registros de vacunas o desparasitación.",
    "nextDueLabel" to "Próxima dosis",
    "givenLabel" to "Aplicada",
    "overdue" to "Atrasada",
    "dueSoon" to "Próxima a vencer",
    "upToDate" to "Al día",
    "recordNameRequired" to "El nombre es obligatorio",
    "dateGivenRequired" to "La fecha aplicada es obligatoria",
    "failedToAddRecord" to "No se pudo agregar el registro",
    "confirmDeleteRecord" to "¿Eliminar este registro?",
    "maxCapacity" to "Máximo de mascotas a la vez (opcional)",
    "placePetHere" to "Colocar una mascota aquí",
    "noAvailablePetsToPlace" to "No tienes mascotas disponibles para colocar en este momento.",
    "selectPet" to "Selecciona una mascota",
    "startPlacementBtn" to "Iniciar acogida",
    "placementStarted" to "¡Acogida temporal iniciada!",
    "petsCurrentlyWithMe" to "Mascotas actualmente conmigo",
    "noPetsCurrentlyFostered" to "No hay mascotas actualmente contigo.",
    "sinceLabel" to "desde",
    "fosterPlacement" to "Acogida temporal",
    "endPlacement" to "Finalizar acogida",
    "currentlyWithLabel" to "Actualmente con",
    "notCurrentlyFostered" to "No está actualmente en acogida temporal.",
    "confirmEndPlacement" to "¿Finalizar esta acogida temporal? La mascota se registrará de vuelta contigo.",
    "aTemporalHome" to "un hogar temporal",
    "viewsLabel" to "Vistas",
    "inquiriesLabel" to "Solicitudes",
    "conversionLabel" to "Conversión",
    "messageOptional" to "Mensaje (opcional)",
    "requestAdoption" to "Solicitar adopción",
    "editPet" to "Editar mascota",
    "adoptionRequestSubmitted" to "¡Solicitud de adopción enviada!",
    "failedSubmitRequest" to "Error al enviar la solicitud",
    "noPhotosYet" to "Aún no hay fotos.",
    "rabbit" to "Conejo",
    "safeFoods" to "Alimentos seguros",
    "harmfulFoods" to "Alimentos dañinos",
    "cannotEatFoods" to "No puede comer",
    "legendSafe" to "Seguro",
    "legendHarmful" to "Dañino",
    "legendToxic" to "Tóxico",
    "food_dog_safe_chicken_name" to "Pollo",
    "food_dog_safe_chicken_detail" to "Cocido, sin huesos",
    "food_dog_safe_chicken_desc" to "Proteína magra, fácil de digerir",
    "food_dog_safe_rice_name" to "Arroz",
    "food_dog_safe_rice_detail" to "Blanco o integral",
    "food_dog_safe_rice_desc" to "Buena fuente de carbohidratos",
    "food_dog_safe_carrots_name" to "Zanahorias",
    "food_dog_safe_carrots_detail" to "Crudas o cocidas",
    "food_dog_safe_carrots_desc" to "Bajas en calorías, altas en fibra",
    "food_dog_safe_apples_name" to "Manzanas",
    "food_dog_safe_apples_detail" to "Sin semillas",
    "food_dog_safe_apples_desc" to "Ricas en vitaminas",
    "food_dog_safe_peanutbutter_name" to "Mantequilla de maní",
    "food_dog_safe_peanutbutter_detail" to "Sin sal, sin xilitol",
    "food_dog_safe_peanutbutter_desc" to "Buena fuente de proteína",
    "food_dog_harmful_grapes_name" to "Uvas",
    "food_dog_harmful_grapes_detail" to "Todas las variedades",
    "food_dog_harmful_grapes_desc" to "Puede causar insuficiencia renal",
    "food_dog_harmful_onions_name" to "Cebollas",
    "food_dog_harmful_onions_detail" to "Todas las formas",
    "food_dog_harmful_onions_desc" to "Daña los glóbulos rojos",
    "food_dog_harmful_chocolate_name" to "Chocolate",
    "food_dog_harmful_chocolate_detail" to "Todos los tipos",
    "food_dog_harmful_chocolate_desc" to "Tóxico para perros",
    "food_dog_harmful_garlic_name" to "Ajo",
    "food_dog_harmful_garlic_detail" to "Todas las formas",
    "food_dog_harmful_garlic_desc" to "Causa anemia",
    "food_dog_cannot_xylitol_name" to "Xilitol",
    "food_dog_cannot_xylitol_detail" to "Edulcorante artificial",
    "food_dog_cannot_xylitol_desc" to "Altamente tóxico",
    "food_dog_cannot_macadamia_name" to "Nueces de macadamia",
    "food_dog_cannot_macadamia_detail" to "Todas las formas",
    "food_dog_cannot_macadamia_desc" to "Causa debilidad",
    "food_dog_cannot_avocado_name" to "Aguacate",
    "food_dog_cannot_avocado_detail" to "Hueso, piel, todo",
    "food_dog_cannot_avocado_desc" to "Contiene persina",
    "food_dog_cannot_alcohol_name" to "Alcohol",
    "food_dog_cannot_alcohol_detail" to "Todas las formas",
    "food_dog_cannot_alcohol_desc" to "Altamente tóxico",
    "food_cat_safe_cookedfish_name" to "Pescado cocido",
    "food_cat_safe_cookedfish_detail" to "Sin espinas",
    "food_cat_safe_cookedfish_desc" to "Alto en proteína",
    "food_cat_safe_chicken_name" to "Pollo",
    "food_cat_safe_chicken_detail" to "Cocido, sin condimentos",
    "food_cat_safe_chicken_desc" to "Buena fuente de proteína",
    "food_cat_safe_pumpkin_name" to "Calabaza",
    "food_cat_safe_pumpkin_detail" to "Natural, cocida",
    "food_cat_safe_pumpkin_desc" to "Ayuda a la digestión",
    "food_cat_safe_eggs_name" to "Huevos",
    "food_cat_safe_eggs_detail" to "Cocidos",
    "food_cat_safe_eggs_desc" to "Proteína completa",
    "food_cat_harmful_raweggs_name" to "Huevos crudos",
    "food_cat_harmful_raweggs_detail" to "Con avidina",
    "food_cat_harmful_raweggs_desc" to "Interfiere con la biotina",
    "food_cat_harmful_rawfish_name" to "Pescado crudo",
    "food_cat_harmful_rawfish_detail" to "Contiene tiaminasa",
    "food_cat_harmful_rawfish_desc" to "Descompone las vitaminas B",
    "food_cat_harmful_dogfood_name" to "Comida para perros",
    "food_cat_harmful_dogfood_detail" to "Cualquiera",
    "food_cat_harmful_dogfood_desc" to "Carece de taurina",
    "food_cat_harmful_milk_name" to "Leche",
    "food_cat_harmful_milk_detail" to "La mayoría de los gatos",
    "food_cat_harmful_milk_desc" to "Intolerancia a la lactosa",
    "food_cat_cannot_chocolate_name" to "Chocolate",
    "food_cat_cannot_chocolate_detail" to "Todos los tipos",
    "food_cat_cannot_chocolate_desc" to "Teobromina tóxica",
    "food_cat_cannot_onionsgarlic_name" to "Cebolla/Ajo",
    "food_cat_cannot_onionsgarlic_detail" to "Todas las formas",
    "food_cat_cannot_onionsgarlic_desc" to "Daña los glóbulos rojos",
    "food_cat_cannot_grapesraisins_name" to "Uvas/Pasas",
    "food_cat_cannot_grapesraisins_detail" to "Todas las variedades",
    "food_cat_cannot_grapesraisins_desc" to "Daño renal",
    "food_cat_cannot_alcohol_name" to "Alcohol",
    "food_cat_cannot_alcohol_detail" to "Todas las formas",
    "food_cat_cannot_alcohol_desc" to "Muy tóxico",
    "food_bird_safe_seeds_name" to "Semillas",
    "food_bird_safe_seeds_detail" to "Mezcla variada",
    "food_bird_safe_seeds_desc" to "Buena fuente de grasa",
    "food_bird_safe_fruits_name" to "Frutas",
    "food_bird_safe_fruits_detail" to "La mayoría de variedades",
    "food_bird_safe_fruits_desc" to "Vitaminas y minerales",
    "food_bird_safe_vegetables_name" to "Verduras",
    "food_bird_safe_vegetables_detail" to "Hojas verdes",
    "food_bird_safe_vegetables_desc" to "Nutrientes esenciales",
    "food_bird_safe_pellets_name" to "Pellets",
    "food_bird_safe_pellets_detail" to "Formulación de calidad",
    "food_bird_safe_pellets_desc" to "Nutrición completa",
    "food_bird_harmful_avocado_name" to "Aguacate",
    "food_bird_harmful_avocado_detail" to "Todas las partes",
    "food_bird_harmful_avocado_desc" to "Persina tóxica",
    "food_bird_harmful_fruitpits_name" to "Huesos de fruta",
    "food_bird_harmful_fruitpits_detail" to "Manzana, durazno",
    "food_bird_harmful_fruitpits_desc" to "Trazas de cianuro",
    "food_bird_harmful_salt_name" to "Sal",
    "food_bird_harmful_salt_detail" to "Cualquier forma",
    "food_bird_harmful_salt_desc" to "Tóxica para las aves",
    "food_bird_harmful_caffeine_name" to "Cafeína",
    "food_bird_harmful_caffeine_detail" to "Café, té",
    "food_bird_harmful_caffeine_desc" to "Problemas cardíacos",
    "food_bird_cannot_chocolate_name" to "Chocolate",
    "food_bird_cannot_chocolate_detail" to "Todos los tipos",
    "food_bird_cannot_chocolate_desc" to "Teobromina tóxica",
    "food_bird_cannot_onions_name" to "Cebollas",
    "food_bird_cannot_onions_detail" to "Todas las formas",
    "food_bird_cannot_onions_desc" to "Daña los glóbulos rojos",
    "food_bird_cannot_garlic_name" to "Ajo",
    "food_bird_cannot_garlic_detail" to "Todas las formas",
    "food_bird_cannot_garlic_desc" to "Tóxico",
    "food_bird_cannot_mushrooms_name" to "Hongos",
    "food_bird_cannot_mushrooms_detail" to "Variedades silvestres",
    "food_bird_cannot_mushrooms_desc" to "Posibles toxinas",
    "food_fish_safe_flakes_name" to "Escamas",
    "food_fish_safe_flakes_detail" to "Marcas de calidad",
    "food_fish_safe_flakes_desc" to "Alimento completo para peces",
    "food_fish_safe_pellets_name" to "Pellets",
    "food_fish_safe_pellets_detail" to "Flotantes/hundibles",
    "food_fish_safe_pellets_desc" to "Nutrición equilibrada",
    "food_fish_safe_frozenfood_name" to "Comida congelada",
    "food_fish_safe_frozenfood_detail" to "Gusanos de sangre, artemia",
    "food_fish_safe_frozenfood_desc" to "Fuente de proteína",
    "food_fish_safe_vegetables_name" to "Verduras",
    "food_fish_safe_vegetables_detail" to "Calabacín escaldado",
    "food_fish_safe_vegetables_desc" to "Fuente de fibra",
    "food_fish_harmful_bread_name" to "Pan",
    "food_fish_harmful_bread_detail" to "Cualquier tipo",
    "food_fish_harmful_bread_desc" to "Sin nutrición, llena en exceso",
    "food_fish_harmful_humanfood_name" to "Comida humana",
    "food_fish_harmful_humanfood_detail" to "Platos cocinados",
    "food_fish_harmful_humanfood_desc" to "Nutrición inadecuada",
    "food_fish_harmful_livefeed_name" to "Alimento vivo",
    "food_fish_harmful_livefeed_detail" to "Insectos silvestres",
    "food_fish_harmful_livefeed_desc" to "Posibles parásitos",
    "food_fish_cannot_landinsect_name" to "Insecto terrestre",
    "food_fish_cannot_landinsect_detail" to "Bichos rastreros",
    "food_fish_cannot_landinsect_desc" to "No es alimento natural",
    "food_fish_cannot_mammalmeat_name" to "Carne de mamífero",
    "food_fish_cannot_mammalmeat_detail" to "Pollo, res",
    "food_fish_cannot_mammalmeat_desc" to "Inadecuada para la mayoría de peces",
    "food_fish_cannot_dairy_name" to "Lácteos",
    "food_fish_cannot_dairy_detail" to "Cualquiera",
    "food_fish_cannot_dairy_desc" to "No pueden digerirlos",
    "food_fish_cannot_breadcrumbs_name" to "Pan rallado",
    "food_fish_cannot_breadcrumbs_detail" to "Muchos contienen ajo",
    "food_fish_cannot_breadcrumbs_desc" to "Aditivos dañinos",
    "food_rabbit_safe_hay_name" to "Heno",
    "food_rabbit_safe_hay_detail" to "Pasto Timothy",
    "food_rabbit_safe_hay_desc" to "80% de la dieta",
    "food_rabbit_safe_leafygreens_name" to "Hojas verdes",
    "food_rabbit_safe_leafygreens_detail" to "Lechuga romana, cilantro",
    "food_rabbit_safe_leafygreens_desc" to "Vitaminas diarias",
    "food_rabbit_safe_pellets_name" to "Pellets",
    "food_rabbit_safe_pellets_detail" to "A base de Timothy",
    "food_rabbit_safe_pellets_desc" to "Nutrición equilibrada",
    "food_rabbit_safe_carrots_name" to "Zanahorias",
    "food_rabbit_safe_carrots_detail" to "Con moderación",
    "food_rabbit_safe_carrots_desc" to "Golosina",
    "food_rabbit_harmful_icebergletttuce_name" to "Lechuga iceberg",
    "food_rabbit_harmful_icebergletttuce_detail" to "Sin nutrición",
    "food_rabbit_harmful_icebergletttuce_desc" to "Puede causar estasis gastrointestinal",
    "food_rabbit_harmful_beans_name" to "Frijoles",
    "food_rabbit_harmful_beans_detail" to "Todas las legumbres",
    "food_rabbit_harmful_beans_desc" to "Gases, problemas digestivos",
    "food_rabbit_harmful_corn_name" to "Maíz",
    "food_rabbit_harmful_corn_detail" to "Cualquier forma",
    "food_rabbit_harmful_corn_desc" to "No puede digerirlo",
    "food_rabbit_harmful_potatoes_name" to "Papas",
    "food_rabbit_harmful_potatoes_detail" to "Cualquier forma",
    "food_rabbit_harmful_potatoes_desc" to "Nutrientes inadecuados",
    "food_rabbit_cannot_chocolate_name" to "Chocolate",
    "food_rabbit_cannot_chocolate_detail" to "Todos los tipos",
    "food_rabbit_cannot_chocolate_desc" to "Teobromina tóxica",
    "food_rabbit_cannot_onions_name" to "Cebollas",
    "food_rabbit_cannot_onions_detail" to "Todas las formas",
    "food_rabbit_cannot_onions_desc" to "Muy tóxicas",
    "food_rabbit_cannot_garlic_name" to "Ajo",
    "food_rabbit_cannot_garlic_detail" to "Todas las formas",
    "food_rabbit_cannot_garlic_desc" to "Tóxico",
    "food_rabbit_cannot_avocado_name" to "Aguacate",
    "food_rabbit_cannot_avocado_detail" to "Todas las partes",
    "food_rabbit_cannot_avocado_desc" to "Contiene persina",
    "primary" to "Principal",
    "passkeyRegisteredSuccess" to "¡Passkey registrada exitosamente!",
    "passkeyRegistrationFailed" to "Error al registrar passkey",
    "passkeyNameRequired" to "Se requiere nombre de passkey",
    "registeringPasskey" to "Registrando passkey...",
    "passkeyRegistered" to "Passkey registrada",
    "noPasskey" to "Sin passkey registrada",
    "noPets" to "No hay mascotas aún.",
    "photographerCountryStateRequired" to "Por favor completa país y estado para servicios de fotografía",
        "temporalHomeAliasCountryCityRequired" to "Por favor completa alias, país y ciudad para casa temporal",
        "shelterNameCountryCityAddressRequired" to "Por favor completa nombre, país, ciudad y dirección para refugio",
        "sterilizationNameCountryCityAddressRequired" to "Por favor completa nombre, país, ciudad y dirección para esterilización",
        "country.afghanistan" to "Afganistán",
        "country.albania" to "Albania",
        "country.algeria" to "Argelia",
        "country.argentina" to "Argentina",
        "country.armenia" to "Armenia",
        "country.australia" to "Australia",
        "country.austria" to "Austria",
        "country.azerbaijan" to "Azerbaiyán",
        "country.bangladesh" to "Bangladesh",
        "country.belarus" to "Bielorrusia",
        "country.belgium" to "Bélgica",
        "country.bolivia" to "Bolivia",
        "country.bosnia" to "Bosnia y Herzegovina",
        "country.brazil" to "Brasil",
        "country.bulgaria" to "Bulgaria",
        "country.cambodia" to "Camboya",
        "country.cameroon" to "Camerún",
        "country.canada" to "Canadá",
        "country.chile" to "Chile",
        "country.china" to "China",
        "country.colombia" to "Colombia",
        "country.costaRica" to "Costa Rica",
        "country.croatia" to "Croacia",
        "country.cuba" to "Cuba",
        "country.czechia" to "República Checa",
        "country.denmark" to "Dinamarca",
        "country.dominicanRepublic" to "República Dominicana",
        "country.ecuador" to "Ecuador",
        "country.egypt" to "Egipto",
        "country.elSalvador" to "El Salvador",
        "country.estonia" to "Estonia",
        "country.ethiopia" to "Etiopía",
        "country.finland" to "Finlandia",
        "country.france" to "Francia",
        "country.georgia" to "Georgia",
        "country.germany" to "Alemania",
        "country.ghana" to "Ghana",
        "country.greece" to "Grecia",
        "country.guatemala" to "Guatemala",
        "country.haiti" to "Haití",
        "country.honduras" to "Honduras",
        "country.hungary" to "Hungría",
        "country.iceland" to "Islandia",
        "country.india" to "India",
        "country.indonesia" to "Indonesia",
        "country.iran" to "Irán",
        "country.iraq" to "Irak",
        "country.ireland" to "Irlanda",
        "country.israel" to "Israel",
        "country.italy" to "Italia",
        "country.jamaica" to "Jamaica",
        "country.japan" to "Japón",
        "country.jordan" to "Jordania",
        "country.kazakhstan" to "Kazajstán",
        "country.kenya" to "Kenia",
        "country.kuwait" to "Kuwait",
        "country.latvia" to "Letonia",
        "country.lebanon" to "Líbano",
        "country.libya" to "Libia",
        "country.lithuania" to "Lituania",
        "country.luxembourg" to "Luxemburgo",
        "country.malaysia" to "Malasia",
        "country.mexico" to "México",
        "country.moldova" to "Moldavia",
        "country.mongolia" to "Mongolia",
        "country.montenegro" to "Montenegro",
        "country.morocco" to "Marruecos",
        "country.myanmar" to "Myanmar",
        "country.nepal" to "Nepal",
        "country.netherlands" to "Países Bajos",
        "country.newZealand" to "Nueva Zelanda",
        "country.nicaragua" to "Nicaragua",
        "country.nigeria" to "Nigeria",
        "country.northKorea" to "Corea del Norte",
        "country.norway" to "Noruega",
        "country.pakistan" to "Pakistán",
        "country.panama" to "Panamá",
        "country.paraguay" to "Paraguay",
        "country.peru" to "Perú",
        "country.philippines" to "Filipinas",
        "country.poland" to "Polonia",
        "country.portugal" to "Portugal",
        "country.puertoRico" to "Puerto Rico",
        "country.qatar" to "Catar",
        "country.romania" to "Rumania",
        "country.russia" to "Rusia",
        "country.saudiArabia" to "Arabia Saudita",
        "country.serbia" to "Serbia",
        "country.singapore" to "Singapur",
        "country.slovakia" to "Eslovaquia",
        "country.slovenia" to "Eslovenia",
        "country.southAfrica" to "Sudáfrica",
        "country.southKorea" to "Corea del Sur",
        "country.spain" to "España",
        "country.sriLanka" to "Sri Lanka",
        "country.sudan" to "Sudán",
        "country.sweden" to "Suecia",
        "country.switzerland" to "Suiza",
        "country.syria" to "Siria",
        "country.taiwan" to "Taiwán",
        "country.thailand" to "Tailandia",
        "country.tunisia" to "Túnez",
        "country.turkey" to "Turquía",
        "country.ukraine" to "Ucrania",
        "country.uae" to "Emiratos Árabes Unidos",
        "country.uk" to "Reino Unido",
        "country.usa" to "Estados Unidos",
        "country.uruguay" to "Uruguay",
        "country.uzbekistan" to "Uzbekistán",
        "country.venezuela" to "Venezuela",
        "country.vietnam" to "Vietnam",
        "country.yemen" to "Yemen",
        "loading" to "Cargando...",
        "countryRequired" to "Por favor selecciona un país para buscar",
        "noSheltersFound" to "Aún no se encontraron refugios. ¡Vuelve más tarde!",
        "visitWebsite" to "Visitar sitio web",
        "fiscalId" to "ID fiscal",
        "donationInformation" to "Información de donación",
        "bankName" to "Banco",
        "accountHolder" to "Titular de la cuenta",
        "accountNumber" to "Número de cuenta",
        "currency" to "Moneda",
        "errorLoadingShelters" to "Error al cargar los refugios",
        "pleaseSelectCountry" to "Por favor selecciona un país",
        "actions" to "Acciones",
        "addShelter" to "Agregar refugio",
        "addEditShelter" to "Agregar / Editar refugio",
        "allStates" to "Todos los estados",
        "confirmDelete" to "¿Seguro que quieres eliminar esta ubicación?",
        "confirmDeleteShelter" to "¿Seguro que quieres eliminar este refugio?",
        "confirmUnbanUser" to "¿Desbloquear a este usuario?",
        "confirmResetUserCredentials" to "Esto invalidará la contraseña y las passkeys actuales de {email} y le enviará un enlace de restablecimiento por correo electrónico. ¿Continuar?",
        "confirmDeactivateUser" to "¿Desactivar este usuario? Podrá reactivarse más tarde.",
        "confirmDeactivatePet" to "¿Desactivar esta mascota? Se ocultará de los listados públicos y podrá reactivarse más tarde.",
        "confirmBlockRescuer" to "¿Bloquear a este rescatista para que no te envíe más solicitudes?",
        "confirmDeletePet" to "¿Eliminar esta mascota?",
        "confirmDeleteStorage" to "¿Eliminar este almacenamiento?",
        "contact" to "Contacto",
        "delete" to "Eliminar",
        "edit" to "Editar",
        "errorDeletingShelter" to "Error al eliminar el refugio",
        "errorLoadingLocations" to "Error al cargar las ubicaciones",
        "errorLoadingShelter" to "Error al cargar el refugio",
        "errorSaving" to "Error al guardar",
        "errorSavingShelter" to "Error al guardar el refugio",
        "location" to "Ubicación",
        "noLocationsFound" to "No se encontraron ubicaciones",
        "selectCountry" to "Selecciona un país",
        "selectCountryToFilter" to "Por favor selecciona un país para filtrar",
        "shelterAdded" to "Refugio agregado con éxito",
        "shelterDeleted" to "Refugio eliminado con éxito",
        "shelterUpdated" to "Refugio actualizado con éxito",
        "updateShelter" to "Actualizar refugio",
        "errorLoadingPhotographers" to "Error al cargar los fotógrafos",
        "noPhotographersAvailable" to "No hay fotógrafos disponibles",
        "requestPhotoSession" to "Solicitar sesión de fotos",
        "selectPhotographer" to "Seleccionar",
        "photographersSelected" to "{count} seleccionados",
        "requestSelectedPhotographers" to "Solicitar fotógrafos seleccionados",
        "clearSelection" to "Borrar selección",
        "requestToMultiple" to "Solicitar a",
        "messageRequired" to "Por favor escribe un mensaje para los fotógrafos.",
        "requestsSentSuccessfully" to "Solicitudes enviadas correctamente",
        "message" to "Mensaje",
        "sendRequest" to "Enviar solicitud",
        "requestSentSuccessfully" to "¡Solicitud enviada con éxito!",
        "requestTo" to "Solicitar a",
        "enterMessage" to "Escribe un mensaje para {name}...",
        "reportUrgent" to "Urgente",
        "topRescuers" to "Mejores Rescatistas",
        "urgentRescuerSettings" to "Ajustes de Rescatista de Urgencias",
        "urgentRescueAlerts" to "Alertas de Rescate Urgente",
        "reportUrgentTitle" to "Reportar una mascota en peligro",
        "reportUrgentIntro" to "Si encontraste una mascota herida, maltratada, hambrienta o abandonada que necesita ayuda inmediata, cuéntanos dónde está y qué le pasa - avisaremos de inmediato a los rescatistas de urgencias cercanos.",
        "dangerType" to "¿Qué le pasa?",
        "dangerInjured" to "Herida",
        "dangerAbused" to "Siendo maltratada / abusada",
        "dangerStarving" to "Muriendo de hambre",
        "dangerTooYoung" to "Muy joven para sobrevivir sola",
        "dangerOther" to "Otra emergencia",
        "useMyLocation" to "Usar mi ubicación actual",
        "confirmMyLocation" to "Confirmar mi ubicación actual",
        "locationPermissionHint" to "Tu navegador pedirá permiso para compartir tu ubicación; solo se usa para avisarte de reportes cercanos.",
        "phoneOptional" to "Tu teléfono (opcional)",
        "submitReport" to "Enviar reporte",
        "urgentRescuerSettingsTitle" to "Ajustes de Rescatista de Urgencias",
        "urgentRescuerSettingsIntro" to "Recibe una alerta cuando se reporte una mascota en peligro cerca de tu área de cobertura. El primero en aceptar se hace cargo del caso.",
        "urgentRescuerActive" to "Estoy disponible para responder a reportes urgentes",
        "coverageAreaMode" to "Área de cobertura",
        "coverageArea" to "Área de cobertura",
        "coverageAreaHint" to "Confirma tu ubicación actual, arrastra el pin o escribe una dirección abajo: el mapa y los campos se mantienen sincronizados.",
        "useMyLocationAndRadius" to "Usar mi ubicación + un radio",
        "pickAZone" to "Elegir un país/estado/ciudad en su lugar",
        "radiusKm" to "Radio (km)",
        "urgentRescueAlertsTitle" to "Alertas de Rescate Urgente",
        "urgentRescuerLeaderboardTitle" to "Mejores Rescatistas de Urgencias (últimos 12 meses)",
        "locating" to "Ubicando...",
        "geolocationUnsupported" to "Tu navegador no soporta geolocalización",
        "locationCaptured" to "Ubicación capturada",
        "locationDenied" to "No pudimos obtener tu ubicación - permite el acceso a tu ubicación o ingresa una ciudad",
        "street" to "Calle",
        "exteriorNumber" to "Número exterior",
        "referenceNotes" to "Datos de referencia (ej. color de la casa, algo cercano que la identifique)",
        "concreteAddressHint" to "Agrega la calle y el número si puedes - esto es lo que permite a un rescatista pedir un taxi o abrir una app de mapas directo al lugar.",
        "descriptionRequired" to "Por favor describe la situación",
        "locationRequired" to "Por favor proporciona una ubicación (usa tu ubicación actual o ingresa un país y ciudad)",
        "submittingEllipsis" to "Enviando...",
        "reportSubmitted" to "Reporte enviado. Gracias - los rescatistas cercanos están siendo alertados ahora.",
        "reportSubmitFailed" to "Error al enviar el reporte",
        "phoneRequired" to "Por favor ingresa un número de teléfono",
        "savingEllipsis" to "Guardando...",
        "saved" to "Guardado",
        "saveFailed" to "Error al guardar",
        "noUrgentPages" to "No hay alertas urgentes pendientes en este momento",
        "accept" to "Aceptar",
        "alreadyAccepted" to "Otra persona ya aceptó este reporte",
        "noLeaderboardData" to "Aún no hay rescates aceptados en los últimos 12 meses",
        "rescueAcceptedTitle" to "Rescate Aceptado",
        "rescueAcceptedMessage" to "Gracias - ya estás en camino. Dirígete al lugar reportado tan pronto como puedas.",
        "rescueAcceptFailedTitle" to "No se pudo aceptar",
        "rescueAcceptFailedMessage" to "Este reporte ya fue aceptado por otro rescatista, o el enlace es inválido o expiró.",
        "share" to "Compartir",
        "shareViaFacebook" to "Compartir en Facebook",
        "shareViaX" to "Compartir en X",
        "shareViaWhatsapp" to "Compartir en WhatsApp",
        "shareViaEmail" to "Compartir por correo",
        "copyLink" to "Copiar enlace",
        "linkCopied" to "¡Enlace copiado!",
        "reportLostFoundTitle" to "Reportar una mascota perdida o encontrada",
        "reportLostFoundIntro" to "¿Perdiste a tu mascota, o encontraste una perdida? Presenta un reporte y lo compararemos con otros reportes cercanos.",
        "browseLostFound" to "Ver reportes de mascotas perdidas y encontradas",
        "kindLost" to "Perdí a mi mascota",
        "kindFound" to "Encontré una mascota",
        "petType" to "Tipo de mascota",
        "petTypeUnknown" to "No estoy seguro",
        "lostFoundBrowseTitle" to "Mascotas Perdidas y Encontradas",
        "showing" to "Mostrando",
        "kindLostPlural" to "Mascotas perdidas",
        "kindFoundPlural" to "Mascotas encontradas",
        "reportResolvedTitle" to "Reporte Resuelto",
        "reportResolvedMessage" to "Gracias por la actualización - este reporte ahora está cerrado.",
        "reportResolveFailedTitle" to "No se pudo resolver",
        "reportResolveFailedMessage" to "Este reporte ya fue resuelto, o el enlace es inválido o expiró.",
        "reportLostFound" to "Perdidos/\u200BEncontrados",
        "noLostFoundReportsFound" to "No se encontraron reportes de mascotas perdidas o encontradas",
        "errorLoadingReports" to "Error al cargar los reportes",
        "yourEmail" to "Tu correo electrónico",
        "contactReporter" to "Contactar a quien reportó",
        "contactFormIncomplete" to "Por favor ingresa tu correo y un mensaje",
        "contactSent" to "Mensaje enviado",
        "contactFailed" to "Error al enviar el mensaje",
        "lostFoundReportSubmitted" to "Reporte enviado. Gracias - lo compararemos con otros reportes cercanos.",
        "video" to "Video (opcional)",
        "removeVideo" to "Eliminar video",
        "saveThisSearch" to "Guardar esta búsqueda",
        "savedSearchSaved" to "¡Búsqueda guardada! Te avisaremos por correo cuando haya una nueva coincidencia.",
        "savedSearchFailed" to "Error al guardar la búsqueda",
        "favorite" to "Favorito",
        "addToFavorites" to "Añadir a Favoritos",
        "removeFromFavorites" to "Quitar de Favoritos",
        "myFavorites" to "Mis Favoritos",
        "noFavoritesYet" to "Aún no tienes favoritos",
        "mySavedSearches" to "Mis Búsquedas Guardadas",
        "noSavedSearchesYet" to "Aún no tienes búsquedas guardadas",
        "donationPromptText" to "Gracias por ayudar a una mascota hoy. Si puedes, considera hacer una pequeña donación para que Adopt-U siga siendo gratuito para todos.",
        "donationPromptCta" to "Donar",
        "dismiss" to "Cerrar",
    )

    private val frenchTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Connexion",
    "register" to "S'inscrire",
    "logout" to "Fermer la session",
    "profile" to "Profil",
    "myPets" to "Mes animaux",
    "addPet" to "Ajouter un animal",
    "save" to "Enregistrer",
    "cancel" to "Annuler",
    "search" to "Rechercher",
    "shelters" to "Refuges",
    "photographers" to "Photographes",
    "temporalHomes" to "Foyers temporaires",
    "findTemporalHomes" to "Trouver des foyers temporaires",
    "sterilizationLocations" to "Lieux de stérilisation",
    "donate" to "Faire un don",
    "countryLabel" to "Pays",
    "selectCountryToSearch" to "Sélectionnez un pays",
    "selectCountryFirst" to "Sélectionnez un pays pour activer ces filtres",
    "state" to "État",
    "city" to "Ville",
    "zipCode" to "Code postal",
    "neighborhood" to "Quartier",
    "name" to "Nom",
    "email" to "E-mail",
    "phone" to "Téléphone",
    "address" to "Adresse",
    "description" to "Description",
    "animalShelters" to "Refuges animaliers",
    "sheltersDescription" to "Trouvez des refuges animaliers où adopter un animal ou le confier après accord préalable avec le refuge",
    "photographerDescription" to "Photographes professionnels proposant des séances photos",
    "sterilizationLocationsDescription" to "Trouvez des lieux pour stériliser vos animaux.",
    "privacyPolicy" to "Politique de confidentialité",
    "privacyLastUpdated" to "Dernière mise à jour : avril 2025",
    "privacyHighlight" to "Chez Adopt-U, nous croyons en une collecte minimale de données. Nous ne collectons que votre adresse e-mail pour permettre la communication entre adoptants et sauveteurs. Nous n'utilisons pas vos informations à des fins publicitaires et ne partagerons jamais vos données avec des tiers à des fins marketing.",
    "privacyInfoCollectedH2" to "Informations que nous collectons",
    "privacyAccountRegH3" to "Inscription du compte",
    "privacyAccountRegIntro" to "Pour créer un compte sur Adopt-U, nous collectons les informations suivantes :",
    "privacyEmailLabel" to "Adresse e-mail : ",
    "privacyEmailDesc" to "Nécessaire pour la vérification du compte, la récupération de mot de passe et la communication entre adoptants et sauveteurs",
    "privacyDisplayNameLabel" to "Nom d'affichage : ",
    "privacyDisplayNameDesc" to "Un nom que vous choisissez pour être identifié sur la plateforme",
    "privacyLangPrefLabel" to "Préférence linguistique : ",
    "privacyLangPrefDesc" to "Pour proposer la plateforme dans votre langue préférée",
    "privacyNoFullNameEtc" to "Nous ne collectons pas votre nom complet, numéro de téléphone, adresse physique, documents d'identité, informations de paiement ou toute autre donnée personnelle au-delà de ce qui est mentionné ci-dessus.",
    "privacyOptionalProfileH3" to "Informations de profil facultatives",
    "privacyOptionalProfileIntro" to "Selon votre rôle, vous pouvez éventuellement fournir :",
    "privacyAdopterLabel" to "En tant qu'Adoptant : ",
    "privacyAdopterDesc" to "Aucune information supplémentaire n'est requise ou collectée",
    "privacyRescuerLabel" to "En tant que Sauveteur : ",
    "privacyRescuerDesc" to "Informations de localisation des animaux que vous proposez à l'adoption",
    "privacyPhotographerLabel" to "En tant que Photographe : ",
    "privacyPhotographerDesc" to "Lieu de service et préférences de contact",
    "privacyTemporalHomeLabel" to "En tant que Foyer Temporaire : ",
    "privacyTemporalHomeDesc" to "Lieu où vous pouvez offrir un hébergement temporaire",
    "privacyLocationUsage" to "Toutes les informations de localisation sont utilisées uniquement pour mettre en relation les adoptants avec les sauveteurs et prestataires de services à proximité.",
    "privacyPetInfoH3" to "Informations sur l'animal",
    "privacyPetInfoIntro" to "Les sauveteurs peuvent publier des informations sur les animaux, notamment :",
    "privacyPetPhotos" to "Photos de l'animal",
    "privacyPetDescBreedAge" to "Description, race, âge et caractéristiques",
    "privacyPetHealth" to "État de santé et vaccination",
    "privacyPetLocation" to "Lieu où l'animal est disponible",
    "privacyPetInfoNotVerified" to "Ces informations sont fournies par les sauveteurs et ne sont pas vérifiées de manière indépendante par Adopt-U.",
    "privacyHowWeUseH2" to "Comment nous utilisons vos informations",
    "privacyPrimaryPurposeH3" to "Objectif principal : Faciliter les mises en relation pour l'adoption",
    "privacyEmailUsedFor" to "Votre adresse e-mail est utilisée exclusivement aux fins suivantes :",
    "privacyUseVerification" to "Vous envoyer un lien de vérification lors de la création d'un compte",
    "privacyUseRescuerContact" to "Permettre aux sauveteurs de vous contacter au sujet des animaux qui vous intéressent",
    "privacyUseContactRescuer" to "Vous permettre de contacter les sauveteurs au sujet des animaux qu'ils proposent",
    "privacyUseNotifications" to "Vous envoyer des notifications importantes concernant votre compte et la sécurité",
    "privacyOnceConnected" to "Une fois que vous êtes en contact avec un autre utilisateur via notre plateforme, toute communication ultérieure se déroule directement entre vous et cette personne. Nous ne surveillons pas, ne stockons pas et n'avons pas accès au contenu de vos communications.",
    "privacyNeverDoH3" to "Ce que nous ne ferons jamais",
    "privacyCommitIntro" to "Nous nous engageons solennellement à :",
    "privacyNoAdsLabel" to "Pas de publicité : ",
    "privacyNoAdsDesc" to "Nous n'afficherons jamais de publicités ciblées ou non ciblées basées sur votre activité",
    "privacyNoMarketingLabel" to "Pas de marketing : ",
    "privacyNoMarketingDesc" to "Nous ne partagerons jamais votre e-mail avec des tiers à des fins de marketing ou promotionnelles",
    "privacyNoSellingLabel" to "Pas de vente de données : ",
    "privacyNoSellingDesc" to "Nous ne vendrons, ne louerons ni ne transférerons jamais vos informations personnelles à un tiers",
    "privacyNoProfilingLabel" to "Pas de profilage : ",
    "privacyNoProfilingDesc" to "Nous n'utiliserons pas vos données pour créer des profils à d'autres fins que la gestion de base du compte",
    "privacyNoTrackingLabel" to "Pas de suivi : ",
    "privacyNoTrackingDesc" to "Nous n'utilisons pas de pixels de suivi, de cookies publicitaires ni de technologies de suivi similaires",
    "privacyDataStorageH2" to "Stockage et sécurité des données",
    "privacyDataStoredSecurely" to "Vos données sont stockées en toute sécurité grâce à un chiffrement conforme aux normes du secteur. Nous mettons en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos informations personnelles contre tout accès, modification, divulgation ou destruction non autorisés.",
    "privacyDataRetention" to "Nous conservons les informations de votre compte tant que celui-ci reste actif. Vous pouvez demander la suppression de votre compte et des données associées à tout moment en nous contactant.",
    "privacyYourRightsH2" to "Vos droits",
    "privacyRightsIntro" to "Vous disposez des droits suivants concernant vos données personnelles :",
    "privacyRightAccess" to "Accès : Vous pouvez demander une copie de toutes les données personnelles que nous détenons à votre sujet",
    "privacyRightCorrection" to "Rectification : Vous pouvez demander que nous corrigions toute information inexacte",
    "privacyRightDeletion" to "Suppression : Vous pouvez demander que nous supprimions votre compte et toutes les données associées",
    "privacyRightPortability" to "Portabilité : Vous pouvez demander que nous vous fournissions vos données dans un format couramment utilisé",
    "privacyExerciseRights" to "Pour exercer l'un de ces droits, veuillez nous contacter à adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Cookies",
    "privacyCookiesText" to "Adopt-U utilise le minimum de cookies nécessaires au fonctionnement de la plateforme, notamment la gestion des sessions et les préférences linguistiques. Nous n'utilisons pas de cookies publicitaires ou de suivi.",
    "privacyThirdPartyH2" to "Services tiers",
    "privacyThirdPartyIntro" to "Nous utilisons les services tiers suivants, qui peuvent traiter vos données :",
    "privacyAws" to "AWS (Amazon Web Services) : Pour le stockage des données et l'envoi d'e-mails. Leurs politiques de confidentialité s'appliquent au traitement de vos données.",
    "privacyWebauthn" to "WebAuthn/FIDO2 : Pour l'authentification sécurisée par passkey. Cela implique des opérations cryptographiques dans votre navigateur.",
    "privacyThirdPartySelected" to "Nous avons soigneusement sélectionné des prestataires de services qui partagent notre engagement envers la confidentialité des données.",
    "privacyChildrensH2" to "Confidentialité des mineurs",
    "privacyChildrensText" to "Adopt-U n'est pas destiné aux utilisateurs de moins de 18 ans. Nous ne collectons pas sciemment d'informations auprès de mineurs. Si vous pensez qu'un mineur a créé un compte, veuillez nous contacter pour le faire supprimer.",
    "privacyChangesH2" to "Modifications de cette politique",
    "privacyChangesText" to "Nous pouvons mettre à jour cette politique de confidentialité de temps à autre pour refléter des changements dans nos pratiques ou des exigences légales. Nous informerons les utilisateurs des changements importants par e-mail ou par un avis bien visible sur la plateforme.",
    "privacyContactH2" to "Nous contacter",
    "privacyContactText" to "Si vous avez des questions ou des préoccupations concernant cette politique de confidentialité, ou pour exercer vos droits sur vos données, veuillez nous contacter à adopt-u@adopt-u.org.",
    "termsConditions" to "Conditions générales",
    "installApp" to "Installer l'application",
    "termsAcceptanceH2" to "Acceptation des conditions",
    "termsAcceptanceText" to "En accédant à Adopt-U et en l'utilisant, vous acceptez d'être lié par les termes et dispositions de cet accord. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre plateforme.",
    "termsNatureH2" to "Nature de notre service",
    "termsNatureText1" to "Adopt-U est une plateforme de mise en relation qui facilite le contact entre sauveteurs d'animaux et adoptants potentiels. Nous ne sommes ni une animalerie, ni un courtier, ni un refuge, ni une agence d'adoption. Nous ne possédons, ne gardons ni ne gérons directement aucun animal répertorié sur notre plateforme.",
    "termsNatureText2" to "Notre rôle se limite strictement à fournir un canal de communication entre les parties. Nous ne participons pas aux négociations d'adoption, aux contrats ni au transfert physique des animaux.",
    "termsUserRespH2" to "Responsabilités de l'utilisateur",
    "termsForRescuersH3" to "Pour les Sauveteurs :",
    "termsRescuerResp1" to "Fournir des informations exactes et véridiques sur les animaux dont vous avez la charge",
    "termsRescuerResp2" to "S'assurer que les animaux sont en bonne santé, vaccinés et correctement examinés avant leur publication",
    "termsRescuerResp3" to "Effectuer une sélection responsable des adoptants potentiels",
    "termsRescuerResp4" to "Organiser un transfert sécurisé des animaux vers leur nouveau foyer",
    "termsRescuerResp5" to "Accepter la responsabilité de l'exactitude de toutes les informations fournies",
    "termsRescuerResp6" to "Se conformer à toutes les lois et réglementations locales relatives à l'adoption d'animaux",
    "termsRescuerResp7" to "Ne jamais utiliser la plateforme à des fins de vente commerciale d'animaux ou d'élevage",
    "termsForAdoptersH3" to "Pour les Adoptants :",
    "termsAdopterResp1" to "Fournir des informations exactes sur votre situation de logement et votre expérience avec les animaux",
    "termsAdopterResp2" to "Être prêt à démontrer votre capacité à offrir des soins appropriés",
    "termsAdopterResp3" to "Comprendre que l'annulation de l'adoption d'un animal lui cause un stress important",
    "termsAdopterResp4" to "Respecter le droit du sauveteur de refuser votre demande d'adoption",
    "termsPetListingsH2" to "Annonces et informations sur les animaux",
    "termsPetListingsText1" to "Adopt-U ne vérifie, n'approuve ni ne garantit l'exactitude des informations fournies dans les annonces d'animaux. Les informations, y compris mais sans s'y limiter la race, l'âge, l'état de santé, le tempérament et les registres de vaccination, sont fournies par les sauveteurs et n'ont pas été vérifiées de manière indépendante.",
    "termsPetListingsText2" to "Les photos et descriptions sont fournies par les sauveteurs. Bien que nous interdisions toute fausse déclaration délibérée, nous ne pouvons garantir que toutes les annonces représentent fidèlement l'état actuel de l'animal.",
    "termsPetListingsText3" to "Les utilisateurs sont encouragés à poser des questions, demander les dossiers vétérinaires et rencontrer les animaux avant de finaliser tout arrangement d'adoption.",
    "termsAdoptionProcessH2" to "Processus d'adoption",
    "termsAdoptionProcessIntro" to "Une fois qu'un sauveteur et un adoptant conviennent de procéder à une adoption :",
    "termsAdoptionStep1" to "Tous les arrangements, y compris la date de transfert, le lieu et les coûts associés, sont négociés directement entre les parties",
    "termsAdoptionStep2" to "Adopt-U ne facilite ni ne garantit aucune transaction de paiement",
    "termsAdoptionStep3" to "La plateforme ne fournit pas de contrats d'adoption ; tout arrangement contractuel relève de la seule responsabilité des parties concernées",
    "termsAdoptionStep4" to "Nous recommandons vivement de documenter tous les accords par écrit",
    "termsLiabilityH2" to "Limitation de responsabilité",
    "termsLiabilityIntro" to "Adopt-U ne pourra être tenu responsable de :",
    "termsLiability1" to "Tout litige, désaccord ou conflit entre sauveteurs et adoptants",
    "termsLiability2" to "L'exactitude des informations fournies dans les annonces d'animaux",
    "termsLiability3" to "L'état de santé, le comportement ou la condition de tout animal avant, pendant ou après l'adoption",
    "termsLiability4" to "Toute perte financière, tout dommage ou toute blessure résultant de l'utilisation de la plateforme",
    "termsLiability5" to "Le résultat de toute adoption, y compris mais sans s'y limiter les retours, problèmes de santé ou de compatibilité",
    "termsLiability6" to "Toute action entreprise par les utilisateurs en dehors de la plateforme après l'établissement du contact",
    "termsLiabilityAck" to "Les utilisateurs reconnaissent que l'adoption d'un animal est un engagement important et qu'ils assument l'entière responsabilité de leur décision d'adopter.",
    "termsProhibitedH2" to "Activités interdites",
    "termsProhibitedIntro" to "Les activités suivantes sont strictement interdites sur Adopt-U :",
    "termsProhibited1" to "Vente commerciale d'animaux, opérations d'élevage ou élevages intensifs",
    "termsProhibited2" to "Publier des annonces pour des animaux dont vous n'avez pas directement la charge",
    "termsProhibited3" to "Informations fausses ou trompeuses dans les annonces ou les profils",
    "termsProhibited4" to "Harcèlement, discrimination ou contact inapproprié avec d'autres utilisateurs",
    "termsProhibited5" to "Utiliser la plateforme pour collecter des informations personnelles à des fins sans rapport avec l'adoption d'animaux",
    "termsProhibited6" to "Toute activité illégale ou violation des lois sur le bien-être animal",
    "termsAccountTerminationH2" to "Résiliation de compte",
    "termsAccountTerminationText" to "Adopt-U se réserve le droit de suspendre ou de résilier les comptes qui violent ces conditions, se livrent à des activités interdites ou nuisent à la réputation de la plateforme. Nous pouvons également supprimer les annonces contenant du contenu faux, trompeur ou inapproprié.",
    "termsModificationsH2" to "Modifications des conditions",
    "termsModificationsText" to "Nous nous réservons le droit de modifier ces conditions à tout moment. La poursuite de l'utilisation de la plateforme après les modifications constitue une acceptation des conditions modifiées. Nous informerons les utilisateurs des changements importants par e-mail ou par des annonces sur la plateforme.",
    "termsContactText" to "Si vous avez des questions concernant ces conditions générales, veuillez nous contacter à adopt-u@adopt-u.org.",
    "language" to "Langue",
    "displayName" to "Nom d'affichage",
    "saveProfile" to "Enregistrer le profil",
    "accountSettings" to "Paramètres du compte",
    "yourRoles" to "Vos rôles:",
    "resources" to "Ressources",
    "loginWithPasskey" to "Connexion avec Passkey",
    "signInWithPasskey" to "Se connecter avec une Passkey",
    "resendVerificationEmail" to "Renvoyer l'e-mail de vérification",
    "usesFido" to "Utilise FIDO2 / WebAuthn pour une authentification sécurisée sans mot de passe.",
    "loginWithMagicLink" to "Connexion avec lien e-mail",
    "magicLinkDesc" to "Nous vous enverrons un lien de connexion à votre e-mail (valide pendant 5 minutes).",
    "sendMagicLink" to "Envoyer le lien de connexion",
    "loginWithPassword" to "Connexion avec mot de passe",
    "signIn" to "Se connecter",
    "forgotPassword" to "Mot de passe oublié?",
    "registerNewAccount" to "Créer un compte",
    "accountDetails" to "Détails du compte",
    "registrationMethod" to "Méthode d'inscription",
    "selectAdditionalRoles" to "Sélectionnez des rôles supplémentaires:",
    "adoptPet" to "Adopter un animal",
    "publishPets" to "Publier des animaux pour adoption",
    "offerPhotography" to "Offrir des services photographiques",
    "photographerSettings" to "Paramètres du photographe",
    "sessionFee" to "Frais de séance",
    "provideTemporaryHome" to "Fournir un foyer temporaire pour les animaux",
    "passkeyNoPassword" to "Les passkeys sont plus sécurisées que les mots de passe et fonctionnent sur tous vos appareils.",
    "registerWithPasskey" to "S'inscrire avec Passkey",
    "passkeySettings" to "Paramètres de passkey",
    "password" to "Mot de passe",
    "confirmPassword" to "Confirmer le mot de passe",
    "passwordRequirements" to "Le mot de passe doit comporter au moins 8 caractères et contenir des majuscules, des minuscules, un chiffre et un symbole",
    "securityInfo" to "Informations de sécurité",
    "passkeyMostSecure" to "Passkey : Le plus sécurisé, recommandé",
    "passkeyBrowserCompat" to "Passkey peut ne pas fonctionner sur tous les navigateurs",
    "passwordLeastSecure" to "Mot de passe : Moins sécurisé, non recommandé",
    "selectLoginMethods" to "Sélectionnez une ou plusieurs méthodes de connexion :",
    "registerPasskeyOption" to "Passkey (le plus sécurisé, fonctionne sur tous vos appareils)",
    "registerPasswordOption" to "Mot de passe (moins sécurisé, à utiliser uniquement comme solution de secours)",
    "howPasskeysWorkTitle" to "Comment fonctionne une passkey ?",
    "howPasskeysWorkIntro" to "Une passkey remplace votre mot de passe par la sécurité intégrée de votre appareil : empreinte digitale, reconnaissance faciale ou code de verrouillage.",
    "howPasskeysWorkSecurity" to "Elle est stockée uniquement sur votre appareil, ne peut pas être hameçonnée ni divulguée lors d'une fuite de données, et se synchronise automatiquement entre vos appareils.",
    "howPasskeysWorkBackup" to "Vous pouvez aussi ajouter un mot de passe, mais la passkey seule suffit pour vous connecter, sans mot de passe.",
    "gotIt" to "Compris",
    "alreadyHaveAccount" to "Vous avez déjà un compte?",
    "all" to "Tous",
    "filterDogs" to "Chiens",
    "filterCats" to "Chats",
    "filterBirds" to "Oiseaux",
    "filterFish" to "Poissons",
    "filterAllSexOption" to "Tous les sexes",
    "filterMaleOption" to "♂ Mâle",
    "filterFemaleOption" to "♀ Femelle",
    "age" to "Âge",
    "rescued" to "Sauvé le",
    "years" to "ans",
    "months" to "mois",
    "year" to "an",
    "month" to "mois",
    "loginToAdopt" to "Connectez-vous pour demander l'adoption",
    "requiredMarker" to "(requis)",
    "pageNotFoundTitle" to "Page introuvable",
    "pageNotFoundHint" to "Le lien est peut-être ancien ou mal saisi. Les animaux sont toujours là.",
    "browsePets" to "Voir les animaux",
    "requestsFromRescuers" to "Demandes des sauveteurs",
    "signingOut" to "Déconnexion...",
    "adopter" to "Adoptant",
    "country" to "Pays",
    "zip" to "Code postal",
    "viewPublic" to "Voir la page publique",
    "banUser" to "Bannir l'utilisateur",
    "banReason" to "Motif (facultatif)",
    "myPhotoRequests" to "Mes demandes de séance photo",
    "noPhotoRequestsYet" to "Vous n'avez pas encore demandé de séance photo.",
    "photoRequestsReceived" to "Demandes de séance reçues",
    "noPhotoRequestsReceived" to "Aucune demande de séance pour le moment.",
    "photoStatusPending" to "En attente",
    "photoStatusApproved" to "Approuvée",
    "photoStatusRejected" to "Refusée",
    "photoStatusCancelled" to "Annulée",
    "photoStatusCompleted" to "Terminée",
    "cancelRequest" to "Annuler la demande",
    "registerConsentPrefix" to "En créant un compte, vous acceptez nos",
    "registerConsentTerms" to "Conditions générales",
    "registerConsentAnd" to "et notre",
    "registerConsentPrivacy" to "Politique de confidentialité",
    "petStatusAvailable" to "Disponible",
    "petStatusAdopted" to "Adopté",
    "petStatusDisabled" to "Désactivé",
    "petStatusPending" to "En attente",
    "addEditLocation" to "Ajouter/Modifier le lieu",
    "addLocation" to "Ajouter un lieu",
    "manage" to "Gérer",
    "adminPanel" to "Panneau d'administration",
    "adminSterilizationLocations" to "Gérer les lieux de stérilisation",
    "adopterRequired" to "Adoptant (requis)",
    "adoptionFee" to "Frais d'adoption",
    "adoptionRequests" to "Demandes d'adoption",
    "ageMonths" to "Âge (mois)",
    "ageYears" to "Âge (années)",
    "alias" to "Alias",
    "bird" to "Oiseau",
    "breed" to "Race",
    "medicalEventsOverview" to "Aperçu des événements médicaux",
    "back" to "Retour",
    "cat" to "Chat",
    "color" to "Couleur",
    "compatibility" to "Compatibilité",
    "currentPassword" to "Mot de passe actuel",
    "dog" to "Chien",
    "emailChange" to "Changer d'e-mail",
    "emailChangeDesc" to "Mettez à jour votre adresse e-mail. Un lien de vérification sera envoyé à votre nouvel e-mail.",
    "energyLevel" to "Niveau d'énergie",
    "female" to "Femelle",
    "findTemporalHome" to "Trouver un foyer temporaire",
    "fish" to "Poisson",
    "goodWithCats" to "Bon avec les chats",
    "goodWithDogs" to "Bon avec les chiens",
    "goodWithKids" to "Bon avec les enfants",
    "high" to "Élevé",
    "houseTrained" to "Propre",
    "large" to "Grand",
    "low" to "Faible",
    "male" to "Mâle",
    "managePets" to "Gérer les animaux",
    "managePetsBtn" to "Ajouter ou modifier des animaux",
    "managePetsDescription" to "Gérez toutes les pages d'animaux. Ajoutez ou supprimez des animaux.",
    "manageShelters" to "Gérer les refuges",
    "manageSheltersBtn" to "Ajouter ou modifier des refuges",
    "manageSheltersDescription" to "Gérez toutes les pages de refuges. Ajoutez ou supprimez des refuges.",
    "manageUsers" to "Gérer les utilisateurs",
    "medical" to "Médical",
    "medium" to "Moyen",
    "backToSearch" to "Retour à la recherche",
    "contactThisHome" to "Contacter ce foyer temporaire",
    "loginAsRescuerToContact" to "Connectez-vous en tant que sauveteur pour envoyer une demande à ce foyer temporaire.",
    "memberSince" to "Membre depuis",
    "microchipId" to "ID de puce",
    "microchipped" to "Avec puce",
    "myTemporalHome" to "Mon foyer temporaire",
    "newEmail" to "Nouvel e-mail",
    "noTemporalHomes" to "Aucun foyer temporaire trouvé.",
    "requestSentToHome" to "Votre demande a été envoyée !",
    "sendRequestBtn" to "Envoyer la demande",
    "temporalHomeNotFound" to "Ce foyer temporaire est introuvable.",
    "viewDetails" to "Voir les détails",
    "rescuerDirectory" to "Sauveteurs",
    "noRescuersFound" to "Aucun sauveteur trouvé.",
    "availablePetsCount" to "Animaux disponibles",
    "availablePets" to "Animaux disponibles",
    "noAvailablePetsForRescuer" to "Ce sauveteur n'a actuellement aucun animal disponible.",
    "rescuerNotFound" to "Ce sauveteur est introuvable.",
    "errorLoadingResults" to "Erreur lors du chargement des résultats.",
    "volunteerForThisRescuer" to "Devenir bénévole pour ce sauveteur",
    "volunteerExplanation" to "Les bénévoles approuvés peuvent proposer des modifications aux annonces d'animaux de ce sauveteur.",
    "applyToVolunteerBtn" to "Postuler comme bénévole",
    "loginToVolunteer" to "Connectez-vous pour postuler comme bénévole pour ce sauveteur.",
    "volunteerApplicationSent" to "Votre candidature de bénévolat a été envoyée !",
    "myVolunteering" to "Mon bénévolat",
    "noVolunteerApplicationsYet" to "Vous n'avez encore postulé comme bénévole auprès d'aucun sauveteur.",
    "howToVolunteerHint" to "Pour postuler, ouvrez la page d'un sauveteur depuis l'annuaire et appuyez sur \"Postuler comme bénévole\".",
    "volunteerStatusPending" to "En attente",
    "volunteerStatusActive" to "Actif",
    "volunteerStatusRejected" to "Rejeté",
    "volunteerApplications" to "Candidatures de bénévolat",
    "noVolunteerApplications" to "Aucune candidature de bénévolat.",
    "petEditSuggestions" to "Suggestions de modification",
    "noPetEditSuggestions" to "Aucune suggestion de modification en attente.",
    "suggestedByLabel" to "suggéré par",
    "suggestAnEditTitle" to "Suggérer une modification",
    "suggestAnEditExplanation" to "En tant que bénévole approuvé, vous pouvez suggérer des modifications à cette annonce. Le sauveteur les examinera avant leur publication.",
    "suggestEditBtn" to "Envoyer la suggestion",
    "editSuggestionSent" to "Votre suggestion a été envoyée au sauveteur pour examen !",
    "sponsorThisPetTitle" to "Parrainer cet animal",
    "sponsorExplanation" to "Vous ne pouvez pas adopter mais voulez aider avec les frais ? Proposez de l'argent ou une aide en nature et connectez-vous directement avec le sauveteur.",
    "sponsorThisRescuerTitle" to "Parrainer ce sauveteur",
    "sponsorRescuerExplanation" to "Soutenez les frais généraux de ce sauveteur avec de l'argent ou une aide en nature.",
    "sponsorTypeLabel" to "Que pouvez-vous offrir ?",
    "sponsorTypeMoney" to "Argent",
    "sponsorTypeInKind" to "En nature (nourriture, fournitures, etc.)",
    "amountLabel" to "Montant (mensuel)",
    "currencyLabel" to "Devise",
    "inKindDescriptionLabel" to "Décrivez ce que vous pouvez offrir",
    "messageLabel" to "Message",
    "sendOfferBtn" to "Envoyer l'offre",
    "sponsorOfferSent" to "Votre offre a été envoyée ! Le sauveteur vous contactera pour organiser les détails.",
    "sponsorshipOffersReceived" to "Offres de parrainage",
    "noSponsorshipOffers" to "Aucune offre de parrainage pour le moment.",
    "generalFundLabel" to "Fonds général",
    "offeringLabel" to "Offre",
    "sponsorshipStatusSent" to "Nouveau",
    "sponsorshipStatusRead" to "Lu",
    "markAsReadBtn" to "Marquer comme lu",
    "mySponsorshipOffers" to "Mes offres de parrainage",
    "noSponsorshipOffersYet" to "Vous n'avez encore envoyé aucune offre de parrainage.",
    "howToSponsorHint" to "Pour parrainer, ouvrez la page d'un sauveteur (soutien général) ou celle d'un animal (frais de cet animal) et envoyez une offre.",
    "browsePetsLink" to "Voir les animaux",
    "yourMessage" to "Votre message",
    "newPassword" to "Nouveau mot de passe",
    "passkeyDesc" to "Ajoutez des passkeys supplémentaires à votre compte pour une connexion facile sur tous vos appareils.",
    "passkeyName" to "Nom de la passkey",
    "pers" to "Personnes",
    "petFoodDescription" to "Découvrez quels aliments sont sûrs, nocifs ou toxiques pour vos animaux",
    "petFoodGuide" to "Guide alimentaire pour animaux",
    "foodInformation" to "Informations alimentaires",
    "petsForAdoption" to "Animaux pour l'adoption",
    "chooseCountryPrompt" to "Trouvez votre nouveau meilleur ami",
    "chooseCountryPromptHint" to "Choisissez un pays ci-dessus pour voir les animaux disponibles à l'adoption près de chez vous.",
    "noPetsFound" to "Aucun animal trouvé",
    "noPetsFoundHint" to "Essayez un autre filtre ou revenez bientôt — de nouveaux animaux sont ajoutés régulièrement.",
    "loadingMorePets" to "Chargement d'autres animaux…",
    "photos" to "Photos (max 12)",
    "profilePhotographerRole" to "Photographe - Offrir des services photographiques",
    "profileRescuerRole" to "Rescuteur - Publier des animaux pour l'adoption",
    "profileTemporalHomeRole" to "Foyer temporaire - Fournir un foyer temporaire pour les animaux",
    "provideShelter" to "Refuge - Gérer un refuge pour animaux",
    "provideSterilization" to "Service de stérilisation - Fournir des services de stérilisation",
    "requestEmailChange" to "Demander un changement d'e-mail",
    "rescueDate" to "Date de sauvetage",
    "rescueLocation" to "Lieu de sauvetage",
    "savePassword" to "Enregistrer le mot de passe",
    "selectEnergy" to "Sélectionnez l'énergie",
    "selectSize" to "Sélectionnez la taille",
    "sex" to "Sexe",
    "shelterSettings" to "Paramètres du refuge",
    "size" to "Taille",
    "small" to "Petit",
    "specialNeeds" to "Besoins spéciaux",
    "sterilizationSettings" to "Paramètres de stérilisation",
    "sterilized" to "Stérilisé",
    "temporalHomeSettings" to "Paramètres du foyer temporaire",
    "temperament" to "Tempérament",
    "type" to "Type *",
    "urgentAdoption" to "Adoptions urgentes",
    "vaccinations" to "Vaccinations",
    "vetDisclaimer" to "Consultez votre vétérinaire avant de modifier l'alimentation.",
    "weight" to "Poids (kg)",
    "weightLabel" to "Poids",
    "website" to "Site web",
    "securitySettings" to "Paramètres de sécurité",
    "emailVerifiedTitle" to "E-mail vérifié avec succès!",
    "emailVerifiedMessage" to "Votre e-mail a été vérifié. Votre inscription est maintenant complète.",
    "redirectingIn" to "Redirection vers la page principale dans ",
    "canAlsoLogin" to "Vous pouvez aussi ",
    "loginNow" to "vous connecter maintenant",
    "emailVerificationFailed" to "Échec de la vérification",
    "invalidOrExpiredToken" to "Le lien de vérification n'est pas valide ou a expiré.",
    "tryRegisteringAgain" to "Veuillez essayer de vous inscrire à nouveau.",
    "verificationEmailResent" to "Le e-mail de vérification a expiré. Un nouveau e-mail de vérification a été envoyé. Veuillez vérifier votre boîte de réception.",
    "emailVerificationSent" to "Un e-mail de vérification a été envoyé. Veuillez vérifier votre boîte de réception avant de vous connecter.",
    "emailNotVerifiedLogin" to "Veuillez vérifier votre e-mail avant de vous connecter. Consultez votre boîte de réception pour le lien de vérification.",
    "invalidOrExpiredMagicLink" to "Lien de connexion invalide ou expiré. Veuillez en demander un nouveau.",
    "invalidToken" to "Token invalide.",
    "accountBanned" to "Votre compte a été banni.",
    "loginError" to "Une erreur de connexion s'est produite.",
    "profileUpdated" to "Profil mis à jour !",
    "displayNameRequired" to "Le nom d'affichage ne peut pas être vide",
    "saving" to "Enregistrement...",
    "passwordSet" to "Mot de passe défini",
    "noPassword" to "Pas de mot de passe",
    "passwordChangeSuccess" to "Mot de passe changé avec succès !",
    "passwordChangeFailed" to "Échec du changement de mot de passe",
    "passwordSetSuccess" to "Mot de passe défini avec succès !",
    "passwordSetFailed" to "Échec de la définition du mot de passe",
    "passwordsDoNotMatch" to "Les mots de passe ne correspondent pas",
    "currentPasswordRequired" to "Le mot de passe actuel est requis",
    "emailChangeSent" to "E-mail de vérification envoyé à votre nouvelle adresse !",
    "emailChangeFailed" to "Échec de la demande de changement d'e-mail",
    "emailRequired" to "L'e-mail est requis",
    "sendingEllipsis" to "Envoi en cours...",
    "resetLinkSent" to "Lien de réinitialisation envoyé ! Vérifiez votre e-mail.",
    "magicLinkSent" to "Lien de connexion envoyé ! Vérifiez votre e-mail.",
    "magicLinkSendFailed" to "Échec de l'envoi du lien de connexion",
    "failedSendResetLink" to "Échec de l'envoi du lien de réinitialisation.",
    "invalidOrMissingToken" to "Jeton invalide ou manquant.",
    "passwordTooShort" to "Le mot de passe doit contenir au moins 8 caractères.",
    "passwordNeedUppercase" to "Le mot de passe doit contenir au moins 1 lettre majuscule.",
    "passwordNeedLowercase" to "Le mot de passe doit contenir au moins 1 lettre minuscule.",
    "passwordNeedNumber" to "Le mot de passe doit contenir au moins 1 chiffre.",
    "passwordNeedSymbol" to "Le mot de passe doit contenir au moins 1 symbole.",
    "resettingPassword" to "Réinitialisation du mot de passe...",
    "passwordResetSuccess" to "Mot de passe réinitialisé avec succès ! Vous pouvez maintenant vous connecter.",
    "failedResetPassword" to "Échec de la réinitialisation du mot de passe.",
    "loginSuccessRedirecting" to "Connexion réussie ! Redirection...",
    "loginFailedExpiredLink" to "Échec de la connexion. Le lien est peut-être invalide ou expiré.",
    "loginFailed" to "Échec de la connexion.",
    "emailChangedSuccess" to "E-mail modifié avec succès !",
    "failedChangeEmailExpired" to "Échec du changement d'e-mail. Le lien est peut-être invalide ou expiré.",
    "failedChangeEmail" to "Échec du changement d'e-mail.",
    "emailVerifiedSuccess" to "E-mail vérifié avec succès !",
    "failedVerifyEmailExpired" to "Échec de la vérification de l'e-mail. Le lien est peut-être invalide ou expiré.",
    "failedVerifyEmail" to "Échec de la vérification de l'e-mail.",
    "dropImagesHint" to "Déposez des images ici ou cliquez pour parcourir",
    "forgotPasswordTitle" to "Mot de passe oublié",
    "forgotPasswordIntro" to "Entrez votre e-mail et nous vous enverrons un lien pour réinitialiser votre mot de passe.",
    "sendResetLink" to "Envoyer le lien de réinitialisation",
    "backToLogin" to "Retour à la connexion",
    "resetPasswordTitle" to "Réinitialiser le mot de passe",
    "resetPasswordIntro" to "Entrez votre nouveau mot de passe ci-dessous.",
    "emailLinkLoginTitle" to "Connexion par lien e-mail",
    "verifying" to "Vérification...",
    "emailChangeTitle" to "Changement d'e-mail",
    "verifyContactEmailTitle" to "Vérifier l'e-mail de contact",
    "status" to "Statut",
    "yes" to "Oui",
    "no" to "Non",
    "noDescription" to "Pas de description.",
    "urgentBadge" to "URGENT - A besoin d'un foyer rapidement !",
    "needsNewHome" to "Cet animal a besoin d'urgence d'un nouveau foyer",
    "promotedReasonLabel" to "Raison",
    "selectReason" to "Sélectionnez une raison",
    "promotedReasonMoving" to "Le propriétaire déménage",
    "promotedReasonComplaints" to "Plaintes / ne peut plus garder d'animaux",
    "promotedReasonPetConflict" to "Conflit avec un autre animal",
    "promotedReasonOther" to "Autre",
    "promotedReasonDetailLabel" to "Détails supplémentaires (facultatif)",
    "promotedReasonRequired" to "Veuillez sélectionner une raison",
    "needsNewHomeBadge" to "Besoin d'un nouveau foyer",
    "housingType" to "Type de logement",
    "preferNotToSay" to "Préfère ne pas dire",
    "houseHousing" to "Maison",
    "apartmentHousing" to "Appartement",
    "hasYard" to "A un jardin",
    "hasOtherPets" to "Autres animaux à la maison",
    "adoptionExperience" to "Expérience d'adoption",
    "firstTimeAdopter" to "Première adoption",
    "experiencedAdopter" to "Adoptant expérimenté",
    "noAdoptionRequests" to "Aucune demande d'adoption",
    "noMessage" to "Aucun message",
    "markUnderReview" to "Marquer en révision",
    "approve" to "Approuver",
    "reject" to "Refuser",
    "reviewNoteLabel" to "Note privée de révision",
    "saveNote" to "Enregistrer la note",
    "adoptionStatusPending" to "En attente",
    "adoptionStatusUnderReview" to "En révision",
    "adoptionStatusApproved" to "Approuvée",
    "adoptionStatusRejected" to "Non approuvée",
    "myAdoptionRequests" to "Mes demandes d'adoption",
    "noAdoptionRequestsYet" to "Vous n'avez encore demandé à adopter aucun animal.",
    "medicalHistory" to "Antécédents médicaux",
    "medicalSchedule" to "Calendrier de vaccination et de vermifugation",
    "recordType" to "Type",
    "vaccination" to "Vaccination",
    "deworming" to "Vermifugation",
    "recordName" to "Nom",
    "dateGiven" to "Date d'administration",
    "nextDueDate" to "Prochaine échéance (facultatif)",
    "notesOptional" to "Notes (facultatif)",
    "addRecord" to "Ajouter un enregistrement",
    "noMedicalRecords" to "Aucun enregistrement de vaccination ou de vermifugation pour le moment.",
    "nextDueLabel" to "Prochaine échéance",
    "givenLabel" to "Administré",
    "overdue" to "En retard",
    "dueSoon" to "Bientôt dû",
    "upToDate" to "À jour",
    "recordNameRequired" to "Le nom est requis",
    "dateGivenRequired" to "La date d'administration est requise",
    "failedToAddRecord" to "Échec de l'ajout de l'enregistrement",
    "confirmDeleteRecord" to "Supprimer cet enregistrement ?",
    "maxCapacity" to "Nombre max d'animaux à la fois (facultatif)",
    "placePetHere" to "Placer un animal ici",
    "noAvailablePetsToPlace" to "Vous n'avez aucun animal disponible à placer pour le moment.",
    "selectPet" to "Sélectionner un animal",
    "startPlacementBtn" to "Démarrer l'accueil",
    "placementStarted" to "Accueil temporaire démarré !",
    "petsCurrentlyWithMe" to "Animaux actuellement chez moi",
    "noPetsCurrentlyFostered" to "Aucun animal n'est actuellement placé chez vous.",
    "sinceLabel" to "depuis",
    "fosterPlacement" to "Accueil temporaire",
    "endPlacement" to "Terminer l'accueil",
    "currentlyWithLabel" to "Actuellement chez",
    "notCurrentlyFostered" to "Pas actuellement en accueil temporaire.",
    "confirmEndPlacement" to "Terminer cet accueil temporaire ? L'animal sera enregistré comme revenu chez vous.",
    "aTemporalHome" to "un foyer temporaire",
    "viewsLabel" to "Vues",
    "inquiriesLabel" to "Demandes",
    "conversionLabel" to "Conversion",
    "messageOptional" to "Message (facultatif)",
    "requestAdoption" to "Demander l'adoption",
    "editPet" to "Modifier l'animal",
    "adoptionRequestSubmitted" to "Demande d'adoption envoyée !",
    "failedSubmitRequest" to "Échec de l'envoi de la demande",
    "noPhotosYet" to "Pas encore de photos.",
    "rabbit" to "Lapin",
    "safeFoods" to "Aliments sûrs",
    "harmfulFoods" to "Aliments nocifs",
    "cannotEatFoods" to "À ne pas manger",
    "legendSafe" to "Sûr",
    "legendHarmful" to "Nocif",
    "legendToxic" to "Toxique",
    "food_dog_safe_chicken_name" to "Poulet",
    "food_dog_safe_chicken_detail" to "Cuit, sans os",
    "food_dog_safe_chicken_desc" to "Protéine maigre, facile à digérer",
    "food_dog_safe_rice_name" to "Riz",
    "food_dog_safe_rice_detail" to "Blanc ou complet",
    "food_dog_safe_rice_desc" to "Bonne source de glucides",
    "food_dog_safe_carrots_name" to "Carottes",
    "food_dog_safe_carrots_detail" to "Crues ou cuites",
    "food_dog_safe_carrots_desc" to "Faibles en calories, riches en fibres",
    "food_dog_safe_apples_name" to "Pommes",
    "food_dog_safe_apples_detail" to "Sans pépins",
    "food_dog_safe_apples_desc" to "Riches en vitamines",
    "food_dog_safe_peanutbutter_name" to "Beurre de cacahuète",
    "food_dog_safe_peanutbutter_detail" to "Non salé, sans xylitol",
    "food_dog_safe_peanutbutter_desc" to "Bonne source de protéines",
    "food_dog_harmful_grapes_name" to "Raisins",
    "food_dog_harmful_grapes_detail" to "Toutes variétés",
    "food_dog_harmful_grapes_desc" to "Peut provoquer une insuffisance rénale",
    "food_dog_harmful_onions_name" to "Oignons",
    "food_dog_harmful_onions_detail" to "Toutes formes",
    "food_dog_harmful_onions_desc" to "Endommage les globules rouges",
    "food_dog_harmful_chocolate_name" to "Chocolat",
    "food_dog_harmful_chocolate_detail" to "Tous types",
    "food_dog_harmful_chocolate_desc" to "Toxique pour les chiens",
    "food_dog_harmful_garlic_name" to "Ail",
    "food_dog_harmful_garlic_detail" to "Toutes formes",
    "food_dog_harmful_garlic_desc" to "Provoque de l'anémie",
    "food_dog_cannot_xylitol_name" to "Xylitol",
    "food_dog_cannot_xylitol_detail" to "Édulcorant artificiel",
    "food_dog_cannot_xylitol_desc" to "Hautement toxique",
    "food_dog_cannot_macadamia_name" to "Noix de macadamia",
    "food_dog_cannot_macadamia_detail" to "Toutes formes",
    "food_dog_cannot_macadamia_desc" to "Provoque une faiblesse",
    "food_dog_cannot_avocado_name" to "Avocat",
    "food_dog_cannot_avocado_detail" to "Noyau, peau, tout",
    "food_dog_cannot_avocado_desc" to "Contient de la persine",
    "food_dog_cannot_alcohol_name" to "Alcool",
    "food_dog_cannot_alcohol_detail" to "Toutes formes",
    "food_dog_cannot_alcohol_desc" to "Hautement toxique",
    "food_cat_safe_cookedfish_name" to "Poisson cuit",
    "food_cat_safe_cookedfish_detail" to "Sans arêtes",
    "food_cat_safe_cookedfish_desc" to "Riche en protéines",
    "food_cat_safe_chicken_name" to "Poulet",
    "food_cat_safe_chicken_detail" to "Cuit, nature",
    "food_cat_safe_chicken_desc" to "Bonne source de protéines",
    "food_cat_safe_pumpkin_name" to "Citrouille",
    "food_cat_safe_pumpkin_detail" to "Nature, cuite",
    "food_cat_safe_pumpkin_desc" to "Aide à la digestion",
    "food_cat_safe_eggs_name" to "Œufs",
    "food_cat_safe_eggs_detail" to "Cuits",
    "food_cat_safe_eggs_desc" to "Protéine complète",
    "food_cat_harmful_raweggs_name" to "Œufs crus",
    "food_cat_harmful_raweggs_detail" to "Avec avidine",
    "food_cat_harmful_raweggs_desc" to "Interfère avec la biotine",
    "food_cat_harmful_rawfish_name" to "Poisson cru",
    "food_cat_harmful_rawfish_detail" to "Contient de la thiaminase",
    "food_cat_harmful_rawfish_desc" to "Détruit les vitamines B",
    "food_cat_harmful_dogfood_name" to "Nourriture pour chiens",
    "food_cat_harmful_dogfood_detail" to "Toute",
    "food_cat_harmful_dogfood_desc" to "Manque de taurine",
    "food_cat_harmful_milk_name" to "Lait",
    "food_cat_harmful_milk_detail" to "La plupart des chats",
    "food_cat_harmful_milk_desc" to "Intolérance au lactose",
    "food_cat_cannot_chocolate_name" to "Chocolat",
    "food_cat_cannot_chocolate_detail" to "Tous types",
    "food_cat_cannot_chocolate_desc" to "Théobromine toxique",
    "food_cat_cannot_onionsgarlic_name" to "Oignon/Ail",
    "food_cat_cannot_onionsgarlic_detail" to "Toutes formes",
    "food_cat_cannot_onionsgarlic_desc" to "Endommage les globules rouges",
    "food_cat_cannot_grapesraisins_name" to "Raisins/Raisins secs",
    "food_cat_cannot_grapesraisins_detail" to "Toutes variétés",
    "food_cat_cannot_grapesraisins_desc" to "Atteinte rénale",
    "food_cat_cannot_alcohol_name" to "Alcool",
    "food_cat_cannot_alcohol_detail" to "Toutes formes",
    "food_cat_cannot_alcohol_desc" to "Très toxique",
    "food_bird_safe_seeds_name" to "Graines",
    "food_bird_safe_seeds_detail" to "Mélange varié",
    "food_bird_safe_seeds_desc" to "Bonne source de graisses",
    "food_bird_safe_fruits_name" to "Fruits",
    "food_bird_safe_fruits_detail" to "La plupart des variétés",
    "food_bird_safe_fruits_desc" to "Vitamines et minéraux",
    "food_bird_safe_vegetables_name" to "Légumes",
    "food_bird_safe_vegetables_detail" to "Légumes-feuilles",
    "food_bird_safe_vegetables_desc" to "Nutriments essentiels",
    "food_bird_safe_pellets_name" to "Granulés",
    "food_bird_safe_pellets_detail" to "Formule de qualité",
    "food_bird_safe_pellets_desc" to "Nutrition complète",
    "food_bird_harmful_avocado_name" to "Avocat",
    "food_bird_harmful_avocado_detail" to "Toutes les parties",
    "food_bird_harmful_avocado_desc" to "Persine toxique",
    "food_bird_harmful_fruitpits_name" to "Noyaux de fruits",
    "food_bird_harmful_fruitpits_detail" to "Pomme, pêche",
    "food_bird_harmful_fruitpits_desc" to "Traces de cyanure",
    "food_bird_harmful_salt_name" to "Sel",
    "food_bird_harmful_salt_detail" to "Toute forme",
    "food_bird_harmful_salt_desc" to "Toxique pour les oiseaux",
    "food_bird_harmful_caffeine_name" to "Caféine",
    "food_bird_harmful_caffeine_detail" to "Café, thé",
    "food_bird_harmful_caffeine_desc" to "Problèmes cardiaques",
    "food_bird_cannot_chocolate_name" to "Chocolat",
    "food_bird_cannot_chocolate_detail" to "Tous types",
    "food_bird_cannot_chocolate_desc" to "Théobromine toxique",
    "food_bird_cannot_onions_name" to "Oignons",
    "food_bird_cannot_onions_detail" to "Toutes formes",
    "food_bird_cannot_onions_desc" to "Endommage les globules rouges",
    "food_bird_cannot_garlic_name" to "Ail",
    "food_bird_cannot_garlic_detail" to "Toutes formes",
    "food_bird_cannot_garlic_desc" to "Toxique",
    "food_bird_cannot_mushrooms_name" to "Champignons",
    "food_bird_cannot_mushrooms_detail" to "Variétés sauvages",
    "food_bird_cannot_mushrooms_desc" to "Toxines potentielles",
    "food_fish_safe_flakes_name" to "Flocons",
    "food_fish_safe_flakes_detail" to "Marques de qualité",
    "food_fish_safe_flakes_desc" to "Nourriture complète pour poissons",
    "food_fish_safe_pellets_name" to "Granulés",
    "food_fish_safe_pellets_detail" to "Flottants/coulants",
    "food_fish_safe_pellets_desc" to "Nutrition équilibrée",
    "food_fish_safe_frozenfood_name" to "Nourriture congelée",
    "food_fish_safe_frozenfood_detail" to "Vers de vase, artémia",
    "food_fish_safe_frozenfood_desc" to "Source de protéines",
    "food_fish_safe_vegetables_name" to "Légumes",
    "food_fish_safe_vegetables_detail" to "Courgette blanchie",
    "food_fish_safe_vegetables_desc" to "Source de fibres",
    "food_fish_harmful_bread_name" to "Pain",
    "food_fish_harmful_bread_detail" to "Tout type",
    "food_fish_harmful_bread_desc" to "Aucune nutrition, remplit l'estomac",
    "food_fish_harmful_humanfood_name" to "Nourriture humaine",
    "food_fish_harmful_humanfood_detail" to "Plats cuisinés",
    "food_fish_harmful_humanfood_desc" to "Mauvaise nutrition",
    "food_fish_harmful_livefeed_name" to "Nourriture vivante",
    "food_fish_harmful_livefeed_detail" to "Insectes sauvages",
    "food_fish_harmful_livefeed_desc" to "Parasites potentiels",
    "food_fish_cannot_landinsect_name" to "Insecte terrestre",
    "food_fish_cannot_landinsect_detail" to "Insectes rampants",
    "food_fish_cannot_landinsect_desc" to "Pas une nourriture naturelle",
    "food_fish_cannot_mammalmeat_name" to "Viande de mammifère",
    "food_fish_cannot_mammalmeat_detail" to "Poulet, bœuf",
    "food_fish_cannot_mammalmeat_desc" to "Inadaptée à la plupart des poissons",
    "food_fish_cannot_dairy_name" to "Produits laitiers",
    "food_fish_cannot_dairy_detail" to "Tous",
    "food_fish_cannot_dairy_desc" to "Ne peuvent pas les digérer",
    "food_fish_cannot_breadcrumbs_name" to "Chapelure",
    "food_fish_cannot_breadcrumbs_detail" to "Beaucoup contiennent de l'ail",
    "food_fish_cannot_breadcrumbs_desc" to "Additifs nocifs",
    "food_rabbit_safe_hay_name" to "Foin",
    "food_rabbit_safe_hay_detail" to "Foin de Timothy",
    "food_rabbit_safe_hay_desc" to "80% de l'alimentation",
    "food_rabbit_safe_leafygreens_name" to "Légumes-feuilles",
    "food_rabbit_safe_leafygreens_detail" to "Romaine, coriandre",
    "food_rabbit_safe_leafygreens_desc" to "Vitamines quotidiennes",
    "food_rabbit_safe_pellets_name" to "Granulés",
    "food_rabbit_safe_pellets_detail" to "À base de Timothy",
    "food_rabbit_safe_pellets_desc" to "Nutrition équilibrée",
    "food_rabbit_safe_carrots_name" to "Carottes",
    "food_rabbit_safe_carrots_detail" to "Avec modération",
    "food_rabbit_safe_carrots_desc" to "Friandise",
    "food_rabbit_harmful_icebergletttuce_name" to "Laitue iceberg",
    "food_rabbit_harmful_icebergletttuce_detail" to "Aucune nutrition",
    "food_rabbit_harmful_icebergletttuce_desc" to "Peut provoquer une stase gastro-intestinale",
    "food_rabbit_harmful_beans_name" to "Haricots",
    "food_rabbit_harmful_beans_detail" to "Tous les légumineux",
    "food_rabbit_harmful_beans_desc" to "Gaz, troubles digestifs",
    "food_rabbit_harmful_corn_name" to "Maïs",
    "food_rabbit_harmful_corn_detail" to "Toute forme",
    "food_rabbit_harmful_corn_desc" to "Ne peut pas le digérer",
    "food_rabbit_harmful_potatoes_name" to "Pommes de terre",
    "food_rabbit_harmful_potatoes_detail" to "Toute forme",
    "food_rabbit_harmful_potatoes_desc" to "Nutriments inadaptés",
    "food_rabbit_cannot_chocolate_name" to "Chocolat",
    "food_rabbit_cannot_chocolate_detail" to "Tous types",
    "food_rabbit_cannot_chocolate_desc" to "Théobromine toxique",
    "food_rabbit_cannot_onions_name" to "Oignons",
    "food_rabbit_cannot_onions_detail" to "Toutes formes",
    "food_rabbit_cannot_onions_desc" to "Très toxiques",
    "food_rabbit_cannot_garlic_name" to "Ail",
    "food_rabbit_cannot_garlic_detail" to "Toutes formes",
    "food_rabbit_cannot_garlic_desc" to "Toxique",
    "food_rabbit_cannot_avocado_name" to "Avocat",
    "food_rabbit_cannot_avocado_detail" to "Toutes les parties",
    "food_rabbit_cannot_avocado_desc" to "Contient de la persine",
    "primary" to "Principale",
    "passkeyRegisteredSuccess" to "Passkey enregistrée avec succès !",
    "passkeyRegistrationFailed" to "Échec de l'enregistrement de la passkey",
    "passkeyNameRequired" to "Le nom de la passkey est requis",
    "registeringPasskey" to "Enregistrement de la passkey...",
    "passkeyRegistered" to "Passkey enregistrée",
    "noPasskey" to "Aucune passkey enregistrée",
    "noPets" to "Aucun animal pour le moment.",
    "photographerCountryStateRequired" to "Veuillez remplir le pays et l'état pour les services photographiques",
        "temporalHomeAliasCountryCityRequired" to "Veuillez remplir l'alias, le pays et la ville pour le foyer temporaire",
        "shelterNameCountryCityAddressRequired" to "Veuillez remplir le nom, le pays, la ville et l'adresse pour le refuge",
        "sterilizationNameCountryCityAddressRequired" to "Veuillez remplir le nom, le pays, la ville et l'adresse pour la stérilisation",
        "country.afghanistan" to "Afghanistan",
        "country.albania" to "Albanie",
        "country.algeria" to "Algérie",
        "country.argentina" to "Argentine",
        "country.armenia" to "Arménie",
        "country.australia" to "Australie",
        "country.austria" to "Autriche",
        "country.azerbaijan" to "Azerbaïdjan",
        "country.bangladesh" to "Bangladesh",
        "country.belarus" to "Biélorussie",
        "country.belgium" to "Belgique",
        "country.bolivia" to "Bolivie",
        "country.bosnia" to "Bosnie-Herzégovine",
        "country.brazil" to "Brésil",
        "country.bulgaria" to "Bulgarie",
        "country.cambodia" to "Cambodge",
        "country.cameroon" to "Cameroun",
        "country.canada" to "Canada",
        "country.chile" to "Chili",
        "country.china" to "Chine",
        "country.colombia" to "Colombie",
        "country.costaRica" to "Costa Rica",
        "country.croatia" to "Croatie",
        "country.cuba" to "Cuba",
        "country.czechia" to "Tchéquie",
        "country.denmark" to "Danemark",
        "country.dominicanRepublic" to "République dominicaine",
        "country.ecuador" to "Équateur",
        "country.egypt" to "Égypte",
        "country.elSalvador" to "El Salvador",
        "country.estonia" to "Estonie",
        "country.ethiopia" to "Éthiopie",
        "country.finland" to "Finlande",
        "country.france" to "France",
        "country.georgia" to "Géorgie",
        "country.germany" to "Allemagne",
        "country.ghana" to "Ghana",
        "country.greece" to "Grèce",
        "country.guatemala" to "Guatemala",
        "country.haiti" to "Haïti",
        "country.honduras" to "Honduras",
        "country.hungary" to "Hongrie",
        "country.iceland" to "Islande",
        "country.india" to "Inde",
        "country.indonesia" to "Indonésie",
        "country.iran" to "Iran",
        "country.iraq" to "Irak",
        "country.ireland" to "Irlande",
        "country.israel" to "Israël",
        "country.italy" to "Italie",
        "country.jamaica" to "Jamaïque",
        "country.japan" to "Japon",
        "country.jordan" to "Jordanie",
        "country.kazakhstan" to "Kazakhstan",
        "country.kenya" to "Kenya",
        "country.kuwait" to "Koweït",
        "country.latvia" to "Lettonie",
        "country.lebanon" to "Liban",
        "country.libya" to "Libye",
        "country.lithuania" to "Lituanie",
        "country.luxembourg" to "Luxembourg",
        "country.malaysia" to "Malaisie",
        "country.mexico" to "Mexique",
        "country.moldova" to "Moldavie",
        "country.mongolia" to "Mongolie",
        "country.montenegro" to "Monténégro",
        "country.morocco" to "Maroc",
        "country.myanmar" to "Myanmar",
        "country.nepal" to "Népal",
        "country.netherlands" to "Pays-Bas",
        "country.newZealand" to "Nouvelle-Zélande",
        "country.nicaragua" to "Nicarauga",
        "country.nigeria" to "Nigéria",
        "country.northKorea" to "Corée du Nord",
        "country.norway" to "Norvège",
        "country.pakistan" to "Pakistan",
        "country.panama" to "Panama",
        "country.paraguay" to "Paraguay",
        "country.peru" to "Pérou",
        "country.philippines" to "Philippines",
        "country.poland" to "Pologne",
        "country.portugal" to "Portugal",
        "country.puertoRico" to "Porto Rico",
        "country.qatar" to "Qatar",
        "country.romania" to "Roumanie",
        "country.russia" to "Russie",
        "country.saudiArabia" to "Arabie saoudite",
        "country.serbia" to "Serbie",
        "country.singapore" to "Singapour",
        "country.slovakia" to "Slovaquie",
        "country.slovenia" to "Slovénie",
        "country.southAfrica" to "Afrique du Sud",
        "country.southKorea" to "Corée du Sud",
        "country.spain" to "Espagne",
        "country.sriLanka" to "Sri Lanka",
        "country.sudan" to "Soudan",
        "country.sweden" to "Suède",
        "country.switzerland" to "Suisse",
        "country.syria" to "Syrie",
        "country.taiwan" to "Taïwan",
        "country.thailand" to "Thaïlande",
        "country.tunisia" to "Tunisie",
        "country.turkey" to "Turquie",
        "country.ukraine" to "Ukraine",
        "country.uae" to "Émirats arabes unis",
        "country.uk" to "Royaume-Uni",
        "country.usa" to "États-Unis",
        "country.uruguay" to "Uruguay",
        "country.uzbekistan" to "Ouzbékistan",
        "country.venezuela" to "Venezuela",
        "country.vietnam" to "Vietnam",
        "country.yemen" to "Yémen",
        "loading" to "Chargement...",
        "countryRequired" to "Veuillez sélectionner un pays pour rechercher",
        "noSheltersFound" to "Aucun refuge trouvé. Revenez plus tard!",
        "visitWebsite" to "Visiter le site web",
        "fiscalId" to "Numéro fiscal",
        "donationInformation" to "Informations de don",
        "bankName" to "Banque",
        "accountHolder" to "Titulaire du compte",
        "accountNumber" to "Numéro de compte",
        "currency" to "Devise",
        "errorLoadingShelters" to "Erreur lors du chargement des refuges",
        "pleaseSelectCountry" to "Veuillez sélectionner un pays",
        "actions" to "Actions",
        "addShelter" to "Ajouter un refuge",
        "addEditShelter" to "Ajouter / Modifier un refuge",
        "allStates" to "Tous les états",
        "confirmDelete" to "Voulez-vous vraiment supprimer cet emplacement ?",
        "confirmDeleteShelter" to "Voulez-vous vraiment supprimer ce refuge ?",
        "confirmUnbanUser" to "Débloquer cet utilisateur ?",
        "confirmResetUserCredentials" to "Cela invalidera le mot de passe et les passkeys actuels de {email} et lui enverra un lien de réinitialisation par e-mail. Continuer ?",
        "confirmDeactivateUser" to "Désactiver cet utilisateur ? Il pourra être réactivé plus tard.",
        "confirmDeactivatePet" to "Désactiver cet animal ? Il sera masqué des annonces publiques et pourra être réactivé plus tard.",
        "confirmBlockRescuer" to "Bloquer ce sauveteur pour qu'il ne vous envoie plus de demandes ?",
        "confirmDeletePet" to "Supprimer cet animal ?",
        "confirmDeleteStorage" to "Supprimer ce stockage ?",
        "contact" to "Contact",
        "delete" to "Supprimer",
        "edit" to "Modifier",
        "errorDeletingShelter" to "Erreur lors de la suppression du refuge",
        "errorLoadingLocations" to "Erreur lors du chargement des emplacements",
        "errorLoadingShelter" to "Erreur lors du chargement du refuge",
        "errorSaving" to "Erreur lors de l'enregistrement",
        "errorSavingShelter" to "Erreur lors de l'enregistrement du refuge",
        "location" to "Emplacement",
        "noLocationsFound" to "Aucun emplacement trouvé",
        "selectCountry" to "Sélectionnez un pays",
        "selectCountryToFilter" to "Veuillez sélectionner un pays pour filtrer",
        "shelterAdded" to "Refuge ajouté avec succès",
        "shelterDeleted" to "Refuge supprimé avec succès",
        "shelterUpdated" to "Refuge mis à jour avec succès",
        "updateShelter" to "Mettre à jour le refuge",
        "errorLoadingPhotographers" to "Erreur lors du chargement des photographes",
        "noPhotographersAvailable" to "Aucun photographe disponible",
        "requestPhotoSession" to "Demander une séance photo",
        "selectPhotographer" to "Sélectionner",
        "photographersSelected" to "{count} sélectionné(s)",
        "requestSelectedPhotographers" to "Demander aux photographes sélectionnés",
        "clearSelection" to "Effacer la sélection",
        "requestToMultiple" to "Demander à",
        "messageRequired" to "Veuillez écrire un message pour les photographes.",
        "requestsSentSuccessfully" to "Demandes envoyées avec succès",
        "message" to "Message",
        "sendRequest" to "Envoyer la demande",
        "requestSentSuccessfully" to "Demande envoyée avec succès !",
        "requestTo" to "Demande à",
        "enterMessage" to "Écrivez un message pour {name}...",
        "reportUrgent" to "Urgence",
        "topRescuers" to "Meilleurs sauveteurs",
        "urgentRescuerSettings" to "Paramètres de sauveteur d'urgence",
        "urgentRescueAlerts" to "Alertes de sauvetage urgent",
        "reportUrgentTitle" to "Signaler un animal en danger",
        "reportUrgentIntro" to "Si vous avez trouvé un animal blessé, maltraité, affamé ou abandonné qui a besoin d'aide immédiate, dites-nous où il se trouve et ce qui ne va pas - nous alerterons immédiatement les sauveteurs d'urgence à proximité.",
        "dangerType" to "Quel est le problème ?",
        "dangerInjured" to "Blessé",
        "dangerAbused" to "Maltraité / victime d'abus",
        "dangerStarving" to "Affamé",
        "dangerTooYoung" to "Trop jeune pour survivre seul",
        "dangerOther" to "Autre urgence",
        "useMyLocation" to "Utiliser ma position actuelle",
        "confirmMyLocation" to "Confirmer ma position actuelle",
        "locationPermissionHint" to "Votre navigateur demandera l'autorisation de partager votre position ; elle sert uniquement à vous alerter des signalements proches.",
        "phoneOptional" to "Votre téléphone (facultatif)",
        "submitReport" to "Envoyer le signalement",
        "urgentRescuerSettingsTitle" to "Paramètres de sauveteur d'urgence",
        "urgentRescuerSettingsIntro" to "Soyez alerté lorsqu'un animal en danger est signalé près de votre zone de couverture. Le premier à accepter prend en charge le cas.",
        "urgentRescuerActive" to "Je suis disponible pour répondre aux signalements urgents",
        "coverageAreaMode" to "Zone de couverture",
        "coverageArea" to "Zone de couverture",
        "coverageAreaHint" to "Confirmez votre position actuelle, faites glisser le repère ou saisissez une adresse ci-dessous : la carte et les champs restent synchronisés.",
        "useMyLocationAndRadius" to "Utiliser ma position + un rayon",
        "pickAZone" to "Choisir un pays/état/ville à la place",
        "radiusKm" to "Rayon (km)",
        "urgentRescueAlertsTitle" to "Alertes de sauvetage urgent",
        "urgentRescuerLeaderboardTitle" to "Meilleurs sauveteurs d'urgence (12 derniers mois)",
        "locating" to "Localisation...",
        "geolocationUnsupported" to "La géolocalisation n'est pas prise en charge par votre navigateur",
        "locationCaptured" to "Position capturée",
        "locationDenied" to "Impossible d'obtenir votre position - veuillez autoriser l'accès à la position ou saisir une ville",
        "street" to "Rue",
        "exteriorNumber" to "Numéro",
        "referenceNotes" to "Repères (ex. couleur de la maison, point de repère proche)",
        "concreteAddressHint" to "Ajoutez une rue et un numéro si possible - c'est ce qui permet à un sauveteur de prendre un taxi ou d'ouvrir une appli de cartes directement vers l'endroit.",
        "descriptionRequired" to "Veuillez décrire la situation",
        "locationRequired" to "Veuillez fournir une position (utilisez votre position actuelle ou saisissez un pays et une ville)",
        "submittingEllipsis" to "Envoi...",
        "reportSubmitted" to "Signalement envoyé. Merci - les sauveteurs à proximité sont alertés dès maintenant.",
        "reportSubmitFailed" to "Échec de l'envoi du signalement",
        "phoneRequired" to "Veuillez saisir un numéro de téléphone",
        "savingEllipsis" to "Enregistrement...",
        "saved" to "Enregistré",
        "saveFailed" to "Échec de l'enregistrement",
        "noUrgentPages" to "Aucune alerte urgente en attente pour le moment",
        "accept" to "Accepter",
        "alreadyAccepted" to "Quelqu'un d'autre a déjà accepté ce signalement",
        "noLeaderboardData" to "Aucun sauvetage accepté au cours des 12 derniers mois pour le moment",
        "rescueAcceptedTitle" to "Sauvetage accepté",
        "rescueAcceptedMessage" to "Merci - vous êtes sur le coup. Rendez-vous sur les lieux signalés dès que possible.",
        "rescueAcceptFailedTitle" to "Impossible d'accepter",
        "rescueAcceptFailedMessage" to "Ce signalement a déjà été accepté par un autre sauveteur, ou le lien est invalide ou a expiré.",
        "share" to "Partager",
        "shareViaFacebook" to "Partager sur Facebook",
        "shareViaX" to "Partager sur X",
        "shareViaWhatsapp" to "Partager sur WhatsApp",
        "shareViaEmail" to "Partager par e-mail",
        "copyLink" to "Copier le lien",
        "linkCopied" to "Lien copié !",
        "reportLostFoundTitle" to "Signaler un animal perdu ou trouvé",
        "reportLostFoundIntro" to "Vous avez perdu votre animal, ou trouvé un animal errant ? Déposez un signalement et nous le comparerons aux autres signalements à proximité.",
        "browseLostFound" to "Parcourir les signalements d'animaux perdus et trouvés",
        "kindLost" to "J'ai perdu mon animal",
        "kindFound" to "J'ai trouvé un animal",
        "petType" to "Type d'animal",
        "petTypeUnknown" to "Je ne sais pas",
        "lostFoundBrowseTitle" to "Animaux Perdus et Trouvés",
        "showing" to "Affichage",
        "kindLostPlural" to "Animaux perdus",
        "kindFoundPlural" to "Animaux trouvés",
        "reportResolvedTitle" to "Signalement résolu",
        "reportResolvedMessage" to "Merci pour la mise à jour - ce signalement est maintenant clos.",
        "reportResolveFailedTitle" to "Impossible de résoudre",
        "reportResolveFailedMessage" to "Ce signalement a déjà été résolu, ou le lien est invalide ou a expiré.",
        "reportLostFound" to "Perdus/\u200BTrouvés",
        "noLostFoundReportsFound" to "Aucun signalement d'animal perdu ou trouvé",
        "errorLoadingReports" to "Erreur lors du chargement des signalements",
        "yourEmail" to "Votre e-mail",
        "contactReporter" to "Contacter la personne",
        "contactFormIncomplete" to "Veuillez saisir votre e-mail et un message",
        "contactSent" to "Message envoyé",
        "contactFailed" to "Échec de l'envoi du message",
        "lostFoundReportSubmitted" to "Signalement envoyé. Merci - nous le comparerons aux autres signalements à proximité.",
        "video" to "Vidéo (facultatif)",
        "removeVideo" to "Supprimer la vidéo",
        "saveThisSearch" to "Enregistrer cette recherche",
        "savedSearchSaved" to "Recherche enregistrée ! Nous vous enverrons un e-mail lorsqu'une nouvelle correspondance sera publiée.",
        "savedSearchFailed" to "Échec de l'enregistrement de la recherche",
        "favorite" to "Favori",
        "addToFavorites" to "Ajouter aux favoris",
        "removeFromFavorites" to "Retirer des favoris",
        "myFavorites" to "Mes Favoris",
        "noFavoritesYet" to "Aucun favori pour le moment",
        "mySavedSearches" to "Mes Recherches Enregistrées",
        "noSavedSearchesYet" to "Aucune recherche enregistrée pour le moment",
        "donationPromptText" to "Merci d'avoir aidé un animal aujourd'hui. Si vous le pouvez, envisagez un petit don pour qu'Adopt-U reste gratuit pour tous.",
        "donationPromptCta" to "Faire un don",
        "dismiss" to "Fermer",
)

private val portugueseTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Entrar",
    "register" to "Registrar",
    "logout" to "Encerrar sessão",
    "profile" to "Perfil",
    "myPets" to "Meus pets",
    "addPet" to "Adicionar pet",
    "save" to "Salvar",
    "cancel" to "Cancelar",
    "search" to "Pesquisar",
    "shelters" to "Abrigos",
    "photographers" to "Fotógrafos",
    "temporalHomes" to "Lares temporários",
    "findTemporalHomes" to "Encontrar lares temporários",
    "sterilizationLocations" to "Locais de esterilização",
    "donate" to "Doar",
    "countryLabel" to "País",
    "selectCountryToSearch" to "Selecione um país",
    "selectCountryFirst" to "Selecione um país para ativar estes filtros",
    "state" to "Estado",
    "city" to "Cidade",
    "zipCode" to "CEP",
    "neighborhood" to "Bairro",
    "name" to "Nome",
    "email" to "E-mail",
    "phone" to "Telefone",
    "address" to "Endereço",
    "description" to "Descrição",
    "animalShelters" to "Abrigos de animais",
    "sheltersDescription" to "Encontre abrigos de animais onde adotar um animal ou deixá-lo mediante acordo prévio com o abrigo",
    "photographerDescription" to "Fotógrafos profissionais oferecendo sessões de fotos",
    "sterilizationLocationsDescription" to "Encontre lugares para esterilizar seus animais.",
    "privacyPolicy" to "Política de privacidade",
    "privacyLastUpdated" to "Última atualização: abril de 2025",
    "privacyHighlight" to "Na Adopt-U, acreditamos na coleta mínima de dados. Coletamos apenas seu endereço de e-mail para permitir a comunicação entre adotantes e resgatistas. Não usamos suas informações para publicidade e nunca compartilharemos seus dados com terceiros para fins de marketing.",
    "privacyInfoCollectedH2" to "Informações que coletamos",
    "privacyAccountRegH3" to "Registro de conta",
    "privacyAccountRegIntro" to "Para criar uma conta no Adopt-U, coletamos as seguintes informações:",
    "privacyEmailLabel" to "Endereço de e-mail: ",
    "privacyEmailDesc" to "Necessário para verificação de conta, recuperação de senha e comunicação entre adotantes e resgatistas",
    "privacyDisplayNameLabel" to "Nome de exibição: ",
    "privacyDisplayNameDesc" to "Um nome que você escolhe para ser identificado na plataforma",
    "privacyLangPrefLabel" to "Preferência de idioma: ",
    "privacyLangPrefDesc" to "Para oferecer a plataforma no seu idioma preferido",
    "privacyNoFullNameEtc" to "Não coletamos seu nome completo, número de telefone, endereço físico, documentos de identificação, informações de pagamento ou quaisquer outros dados pessoais além dos listados acima.",
    "privacyOptionalProfileH3" to "Informações de perfil opcionais",
    "privacyOptionalProfileIntro" to "Dependendo da sua função, você pode fornecer opcionalmente:",
    "privacyAdopterLabel" to "Como Adotante: ",
    "privacyAdopterDesc" to "Nenhuma informação adicional é necessária ou coletada",
    "privacyRescuerLabel" to "Como Resgatista: ",
    "privacyRescuerDesc" to "Informações de localização dos pets que você tem disponíveis para adoção",
    "privacyPhotographerLabel" to "Como Fotógrafo: ",
    "privacyPhotographerDesc" to "Localização do serviço e preferências de contato",
    "privacyTemporalHomeLabel" to "Como Lar Temporário: ",
    "privacyTemporalHomeDesc" to "Local onde você pode oferecer moradia temporária",
    "privacyLocationUsage" to "Todas as informações de localização são usadas exclusivamente para conectar adotantes a resgatistas e prestadores de serviços próximos.",
    "privacyPetInfoH3" to "Informações do pet",
    "privacyPetInfoIntro" to "Os resgatistas podem publicar informações sobre os pets, incluindo:",
    "privacyPetPhotos" to "Fotos do pet",
    "privacyPetDescBreedAge" to "Descrição, raça, idade e características",
    "privacyPetHealth" to "Status de saúde e vacinação",
    "privacyPetLocation" to "Local onde o pet está disponível",
    "privacyPetInfoNotVerified" to "Essas informações são fornecidas pelos resgatistas e não são verificadas de forma independente pelo Adopt-U.",
    "privacyHowWeUseH2" to "Como usamos suas informações",
    "privacyPrimaryPurposeH3" to "Propósito principal: Facilitar conexões de adoção",
    "privacyEmailUsedFor" to "Seu endereço de e-mail é usado exclusivamente para os seguintes fins:",
    "privacyUseVerification" to "Enviar um link de verificação quando você cria uma conta",
    "privacyUseRescuerContact" to "Permitir que resgatistas entrem em contato sobre pets do seu interesse",
    "privacyUseContactRescuer" to "Permitir que você entre em contato com resgatistas sobre os pets anunciados",
    "privacyUseNotifications" to "Enviar notificações importantes sobre conta e segurança",
    "privacyOnceConnected" to "Depois que você se conecta com outro usuário através da nossa plataforma, toda comunicação adicional ocorre diretamente entre vocês. Não monitoramos, armazenamos nem temos acesso ao conteúdo de suas comunicações.",
    "privacyNeverDoH3" to "O que nunca faremos",
    "privacyCommitIntro" to "Comprometemo-nos solenemente com o seguinte:",
    "privacyNoAdsLabel" to "Sem publicidade: ",
    "privacyNoAdsDesc" to "Nunca exibiremos anúncios direcionados ou não direcionados com base na sua atividade",
    "privacyNoMarketingLabel" to "Sem marketing: ",
    "privacyNoMarketingDesc" to "Nunca compartilharemos seu e-mail com terceiros para fins de marketing ou promocionais",
    "privacyNoSellingLabel" to "Sem venda de dados: ",
    "privacyNoSellingDesc" to "Nunca venderemos, alugaremos ou transferiremos de outra forma suas informações pessoais a terceiros",
    "privacyNoProfilingLabel" to "Sem criação de perfis: ",
    "privacyNoProfilingDesc" to "Não usaremos seus dados para criar perfis para qualquer finalidade além do gerenciamento básico da conta",
    "privacyNoTrackingLabel" to "Sem rastreamento: ",
    "privacyNoTrackingDesc" to "Não usamos pixels de rastreamento, cookies de publicidade ou tecnologias de rastreamento semelhantes",
    "privacyDataStorageH2" to "Armazenamento e segurança de dados",
    "privacyDataStoredSecurely" to "Seus dados são armazenados com segurança usando criptografia padrão do setor. Empregamos medidas técnicas e organizacionais apropriadas para proteger suas informações pessoais contra acesso, alteração, divulgação ou destruição não autorizados.",
    "privacyDataRetention" to "Mantemos as informações da sua conta enquanto ela permanecer ativa. Você pode solicitar a exclusão da sua conta e dos dados associados a qualquer momento entrando em contato conosco.",
    "privacyYourRightsH2" to "Seus direitos",
    "privacyRightsIntro" to "Você tem os seguintes direitos em relação aos seus dados pessoais:",
    "privacyRightAccess" to "Acesso: Você pode solicitar uma cópia de todos os dados pessoais que temos sobre você",
    "privacyRightCorrection" to "Correção: Você pode solicitar que corrijamos qualquer informação imprecisa",
    "privacyRightDeletion" to "Exclusão: Você pode solicitar que excluamos sua conta e todos os dados associados",
    "privacyRightPortability" to "Portabilidade: Você pode solicitar que forneçamos seus dados em um formato de uso comum",
    "privacyExerciseRights" to "Para exercer qualquer um desses direitos, entre em contato conosco em adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Cookies",
    "privacyCookiesText" to "O Adopt-U usa cookies mínimos necessários para o funcionamento da plataforma, incluindo gerenciamento de sessão e preferências de idioma. Não usamos cookies de publicidade ou de rastreamento.",
    "privacyThirdPartyH2" to "Serviços de terceiros",
    "privacyThirdPartyIntro" to "Usamos os seguintes serviços de terceiros, que podem processar seus dados:",
    "privacyAws" to "AWS (Amazon Web Services): Para armazenamento de dados e envio de e-mails. Suas políticas de privacidade se aplicam ao processamento dos seus dados.",
    "privacyWebauthn" to "WebAuthn/FIDO2: Para autenticação segura por passkey. Isso envolve operações criptográficas no seu navegador.",
    "privacyThirdPartySelected" to "Selecionamos cuidadosamente provedores de serviços que compartilham nosso compromisso com a privacidade de dados.",
    "privacyChildrensH2" to "Privacidade infantil",
    "privacyChildrensText" to "O Adopt-U não é destinado a usuários menores de 18 anos. Não coletamos intencionalmente informações de menores. Se você acredita que um menor criou uma conta, entre em contato conosco para removê-la.",
    "privacyChangesH2" to "Alterações nesta política",
    "privacyChangesText" to "Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas ou requisitos legais. Notificaremos os usuários sobre alterações significativas por e-mail ou aviso destacado na plataforma.",
    "privacyContactH2" to "Fale conosco",
    "privacyContactText" to "Se você tiver dúvidas ou preocupações sobre esta Política de Privacidade, ou quiser exercer seus direitos sobre dados, entre em contato conosco em adopt-u@adopt-u.org.",
    "termsConditions" to "Termos e condições",
    "installApp" to "Instalar Aplicativo",
    "termsAcceptanceH2" to "Aceitação dos termos",
    "termsAcceptanceText" to "Ao acessar e usar o Adopt-U, você aceita e concorda em cumprir os termos e disposições deste acordo. Se você não concorda com estes termos, não utilize nossa plataforma.",
    "termsNatureH2" to "Natureza do nosso serviço",
    "termsNatureText1" to "O Adopt-U é uma plataforma de conexão que facilita o contato entre resgatistas de pets e potenciais adotantes. Não somos uma loja de animais, corretor, abrigo ou agência de adoção. Não possuímos, custodiamos ou lidamos diretamente com nenhum pet listado em nossa plataforma.",
    "termsNatureText2" to "Nosso papel é estritamente limitado a fornecer um canal de comunicação entre as partes. Não participamos de negociações de adoção, contratos ou da transferência física dos animais.",
    "termsUserRespH2" to "Responsabilidades do usuário",
    "termsForRescuersH3" to "Para Resgatistas:",
    "termsRescuerResp1" to "Fornecer informações precisas e verdadeiras sobre os pets sob seus cuidados",
    "termsRescuerResp2" to "Garantir que os pets estejam saudáveis, vacinados e devidamente avaliados antes de serem anunciados",
    "termsRescuerResp3" to "Realizar uma triagem responsável dos potenciais adotantes",
    "termsRescuerResp4" to "Organizar a transferência segura dos pets para seus novos lares",
    "termsRescuerResp5" to "Aceitar a responsabilidade pela precisão de todas as informações fornecidas",
    "termsRescuerResp6" to "Cumprir todas as leis e regulamentos locais referentes à adoção de pets",
    "termsRescuerResp7" to "Nunca usar a plataforma para venda comercial de pets ou fins de reprodução",
    "termsForAdoptersH3" to "Para Adotantes:",
    "termsAdopterResp1" to "Fornecer informações precisas sobre sua situação de moradia e experiência com pets",
    "termsAdopterResp2" to "Estar preparado para demonstrar sua capacidade de fornecer os cuidados adequados",
    "termsAdopterResp3" to "Entender que cancelar a adoção de um pet causa estresse significativo ao animal",
    "termsAdopterResp4" to "Respeitar o direito do resgatista de recusar sua solicitação de adoção",
    "termsPetListingsH2" to "Anúncios e informações de pets",
    "termsPetListingsText1" to "O Adopt-U não verifica, endossa ou garante a precisão das informações fornecidas nos anúncios de pets. As informações, incluindo mas não se limitando a raça, idade, estado de saúde, temperamento e registros de vacinação, são fornecidas pelos resgatistas e não foram verificadas de forma independente.",
    "termsPetListingsText2" to "As fotos e descrições são fornecidas pelos resgatistas. Embora proibamos deturpações deliberadas, não podemos garantir que todos os anúncios representem com precisão a condição atual do animal.",
    "termsPetListingsText3" to "Os usuários são incentivados a fazer perguntas, solicitar registros veterinários e conhecer os pets antes de finalizar qualquer acordo de adoção.",
    "termsAdoptionProcessH2" to "Processo de adoção",
    "termsAdoptionProcessIntro" to "Assim que um resgatista e um adotante concordam em prosseguir com uma adoção:",
    "termsAdoptionStep1" to "Todos os arranjos, incluindo data de transferência, local e quaisquer custos associados, são negociados diretamente entre as partes",
    "termsAdoptionStep2" to "O Adopt-U não facilita nem garante quaisquer transações de pagamento",
    "termsAdoptionStep3" to "A plataforma não fornece contratos de adoção; quaisquer acordos contratuais são de responsabilidade exclusiva das partes envolvidas",
    "termsAdoptionStep4" to "Recomendamos fortemente documentar todos os acordos por escrito",
    "termsLiabilityH2" to "Limitação de responsabilidade",
    "termsLiabilityIntro" to "O Adopt-U não será responsabilizado por:",
    "termsLiability1" to "Quaisquer disputas, desentendimentos ou conflitos entre resgatistas e adotantes",
    "termsLiability2" to "A precisão das informações fornecidas nos anúncios de pets",
    "termsLiability3" to "A saúde, o comportamento ou a condição de qualquer animal antes, durante ou após a adoção",
    "termsLiability4" to "Quaisquer perdas financeiras, danos ou lesões decorrentes do uso da plataforma",
    "termsLiability5" to "O resultado de qualquer adoção, incluindo mas não se limitando a devoluções, problemas de saúde ou de compatibilidade",
    "termsLiability6" to "Quaisquer ações realizadas pelos usuários fora da plataforma após o contato ter sido estabelecido",
    "termsLiabilityAck" to "Os usuários reconhecem que adotar um pet é um compromisso significativo e que assumem total responsabilidade por sua decisão de adotar.",
    "termsProhibitedH2" to "Atividades proibidas",
    "termsProhibitedIntro" to "As seguintes atividades são estritamente proibidas no Adopt-U:",
    "termsProhibited1" to "Venda comercial de pets, operações de reprodução ou criadouros comerciais",
    "termsProhibited2" to "Anunciar pets pelos quais você não cuida diretamente",
    "termsProhibited3" to "Informações falsas ou enganosas em anúncios ou perfis",
    "termsProhibited4" to "Assédio, discriminação ou contato inadequado com outros usuários",
    "termsProhibited5" to "Usar a plataforma para coletar informações pessoais para fins não relacionados à adoção de pets",
    "termsProhibited6" to "Quaisquer atividades ilegais ou violações das leis de bem-estar animal",
    "termsAccountTerminationH2" to "Encerramento de conta",
    "termsAccountTerminationText" to "O Adopt-U reserva-se o direito de suspender ou encerrar contas que violem estes termos, se envolvam em atividades proibidas ou prejudiquem a reputação da plataforma. Também podemos remover anúncios que contenham conteúdo falso, enganoso ou inadequado.",
    "termsModificationsH2" to "Modificações nos termos",
    "termsModificationsText" to "Reservamo-nos o direito de modificar estes termos a qualquer momento. O uso continuado da plataforma após as alterações constitui aceitação dos termos modificados. Notificaremos os usuários sobre mudanças significativas por e-mail ou anúncios na plataforma.",
    "termsContactText" to "Se você tiver dúvidas sobre estes Termos e Condições, entre em contato conosco em adopt-u@adopt-u.org.",
    "language" to "Idioma",
    "displayName" to "Nome de exibição",
    "saveProfile" to "Salvar perfil",
    "accountSettings" to "Configurações da conta",
    "yourRoles" to "Seus papéis:",
    "resources" to "Recursos",
    "loginWithPasskey" to "Entrar com Passkey",
    "signInWithPasskey" to "Entrar com Passkey",
    "resendVerificationEmail" to "Reenviar e-mail de verificação",
    "usesFido" to "Usa FIDO2 / WebAuthn para autenticação segura sem senha.",
    "loginWithMagicLink" to "Entrar com link de e-mail",
    "magicLinkDesc" to "Enviaremos um link de login para seu e-mail (válido por 5 minutos).",
    "sendMagicLink" to "Enviar link de login",
    "loginWithPassword" to "Entrar com senha",
    "signIn" to "Entrar",
    "forgotPassword" to "Esqueceu a senha?",
    "registerNewAccount" to "Criar conta",
    "accountDetails" to "Detalhes da conta",
    "registrationMethod" to "Método de registro",
    "selectAdditionalRoles" to "Selecione funções adicionais:",
    "adoptPet" to "Adotar um pet",
    "publishPets" to "Publicar pets para adoção",
    "offerPhotography" to "Oferecer serviços de fotografia",
    "photographerSettings" to "Configurações do fotógrafo",
    "sessionFee" to "Taxa por sessão",
    "provideTemporaryHome" to "Fornecer lar temporário para pets",
    "passkeyNoPassword" to "Passkeys são mais seguras que senhas e funcionam em todos os seus dispositivos.",
    "registerWithPasskey" to "Registrar com Passkey",
    "passkeySettings" to "Configurações de passkey",
    "password" to "Senha",
    "confirmPassword" to "Confirmar senha",
    "passwordRequirements" to "A senha deve ter pelo menos 8 caracteres e conter maiúsculas, minúsculas, número e símbolo",
    "securityInfo" to "Informações de segurança",
    "passkeyMostSecure" to "Passkey: Mais seguro, recomendado",
    "passkeyBrowserCompat" to "Passkey pode não funcionar em todos os navegadores",
    "passwordLeastSecure" to "Senha: Menos segura, não recomendada",
    "selectLoginMethods" to "Selecione um ou mais métodos de login:",
    "registerPasskeyOption" to "Passkey (mais seguro, funciona em todos os seus dispositivos)",
    "registerPasswordOption" to "Senha (menos seguro, use apenas como backup)",
    "howPasskeysWorkTitle" to "Como funciona uma passkey?",
    "howPasskeysWorkIntro" to "Uma passkey substitui sua senha pela segurança integrada do seu dispositivo: impressão digital, reconhecimento facial ou PIN de bloqueio de tela.",
    "howPasskeysWorkSecurity" to "Ela é armazenada apenas no seu dispositivo, não pode ser roubada por phishing nem vazada em um vazamento de dados, e sincroniza automaticamente entre seus dispositivos.",
    "howPasskeysWorkBackup" to "Você também pode adicionar uma senha, mas a passkey sozinha já é suficiente para entrar, sem precisar de senha.",
    "gotIt" to "Entendi",
    "alreadyHaveAccount" to "Já tem uma conta?",
    "all" to "Todos",
    "filterDogs" to "Cães",
    "filterCats" to "Gatos",
    "filterBirds" to "Aves",
    "filterFish" to "Peixes",
    "filterAllSexOption" to "Todos os sexos",
    "filterMaleOption" to "♂ Macho",
    "filterFemaleOption" to "♀ Fêmea",
    "age" to "Idade",
    "rescued" to "Resgatado",
    "years" to "anos",
    "months" to "meses",
    "year" to "ano",
    "month" to "mês",
    "loginToAdopt" to "Faça login para solicitar a adoção",
    "requiredMarker" to "(obrigatório)",
    "pageNotFoundTitle" to "Página não encontrada",
    "pageNotFoundHint" to "O link pode ser antigo ou estar errado. Os pets continuam aqui.",
    "browsePets" to "Ver pets",
    "requestsFromRescuers" to "Solicitações de resgatadores",
    "signingOut" to "Saindo...",
    "adopter" to "Adotante",
    "country" to "País",
    "zip" to "CEP",
    "viewPublic" to "Ver página pública",
    "banUser" to "Banir usuário",
    "banReason" to "Motivo (opcional)",
    "myPhotoRequests" to "Minhas solicitações de sessão de fotos",
    "noPhotoRequestsYet" to "Você ainda não solicitou uma sessão de fotos.",
    "photoRequestsReceived" to "Solicitações de sessão recebidas",
    "noPhotoRequestsReceived" to "Ainda não há solicitações de sessão.",
    "photoStatusPending" to "Pendente",
    "photoStatusApproved" to "Aprovada",
    "photoStatusRejected" to "Rejeitada",
    "photoStatusCancelled" to "Cancelada",
    "photoStatusCompleted" to "Concluída",
    "cancelRequest" to "Cancelar solicitação",
    "registerConsentPrefix" to "Ao criar uma conta você aceita nossos",
    "registerConsentTerms" to "Termos e Condições",
    "registerConsentAnd" to "e a",
    "registerConsentPrivacy" to "Política de Privacidade",
    "petStatusAvailable" to "Disponível",
    "petStatusAdopted" to "Adotado",
    "petStatusDisabled" to "Desativado",
    "petStatusPending" to "Pendente",
    "addEditLocation" to "Adicionar/Editar local",
    "addLocation" to "Adicionar local",
    "manage" to "Gerenciar",
    "adminPanel" to "Painel administrativo",
    "adminSterilizationLocations" to "Gerenciar locais de esterilização",
    "adopterRequired" to "Adotante (requerido)",
    "adoptionFee" to "Taxa de adoção",
    "adoptionRequests" to "Solicitações de adoção",
    "ageMonths" to "Idade (meses)",
    "ageYears" to "Idade (anos)",
    "alias" to "Apelido",
    "bird" to "Pássaro",
    "breed" to "Raça",
    "medicalEventsOverview" to "Resumo dos eventos médicos",
    "back" to "Voltar",
    "cat" to "Gato",
    "color" to "Cor",
    "compatibility" to "Compatibilidade",
    "currentPassword" to "Senha atual",
    "dog" to "Cão",
    "emailChange" to "Alterar e-mail",
    "emailChangeDesc" to "Atualize seu endereço de e-mail. Um link de verificação será enviado para seu novo e-mail.",
    "energyLevel" to "Nível de energia",
    "female" to "Fêmea",
    "findTemporalHome" to "Encontrar lar temporário",
    "fish" to "Peixe",
    "goodWithCats" to "Bom com gatos",
    "goodWithDogs" to "Bom com cães",
    "goodWithKids" to "Bom com crianças",
    "high" to "Alto",
    "houseTrained" to "Treinado",
    "large" to "Grande",
    "low" to "Baixo",
    "male" to "Macho",
    "managePets" to "Gerenciar pets",
    "managePetsBtn" to "Adicionar ou editar pets",
    "managePetsDescription" to "Gerencie todas as páginas de pets. Adicione ou remova pets.",
    "manageShelters" to "Gerenciar abrigos",
    "manageSheltersBtn" to "Adicionar ou editar abrigos",
    "manageSheltersDescription" to "Gerencie todas as páginas de abrigos. Adicione ou remova abrigos.",
    "manageUsers" to "Gerenciar usuários",
    "medical" to "Médico",
    "medium" to "Médio",
    "backToSearch" to "Voltar à busca",
    "contactThisHome" to "Contatar este lar temporário",
    "loginAsRescuerToContact" to "Faça login como resgatador para enviar uma solicitação a este lar temporário.",
    "memberSince" to "Membro desde",
    "microchipId" to "ID do microchip",
    "microchipped" to "Com microchip",
    "myTemporalHome" to "Meu lar temporário",
    "newEmail" to "Novo e-mail",
    "noTemporalHomes" to "Nenhum lar temporário encontrado.",
    "requestSentToHome" to "Sua solicitação foi enviada!",
    "sendRequestBtn" to "Enviar solicitação",
    "temporalHomeNotFound" to "Este lar temporário não foi encontrado.",
    "viewDetails" to "Ver detalhes",
    "rescuerDirectory" to "Resgatadores",
    "noRescuersFound" to "Nenhum resgatador encontrado.",
    "availablePetsCount" to "Pets disponíveis",
    "availablePets" to "Pets Disponíveis",
    "noAvailablePetsForRescuer" to "Este resgatador não tem pets disponíveis no momento.",
    "rescuerNotFound" to "Este resgatador não foi encontrado.",
    "errorLoadingResults" to "Erro ao carregar os resultados.",
    "volunteerForThisRescuer" to "Seja voluntário para este resgatador",
    "volunteerExplanation" to "Voluntários aprovados podem sugerir edições nos anúncios de pets deste resgatador.",
    "applyToVolunteerBtn" to "Candidatar-se como voluntário",
    "loginToVolunteer" to "Faça login para se candidatar como voluntário deste resgatador.",
    "volunteerApplicationSent" to "Sua candidatura de voluntariado foi enviada!",
    "myVolunteering" to "Meu Voluntariado",
    "noVolunteerApplicationsYet" to "Você ainda não se candidatou como voluntário para nenhum resgatador.",
    "howToVolunteerHint" to "Para se candidatar, abra a página de um resgatador no diretório e toque em \"Candidatar-se como voluntário\".",
    "volunteerStatusPending" to "Pendente",
    "volunteerStatusActive" to "Ativo",
    "volunteerStatusRejected" to "Rejeitado",
    "volunteerApplications" to "Candidaturas de Voluntariado",
    "noVolunteerApplications" to "Nenhuma candidatura de voluntariado.",
    "petEditSuggestions" to "Sugestões de Edição",
    "noPetEditSuggestions" to "Nenhuma sugestão de edição pendente.",
    "suggestedByLabel" to "sugerido por",
    "suggestAnEditTitle" to "Sugerir uma Edição",
    "suggestAnEditExplanation" to "Como voluntário aprovado, você pode sugerir alterações a este anúncio. O resgatador revisará antes de publicar.",
    "suggestEditBtn" to "Enviar Sugestão",
    "editSuggestionSent" to "Sua sugestão foi enviada ao resgatador para revisão!",
    "sponsorThisPetTitle" to "Apadrinhar Este Pet",
    "sponsorExplanation" to "Não pode adotar mas quer ajudar com as despesas? Ofereça dinheiro ou ajuda material e conecte-se diretamente com o resgatador.",
    "sponsorThisRescuerTitle" to "Apadrinhar Este Resgatador",
    "sponsorRescuerExplanation" to "Apoie os custos gerais deste resgatador com dinheiro ou ajuda material.",
    "sponsorTypeLabel" to "O que você pode oferecer?",
    "sponsorTypeMoney" to "Dinheiro",
    "sponsorTypeInKind" to "Material (ração, suprimentos, etc.)",
    "amountLabel" to "Valor (mensal)",
    "currencyLabel" to "Moeda",
    "inKindDescriptionLabel" to "Descreva o que você pode oferecer",
    "messageLabel" to "Mensagem",
    "sendOfferBtn" to "Enviar Oferta",
    "sponsorOfferSent" to "Sua oferta foi enviada! O resgatador entrará em contato para combinar os detalhes.",
    "sponsorshipOffersReceived" to "Ofertas de Apadrinamento",
    "noSponsorshipOffers" to "Nenhuma oferta de apadrinamento ainda.",
    "generalFundLabel" to "Fundo geral",
    "offeringLabel" to "Oferecendo",
    "sponsorshipStatusSent" to "Novo",
    "sponsorshipStatusRead" to "Lido",
    "markAsReadBtn" to "Marcar como lido",
    "mySponsorshipOffers" to "Minhas Ofertas de Apadrinamento",
    "noSponsorshipOffersYet" to "Você ainda não enviou nenhuma oferta de apadrinamento.",
    "howToSponsorHint" to "Para apadrinhar, abra a página de um resgatador (apoio geral) ou a de um pet (custos desse pet) e envie uma oferta.",
    "browsePetsLink" to "Ver pets",
    "yourMessage" to "Sua mensagem",
    "newPassword" to "Nova senha",
    "passkeyDesc" to "Adicione passkeys adicionais à sua conta para facilitar o login em todos os dispositivos.",
    "passkeyName" to "Nome da passkey",
    "pers" to "Pessoas",
    "petFoodDescription" to "Saiba quais alimentos são seguros, nocivos ou tóxicos para seus pets",
    "petFoodGuide" to "Guia de alimentos para pets",
    "foodInformation" to "Informações alimentares",
    "petsForAdoption" to "Pets para adoção",
    "chooseCountryPrompt" to "Encontre seu novo melhor amigo",
    "chooseCountryPromptHint" to "Escolha um país acima para ver os pets disponíveis para adoção perto de você.",
    "noPetsFound" to "Nenhum pet encontrado",
    "noPetsFoundHint" to "Tente outro filtro ou volte em breve — novos pets são adicionados regularmente.",
    "loadingMorePets" to "Carregando mais pets…",
    "photos" to "Fotos (máx 12)",
    "profilePhotographerRole" to "Fotógrafo - Ofereça serviços de fotografia",
    "profileRescuerRole" to "Resgatista - Publica pets para adoção",
    "profileTemporalHomeRole" to "Lar temporário - Forneça lar temporário para pets",
    "provideShelter" to "Abrigo - Gerencie um abrigo de animais",
    "provideSterilization" to "Serviço de esterilização - Forneça serviços de esterilização",
    "requestEmailChange" to "Solicitar alteração de e-mail",
    "rescueDate" to "Data de resgate",
    "rescueLocation" to "Local de resgate",
    "savePassword" to "Salvar senha",
    "selectEnergy" to "Selecione energia",
    "selectSize" to "Selecione tamanho",
    "sex" to "Sexo",
    "shelterSettings" to "Configurações do abrigo",
    "size" to "Tamanho",
    "small" to "Pequeno",
    "specialNeeds" to "Necessidades especiais",
    "sterilizationSettings" to "Configurações de esterilização",
    "sterilized" to "Esterilizado",
    "temporalHomeSettings" to "Configurações de lar temporário",
    "temperament" to "Temperamento",
    "type" to "Tipo *",
    "urgentAdoption" to "Adoção urgente",
    "vaccinations" to "Vacinas",
    "vetDisclaimer" to "Consulte seu veterinário antes de fazer mudanças na dieta.",
    "weight" to "Peso (kg)",
    "weightLabel" to "Peso",
    "website" to "Site web",
    "securitySettings" to "Configurações de segurança",
    "emailVerifiedTitle" to "E-mail verificado com sucesso!",
    "emailVerifiedMessage" to "Seu e-mail foi verificado. Seu registro está completo.",
    "redirectingIn" to "Redirecionando para a página principal em ",
    "canAlsoLogin" to "Você também pode ",
    "loginNow" to "fazer login agora",
    "emailVerificationFailed" to "Falha na verificação",
    "invalidOrExpiredToken" to "O link de verificação não é válido ou expirou.",
    "tryRegisteringAgain" to "Por favor, tente registrar novamente.",
    "verificationEmailResent" to "O e-mail de verificação expirou. Um novo e-mail de verificação foi enviado. Por favor, verifique sua caixa de entrada.",
    "emailVerificationSent" to "Um e-mail de verificação foi enviado. Por favor, verifique sua caixa de entrada antes de fazer login.",
    "emailNotVerifiedLogin" to "Por favor, verifique seu e-mail antes de fazer login. Verifique sua caixa de entrada para o link de verificação.",
    "invalidOrExpiredMagicLink" to "Link de login inválido ou expirado. Por favor, solicite um novo.",
    "invalidToken" to "Token inválido.",
    "accountBanned" to "Sua conta foi banida.",
    "loginError" to "Ocorreu um erro de login.",
    "profileUpdated" to "Perfil atualizado!",
    "displayNameRequired" to "O nome de exibição não pode estar vazio",
    "saving" to "Salvando...",
    "passwordSet" to "Senha definida",
    "noPassword" to "Sem senha",
    "passwordChangeSuccess" to "Senha alterada com sucesso!",
    "passwordChangeFailed" to "Falha ao alterar senha",
    "passwordSetSuccess" to "Senha definida com sucesso!",
    "passwordSetFailed" to "Falha ao definir senha",
    "passwordsDoNotMatch" to "As senhas não coincidem",
    "currentPasswordRequired" to "Senha atual é obrigatória",
    "emailChangeSent" to "E-mail de verificação enviado para seu novo endereço!",
    "emailChangeFailed" to "Falha ao solicitar alteração de e-mail",
    "emailRequired" to "E-mail é obrigatório",
    "sendingEllipsis" to "Enviando...",
    "resetLinkSent" to "Link de redefinição enviado! Verifique seu e-mail.",
    "magicLinkSent" to "Link de login enviado! Verifique seu e-mail.",
    "magicLinkSendFailed" to "Falha ao enviar o link de login",
    "failedSendResetLink" to "Falha ao enviar o link de redefinição.",
    "invalidOrMissingToken" to "Token inválido ou ausente.",
    "passwordTooShort" to "A senha deve ter pelo menos 8 caracteres.",
    "passwordNeedUppercase" to "A senha precisa de pelo menos 1 letra maiúscula.",
    "passwordNeedLowercase" to "A senha precisa de pelo menos 1 letra minúscula.",
    "passwordNeedNumber" to "A senha precisa de pelo menos 1 número.",
    "passwordNeedSymbol" to "A senha precisa de pelo menos 1 símbolo.",
    "resettingPassword" to "Redefinindo senha...",
    "passwordResetSuccess" to "Senha redefinida com sucesso! Você já pode fazer login.",
    "failedResetPassword" to "Falha ao redefinir a senha.",
    "loginSuccessRedirecting" to "Login realizado com sucesso! Redirecionando...",
    "loginFailedExpiredLink" to "Falha no login. O link pode ser inválido ou ter expirado.",
    "loginFailed" to "Falha no login.",
    "emailChangedSuccess" to "E-mail alterado com sucesso!",
    "failedChangeEmailExpired" to "Falha ao alterar o e-mail. O link pode ser inválido ou ter expirado.",
    "failedChangeEmail" to "Falha ao alterar o e-mail.",
    "emailVerifiedSuccess" to "E-mail verificado com sucesso!",
    "failedVerifyEmailExpired" to "Falha ao verificar o e-mail. O link pode ser inválido ou ter expirado.",
    "failedVerifyEmail" to "Falha ao verificar o e-mail.",
    "dropImagesHint" to "Solte imagens aqui ou clique para procurar",
    "forgotPasswordTitle" to "Esqueceu a senha",
    "forgotPasswordIntro" to "Digite seu e-mail e enviaremos um link para redefinir sua senha.",
    "sendResetLink" to "Enviar link de redefinição",
    "backToLogin" to "Voltar ao login",
    "resetPasswordTitle" to "Redefinir senha",
    "resetPasswordIntro" to "Digite sua nova senha abaixo.",
    "emailLinkLoginTitle" to "Login por link de e-mail",
    "verifying" to "Verificando...",
    "emailChangeTitle" to "Alteração de e-mail",
    "verifyContactEmailTitle" to "Verificar e-mail de contato",
    "status" to "Status",
    "yes" to "Sim",
    "no" to "Não",
    "noDescription" to "Sem descrição.",
    "urgentBadge" to "URGENTE - Precisa de um lar em breve!",
    "needsNewHome" to "Este pet precisa urgentemente de um novo lar",
    "promotedReasonLabel" to "Motivo",
    "selectReason" to "Selecione um motivo",
    "promotedReasonMoving" to "O dono está se mudando",
    "promotedReasonComplaints" to "Reclamações / não pode ter mais pets",
    "promotedReasonPetConflict" to "Conflito com outro animal",
    "promotedReasonOther" to "Outro",
    "promotedReasonDetailLabel" to "Detalhes adicionais (opcional)",
    "promotedReasonRequired" to "Por favor selecione um motivo",
    "needsNewHomeBadge" to "Precisa de novo lar",
    "housingType" to "Tipo de moradia",
    "preferNotToSay" to "Prefiro não dizer",
    "houseHousing" to "Casa",
    "apartmentHousing" to "Apartamento",
    "hasYard" to "Tem quintal",
    "hasOtherPets" to "Outros animais em casa",
    "adoptionExperience" to "Experiência com adoções",
    "firstTimeAdopter" to "Primeira adoção",
    "experiencedAdopter" to "Adotante experiente",
    "noAdoptionRequests" to "Nenhuma solicitação de adoção",
    "noMessage" to "Sem mensagem",
    "markUnderReview" to "Marcar em análise",
    "approve" to "Aprovar",
    "reject" to "Rejeitar",
    "reviewNoteLabel" to "Nota privada de análise",
    "saveNote" to "Salvar nota",
    "adoptionStatusPending" to "Pendente",
    "adoptionStatusUnderReview" to "Em análise",
    "adoptionStatusApproved" to "Aprovada",
    "adoptionStatusRejected" to "Não aprovada",
    "myAdoptionRequests" to "Minhas solicitações de adoção",
    "noAdoptionRequestsYet" to "Você ainda não solicitou a adoção de nenhum pet.",
    "medicalHistory" to "Histórico médico",
    "medicalSchedule" to "Calendário de vacinas e vermifugação",
    "recordType" to "Tipo",
    "vaccination" to "Vacina",
    "deworming" to "Vermifugação",
    "recordName" to "Nome",
    "dateGiven" to "Data aplicada",
    "nextDueDate" to "Próxima dose (opcional)",
    "notesOptional" to "Notas (opcional)",
    "addRecord" to "Adicionar registro",
    "noMedicalRecords" to "Ainda não há registros de vacinas ou vermifugação.",
    "nextDueLabel" to "Próxima dose",
    "givenLabel" to "Aplicada",
    "overdue" to "Atrasada",
    "dueSoon" to "Vencendo em breve",
    "upToDate" to "Em dia",
    "recordNameRequired" to "O nome é obrigatório",
    "dateGivenRequired" to "A data aplicada é obrigatória",
    "failedToAddRecord" to "Falha ao adicionar registro",
    "confirmDeleteRecord" to "Excluir este registro?",
    "maxCapacity" to "Máximo de pets por vez (opcional)",
    "placePetHere" to "Colocar um pet aqui",
    "noAvailablePetsToPlace" to "Você não tem pets disponíveis para colocar agora.",
    "selectPet" to "Selecione o pet",
    "startPlacementBtn" to "Iniciar acolhimento",
    "placementStarted" to "Acolhimento temporário iniciado!",
    "petsCurrentlyWithMe" to "Pets atualmente comigo",
    "noPetsCurrentlyFostered" to "Nenhum pet está atualmente com você.",
    "sinceLabel" to "desde",
    "fosterPlacement" to "Acolhimento temporário",
    "endPlacement" to "Encerrar acolhimento",
    "currentlyWithLabel" to "Atualmente com",
    "notCurrentlyFostered" to "Não está atualmente em acolhimento temporário.",
    "confirmEndPlacement" to "Encerrar este acolhimento temporário? O pet será registrado de volta com você.",
    "aTemporalHome" to "um lar temporário",
    "viewsLabel" to "Visualizações",
    "inquiriesLabel" to "Solicitações",
    "conversionLabel" to "Conversão",
    "messageOptional" to "Mensagem (opcional)",
    "requestAdoption" to "Solicitar adoção",
    "editPet" to "Editar pet",
    "adoptionRequestSubmitted" to "Solicitação de adoção enviada!",
    "failedSubmitRequest" to "Falha ao enviar a solicitação",
    "noPhotosYet" to "Ainda sem fotos.",
    "rabbit" to "Coelho",
    "safeFoods" to "Alimentos seguros",
    "harmfulFoods" to "Alimentos prejudiciais",
    "cannotEatFoods" to "Não pode comer",
    "legendSafe" to "Seguro",
    "legendHarmful" to "Prejudicial",
    "legendToxic" to "Tóxico",
    "food_dog_safe_chicken_name" to "Frango",
    "food_dog_safe_chicken_detail" to "Cozido, sem ossos",
    "food_dog_safe_chicken_desc" to "Proteína magra, fácil de digerir",
    "food_dog_safe_rice_name" to "Arroz",
    "food_dog_safe_rice_detail" to "Branco ou integral",
    "food_dog_safe_rice_desc" to "Boa fonte de carboidratos",
    "food_dog_safe_carrots_name" to "Cenouras",
    "food_dog_safe_carrots_detail" to "Cruas ou cozidas",
    "food_dog_safe_carrots_desc" to "Baixas em calorias, ricas em fibras",
    "food_dog_safe_apples_name" to "Maçãs",
    "food_dog_safe_apples_detail" to "Sem sementes",
    "food_dog_safe_apples_desc" to "Ricas em vitaminas",
    "food_dog_safe_peanutbutter_name" to "Manteiga de amendoim",
    "food_dog_safe_peanutbutter_detail" to "Sem sal, sem xilitol",
    "food_dog_safe_peanutbutter_desc" to "Boa fonte de proteína",
    "food_dog_harmful_grapes_name" to "Uvas",
    "food_dog_harmful_grapes_detail" to "Todas as variedades",
    "food_dog_harmful_grapes_desc" to "Pode causar insuficiência renal",
    "food_dog_harmful_onions_name" to "Cebolas",
    "food_dog_harmful_onions_detail" to "Todas as formas",
    "food_dog_harmful_onions_desc" to "Danifica os glóbulos vermelhos",
    "food_dog_harmful_chocolate_name" to "Chocolate",
    "food_dog_harmful_chocolate_detail" to "Todos os tipos",
    "food_dog_harmful_chocolate_desc" to "Tóxico para cães",
    "food_dog_harmful_garlic_name" to "Alho",
    "food_dog_harmful_garlic_detail" to "Todas as formas",
    "food_dog_harmful_garlic_desc" to "Causa anemia",
    "food_dog_cannot_xylitol_name" to "Xilitol",
    "food_dog_cannot_xylitol_detail" to "Adoçante artificial",
    "food_dog_cannot_xylitol_desc" to "Altamente tóxico",
    "food_dog_cannot_macadamia_name" to "Nozes de macadâmia",
    "food_dog_cannot_macadamia_detail" to "Todas as formas",
    "food_dog_cannot_macadamia_desc" to "Causa fraqueza",
    "food_dog_cannot_avocado_name" to "Abacate",
    "food_dog_cannot_avocado_detail" to "Caroço, casca, tudo",
    "food_dog_cannot_avocado_desc" to "Contém persina",
    "food_dog_cannot_alcohol_name" to "Álcool",
    "food_dog_cannot_alcohol_detail" to "Todas as formas",
    "food_dog_cannot_alcohol_desc" to "Altamente tóxico",
    "food_cat_safe_cookedfish_name" to "Peixe cozido",
    "food_cat_safe_cookedfish_detail" to "Sem espinhas",
    "food_cat_safe_cookedfish_desc" to "Alto teor de proteína",
    "food_cat_safe_chicken_name" to "Frango",
    "food_cat_safe_chicken_detail" to "Cozido, sem tempero",
    "food_cat_safe_chicken_desc" to "Boa fonte de proteína",
    "food_cat_safe_pumpkin_name" to "Abóbora",
    "food_cat_safe_pumpkin_detail" to "Natural, cozida",
    "food_cat_safe_pumpkin_desc" to "Ajuda na digestão",
    "food_cat_safe_eggs_name" to "Ovos",
    "food_cat_safe_eggs_detail" to "Cozidos",
    "food_cat_safe_eggs_desc" to "Proteína completa",
    "food_cat_harmful_raweggs_name" to "Ovos crus",
    "food_cat_harmful_raweggs_detail" to "Com avidina",
    "food_cat_harmful_raweggs_desc" to "Interfere na biotina",
    "food_cat_harmful_rawfish_name" to "Peixe cru",
    "food_cat_harmful_rawfish_detail" to "Contém tiaminase",
    "food_cat_harmful_rawfish_desc" to "Decompõe as vitaminas B",
    "food_cat_harmful_dogfood_name" to "Ração para cães",
    "food_cat_harmful_dogfood_detail" to "Qualquer",
    "food_cat_harmful_dogfood_desc" to "Não tem taurina",
    "food_cat_harmful_milk_name" to "Leite",
    "food_cat_harmful_milk_detail" to "A maioria dos gatos",
    "food_cat_harmful_milk_desc" to "Intolerância à lactose",
    "food_cat_cannot_chocolate_name" to "Chocolate",
    "food_cat_cannot_chocolate_detail" to "Todos os tipos",
    "food_cat_cannot_chocolate_desc" to "Teobromina tóxica",
    "food_cat_cannot_onionsgarlic_name" to "Cebola/Alho",
    "food_cat_cannot_onionsgarlic_detail" to "Todas as formas",
    "food_cat_cannot_onionsgarlic_desc" to "Danifica os glóbulos vermelhos",
    "food_cat_cannot_grapesraisins_name" to "Uvas/Passas",
    "food_cat_cannot_grapesraisins_detail" to "Todas as variedades",
    "food_cat_cannot_grapesraisins_desc" to "Dano renal",
    "food_cat_cannot_alcohol_name" to "Álcool",
    "food_cat_cannot_alcohol_detail" to "Todas as formas",
    "food_cat_cannot_alcohol_desc" to "Muito tóxico",
    "food_bird_safe_seeds_name" to "Sementes",
    "food_bird_safe_seeds_detail" to "Mistura variada",
    "food_bird_safe_seeds_desc" to "Boa fonte de gordura",
    "food_bird_safe_fruits_name" to "Frutas",
    "food_bird_safe_fruits_detail" to "A maioria das variedades",
    "food_bird_safe_fruits_desc" to "Vitaminas e minerais",
    "food_bird_safe_vegetables_name" to "Vegetais",
    "food_bird_safe_vegetables_detail" to "Folhas verdes",
    "food_bird_safe_vegetables_desc" to "Nutrientes essenciais",
    "food_bird_safe_pellets_name" to "Pellets",
    "food_bird_safe_pellets_detail" to "Fórmula de qualidade",
    "food_bird_safe_pellets_desc" to "Nutrição completa",
    "food_bird_harmful_avocado_name" to "Abacate",
    "food_bird_harmful_avocado_detail" to "Todas as partes",
    "food_bird_harmful_avocado_desc" to "Persina tóxica",
    "food_bird_harmful_fruitpits_name" to "Caroços de frutas",
    "food_bird_harmful_fruitpits_detail" to "Maçã, pêssego",
    "food_bird_harmful_fruitpits_desc" to "Traços de cianeto",
    "food_bird_harmful_salt_name" to "Sal",
    "food_bird_harmful_salt_detail" to "Qualquer forma",
    "food_bird_harmful_salt_desc" to "Tóxico para aves",
    "food_bird_harmful_caffeine_name" to "Cafeína",
    "food_bird_harmful_caffeine_detail" to "Café, chá",
    "food_bird_harmful_caffeine_desc" to "Problemas cardíacos",
    "food_bird_cannot_chocolate_name" to "Chocolate",
    "food_bird_cannot_chocolate_detail" to "Todos os tipos",
    "food_bird_cannot_chocolate_desc" to "Teobromina tóxica",
    "food_bird_cannot_onions_name" to "Cebolas",
    "food_bird_cannot_onions_detail" to "Todas as formas",
    "food_bird_cannot_onions_desc" to "Danifica os glóbulos vermelhos",
    "food_bird_cannot_garlic_name" to "Alho",
    "food_bird_cannot_garlic_detail" to "Todas as formas",
    "food_bird_cannot_garlic_desc" to "Tóxico",
    "food_bird_cannot_mushrooms_name" to "Cogumelos",
    "food_bird_cannot_mushrooms_detail" to "Variedades silvestres",
    "food_bird_cannot_mushrooms_desc" to "Toxinas potenciais",
    "food_fish_safe_flakes_name" to "Flocos",
    "food_fish_safe_flakes_detail" to "Marcas de qualidade",
    "food_fish_safe_flakes_desc" to "Ração completa para peixes",
    "food_fish_safe_pellets_name" to "Pellets",
    "food_fish_safe_pellets_detail" to "Flutuantes/afundáveis",
    "food_fish_safe_pellets_desc" to "Nutrição balanceada",
    "food_fish_safe_frozenfood_name" to "Alimento congelado",
    "food_fish_safe_frozenfood_detail" to "Larvas de mosquito, artêmia",
    "food_fish_safe_frozenfood_desc" to "Fonte de proteína",
    "food_fish_safe_vegetables_name" to "Vegetais",
    "food_fish_safe_vegetables_detail" to "Abobrinha branqueada",
    "food_fish_safe_vegetables_desc" to "Fonte de fibra",
    "food_fish_harmful_bread_name" to "Pão",
    "food_fish_harmful_bread_detail" to "Qualquer tipo",
    "food_fish_harmful_bread_desc" to "Sem nutrição, causa saciedade",
    "food_fish_harmful_humanfood_name" to "Comida humana",
    "food_fish_harmful_humanfood_detail" to "Refeições cozidas",
    "food_fish_harmful_humanfood_desc" to "Nutrição inadequada",
    "food_fish_harmful_livefeed_name" to "Alimento vivo",
    "food_fish_harmful_livefeed_detail" to "Insetos silvestres",
    "food_fish_harmful_livefeed_desc" to "Parasitas potenciais",
    "food_fish_cannot_landinsect_name" to "Inseto terrestre",
    "food_fish_cannot_landinsect_detail" to "Insetos rasteiros",
    "food_fish_cannot_landinsect_desc" to "Não é alimento natural",
    "food_fish_cannot_mammalmeat_name" to "Carne de mamífero",
    "food_fish_cannot_mammalmeat_detail" to "Frango, boi",
    "food_fish_cannot_mammalmeat_desc" to "Inadequada para a maioria dos peixes",
    "food_fish_cannot_dairy_name" to "Laticínios",
    "food_fish_cannot_dairy_detail" to "Qualquer",
    "food_fish_cannot_dairy_desc" to "Não conseguem digerir",
    "food_fish_cannot_breadcrumbs_name" to "Farinha de rosca",
    "food_fish_cannot_breadcrumbs_detail" to "Muitas contêm alho",
    "food_fish_cannot_breadcrumbs_desc" to "Aditivos prejudiciais",
    "food_rabbit_safe_hay_name" to "Feno",
    "food_rabbit_safe_hay_detail" to "Capim Timothy",
    "food_rabbit_safe_hay_desc" to "80% da dieta",
    "food_rabbit_safe_leafygreens_name" to "Folhas verdes",
    "food_rabbit_safe_leafygreens_detail" to "Alface romana, coentro",
    "food_rabbit_safe_leafygreens_desc" to "Vitaminas diárias",
    "food_rabbit_safe_pellets_name" to "Pellets",
    "food_rabbit_safe_pellets_detail" to "À base de Timothy",
    "food_rabbit_safe_pellets_desc" to "Nutrição balanceada",
    "food_rabbit_safe_carrots_name" to "Cenouras",
    "food_rabbit_safe_carrots_detail" to "Com moderação",
    "food_rabbit_safe_carrots_desc" to "Petisco",
    "food_rabbit_harmful_icebergletttuce_name" to "Alface americana",
    "food_rabbit_harmful_icebergletttuce_detail" to "Sem nutrição",
    "food_rabbit_harmful_icebergletttuce_desc" to "Pode causar estase gastrointestinal",
    "food_rabbit_harmful_beans_name" to "Feijões",
    "food_rabbit_harmful_beans_detail" to "Todas as leguminosas",
    "food_rabbit_harmful_beans_desc" to "Gases, problemas digestivos",
    "food_rabbit_harmful_corn_name" to "Milho",
    "food_rabbit_harmful_corn_detail" to "Qualquer forma",
    "food_rabbit_harmful_corn_desc" to "Não consegue digerir",
    "food_rabbit_harmful_potatoes_name" to "Batatas",
    "food_rabbit_harmful_potatoes_detail" to "Qualquer forma",
    "food_rabbit_harmful_potatoes_desc" to "Nutrientes inadequados",
    "food_rabbit_cannot_chocolate_name" to "Chocolate",
    "food_rabbit_cannot_chocolate_detail" to "Todos os tipos",
    "food_rabbit_cannot_chocolate_desc" to "Teobromina tóxica",
    "food_rabbit_cannot_onions_name" to "Cebolas",
    "food_rabbit_cannot_onions_detail" to "Todas as formas",
    "food_rabbit_cannot_onions_desc" to "Muito tóxicas",
    "food_rabbit_cannot_garlic_name" to "Alho",
    "food_rabbit_cannot_garlic_detail" to "Todas as formas",
    "food_rabbit_cannot_garlic_desc" to "Tóxico",
    "food_rabbit_cannot_avocado_name" to "Abacate",
    "food_rabbit_cannot_avocado_detail" to "Todas as partes",
    "food_rabbit_cannot_avocado_desc" to "Contém persina",
    "primary" to "Principal",
    "passkeyRegisteredSuccess" to "Passkey registrada com sucesso!",
    "passkeyRegistrationFailed" to "Falha ao registrar passkey",
    "passkeyNameRequired" to "Nome da passkey é obrigatório",
    "registeringPasskey" to "Registrando passkey...",
    "passkeyRegistered" to "Passkey registrada",
    "noPasskey" to "Nenhuma passkey registrada",
    "noPets" to "Nenhum pet ainda.",
    "photographerCountryStateRequired" to "Preencha país e estado para serviços de fotografia",
    "temporalHomeAliasCountryCityRequired" to "Preencha apelido, país e cidade para lar temporário",
    "shelterNameCountryCityAddressRequired" to "Preencha nome, país, cidade e endereço para abrigo",
    "sterilizationNameCountryCityAddressRequired" to "Preencha nome, país, cidade e endereço para esterilização",
    "loading" to "Carregando...",
    "countryRequired" to "Por favor, selecione um país para pesquisar",
    "noSheltersFound" to "Nenhum abrigo encontrado ainda. Volte mais tarde!",
    "visitWebsite" to "Visitar site",
    "fiscalId" to "CNPJ/CPF",
    "donationInformation" to "Informações de doação",
    "bankName" to "Banco",
    "accountHolder" to "Titular da conta",
    "accountNumber" to "Número da conta",
    "currency" to "Moeda",
    "errorLoadingShelters" to "Erro ao carregar os abrigos",
    "actions" to "Ações",
    "addShelter" to "Adicionar abrigo",
    "addEditShelter" to "Adicionar / Editar abrigo",
    "allStates" to "Todos os estados",
    "confirmDelete" to "Tem certeza de que deseja excluir este local?",
    "confirmDeleteShelter" to "Tem certeza de que deseja excluir este abrigo?",
    "confirmUnbanUser" to "Desbanir este usuário?",
    "confirmResetUserCredentials" to "Isso invalidará a senha e as passkeys atuais de {email} e enviará um link de redefinição por e-mail. Continuar?",
    "confirmDeactivateUser" to "Desativar este usuário? Ele poderá ser reativado depois.",
    "confirmDeactivatePet" to "Desativar este pet? Ele ficará oculto das listagens públicas e poderá ser reativado depois.",
    "confirmBlockRescuer" to "Bloquear este resgatista para que não envie mais solicitações?",
    "confirmDeletePet" to "Excluir este pet?",
    "confirmDeleteStorage" to "Excluir este armazenamento?",
    "contact" to "Contato",
    "delete" to "Excluir",
    "edit" to "Editar",
    "errorDeletingShelter" to "Erro ao excluir o abrigo",
    "errorLoadingLocations" to "Erro ao carregar os locais",
    "errorLoadingShelter" to "Erro ao carregar o abrigo",
    "errorSaving" to "Erro ao salvar",
    "errorSavingShelter" to "Erro ao salvar o abrigo",
    "location" to "Localização",
    "noLocationsFound" to "Nenhum local encontrado",
    "selectCountry" to "Selecione um país",
    "selectCountryToFilter" to "Selecione um país para filtrar",
    "shelterAdded" to "Abrigo adicionado com sucesso",
    "shelterDeleted" to "Abrigo excluído com sucesso",
    "shelterUpdated" to "Abrigo atualizado com sucesso",
    "updateShelter" to "Atualizar abrigo",
    "country.afghanistan" to "Afeganistão",
    "country.albania" to "Albânia",
    "country.algeria" to "Argélia",
    "country.argentina" to "Argentina",
    "country.armenia" to "Armênia",
    "country.australia" to "Austrália",
    "country.austria" to "Áustria",
    "country.azerbaijan" to "Azerbaijão",
    "country.bangladesh" to "Bangladesh",
    "country.belarus" to "Bielorrússia",
    "country.belgium" to "Bélgica",
    "country.bolivia" to "Bolívia",
    "country.bosnia" to "Bósnia e Herzegovina",
    "country.brazil" to "Brasil",
    "country.bulgaria" to "Bulgária",
    "country.cambodia" to "Camboja",
    "country.cameroon" to "Camarões",
    "country.canada" to "Canadá",
    "country.chile" to "Chile",
    "country.china" to "China",
    "country.colombia" to "Colômbia",
    "country.costaRica" to "Costa Rica",
    "country.croatia" to "Croácia",
    "country.cuba" to "Cuba",
    "country.czechia" to "República Tcheca",
    "country.denmark" to "Dinamarca",
    "country.dominicanRepublic" to "República Dominicana",
    "country.ecuador" to "Equador",
    "country.egypt" to "Egito",
    "country.elSalvador" to "El Salvador",
    "country.estonia" to "Estônia",
    "country.ethiopia" to "Etiópia",
    "country.finland" to "Finlândia",
    "country.france" to "França",
    "country.georgia" to "Geórgia",
    "country.germany" to "Alemanha",
    "country.ghana" to "Gana",
    "country.greece" to "Grécia",
    "country.guatemala" to "Guatemala",
    "country.haiti" to "Haiti",
    "country.honduras" to "Honduras",
    "country.hungary" to "Hungria",
    "country.iceland" to "Islândia",
    "country.india" to "Índia",
    "country.indonesia" to "Indonésia",
    "country.iran" to "Irã",
    "country.iraq" to "Iraque",
    "country.ireland" to "Irlanda",
    "country.israel" to "Israel",
    "country.italy" to "Itália",
    "country.jamaica" to "Jamaica",
    "country.japan" to "Japão",
    "country.jordan" to "Jordânia",
    "country.kazakhstan" to "Cazaquistão",
    "country.kenya" to "Quênia",
    "country.kuwait" to "Kuwait",
    "country.latvia" to "Letônia",
    "country.lebanon" to "Líbano",
    "country.libya" to "Líbia",
    "country.lithuania" to "Lituânia",
    "country.luxembourg" to "Luxemburgo",
    "country.malaysia" to "Malásia",
    "country.mexico" to "México",
    "country.moldova" to "Moldávia",
    "country.mongolia" to "Mongólia",
    "country.montenegro" to "Montenegro",
    "country.morocco" to "Marrocos",
    "country.myanmar" to "Mianmar",
    "country.nepal" to "Nepal",
    "country.netherlands" to "Países Baixos",
    "country.newZealand" to "Nova Zelândia",
    "country.nicaragua" to "Nicarágua",
    "country.nigeria" to "Nigéria",
    "country.northKorea" to "Coreia do Norte",
    "country.norway" to "Noruega",
    "country.pakistan" to "Paquistão",
    "country.panama" to "Panamá",
    "country.paraguay" to "Paraguai",
    "country.peru" to "Peru",
    "country.philippines" to "Filipinas",
    "country.poland" to "Polônia",
    "country.portugal" to "Portugal",
    "country.puertoRico" to "Porto Rico",
    "country.qatar" to "Catar",
    "country.romania" to "Romênia",
    "country.russia" to "Rússia",
    "country.saudiArabia" to "Arábia Saudita",
    "country.serbia" to "Sérvia",
    "country.singapore" to "Cingapura",
    "country.slovakia" to "Eslováquia",
    "country.slovenia" to "Eslovênia",
    "country.southAfrica" to "África do Sul",
    "country.southKorea" to "Coreia do Sul",
    "country.spain" to "Espanha",
    "country.sriLanka" to "Sri Lanka",
    "country.sudan" to "Sudão",
    "country.sweden" to "Suécia",
    "country.switzerland" to "Suíça",
    "country.syria" to "Síria",
    "country.taiwan" to "Taiwan",
    "country.thailand" to "Tailândia",
    "country.tunisia" to "Tunísia",
    "country.turkey" to "Turquia",
    "country.ukraine" to "Ucrânia",
    "country.uae" to "Emirados Árabes Unidos",
    "country.uk" to "Reino Unido",
    "country.usa" to "Estados Unidos",
    "country.uruguay" to "Uruguai",
    "country.uzbekistan" to "Uzbequistão",
    "country.venezuela" to "Venezuela",
    "country.vietnam" to "Vietnã",
    "country.yemen" to "Iêmen",
    "pleaseSelectCountry" to "Selecione um país",
    "errorLoadingPhotographers" to "Erro ao carregar os fotógrafos",
    "noPhotographersAvailable" to "Nenhum fotógrafo disponível",
    "requestPhotoSession" to "Solicitar sessão de fotos",
    "selectPhotographer" to "Selecionar",
    "photographersSelected" to "{count} selecionados",
    "requestSelectedPhotographers" to "Solicitar fotógrafos selecionados",
    "clearSelection" to "Limpar seleção",
    "requestToMultiple" to "Solicitar a",
    "messageRequired" to "Por favor, escreva uma mensagem para os fotógrafos.",
    "requestsSentSuccessfully" to "Solicitações enviadas com sucesso",
    "message" to "Mensagem",
    "sendRequest" to "Enviar solicitação",
    "requestSentSuccessfully" to "Solicitação enviada com sucesso!",
    "requestTo" to "Solicitar a",
    "enterMessage" to "Digite uma mensagem para {name}...",
    "reportUrgent" to "Urgente",
    "topRescuers" to "Melhores Resgatistas",
    "urgentRescuerSettings" to "Configurações de Resgatista de Urgência",
    "urgentRescueAlerts" to "Alertas de Resgate Urgente",
    "reportUrgentTitle" to "Reportar um animal em perigo",
    "reportUrgentIntro" to "Se você encontrou um animal ferido, maltratado, faminto ou abandonado que precisa de ajuda imediata, conte-nos onde está e o que está acontecendo - alertaremos os resgatistas de urgência próximos imediatamente.",
    "dangerType" to "O que está errado?",
    "dangerInjured" to "Ferido",
    "dangerAbused" to "Sendo maltratado / abusado",
    "dangerStarving" to "Faminto",
    "dangerTooYoung" to "Muito jovem para sobreviver sozinho",
    "dangerOther" to "Outra emergência",
    "useMyLocation" to "Usar minha localização atual",
    "confirmMyLocation" to "Confirmar minha localização atual",
    "locationPermissionHint" to "Seu navegador pedirá permissão para compartilhar sua localização; ela é usada apenas para avisá-lo sobre denúncias próximas.",
    "phoneOptional" to "Seu telefone (opcional)",
    "submitReport" to "Enviar relato",
    "urgentRescuerSettingsTitle" to "Configurações de Resgatista de Urgência",
    "urgentRescuerSettingsIntro" to "Seja avisado quando um animal em perigo for relatado perto da sua área de cobertura. O primeiro a aceitar assume o caso.",
    "urgentRescuerActive" to "Estou disponível para responder a relatos urgentes",
    "coverageAreaMode" to "Área de cobertura",
    "coverageArea" to "Área de cobertura",
    "coverageAreaHint" to "Confirme sua localização atual, arraste o pino ou digite um endereço abaixo - o mapa e os campos ficam sincronizados.",
    "useMyLocationAndRadius" to "Usar minha localização + um raio",
    "pickAZone" to "Escolher um país/estado/cidade em vez disso",
    "radiusKm" to "Raio (km)",
    "urgentRescueAlertsTitle" to "Alertas de Resgate Urgente",
    "urgentRescuerLeaderboardTitle" to "Melhores Resgatistas de Urgência (últimos 12 meses)",
    "locating" to "Localizando...",
    "geolocationUnsupported" to "Geolocalização não é suportada pelo seu navegador",
    "locationCaptured" to "Localização capturada",
    "locationDenied" to "Não foi possível obter sua localização - permita o acesso à localização ou digite uma cidade",
    "street" to "Rua",
    "exteriorNumber" to "Número",
    "referenceNotes" to "Pontos de referência (ex. cor da casa, ponto próximo conhecido)",
    "concreteAddressHint" to "Adicione uma rua e um número se puder - é isso que permite que um resgatista pegue um táxi ou abra um app de mapas direto para o local.",
    "descriptionRequired" to "Por favor, descreva a situação",
    "locationRequired" to "Por favor, forneça uma localização (use sua localização atual ou digite um país e cidade)",
    "submittingEllipsis" to "Enviando...",
    "reportSubmitted" to "Relato enviado. Obrigado - os resgatistas próximos estão sendo alertados agora.",
    "reportSubmitFailed" to "Falha ao enviar o relato",
    "phoneRequired" to "Por favor, digite um número de telefone",
    "savingEllipsis" to "Salvando...",
    "saved" to "Salvo",
    "saveFailed" to "Falha ao salvar",
    "noUrgentPages" to "Nenhum alerta urgente pendente no momento",
    "accept" to "Aceitar",
    "alreadyAccepted" to "Outra pessoa já aceitou este relato",
    "noLeaderboardData" to "Ainda não há resgates aceitos nos últimos 12 meses",
    "rescueAcceptedTitle" to "Resgate Aceito",
    "rescueAcceptedMessage" to "Obrigado - você está a caminho. Vá até o local relatado assim que possível.",
    "rescueAcceptFailedTitle" to "Não foi possível aceitar",
    "rescueAcceptFailedMessage" to "Este relato já foi aceito por outro resgatista, ou o link é inválido ou expirou.",
    "share" to "Compartilhar",
    "shareViaFacebook" to "Compartilhar no Facebook",
    "shareViaX" to "Compartilhar no X",
    "shareViaWhatsapp" to "Compartilhar no WhatsApp",
    "shareViaEmail" to "Compartilhar por e-mail",
    "copyLink" to "Copiar link",
    "linkCopied" to "Link copiado!",
    "reportLostFoundTitle" to "Reportar um animal perdido ou encontrado",
    "reportLostFoundIntro" to "Perdeu seu animal, ou encontrou um animal perdido? Envie um relato e vamos compará-lo com outros relatos próximos.",
    "browseLostFound" to "Ver relatos de animais perdidos e encontrados",
    "kindLost" to "Perdi meu animal",
    "kindFound" to "Encontrei um animal",
    "petType" to "Tipo de animal",
    "petTypeUnknown" to "Não sei",
    "lostFoundBrowseTitle" to "Animais Perdidos e Encontrados",
    "showing" to "Mostrando",
    "kindLostPlural" to "Animais perdidos",
    "kindFoundPlural" to "Animais encontrados",
    "reportResolvedTitle" to "Relato Resolvido",
    "reportResolvedMessage" to "Obrigado pela atualização - este relato está agora encerrado.",
    "reportResolveFailedTitle" to "Não foi possível resolver",
    "reportResolveFailedMessage" to "Este relato já foi resolvido, ou o link é inválido ou expirou.",
    "reportLostFound" to "Perdidos/\u200BEncontrados",
    "noLostFoundReportsFound" to "Nenhum relato de animal perdido ou encontrado",
    "errorLoadingReports" to "Erro ao carregar os relatos",
    "yourEmail" to "Seu e-mail",
    "contactReporter" to "Contatar quem relatou",
    "contactFormIncomplete" to "Por favor, digite seu e-mail e uma mensagem",
    "contactSent" to "Mensagem enviada",
    "contactFailed" to "Falha ao enviar a mensagem",
    "lostFoundReportSubmitted" to "Relato enviado. Obrigado - vamos compará-lo com outros relatos próximos.",
    "video" to "Vídeo (opcional)",
    "removeVideo" to "Remover vídeo",
    "saveThisSearch" to "Salvar esta busca",
    "savedSearchSaved" to "Busca salva! Enviaremos um e-mail quando um novo resultado for listado.",
    "savedSearchFailed" to "Falha ao salvar a busca",
    "favorite" to "Favorito",
    "addToFavorites" to "Adicionar aos Favoritos",
    "removeFromFavorites" to "Remover dos Favoritos",
    "myFavorites" to "Meus Favoritos",
    "noFavoritesYet" to "Ainda sem favoritos",
    "mySavedSearches" to "Minhas Buscas Salvas",
    "noSavedSearchesYet" to "Ainda sem buscas salvas",
    "donationPromptText" to "Obrigado por ajudar um animal hoje. Se puder, considere fazer uma pequena doação para manter o Adopt-U gratuito para todos.",
    "donationPromptCta" to "Doar",
    "dismiss" to "Fechar",
)

private val chineseTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "登录",
    "register" to "注册",
    "logout" to "关闭会话",
    "profile" to "个人资料",
    "myPets" to "我的宠物",
    "addPet" to "添加宠物",
    "save" to "保存",
    "cancel" to "取消",
    "search" to "搜索",
    "shelters" to "收容所",
    "photographers" to "摄影师",
    "temporalHomes" to "临时住所",
    "findTemporalHomes" to "寻找临时住所",
    "sterilizationLocations" to "绝育地点",
    "donate" to "捐赠",
    "countryLabel" to "国家",
    "selectCountryToSearch" to "选择国家",
    "selectCountryFirst" to "请先选择国家以启用这些筛选项",
    "state" to "州/省",
    "city" to "城市",
    "zipCode" to "邮编",
    "neighborhood" to "社区",
    "name" to "姓名",
    "email" to "电子邮件",
    "phone" to "电话",
    "address" to "地址",
    "description" to "描述",
    "animalShelters" to "动物收容所",
    "sheltersDescription" to "查找可以领养宠物或经收容所事先同意后寄养宠物的动物收容所",
    "photographerDescription" to "专业宠物摄影师",
    "sterilizationLocationsDescription" to "寻找宠物绝育地点",
    "privacyPolicy" to "隐私政策",
    "privacyLastUpdated" to "最后更新：2025年4月",
    "privacyHighlight" to "在 Adopt-U，我们坚持最小化数据收集原则。我们仅收集您的电子邮件地址，以便领养人与救助人之间进行沟通。我们不会将您的信息用于广告，也绝不会出于营销目的与第三方共享您的数据。",
    "privacyInfoCollectedH2" to "我们收集的信息",
    "privacyAccountRegH3" to "账户注册",
    "privacyAccountRegIntro" to "为了在 Adopt-U 创建账户，我们会收集以下信息：",
    "privacyEmailLabel" to "电子邮件地址：",
    "privacyEmailDesc" to "用于账户验证、密码找回以及领养人与救助人之间的沟通",
    "privacyDisplayNameLabel" to "显示名称：",
    "privacyDisplayNameDesc" to "您在平台上用于标识自己的自选名称",
    "privacyLangPrefLabel" to "语言偏好：",
    "privacyLangPrefDesc" to "以便以您偏好的语言提供平台服务",
    "privacyNoFullNameEtc" to "除上述内容外，我们不会收集您的全名、电话号码、实际地址、身份证件、付款信息或任何其他个人数据。",
    "privacyOptionalProfileH3" to "可选的个人资料信息",
    "privacyOptionalProfileIntro" to "根据您的角色，您可以选择性地提供：",
    "privacyAdopterLabel" to "作为领养人：",
    "privacyAdopterDesc" to "无需提供或收集额外信息",
    "privacyRescuerLabel" to "作为救助人：",
    "privacyRescuerDesc" to "您可供领养的宠物的位置信息",
    "privacyPhotographerLabel" to "作为摄影师：",
    "privacyPhotographerDesc" to "服务地点及联系偏好",
    "privacyTemporalHomeLabel" to "作为临时寄养家庭：",
    "privacyTemporalHomeDesc" to "您可以提供临时住所的位置",
    "privacyLocationUsage" to "所有位置信息仅用于将领养人与附近的救助人及服务提供者进行匹配。",
    "privacyPetInfoH3" to "宠物信息",
    "privacyPetInfoIntro" to "救助人可以发布有关宠物的信息，包括：",
    "privacyPetPhotos" to "宠物照片",
    "privacyPetDescBreedAge" to "描述、品种、年龄及特征",
    "privacyPetHealth" to "健康及疫苗接种状况",
    "privacyPetLocation" to "宠物所在地",
    "privacyPetInfoNotVerified" to "此信息由救助人提供，Adopt-U 不进行独立核实。",
    "privacyHowWeUseH2" to "我们如何使用您的信息",
    "privacyPrimaryPurposeH3" to "主要目的：促进领养联系",
    "privacyEmailUsedFor" to "您的电子邮件地址仅用于以下目的：",
    "privacyUseVerification" to "在您创建账户时向您发送验证链接",
    "privacyUseRescuerContact" to "允许救助人就您感兴趣的宠物与您联系",
    "privacyUseContactRescuer" to "允许您就救助人发布的宠物与其联系",
    "privacyUseNotifications" to "向您发送重要的账户及安全通知",
    "privacyOnceConnected" to "一旦您通过我们的平台与其他用户建立联系，后续所有沟通均直接在双方之间进行。我们不会监控、存储或访问您沟通内容。",
    "privacyNeverDoH3" to "我们绝不会做的事",
    "privacyCommitIntro" to "我们郑重承诺以下事项：",
    "privacyNoAdsLabel" to "无广告：",
    "privacyNoAdsDesc" to "我们绝不会根据您的活动展示定向或非定向广告",
    "privacyNoMarketingLabel" to "无营销：",
    "privacyNoMarketingDesc" to "我们绝不会将您的电子邮件与第三方共享用于营销或推广目的",
    "privacyNoSellingLabel" to "不出售数据：",
    "privacyNoSellingDesc" to "我们绝不会将您的个人信息出售、出租或以其他方式转让给任何第三方",
    "privacyNoProfilingLabel" to "不建立用户画像：",
    "privacyNoProfilingDesc" to "我们不会将您的数据用于账户基本管理之外的用户画像构建",
    "privacyNoTrackingLabel" to "不跟踪：",
    "privacyNoTrackingDesc" to "我们不使用跟踪像素、广告 Cookie 或任何类似的跟踪技术",
    "privacyDataStorageH2" to "数据存储与安全",
    "privacyDataStoredSecurely" to "您的数据采用行业标准加密技术安全存储。我们采取适当的技术和组织措施，保护您的个人信息免遭未经授权的访问、篡改、披露或销毁。",
    "privacyDataRetention" to "只要您的账户处于活跃状态，我们就会保留您的账户信息。您可以随时联系我们，要求删除您的账户及相关数据。",
    "privacyYourRightsH2" to "您的权利",
    "privacyRightsIntro" to "您对个人数据享有以下权利：",
    "privacyRightAccess" to "访问权：您可以要求获取我们持有的您所有个人数据的副本",
    "privacyRightCorrection" to "更正权：您可以要求我们更正任何不准确的信息",
    "privacyRightDeletion" to "删除权：您可以要求我们删除您的账户及所有相关数据",
    "privacyRightPortability" to "可携带权：您可以要求我们以常用格式向您提供数据",
    "privacyExerciseRights" to "如需行使上述任何权利，请通过 adopt-u@adopt-u.org 与我们联系。",
    "privacyCookiesH2" to "Cookie",
    "privacyCookiesText" to "Adopt-U 仅使用平台功能所必需的最少量 Cookie，包括会话管理和语言偏好设置。我们不使用广告 Cookie 或跟踪 Cookie。",
    "privacyThirdPartyH2" to "第三方服务",
    "privacyThirdPartyIntro" to "我们使用以下可能处理您数据的第三方服务：",
    "privacyAws" to "AWS（Amazon Web Services）：用于数据存储和邮件发送。其隐私政策适用于对您数据的处理。",
    "privacyWebauthn" to "WebAuthn/FIDO2：用于安全的 Passkey 身份验证，涉及在您浏览器中进行的加密运算。",
    "privacyThirdPartySelected" to "我们精心挑选了与我们同样重视数据隐私的服务提供商。",
    "privacyChildrensH2" to "儿童隐私",
    "privacyChildrensText" to "Adopt-U 不面向 18 岁以下的用户。我们不会故意收集未成年人的信息。如果您认为某未成年人已创建账户，请联系我们予以删除。",
    "privacyChangesH2" to "本政策的变更",
    "privacyChangesText" to "我们可能会不时更新本隐私政策，以反映我们做法或法律要求的变化。我们将通过电子邮件或平台上的显著通知告知用户重大变更。",
    "privacyContactH2" to "联系我们",
    "privacyContactText" to "如果您对本隐私政策有任何疑问或顾虑，或希望行使您的数据权利，请通过 adopt-u@adopt-u.org 与我们联系。",
    "termsConditions" to "条款和条件",
    "installApp" to "安装应用",
    "termsAcceptanceH2" to "条款接受",
    "termsAcceptanceText" to "访问和使用 Adopt-U 即表示您接受并同意受本协议条款和规定的约束。如果您不同意这些条款，请不要使用我们的平台。",
    "termsNatureH2" to "服务性质",
    "termsNatureText1" to "Adopt-U 是一个连接平台，旨在促进宠物救助人与潜在领养人之间的联系。我们不是宠物商店、中介、收容所或领养机构。我们不拥有、监管或直接处理平台上列出的任何宠物。",
    "termsNatureText2" to "我们的作用仅限于为各方提供沟通渠道。我们不参与领养协商、合同签订或动物的实际交接。",
    "termsUserRespH2" to "用户责任",
    "termsForRescuersH3" to "对于救助人：",
    "termsRescuerResp1" to "提供关于您所照顾宠物的准确、真实信息",
    "termsRescuerResp2" to "确保宠物在发布前身体健康、已接种疫苗并经过适当兽医检查",
    "termsRescuerResp3" to "对潜在领养人进行负责任的审核",
    "termsRescuerResp4" to "安排宠物安全送达新家",
    "termsRescuerResp5" to "对所提供信息的准确性负责",
    "termsRescuerResp6" to "遵守所有关于宠物领养的当地法律法规",
    "termsRescuerResp7" to "严禁将平台用于商业宠物销售或繁殖目的",
    "termsForAdoptersH3" to "对于领养人：",
    "termsAdopterResp1" to "提供关于您居住情况及养宠经验的准确信息",
    "termsAdopterResp2" to "准备好证明您有能力提供适当的照顾",
    "termsAdopterResp3" to "理解取消宠物领养会给动物带来严重的压力",
    "termsAdopterResp4" to "尊重救助人拒绝您领养申请的权利",
    "termsPetListingsH2" to "宠物列表与信息",
    "termsPetListingsText1" to "Adopt-U 不对宠物列表中提供的信息进行核实、认可或保证其准确性。包括但不限于品种、年龄、健康状况、性情和疫苗接种记录在内的信息均由救助人提供，未经独立核实。",
    "termsPetListingsText2" to "照片和描述均由救助人提供。虽然我们禁止故意虚假陈述，但我们无法保证所有列表都能准确反映动物的当前状况。",
    "termsPetListingsText3" to "我们鼓励用户在最终确定任何领养安排之前提出问题、索取兽医记录并与宠物见面。",
    "termsAdoptionProcessH2" to "领养流程",
    "termsAdoptionProcessIntro" to "一旦救助人与领养人同意进行领养：",
    "termsAdoptionStep1" to "所有安排，包括交接日期、地点及相关费用，均由双方直接协商",
    "termsAdoptionStep2" to "Adopt-U 不促成也不担保任何付款交易",
    "termsAdoptionStep3" to "本平台不提供领养合同；任何合同安排均由相关各方自行负责",
    "termsAdoptionStep4" to "我们强烈建议将所有协议以书面形式记录",
    "termsLiabilityH2" to "责任限制",
    "termsLiabilityIntro" to "Adopt-U 对以下事项不承担责任：",
    "termsLiability1" to "救助人与领养人之间的任何争议、分歧或冲突",
    "termsLiability2" to "宠物列表中所提供信息的准确性",
    "termsLiability3" to "任何动物在领养前、领养中或领养后的健康、行为或状态",
    "termsLiability4" to "因使用本平台而产生的任何经济损失、损害或伤害",
    "termsLiability5" to "任何领养的结果，包括但不限于退回、健康问题或适配性问题",
    "termsLiability6" to "用户在建立联系后于平台之外采取的任何行动",
    "termsLiabilityAck" to "用户承认领养宠物是一项重大承诺，并对其领养决定承担全部责任。",
    "termsProhibitedH2" to "禁止行为",
    "termsProhibitedIntro" to "以下行为在 Adopt-U 严格禁止：",
    "termsProhibited1" to "商业宠物销售、繁殖经营或幼犬繁殖场",
    "termsProhibited2" to "发布您并非直接照顾的宠物信息",
    "termsProhibited3" to "在列表或个人资料中提供虚假或误导性信息",
    "termsProhibited4" to "骚扰、歧视或与其他用户的不当接触",
    "termsProhibited5" to "利用平台收集与宠物领养无关的个人信息",
    "termsProhibited6" to "任何违法行为或违反动物福利法的行为",
    "termsAccountTerminationH2" to "账户终止",
    "termsAccountTerminationText" to "Adopt-U 保留暂停或终止违反这些条款、从事禁止活动或损害平台声誉账户的权利。我们也可能删除包含虚假、误导性或不当内容的列表。",
    "termsModificationsH2" to "条款修改",
    "termsModificationsText" to "我们保留随时修改这些条款的权利。条款变更后继续使用本平台即表示接受修改后的条款。我们将通过电子邮件或平台公告通知用户重大变更。",
    "termsContactText" to "如果您对本条款有任何疑问，请通过 adopt-u@adopt-u.org 与我们联系。",
    "language" to "语言",
    "displayName" to "显示名称",
    "saveProfile" to "保存资料",
    "accountSettings" to "账户设置",
    "yourRoles" to "您的角色：",
    "resources" to "资源",
    "loginWithPasskey" to "使用 Passkey 登录",
    "signInWithPasskey" to "使用通行密钥登录",
    "resendVerificationEmail" to "重新发送验证邮件",
    "usesFido" to "使用 FIDO2 / WebAuthn 实现无密码安全认证。",
    "loginWithMagicLink" to "使用邮件链接登录",
    "magicLinkDesc" to "我们将向您的邮箱发送登录链接（有效期5分钟）。",
    "sendMagicLink" to "发送登录链接",
    "loginWithPassword" to "使用密码登录",
    "signIn" to "登录",
    "forgotPassword" to "忘记密码？",
    "registerNewAccount" to "创建账户",
    "accountDetails" to "账户详情",
    "registrationMethod" to "注册方式",
    "selectAdditionalRoles" to "选择附加角色：",
    "adoptPet" to "领养宠物",
    "publishPets" to "发布待领养宠物",
    "offerPhotography" to "提供摄影服务",
    "photographerSettings" to "摄影师设置",
    "sessionFee" to "会话费用",
    "provideTemporaryHome" to "提供宠物临时住所",
    "passkeyNoPassword" to "Passkey 比密码更安全，且适用于您的所有设备。",
    "registerWithPasskey" to "使用 Passkey 注册",
    "passkeySettings" to "Passkey 设置",
    "password" to "密码",
    "confirmPassword" to "确认密码",
    "passwordRequirements" to "密码必须至少8个字符，并包含大写字母、小写字母、数字和符号",
    "securityInfo" to "安全信息",
    "passkeyMostSecure" to "Passkey：最安全，推荐",
    "passkeyBrowserCompat" to "Passkey 可能不适用于所有浏览器",
    "passwordLeastSecure" to "密码：安全性较低，不推荐",
    "selectLoginMethods" to "选择一种或多种登录方式：",
    "registerPasskeyOption" to "Passkey（最安全，适用于您的所有设备）",
    "registerPasswordOption" to "密码（安全性较低，仅作为备用方式）",
    "howPasskeysWorkTitle" to "Passkey 是如何工作的？",
    "howPasskeysWorkIntro" to "Passkey 用设备自带的安全验证方式（指纹、面部识别或屏幕锁定 PIN）取代密码。",
    "howPasskeysWorkSecurity" to "它仅存储在您的设备上，无法被钓鱼窃取，也不会在数据泄露中泄露，并会自动在您的设备间同步。",
    "howPasskeysWorkBackup" to "您也可以添加密码作为备用，但仅凭 Passkey 即可登录，无需密码。",
    "gotIt" to "知道了",
    "alreadyHaveAccount" to "已有账户？",
    "all" to "全部",
    "filterDogs" to "狗",
    "filterCats" to "猫",
    "filterBirds" to "鸟",
    "filterFish" to "鱼",
    "filterAllSexOption" to "所有性别",
    "filterMaleOption" to "♂ 公",
    "filterFemaleOption" to "♀ 母",
    "age" to "年龄",
    "rescued" to "救助日期",
    "years" to "岁",
    "months" to "个月",
    "year" to "岁",
    "month" to "个月",
    "loginToAdopt" to "登录以申请领养",
    "requiredMarker" to "（必选）",
    "pageNotFoundTitle" to "页面未找到",
    "pageNotFoundHint" to "链接可能已过期或输入有误。宠物们还在这里。",
    "browsePets" to "浏览宠物",
    "requestsFromRescuers" to "救援者的请求",
    "signingOut" to "正在退出...",
    "adopter" to "领养人",
    "country" to "国家",
    "zip" to "邮编",
    "viewPublic" to "查看公开页面",
    "banUser" to "封禁用户",
    "banReason" to "原因（可选）",
    "myPhotoRequests" to "我的拍摄请求",
    "noPhotoRequestsYet" to "您还没有预约拍摄。",
    "photoRequestsReceived" to "收到的拍摄请求",
    "noPhotoRequestsReceived" to "暂无拍摄请求。",
    "photoStatusPending" to "待处理",
    "photoStatusApproved" to "已批准",
    "photoStatusRejected" to "已拒绝",
    "photoStatusCancelled" to "已取消",
    "photoStatusCompleted" to "已完成",
    "cancelRequest" to "取消请求",
    "registerConsentPrefix" to "创建账户即表示您接受我们的",
    "registerConsentTerms" to "条款和条件",
    "registerConsentAnd" to "和",
    "registerConsentPrivacy" to "隐私政策",
    "petStatusAvailable" to "可领养",
    "petStatusAdopted" to "已领养",
    "petStatusDisabled" to "已禁用",
    "petStatusPending" to "待定",
    "addEditLocation" to "添加/编辑地点",
    "addLocation" to "添加地点",
    "manage" to "管理",
    "adminPanel" to "管理面板",
    "adminSterilizationLocations" to "管理绝育地点",
    "adopterRequired" to "领养人（必选）",
    "adoptionFee" to "领养费",
    "adoptionRequests" to "领养申请",
    "ageMonths" to "年龄（月）",
    "ageYears" to "年龄（岁）",
    "alias" to "别名",
    "bird" to "鸟类",
    "breed" to "品种",
    "medicalEventsOverview" to "医疗事件概览",
    "back" to "返回",
    "cat" to "猫",
    "color" to "颜色",
    "compatibility" to "兼容性",
    "currentPassword" to "当前密码",
    "dog" to "狗",
    "emailChange" to "更改电子邮件",
    "emailChangeDesc" to "更新您的电子邮件地址。系统将向新邮箱发送验证链接。",
    "energyLevel" to "活力等级",
    "female" to "雌性",
    "findTemporalHome" to "寻找临时住所",
    "fish" to "鱼类",
    "goodWithCats" to "与猫友好",
    "goodWithDogs" to "与狗友好",
    "goodWithKids" to "与孩子友好",
    "high" to "高",
    "houseTrained" to "已训练",
    "large" to "大型",
    "low" to "低",
    "male" to "雄性",
    "managePets" to "管理宠物",
    "managePetsBtn" to "添加或编辑宠物",
    "managePetsDescription" to "管理所有宠物页面。添加或删除宠物。",
    "manageShelters" to "管理收容所",
    "manageSheltersBtn" to "添加或编辑收容所",
    "manageSheltersDescription" to "管理所有收容所页面。添加或删除收容所。",
    "manageUsers" to "管理用户",
    "medical" to "医疗",
    "medium" to "中型",
    "backToSearch" to "返回搜索",
    "contactThisHome" to "联系此临时住所",
    "loginAsRescuerToContact" to "请以救援者身份登录以向此临时住所发送请求。",
    "memberSince" to "加入时间",
    "microchipId" to "芯片编号",
    "microchipped" to "已植入芯片",
    "myTemporalHome" to "我的临时住所",
    "newEmail" to "新电子邮件",
    "noTemporalHomes" to "未找到临时住所。",
    "requestSentToHome" to "您的请求已发送！",
    "sendRequestBtn" to "发送请求",
    "temporalHomeNotFound" to "找不到此临时住所。",
    "viewDetails" to "查看详情",
    "rescuerDirectory" to "救援者",
    "noRescuersFound" to "未找到救援者。",
    "availablePetsCount" to "可领养宠物数",
    "availablePets" to "可领养宠物",
    "noAvailablePetsForRescuer" to "该救援者目前没有可领养的宠物。",
    "rescuerNotFound" to "找不到该救援者。",
    "errorLoadingResults" to "加载结果时出错。",
    "volunteerForThisRescuer" to "为该救援者做志愿者",
    "volunteerExplanation" to "已批准的志愿者可以为该救援者的宠物信息建议修改。",
    "applyToVolunteerBtn" to "申请成为志愿者",
    "loginToVolunteer" to "登录以申请成为该救援者的志愿者。",
    "volunteerApplicationSent" to "您的志愿者申请已发送！",
    "myVolunteering" to "我的志愿服务",
    "noVolunteerApplicationsYet" to "您尚未向任何救援者申请成为志愿者。",
    "howToVolunteerHint" to "如需申请，请从目录打开某位救助者的页面，然后点击\"申请成为志愿者\"。",
    "volunteerStatusPending" to "待处理",
    "volunteerStatusActive" to "活跃",
    "volunteerStatusRejected" to "已拒绝",
    "volunteerApplications" to "志愿者申请",
    "noVolunteerApplications" to "暂无志愿者申请。",
    "petEditSuggestions" to "宠物信息修改建议",
    "noPetEditSuggestions" to "暂无待处理的修改建议。",
    "suggestedByLabel" to "建议人：",
    "suggestAnEditTitle" to "建议修改",
    "suggestAnEditExplanation" to "作为已批准的志愿者，您可以为此宠物信息建议修改。救援者会在发布前进行审核。",
    "suggestEditBtn" to "发送建议",
    "editSuggestionSent" to "您的建议已发送给救援者审核！",
    "sponsorThisPetTitle" to "资助这只宠物",
    "sponsorExplanation" to "不能领养但想帮忙分担费用？提供金钱或实物支持，直接与救援者联系。",
    "sponsorThisRescuerTitle" to "资助这位救援者",
    "sponsorRescuerExplanation" to "以金钱或实物帮助支持这位救援者的日常开销。",
    "sponsorTypeLabel" to "您可以提供什么？",
    "sponsorTypeMoney" to "金钱",
    "sponsorTypeInKind" to "实物（食物、用品等）",
    "amountLabel" to "金额（每月）",
    "currencyLabel" to "货币",
    "inKindDescriptionLabel" to "描述您可以提供的内容",
    "messageLabel" to "留言",
    "sendOfferBtn" to "发送资助",
    "sponsorOfferSent" to "您的资助已发送！救援者会联系您安排详情。",
    "sponsorshipOffersReceived" to "资助提议",
    "noSponsorshipOffers" to "暂无资助提议。",
    "generalFundLabel" to "通用基金",
    "offeringLabel" to "提供",
    "sponsorshipStatusSent" to "新",
    "sponsorshipStatusRead" to "已读",
    "markAsReadBtn" to "标记为已读",
    "mySponsorshipOffers" to "我的资助提议",
    "noSponsorshipOffersYet" to "您还没有发送过任何资助提议。",
    "howToSponsorHint" to "如需赞助，请打开某位救助者的页面（一般支持）或某只宠物的页面（该宠物的费用），然后发送报价。",
    "browsePetsLink" to "浏览宠物",
    "yourMessage" to "您的留言",
    "newPassword" to "新密码",
    "passkeyDesc" to "为您的账户添加额外的 Passkey，以便在所有设备上轻松登录。",
    "passkeyName" to "Passkey 名称",
    "pers" to "人员",
    "petFoodDescription" to "了解哪些食物对您的宠物安全、有害或有毒",
    "petFoodGuide" to "宠物食品指南",
    "foodInformation" to "食品信息",
    "petsForAdoption" to "待领养宠物",
    "chooseCountryPrompt" to "找到你的新朋友",
    "chooseCountryPromptHint" to "请选择上方的国家，查看附近可领养的宠物。",
    "noPetsFound" to "未找到宠物",
    "noPetsFoundHint" to "试试其他筛选条件，或稍后再来看看——我们会定期添加新宠物。",
    "loadingMorePets" to "正在加载更多宠物…",
    "photos" to "照片（最多12张）",
    "profilePhotographerRole" to "摄影师 - 提供摄影服务",
    "profileRescuerRole" to "救援者 - 发布待领养宠物",
    "profileTemporalHomeRole" to "临时住所 - 提供宠物临时住所",
    "provideShelter" to "收容所 - 管理动物收容所",
    "provideSterilization" to "绝育服务 - 提供绝育服务",
    "requestEmailChange" to "请求更改电子邮件",
    "rescueDate" to "救援日期",
    "rescueLocation" to "救援地点",
    "savePassword" to "保存密码",
    "selectEnergy" to "选择活力",
    "selectSize" to "选择体型",
    "sex" to "性别",
    "shelterSettings" to "收容所设置",
    "size" to "体型",
    "small" to "小型",
    "specialNeeds" to "特殊需求",
    "sterilizationSettings" to "绝育设置",
    "sterilized" to "已绝育",
    "temporalHomeSettings" to "临时住所设置",
    "temperament" to "性格",
    "type" to "类型 *",
    "urgentAdoption" to "紧急领养",
    "vaccinations" to "疫苗接种",
    "vetDisclaimer" to "在改变饮食前请咨询您的兽医。",
    "weight" to "体重（公斤）",
    "weightLabel" to "体重",
    "website" to "网站",
    "securitySettings" to "安全设置",
    "emailVerifiedTitle" to "邮箱验证成功！",
    "emailVerifiedMessage" to "您的邮箱已验证。注册现已完成。",
    "redirectingIn" to "将在 ",
    "canAlsoLogin" to "您也可以 ",
    "loginNow" to "立即登录",
    "emailVerificationFailed" to "验证失败",
    "invalidOrExpiredToken" to "验证链接无效或已过期。",
    "tryRegisteringAgain" to "请尝试重新注册。",
    "verificationEmailResent" to "验证邮件已过期。新的验证邮件已发送。请检查您的收件箱。",
    "emailVerificationSent" to "验证邮件已发送。请在登录前检查您的收件箱。",
    "emailNotVerifiedLogin" to "请在登录前验证您的邮箱。请检查您的收件箱中的验证链接。",
    "invalidOrExpiredMagicLink" to "登录链接无效或已过期。请请求一个新的链接。",
    "invalidToken" to "无效令牌。",
    "accountBanned" to "您的账户已被封禁。",
    "loginError" to "发生登录错误。",
    "profileUpdated" to "个人资料已更新！",
    "displayNameRequired" to "显示名称不能为空",
    "saving" to "保存中...",
    "passwordSet" to "密码已设置",
    "noPassword" to "未设置密码",
    "passwordChangeSuccess" to "密码修改成功！",
    "passwordChangeFailed" to "密码修改失败",
    "passwordSetSuccess" to "密码设置成功！",
    "passwordSetFailed" to "密码设置失败",
    "passwordsDoNotMatch" to "密码不匹配",
    "currentPasswordRequired" to "需要当前密码",
    "emailChangeSent" to "验证邮件已发送到您的新邮箱！",
    "emailChangeFailed" to "请求更改邮箱失败",
    "emailRequired" to "邮箱是必填项",
    "sendingEllipsis" to "发送中...",
    "resetLinkSent" to "重置链接已发送！请查收邮件。",
    "magicLinkSent" to "登录链接已发送！请查收邮件。",
    "magicLinkSendFailed" to "登录链接发送失败",
    "failedSendResetLink" to "发送重置链接失败。",
    "invalidOrMissingToken" to "令牌无效或缺失。",
    "passwordTooShort" to "密码至少需要8个字符。",
    "passwordNeedUppercase" to "密码需要至少1个大写字母。",
    "passwordNeedLowercase" to "密码需要至少1个小写字母。",
    "passwordNeedNumber" to "密码需要至少1个数字。",
    "passwordNeedSymbol" to "密码需要至少1个符号。",
    "resettingPassword" to "正在重置密码...",
    "passwordResetSuccess" to "密码重置成功！您现在可以登录了。",
    "failedResetPassword" to "重置密码失败。",
    "loginSuccessRedirecting" to "登录成功！正在跳转...",
    "loginFailedExpiredLink" to "登录失败。链接可能无效或已过期。",
    "loginFailed" to "登录失败。",
    "emailChangedSuccess" to "邮箱更改成功！",
    "failedChangeEmailExpired" to "更改邮箱失败。链接可能无效或已过期。",
    "failedChangeEmail" to "更改邮箱失败。",
    "emailVerifiedSuccess" to "邮箱验证成功！",
    "failedVerifyEmailExpired" to "验证邮箱失败。链接可能无效或已过期。",
    "failedVerifyEmail" to "验证邮箱失败。",
    "dropImagesHint" to "将图片拖放到此处或点击浏览",
    "forgotPasswordTitle" to "忘记密码",
    "forgotPasswordIntro" to "输入您的电子邮件，我们将向您发送重置密码的链接。",
    "sendResetLink" to "发送重置链接",
    "backToLogin" to "返回登录",
    "resetPasswordTitle" to "重置密码",
    "resetPasswordIntro" to "请在下方输入您的新密码。",
    "emailLinkLoginTitle" to "邮件链接登录",
    "verifying" to "正在验证...",
    "emailChangeTitle" to "邮箱更改",
    "verifyContactEmailTitle" to "验证联系邮箱",
    "status" to "状态",
    "yes" to "是",
    "no" to "否",
    "noDescription" to "暂无描述。",
    "urgentBadge" to "紧急 - 急需寻找家庭！",
    "needsNewHome" to "这只宠物急需一个新家",
    "promotedReasonLabel" to "原因",
    "selectReason" to "请选择原因",
    "promotedReasonMoving" to "主人正在搬家",
    "promotedReasonComplaints" to "投诉/无法再养更多宠物",
    "promotedReasonPetConflict" to "与其他宠物冲突",
    "promotedReasonOther" to "其他",
    "promotedReasonDetailLabel" to "补充说明（可选）",
    "promotedReasonRequired" to "请选择原因",
    "needsNewHomeBadge" to "急需新家",
    "housingType" to "住房类型",
    "preferNotToSay" to "不愿透露",
    "houseHousing" to "独立房屋",
    "apartmentHousing" to "公寓",
    "hasYard" to "有院子",
    "hasOtherPets" to "家中有其他宠物",
    "adoptionExperience" to "领养经验",
    "firstTimeAdopter" to "首次领养",
    "experiencedAdopter" to "有经验的领养人",
    "noAdoptionRequests" to "暂无领养申请",
    "noMessage" to "无留言",
    "markUnderReview" to "标记为审核中",
    "approve" to "批准",
    "reject" to "拒绝",
    "reviewNoteLabel" to "私密审核备注",
    "saveNote" to "保存备注",
    "adoptionStatusPending" to "待处理",
    "adoptionStatusUnderReview" to "审核中",
    "adoptionStatusApproved" to "已批准",
    "adoptionStatusRejected" to "未通过",
    "myAdoptionRequests" to "我的领养申请",
    "noAdoptionRequestsYet" to "您还没有申请领养任何宠物。",
    "medicalHistory" to "病历",
    "medicalSchedule" to "疫苗与驱虫计划",
    "recordType" to "类型",
    "vaccination" to "疫苗",
    "deworming" to "驱虫",
    "recordName" to "名称",
    "dateGiven" to "接种日期",
    "nextDueDate" to "下次到期日（可选）",
    "notesOptional" to "备注（可选）",
    "addRecord" to "添加记录",
    "noMedicalRecords" to "暂无疫苗或驱虫记录。",
    "nextDueLabel" to "下次到期",
    "givenLabel" to "接种于",
    "overdue" to "已逾期",
    "dueSoon" to "即将到期",
    "upToDate" to "已是最新",
    "recordNameRequired" to "名称为必填项",
    "dateGivenRequired" to "接种日期为必填项",
    "failedToAddRecord" to "添加记录失败",
    "confirmDeleteRecord" to "删除此记录？",
    "maxCapacity" to "同时最多可养宠物数（可选）",
    "placePetHere" to "将宠物寄养于此",
    "noAvailablePetsToPlace" to "您目前没有可安排寄养的宠物。",
    "selectPet" to "选择宠物",
    "startPlacementBtn" to "开始寄养",
    "placementStarted" to "寄养已开始！",
    "petsCurrentlyWithMe" to "目前在我这里的宠物",
    "noPetsCurrentlyFostered" to "目前没有宠物寄养在您这里。",
    "sinceLabel" to "自",
    "fosterPlacement" to "寄养安排",
    "endPlacement" to "结束寄养",
    "currentlyWithLabel" to "目前寄养于",
    "notCurrentlyFostered" to "目前不在寄养中。",
    "confirmEndPlacement" to "结束此次寄养？该宠物将被记录为已返回给您。",
    "aTemporalHome" to "一个临时寄养家庭",
    "viewsLabel" to "浏览量",
    "inquiriesLabel" to "咨询数",
    "conversionLabel" to "转化率",
    "messageOptional" to "留言（可选）",
    "requestAdoption" to "申请领养",
    "editPet" to "编辑宠物",
    "adoptionRequestSubmitted" to "领养申请已提交！",
    "failedSubmitRequest" to "提交申请失败",
    "noPhotosYet" to "暂无照片。",
    "rabbit" to "兔子",
    "safeFoods" to "安全食物",
    "harmfulFoods" to "有害食物",
    "cannotEatFoods" to "不可食用",
    "legendSafe" to "安全",
    "legendHarmful" to "有害",
    "legendToxic" to "有毒",
    "food_dog_safe_chicken_name" to "鸡肉",
    "food_dog_safe_chicken_detail" to "煮熟去骨",
    "food_dog_safe_chicken_desc" to "瘦肉蛋白，易消化",
    "food_dog_safe_rice_name" to "米饭",
    "food_dog_safe_rice_detail" to "白米或糙米",
    "food_dog_safe_rice_desc" to "良好的碳水化合物来源",
    "food_dog_safe_carrots_name" to "胡萝卜",
    "food_dog_safe_carrots_detail" to "生的或煮熟的",
    "food_dog_safe_carrots_desc" to "低热量，高纤维",
    "food_dog_safe_apples_name" to "苹果",
    "food_dog_safe_apples_detail" to "去籽",
    "food_dog_safe_apples_desc" to "富含维生素",
    "food_dog_safe_peanutbutter_name" to "花生酱",
    "food_dog_safe_peanutbutter_detail" to "无盐，不含木糖醇",
    "food_dog_safe_peanutbutter_desc" to "良好的蛋白质来源",
    "food_dog_harmful_grapes_name" to "葡萄",
    "food_dog_harmful_grapes_detail" to "所有品种",
    "food_dog_harmful_grapes_desc" to "可能导致肾衰竭",
    "food_dog_harmful_onions_name" to "洋葱",
    "food_dog_harmful_onions_detail" to "所有形式",
    "food_dog_harmful_onions_desc" to "损害红细胞",
    "food_dog_harmful_chocolate_name" to "巧克力",
    "food_dog_harmful_chocolate_detail" to "所有种类",
    "food_dog_harmful_chocolate_desc" to "对狗有毒",
    "food_dog_harmful_garlic_name" to "大蒜",
    "food_dog_harmful_garlic_detail" to "所有形式",
    "food_dog_harmful_garlic_desc" to "导致贫血",
    "food_dog_cannot_xylitol_name" to "木糖醇",
    "food_dog_cannot_xylitol_detail" to "人工甜味剂",
    "food_dog_cannot_xylitol_desc" to "剧毒",
    "food_dog_cannot_macadamia_name" to "澳洲坚果",
    "food_dog_cannot_macadamia_detail" to "所有形式",
    "food_dog_cannot_macadamia_desc" to "导致虚弱",
    "food_dog_cannot_avocado_name" to "牛油果",
    "food_dog_cannot_avocado_detail" to "果核、果皮、全部",
    "food_dog_cannot_avocado_desc" to "含有柿子素",
    "food_dog_cannot_alcohol_name" to "酒精",
    "food_dog_cannot_alcohol_detail" to "所有形式",
    "food_dog_cannot_alcohol_desc" to "剧毒",
    "food_cat_safe_cookedfish_name" to "熟鱼",
    "food_cat_safe_cookedfish_detail" to "去骨",
    "food_cat_safe_cookedfish_desc" to "高蛋白",
    "food_cat_safe_chicken_name" to "鸡肉",
    "food_cat_safe_chicken_detail" to "煮熟，原味",
    "food_cat_safe_chicken_desc" to "良好的蛋白质来源",
    "food_cat_safe_pumpkin_name" to "南瓜",
    "food_cat_safe_pumpkin_detail" to "原味，煮熟",
    "food_cat_safe_pumpkin_desc" to "帮助消化",
    "food_cat_safe_eggs_name" to "鸡蛋",
    "food_cat_safe_eggs_detail" to "煮熟",
    "food_cat_safe_eggs_desc" to "完全蛋白质",
    "food_cat_harmful_raweggs_name" to "生鸡蛋",
    "food_cat_harmful_raweggs_detail" to "含卵白素",
    "food_cat_harmful_raweggs_desc" to "干扰生物素吸收",
    "food_cat_harmful_rawfish_name" to "生鱼",
    "food_cat_harmful_rawfish_detail" to "含硫胺素酶",
    "food_cat_harmful_rawfish_desc" to "分解B族维生素",
    "food_cat_harmful_dogfood_name" to "狗粮",
    "food_cat_harmful_dogfood_detail" to "任何种类",
    "food_cat_harmful_dogfood_desc" to "缺乏牛磺酸",
    "food_cat_harmful_milk_name" to "牛奶",
    "food_cat_harmful_milk_detail" to "大多数猫",
    "food_cat_harmful_milk_desc" to "乳糖不耐受",
    "food_cat_cannot_chocolate_name" to "巧克力",
    "food_cat_cannot_chocolate_detail" to "所有种类",
    "food_cat_cannot_chocolate_desc" to "可可碱中毒",
    "food_cat_cannot_onionsgarlic_name" to "洋葱/大蒜",
    "food_cat_cannot_onionsgarlic_detail" to "所有形式",
    "food_cat_cannot_onionsgarlic_desc" to "损害红细胞",
    "food_cat_cannot_grapesraisins_name" to "葡萄/葡萄干",
    "food_cat_cannot_grapesraisins_detail" to "所有品种",
    "food_cat_cannot_grapesraisins_desc" to "肾脏损伤",
    "food_cat_cannot_alcohol_name" to "酒精",
    "food_cat_cannot_alcohol_detail" to "所有形式",
    "food_cat_cannot_alcohol_desc" to "剧毒",
    "food_bird_safe_seeds_name" to "种子",
    "food_bird_safe_seeds_detail" to "混合品种",
    "food_bird_safe_seeds_desc" to "良好的脂肪来源",
    "food_bird_safe_fruits_name" to "水果",
    "food_bird_safe_fruits_detail" to "大多数品种",
    "food_bird_safe_fruits_desc" to "维生素和矿物质",
    "food_bird_safe_vegetables_name" to "蔬菜",
    "food_bird_safe_vegetables_detail" to "绿叶菜",
    "food_bird_safe_vegetables_desc" to "必需营养素",
    "food_bird_safe_pellets_name" to "颗粒饲料",
    "food_bird_safe_pellets_detail" to "优质配方",
    "food_bird_safe_pellets_desc" to "完整营养",
    "food_bird_harmful_avocado_name" to "牛油果",
    "food_bird_harmful_avocado_detail" to "所有部位",
    "food_bird_harmful_avocado_desc" to "柿子素中毒",
    "food_bird_harmful_fruitpits_name" to "果核",
    "food_bird_harmful_fruitpits_detail" to "苹果、桃子",
    "food_bird_harmful_fruitpits_desc" to "微量氰化物",
    "food_bird_harmful_salt_name" to "盐",
    "food_bird_harmful_salt_detail" to "任何形式",
    "food_bird_harmful_salt_desc" to "对鸟类有毒",
    "food_bird_harmful_caffeine_name" to "咖啡因",
    "food_bird_harmful_caffeine_detail" to "咖啡、茶",
    "food_bird_harmful_caffeine_desc" to "心脏问题",
    "food_bird_cannot_chocolate_name" to "巧克力",
    "food_bird_cannot_chocolate_detail" to "所有种类",
    "food_bird_cannot_chocolate_desc" to "可可碱中毒",
    "food_bird_cannot_onions_name" to "洋葱",
    "food_bird_cannot_onions_detail" to "所有形式",
    "food_bird_cannot_onions_desc" to "损害红细胞",
    "food_bird_cannot_garlic_name" to "大蒜",
    "food_bird_cannot_garlic_detail" to "所有形式",
    "food_bird_cannot_garlic_desc" to "有毒",
    "food_bird_cannot_mushrooms_name" to "蘑菇",
    "food_bird_cannot_mushrooms_detail" to "野生品种",
    "food_bird_cannot_mushrooms_desc" to "可能含有毒素",
    "food_fish_safe_flakes_name" to "片状鱼食",
    "food_fish_safe_flakes_detail" to "优质品牌",
    "food_fish_safe_flakes_desc" to "完整鱼类食品",
    "food_fish_safe_pellets_name" to "颗粒饲料",
    "food_fish_safe_pellets_detail" to "浮性/沉性",
    "food_fish_safe_pellets_desc" to "均衡营养",
    "food_fish_safe_frozenfood_name" to "冷冻饲料",
    "food_fish_safe_frozenfood_detail" to "血虫、卤虫",
    "food_fish_safe_frozenfood_desc" to "蛋白质来源",
    "food_fish_safe_vegetables_name" to "蔬菜",
    "food_fish_safe_vegetables_detail" to "焯水西葫芦",
    "food_fish_safe_vegetables_desc" to "纤维来源",
    "food_fish_harmful_bread_name" to "面包",
    "food_fish_harmful_bread_detail" to "任何种类",
    "food_fish_harmful_bread_desc" to "无营养，容易饱腹",
    "food_fish_harmful_humanfood_name" to "人类食物",
    "food_fish_harmful_humanfood_detail" to "熟食",
    "food_fish_harmful_humanfood_desc" to "营养不适宜",
    "food_fish_harmful_livefeed_name" to "活饵",
    "food_fish_harmful_livefeed_detail" to "野外捕获的昆虫",
    "food_fish_harmful_livefeed_desc" to "可能携带寄生虫",
    "food_fish_cannot_landinsect_name" to "陆生昆虫",
    "food_fish_cannot_landinsect_detail" to "爬行昆虫",
    "food_fish_cannot_landinsect_desc" to "非天然食物",
    "food_fish_cannot_mammalmeat_name" to "哺乳动物肉",
    "food_fish_cannot_mammalmeat_detail" to "鸡肉、牛肉",
    "food_fish_cannot_mammalmeat_desc" to "不适合大多数鱼类",
    "food_fish_cannot_dairy_name" to "乳制品",
    "food_fish_cannot_dairy_detail" to "任何",
    "food_fish_cannot_dairy_desc" to "无法消化",
    "food_fish_cannot_breadcrumbs_name" to "面包屑",
    "food_fish_cannot_breadcrumbs_detail" to "许多含有大蒜",
    "food_fish_cannot_breadcrumbs_desc" to "有害添加剂",
    "food_rabbit_safe_hay_name" to "干草",
    "food_rabbit_safe_hay_detail" to "提摩西草",
    "food_rabbit_safe_hay_desc" to "占饮食的80%",
    "food_rabbit_safe_leafygreens_name" to "绿叶菜",
    "food_rabbit_safe_leafygreens_detail" to "罗马生菜、香菜",
    "food_rabbit_safe_leafygreens_desc" to "每日所需维生素",
    "food_rabbit_safe_pellets_name" to "颗粒饲料",
    "food_rabbit_safe_pellets_detail" to "提摩西草为主",
    "food_rabbit_safe_pellets_desc" to "均衡营养",
    "food_rabbit_safe_carrots_name" to "胡萝卜",
    "food_rabbit_safe_carrots_detail" to "适量",
    "food_rabbit_safe_carrots_desc" to "零食",
    "food_rabbit_harmful_icebergletttuce_name" to "结球生菜",
    "food_rabbit_harmful_icebergletttuce_detail" to "无营养",
    "food_rabbit_harmful_icebergletttuce_desc" to "可能导致肠胃蠕动停滞",
    "food_rabbit_harmful_beans_name" to "豆类",
    "food_rabbit_harmful_beans_detail" to "所有豆科植物",
    "food_rabbit_harmful_beans_desc" to "胀气、消化问题",
    "food_rabbit_harmful_corn_name" to "玉米",
    "food_rabbit_harmful_corn_detail" to "任何形式",
    "food_rabbit_harmful_corn_desc" to "无法消化",
    "food_rabbit_harmful_potatoes_name" to "土豆",
    "food_rabbit_harmful_potatoes_detail" to "任何形式",
    "food_rabbit_harmful_potatoes_desc" to "营养成分不适宜",
    "food_rabbit_cannot_chocolate_name" to "巧克力",
    "food_rabbit_cannot_chocolate_detail" to "所有种类",
    "food_rabbit_cannot_chocolate_desc" to "可可碱中毒",
    "food_rabbit_cannot_onions_name" to "洋葱",
    "food_rabbit_cannot_onions_detail" to "所有形式",
    "food_rabbit_cannot_onions_desc" to "剧毒",
    "food_rabbit_cannot_garlic_name" to "大蒜",
    "food_rabbit_cannot_garlic_detail" to "所有形式",
    "food_rabbit_cannot_garlic_desc" to "有毒",
    "food_rabbit_cannot_avocado_name" to "牛油果",
    "food_rabbit_cannot_avocado_detail" to "所有部位",
    "food_rabbit_cannot_avocado_desc" to "含有柿子素",
    "primary" to "主图",
    "passkeyRegisteredSuccess" to "Passkey 注册成功！",
    "passkeyRegistrationFailed" to "Passkey 注册失败",
    "passkeyNameRequired" to "Passkey 名称是必填项",
    "registeringPasskey" to "正在注册 Passkey...",
    "passkeyRegistered" to "Passkey 已注册",
    "noPasskey" to "未注册 Passkey",
    "noPets" to "暂无宠物。",
    "photographerCountryStateRequired" to "请填写国家和州/省以提供摄影服务",
    "temporalHomeAliasCountryCityRequired" to "请填写别名、国家和城市以设置临时住所",
    "shelterNameCountryCityAddressRequired" to "请填写名称、国家、城市和地址以设置收容所",
    "sterilizationNameCountryCityAddressRequired" to "请填写名称、国家、城市和地址以设置绝育地点",
    "country.afghanistan" to "阿富汗",
    "country.albania" to "阿尔巴尼亚",
    "country.algeria" to "阿尔及利亚",
    "country.argentina" to "阿根廷",
    "country.armenia" to "亚美尼亚",
    "country.australia" to "澳大利亚",
    "country.austria" to "奥地利",
    "country.azerbaijan" to "阿塞拜疆",
    "country.bangladesh" to "孟加拉国",
    "country.belarus" to "白俄罗斯",
    "country.belgium" to "比利时",
    "country.bolivia" to "玻利维亚",
    "country.bosnia" to "波斯尼亚和黑塞哥维那",
    "country.brazil" to "巴西",
    "country.bulgaria" to "保加利亚",
    "country.cambodia" to "柬埔寨",
    "country.cameroon" to "喀麦隆",
    "country.canada" to "加拿大",
    "country.chile" to "智利",
    "country.china" to "中国",
    "country.colombia" to "哥伦比亚",
    "country.costaRica" to "哥斯达黎加",
    "country.croatia" to "克罗地亚",
    "country.cuba" to "古巴",
    "country.czechia" to "捷克共和国",
    "country.denmark" to "丹麦",
    "country.dominicanRepublic" to "多米尼加共和国",
    "country.ecuador" to "厄瓜多尔",
    "country.egypt" to "埃及",
    "country.elSalvador" to "萨尔瓦多",
    "country.estonia" to "爱沙尼亚",
    "country.ethiopia" to "埃塞俄比亚",
    "country.finland" to "芬兰",
    "country.france" to "法国",
    "country.georgia" to "格鲁吉亚",
    "country.germany" to "德国",
    "country.ghana" to "加纳",
    "country.greece" to "希腊",
    "country.guatemala" to "危地马拉",
    "country.haiti" to "海地",
    "country.honduras" to "洪都拉斯",
    "country.hungary" to "匈牙利",
    "country.iceland" to "冰岛",
    "country.india" to "印度",
    "country.indonesia" to "印度尼西亚",
    "country.iran" to "伊朗",
    "country.iraq" to "伊拉克",
    "country.ireland" to "爱尔兰",
    "country.israel" to "以色列",
    "country.italy" to "意大利",
    "country.jamaica" to "牙买加",
    "country.japan" to "日本",
    "country.jordan" to "约旦",
    "country.kazakhstan" to "哈萨克斯坦",
    "country.kenya" to "肯尼亚",
    "country.kuwait" to "科威特",
    "country.latvia" to "拉脱维亚",
    "country.lebanon" to "黎巴嫩",
    "country.libya" to "利比亚",
    "country.lithuania" to "立陶宛",
    "country.luxembourg" to "卢森堡",
    "country.malaysia" to "马来西亚",
    "country.mexico" to "墨西哥",
    "country.moldova" to "摩尔多瓦",
    "country.mongolia" to "蒙古",
    "country.montenegro" to "黑山",
    "country.morocco" to "摩洛哥",
    "country.myanmar" to "缅甸",
    "country.nepal" to "尼泊尔",
    "country.netherlands" to "荷兰",
    "country.newZealand" to "新西兰",
    "country.nicaragua" to "尼加拉瓜",
    "country.nigeria" to "尼日利亚",
    "country.northKorea" to "朝鲜",
    "country.norway" to "挪威",
    "country.pakistan" to "巴基斯坦",
    "country.panama" to "巴拿马",
    "country.paraguay" to "巴拉圭",
    "country.peru" to "秘鲁",
    "country.philippines" to "菲律宾",
    "country.poland" to "波兰",
    "country.portugal" to "葡萄牙",
    "country.puertoRico" to "波多黎各",
    "country.qatar" to "卡塔尔",
    "country.romania" to "罗马尼亚",
    "country.russia" to "俄罗斯",
    "country.saudiArabia" to "沙特阿拉伯",
    "country.serbia" to "塞尔维亚",
    "country.singapore" to "新加坡",
    "country.slovakia" to "斯洛伐克",
    "country.slovenia" to "斯洛文尼亚",
    "country.southAfrica" to "南非",
    "country.southKorea" to "韩国",
    "country.spain" to "西班牙",
    "country.sriLanka" to "斯里兰卡",
    "country.sudan" to "苏丹",
    "country.sweden" to "瑞典",
    "country.switzerland" to "瑞士",
    "country.syria" to "叙利亚",
    "country.taiwan" to "台湾",
    "country.thailand" to "泰国",
    "country.tunisia" to "突尼斯",
    "country.turkey" to "土耳其",
    "country.ukraine" to "乌克兰",
    "country.uae" to "阿拉伯联合酋长国",
    "country.uk" to "英国",
    "country.usa" to "美国",
    "country.uruguay" to "乌拉圭",
    "country.uzbekistan" to "乌兹别克斯坦",
    "country.venezuela" to "委内瑞拉",
    "country.vietnam" to "越南",
    "country.yemen" to "也门",
    "loading" to "加载中...",
    "countryRequired" to "请选择一个国家进行搜索",
    "noSheltersFound" to "暂未找到收容所。稍后再来查看！",
    "visitWebsite" to "访问网站",
    "fiscalId" to "税号",
    "donationInformation" to "捐赠信息",
    "bankName" to "银行",
    "accountHolder" to "账户持有人",
    "accountNumber" to "账号",
    "currency" to "货币",
    "errorLoadingShelters" to "加载收容所时出错",
    "pleaseSelectCountry" to "请选择一个国家",
    "actions" to "操作",
    "addShelter" to "添加收容所",
    "addEditShelter" to "添加/编辑收容所",
    "allStates" to "所有州",
    "confirmDelete" to "确定要删除此地点吗？",
    "confirmDeleteShelter" to "确定要删除此收容所吗？",
    "confirmUnbanUser" to "取消封禁此用户？",
    "confirmResetUserCredentials" to "这将使 {email} 当前的密码和 passkey 失效，并通过电子邮件发送重置链接。是否继续？",
    "confirmDeactivateUser" to "停用此用户？之后可以重新激活。",
    "confirmDeactivatePet" to "停用此宠物？它将从公开列表中隐藏，之后可以重新激活。",
    "confirmBlockRescuer" to "屏蔽此救援者，使其无法再向您发送请求？",
    "confirmDeletePet" to "删除此宠物？",
    "confirmDeleteStorage" to "删除此存储？",
    "contact" to "联系方式",
    "delete" to "删除",
    "edit" to "编辑",
    "errorDeletingShelter" to "删除收容所时出错",
    "errorLoadingLocations" to "加载地点时出错",
    "errorLoadingShelter" to "加载收容所时出错",
    "errorSaving" to "保存时出错",
    "errorSavingShelter" to "保存收容所时出错",
    "location" to "地点",
    "noLocationsFound" to "未找到地点",
    "selectCountry" to "选择一个国家",
    "selectCountryToFilter" to "请选择一个国家进行筛选",
    "shelterAdded" to "收容所添加成功",
    "shelterDeleted" to "收容所删除成功",
    "shelterUpdated" to "收容所更新成功",
    "updateShelter" to "更新收容所",
    "errorLoadingPhotographers" to "加载摄影师时出错",
    "noPhotographersAvailable" to "暂无可用摄影师",
    "requestPhotoSession" to "预约拍摄",
    "selectPhotographer" to "选择",
    "photographersSelected" to "已选择 {count} 位",
    "requestSelectedPhotographers" to "邀请已选摄影师",
    "clearSelection" to "清除选择",
    "requestToMultiple" to "邀请",
    "messageRequired" to "请为摄影师写一条留言。",
    "requestsSentSuccessfully" to "请求发送成功",
    "message" to "留言",
    "sendRequest" to "发送请求",
    "requestSentSuccessfully" to "请求发送成功！",
    "requestTo" to "请求对象",
    "enterMessage" to "为{name}输入留言...",
    "reportUrgent" to "紧急",
    "topRescuers" to "顶尖救援者",
    "urgentRescuerSettings" to "紧急救援者设置",
    "urgentRescueAlerts" to "紧急救援提醒",
    "reportUrgentTitle" to "举报处于危险中的宠物",
    "reportUrgentIntro" to "如果您发现受伤、受虐待、挨饿或被遗弃的宠物急需帮助，请告诉我们它在哪里以及情况如何——我们会立即通知附近的紧急救援者。",
    "dangerType" to "发生了什么？",
    "dangerInjured" to "受伤",
    "dangerAbused" to "正在遭受虐待",
    "dangerStarving" to "正在挨饿",
    "dangerTooYoung" to "太小无法独自存活",
    "dangerOther" to "其他紧急情况",
    "useMyLocation" to "使用我的当前位置",
    "confirmMyLocation" to "确认我的当前位置",
    "locationPermissionHint" to "浏览器将请求您授权共享位置；该位置仅用于通知您附近的报告。",
    "phoneOptional" to "您的电话（可选）",
    "submitReport" to "提交举报",
    "urgentRescuerSettingsTitle" to "紧急救援者设置",
    "urgentRescuerSettingsIntro" to "当您覆盖区域附近报告有宠物处于危险中时，您将收到提醒。第一个接受的人将负责此案。",
    "urgentRescuerActive" to "我可以响应紧急举报",
    "coverageAreaMode" to "覆盖区域",
    "coverageArea" to "覆盖区域",
    "coverageAreaHint" to "确认您的当前位置、拖动图钉，或在下方输入地址——地图和字段会保持同步。",
    "useMyLocationAndRadius" to "使用我的位置 + 半径",
    "pickAZone" to "改为选择国家/州/城市",
    "radiusKm" to "半径（公里）",
    "urgentRescueAlertsTitle" to "紧急救援提醒",
    "urgentRescuerLeaderboardTitle" to "顶尖紧急救援者（最近12个月）",
    "locating" to "定位中...",
    "geolocationUnsupported" to "您的浏览器不支持地理定位",
    "locationCaptured" to "已获取位置",
    "locationDenied" to "无法获取您的位置——请允许访问位置或输入城市",
    "street" to "街道",
    "exteriorNumber" to "门牌号",
    "referenceNotes" to "参考信息（例如房屋颜色、附近地标）",
    "concreteAddressHint" to "如果可以，请填写街道和门牌号——这样救援者才能叫出租车或直接用地图应用导航到现场。",
    "descriptionRequired" to "请描述情况",
    "locationRequired" to "请提供位置信息（使用当前位置或输入国家和城市）",
    "submittingEllipsis" to "提交中...",
    "reportSubmitted" to "举报已提交。谢谢——我们正在通知附近的救援者。",
    "reportSubmitFailed" to "提交举报失败",
    "phoneRequired" to "请输入电话号码",
    "savingEllipsis" to "保存中...",
    "saved" to "已保存",
    "saveFailed" to "保存失败",
    "noUrgentPages" to "目前没有待处理的紧急提醒",
    "accept" to "接受",
    "alreadyAccepted" to "已有其他人接受了此举报",
    "noLeaderboardData" to "最近12个月内还没有已接受的救援",
    "rescueAcceptedTitle" to "已接受救援",
    "rescueAcceptedMessage" to "谢谢——您已接手此案。请尽快前往报告的地点。",
    "rescueAcceptFailedTitle" to "无法接受",
    "rescueAcceptFailedMessage" to "此举报已被其他救援者接受，或链接无效或已过期。",
    "share" to "分享",
    "shareViaFacebook" to "分享到 Facebook",
    "shareViaX" to "分享到 X",
    "shareViaWhatsapp" to "分享到 WhatsApp",
    "shareViaEmail" to "通过邮件分享",
    "copyLink" to "复制链接",
    "linkCopied" to "链接已复制！",
    "reportLostFoundTitle" to "举报走失或发现的宠物",
    "reportLostFoundIntro" to "丢失了您的宠物，或发现了一只流浪宠物？提交举报，我们会将其与附近的其他举报进行比对。",
    "browseLostFound" to "浏览现有的走失与寻回举报",
    "kindLost" to "我丢失了宠物",
    "kindFound" to "我发现了一只宠物",
    "petType" to "宠物类型",
    "petTypeUnknown" to "不确定",
    "lostFoundBrowseTitle" to "走失与寻回宠物",
    "showing" to "显示",
    "kindLostPlural" to "走失的宠物",
    "kindFoundPlural" to "发现的宠物",
    "reportResolvedTitle" to "举报已解决",
    "reportResolvedMessage" to "感谢您的更新——此举报现已关闭。",
    "reportResolveFailedTitle" to "无法解决",
    "reportResolveFailedMessage" to "此举报已被解决，或链接无效或已过期。",
    "reportLostFound" to "走失/\u200B寻回",
    "noLostFoundReportsFound" to "未找到走失或寻回的举报",
    "errorLoadingReports" to "加载举报时出错",
    "yourEmail" to "您的电子邮件",
    "contactReporter" to "联系举报人",
    "contactFormIncomplete" to "请输入您的电子邮件和留言",
    "contactSent" to "消息已发送",
    "contactFailed" to "消息发送失败",
    "lostFoundReportSubmitted" to "举报已提交。谢谢——我们会将其与附近的其他举报进行比对。",
    "video" to "视频（可选）",
    "removeVideo" to "删除视频",
    "saveThisSearch" to "保存此搜索",
    "savedSearchSaved" to "搜索已保存！有新匹配时我们会发邮件通知您。",
    "savedSearchFailed" to "保存搜索失败",
    "favorite" to "收藏",
    "addToFavorites" to "加入收藏",
    "removeFromFavorites" to "取消收藏",
    "myFavorites" to "我的收藏",
    "noFavoritesYet" to "暂无收藏",
    "mySavedSearches" to "我的保存搜索",
    "noSavedSearchesYet" to "暂无保存的搜索",
    "donationPromptText" to "感谢您今天帮助了一只宠物。如果可以的话，请考虑捐款支持，让 Adopt-U 对每个人都保持免费。",
    "donationPromptCta" to "捐款",
    "dismiss" to "关闭",
)

private val arabicTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "تسجيل الدخول",
    "register" to "إنشاء حساب",
    "logout" to "تسجيل الخروج",
    "profile" to "الملف الشخصي",
    "myPets" to "حيواناتي الأليفة",
    "addPet" to "إضافة حيوان أليف",
    "save" to "حفظ",
    "cancel" to "إلغاء",
    "search" to "بحث",
    "shelters" to "الملاجئ",
    "photographers" to "المصورون",
    "temporalHomes" to "البيوت المؤقتة",
    "findTemporalHomes" to "البحث عن بيوت مؤقتة",
    "sterilizationLocations" to "مراكز التعقيم",
    "donate" to "تبرع",
    "countryLabel" to "الدولة",
    "selectCountryToSearch" to "اختر دولة",
    "selectCountryFirst" to "اختر دولة لتفعيل هذه الفلاتر",
    "state" to "الولاية",
    "city" to "المدينة",
    "zipCode" to "الرمز البريدي",
    "neighborhood" to "الحي",
    "name" to "الاسم",
    "email" to "البريد الإلكتروني",
    "phone" to "الهاتف",
    "address" to "العنوان",
    "description" to "الوصف",
    "animalShelters" to "ملاجئ الحيوانات",
    "sheltersDescription" to "ابحث عن ملاجئ الحيوانات حيث يمكنك العثور على حيوانات أليفة للتبني أو تركها باتفاق مسبق مع الملجأ",
    "photographerDescription" to "مصورون محترفون يقدمون جلسات تصوير للحيوانات الأليفة",
    "sterilizationLocationsDescription" to "ابحث عن الأماكن التي يمكنك فيها إحضار الحيوانات لتعقيمها.",
    "privacyPolicy" to "سياسة الخصوصية",
    "privacyLastUpdated" to "آخر تحديث: أبريل 2025",
    "privacyHighlight" to "في Adopt-U، نؤمن بجمع أقل قدر ممكن من البيانات. نقوم فقط بجمع عنوان بريدك الإلكتروني لتمكين التواصل بين المتبنين والمنقذين. نحن لا نستخدم معلوماتك لأغراض إعلانية، ولن نشارك بياناتك أبدًا مع أطراف ثالثة لأغراض تسويقية.",
    "privacyInfoCollectedH2" to "المعلومات التي نجمعها",
    "privacyAccountRegH3" to "تسجيل الحساب",
    "privacyAccountRegIntro" to "لإنشاء حساب على Adopt-U، نقوم بجمع المعلومات التالية:",
    "privacyEmailLabel" to "عنوان البريد الإلكتروني: ",
    "privacyEmailDesc" to "مطلوب للتحقق من الحساب، واسترداد كلمة المرور، والتواصل بين المتبنين والمنقذين",
    "privacyDisplayNameLabel" to "الاسم المعروض: ",
    "privacyDisplayNameDesc" to "اسم تختاره لتُعرف به على المنصة",
    "privacyLangPrefLabel" to "تفضيل اللغة: ",
    "privacyLangPrefDesc" to "لتوفير المنصة بلغتك المفضلة",
    "privacyNoFullNameEtc" to "نحن لا نجمع اسمك الكامل، أو رقم هاتفك، أو عنوانك الفعلي، أو وثائق الهوية، أو معلومات الدفع، أو أي بيانات شخصية أخرى غير المذكورة أعلاه.",
    "privacyOptionalProfileH3" to "معلومات الملف الشخصي الاختيارية",
    "privacyOptionalProfileIntro" to "حسب دورك، يمكنك اختياريًا تقديم:",
    "privacyAdopterLabel" to "كمتبنٍ: ",
    "privacyAdopterDesc" to "لا حاجة لأي معلومات إضافية ولا يتم جمعها",
    "privacyRescuerLabel" to "كمنقذ: ",
    "privacyRescuerDesc" to "معلومات الموقع للحيوانات الأليفة المتاحة لديك للتبني",
    "privacyPhotographerLabel" to "كمصور: ",
    "privacyPhotographerDesc" to "موقع الخدمة وتفضيلات الاتصال",
    "privacyTemporalHomeLabel" to "كبيت مؤقت: ",
    "privacyTemporalHomeDesc" to "الموقع الذي يمكنك فيه توفير سكن مؤقت",
    "privacyLocationUsage" to "تُستخدم جميع معلومات الموقع حصريًا لمطابقة المتبنين بالمنقذين ومقدمي الخدمات القريبين منهم.",
    "privacyPetInfoH3" to "معلومات الحيوان الأليف",
    "privacyPetInfoIntro" to "قد ينشر المنقذون معلومات عن الحيوانات الأليفة، بما في ذلك:",
    "privacyPetPhotos" to "صور الحيوان الأليف",
    "privacyPetDescBreedAge" to "الوصف والسلالة والعمر والخصائص",
    "privacyPetHealth" to "الحالة الصحية وحالة التطعيمات",
    "privacyPetLocation" to "الموقع الذي يتوفر فيه الحيوان الأليف",
    "privacyPetInfoNotVerified" to "يتم تقديم هذه المعلومات من قبل المنقذين ولا يتم التحقق منها بشكل مستقل من قبل Adopt-U.",
    "privacyHowWeUseH2" to "كيف نستخدم معلوماتك",
    "privacyPrimaryPurposeH3" to "الغرض الأساسي: تسهيل التواصل من أجل التبني",
    "privacyEmailUsedFor" to "يُستخدم عنوان بريدك الإلكتروني حصريًا للأغراض التالية:",
    "privacyUseVerification" to "إرسال رابط تحقق إليك عند إنشاء حساب",
    "privacyUseRescuerContact" to "السماح للمنقذين بالتواصل معك بشأن الحيوانات الأليفة التي تهتم بها",
    "privacyUseContactRescuer" to "السماح لك بالتواصل مع المنقذين بشأن حيواناتهم المدرجة",
    "privacyUseNotifications" to "إرسال إشعارات مهمة متعلقة بالحساب والأمان",
    "privacyOnceConnected" to "بمجرد تواصلك مع مستخدم آخر عبر منصتنا، تتم جميع الاتصالات اللاحقة مباشرة بينك وبين ذلك الطرف. نحن لا نراقب أو نخزن أو نصل إلى محتوى اتصالاتك.",
    "privacyNeverDoH3" to "ما لن نفعله أبدًا",
    "privacyCommitIntro" to "نلتزم رسميًا بما يلي:",
    "privacyNoAdsLabel" to "لا إعلانات: ",
    "privacyNoAdsDesc" to "لن نعرض أبدًا إعلانات مستهدفة أو غير مستهدفة بناءً على نشاطك",
    "privacyNoMarketingLabel" to "لا تسويق: ",
    "privacyNoMarketingDesc" to "لن نشارك بريدك الإلكتروني أبدًا مع أطراف ثالثة لأغراض تسويقية أو ترويجية",
    "privacyNoSellingLabel" to "لا بيع للبيانات: ",
    "privacyNoSellingDesc" to "لن نبيع أو نؤجر أو ننقل معلوماتك الشخصية بأي شكل من الأشكال إلى أي طرف ثالث",
    "privacyNoProfilingLabel" to "لا تنميط: ",
    "privacyNoProfilingDesc" to "لن نستخدم بياناتك لإنشاء ملفات تعريف لأي غرض يتجاوز إدارة الحساب الأساسية",
    "privacyNoTrackingLabel" to "لا تتبع: ",
    "privacyNoTrackingDesc" to "نحن لا نستخدم بكسلات التتبع، أو ملفات تعريف الارتباط الإعلانية، أو أي تقنيات تتبع مماثلة",
    "privacyDataStorageH2" to "تخزين البيانات وأمانها",
    "privacyDataStoredSecurely" to "يتم تخزين بياناتك بأمان باستخدام تشفير يلبي معايير الصناعة. نتبع تدابير تقنية وتنظيمية مناسبة لحماية معلوماتك الشخصية من الوصول غير المصرح به أو التعديل أو الإفصاح أو الإتلاف.",
    "privacyDataRetention" to "نحتفظ بمعلومات حسابك طالما ظل حسابك نشطًا. يمكنك طلب حذف حسابك والبيانات المرتبطة به في أي وقت عن طريق التواصل معنا.",
    "privacyYourRightsH2" to "حقوقك",
    "privacyRightsIntro" to "لديك الحقوق التالية فيما يتعلق ببياناتك الشخصية:",
    "privacyRightAccess" to "الوصول: يمكنك طلب نسخة من جميع البيانات الشخصية التي نحتفظ بها عنك",
    "privacyRightCorrection" to "التصحيح: يمكنك طلب تصحيح أي معلومات غير دقيقة",
    "privacyRightDeletion" to "الحذف: يمكنك طلب حذف حسابك وجميع البيانات المرتبطة به",
    "privacyRightPortability" to "قابلية النقل: يمكنك طلب تزويدك ببياناتك بتنسيق شائع الاستخدام",
    "privacyExerciseRights" to "لممارسة أي من هذه الحقوق، يرجى التواصل معنا على adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "ملفات تعريف الارتباط",
    "privacyCookiesText" to "يستخدم Adopt-U الحد الأدنى من ملفات تعريف الارتباط اللازمة لعمل المنصة، بما في ذلك إدارة الجلسات وتفضيلات اللغة. نحن لا نستخدم ملفات تعريف ارتباط إعلانية أو ملفات تعريف ارتباط للتتبع.",
    "privacyThirdPartyH2" to "خدمات الأطراف الثالثة",
    "privacyThirdPartyIntro" to "نستخدم خدمات الأطراف الثالثة التالية، والتي قد تعالج بياناتك:",
    "privacyAws" to "AWS (خدمات أمازون ويب): لتخزين البيانات وتسليم البريد الإلكتروني. تنطبق سياسات الخصوصية الخاصة بها على معالجتها لبياناتك.",
    "privacyWebauthn" to "WebAuthn/FIDO2: للمصادقة الآمنة بمفتاح المرور. يتضمن ذلك عمليات تشفيرية في متصفحك.",
    "privacyThirdPartySelected" to "لقد اخترنا بعناية مقدمي خدمات يشاركوننا التزامنا بخصوصية البيانات.",
    "privacyChildrensH2" to "خصوصية الأطفال",
    "privacyChildrensText" to "منصة Adopt-U غير مخصصة للمستخدمين دون سن 18 عامًا. نحن لا نجمع معلومات من القاصرين عن علم. إذا كنت تعتقد أن قاصرًا قد أنشأ حسابًا، يرجى التواصل معنا لإزالته.",
    "privacyChangesH2" to "التغييرات على هذه السياسة",
    "privacyChangesText" to "قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر لتعكس التغييرات في ممارساتنا أو المتطلبات القانونية. سنقوم بإخطار المستخدمين بالتغييرات الجوهرية عبر البريد الإلكتروني أو إشعار بارز على المنصة.",
    "privacyContactH2" to "تواصل معنا",
    "privacyContactText" to "إذا كانت لديك أي أسئلة أو استفسارات حول سياسة الخصوصية هذه، أو لممارسة حقوقك المتعلقة بالبيانات، يرجى التواصل معنا على adopt-u@adopt-u.org.",
    "termsConditions" to "الشروط والأحكام",
    "installApp" to "تثبيت التطبيق",
    "termsAcceptanceH2" to "قبول الشروط",
    "termsAcceptanceText" to "من خلال الوصول إلى Adopt-U واستخدامه، فإنك تقبل وتوافق على الالتزام بشروط وأحكام هذه الاتفاقية. إذا كنت لا توافق على هذه الشروط، يرجى عدم استخدام منصتنا.",
    "termsNatureH2" to "طبيعة خدمتنا",
    "termsNatureText1" to "Adopt-U هي منصة تواصل تسهل الاتصال بين منقذي الحيوانات الأليفة والمتبنين المحتملين. نحن لسنا متجرًا لبيع الحيوانات الأليفة، أو وسيطًا، أو ملجأً، أو وكالة تبني. نحن لا نملك أو نحتفظ بحضانة أو نتوسط بشكل مباشر في أي حيوانات مدرجة على منصتنا.",
    "termsNatureText2" to "يقتصر دورنا بشكل صارم على توفير قناة اتصال بين الأطراف. نحن لا نشارك في مفاوضات التبني، أو العقود، أو النقل الفعلي للحيوانات.",
    "termsUserRespH2" to "مسؤوليات المستخدم",
    "termsForRescuersH3" to "للمنقذين:",
    "termsRescuerResp1" to "قدّم معلومات دقيقة وصادقة عن الحيوانات الأليفة التي تحت رعايتك",
    "termsRescuerResp2" to "تأكد من أن الحيوانات الأليفة سليمة صحيًا وملقحة وخضعت لفحص بيطري مناسب قبل إدراجها",
    "termsRescuerResp3" to "قم بإجراء فحص مسؤول للمتبنين المحتملين",
    "termsRescuerResp4" to "رتّب نقلًا آمنًا للحيوانات الأليفة إلى بيوتها الجديدة",
    "termsRescuerResp5" to "تحمّل مسؤولية دقة جميع المعلومات المقدمة",
    "termsRescuerResp6" to "امتثل لجميع القوانين واللوائح المحلية المتعلقة بتبني الحيوانات الأليفة",
    "termsRescuerResp7" to "لا تستخدم المنصة أبدًا لبيع الحيوانات الأليفة تجاريًا أو لأغراض التربية",
    "termsForAdoptersH3" to "للمتبنين:",
    "termsAdopterResp1" to "قدّم معلومات دقيقة عن وضعك المعيشي وخبرتك مع الحيوانات الأليفة",
    "termsAdopterResp2" to "كن مستعدًا لإثبات قدرتك على تقديم الرعاية المناسبة",
    "termsAdopterResp3" to "افهم أن التراجع عن تبني حيوان أليف يسبب ضغطًا كبيرًا للحيوان",
    "termsAdopterResp4" to "احترم حق المنقذ في رفض طلب التبني الخاص بك",
    "termsPetListingsH2" to "قوائم الحيوانات الأليفة والمعلومات",
    "termsPetListingsText1" to "لا يتحقق Adopt-U من دقة المعلومات المقدمة في قوائم الحيوانات الأليفة ولا يصادق عليها أو يضمنها. تُقدَّم المعلومات، بما في ذلك على سبيل المثال لا الحصر السلالة والعمر والحالة الصحية والمزاج وسجلات التطعيم، من قبل المنقذين ولم يتم التحقق منها بشكل مستقل.",
    "termsPetListingsText2" to "الصور والأوصاف مقدمة من قبل المنقذين. بينما نحظر التحريف المتعمد، لا يمكننا ضمان أن جميع القوائم تمثل بدقة الحالة الحالية للحيوان.",
    "termsPetListingsText3" to "يُشجَّع المستخدمون على طرح الأسئلة وطلب السجلات البيطرية ومقابلة الحيوانات الأليفة قبل إتمام أي ترتيب للتبني.",
    "termsAdoptionProcessH2" to "عملية التبني",
    "termsAdoptionProcessIntro" to "بمجرد موافقة المنقذ والمتبني على المضي قدمًا في التبني:",
    "termsAdoptionStep1" to "يتم التفاوض على جميع الترتيبات، بما في ذلك تاريخ ومكان النقل وأي تكاليف مرتبطة، مباشرة بين الطرفين",
    "termsAdoptionStep2" to "لا يسهّل Adopt-U أو يضمن أي معاملات دفع",
    "termsAdoptionStep3" to "لا توفر المنصة عقود تبني؛ وأي ترتيبات تعاقدية هي مسؤولية الأطراف المعنية وحدها",
    "termsAdoptionStep4" to "نوصي بشدة بتوثيق جميع الاتفاقيات كتابيًا",
    "termsLiabilityH2" to "تحديد المسؤولية",
    "termsLiabilityIntro" to "لن يتحمل Adopt-U المسؤولية عن:",
    "termsLiability1" to "أي نزاعات أو خلافات أو صراعات بين المنقذين والمتبنين",
    "termsLiability2" to "دقة المعلومات المقدمة في قوائم الحيوانات الأليفة",
    "termsLiability3" to "صحة أو سلوك أو حالة أي حيوان قبل أو أثناء أو بعد التبني",
    "termsLiability4" to "أي خسائر مالية أو أضرار أو إصابات ناتجة عن استخدام المنصة",
    "termsLiability5" to "نتيجة أي عملية تبني، بما في ذلك على سبيل المثال لا الحصر الإعادة أو المشاكل الصحية أو مشاكل التوافق",
    "termsLiability6" to "أي إجراءات يتخذها المستخدمون خارج المنصة بعد إقامة الاتصال",
    "termsLiabilityAck" to "يقر المستخدمون بأن تبني حيوان أليف التزام كبير وأنهم يتحملون المسؤولية الكاملة عن قرارهم بالتبني.",
    "termsProhibitedH2" to "الأنشطة المحظورة",
    "termsProhibitedIntro" to "الأنشطة التالية محظورة تمامًا على Adopt-U:",
    "termsProhibited1" to "بيع الحيوانات الأليفة تجاريًا، أو عمليات التربية، أو مصانع الجراء",
    "termsProhibited2" to "إدراج حيوانات أليفة لا ترعاها مباشرة",
    "termsProhibited3" to "معلومات كاذبة أو مضللة في القوائم أو الملفات الشخصية",
    "termsProhibited4" to "التحرش أو التمييز أو التواصل غير اللائق مع المستخدمين الآخرين",
    "termsProhibited5" to "استخدام المنصة لجمع معلومات شخصية لأغراض غير متعلقة بتبني الحيوانات الأليفة",
    "termsProhibited6" to "أي أنشطة غير قانونية أو انتهاكات لقوانين رفاهية الحيوان",
    "termsAccountTerminationH2" to "إنهاء الحساب",
    "termsAccountTerminationText" to "يحتفظ Adopt-U بالحق في تعليق أو إنهاء الحسابات التي تنتهك هذه الشروط، أو تشارك في أنشطة محظورة، أو تسيء لسمعة المنصة. كما يجوز لنا إزالة القوائم التي تحتوي على محتوى كاذب أو مضلل أو غير لائق.",
    "termsModificationsH2" to "التعديلات على الشروط",
    "termsModificationsText" to "نحتفظ بالحق في تعديل هذه الشروط في أي وقت. يشكل استمرار استخدام المنصة بعد إجراء التغييرات قبولًا للشروط المعدلة. سنقوم بإخطار المستخدمين بالتغييرات الجوهرية عبر البريد الإلكتروني أو إعلانات المنصة.",
    "termsContactText" to "إذا كانت لديك أي أسئلة حول هذه الشروط والأحكام، يرجى التواصل معنا على adopt-u@adopt-u.org.",
    "language" to "اللغة",
    "displayName" to "الاسم المعروض",
    "saveProfile" to "حفظ الملف الشخصي",
    "accountSettings" to "إعدادات الحساب",
    "yourRoles" to "أدوارك:",
    "resources" to "الموارد",
    "loginWithPasskey" to "تسجيل الدخول باستخدام مفتاح المرور",
    "signInWithPasskey" to "تسجيل الدخول بمفتاح المرور",
    "resendVerificationEmail" to "إعادة إرسال بريد التحقق",
    "usesFido" to "يستخدم FIDO2 / WebAuthn للمصادقة الآمنة بدون كلمة مرور.",
    "loginWithMagicLink" to "تسجيل الدخول برابط البريد الإلكتروني",
    "magicLinkDesc" to "سنرسل لك رابط تسجيل دخول إلى بريدك الإلكتروني (صالح لمدة 5 دقائق).",
    "sendMagicLink" to "إرسال رابط تسجيل الدخول",
    "loginWithPassword" to "تسجيل الدخول بكلمة المرور",
    "signIn" to "تسجيل الدخول",
    "forgotPassword" to "نسيت كلمة المرور؟",
    "registerNewAccount" to "إنشاء حساب",
    "accountDetails" to "تفاصيل الحساب",
    "registrationMethod" to "طريقة التسجيل",
    "selectAdditionalRoles" to "اختر أدوارًا إضافية:",
    "adoptPet" to "تبني حيوان أليف",
    "publishPets" to "نشر حيوانات أليفة للتبني",
    "offerPhotography" to "تقديم خدمات تصوير",
    "photographerSettings" to "إعدادات المصور",
    "sessionFee" to "رسوم الجلسة",
    "provideTemporaryHome" to "توفير بيت مؤقت للحيوانات الأليفة",
    "passkeyNoPassword" to "مفاتيح المرور أكثر أمانًا من كلمات المرور وتعمل على جميع أجهزتك.",
    "registerWithPasskey" to "التسجيل بمفتاح المرور",
    "passkeySettings" to "إعدادات مفتاح المرور",
    "password" to "كلمة المرور",
    "confirmPassword" to "تأكيد كلمة المرور",
    "passwordRequirements" to "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل وتحتوي على حرف كبير وحرف صغير ورقم ورمز",
    "securityInfo" to "معلومات الأمان",
    "passkeyMostSecure" to "مفتاح المرور: الأكثر أمانًا، موصى به",
    "passkeyBrowserCompat" to "قد لا يعمل مفتاح المرور على جميع المتصفحات",
    "passwordLeastSecure" to "كلمة المرور: أقل أمانًا، غير موصى بها",
    "selectLoginMethods" to "اختر طريقة واحدة أو أكثر لتسجيل الدخول:",
    "registerPasskeyOption" to "مفتاح المرور (الأكثر أمانًا، يعمل على جميع أجهزتك)",
    "registerPasswordOption" to "كلمة المرور (أقل أمانًا، تُستخدم فقط كخيار احتياطي)",
    "howPasskeysWorkTitle" to "كيف يعمل مفتاح المرور؟",
    "howPasskeysWorkIntro" to "يستبدل مفتاح المرور كلمة مرورك بميزة الأمان المدمجة في جهازك - بصمة الإصبع، أو التعرف على الوجه، أو رمز قفل الشاشة.",
    "howPasskeysWorkSecurity" to "يُخزَّن فقط على جهازك، ولا يمكن اختراقه بالتصيد الاحتيالي أو تسريبه في اختراق للبيانات، ويتزامن تلقائيًا عبر أجهزتك.",
    "howPasskeysWorkBackup" to "يمكنك أيضًا إضافة كلمة مرور، لكن مفتاح المرور وحده كافٍ لتسجيل الدخول - دون الحاجة إلى كلمة مرور.",
    "gotIt" to "فهمت",
    "alreadyHaveAccount" to "هل لديك حساب بالفعل؟",
    "all" to "الكل",
    "filterDogs" to "الكلاب",
    "filterCats" to "القطط",
    "filterBirds" to "الطيور",
    "filterFish" to "الأسماك",
    "filterAllSexOption" to "كل الأجناس",
    "filterMaleOption" to "♂ ذكر",
    "filterFemaleOption" to "♀ أنثى",
    "age" to "العمر",
    "rescued" to "تم إنقاذه",
    "years" to "سنوات",
    "months" to "أشهر",
    "year" to "سنة",
    "month" to "شهر",
    "loginToAdopt" to "سجّل الدخول لطلب التبني",
    "requiredMarker" to "(مطلوب)",
    "pageNotFoundTitle" to "الصفحة غير موجودة",
    "pageNotFoundHint" to "قد يكون الرابط قديمًا أو مكتوبًا بشكل خاطئ. الحيوانات ما زالت هنا.",
    "browsePets" to "تصفح الحيوانات",
    "requestsFromRescuers" to "طلبات المنقذين",
    "signingOut" to "جارٍ تسجيل الخروج...",
    "adopter" to "المتبني",
    "country" to "الدولة",
    "zip" to "الرمز البريدي",
    "viewPublic" to "عرض الصفحة العامة",
    "banUser" to "حظر المستخدم",
    "banReason" to "السبب (اختياري)",
    "myPhotoRequests" to "طلبات جلسات التصوير الخاصة بي",
    "noPhotoRequestsYet" to "لم تطلب جلسة تصوير بعد.",
    "photoRequestsReceived" to "طلبات التصوير المستلمة",
    "noPhotoRequestsReceived" to "لا توجد طلبات تصوير بعد.",
    "photoStatusPending" to "قيد الانتظار",
    "photoStatusApproved" to "مقبول",
    "photoStatusRejected" to "مرفوض",
    "photoStatusCancelled" to "ملغى",
    "photoStatusCompleted" to "مكتمل",
    "cancelRequest" to "إلغاء الطلب",
    "registerConsentPrefix" to "بإنشاء حساب فإنك توافق على",
    "registerConsentTerms" to "الشروط والأحكام",
    "registerConsentAnd" to "و",
    "registerConsentPrivacy" to "سياسة الخصوصية",
    "petStatusAvailable" to "متاح",
    "petStatusAdopted" to "تم تبنيه",
    "petStatusDisabled" to "معطل",
    "petStatusPending" to "قيد الانتظار",
    "addEditLocation" to "إضافة/تعديل الموقع",
    "addLocation" to "إضافة موقع",
    "manage" to "إدارة",
    "adminPanel" to "لوحة الإدارة",
    "adminSterilizationLocations" to "إدارة مراكز التعقيم",
    "adopterRequired" to "المتبني (مطلوب)",
    "adoptionFee" to "رسوم التبني",
    "adoptionRequests" to "طلبات التبني",
    "ageMonths" to "العمر (أشهر)",
    "ageYears" to "العمر (سنوات)",
    "alias" to "الاسم المستعار",
    "bird" to "طائر",
    "breed" to "السلالة",
    "medicalEventsOverview" to "نظرة عامة على الأحداث الطبية",
    "back" to "رجوع",
    "cat" to "قطة",
    "color" to "اللون",
    "compatibility" to "التوافق",
    "currentPassword" to "كلمة المرور الحالية",
    "dog" to "كلب",
    "emailChange" to "تغيير البريد الإلكتروني",
    "emailChangeDesc" to "قم بتحديث عنوان بريدك الإلكتروني. سيُرسل رابط تحقق إلى البريد الإلكتروني الجديد.",
    "energyLevel" to "مستوى الطاقة",
    "female" to "أنثى",
    "findTemporalHome" to "البحث عن بيت مؤقت",
    "fish" to "سمكة",
    "goodWithCats" to "يتوافق مع القطط",
    "goodWithDogs" to "يتوافق مع الكلاب",
    "goodWithKids" to "يتوافق مع الأطفال",
    "high" to "مرتفع",
    "houseTrained" to "مدرب على استخدام المرحاض",
    "large" to "كبير",
    "low" to "منخفض",
    "male" to "ذكر",
    "managePets" to "إدارة الحيوانات الأليفة",
    "managePetsBtn" to "إضافة أو تعديل الحيوانات الأليفة",
    "managePetsDescription" to "إدارة جميع صفحات الحيوانات الأليفة. إضافة أو إزالة الحيوانات الأليفة.",
    "manageShelters" to "إدارة الملاجئ",
    "manageSheltersBtn" to "إضافة أو تعديل الملاجئ",
    "manageSheltersDescription" to "إدارة جميع صفحات الملاجئ. إضافة أو إزالة الملاجئ.",
    "manageUsers" to "إدارة المستخدمين",
    "medical" to "طبي",
    "medium" to "متوسط",
    "backToSearch" to "العودة إلى البحث",
    "contactThisHome" to "تواصل مع هذا البيت المؤقت",
    "loginAsRescuerToContact" to "سجّل الدخول كمنقذ لإرسال طلب إلى هذا البيت المؤقت.",
    "memberSince" to "عضو منذ",
    "microchipId" to "رقم الشريحة الإلكترونية",
    "microchipped" to "مزود بشريحة إلكترونية",
    "myTemporalHome" to "بيتي المؤقت",
    "newEmail" to "البريد الإلكتروني الجديد",
    "noTemporalHomes" to "لم يتم العثور على بيوت مؤقتة.",
    "requestSentToHome" to "تم إرسال طلبك!",
    "sendRequestBtn" to "إرسال الطلب",
    "temporalHomeNotFound" to "تعذر العثور على هذا البيت المؤقت.",
    "viewDetails" to "عرض التفاصيل",
    "rescuerDirectory" to "المنقذون",
    "noRescuersFound" to "لم يتم العثور على منقذين.",
    "availablePetsCount" to "الحيوانات الأليفة المتاحة",
    "availablePets" to "الحيوانات الأليفة المتاحة",
    "noAvailablePetsForRescuer" to "لا يوجد لدى هذا المنقذ حيوانات أليفة متاحة حاليًا.",
    "rescuerNotFound" to "تعذر العثور على هذا المنقذ.",
    "errorLoadingResults" to "خطأ في تحميل النتائج.",
    "volunteerForThisRescuer" to "تطوّع لدى هذا المنقذ",
    "volunteerExplanation" to "يمكن للمتطوعين المعتمدين اقتراح تعديلات على قوائم حيوانات هذا المنقذ.",
    "applyToVolunteerBtn" to "التقدم للتطوع",
    "loginToVolunteer" to "سجّل الدخول للتقدم للتطوع لدى هذا المنقذ.",
    "volunteerApplicationSent" to "تم إرسال طلب التطوع الخاص بك!",
    "myVolunteering" to "تطوعي",
    "noVolunteerApplicationsYet" to "لم تتقدم بعد للتطوع لدى أي منقذ.",
    "howToVolunteerHint" to "للتقديم، افتح صفحة أحد المنقذين من الدليل ثم اضغط \"التقدم كمتطوع\".",
    "volunteerStatusPending" to "قيد الانتظار",
    "volunteerStatusActive" to "نشط",
    "volunteerStatusRejected" to "مرفوض",
    "volunteerApplications" to "طلبات التطوع",
    "noVolunteerApplications" to "لا توجد طلبات تطوع.",
    "petEditSuggestions" to "اقتراحات تعديل الحيوانات الأليفة",
    "noPetEditSuggestions" to "لا توجد اقتراحات تعديل معلقة.",
    "suggestedByLabel" to "اقترحه",
    "suggestAnEditTitle" to "اقتراح تعديل",
    "suggestAnEditExplanation" to "بصفتك متطوعًا معتمدًا، يمكنك اقتراح تغييرات على هذه القائمة. سيراجعها المنقذ قبل نشرها.",
    "suggestEditBtn" to "إرسال الاقتراح",
    "editSuggestionSent" to "تم إرسال اقتراحك إلى المنقذ للمراجعة!",
    "sponsorThisPetTitle" to "كفالة هذا الحيوان الأليف",
    "sponsorExplanation" to "لا يمكنك التبني لكن تريد المساعدة في النفقات؟ قدّم مالًا أو دعمًا عينيًا وتواصل مباشرة مع المنقذ.",
    "sponsorThisRescuerTitle" to "كفالة هذا المنقذ",
    "sponsorRescuerExplanation" to "ادعم التكاليف العامة لهذا المنقذ بالمال أو المساعدة العينية.",
    "sponsorTypeLabel" to "ماذا يمكنك أن تقدم؟",
    "sponsorTypeMoney" to "مال",
    "sponsorTypeInKind" to "عيني (طعام، لوازم، إلخ.)",
    "amountLabel" to "المبلغ (شهريًا)",
    "currencyLabel" to "العملة",
    "inKindDescriptionLabel" to "صف ما يمكنك تقديمه",
    "messageLabel" to "الرسالة",
    "sendOfferBtn" to "إرسال العرض",
    "sponsorOfferSent" to "تم إرسال عرضك! سيتواصل معك المنقذ لترتيب التفاصيل.",
    "sponsorshipOffersReceived" to "عروض الكفالة",
    "noSponsorshipOffers" to "لا توجد عروض كفالة بعد.",
    "generalFundLabel" to "الصندوق العام",
    "offeringLabel" to "العرض",
    "sponsorshipStatusSent" to "جديد",
    "sponsorshipStatusRead" to "مقروء",
    "markAsReadBtn" to "وضع علامة كمقروء",
    "mySponsorshipOffers" to "عروض الكفالة الخاصة بي",
    "noSponsorshipOffersYet" to "لم ترسل أي عروض كفالة بعد.",
    "howToSponsorHint" to "للرعاية، افتح صفحة أحد المنقذين (دعم عام) أو صفحة حيوان أليف (تكاليف ذلك الحيوان) وأرسل عرضًا.",
    "browsePetsLink" to "تصفح الحيوانات الأليفة",
    "yourMessage" to "رسالتك",
    "newPassword" to "كلمة المرور الجديدة",
    "passkeyDesc" to "أضف مفاتيح مرور إضافية إلى حسابك لتسجيل دخول سهل عبر الأجهزة.",
    "passkeyName" to "اسم مفتاح المرور",
    "pers" to "أشخاص",
    "petFoodDescription" to "تعرف على الأطعمة الآمنة أو الضارة أو السامة لحيواناتك الأليفة",
    "petFoodGuide" to "دليل طعام الحيوانات الأليفة",
    "foodInformation" to "معلومات عن الطعام",
    "petsForAdoption" to "حيوانات أليفة للتبني",
    "chooseCountryPrompt" to "اعثر على أفضل صديق جديد لك",
    "chooseCountryPromptHint" to "اختر دولة أعلاه لتصفح الحيوانات الأليفة المتاحة للتبني بالقرب منك.",
    "noPetsFound" to "لم يتم العثور على حيوانات أليفة",
    "noPetsFoundHint" to "جرّب فلترًا مختلفًا أو تحقق مرة أخرى قريبًا - تتم إضافة حيوانات أليفة جديدة بانتظام.",
    "loadingMorePets" to "جارٍ تحميل المزيد من الحيوانات الأليفة…",
    "photos" to "الصور (بحد أقصى 12)",
    "profilePhotographerRole" to "مصور - تقديم خدمات تصوير",
    "profileRescuerRole" to "منقذ - نشر حيوانات أليفة للتبني",
    "profileTemporalHomeRole" to "بيت مؤقت - توفير بيت مؤقت للحيوانات الأليفة",
    "provideShelter" to "ملجأ - إدارة ملجأ للحيوانات",
    "provideSterilization" to "خدمة تعقيم - تقديم خدمات التعقيم",
    "requestEmailChange" to "طلب تغيير البريد الإلكتروني",
    "rescueDate" to "تاريخ الإنقاذ",
    "rescueLocation" to "موقع الإنقاذ",
    "savePassword" to "حفظ كلمة المرور",
    "selectEnergy" to "اختر مستوى الطاقة",
    "selectSize" to "اختر الحجم",
    "sex" to "الجنس",
    "shelterSettings" to "إعدادات الملجأ",
    "size" to "الحجم",
    "small" to "صغير",
    "specialNeeds" to "احتياجات خاصة",
    "sterilizationSettings" to "إعدادات التعقيم",
    "sterilized" to "معقّم",
    "temporalHomeSettings" to "إعدادات البيت المؤقت",
    "temperament" to "المزاج",
    "type" to "النوع *",
    "urgentAdoption" to "تبني عاجل مطلوب",
    "vaccinations" to "التطعيمات",
    "vetDisclaimer" to "استشر طبيبك البيطري قبل إجراء أي تغييرات غذائية.",
    "weight" to "الوزن (كجم)",
    "weightLabel" to "الوزن",
    "website" to "الموقع الإلكتروني",
    "securitySettings" to "إعدادات الأمان",
    "emailVerifiedTitle" to "تم التحقق من البريد الإلكتروني بنجاح!",
    "emailVerifiedMessage" to "تم التحقق من بريدك الإلكتروني. تسجيلك مكتمل الآن.",
    "redirectingIn" to "جارٍ إعادة التوجيه إلى الصفحة الرئيسية خلال ",
    "canAlsoLogin" to "يمكنك أيضًا ",
    "loginNow" to "تسجيل الدخول الآن",
    "emailVerificationFailed" to "فشل التحقق",
    "invalidOrExpiredToken" to "رابط التحقق غير صالح أو منتهي الصلاحية.",
    "tryRegisteringAgain" to "يرجى محاولة التسجيل مرة أخرى.",
    "verificationEmailResent" to "انتهت صلاحية بريد التحقق. تم إرسال بريد تحقق جديد. يرجى مراجعة صندوق الوارد الخاص بك.",
    "emailVerificationSent" to "تم إرسال بريد تحقق. يرجى مراجعة صندوق الوارد قبل تسجيل الدخول.",
    "emailNotVerifiedLogin" to "يرجى التحقق من بريدك الإلكتروني قبل تسجيل الدخول. تحقق من صندوق الوارد للحصول على رابط التحقق.",
    "invalidOrExpiredMagicLink" to "رابط تسجيل الدخول غير صالح أو منتهي الصلاحية. يرجى طلب رابط جديد.",
    "invalidToken" to "رمز غير صالح.",
    "accountBanned" to "تم حظر حسابك.",
    "loginError" to "حدث خطأ في تسجيل الدخول.",
    "profileUpdated" to "تم تحديث الملف الشخصي!",
    "displayNameRequired" to "لا يمكن أن يكون الاسم المعروض فارغًا",
    "saving" to "جارٍ الحفظ...",
    "passwordSet" to "تم تعيين كلمة المرور",
    "noPassword" to "لم يتم تعيين كلمة مرور",
    "passwordChangeSuccess" to "تم تغيير كلمة المرور بنجاح!",
    "passwordChangeFailed" to "فشل تغيير كلمة المرور",
    "passwordSetSuccess" to "تم تعيين كلمة المرور بنجاح!",
    "passwordSetFailed" to "فشل تعيين كلمة المرور",
    "passwordsDoNotMatch" to "كلمتا المرور غير متطابقتين",
    "currentPasswordRequired" to "كلمة المرور الحالية مطلوبة",
    "emailChangeSent" to "تم إرسال بريد تحقق إلى عنوان بريدك الإلكتروني الجديد!",
    "emailChangeFailed" to "فشل طلب تغيير البريد الإلكتروني",
    "emailRequired" to "البريد الإلكتروني مطلوب",
    "sendingEllipsis" to "جارٍ الإرسال...",
    "resetLinkSent" to "تم إرسال رابط إعادة تعيين كلمة المرور! تحقق من بريدك الإلكتروني.",
    "magicLinkSent" to "تم إرسال رابط تسجيل الدخول! تحقق من بريدك الإلكتروني.",
    "magicLinkSendFailed" to "فشل إرسال رابط تسجيل الدخول",
    "failedSendResetLink" to "فشل إرسال رابط إعادة التعيين.",
    "invalidOrMissingToken" to "رمز غير صالح أو مفقود.",
    "passwordTooShort" to "يجب أن تتكون كلمة المرور من 8 أحرف على الأقل.",
    "passwordNeedUppercase" to "تحتاج كلمة المرور إلى حرف كبير واحد على الأقل.",
    "passwordNeedLowercase" to "تحتاج كلمة المرور إلى حرف صغير واحد على الأقل.",
    "passwordNeedNumber" to "تحتاج كلمة المرور إلى رقم واحد على الأقل.",
    "passwordNeedSymbol" to "تحتاج كلمة المرور إلى رمز واحد على الأقل.",
    "resettingPassword" to "جارٍ إعادة تعيين كلمة المرور...",
    "passwordResetSuccess" to "تمت إعادة تعيين كلمة المرور بنجاح! يمكنك الآن تسجيل الدخول.",
    "failedResetPassword" to "فشلت إعادة تعيين كلمة المرور.",
    "loginSuccessRedirecting" to "تم تسجيل الدخول بنجاح! جارٍ إعادة التوجيه...",
    "loginFailedExpiredLink" to "فشل تسجيل الدخول. قد يكون الرابط غير صالح أو منتهي الصلاحية.",
    "loginFailed" to "فشل تسجيل الدخول.",
    "emailChangedSuccess" to "تم تغيير البريد الإلكتروني بنجاح!",
    "failedChangeEmailExpired" to "فشل تغيير البريد الإلكتروني. قد يكون الرابط غير صالح أو منتهي الصلاحية.",
    "failedChangeEmail" to "فشل تغيير البريد الإلكتروني.",
    "emailVerifiedSuccess" to "تم التحقق من البريد الإلكتروني بنجاح!",
    "failedVerifyEmailExpired" to "فشل التحقق من البريد الإلكتروني. قد يكون الرابط غير صالح أو منتهي الصلاحية.",
    "failedVerifyEmail" to "فشل التحقق من البريد الإلكتروني.",
    "dropImagesHint" to "أفلت الصور هنا أو انقر للتصفح",
    "forgotPasswordTitle" to "نسيت كلمة المرور",
    "forgotPasswordIntro" to "أدخل بريدك الإلكتروني وسنرسل لك رابطًا لإعادة تعيين كلمة المرور.",
    "sendResetLink" to "إرسال رابط إعادة التعيين",
    "backToLogin" to "العودة إلى تسجيل الدخول",
    "resetPasswordTitle" to "إعادة تعيين كلمة المرور",
    "resetPasswordIntro" to "أدخل كلمة المرور الجديدة أدناه.",
    "emailLinkLoginTitle" to "تسجيل الدخول برابط البريد الإلكتروني",
    "verifying" to "جارٍ التحقق...",
    "emailChangeTitle" to "تغيير البريد الإلكتروني",
    "verifyContactEmailTitle" to "التحقق من بريد التواصل",
    "status" to "الحالة",
    "yes" to "نعم",
    "no" to "لا",
    "noDescription" to "لا يوجد وصف.",
    "urgentBadge" to "عاجل - يحتاج إلى منزل قريبًا!",
    "needsNewHome" to "يحتاج هذا الحيوان الأليف بشكل عاجل إلى منزل جديد",
    "promotedReasonLabel" to "السبب",
    "selectReason" to "اختر سببًا",
    "promotedReasonMoving" to "المالك ينتقل للسكن في مكان آخر",
    "promotedReasonComplaints" to "شكاوى / لا يمكن الاحتفاظ بمزيد من الحيوانات الأليفة",
    "promotedReasonPetConflict" to "تعارض مع حيوان أليف آخر",
    "promotedReasonOther" to "أخرى",
    "promotedReasonDetailLabel" to "تفاصيل إضافية (اختياري)",
    "promotedReasonRequired" to "يرجى اختيار سبب",
    "needsNewHomeBadge" to "يحتاج إلى منزل جديد",
    "housingType" to "نوع السكن",
    "preferNotToSay" to "أفضل عدم الإفصاح",
    "houseHousing" to "منزل",
    "apartmentHousing" to "شقة",
    "hasYard" to "لديه حديقة",
    "hasOtherPets" to "حيوانات أليفة أخرى في المنزل",
    "adoptionExperience" to "خبرة التبني",
    "firstTimeAdopter" to "متبنٍ لأول مرة",
    "experiencedAdopter" to "متبنٍ ذو خبرة",
    "noAdoptionRequests" to "لا توجد طلبات تبني",
    "noMessage" to "لا توجد رسالة",
    "markUnderReview" to "وضع علامة قيد المراجعة",
    "approve" to "موافقة",
    "reject" to "رفض",
    "reviewNoteLabel" to "ملاحظة مراجعة خاصة",
    "saveNote" to "حفظ الملاحظة",
    "adoptionStatusPending" to "قيد الانتظار",
    "adoptionStatusUnderReview" to "قيد المراجعة",
    "adoptionStatusApproved" to "تمت الموافقة",
    "adoptionStatusRejected" to "لم تتم الموافقة",
    "myAdoptionRequests" to "طلبات التبني الخاصة بي",
    "noAdoptionRequestsYet" to "لم تطلب تبني أي حيوان أليف بعد.",
    "medicalHistory" to "السجل الطبي",
    "medicalSchedule" to "جدول التطعيمات والتخلص من الديدان",
    "recordType" to "النوع",
    "vaccination" to "تطعيم",
    "deworming" to "التخلص من الديدان",
    "recordName" to "الاسم",
    "dateGiven" to "تاريخ الإعطاء",
    "nextDueDate" to "الموعد التالي (اختياري)",
    "notesOptional" to "ملاحظات (اختياري)",
    "addRecord" to "إضافة سجل",
    "noMedicalRecords" to "لا توجد سجلات تطعيم أو تخلص من الديدان بعد.",
    "nextDueLabel" to "الموعد التالي",
    "givenLabel" to "تم إعطاؤه",
    "overdue" to "متأخر",
    "dueSoon" to "مستحق قريبًا",
    "upToDate" to "محدّث",
    "recordNameRequired" to "الاسم مطلوب",
    "dateGivenRequired" to "تاريخ الإعطاء مطلوب",
    "failedToAddRecord" to "فشل إضافة السجل",
    "confirmDeleteRecord" to "هل تريد حذف هذا السجل؟",
    "maxCapacity" to "الحد الأقصى للحيوانات الأليفة في المرة الواحدة (اختياري)",
    "placePetHere" to "وضع حيوان أليف هنا",
    "noAvailablePetsToPlace" to "ليس لديك حيوانات أليفة متاحة لوضعها الآن.",
    "selectPet" to "اختر حيوانًا أليفًا",
    "startPlacementBtn" to "بدء الإيداع",
    "placementStarted" to "بدأ إيداع الحضانة!",
    "petsCurrentlyWithMe" to "الحيوانات الأليفة الموجودة لدي حاليًا",
    "noPetsCurrentlyFostered" to "لا توجد حيوانات أليفة موضوعة لديك حاليًا.",
    "sinceLabel" to "منذ",
    "fosterPlacement" to "إيداع الحضانة",
    "endPlacement" to "إنهاء الإيداع",
    "currentlyWithLabel" to "حاليًا مع",
    "notCurrentlyFostered" to "ليس في حضانة حاليًا.",
    "confirmEndPlacement" to "إنهاء إيداع الحضانة هذا؟ سيُسجَّل الحيوان الأليف على أنه عاد إليك.",
    "aTemporalHome" to "بيت مؤقت",
    "viewsLabel" to "المشاهدات",
    "inquiriesLabel" to "الاستفسارات",
    "conversionLabel" to "التحويل",
    "messageOptional" to "رسالة (اختياري)",
    "requestAdoption" to "طلب التبني",
    "editPet" to "تعديل الحيوان الأليف",
    "adoptionRequestSubmitted" to "تم إرسال طلب التبني!",
    "failedSubmitRequest" to "فشل إرسال الطلب",
    "noPhotosYet" to "لا توجد صور بعد.",
    "rabbit" to "أرنب",
    "safeFoods" to "أطعمة آمنة",
    "harmfulFoods" to "أطعمة ضارة",
    "cannotEatFoods" to "لا يمكن تناولها",
    "legendSafe" to "آمن",
    "legendHarmful" to "ضار",
    "legendToxic" to "سام",
    "food_dog_safe_chicken_name" to "دجاج",
    "food_dog_safe_chicken_detail" to "مطبوخ، بدون عظم",
    "food_dog_safe_chicken_desc" to "بروتين خفيف وسهل الهضم",
    "food_dog_safe_rice_name" to "أرز",
    "food_dog_safe_rice_detail" to "أبيض أو بني",
    "food_dog_safe_rice_desc" to "مصدر جيد للكربوهيدرات",
    "food_dog_safe_carrots_name" to "جزر",
    "food_dog_safe_carrots_detail" to "نيء أو مطبوخ",
    "food_dog_safe_carrots_desc" to "منخفض السعرات، غني بالألياف",
    "food_dog_safe_apples_name" to "تفاح",
    "food_dog_safe_apples_detail" to "بدون بذور",
    "food_dog_safe_apples_desc" to "غني بالفيتامينات",
    "food_dog_safe_peanutbutter_name" to "زبدة الفول السوداني",
    "food_dog_safe_peanutbutter_detail" to "غير مملحة، بدون زيليتول",
    "food_dog_safe_peanutbutter_desc" to "مصدر جيد للبروتين",
    "food_dog_harmful_grapes_name" to "عنب",
    "food_dog_harmful_grapes_detail" to "جميع الأنواع",
    "food_dog_harmful_grapes_desc" to "قد يسبب فشلًا كلويًا",
    "food_dog_harmful_onions_name" to "بصل",
    "food_dog_harmful_onions_detail" to "جميع الأشكال",
    "food_dog_harmful_onions_desc" to "يتلف خلايا الدم الحمراء",
    "food_dog_harmful_chocolate_name" to "شوكولاتة",
    "food_dog_harmful_chocolate_detail" to "جميع الأنواع",
    "food_dog_harmful_chocolate_desc" to "سام للكلاب",
    "food_dog_harmful_garlic_name" to "ثوم",
    "food_dog_harmful_garlic_detail" to "جميع الأشكال",
    "food_dog_harmful_garlic_desc" to "يسبب فقر الدم",
    "food_dog_cannot_xylitol_name" to "زيليتول",
    "food_dog_cannot_xylitol_detail" to "محلي صناعي",
    "food_dog_cannot_xylitol_desc" to "سام جدًا",
    "food_dog_cannot_macadamia_name" to "جوز المكاديميا",
    "food_dog_cannot_macadamia_detail" to "جميع الأشكال",
    "food_dog_cannot_macadamia_desc" to "يسبب ضعفًا",
    "food_dog_cannot_avocado_name" to "أفوكادو",
    "food_dog_cannot_avocado_detail" to "النواة والقشرة وكل الأجزاء",
    "food_dog_cannot_avocado_desc" to "يحتوي على البرسين",
    "food_dog_cannot_alcohol_name" to "كحول",
    "food_dog_cannot_alcohol_detail" to "جميع الأشكال",
    "food_dog_cannot_alcohol_desc" to "سام جدًا",
    "food_cat_safe_cookedfish_name" to "سمك مطبوخ",
    "food_cat_safe_cookedfish_detail" to "بدون عظام",
    "food_cat_safe_cookedfish_desc" to "غني بالبروتين",
    "food_cat_safe_chicken_name" to "دجاج",
    "food_cat_safe_chicken_detail" to "مطبوخ وسادة",
    "food_cat_safe_chicken_desc" to "مصدر جيد للبروتين",
    "food_cat_safe_pumpkin_name" to "يقطين",
    "food_cat_safe_pumpkin_detail" to "سادة ومطبوخ",
    "food_cat_safe_pumpkin_desc" to "يساعد على الهضم",
    "food_cat_safe_eggs_name" to "بيض",
    "food_cat_safe_eggs_detail" to "مطبوخ",
    "food_cat_safe_eggs_desc" to "بروتين كامل",
    "food_cat_harmful_raweggs_name" to "بيض نيء",
    "food_cat_harmful_raweggs_detail" to "يحتوي على الأفيدين",
    "food_cat_harmful_raweggs_desc" to "يتداخل مع البيوتين",
    "food_cat_harmful_rawfish_name" to "سمك نيء",
    "food_cat_harmful_rawfish_detail" to "يحتوي على الثياميناز",
    "food_cat_harmful_rawfish_desc" to "يحلل فيتامينات ب",
    "food_cat_harmful_dogfood_name" to "طعام الكلاب",
    "food_cat_harmful_dogfood_detail" to "أي نوع",
    "food_cat_harmful_dogfood_desc" to "يفتقر إلى التورين",
    "food_cat_harmful_milk_name" to "حليب",
    "food_cat_harmful_milk_detail" to "معظم القطط",
    "food_cat_harmful_milk_desc" to "عدم تحمل اللاكتوز",
    "food_cat_cannot_chocolate_name" to "شوكولاتة",
    "food_cat_cannot_chocolate_detail" to "جميع الأنواع",
    "food_cat_cannot_chocolate_desc" to "الثيوبرومين سام",
    "food_cat_cannot_onionsgarlic_name" to "بصل/ثوم",
    "food_cat_cannot_onionsgarlic_detail" to "جميع الأشكال",
    "food_cat_cannot_onionsgarlic_desc" to "يتلف خلايا الدم الحمراء",
    "food_cat_cannot_grapesraisins_name" to "عنب/زبيب",
    "food_cat_cannot_grapesraisins_detail" to "جميع الأنواع",
    "food_cat_cannot_grapesraisins_desc" to "ضرر كلوي",
    "food_cat_cannot_alcohol_name" to "كحول",
    "food_cat_cannot_alcohol_detail" to "جميع الأشكال",
    "food_cat_cannot_alcohol_desc" to "سام جدًا",
    "food_bird_safe_seeds_name" to "بذور",
    "food_bird_safe_seeds_detail" to "خليط متنوع",
    "food_bird_safe_seeds_desc" to "مصدر جيد للدهون",
    "food_bird_safe_fruits_name" to "فواكه",
    "food_bird_safe_fruits_detail" to "معظم الأنواع",
    "food_bird_safe_fruits_desc" to "فيتامينات ومعادن",
    "food_bird_safe_vegetables_name" to "خضروات",
    "food_bird_safe_vegetables_detail" to "خضروات ورقية",
    "food_bird_safe_vegetables_desc" to "عناصر غذائية أساسية",
    "food_bird_safe_pellets_name" to "حبيبات علف",
    "food_bird_safe_pellets_detail" to "مركبة بجودة عالية",
    "food_bird_safe_pellets_desc" to "تغذية كاملة",
    "food_bird_harmful_avocado_name" to "أفوكادو",
    "food_bird_harmful_avocado_detail" to "جميع الأجزاء",
    "food_bird_harmful_avocado_desc" to "البرسين سام",
    "food_bird_harmful_fruitpits_name" to "نوى الفاكهة",
    "food_bird_harmful_fruitpits_detail" to "تفاح، خوخ",
    "food_bird_harmful_fruitpits_desc" to "آثار سيانيد",
    "food_bird_harmful_salt_name" to "ملح",
    "food_bird_harmful_salt_detail" to "أي شكل",
    "food_bird_harmful_salt_desc" to "سام للطيور",
    "food_bird_harmful_caffeine_name" to "كافيين",
    "food_bird_harmful_caffeine_detail" to "قهوة، شاي",
    "food_bird_harmful_caffeine_desc" to "مشاكل قلبية",
    "food_bird_cannot_chocolate_name" to "شوكولاتة",
    "food_bird_cannot_chocolate_detail" to "جميع الأنواع",
    "food_bird_cannot_chocolate_desc" to "الثيوبرومين سام",
    "food_bird_cannot_onions_name" to "بصل",
    "food_bird_cannot_onions_detail" to "جميع الأشكال",
    "food_bird_cannot_onions_desc" to "يتلف خلايا الدم الحمراء",
    "food_bird_cannot_garlic_name" to "ثوم",
    "food_bird_cannot_garlic_detail" to "جميع الأشكال",
    "food_bird_cannot_garlic_desc" to "سام",
    "food_bird_cannot_mushrooms_name" to "فطر",
    "food_bird_cannot_mushrooms_detail" to "الأنواع البرية",
    "food_bird_cannot_mushrooms_desc" to "سموم محتملة",
    "food_fish_safe_flakes_name" to "رقائق",
    "food_fish_safe_flakes_detail" to "علامات تجارية جيدة",
    "food_fish_safe_flakes_desc" to "غذاء سمك كامل",
    "food_fish_safe_pellets_name" to "حبيبات علف",
    "food_fish_safe_pellets_detail" to "طافية/غاطسة",
    "food_fish_safe_pellets_desc" to "تغذية متوازنة",
    "food_fish_safe_frozenfood_name" to "طعام مجمد",
    "food_fish_safe_frozenfood_detail" to "ديدان الدم، الأرتيميا",
    "food_fish_safe_frozenfood_desc" to "مصدر بروتين",
    "food_fish_safe_vegetables_name" to "خضروات",
    "food_fish_safe_vegetables_detail" to "كوسة مسلوقة",
    "food_fish_safe_vegetables_desc" to "مصدر ألياف",
    "food_fish_harmful_bread_name" to "خبز",
    "food_fish_harmful_bread_detail" to "أي نوع",
    "food_fish_harmful_bread_desc" to "بلا قيمة غذائية، يسبب الشبع",
    "food_fish_harmful_humanfood_name" to "طعام بشري",
    "food_fish_harmful_humanfood_detail" to "وجبات مطبوخة",
    "food_fish_harmful_humanfood_desc" to "تغذية غير مناسبة",
    "food_fish_harmful_livefeed_name" to "علف حي",
    "food_fish_harmful_livefeed_detail" to "حشرات برية مصطادة",
    "food_fish_harmful_livefeed_desc" to "طفيليات محتملة",
    "food_fish_cannot_landinsect_name" to "حشرات برية",
    "food_fish_cannot_landinsect_detail" to "حشرات زاحفة",
    "food_fish_cannot_landinsect_desc" to "ليست غذاءً طبيعيًا",
    "food_fish_cannot_mammalmeat_name" to "لحم الثدييات",
    "food_fish_cannot_mammalmeat_detail" to "دجاج، لحم بقري",
    "food_fish_cannot_mammalmeat_desc" to "غير مناسب لمعظم الأسماك",
    "food_fish_cannot_dairy_name" to "ألبان",
    "food_fish_cannot_dairy_detail" to "أي نوع",
    "food_fish_cannot_dairy_desc" to "لا يمكن هضمها",
    "food_fish_cannot_breadcrumbs_name" to "فتات الخبز",
    "food_fish_cannot_breadcrumbs_detail" to "يحتوي الكثير منها على ثوم",
    "food_fish_cannot_breadcrumbs_desc" to "إضافات ضارة",
    "food_rabbit_safe_hay_name" to "قش",
    "food_rabbit_safe_hay_detail" to "قش تيموثي",
    "food_rabbit_safe_hay_desc" to "80٪ من النظام الغذائي",
    "food_rabbit_safe_leafygreens_name" to "خضروات ورقية",
    "food_rabbit_safe_leafygreens_detail" to "خس روماني، كزبرة",
    "food_rabbit_safe_leafygreens_desc" to "فيتامينات يومية",
    "food_rabbit_safe_pellets_name" to "حبيبات علف",
    "food_rabbit_safe_pellets_detail" to "أساسها تيموثي",
    "food_rabbit_safe_pellets_desc" to "تغذية متوازنة",
    "food_rabbit_safe_carrots_name" to "جزر",
    "food_rabbit_safe_carrots_detail" to "باعتدال",
    "food_rabbit_safe_carrots_desc" to "مكافأة",
    "food_rabbit_harmful_icebergletttuce_name" to "خس الآيسبرغ",
    "food_rabbit_harmful_icebergletttuce_detail" to "بلا قيمة غذائية",
    "food_rabbit_harmful_icebergletttuce_desc" to "قد يسبب ركودًا هضميًا",
    "food_rabbit_harmful_beans_name" to "فاصوليا",
    "food_rabbit_harmful_beans_detail" to "جميع البقوليات",
    "food_rabbit_harmful_beans_desc" to "غازات، مشاكل هضمية",
    "food_rabbit_harmful_corn_name" to "ذرة",
    "food_rabbit_harmful_corn_detail" to "أي شكل",
    "food_rabbit_harmful_corn_desc" to "لا يمكن هضمها",
    "food_rabbit_harmful_potatoes_name" to "بطاطس",
    "food_rabbit_harmful_potatoes_detail" to "أي شكل",
    "food_rabbit_harmful_potatoes_desc" to "عناصر غذائية غير مناسبة",
    "food_rabbit_cannot_chocolate_name" to "شوكولاتة",
    "food_rabbit_cannot_chocolate_detail" to "جميع الأنواع",
    "food_rabbit_cannot_chocolate_desc" to "الثيوبرومين سام",
    "food_rabbit_cannot_onions_name" to "بصل",
    "food_rabbit_cannot_onions_detail" to "جميع الأشكال",
    "food_rabbit_cannot_onions_desc" to "سام جدًا",
    "food_rabbit_cannot_garlic_name" to "ثوم",
    "food_rabbit_cannot_garlic_detail" to "جميع الأشكال",
    "food_rabbit_cannot_garlic_desc" to "سام",
    "food_rabbit_cannot_avocado_name" to "أفوكادو",
    "food_rabbit_cannot_avocado_detail" to "جميع الأجزاء",
    "food_rabbit_cannot_avocado_desc" to "يحتوي على البرسين",
    "primary" to "أساسي",
    "passkeyRegisteredSuccess" to "تم تسجيل مفتاح المرور بنجاح!",
    "passkeyRegistrationFailed" to "فشل تسجيل مفتاح المرور",
    "passkeyNameRequired" to "اسم مفتاح المرور مطلوب",
    "registeringPasskey" to "جارٍ تسجيل مفتاح المرور...",
    "passkeyRegistered" to "تم تسجيل مفتاح المرور",
    "noPasskey" to "لم يتم تسجيل أي مفتاح مرور",
    "noPets" to "لا توجد حيوانات أليفة بعد.",
    "photographerCountryStateRequired" to "يرجى ملء الدولة والولاية لخدمات التصوير",
    "temporalHomeAliasCountryCityRequired" to "يرجى ملء الاسم المستعار والدولة والمدينة للبيت المؤقت",
    "shelterNameCountryCityAddressRequired" to "يرجى ملء الاسم والدولة والمدينة والعنوان للملجأ",
    "sterilizationNameCountryCityAddressRequired" to "يرجى ملء الاسم والدولة والمدينة والعنوان لمركز التعقيم",
    "country.afghanistan" to "أفغانستان",
    "country.albania" to "ألبانيا",
    "country.algeria" to "الجزائر",
    "country.argentina" to "الأرجنتين",
    "country.armenia" to "أرمينيا",
    "country.australia" to "أستراليا",
    "country.austria" to "النمسا",
    "country.azerbaijan" to "أذربيجان",
    "country.bangladesh" to "بنغلاديش",
    "country.belarus" to "بيلاروسيا",
    "country.belgium" to "بلجيكا",
    "country.bolivia" to "بوليفيا",
    "country.bosnia" to "البوسنة والهرسك",
    "country.brazil" to "البرازيل",
    "country.bulgaria" to "بلغاريا",
    "country.cambodia" to "كمبوديا",
    "country.cameroon" to "الكاميرون",
    "country.canada" to "كندا",
    "country.chile" to "تشيلي",
    "country.china" to "الصين",
    "country.colombia" to "كولومبيا",
    "country.costaRica" to "كوستاريكا",
    "country.croatia" to "كرواتيا",
    "country.cuba" to "كوبا",
    "country.czechia" to "جمهورية التشيك",
    "country.denmark" to "الدنمارك",
    "country.dominicanRepublic" to "جمهورية الدومينيكان",
    "country.ecuador" to "الإكوادور",
    "country.egypt" to "مصر",
    "country.elSalvador" to "السلفادور",
    "country.estonia" to "إستونيا",
    "country.ethiopia" to "إثيوبيا",
    "country.finland" to "فنلندا",
    "country.france" to "فرنسا",
    "country.georgia" to "جورجيا",
    "country.germany" to "ألمانيا",
    "country.ghana" to "غانا",
    "country.greece" to "اليونان",
    "country.guatemala" to "غواتيمالا",
    "country.haiti" to "هايتي",
    "country.honduras" to "هندوراس",
    "country.hungary" to "المجر",
    "country.iceland" to "آيسلندا",
    "country.india" to "الهند",
    "country.indonesia" to "إندونيسيا",
    "country.iran" to "إيران",
    "country.iraq" to "العراق",
    "country.ireland" to "أيرلندا",
    "country.israel" to "إسرائيل",
    "country.italy" to "إيطاليا",
    "country.jamaica" to "جامايكا",
    "country.japan" to "اليابان",
    "country.jordan" to "الأردن",
    "country.kazakhstan" to "كازاخستان",
    "country.kenya" to "كينيا",
    "country.kuwait" to "الكويت",
    "country.latvia" to "لاتفيا",
    "country.lebanon" to "لبنان",
    "country.libya" to "ليبيا",
    "country.lithuania" to "ليتوانيا",
    "country.luxembourg" to "لوكسمبورغ",
    "country.malaysia" to "ماليزيا",
    "country.mexico" to "المكسيك",
    "country.moldova" to "مولدوفا",
    "country.mongolia" to "منغوليا",
    "country.montenegro" to "الجبل الأسود",
    "country.morocco" to "المغرب",
    "country.myanmar" to "ميانمار",
    "country.nepal" to "نيبال",
    "country.netherlands" to "هولندا",
    "country.newZealand" to "نيوزيلندا",
    "country.nicaragua" to "نيكاراغوا",
    "country.nigeria" to "نيجيريا",
    "country.northKorea" to "كوريا الشمالية",
    "country.norway" to "النرويج",
    "country.pakistan" to "باكستان",
    "country.panama" to "بنما",
    "country.paraguay" to "باراغواي",
    "country.peru" to "بيرو",
    "country.philippines" to "الفلبين",
    "country.poland" to "بولندا",
    "country.portugal" to "البرتغال",
    "country.puertoRico" to "بورتوريكو",
    "country.qatar" to "قطر",
    "country.romania" to "رومانيا",
    "country.russia" to "روسيا",
    "country.saudiArabia" to "المملكة العربية السعودية",
    "country.serbia" to "صربيا",
    "country.singapore" to "سنغافورة",
    "country.slovakia" to "سلوفاكيا",
    "country.slovenia" to "سلوفينيا",
    "country.southAfrica" to "جنوب أفريقيا",
    "country.southKorea" to "كوريا الجنوبية",
    "country.spain" to "إسبانيا",
    "country.sriLanka" to "سريلانكا",
    "country.sudan" to "السودان",
    "country.sweden" to "السويد",
    "country.switzerland" to "سويسرا",
    "country.syria" to "سوريا",
    "country.taiwan" to "تايوان",
    "country.thailand" to "تايلاند",
    "country.tunisia" to "تونس",
    "country.turkey" to "تركيا",
    "country.ukraine" to "أوكرانيا",
    "country.uae" to "الإمارات العربية المتحدة",
    "country.uk" to "المملكة المتحدة",
    "country.usa" to "الولايات المتحدة",
    "country.uruguay" to "أوروغواي",
    "country.uzbekistan" to "أوزبكستان",
    "country.venezuela" to "فنزويلا",
    "country.vietnam" to "فيتنام",
    "country.yemen" to "اليمن",
    "loading" to "جارٍ التحميل...",
    "countryRequired" to "يرجى اختيار دولة للبحث",
    "noSheltersFound" to "لم يتم العثور على ملاجئ بعد. تحقق مرة أخرى لاحقًا!",
    "visitWebsite" to "زيارة الموقع الإلكتروني",
    "fiscalId" to "الرقم الضريبي",
    "donationInformation" to "معلومات التبرع",
    "bankName" to "البنك",
    "accountHolder" to "صاحب الحساب",
    "accountNumber" to "رقم الحساب",
    "currency" to "العملة",
    "errorLoadingShelters" to "خطأ في تحميل الملاجئ",
    "pleaseSelectCountry" to "يرجى اختيار دولة",
    "actions" to "الإجراءات",
    "addShelter" to "إضافة ملجأ",
    "addEditShelter" to "إضافة / تعديل ملجأ",
    "allStates" to "جميع الولايات",
    "confirmDelete" to "هل أنت متأكد أنك تريد حذف هذا الموقع؟",
    "confirmDeleteShelter" to "هل أنت متأكد أنك تريد حذف هذا الملجأ؟",
    "confirmUnbanUser" to "إلغاء حظر هذا المستخدم؟",
    "confirmResetUserCredentials" to "سيؤدي هذا إلى إبطال كلمة المرور ومفاتيح المرور الحالية لـ {email} وإرسال رابط إعادة تعيين له عبر البريد الإلكتروني. هل تريد المتابعة؟",
    "confirmDeactivateUser" to "تعطيل هذا المستخدم؟ يمكن إعادة تفعيله لاحقًا.",
    "confirmDeactivatePet" to "تعطيل هذا الحيوان الأليف؟ سيتم إخفاؤه من القوائم العامة ويمكن إعادة تفعيله لاحقًا.",
    "confirmBlockRescuer" to "حظر هذا المنقذ من إرسال المزيد من الطلبات إليك؟",
    "confirmDeletePet" to "حذف هذا الحيوان الأليف؟",
    "confirmDeleteStorage" to "حذف هذا التخزين؟",
    "contact" to "التواصل",
    "delete" to "حذف",
    "edit" to "تعديل",
    "errorDeletingShelter" to "خطأ في حذف الملجأ",
    "errorLoadingLocations" to "خطأ في تحميل المواقع",
    "errorLoadingShelter" to "خطأ في تحميل الملجأ",
    "errorSaving" to "خطأ في الحفظ",
    "errorSavingShelter" to "خطأ في حفظ الملجأ",
    "location" to "الموقع",
    "noLocationsFound" to "لم يتم العثور على مواقع",
    "selectCountry" to "اختر دولة",
    "selectCountryToFilter" to "يرجى اختيار دولة للفلترة",
    "shelterAdded" to "تمت إضافة الملجأ بنجاح",
    "shelterDeleted" to "تم حذف الملجأ بنجاح",
    "shelterUpdated" to "تم تحديث الملجأ بنجاح",
    "updateShelter" to "تحديث الملجأ",
    "errorLoadingPhotographers" to "خطأ في تحميل المصورين",
    "noPhotographersAvailable" to "لا يوجد مصورون متاحون",
    "requestPhotoSession" to "طلب جلسة تصوير",
    "selectPhotographer" to "اختيار",
    "photographersSelected" to "تم اختيار {count}",
    "requestSelectedPhotographers" to "طلب المصورين المحددين",
    "clearSelection" to "مسح التحديد",
    "requestToMultiple" to "طلب إلى",
    "messageRequired" to "يرجى كتابة رسالة للمصورين.",
    "requestsSentSuccessfully" to "تم إرسال الطلبات بنجاح",
    "message" to "الرسالة",
    "sendRequest" to "إرسال الطلب",
    "requestSentSuccessfully" to "تم إرسال الطلب بنجاح!",
    "requestTo" to "طلب إلى",
    "enterMessage" to "أدخل رسالة لـ {name}...",
    "reportUrgent" to "طارئ",
    "topRescuers" to "أفضل المنقذين",
    "urgentRescuerSettings" to "إعدادات المنقذ العاجل",
    "urgentRescueAlerts" to "تنبيهات الإنقاذ العاجل",
    "reportUrgentTitle" to "الإبلاغ عن حيوان أليف في خطر",
    "reportUrgentIntro" to "إذا وجدت حيوانًا أليفًا مصابًا أو يتعرض لسوء المعاملة أو يعاني من الجوع أو مهجورًا ويحتاج إلى مساعدة فورية، أخبرنا بمكانه وما المشكلة - سننبه المنقذين العاجلين القريبين على الفور.",
    "dangerType" to "ما المشكلة؟",
    "dangerInjured" to "مصاب",
    "dangerAbused" to "يتعرض لسوء المعاملة / الإساءة",
    "dangerStarving" to "يعاني من الجوع",
    "dangerTooYoung" to "صغير جدًا على النجاة بمفرده",
    "dangerOther" to "حالة طوارئ أخرى",
    "useMyLocation" to "استخدام موقعي الحالي",
    "confirmMyLocation" to "تأكيد موقعي الحالي",
    "locationPermissionHint" to "سيطلب متصفحك الإذن لمشاركة موقعك؛ يُستخدم فقط لتنبيهك بالبلاغات القريبة منك.",
    "phoneOptional" to "هاتفك (اختياري)",
    "submitReport" to "إرسال البلاغ",
    "urgentRescuerSettingsTitle" to "إعدادات المنقذ العاجل",
    "urgentRescuerSettingsIntro" to "احصل على تنبيه عند الإبلاغ عن حيوان أليف في خطر بالقرب من منطقة تغطيتك. أول من يقبل يتولى الحالة.",
    "urgentRescuerActive" to "أنا متاح للاستجابة للبلاغات العاجلة",
    "coverageAreaMode" to "منطقة التغطية",
    "coverageArea" to "منطقة التغطية",
    "coverageAreaHint" to "أكد موقعك الحالي، أو اسحب العلامة، أو اكتب عنوانًا أدناه - تبقى الخريطة والحقول متزامنة.",
    "useMyLocationAndRadius" to "استخدام موقعي + نطاق محدد",
    "pickAZone" to "اختيار دولة/ولاية/مدينة بدلاً من ذلك",
    "radiusKm" to "النطاق (كم)",
    "urgentRescueAlertsTitle" to "تنبيهات الإنقاذ العاجل",
    "urgentRescuerLeaderboardTitle" to "أفضل منقذي الحالات العاجلة (آخر 12 شهرًا)",
    "locating" to "جارٍ تحديد الموقع...",
    "geolocationUnsupported" to "تحديد الموقع الجغرافي غير مدعوم في متصفحك",
    "locationCaptured" to "تم تحديد الموقع",
    "locationDenied" to "تعذر الحصول على موقعك - يرجى السماح بالوصول إلى الموقع أو إدخال مدينة بدلاً من ذلك",
    "street" to "الشارع",
    "exteriorNumber" to "رقم المبنى",
    "referenceNotes" to "ملاحظات مرجعية (مثل لون المنزل، معلم قريب)",
    "concreteAddressHint" to "أضف اسم الشارع ورقمه إن أمكن - هذا ما يتيح للمنقذ ركوب سيارة أجرة أو فتح تطبيق خرائط مباشرة إلى الموقع.",
    "descriptionRequired" to "يرجى وصف الحالة",
    "locationRequired" to "يرجى تقديم موقع (استخدم موقعك الحالي أو أدخل دولة ومدينة)",
    "submittingEllipsis" to "جارٍ الإرسال...",
    "reportSubmitted" to "تم إرسال البلاغ. شكرًا لك - يتم الآن تنبيه المنقذين القريبين.",
    "reportSubmitFailed" to "فشل إرسال البلاغ",
    "phoneRequired" to "يرجى إدخال رقم هاتف",
    "savingEllipsis" to "جارٍ الحفظ...",
    "saved" to "تم الحفظ",
    "saveFailed" to "فشل الحفظ",
    "noUrgentPages" to "لا توجد تنبيهات عاجلة معلقة حاليًا",
    "accept" to "قبول",
    "alreadyAccepted" to "قام شخص آخر بالفعل بقبول هذا البلاغ",
    "noLeaderboardData" to "لا توجد عمليات إنقاذ مقبولة في آخر 12 شهرًا بعد",
    "rescueAcceptedTitle" to "تم قبول الإنقاذ",
    "rescueAcceptedMessage" to "شكرًا لك - المهمة في يدك الآن. توجّه إلى الموقع المُبلَّغ عنه في أقرب وقت ممكن.",
    "rescueAcceptFailedTitle" to "تعذر القبول",
    "rescueAcceptFailedMessage" to "تم قبول هذا البلاغ بالفعل من قبل منقذ آخر، أو أن الرابط غير صالح أو منتهي الصلاحية.",
    "share" to "مشاركة",
    "shareViaFacebook" to "مشاركة على فيسبوك",
    "shareViaX" to "مشاركة على X",
    "shareViaWhatsapp" to "مشاركة عبر واتساب",
    "shareViaEmail" to "مشاركة عبر البريد الإلكتروني",
    "copyLink" to "نسخ الرابط",
    "linkCopied" to "تم نسخ الرابط!",
    "reportLostFoundTitle" to "الإبلاغ عن حيوان أليف مفقود أو تم العثور عليه",
    "reportLostFoundIntro" to "فقدت حيوانك الأليف، أم وجدت حيوانًا ضائعًا؟ قدّم بلاغًا وسنقارنه بالبلاغات الأخرى القريبة.",
    "browseLostFound" to "تصفح بلاغات المفقودات والموجودات الحالية",
    "kindLost" to "فقدت حيواني الأليف",
    "kindFound" to "وجدت حيوانًا أليفًا",
    "petType" to "نوع الحيوان الأليف",
    "petTypeUnknown" to "غير متأكد",
    "lostFoundBrowseTitle" to "الحيوانات الأليفة المفقودة والموجودة",
    "showing" to "عرض",
    "kindLostPlural" to "حيوانات أليفة مفقودة",
    "kindFoundPlural" to "حيوانات أليفة تم العثور عليها",
    "reportResolvedTitle" to "تم حل البلاغ",
    "reportResolvedMessage" to "شكرًا على التحديث - تم إغلاق هذا البلاغ الآن.",
    "reportResolveFailedTitle" to "تعذر الحل",
    "reportResolveFailedMessage" to "تم حل هذا البلاغ بالفعل، أو أن الرابط غير صالح أو منتهي الصلاحية.",
    "reportLostFound" to "مفقود/\u200Bموجود",
    "noLostFoundReportsFound" to "لم يتم العثور على بلاغات مفقودات وموجودات",
    "errorLoadingReports" to "خطأ في تحميل البلاغات",
    "yourEmail" to "بريدك الإلكتروني",
    "contactReporter" to "التواصل مع مقدم البلاغ",
    "contactFormIncomplete" to "يرجى إدخال بريدك الإلكتروني ورسالة",
    "contactSent" to "تم إرسال الرسالة",
    "contactFailed" to "فشل إرسال الرسالة",
    "lostFoundReportSubmitted" to "تم إرسال البلاغ. شكرًا لك - سنقارنه بالبلاغات الأخرى القريبة.",
    "video" to "فيديو (اختياري)",
    "removeVideo" to "إزالة الفيديو",
    "saveThisSearch" to "حفظ هذا البحث",
    "savedSearchSaved" to "تم حفظ البحث! سنرسل لك بريدًا إلكترونيًا عند إدراج تطابق جديد.",
    "savedSearchFailed" to "فشل حفظ البحث",
    "favorite" to "مفضلة",
    "addToFavorites" to "إضافة إلى المفضلة",
    "removeFromFavorites" to "إزالة من المفضلة",
    "myFavorites" to "مفضلاتي",
    "noFavoritesYet" to "لا توجد مفضلات بعد",
    "mySavedSearches" to "عمليات البحث المحفوظة الخاصة بي",
    "noSavedSearchesYet" to "لا توجد عمليات بحث محفوظة بعد",
    "donationPromptText" to "شكرًا لك على مساعدة حيوان أليف اليوم. إذا استطعت، فكّر في تقديم تبرع بسيط للحفاظ على Adopt-U مجانيًا للجميع.",
    "donationPromptCta" to "تبرع",
    "dismiss" to "إغلاق",
)

private val russianTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Вход",
    "register" to "Регистрация",
    "logout" to "Выйти",
    "profile" to "Профиль",
    "myPets" to "Мои питомцы",
    "addPet" to "Добавить питомца",
    "save" to "Сохранить",
    "cancel" to "Отмена",
    "search" to "Поиск",
    "shelters" to "Приюты",
    "photographers" to "Фотографы",
    "temporalHomes" to "Временные дома",
    "findTemporalHomes" to "Найти временные дома",
    "sterilizationLocations" to "Места стерилизации",
    "donate" to "Пожертвовать",
    "countryLabel" to "Страна",
    "selectCountryToSearch" to "Выберите страну",
    "selectCountryFirst" to "Выберите страну, чтобы включить эти фильтры",
    "state" to "Регион",
    "city" to "Город",
    "zipCode" to "Индекс",
    "neighborhood" to "Район",
    "name" to "Имя",
    "email" to "Email",
    "phone" to "Телефон",
    "address" to "Адрес",
    "description" to "Описание",
    "animalShelters" to "Приюты для животных",
    "sheltersDescription" to "Найдите приюты для животных, где можно найти питомца для усыновления или оставить его по предварительной договорённости с приютом",
    "photographerDescription" to "Профессиональные фотографы, предлагающие фотосессии с питомцами",
    "sterilizationLocationsDescription" to "Найдите места, куда можно отвезти животное на стерилизацию.",
    "privacyPolicy" to "Политика конфиденциальности",
    "privacyLastUpdated" to "Последнее обновление: апрель 2025 г.",
    "privacyHighlight" to "В Adopt-U мы придерживаемся принципа минимального сбора данных. Мы собираем только ваш адрес электронной почты, чтобы обеспечить связь между усыновителями и спасателями. Мы не используем вашу информацию для рекламы и никогда не передадим ваши данные третьим лицам в маркетинговых целях.",
    "privacyInfoCollectedH2" to "Информация, которую мы собираем",
    "privacyAccountRegH3" to "Регистрация аккаунта",
    "privacyAccountRegIntro" to "Для создания аккаунта на Adopt-U мы собираем следующую информацию:",
    "privacyEmailLabel" to "Адрес электронной почты: ",
    "privacyEmailDesc" to "Необходим для подтверждения аккаунта, восстановления пароля и связи между усыновителями и спасателями",
    "privacyDisplayNameLabel" to "Отображаемое имя: ",
    "privacyDisplayNameDesc" to "Имя, под которым вы хотите быть представлены на платформе",
    "privacyLangPrefLabel" to "Языковые предпочтения: ",
    "privacyLangPrefDesc" to "Чтобы предоставлять платформу на предпочитаемом вами языке",
    "privacyNoFullNameEtc" to "Мы не собираем ваше полное имя, номер телефона, физический адрес, документы, удостоверяющие личность, платёжную информацию или любые другие персональные данные, кроме перечисленных выше.",
    "privacyOptionalProfileH3" to "Дополнительная информация профиля",
    "privacyOptionalProfileIntro" to "В зависимости от вашей роли вы можете по желанию указать:",
    "privacyAdopterLabel" to "Как усыновитель: ",
    "privacyAdopterDesc" to "Дополнительная информация не требуется и не собирается",
    "privacyRescuerLabel" to "Как спасатель: ",
    "privacyRescuerDesc" to "Информацию о местоположении питомцев, доступных для усыновления",
    "privacyPhotographerLabel" to "Как фотограф: ",
    "privacyPhotographerDesc" to "Место оказания услуг и предпочтения по связи",
    "privacyTemporalHomeLabel" to "Как временный дом: ",
    "privacyTemporalHomeDesc" to "Место, где вы можете предоставить временное проживание",
    "privacyLocationUsage" to "Вся информация о местоположении используется исключительно для того, чтобы подобрать усыновителям ближайших спасателей и поставщиков услуг.",
    "privacyPetInfoH3" to "Информация о питомце",
    "privacyPetInfoIntro" to "Спасатели могут размещать информацию о питомцах, включая:",
    "privacyPetPhotos" to "Фотографии питомца",
    "privacyPetDescBreedAge" to "Описание, породу, возраст и особенности",
    "privacyPetHealth" to "Состояние здоровья и статус вакцинации",
    "privacyPetLocation" to "Местоположение, где доступен питомец",
    "privacyPetInfoNotVerified" to "Эта информация предоставляется спасателями и не проверяется Adopt-U самостоятельно.",
    "privacyHowWeUseH2" to "Как мы используем вашу информацию",
    "privacyPrimaryPurposeH3" to "Основная цель: содействие в установлении контактов для усыновления",
    "privacyEmailUsedFor" to "Ваш адрес электронной почты используется исключительно для следующих целей:",
    "privacyUseVerification" to "Отправка вам ссылки для подтверждения при создании аккаунта",
    "privacyUseRescuerContact" to "Возможность для спасателей связаться с вами по поводу интересующих вас питомцев",
    "privacyUseContactRescuer" to "Возможность связаться со спасателями по поводу размещённых ими питомцев",
    "privacyUseNotifications" to "Отправка важных уведомлений об аккаунте и безопасности",
    "privacyOnceConnected" to "После того как вы свяжетесь с другим пользователем через нашу платформу, всё дальнейшее общение происходит напрямую между вами и этой стороной. Мы не отслеживаем, не храним и не имеем доступа к содержанию вашей переписки.",
    "privacyNeverDoH3" to "Что мы никогда не сделаем",
    "privacyCommitIntro" to "Мы торжественно обязуемся соблюдать следующее:",
    "privacyNoAdsLabel" to "Никакой рекламы: ",
    "privacyNoAdsDesc" to "Мы никогда не будем показывать таргетированную или нетаргетированную рекламу на основе вашей активности",
    "privacyNoMarketingLabel" to "Никакого маркетинга: ",
    "privacyNoMarketingDesc" to "Мы никогда не передадим ваш email третьим лицам в маркетинговых или рекламных целях",
    "privacyNoSellingLabel" to "Никакой продажи данных: ",
    "privacyNoSellingDesc" to "Мы никогда не продадим, не сдадим в аренду и не передадим иным образом вашу личную информацию какой-либо третьей стороне",
    "privacyNoProfilingLabel" to "Никакого профилирования: ",
    "privacyNoProfilingDesc" to "Мы не будем использовать ваши данные для составления профилей в каких-либо целях, помимо базового управления аккаунтом",
    "privacyNoTrackingLabel" to "Никакого отслеживания: ",
    "privacyNoTrackingDesc" to "Мы не используем пиксели отслеживания, рекламные файлы cookie или любые аналогичные технологии слежения",
    "privacyDataStorageH2" to "Хранение данных и безопасность",
    "privacyDataStoredSecurely" to "Ваши данные надёжно хранятся с использованием отраслевого стандарта шифрования. Мы применяем соответствующие технические и организационные меры для защиты вашей личной информации от несанкционированного доступа, изменения, раскрытия или уничтожения.",
    "privacyDataRetention" to "Мы храним информацию о вашем аккаунте до тех пор, пока он остаётся активным. Вы можете в любой момент запросить удаление своего аккаунта и связанных с ним данных, обратившись к нам.",
    "privacyYourRightsH2" to "Ваши права",
    "privacyRightsIntro" to "В отношении своих персональных данных вы имеете следующие права:",
    "privacyRightAccess" to "Доступ: вы можете запросить копию всех персональных данных, которые мы храним о вас",
    "privacyRightCorrection" to "Исправление: вы можете потребовать исправить любую неточную информацию",
    "privacyRightDeletion" to "Удаление: вы можете потребовать удалить ваш аккаунт и все связанные с ним данные",
    "privacyRightPortability" to "Переносимость: вы можете потребовать предоставить ваши данные в общепринятом формате",
    "privacyExerciseRights" to "Чтобы воспользоваться любым из этих прав, свяжитесь с нами по адресу adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Файлы cookie",
    "privacyCookiesText" to "Adopt-U использует минимальное количество файлов cookie, необходимых для работы платформы, включая управление сеансами и языковые предпочтения. Мы не используем рекламные или отслеживающие файлы cookie.",
    "privacyThirdPartyH2" to "Сторонние сервисы",
    "privacyThirdPartyIntro" to "Мы используем следующие сторонние сервисы, которые могут обрабатывать ваши данные:",
    "privacyAws" to "AWS (Amazon Web Services): для хранения данных и доставки электронной почты. К обработке ваших данных применяется их политика конфиденциальности.",
    "privacyWebauthn" to "WebAuthn/FIDO2: для безопасной аутентификации с помощью passkey. Включает криптографические операции в вашем браузере.",
    "privacyThirdPartySelected" to "Мы тщательно отобрали поставщиков услуг, разделяющих нашу приверженность конфиденциальности данных.",
    "privacyChildrensH2" to "Конфиденциальность детей",
    "privacyChildrensText" to "Adopt-U не предназначен для пользователей младше 18 лет. Мы сознательно не собираем информацию от несовершеннолетних. Если вы считаете, что несовершеннолетний создал аккаунт, свяжитесь с нами, чтобы удалить его.",
    "privacyChangesH2" to "Изменения в настоящей политике",
    "privacyChangesText" to "Мы можем время от времени обновлять настоящую Политику конфиденциальности, чтобы отразить изменения в нашей практике или требованиях законодательства. Мы будем уведомлять пользователей о существенных изменениях по электронной почте или с помощью заметного уведомления на платформе.",
    "privacyContactH2" to "Связаться с нами",
    "privacyContactText" to "Если у вас есть вопросы или замечания относительно настоящей Политики конфиденциальности, а также для реализации ваших прав на данные, свяжитесь с нами по адресу adopt-u@adopt-u.org.",
    "termsConditions" to "Условия использования",
    "installApp" to "Установить приложение",
    "termsAcceptanceH2" to "Принятие условий",
    "termsAcceptanceText" to "Получая доступ к Adopt-U и используя платформу, вы принимаете условия и положения настоящего соглашения и соглашаетесь их соблюдать. Если вы не согласны с этими условиями, пожалуйста, не используйте нашу платформу.",
    "termsNatureH2" to "Характер нашего сервиса",
    "termsNatureText1" to "Adopt-U — это платформа для установления контактов, которая облегчает связь между спасателями животных и потенциальными усыновителями. Мы не являемся зоомагазином, посредником, приютом или агентством по усыновлению. Мы не владеем животными, размещёнными на нашей платформе, не осуществляем над ними опеку и не выступаем посредником в их передаче.",
    "termsNatureText2" to "Наша роль строго ограничена предоставлением канала связи между сторонами. Мы не участвуем в переговорах об усыновлении, заключении договоров или физической передаче животных.",
    "termsUserRespH2" to "Обязанности пользователей",
    "termsForRescuersH3" to "Для спасателей:",
    "termsRescuerResp1" to "Предоставлять точную, достоверную информацию о питомцах, находящихся на вашем попечении",
    "termsRescuerResp2" to "Обеспечивать, чтобы питомцы были здоровы, вакцинированы и надлежащим образом осмотрены ветеринаром перед размещением объявления",
    "termsRescuerResp3" to "Проводить ответственный отбор потенциальных усыновителей",
    "termsRescuerResp4" to "Организовывать безопасную передачу питомцев в их новые дома",
    "termsRescuerResp5" to "Нести ответственность за точность всей предоставленной информации",
    "termsRescuerResp6" to "Соблюдать все местные законы и правила, касающиеся усыновления животных",
    "termsRescuerResp7" to "Никогда не использовать платформу для коммерческой продажи животных или разведения",
    "termsForAdoptersH3" to "Для усыновителей:",
    "termsAdopterResp1" to "Предоставлять точную информацию о ваших жилищных условиях и опыте общения с животными",
    "termsAdopterResp2" to "Быть готовым продемонстрировать способность обеспечить надлежащий уход",
    "termsAdopterResp3" to "Понимать, что отказ от усыновления питомца причиняет животному значительный стресс",
    "termsAdopterResp4" to "Уважать право спасателя отклонить вашу заявку на усыновление",
    "termsPetListingsH2" to "Объявления о питомцах и информация",
    "termsPetListingsText1" to "Adopt-U не проверяет, не подтверждает и не гарантирует точность информации, указанной в объявлениях о питомцах. Информация, включая, помимо прочего, породу, возраст, состояние здоровья, темперамент и данные о вакцинации, предоставляется спасателями и не проходит независимую проверку.",
    "termsPetListingsText2" to "Фотографии и описания предоставляются спасателями. Хотя мы запрещаем намеренное искажение информации, мы не можем гарантировать, что все объявления точно отражают текущее состояние животного.",
    "termsPetListingsText3" to "Пользователям рекомендуется задавать вопросы, запрашивать ветеринарные документы и знакомиться с питомцем лично перед окончательным оформлением усыновления.",
    "termsAdoptionProcessH2" to "Процесс усыновления",
    "termsAdoptionProcessIntro" to "После того как спасатель и усыновитель договорились об усыновлении:",
    "termsAdoptionStep1" to "Все договорённости, включая дату и место передачи, а также любые связанные расходы, обсуждаются напрямую между сторонами",
    "termsAdoptionStep2" to "Adopt-U не проводит и не гарантирует какие-либо платёжные операции",
    "termsAdoptionStep3" to "Платформа не предоставляет договоры об усыновлении; любые договорные отношения являются исключительной ответственностью вовлечённых сторон",
    "termsAdoptionStep4" to "Мы настоятельно рекомендуем документировать все договорённости в письменном виде",
    "termsLiabilityH2" to "Ограничение ответственности",
    "termsLiabilityIntro" to "Adopt-U не несёт ответственности за:",
    "termsLiability1" to "Любые споры, разногласия или конфликты между спасателями и усыновителями",
    "termsLiability2" to "Точность информации, указанной в объявлениях о питомцах",
    "termsLiability3" to "Здоровье, поведение или состояние любого животного до, во время или после усыновления",
    "termsLiability4" to "Любые финансовые потери, ущерб или травмы, возникшие в результате использования платформы",
    "termsLiability5" to "Результат любого усыновления, включая, помимо прочего, возврат животного, проблемы со здоровьем или несовместимость",
    "termsLiability6" to "Любые действия пользователей за пределами платформы после установления контакта",
    "termsLiabilityAck" to "Пользователи признают, что усыновление питомца — это серьёзное обязательство, и берут на себя полную ответственность за своё решение об усыновлении.",
    "termsProhibitedH2" to "Запрещённые действия",
    "termsProhibitedIntro" to "На Adopt-U строго запрещены следующие действия:",
    "termsProhibited1" to "Коммерческая продажа животных, разведение или деятельность «щенячьих фабрик»",
    "termsProhibited2" to "Размещение объявлений о питомцах, за которыми вы не осуществляете непосредственный уход",
    "termsProhibited3" to "Ложная или вводящая в заблуждение информация в объявлениях или профилях",
    "termsProhibited4" to "Домогательства, дискриминация или неуместные контакты с другими пользователями",
    "termsProhibited5" to "Использование платформы для сбора личной информации в целях, не связанных с усыновлением животных",
    "termsProhibited6" to "Любая незаконная деятельность или нарушение законов о защите животных",
    "termsAccountTerminationH2" to "Прекращение действия аккаунта",
    "termsAccountTerminationText" to "Adopt-U оставляет за собой право приостанавливать или прекращать действие аккаунтов, которые нарушают настоящие условия, участвуют в запрещённой деятельности или наносят ущерб репутации платформы. Мы также можем удалять объявления, содержащие ложную, вводящую в заблуждение или неуместную информацию.",
    "termsModificationsH2" to "Изменения условий",
    "termsModificationsText" to "Мы оставляем за собой право изменять настоящие условия в любое время. Продолжение использования платформы после внесения изменений означает согласие с изменёнными условиями. Мы будем уведомлять пользователей о существенных изменениях по электронной почте или с помощью объявлений на платформе.",
    "termsContactText" to "Если у вас есть вопросы относительно настоящих Условий использования, свяжитесь с нами по адресу adopt-u@adopt-u.org.",
    "language" to "Язык",
    "displayName" to "Отображаемое имя",
    "saveProfile" to "Сохранить профиль",
    "accountSettings" to "Настройки аккаунта",
    "yourRoles" to "Ваши роли:",
    "resources" to "Ресурсы",
    "loginWithPasskey" to "Войти с помощью passkey",
    "signInWithPasskey" to "Войти через passkey",
    "resendVerificationEmail" to "Отправить письмо подтверждения повторно",
    "usesFido" to "Использует FIDO2 / WebAuthn для безопасной аутентификации без пароля.",
    "loginWithMagicLink" to "Войти по ссылке из письма",
    "magicLinkDesc" to "Мы отправим вам ссылку для входа на email (действительна 5 минут).",
    "sendMagicLink" to "Отправить ссылку для входа",
    "loginWithPassword" to "Войти с паролем",
    "signIn" to "Войти",
    "forgotPassword" to "Забыли пароль?",
    "registerNewAccount" to "Создать аккаунт",
    "accountDetails" to "Данные аккаунта",
    "registrationMethod" to "Способ регистрации",
    "selectAdditionalRoles" to "Выберите дополнительные роли:",
    "adoptPet" to "Усыновить питомца",
    "publishPets" to "Публиковать питомцев для усыновления",
    "offerPhotography" to "Предлагать услуги фотографии",
    "photographerSettings" to "Настройки фотографа",
    "sessionFee" to "Стоимость сессии",
    "provideTemporaryHome" to "Предоставлять временный дом для питомцев",
    "passkeyNoPassword" to "Passkey надёжнее паролей и работают на всех ваших устройствах.",
    "registerWithPasskey" to "Зарегистрироваться с помощью passkey",
    "passkeySettings" to "Настройки passkey",
    "password" to "Пароль",
    "confirmPassword" to "Подтвердите пароль",
    "passwordRequirements" to "Пароль должен содержать не менее 8 символов, включая заглавную букву, строчную букву, цифру и символ",
    "securityInfo" to "Информация о безопасности",
    "passkeyMostSecure" to "Passkey: максимально безопасно, рекомендуется",
    "passkeyBrowserCompat" to "Passkey может не работать во всех браузерах",
    "passwordLeastSecure" to "Пароль: менее безопасно, не рекомендуется",
    "selectLoginMethods" to "Выберите один или несколько способов входа:",
    "registerPasskeyOption" to "Passkey (максимально безопасно, работает на всех ваших устройствах)",
    "registerPasswordOption" to "Пароль (менее безопасно, используйте только как резервный способ)",
    "howPasskeysWorkTitle" to "Как работает passkey?",
    "howPasskeysWorkIntro" to "Passkey заменяет ваш пароль встроенной защитой устройства — отпечатком пальца, распознаванием лица или PIN-кодом блокировки экрана.",
    "howPasskeysWorkSecurity" to "Он хранится только на вашем устройстве, его нельзя выманить фишингом или потерять при утечке данных, и он автоматически синхронизируется между вашими устройствами.",
    "howPasskeysWorkBackup" to "Вы также можете добавить пароль, но для входа достаточно одного passkey — пароль не нужен.",
    "gotIt" to "Понятно",
    "alreadyHaveAccount" to "Уже есть аккаунт?",
    "all" to "Все",
    "filterDogs" to "Собаки",
    "filterCats" to "Кошки",
    "filterBirds" to "Птицы",
    "filterFish" to "Рыбы",
    "filterAllSexOption" to "Любой пол",
    "filterMaleOption" to "♂ Самец",
    "filterFemaleOption" to "♀ Самка",
    "age" to "Возраст",
    "rescued" to "Спасён",
    "years" to "лет",
    "months" to "мес.",
    "year" to "год",
    "month" to "мес.",
    "loginToAdopt" to "Войдите, чтобы подать заявку на усыновление",
    "requiredMarker" to "(обязательно)",
    "pageNotFoundTitle" to "Страница не найдена",
    "pageNotFoundHint" to "Ссылка могла устареть или содержать опечатку. Питомцы всё ещё здесь.",
    "browsePets" to "Смотреть питомцев",
    "requestsFromRescuers" to "Запросы от спасателей",
    "signingOut" to "Выход...",
    "adopter" to "Усыновитель",
    "country" to "Страна",
    "zip" to "Индекс",
    "viewPublic" to "Открыть публичную страницу",
    "banUser" to "Заблокировать пользователя",
    "banReason" to "Причина (необязательно)",
    "myPhotoRequests" to "Мои запросы на фотосессию",
    "noPhotoRequestsYet" to "Вы ещё не запрашивали фотосессию.",
    "photoRequestsReceived" to "Полученные запросы на фотосессию",
    "noPhotoRequestsReceived" to "Запросов на фотосессию пока нет.",
    "photoStatusPending" to "Ожидает",
    "photoStatusApproved" to "Одобрен",
    "photoStatusRejected" to "Отклонён",
    "photoStatusCancelled" to "Отменён",
    "photoStatusCompleted" to "Завершён",
    "cancelRequest" to "Отменить запрос",
    "registerConsentPrefix" to "Создавая аккаунт, вы принимаете наши",
    "registerConsentTerms" to "Условия использования",
    "registerConsentAnd" to "и",
    "registerConsentPrivacy" to "Политику конфиденциальности",
    "petStatusAvailable" to "Доступен",
    "petStatusAdopted" to "Усыновлён",
    "petStatusDisabled" to "Отключён",
    "petStatusPending" to "В ожидании",
    "addEditLocation" to "Добавить/изменить местоположение",
    "addLocation" to "Добавить местоположение",
    "manage" to "Управление",
    "adminPanel" to "Панель администратора",
    "adminSterilizationLocations" to "Управление местами стерилизации",
    "adopterRequired" to "Усыновитель (обязательно)",
    "adoptionFee" to "Стоимость усыновления",
    "adoptionRequests" to "Заявки на усыновление",
    "ageMonths" to "Возраст (месяцев)",
    "ageYears" to "Возраст (лет)",
    "alias" to "Псевдоним",
    "bird" to "Птица",
    "breed" to "Порода",
    "medicalEventsOverview" to "Обзор медицинских событий",
    "back" to "Назад",
    "cat" to "Кошка",
    "color" to "Окрас",
    "compatibility" to "Совместимость",
    "currentPassword" to "Текущий пароль",
    "dog" to "Собака",
    "emailChange" to "Изменить email",
    "emailChangeDesc" to "Обновите ваш адрес электронной почты. Ссылка для подтверждения будет отправлена на новый email.",
    "energyLevel" to "Уровень активности",
    "female" to "Самка",
    "findTemporalHome" to "Найти временный дом",
    "fish" to "Рыба",
    "goodWithCats" to "Ладит с кошками",
    "goodWithDogs" to "Ладит с собаками",
    "goodWithKids" to "Ладит с детьми",
    "high" to "Высокий",
    "houseTrained" to "Приучен к дому",
    "large" to "Крупный",
    "low" to "Низкий",
    "male" to "Самец",
    "managePets" to "Управление питомцами",
    "managePetsBtn" to "Добавить или изменить питомца",
    "managePetsDescription" to "Управляйте всеми страницами питомцев. Добавляйте или удаляйте питомцев.",
    "manageShelters" to "Управление приютами",
    "manageSheltersBtn" to "Добавить или изменить приют",
    "manageSheltersDescription" to "Управляйте всеми страницами приютов. Добавляйте или удаляйте приюты.",
    "manageUsers" to "Управление пользователями",
    "medical" to "Медицинские данные",
    "medium" to "Средний",
    "backToSearch" to "Назад к поиску",
    "contactThisHome" to "Связаться с этим временным домом",
    "loginAsRescuerToContact" to "Войдите как спасатель, чтобы отправить запрос этому временному дому.",
    "memberSince" to "Участник с",
    "microchipId" to "Номер микрочипа",
    "microchipped" to "Чипирован",
    "myTemporalHome" to "Мой временный дом",
    "newEmail" to "Новый email",
    "noTemporalHomes" to "Временные дома не найдены.",
    "requestSentToHome" to "Ваш запрос отправлен!",
    "sendRequestBtn" to "Отправить запрос",
    "temporalHomeNotFound" to "Этот временный дом не найден.",
    "viewDetails" to "Подробнее",
    "rescuerDirectory" to "Спасатели",
    "noRescuersFound" to "Спасатели не найдены.",
    "availablePetsCount" to "Доступные питомцы",
    "availablePets" to "Доступные питомцы",
    "noAvailablePetsForRescuer" to "У этого спасателя сейчас нет доступных питомцев.",
    "rescuerNotFound" to "Этот спасатель не найден.",
    "errorLoadingResults" to "Ошибка загрузки результатов.",
    "volunteerForThisRescuer" to "Стать волонтёром этого спасателя",
    "volunteerExplanation" to "Одобренные волонтёры могут предлагать изменения к объявлениям о питомцах этого спасателя.",
    "applyToVolunteerBtn" to "Подать заявку волонтёра",
    "loginToVolunteer" to "Войдите, чтобы подать заявку на волонтёрство для этого спасателя.",
    "volunteerApplicationSent" to "Ваша заявка волонтёра отправлена!",
    "myVolunteering" to "Моё волонтёрство",
    "noVolunteerApplicationsYet" to "Вы ещё не подавали заявку на волонтёрство ни для одного спасателя.",
    "howToVolunteerHint" to "Чтобы подать заявку, откройте страницу спасателя в каталоге и нажмите \"Стать волонтёром\".",
    "volunteerStatusPending" to "В ожидании",
    "volunteerStatusActive" to "Активен",
    "volunteerStatusRejected" to "Отклонён",
    "volunteerApplications" to "Заявки волонтёров",
    "noVolunteerApplications" to "Заявок волонтёров нет.",
    "petEditSuggestions" to "Предложения по изменению питомца",
    "noPetEditSuggestions" to "Нет ожидающих предложений по изменению.",
    "suggestedByLabel" to "предложено",
    "suggestAnEditTitle" to "Предложить изменение",
    "suggestAnEditExplanation" to "Как одобренный волонтёр, вы можете предлагать изменения к этому объявлению. Спасатель рассмотрит их перед публикацией.",
    "suggestEditBtn" to "Отправить предложение",
    "editSuggestionSent" to "Ваше предложение отправлено спасателю на рассмотрение!",
    "sponsorThisPetTitle" to "Спонсировать этого питомца",
    "sponsorExplanation" to "Не можете усыновить, но хотите помочь с расходами? Предложите деньги или помощь в натуральной форме и свяжитесь напрямую со спасателем.",
    "sponsorThisRescuerTitle" to "Спонсировать этого спасателя",
    "sponsorRescuerExplanation" to "Поддержите общие расходы этого спасателя деньгами или помощью в натуральной форме.",
    "sponsorTypeLabel" to "Чем вы можете помочь?",
    "sponsorTypeMoney" to "Деньги",
    "sponsorTypeInKind" to "Натуральная помощь (корм, товары и т.д.)",
    "amountLabel" to "Сумма (ежемесячно)",
    "currencyLabel" to "Валюта",
    "inKindDescriptionLabel" to "Опишите, чем вы можете помочь",
    "messageLabel" to "Сообщение",
    "sendOfferBtn" to "Отправить предложение",
    "sponsorOfferSent" to "Ваше предложение отправлено! Спасатель свяжется с вами, чтобы обсудить детали.",
    "sponsorshipOffersReceived" to "Предложения спонсорской помощи",
    "noSponsorshipOffers" to "Пока нет предложений спонсорской помощи.",
    "generalFundLabel" to "Общий фонд",
    "offeringLabel" to "Предложение",
    "sponsorshipStatusSent" to "Новое",
    "sponsorshipStatusRead" to "Прочитано",
    "markAsReadBtn" to "Отметить как прочитанное",
    "mySponsorshipOffers" to "Мои предложения спонсорской помощи",
    "noSponsorshipOffersYet" to "Вы ещё не отправляли предложений спонсорской помощи.",
    "howToSponsorHint" to "Чтобы стать спонсором, откройте страницу спасателя (общая поддержка) или страницу питомца (расходы на него) и отправьте предложение.",
    "browsePetsLink" to "Смотреть питомцев",
    "yourMessage" to "Ваше сообщение",
    "newPassword" to "Новый пароль",
    "passkeyDesc" to "Добавьте дополнительные passkey к вашему аккаунту для удобного входа с разных устройств.",
    "passkeyName" to "Название passkey",
    "pers" to "Люди",
    "petFoodDescription" to "Узнайте, какие продукты безопасны, вредны или токсичны для ваших питомцев",
    "petFoodGuide" to "Справочник по питанию питомцев",
    "foodInformation" to "Информация о продуктах",
    "petsForAdoption" to "Питомцы для усыновления",
    "chooseCountryPrompt" to "Найдите своего нового лучшего друга",
    "chooseCountryPromptHint" to "Выберите страну выше, чтобы просмотреть питомцев, доступных для усыновления рядом с вами.",
    "noPetsFound" to "Питомцы не найдены",
    "noPetsFoundHint" to "Попробуйте другой фильтр или загляните позже — новые питомцы добавляются регулярно.",
    "loadingMorePets" to "Загрузка новых питомцев…",
    "photos" to "Фотографии (макс. 12)",
    "profilePhotographerRole" to "Фотограф — предлагает услуги фотографии",
    "profileRescuerRole" to "Спасатель — публикует питомцев для усыновления",
    "profileTemporalHomeRole" to "Временный дом — предоставляет временное жильё для питомцев",
    "provideShelter" to "Приют — управляет приютом для животных",
    "provideSterilization" to "Служба стерилизации — предоставляет услуги стерилизации",
    "requestEmailChange" to "Запросить изменение email",
    "rescueDate" to "Дата спасения",
    "rescueLocation" to "Место спасения",
    "savePassword" to "Сохранить пароль",
    "selectEnergy" to "Выберите уровень активности",
    "selectSize" to "Выберите размер",
    "sex" to "Пол",
    "shelterSettings" to "Настройки приюта",
    "size" to "Размер",
    "small" to "Маленький",
    "specialNeeds" to "Особые потребности",
    "sterilizationSettings" to "Настройки стерилизации",
    "sterilized" to "Стерилизован",
    "temporalHomeSettings" to "Настройки временного дома",
    "temperament" to "Темперамент",
    "type" to "Тип *",
    "urgentAdoption" to "Требуется срочное усыновление",
    "vaccinations" to "Вакцинации",
    "vetDisclaimer" to "Проконсультируйтесь с ветеринаром перед изменением рациона питания.",
    "weight" to "Вес (кг)",
    "weightLabel" to "Вес",
    "website" to "Веб-сайт",
    "securitySettings" to "Настройки безопасности",
    "emailVerifiedTitle" to "Email успешно подтверждён!",
    "emailVerifiedMessage" to "Ваш email подтверждён. Регистрация завершена.",
    "redirectingIn" to "Перенаправление на главную страницу через ",
    "canAlsoLogin" to "Вы также можете ",
    "loginNow" to "Войти сейчас",
    "emailVerificationFailed" to "Ошибка подтверждения",
    "invalidOrExpiredToken" to "Ссылка для подтверждения недействительна или истекла.",
    "tryRegisteringAgain" to "Пожалуйста, попробуйте зарегистрироваться снова.",
    "verificationEmailResent" to "Срок действия письма для подтверждения истёк. Новое письмо отправлено. Проверьте вашу почту.",
    "emailVerificationSent" to "Письмо для подтверждения отправлено. Проверьте почту перед входом.",
    "emailNotVerifiedLogin" to "Пожалуйста, подтвердите ваш email перед входом. Проверьте почту — там есть ссылка для подтверждения.",
    "invalidOrExpiredMagicLink" to "Недействительная или истёкшая ссылка для входа. Пожалуйста, запросите новую.",
    "invalidToken" to "Недействительный токен.",
    "accountBanned" to "Ваш аккаунт заблокирован.",
    "loginError" to "Произошла ошибка входа.",
    "profileUpdated" to "Профиль обновлён!",
    "displayNameRequired" to "Отображаемое имя не может быть пустым",
    "saving" to "Сохранение...",
    "passwordSet" to "Пароль установлен",
    "noPassword" to "Пароль не установлен",
    "passwordChangeSuccess" to "Пароль успешно изменён!",
    "passwordChangeFailed" to "Не удалось изменить пароль",
    "passwordSetSuccess" to "Пароль успешно установлен!",
    "passwordSetFailed" to "Не удалось установить пароль",
    "passwordsDoNotMatch" to "Пароли не совпадают",
    "currentPasswordRequired" to "Требуется указать текущий пароль",
    "emailChangeSent" to "Письмо для подтверждения отправлено на ваш новый адрес email!",
    "emailChangeFailed" to "Не удалось запросить изменение email",
    "emailRequired" to "Требуется указать email",
    "sendingEllipsis" to "Отправка...",
    "resetLinkSent" to "Ссылка для сброса пароля отправлена! Проверьте почту.",
    "magicLinkSent" to "Ссылка для входа отправлена! Проверьте почту.",
    "magicLinkSendFailed" to "Не удалось отправить ссылку для входа",
    "failedSendResetLink" to "Не удалось отправить ссылку для сброса.",
    "invalidOrMissingToken" to "Недействительный или отсутствующий токен.",
    "passwordTooShort" to "Пароль должен содержать не менее 8 символов.",
    "passwordNeedUppercase" to "Пароль должен содержать хотя бы 1 заглавную букву.",
    "passwordNeedLowercase" to "Пароль должен содержать хотя бы 1 строчную букву.",
    "passwordNeedNumber" to "Пароль должен содержать хотя бы 1 цифру.",
    "passwordNeedSymbol" to "Пароль должен содержать хотя бы 1 символ.",
    "resettingPassword" to "Сброс пароля...",
    "passwordResetSuccess" to "Пароль успешно сброшен! Теперь вы можете войти.",
    "failedResetPassword" to "Не удалось сбросить пароль.",
    "loginSuccessRedirecting" to "Вход выполнен успешно! Перенаправление...",
    "loginFailedExpiredLink" to "Не удалось войти. Ссылка может быть недействительной или истёкшей.",
    "loginFailed" to "Не удалось войти.",
    "emailChangedSuccess" to "Email успешно изменён!",
    "failedChangeEmailExpired" to "Не удалось изменить email. Ссылка может быть недействительной или истёкшей.",
    "failedChangeEmail" to "Не удалось изменить email.",
    "emailVerifiedSuccess" to "Email успешно подтверждён!",
    "failedVerifyEmailExpired" to "Не удалось подтвердить email. Ссылка может быть недействительной или истёкшей.",
    "failedVerifyEmail" to "Не удалось подтвердить email.",
    "dropImagesHint" to "Перетащите изображения сюда или нажмите, чтобы выбрать",
    "forgotPasswordTitle" to "Забыли пароль",
    "forgotPasswordIntro" to "Введите свой email, и мы отправим вам ссылку для сброса пароля.",
    "sendResetLink" to "Отправить ссылку для сброса",
    "backToLogin" to "Назад ко входу",
    "resetPasswordTitle" to "Сброс пароля",
    "resetPasswordIntro" to "Введите ваш новый пароль ниже.",
    "emailLinkLoginTitle" to "Вход по ссылке из письма",
    "verifying" to "Проверка...",
    "emailChangeTitle" to "Изменение email",
    "verifyContactEmailTitle" to "Подтверждение контактного email",
    "status" to "Статус",
    "yes" to "Да",
    "no" to "Нет",
    "noDescription" to "Нет описания.",
    "urgentBadge" to "СРОЧНО - нужен дом как можно скорее!",
    "needsNewHome" to "Этому питомцу срочно нужен новый дом",
    "promotedReasonLabel" to "Причина",
    "selectReason" to "Выберите причину",
    "promotedReasonMoving" to "Владелец переезжает",
    "promotedReasonComplaints" to "Жалобы / не может держать больше животных",
    "promotedReasonPetConflict" to "Конфликт с другим питомцем",
    "promotedReasonOther" to "Другое",
    "promotedReasonDetailLabel" to "Дополнительные сведения (необязательно)",
    "promotedReasonRequired" to "Пожалуйста, выберите причину",
    "needsNewHomeBadge" to "Нужен новый дом",
    "housingType" to "Тип жилья",
    "preferNotToSay" to "Предпочитаю не указывать",
    "houseHousing" to "Дом",
    "apartmentHousing" to "Квартира",
    "hasYard" to "Есть двор",
    "hasOtherPets" to "Другие питомцы дома",
    "adoptionExperience" to "Опыт усыновления",
    "firstTimeAdopter" to "Впервые усыновляет",
    "experiencedAdopter" to "Опытный усыновитель",
    "noAdoptionRequests" to "Нет заявок на усыновление",
    "noMessage" to "Без сообщения",
    "markUnderReview" to "Отметить на рассмотрении",
    "approve" to "Одобрить",
    "reject" to "Отклонить",
    "reviewNoteLabel" to "Личная заметка для рассмотрения",
    "saveNote" to "Сохранить заметку",
    "adoptionStatusPending" to "В ожидании",
    "adoptionStatusUnderReview" to "На рассмотрении",
    "adoptionStatusApproved" to "Одобрено",
    "adoptionStatusRejected" to "Не одобрено",
    "myAdoptionRequests" to "Мои заявки на усыновление",
    "noAdoptionRequestsYet" to "Вы ещё не подавали заявку на усыновление ни одного питомца.",
    "medicalHistory" to "Медицинская история",
    "medicalSchedule" to "График вакцинации и дегельминтизации",
    "recordType" to "Тип",
    "vaccination" to "Вакцинация",
    "deworming" to "Дегельминтизация",
    "recordName" to "Название",
    "dateGiven" to "Дата проведения",
    "nextDueDate" to "Следующий раз (необязательно)",
    "notesOptional" to "Заметки (необязательно)",
    "addRecord" to "Добавить запись",
    "noMedicalRecords" to "Пока нет записей о вакцинации или дегельминтизации.",
    "nextDueLabel" to "Следующий раз",
    "givenLabel" to "Проведено",
    "overdue" to "Просрочено",
    "dueSoon" to "Скоро срок",
    "upToDate" to "Актуально",
    "recordNameRequired" to "Требуется указать название",
    "dateGivenRequired" to "Требуется указать дату проведения",
    "failedToAddRecord" to "Не удалось добавить запись",
    "confirmDeleteRecord" to "Удалить эту запись?",
    "maxCapacity" to "Макс. питомцев одновременно (необязательно)",
    "placePetHere" to "Разместить питомца здесь",
    "noAvailablePetsToPlace" to "У вас сейчас нет доступных питомцев для размещения.",
    "selectPet" to "Выберите питомца",
    "startPlacementBtn" to "Начать размещение",
    "placementStarted" to "Временное размещение начато!",
    "petsCurrentlyWithMe" to "Питомцы, которые сейчас у меня",
    "noPetsCurrentlyFostered" to "Сейчас у вас нет питомцев на временном содержании.",
    "sinceLabel" to "с",
    "fosterPlacement" to "Временное размещение",
    "endPlacement" to "Завершить размещение",
    "currentlyWithLabel" to "Сейчас у",
    "notCurrentlyFostered" to "Сейчас не находится на временном содержании.",
    "confirmEndPlacement" to "Завершить это временное размещение? Питомец будет отмечен как вернувшийся к вам.",
    "aTemporalHome" to "временный дом",
    "viewsLabel" to "Просмотры",
    "inquiriesLabel" to "Запросы",
    "conversionLabel" to "Конверсия",
    "messageOptional" to "Сообщение (необязательно)",
    "requestAdoption" to "Запросить усыновление",
    "editPet" to "Редактировать питомца",
    "adoptionRequestSubmitted" to "Заявка на усыновление отправлена!",
    "failedSubmitRequest" to "Не удалось отправить заявку",
    "noPhotosYet" to "Пока нет фотографий.",
    "rabbit" to "Кролик",
    "safeFoods" to "Безопасные продукты",
    "harmfulFoods" to "Вредные продукты",
    "cannotEatFoods" to "Нельзя давать",
    "legendSafe" to "Безопасно",
    "legendHarmful" to "Вредно",
    "legendToxic" to "Токсично",
    "food_dog_safe_chicken_name" to "Курица",
    "food_dog_safe_chicken_detail" to "Варёная, без костей",
    "food_dog_safe_chicken_desc" to "Нежирный белок, легко усваивается",
    "food_dog_safe_rice_name" to "Рис",
    "food_dog_safe_rice_detail" to "Белый или бурый",
    "food_dog_safe_rice_desc" to "Хороший источник углеводов",
    "food_dog_safe_carrots_name" to "Морковь",
    "food_dog_safe_carrots_detail" to "Сырая или варёная",
    "food_dog_safe_carrots_desc" to "Низкокалорийная, богата клетчаткой",
    "food_dog_safe_apples_name" to "Яблоки",
    "food_dog_safe_apples_detail" to "Без косточек",
    "food_dog_safe_apples_desc" to "Богаты витаминами",
    "food_dog_safe_peanutbutter_name" to "Арахисовая паста",
    "food_dog_safe_peanutbutter_detail" to "Несолёная, без ксилита",
    "food_dog_safe_peanutbutter_desc" to "Хороший источник белка",
    "food_dog_harmful_grapes_name" to "Виноград",
    "food_dog_harmful_grapes_detail" to "Все сорта",
    "food_dog_harmful_grapes_desc" to "Может вызвать почечную недостаточность",
    "food_dog_harmful_onions_name" to "Лук",
    "food_dog_harmful_onions_detail" to "В любом виде",
    "food_dog_harmful_onions_desc" to "Повреждает эритроциты",
    "food_dog_harmful_chocolate_name" to "Шоколад",
    "food_dog_harmful_chocolate_detail" to "Любые виды",
    "food_dog_harmful_chocolate_desc" to "Токсичен для собак",
    "food_dog_harmful_garlic_name" to "Чеснок",
    "food_dog_harmful_garlic_detail" to "В любом виде",
    "food_dog_harmful_garlic_desc" to "Вызывает анемию",
    "food_dog_cannot_xylitol_name" to "Ксилит",
    "food_dog_cannot_xylitol_detail" to "Искусственный подсластитель",
    "food_dog_cannot_xylitol_desc" to "Очень токсичен",
    "food_dog_cannot_macadamia_name" to "Орехи макадамия",
    "food_dog_cannot_macadamia_detail" to "В любом виде",
    "food_dog_cannot_macadamia_desc" to "Вызывает слабость",
    "food_dog_cannot_avocado_name" to "Авокадо",
    "food_dog_cannot_avocado_detail" to "Косточка, кожура, всё",
    "food_dog_cannot_avocado_desc" to "Содержит персин",
    "food_dog_cannot_alcohol_name" to "Алкоголь",
    "food_dog_cannot_alcohol_detail" to "В любом виде",
    "food_dog_cannot_alcohol_desc" to "Очень токсичен",
    "food_cat_safe_cookedfish_name" to "Варёная рыба",
    "food_cat_safe_cookedfish_detail" to "Без костей",
    "food_cat_safe_cookedfish_desc" to "Богата белком",
    "food_cat_safe_chicken_name" to "Курица",
    "food_cat_safe_chicken_detail" to "Варёная, без специй",
    "food_cat_safe_chicken_desc" to "Хороший источник белка",
    "food_cat_safe_pumpkin_name" to "Тыква",
    "food_cat_safe_pumpkin_detail" to "Обычная, варёная",
    "food_cat_safe_pumpkin_desc" to "Помогает пищеварению",
    "food_cat_safe_eggs_name" to "Яйца",
    "food_cat_safe_eggs_detail" to "Варёные",
    "food_cat_safe_eggs_desc" to "Полноценный белок",
    "food_cat_harmful_raweggs_name" to "Сырые яйца",
    "food_cat_harmful_raweggs_detail" to "Содержат авидин",
    "food_cat_harmful_raweggs_desc" to "Мешает усвоению биотина",
    "food_cat_harmful_rawfish_name" to "Сырая рыба",
    "food_cat_harmful_rawfish_detail" to "Содержит тиаминазу",
    "food_cat_harmful_rawfish_desc" to "Разрушает витамины группы B",
    "food_cat_harmful_dogfood_name" to "Корм для собак",
    "food_cat_harmful_dogfood_detail" to "Любой",
    "food_cat_harmful_dogfood_desc" to "Не содержит таурин",
    "food_cat_harmful_milk_name" to "Молоко",
    "food_cat_harmful_milk_detail" to "У большинства кошек",
    "food_cat_harmful_milk_desc" to "Непереносимость лактозы",
    "food_cat_cannot_chocolate_name" to "Шоколад",
    "food_cat_cannot_chocolate_detail" to "Любые виды",
    "food_cat_cannot_chocolate_desc" to "Теобромин токсичен",
    "food_cat_cannot_onionsgarlic_name" to "Лук/Чеснок",
    "food_cat_cannot_onionsgarlic_detail" to "В любом виде",
    "food_cat_cannot_onionsgarlic_desc" to "Повреждает эритроциты",
    "food_cat_cannot_grapesraisins_name" to "Виноград/Изюм",
    "food_cat_cannot_grapesraisins_detail" to "Все сорта",
    "food_cat_cannot_grapesraisins_desc" to "Повреждение почек",
    "food_cat_cannot_alcohol_name" to "Алкоголь",
    "food_cat_cannot_alcohol_detail" to "В любом виде",
    "food_cat_cannot_alcohol_desc" to "Очень токсичен",
    "food_bird_safe_seeds_name" to "Семена",
    "food_bird_safe_seeds_detail" to "Смесь разных видов",
    "food_bird_safe_seeds_desc" to "Хороший источник жиров",
    "food_bird_safe_fruits_name" to "Фрукты",
    "food_bird_safe_fruits_detail" to "Большинство видов",
    "food_bird_safe_fruits_desc" to "Витамины и минералы",
    "food_bird_safe_vegetables_name" to "Овощи",
    "food_bird_safe_vegetables_detail" to "Листовая зелень",
    "food_bird_safe_vegetables_desc" to "Необходимые питательные вещества",
    "food_bird_safe_pellets_name" to "Гранулы",
    "food_bird_safe_pellets_detail" to "Качественный корм",
    "food_bird_safe_pellets_desc" to "Полноценное питание",
    "food_bird_harmful_avocado_name" to "Авокадо",
    "food_bird_harmful_avocado_detail" to "Все части",
    "food_bird_harmful_avocado_desc" to "Персин токсичен",
    "food_bird_harmful_fruitpits_name" to "Косточки фруктов",
    "food_bird_harmful_fruitpits_detail" to "Яблоко, персик",
    "food_bird_harmful_fruitpits_desc" to "Следы цианида",
    "food_bird_harmful_salt_name" to "Соль",
    "food_bird_harmful_salt_detail" to "В любом виде",
    "food_bird_harmful_salt_desc" to "Токсична для птиц",
    "food_bird_harmful_caffeine_name" to "Кофеин",
    "food_bird_harmful_caffeine_detail" to "Кофе, чай",
    "food_bird_harmful_caffeine_desc" to "Проблемы с сердцем",
    "food_bird_cannot_chocolate_name" to "Шоколад",
    "food_bird_cannot_chocolate_detail" to "Любые виды",
    "food_bird_cannot_chocolate_desc" to "Теобромин токсичен",
    "food_bird_cannot_onions_name" to "Лук",
    "food_bird_cannot_onions_detail" to "В любом виде",
    "food_bird_cannot_onions_desc" to "Повреждает эритроциты",
    "food_bird_cannot_garlic_name" to "Чеснок",
    "food_bird_cannot_garlic_detail" to "В любом виде",
    "food_bird_cannot_garlic_desc" to "Токсичен",
    "food_bird_cannot_mushrooms_name" to "Грибы",
    "food_bird_cannot_mushrooms_detail" to "Дикорастущие виды",
    "food_bird_cannot_mushrooms_desc" to "Потенциально токсичны",
    "food_fish_safe_flakes_name" to "Хлопья",
    "food_fish_safe_flakes_detail" to "Качественные бренды",
    "food_fish_safe_flakes_desc" to "Полноценный корм для рыб",
    "food_fish_safe_pellets_name" to "Гранулы",
    "food_fish_safe_pellets_detail" to "Плавающие/тонущие",
    "food_fish_safe_pellets_desc" to "Сбалансированное питание",
    "food_fish_safe_frozenfood_name" to "Замороженный корм",
    "food_fish_safe_frozenfood_detail" to "Мотыль, артемия",
    "food_fish_safe_frozenfood_desc" to "Источник белка",
    "food_fish_safe_vegetables_name" to "Овощи",
    "food_fish_safe_vegetables_detail" to "Бланшированный кабачок",
    "food_fish_safe_vegetables_desc" to "Источник клетчатки",
    "food_fish_harmful_bread_name" to "Хлеб",
    "food_fish_harmful_bread_detail" to "Любой вид",
    "food_fish_harmful_bread_desc" to "Не питателен, вызывает переедание",
    "food_fish_harmful_humanfood_name" to "Еда с человеческого стола",
    "food_fish_harmful_humanfood_detail" to "Приготовленные блюда",
    "food_fish_harmful_humanfood_desc" to "Неподходящее питание",
    "food_fish_harmful_livefeed_name" to "Живой корм",
    "food_fish_harmful_livefeed_detail" to "Дикие насекомые",
    "food_fish_harmful_livefeed_desc" to "Возможные паразиты",
    "food_fish_cannot_landinsect_name" to "Наземные насекомые",
    "food_fish_cannot_landinsect_detail" to "Ползающие насекомые",
    "food_fish_cannot_landinsect_desc" to "Неестественный корм",
    "food_fish_cannot_mammalmeat_name" to "Мясо млекопитающих",
    "food_fish_cannot_mammalmeat_detail" to "Курица, говядина",
    "food_fish_cannot_mammalmeat_desc" to "Не подходит большинству рыб",
    "food_fish_cannot_dairy_name" to "Молочные продукты",
    "food_fish_cannot_dairy_detail" to "Любые",
    "food_fish_cannot_dairy_desc" to "Не усваиваются",
    "food_fish_cannot_breadcrumbs_name" to "Панировочные сухари",
    "food_fish_cannot_breadcrumbs_detail" to "Многие содержат чеснок",
    "food_fish_cannot_breadcrumbs_desc" to "Вредные добавки",
    "food_rabbit_safe_hay_name" to "Сено",
    "food_rabbit_safe_hay_detail" to "Тимофеевка",
    "food_rabbit_safe_hay_desc" to "80% рациона",
    "food_rabbit_safe_leafygreens_name" to "Листовая зелень",
    "food_rabbit_safe_leafygreens_detail" to "Ромэн, кинза",
    "food_rabbit_safe_leafygreens_desc" to "Ежедневные витамины",
    "food_rabbit_safe_pellets_name" to "Гранулы",
    "food_rabbit_safe_pellets_detail" to "На основе тимофеевки",
    "food_rabbit_safe_pellets_desc" to "Сбалансированное питание",
    "food_rabbit_safe_carrots_name" to "Морковь",
    "food_rabbit_safe_carrots_detail" to "В умеренных количествах",
    "food_rabbit_safe_carrots_desc" to "Лакомство",
    "food_rabbit_harmful_icebergletttuce_name" to "Салат айсберг",
    "food_rabbit_harmful_icebergletttuce_detail" to "Не питателен",
    "food_rabbit_harmful_icebergletttuce_desc" to "Может вызвать застой ЖКТ",
    "food_rabbit_harmful_beans_name" to "Бобы",
    "food_rabbit_harmful_beans_detail" to "Все бобовые",
    "food_rabbit_harmful_beans_desc" to "Газообразование, проблемы с пищеварением",
    "food_rabbit_harmful_corn_name" to "Кукуруза",
    "food_rabbit_harmful_corn_detail" to "В любом виде",
    "food_rabbit_harmful_corn_desc" to "Не усваивается",
    "food_rabbit_harmful_potatoes_name" to "Картофель",
    "food_rabbit_harmful_potatoes_detail" to "В любом виде",
    "food_rabbit_harmful_potatoes_desc" to "Неподходящие питательные вещества",
    "food_rabbit_cannot_chocolate_name" to "Шоколад",
    "food_rabbit_cannot_chocolate_detail" to "Любые виды",
    "food_rabbit_cannot_chocolate_desc" to "Теобромин токсичен",
    "food_rabbit_cannot_onions_name" to "Лук",
    "food_rabbit_cannot_onions_detail" to "В любом виде",
    "food_rabbit_cannot_onions_desc" to "Очень токсичен",
    "food_rabbit_cannot_garlic_name" to "Чеснок",
    "food_rabbit_cannot_garlic_detail" to "В любом виде",
    "food_rabbit_cannot_garlic_desc" to "Токсичен",
    "food_rabbit_cannot_avocado_name" to "Авокадо",
    "food_rabbit_cannot_avocado_detail" to "Все части",
    "food_rabbit_cannot_avocado_desc" to "Содержит персин",
    "primary" to "Основной",
    "passkeyRegisteredSuccess" to "Passkey успешно зарегистрирован!",
    "passkeyRegistrationFailed" to "Не удалось зарегистрировать passkey",
    "passkeyNameRequired" to "Требуется указать название passkey",
    "registeringPasskey" to "Регистрация passkey...",
    "passkeyRegistered" to "Passkey зарегистрирован",
    "noPasskey" to "Passkey не зарегистрирован",
    "noPets" to "Пока нет питомцев.",
    "photographerCountryStateRequired" to "Пожалуйста, укажите страну и регион для услуг фотографа",
    "temporalHomeAliasCountryCityRequired" to "Пожалуйста, укажите псевдоним, страну и город для временного дома",
    "shelterNameCountryCityAddressRequired" to "Пожалуйста, укажите название, страну, город и адрес приюта",
    "sterilizationNameCountryCityAddressRequired" to "Пожалуйста, укажите название, страну, город и адрес места стерилизации",
    "country.afghanistan" to "Афганистан",
    "country.albania" to "Албания",
    "country.algeria" to "Алжир",
    "country.argentina" to "Аргентина",
    "country.armenia" to "Армения",
    "country.australia" to "Австралия",
    "country.austria" to "Австрия",
    "country.azerbaijan" to "Азербайджан",
    "country.bangladesh" to "Бангладеш",
    "country.belarus" to "Беларусь",
    "country.belgium" to "Бельгия",
    "country.bolivia" to "Боливия",
    "country.bosnia" to "Босния и Герцеговина",
    "country.brazil" to "Бразилия",
    "country.bulgaria" to "Болгария",
    "country.cambodia" to "Камбоджа",
    "country.cameroon" to "Камерун",
    "country.canada" to "Канада",
    "country.chile" to "Чили",
    "country.china" to "Китай",
    "country.colombia" to "Колумбия",
    "country.costaRica" to "Коста-Рика",
    "country.croatia" to "Хорватия",
    "country.cuba" to "Куба",
    "country.czechia" to "Чехия",
    "country.denmark" to "Дания",
    "country.dominicanRepublic" to "Доминиканская Республика",
    "country.ecuador" to "Эквадор",
    "country.egypt" to "Египет",
    "country.elSalvador" to "Сальвадор",
    "country.estonia" to "Эстония",
    "country.ethiopia" to "Эфиопия",
    "country.finland" to "Финляндия",
    "country.france" to "Франция",
    "country.georgia" to "Грузия",
    "country.germany" to "Германия",
    "country.ghana" to "Гана",
    "country.greece" to "Греция",
    "country.guatemala" to "Гватемала",
    "country.haiti" to "Гаити",
    "country.honduras" to "Гондурас",
    "country.hungary" to "Венгрия",
    "country.iceland" to "Исландия",
    "country.india" to "Индия",
    "country.indonesia" to "Индонезия",
    "country.iran" to "Иран",
    "country.iraq" to "Ирак",
    "country.ireland" to "Ирландия",
    "country.israel" to "Израиль",
    "country.italy" to "Италия",
    "country.jamaica" to "Ямайка",
    "country.japan" to "Япония",
    "country.jordan" to "Иордания",
    "country.kazakhstan" to "Казахстан",
    "country.kenya" to "Кения",
    "country.kuwait" to "Кувейт",
    "country.latvia" to "Латвия",
    "country.lebanon" to "Ливан",
    "country.libya" to "Ливия",
    "country.lithuania" to "Литва",
    "country.luxembourg" to "Люксембург",
    "country.malaysia" to "Малайзия",
    "country.mexico" to "Мексика",
    "country.moldova" to "Молдова",
    "country.mongolia" to "Монголия",
    "country.montenegro" to "Черногория",
    "country.morocco" to "Марокко",
    "country.myanmar" to "Мьянма",
    "country.nepal" to "Непал",
    "country.netherlands" to "Нидерланды",
    "country.newZealand" to "Новая Зеландия",
    "country.nicaragua" to "Никарагуа",
    "country.nigeria" to "Нигерия",
    "country.northKorea" to "Северная Корея",
    "country.norway" to "Норвегия",
    "country.pakistan" to "Пакистан",
    "country.panama" to "Панама",
    "country.paraguay" to "Парагвай",
    "country.peru" to "Перу",
    "country.philippines" to "Филиппины",
    "country.poland" to "Польша",
    "country.portugal" to "Португалия",
    "country.puertoRico" to "Пуэрто-Рико",
    "country.qatar" to "Катар",
    "country.romania" to "Румыния",
    "country.russia" to "Россия",
    "country.saudiArabia" to "Саудовская Аравия",
    "country.serbia" to "Сербия",
    "country.singapore" to "Сингапур",
    "country.slovakia" to "Словакия",
    "country.slovenia" to "Словения",
    "country.southAfrica" to "Южно-Африканская Республика",
    "country.southKorea" to "Южная Корея",
    "country.spain" to "Испания",
    "country.sriLanka" to "Шри-Ланка",
    "country.sudan" to "Судан",
    "country.sweden" to "Швеция",
    "country.switzerland" to "Швейцария",
    "country.syria" to "Сирия",
    "country.taiwan" to "Тайвань",
    "country.thailand" to "Таиланд",
    "country.tunisia" to "Тунис",
    "country.turkey" to "Турция",
    "country.ukraine" to "Украина",
    "country.uae" to "Объединённые Арабские Эмираты",
    "country.uk" to "Великобритания",
    "country.usa" to "США",
    "country.uruguay" to "Уругвай",
    "country.uzbekistan" to "Узбекистан",
    "country.venezuela" to "Венесуэла",
    "country.vietnam" to "Вьетнам",
    "country.yemen" to "Йемен",
    "loading" to "Загрузка...",
    "countryRequired" to "Пожалуйста, выберите страну для поиска",
    "noSheltersFound" to "Приюты пока не найдены. Загляните позже!",
    "visitWebsite" to "Посетить сайт",
    "fiscalId" to "Налоговый номер",
    "donationInformation" to "Информация о пожертвованиях",
    "bankName" to "Банк",
    "accountHolder" to "Владелец счёта",
    "accountNumber" to "Номер счёта",
    "currency" to "Валюта",
    "errorLoadingShelters" to "Ошибка загрузки приютов",
    "pleaseSelectCountry" to "Пожалуйста, выберите страну",
    "actions" to "Действия",
    "addShelter" to "Добавить приют",
    "addEditShelter" to "Добавить / Редактировать приют",
    "allStates" to "Все регионы",
    "confirmDelete" to "Вы уверены, что хотите удалить это местоположение?",
    "confirmDeleteShelter" to "Вы уверены, что хотите удалить этот приют?",
    "confirmUnbanUser" to "Разблокировать этого пользователя?",
    "confirmResetUserCredentials" to "Это аннулирует текущий пароль и passkey пользователя {email} и отправит ему письмо со ссылкой для сброса. Продолжить?",
    "confirmDeactivateUser" to "Деактивировать этого пользователя? Его можно будет восстановить позже.",
    "confirmDeactivatePet" to "Деактивировать этого питомца? Он будет скрыт из публичных объявлений, но его можно будет восстановить позже.",
    "confirmBlockRescuer" to "Заблокировать этого спасателя, чтобы он больше не мог отправлять вам запросы?",
    "confirmDeletePet" to "Удалить этого питомца?",
    "confirmDeleteStorage" to "Удалить это хранилище?",
    "contact" to "Контакт",
    "delete" to "Удалить",
    "edit" to "Редактировать",
    "errorDeletingShelter" to "Ошибка удаления приюта",
    "errorLoadingLocations" to "Ошибка загрузки местоположений",
    "errorLoadingShelter" to "Ошибка загрузки приюта",
    "errorSaving" to "Ошибка сохранения",
    "errorSavingShelter" to "Ошибка сохранения приюта",
    "location" to "Местоположение",
    "noLocationsFound" to "Местоположения не найдены",
    "selectCountry" to "Выберите страну",
    "selectCountryToFilter" to "Пожалуйста, выберите страну для фильтрации",
    "shelterAdded" to "Приют успешно добавлен",
    "shelterDeleted" to "Приют успешно удалён",
    "shelterUpdated" to "Приют успешно обновлён",
    "updateShelter" to "Обновить приют",
    "errorLoadingPhotographers" to "Ошибка загрузки фотографов",
    "noPhotographersAvailable" to "Нет доступных фотографов",
    "requestPhotoSession" to "Запросить фотосессию",
    "selectPhotographer" to "Выбрать",
    "photographersSelected" to "Выбрано: {count}",
    "requestSelectedPhotographers" to "Запросить выбранных фотографов",
    "clearSelection" to "Очистить выбор",
    "requestToMultiple" to "Запросить у",
    "messageRequired" to "Пожалуйста, напишите сообщение для фотографов.",
    "requestsSentSuccessfully" to "Запросы успешно отправлены",
    "message" to "Сообщение",
    "sendRequest" to "Отправить запрос",
    "requestSentSuccessfully" to "Запрос успешно отправлен!",
    "requestTo" to "Запрос к",
    "enterMessage" to "Введите сообщение для {name}...",
    "reportUrgent" to "Срочно",
    "topRescuers" to "Лучшие спасатели",
    "urgentRescuerSettings" to "Настройки срочного спасателя",
    "urgentRescueAlerts" to "Оповещения о срочном спасении",
    "reportUrgentTitle" to "Сообщить о питомце в опасности",
    "reportUrgentIntro" to "Если вы нашли раненого, подвергшегося жестокому обращению, голодающего или брошенного питомца, которому нужна немедленная помощь, сообщите нам, где и что случилось — мы сразу оповестим ближайших срочных спасателей.",
    "dangerType" to "Что случилось?",
    "dangerInjured" to "Ранен",
    "dangerAbused" to "Подвергается жестокому обращению",
    "dangerStarving" to "Голодает",
    "dangerTooYoung" to "Слишком мал, чтобы выжить один",
    "dangerOther" to "Другая экстренная ситуация",
    "useMyLocation" to "Использовать моё текущее местоположение",
    "confirmMyLocation" to "Подтвердить моё текущее местоположение",
    "locationPermissionHint" to "Браузер запросит разрешение на доступ к вашему местоположению; оно используется только для оповещений о сообщениях поблизости.",
    "phoneOptional" to "Ваш телефон (необязательно)",
    "submitReport" to "Отправить сообщение",
    "urgentRescuerSettingsTitle" to "Настройки срочного спасателя",
    "urgentRescuerSettingsIntro" to "Получайте оповещения, когда рядом с зоной вашего покрытия сообщают о питомце в опасности. Кто первым откликнется, берёт дело на себя.",
    "urgentRescuerActive" to "Я готов реагировать на срочные сообщения",
    "coverageAreaMode" to "Зона покрытия",
    "coverageArea" to "Зона покрытия",
    "coverageAreaHint" to "Подтвердите текущее местоположение, перетащите метку или введите адрес ниже - карта и поля будут синхронизированы.",
    "useMyLocationAndRadius" to "Использовать моё местоположение + радиус",
    "pickAZone" to "Выбрать страну/регион/город вместо этого",
    "radiusKm" to "Радиус (км)",
    "urgentRescueAlertsTitle" to "Оповещения о срочном спасении",
    "urgentRescuerLeaderboardTitle" to "Лучшие срочные спасатели (за последние 12 месяцев)",
    "locating" to "Определение местоположения...",
    "geolocationUnsupported" to "Геолокация не поддерживается вашим браузером",
    "locationCaptured" to "Местоположение определено",
    "locationDenied" to "Не удалось определить ваше местоположение — пожалуйста, разрешите доступ к геолокации или укажите город вручную",
    "street" to "Улица",
    "exteriorNumber" to "Номер дома",
    "referenceNotes" to "Ориентиры (например, цвет дома, ближайший ориентир)",
    "concreteAddressHint" to "По возможности укажите улицу и номер дома — это позволит спасателю вызвать такси или сразу открыть карту с нужным местом.",
    "descriptionRequired" to "Пожалуйста, опишите ситуацию",
    "locationRequired" to "Пожалуйста, укажите местоположение (используйте текущее местоположение или введите страну и город)",
    "submittingEllipsis" to "Отправка...",
    "reportSubmitted" to "Сообщение отправлено. Спасибо — ближайшие спасатели уже оповещаются.",
    "reportSubmitFailed" to "Не удалось отправить сообщение",
    "phoneRequired" to "Пожалуйста, введите номер телефона",
    "savingEllipsis" to "Сохранение...",
    "saved" to "Сохранено",
    "saveFailed" to "Не удалось сохранить",
    "noUrgentPages" to "Сейчас нет ожидающих срочных оповещений",
    "accept" to "Принять",
    "alreadyAccepted" to "Кто-то уже принял это сообщение",
    "noLeaderboardData" to "Пока нет принятых спасений за последние 12 месяцев",
    "rescueAcceptedTitle" to "Спасение принято",
    "rescueAcceptedMessage" to "Спасибо — вы взялись за это дело. Отправляйтесь по указанному адресу как можно скорее.",
    "rescueAcceptFailedTitle" to "Не удалось принять",
    "rescueAcceptFailedMessage" to "Это сообщение уже было принято другим спасателем, либо ссылка недействительна или истекла.",
    "share" to "Поделиться",
    "shareViaFacebook" to "Поделиться в Facebook",
    "shareViaX" to "Поделиться в X",
    "shareViaWhatsapp" to "Поделиться в WhatsApp",
    "shareViaEmail" to "Поделиться по эл. почте",
    "copyLink" to "Скопировать ссылку",
    "linkCopied" to "Ссылка скопирована!",
    "reportLostFoundTitle" to "Сообщить о потерянном или найденном питомце",
    "reportLostFoundIntro" to "Потеряли питомца или нашли бездомного? Оставьте сообщение, и мы сверим его с другими сообщениями поблизости.",
    "browseLostFound" to "Просмотреть существующие сообщения о потерянных и найденных",
    "kindLost" to "Я потерял питомца",
    "kindFound" to "Я нашёл питомца",
    "petType" to "Тип питомца",
    "petTypeUnknown" to "Не уверен(а)",
    "lostFoundBrowseTitle" to "Потерянные и найденные питомцы",
    "showing" to "Показано",
    "kindLostPlural" to "Потерянные питомцы",
    "kindFoundPlural" to "Найденные питомцы",
    "reportResolvedTitle" to "Сообщение решено",
    "reportResolvedMessage" to "Спасибо за обновление — это сообщение теперь закрыто.",
    "reportResolveFailedTitle" to "Не удалось закрыть",
    "reportResolveFailedMessage" to "Это сообщение уже было решено, либо ссылка недействительна или истекла.",
    "reportLostFound" to "Потери/\u200Bнаходки",
    "noLostFoundReportsFound" to "Сообщения о потерянных и найденных не найдены",
    "errorLoadingReports" to "Ошибка загрузки сообщений",
    "yourEmail" to "Ваш email",
    "contactReporter" to "Связаться с автором сообщения",
    "contactFormIncomplete" to "Пожалуйста, введите ваш email и сообщение",
    "contactSent" to "Сообщение отправлено",
    "contactFailed" to "Не удалось отправить сообщение",
    "lostFoundReportSubmitted" to "Сообщение отправлено. Спасибо — мы сверим его с другими сообщениями поблизости.",
    "video" to "Видео (необязательно)",
    "removeVideo" to "Удалить видео",
    "saveThisSearch" to "Сохранить этот поиск",
    "savedSearchSaved" to "Поиск сохранён! Мы напишем вам на email, когда появится новое совпадение.",
    "savedSearchFailed" to "Не удалось сохранить поиск",
    "favorite" to "Избранное",
    "addToFavorites" to "Добавить в избранное",
    "removeFromFavorites" to "Удалить из избранного",
    "myFavorites" to "Мои избранные",
    "noFavoritesYet" to "Пока нет избранных",
    "mySavedSearches" to "Мои сохранённые поиски",
    "noSavedSearchesYet" to "Пока нет сохранённых поисков",
    "donationPromptText" to "Спасибо, что помогаете питомцу сегодня. Если можете, рассмотрите небольшое пожертвование, чтобы Adopt-U оставался бесплатным для всех.",
    "donationPromptCta" to "Пожертвовать",
    "dismiss" to "Скрыть",
)

private val koreanTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "로그인",
    "register" to "회원가입",
    "logout" to "로그아웃",
    "profile" to "프로필",
    "myPets" to "내 반려동물",
    "addPet" to "반려동물 등록",
    "save" to "저장",
    "cancel" to "취소",
    "search" to "검색",
    "shelters" to "보호소",
    "photographers" to "사진작가",
    "temporalHomes" to "임시보호처",
    "findTemporalHomes" to "임시보호처 찾기",
    "sterilizationLocations" to "중성화 시술 장소",
    "donate" to "후원하기",
    "countryLabel" to "국가",
    "selectCountryToSearch" to "국가를 선택하세요",
    "selectCountryFirst" to "이 필터를 사용하려면 먼저 국가를 선택하세요",
    "state" to "주/도",
    "city" to "도시",
    "zipCode" to "우편번호",
    "neighborhood" to "동네",
    "name" to "이름",
    "email" to "이메일",
    "phone" to "전화번호",
    "address" to "주소",
    "description" to "설명",
    "animalShelters" to "동물 보호소",
    "sheltersDescription" to "입양할 반려동물을 찾거나 보호소와 사전 협의를 통해 반려동물을 맡길 수 있는 동물 보호소를 찾아보세요",
    "photographerDescription" to "반려동물 사진 촬영 서비스를 제공하는 전문 사진작가",
    "sterilizationLocationsDescription" to "동물의 중성화 시술을 받을 수 있는 장소를 찾아보세요.",
    "privacyPolicy" to "개인정보 처리방침",
    "privacyLastUpdated" to "최종 업데이트: 2025년 4월",
    "privacyHighlight" to "Adopt-U는 최소한의 데이터 수집을 원칙으로 합니다. 입양자와 구조자 간의 소통을 위해 이메일 주소만 수집합니다. 회원님의 정보를 광고에 사용하지 않으며, 마케팅 목적으로 제3자와 데이터를 공유하는 일은 결코 없습니다.",
    "privacyInfoCollectedH2" to "수집하는 정보",
    "privacyAccountRegH3" to "계정 등록",
    "privacyAccountRegIntro" to "Adopt-U 계정을 만들기 위해 다음 정보를 수집합니다:",
    "privacyEmailLabel" to "이메일 주소: ",
    "privacyEmailDesc" to "계정 인증, 비밀번호 복구, 입양자와 구조자 간 소통에 필요",
    "privacyDisplayNameLabel" to "표시 이름: ",
    "privacyDisplayNameDesc" to "플랫폼에서 회원님을 식별하기 위해 선택하는 이름",
    "privacyLangPrefLabel" to "언어 설정: ",
    "privacyLangPrefDesc" to "선호하는 언어로 플랫폼을 제공하기 위함",
    "privacyNoFullNameEtc" to "위에 나열된 것 외에 실명, 전화번호, 실제 주소, 신분증명 서류, 결제 정보 등 다른 개인정보는 수집하지 않습니다.",
    "privacyOptionalProfileH3" to "선택적 프로필 정보",
    "privacyOptionalProfileIntro" to "역할에 따라 다음 정보를 선택적으로 제공할 수 있습니다:",
    "privacyAdopterLabel" to "입양자로서: ",
    "privacyAdopterDesc" to "추가 정보가 필요하거나 수집되지 않습니다",
    "privacyRescuerLabel" to "구조자로서: ",
    "privacyRescuerDesc" to "입양 가능한 반려동물의 위치 정보",
    "privacyPhotographerLabel" to "사진작가로서: ",
    "privacyPhotographerDesc" to "서비스 지역 및 연락 선호도",
    "privacyTemporalHomeLabel" to "임시보호처로서: ",
    "privacyTemporalHomeDesc" to "임시 거처를 제공할 수 있는 위치",
    "privacyLocationUsage" to "모든 위치 정보는 입양자와 인근 구조자 및 서비스 제공자를 연결하기 위한 목적으로만 사용됩니다.",
    "privacyPetInfoH3" to "반려동물 정보",
    "privacyPetInfoIntro" to "구조자는 다음을 포함한 반려동물 정보를 게시할 수 있습니다:",
    "privacyPetPhotos" to "반려동물 사진",
    "privacyPetDescBreedAge" to "설명, 품종, 나이 및 특징",
    "privacyPetHealth" to "건강 상태 및 예방접종 정보",
    "privacyPetLocation" to "반려동물이 있는 위치",
    "privacyPetInfoNotVerified" to "이 정보는 구조자가 제공한 것이며 Adopt-U가 독립적으로 검증하지 않습니다.",
    "privacyHowWeUseH2" to "정보 이용 방법",
    "privacyPrimaryPurposeH3" to "주요 목적: 입양 연결 지원",
    "privacyEmailUsedFor" to "회원님의 이메일 주소는 다음 목적으로만 사용됩니다:",
    "privacyUseVerification" to "계정 생성 시 인증 링크 발송",
    "privacyUseRescuerContact" to "관심 있는 반려동물에 대해 구조자가 회원님께 연락할 수 있도록 함",
    "privacyUseContactRescuer" to "게시된 반려동물에 대해 회원님이 구조자에게 연락할 수 있도록 함",
    "privacyUseNotifications" to "중요한 계정 및 보안 알림 발송",
    "privacyOnceConnected" to "플랫폼을 통해 다른 사용자와 연결된 이후의 모든 소통은 당사자 간에 직접 이루어집니다. 당사는 해당 소통 내용을 모니터링, 저장하거나 접근하지 않습니다.",
    "privacyNeverDoH3" to "절대 하지 않는 것",
    "privacyCommitIntro" to "다음 사항을 엄숙히 약속합니다:",
    "privacyNoAdsLabel" to "광고 없음: ",
    "privacyNoAdsDesc" to "회원님의 활동을 기반으로 한 맞춤형 또는 비맞춤형 광고를 표시하지 않습니다",
    "privacyNoMarketingLabel" to "마케팅 없음: ",
    "privacyNoMarketingDesc" to "마케팅 또는 홍보 목적으로 회원님의 이메일을 제3자와 공유하지 않습니다",
    "privacyNoSellingLabel" to "데이터 판매 없음: ",
    "privacyNoSellingDesc" to "회원님의 개인정보를 어떠한 제3자에게도 판매, 대여, 또는 양도하지 않습니다",
    "privacyNoProfilingLabel" to "프로파일링 없음: ",
    "privacyNoProfilingDesc" to "기본적인 계정 관리 이외의 목적으로 프로필을 구축하기 위해 데이터를 사용하지 않습니다",
    "privacyNoTrackingLabel" to "추적 없음: ",
    "privacyNoTrackingDesc" to "광고용 추적 픽셀, 쿠키 또는 이와 유사한 추적 기술을 사용하지 않습니다",
    "privacyDataStorageH2" to "데이터 저장 및 보안",
    "privacyDataStoredSecurely" to "회원님의 데이터는 업계 표준 암호화를 사용하여 안전하게 저장됩니다. 당사는 개인정보를 무단 접근, 변경, 유출 또는 파기로부터 보호하기 위해 적절한 기술적, 관리적 조치를 취하고 있습니다.",
    "privacyDataRetention" to "회원님의 계정 정보는 계정이 활성화되어 있는 동안 보관됩니다. 언제든지 당사에 문의하여 계정 및 관련 데이터의 삭제를 요청할 수 있습니다.",
    "privacyYourRightsH2" to "회원님의 권리",
    "privacyRightsIntro" to "회원님은 개인정보와 관련하여 다음과 같은 권리를 가집니다:",
    "privacyRightAccess" to "열람권: 당사가 보유한 회원님의 모든 개인정보 사본을 요청할 수 있습니다",
    "privacyRightCorrection" to "정정권: 부정확한 정보의 수정을 요청할 수 있습니다",
    "privacyRightDeletion" to "삭제권: 계정 및 관련된 모든 데이터의 삭제를 요청할 수 있습니다",
    "privacyRightPortability" to "이동권: 일반적으로 사용되는 형식으로 데이터를 제공받도록 요청할 수 있습니다",
    "privacyExerciseRights" to "이러한 권리를 행사하려면 adopt-u@adopt-u.org으로 문의해 주세요.",
    "privacyCookiesH2" to "쿠키",
    "privacyCookiesText" to "Adopt-U는 세션 관리 및 언어 설정 등 플랫폼 기능에 필요한 최소한의 쿠키만 사용합니다. 광고 쿠키나 추적 쿠키는 사용하지 않습니다.",
    "privacyThirdPartyH2" to "제3자 서비스",
    "privacyThirdPartyIntro" to "당사는 회원님의 데이터를 처리할 수 있는 다음의 제3자 서비스를 이용합니다:",
    "privacyAws" to "AWS(Amazon Web Services): 데이터 저장 및 이메일 발송을 위해 사용됩니다. 해당 업체의 개인정보 처리방침이 데이터 처리에 적용됩니다.",
    "privacyWebauthn" to "WebAuthn/FIDO2: 안전한 패스키 인증을 위해 사용됩니다. 브라우저 내에서 암호화 작업이 수행됩니다.",
    "privacyThirdPartySelected" to "당사는 개인정보 보호에 대한 약속을 공유하는 서비스 제공업체를 신중하게 선정했습니다.",
    "privacyChildrensH2" to "아동 개인정보 보호",
    "privacyChildrensText" to "Adopt-U는 18세 미만 사용자를 대상으로 하지 않습니다. 당사는 미성년자로부터 알면서 정보를 수집하지 않습니다. 미성년자가 계정을 생성했다고 판단되는 경우, 계정 삭제를 위해 당사에 문의해 주세요.",
    "privacyChangesH2" to "본 방침의 변경",
    "privacyChangesText" to "당사는 관행 변경이나 법적 요구사항을 반영하기 위해 수시로 본 개인정보 처리방침을 업데이트할 수 있습니다. 중대한 변경 사항은 이메일 또는 플랫폼 내 공지를 통해 사용자에게 알려드립니다.",
    "privacyContactH2" to "문의하기",
    "privacyContactText" to "본 개인정보 처리방침에 대해 질문이나 우려사항이 있으시거나 데이터 권리를 행사하고자 하시면 adopt-u@adopt-u.org으로 문의해 주세요.",
    "termsConditions" to "이용약관",
    "installApp" to "앱 설치",
    "termsAcceptanceH2" to "약관의 동의",
    "termsAcceptanceText" to "Adopt-U에 접속하고 이용함으로써 회원님은 본 약관의 조항과 규정에 동의하고 이에 구속됨을 승인하는 것입니다. 본 약관에 동의하지 않으시면 당사 플랫폼을 이용하지 마시기 바랍니다.",
    "termsNatureH2" to "당사 서비스의 성격",
    "termsNatureText1" to "Adopt-U는 반려동물 구조자와 잠재적 입양자 간의 연결을 지원하는 플랫폼입니다. 당사는 펫숍, 중개업체, 보호소 또는 입양 기관이 아닙니다. 당사는 플랫폼에 게시된 어떠한 반려동물도 소유, 보관, 또는 중개하지 않습니다.",
    "termsNatureText2" to "당사의 역할은 당사자 간 소통 채널을 제공하는 것에 엄격히 한정됩니다. 당사는 입양 협상, 계약 체결, 또는 동물의 물리적 이동에 관여하지 않습니다.",
    "termsUserRespH2" to "사용자의 책임",
    "termsForRescuersH3" to "구조자를 위한 안내:",
    "termsRescuerResp1" to "보호 중인 반려동물에 대해 정확하고 진실한 정보를 제공해야 합니다",
    "termsRescuerResp2" to "게시 전 반려동물이 건강하고 예방접종을 완료했으며 적절히 검진받았는지 확인해야 합니다",
    "termsRescuerResp3" to "잠재적 입양자에 대한 책임 있는 심사를 진행해야 합니다",
    "termsRescuerResp4" to "새 가정으로의 안전한 이동을 준비해야 합니다",
    "termsRescuerResp5" to "제공한 모든 정보의 정확성에 대한 책임을 져야 합니다",
    "termsRescuerResp6" to "반려동물 입양과 관련된 모든 현지 법률 및 규정을 준수해야 합니다",
    "termsRescuerResp7" to "상업적 판매나 번식 목적으로 플랫폼을 사용해서는 안 됩니다",
    "termsForAdoptersH3" to "입양자를 위한 안내:",
    "termsAdopterResp1" to "본인의 생활 환경과 반려동물 경험에 대해 정확한 정보를 제공해야 합니다",
    "termsAdopterResp2" to "적절한 돌봄을 제공할 수 있는 능력을 증명할 준비가 되어 있어야 합니다",
    "termsAdopterResp3" to "입양을 취소하는 것이 동물에게 상당한 스트레스를 유발함을 이해해야 합니다",
    "termsAdopterResp4" to "구조자가 입양 요청을 거절할 권리를 존중해야 합니다",
    "termsPetListingsH2" to "반려동물 게시글 및 정보",
    "termsPetListingsText1" to "Adopt-U는 반려동물 게시글에 제공된 정보의 정확성을 검증, 보증 또는 승인하지 않습니다. 품종, 나이, 건강 상태, 성격, 예방접종 기록을 포함한 정보는 구조자가 제공한 것이며 독립적으로 검증되지 않았습니다.",
    "termsPetListingsText2" to "사진과 설명은 구조자가 제공합니다. 당사는 고의적인 허위 정보 제공을 금지하지만, 모든 게시글이 동물의 현재 상태를 정확히 반영한다고 보증할 수는 없습니다.",
    "termsPetListingsText3" to "사용자는 입양 절차를 최종 확정하기 전에 질문하고, 수의학 기록을 요청하며, 반려동물을 직접 만나볼 것을 권장합니다.",
    "termsAdoptionProcessH2" to "입양 절차",
    "termsAdoptionProcessIntro" to "구조자와 입양자가 입양을 진행하기로 합의한 경우:",
    "termsAdoptionStep1" to "이동 날짜, 장소 및 관련 비용을 포함한 모든 사항은 당사자 간에 직접 협의됩니다",
    "termsAdoptionStep2" to "Adopt-U는 어떠한 결제 거래도 중개하거나 보증하지 않습니다",
    "termsAdoptionStep3" to "본 플랫폼은 입양 계약서를 제공하지 않으며, 계약 관련 사항은 전적으로 당사자의 책임입니다",
    "termsAdoptionStep4" to "모든 합의 사항을 서면으로 기록할 것을 강력히 권장합니다",
    "termsLiabilityH2" to "책임의 제한",
    "termsLiabilityIntro" to "Adopt-U는 다음 사항에 대해 책임을 지지 않습니다:",
    "termsLiability1" to "구조자와 입양자 간의 분쟁, 의견 불일치 또는 갈등",
    "termsLiability2" to "반려동물 게시글에 제공된 정보의 정확성",
    "termsLiability3" to "입양 전, 진행 중, 또는 이후 동물의 건강, 행동, 또는 상태",
    "termsLiability4" to "플랫폼 이용으로 인해 발생하는 금전적 손실, 손해 또는 부상",
    "termsLiability5" to "반환, 건강 문제, 적합성 문제를 포함하되 이에 국한되지 않는 입양의 결과",
    "termsLiability6" to "연락이 성립된 이후 플랫폼 외부에서 사용자가 취한 모든 행위",
    "termsLiabilityAck" to "사용자는 반려동물 입양이 중대한 책임을 수반하며, 입양 결정에 대한 전적인 책임을 본인이 진다는 점을 인정합니다.",
    "termsProhibitedH2" to "금지 행위",
    "termsProhibitedIntro" to "Adopt-U에서는 다음 행위를 엄격히 금지합니다:",
    "termsProhibited1" to "상업적 반려동물 판매, 번식 사업, 또는 강아지 공장",
    "termsProhibited2" to "직접 돌보지 않는 반려동물의 게시",
    "termsProhibited3" to "게시글이나 프로필에 대한 허위 또는 오해의 소지가 있는 정보",
    "termsProhibited4" to "다른 사용자에 대한 괴롭힘, 차별, 또는 부적절한 연락",
    "termsProhibited5" to "반려동물 입양과 무관한 목적으로 개인정보를 수집하기 위해 플랫폼을 이용하는 행위",
    "termsProhibited6" to "동물 복지법 위반을 포함한 모든 불법 행위",
    "termsAccountTerminationH2" to "계정 해지",
    "termsAccountTerminationText" to "Adopt-U는 본 약관을 위반하거나, 금지된 행위를 하거나, 플랫폼의 명예를 훼손하는 계정을 정지하거나 해지할 권리를 보유합니다. 당사는 또한 허위, 오해의 소지가 있거나 부적절한 내용이 포함된 게시글을 삭제할 수 있습니다.",
    "termsModificationsH2" to "약관의 변경",
    "termsModificationsText" to "당사는 언제든지 본 약관을 수정할 권리를 보유합니다. 변경 후 플랫폼을 계속 이용하는 것은 변경된 약관에 대한 동의로 간주됩니다. 중대한 변경 사항은 이메일 또는 플랫폼 공지를 통해 사용자에게 알려드립니다.",
    "termsContactText" to "본 이용약관에 대해 질문이 있으시면 adopt-u@adopt-u.org으로 문의해 주세요.",
    "language" to "언어",
    "displayName" to "표시 이름",
    "saveProfile" to "프로필 저장",
    "accountSettings" to "계정 설정",
    "yourRoles" to "회원님의 역할:",
    "resources" to "자료실",
    "loginWithPasskey" to "패스키로 로그인",
    "signInWithPasskey" to "패스키로 로그인",
    "resendVerificationEmail" to "인증 이메일 재전송",
    "usesFido" to "안전한 비밀번호 없는 인증을 위해 FIDO2 / WebAuthn을 사용합니다.",
    "loginWithMagicLink" to "이메일 링크로 로그인",
    "magicLinkDesc" to "이메일로 로그인 링크를 보내드립니다(5분간 유효).",
    "sendMagicLink" to "로그인 링크 보내기",
    "loginWithPassword" to "비밀번호로 로그인",
    "signIn" to "로그인",
    "forgotPassword" to "비밀번호를 잊으셨나요?",
    "registerNewAccount" to "계정 만들기",
    "accountDetails" to "계정 정보",
    "registrationMethod" to "가입 방법",
    "selectAdditionalRoles" to "추가 역할을 선택하세요:",
    "adoptPet" to "반려동물 입양하기",
    "publishPets" to "입양 대상 반려동물 게시",
    "offerPhotography" to "사진 촬영 서비스 제공",
    "photographerSettings" to "사진작가 설정",
    "sessionFee" to "촬영 비용",
    "provideTemporaryHome" to "반려동물에게 임시 거처 제공",
    "passkeyNoPassword" to "패스키는 비밀번호보다 안전하며 모든 기기에서 사용할 수 있습니다.",
    "registerWithPasskey" to "패스키로 가입",
    "passkeySettings" to "패스키 설정",
    "password" to "비밀번호",
    "confirmPassword" to "비밀번호 확인",
    "passwordRequirements" to "비밀번호는 8자 이상이어야 하며 대문자, 소문자, 숫자, 특수문자를 포함해야 합니다",
    "securityInfo" to "보안 정보",
    "passkeyMostSecure" to "패스키: 가장 안전하며 권장됨",
    "passkeyBrowserCompat" to "패스키는 일부 브라우저에서 작동하지 않을 수 있습니다",
    "passwordLeastSecure" to "비밀번호: 보안이 낮으며 권장되지 않음",
    "selectLoginMethods" to "하나 이상의 로그인 방법을 선택하세요:",
    "registerPasskeyOption" to "패스키(가장 안전하며 모든 기기에서 사용 가능)",
    "registerPasswordOption" to "비밀번호(보안이 낮으며 백업 용도로만 사용)",
    "howPasskeysWorkTitle" to "패스키는 어떻게 작동하나요?",
    "howPasskeysWorkIntro" to "패스키는 지문, 얼굴 인식 또는 화면 잠금 PIN 등 기기에 내장된 보안 기능으로 비밀번호를 대체합니다.",
    "howPasskeysWorkSecurity" to "패스키는 회원님의 기기에만 저장되며, 피싱이나 데이터 유출로 새어나갈 수 없고, 여러 기기 간에 자동으로 동기화됩니다.",
    "howPasskeysWorkBackup" to "비밀번호를 추가로 등록할 수도 있지만, 패스키만으로도 비밀번호 없이 로그인하기에 충분합니다.",
    "gotIt" to "확인했습니다",
    "alreadyHaveAccount" to "이미 계정이 있으신가요?",
    "all" to "전체",
    "filterDogs" to "강아지",
    "filterCats" to "고양이",
    "filterBirds" to "새",
    "filterFish" to "물고기",
    "filterAllSexOption" to "성별 전체",
    "filterMaleOption" to "♂ 수컷",
    "filterFemaleOption" to "♀ 암컷",
    "age" to "나이",
    "rescued" to "구조됨",
    "years" to "세",
    "months" to "개월",
    "year" to "세",
    "month" to "개월",
    "loginToAdopt" to "입양을 신청하려면 로그인하세요",
    "requiredMarker" to "(필수)",
    "pageNotFoundTitle" to "페이지를 찾을 수 없습니다",
    "pageNotFoundHint" to "링크가 오래되었거나 잘못 입력되었을 수 있습니다. 반려동물은 여기 있어요.",
    "browsePets" to "반려동물 보기",
    "requestsFromRescuers" to "구조자 요청",
    "signingOut" to "로그아웃 중...",
    "adopter" to "입양자",
    "country" to "국가",
    "zip" to "우편번호",
    "viewPublic" to "공개 페이지 보기",
    "banUser" to "사용자 차단",
    "banReason" to "사유 (선택)",
    "myPhotoRequests" to "내 사진 촬영 요청",
    "noPhotoRequestsYet" to "아직 사진 촬영을 요청하지 않았습니다.",
    "photoRequestsReceived" to "받은 사진 촬영 요청",
    "noPhotoRequestsReceived" to "아직 사진 촬영 요청이 없습니다.",
    "photoStatusPending" to "대기 중",
    "photoStatusApproved" to "승인됨",
    "photoStatusRejected" to "거절됨",
    "photoStatusCancelled" to "취소됨",
    "photoStatusCompleted" to "완료됨",
    "cancelRequest" to "요청 취소",
    "registerConsentPrefix" to "계정을 만들면 다음에 동의하는 것입니다:",
    "registerConsentTerms" to "이용약관",
    "registerConsentAnd" to "및",
    "registerConsentPrivacy" to "개인정보 처리방침",
    "petStatusAvailable" to "입양 가능",
    "petStatusAdopted" to "입양됨",
    "petStatusDisabled" to "비활성화됨",
    "petStatusPending" to "대기 중",
    "addEditLocation" to "위치 추가/수정",
    "addLocation" to "위치 추가",
    "manage" to "관리",
    "adminPanel" to "관리자 패널",
    "adminSterilizationLocations" to "중성화 시술 장소 관리",
    "adopterRequired" to "입양자(필수)",
    "adoptionFee" to "입양 비용",
    "adoptionRequests" to "입양 신청",
    "ageMonths" to "나이(개월)",
    "ageYears" to "나이(세)",
    "alias" to "별칭",
    "bird" to "새",
    "breed" to "품종",
    "medicalEventsOverview" to "의료 이벤트 개요",
    "back" to "뒤로",
    "cat" to "고양이",
    "color" to "색상",
    "compatibility" to "궁합",
    "currentPassword" to "현재 비밀번호",
    "dog" to "강아지",
    "emailChange" to "이메일 변경",
    "emailChangeDesc" to "이메일 주소를 업데이트합니다. 새 이메일로 인증 링크가 발송됩니다.",
    "energyLevel" to "활동 수준",
    "female" to "암컷",
    "findTemporalHome" to "임시보호처 찾기",
    "fish" to "물고기",
    "goodWithCats" to "고양이와 잘 어울림",
    "goodWithDogs" to "강아지와 잘 어울림",
    "goodWithKids" to "아이들과 잘 어울림",
    "high" to "높음",
    "houseTrained" to "배변 훈련됨",
    "large" to "대형",
    "low" to "낮음",
    "male" to "수컷",
    "managePets" to "반려동물 관리",
    "managePetsBtn" to "반려동물 추가/수정",
    "managePetsDescription" to "모든 반려동물 페이지를 관리합니다. 반려동물을 추가하거나 삭제하세요.",
    "manageShelters" to "보호소 관리",
    "manageSheltersBtn" to "보호소 추가/수정",
    "manageSheltersDescription" to "모든 보호소 페이지를 관리합니다. 보호소를 추가하거나 삭제하세요.",
    "manageUsers" to "사용자 관리",
    "medical" to "의료",
    "medium" to "중형",
    "backToSearch" to "검색으로 돌아가기",
    "contactThisHome" to "이 임시보호처에 문의",
    "loginAsRescuerToContact" to "이 임시보호처에 요청을 보내려면 구조자로 로그인하세요.",
    "memberSince" to "가입일",
    "microchipId" to "마이크로칩 ID",
    "microchipped" to "마이크로칩 삽입됨",
    "myTemporalHome" to "내 임시보호처",
    "newEmail" to "새 이메일",
    "noTemporalHomes" to "임시보호처를 찾을 수 없습니다.",
    "requestSentToHome" to "요청이 전송되었습니다!",
    "sendRequestBtn" to "요청 보내기",
    "temporalHomeNotFound" to "이 임시보호처를 찾을 수 없습니다.",
    "viewDetails" to "자세히 보기",
    "rescuerDirectory" to "구조자",
    "noRescuersFound" to "구조자를 찾을 수 없습니다.",
    "availablePetsCount" to "입양 가능한 반려동물",
    "availablePets" to "입양 가능한 반려동물",
    "noAvailablePetsForRescuer" to "이 구조자에게는 현재 입양 가능한 반려동물이 없습니다.",
    "rescuerNotFound" to "이 구조자를 찾을 수 없습니다.",
    "errorLoadingResults" to "결과를 불러오는 중 오류가 발생했습니다.",
    "volunteerForThisRescuer" to "이 구조자를 위한 자원봉사",
    "volunteerExplanation" to "승인된 자원봉사자는 이 구조자의 반려동물 게시글에 수정을 제안할 수 있습니다.",
    "applyToVolunteerBtn" to "자원봉사 신청",
    "loginToVolunteer" to "이 구조자를 위한 자원봉사를 신청하려면 로그인하세요.",
    "volunteerApplicationSent" to "자원봉사 신청이 전송되었습니다!",
    "myVolunteering" to "내 자원봉사",
    "noVolunteerApplicationsYet" to "아직 어떤 구조자에게도 자원봉사를 신청하지 않았습니다.",
    "howToVolunteerHint" to "신청하려면 디렉터리에서 구조자 페이지를 열고 \"자원봉사 신청\"을 누르세요.",
    "volunteerStatusPending" to "대기 중",
    "volunteerStatusActive" to "활동 중",
    "volunteerStatusRejected" to "거절됨",
    "volunteerApplications" to "자원봉사 신청",
    "noVolunteerApplications" to "자원봉사 신청이 없습니다.",
    "petEditSuggestions" to "반려동물 정보 수정 제안",
    "noPetEditSuggestions" to "대기 중인 수정 제안이 없습니다.",
    "suggestedByLabel" to "제안자",
    "suggestAnEditTitle" to "수정 제안하기",
    "suggestAnEditExplanation" to "승인된 자원봉사자로서 이 게시글에 대한 변경을 제안할 수 있습니다. 구조자가 검토한 후 게시됩니다.",
    "suggestEditBtn" to "제안 보내기",
    "editSuggestionSent" to "제안이 구조자에게 검토를 위해 전송되었습니다!",
    "sponsorThisPetTitle" to "이 반려동물 후원하기",
    "sponsorExplanation" to "입양은 어렵지만 비용을 돕고 싶으신가요? 금전 또는 물품 지원을 제안하고 구조자와 직접 연결하세요.",
    "sponsorThisRescuerTitle" to "이 구조자 후원하기",
    "sponsorRescuerExplanation" to "금전 또는 물품 지원으로 이 구조자의 전반적인 비용을 지원하세요.",
    "sponsorTypeLabel" to "무엇을 제공하실 수 있나요?",
    "sponsorTypeMoney" to "금전",
    "sponsorTypeInKind" to "물품(사료, 용품 등)",
    "amountLabel" to "금액 (월별)",
    "currencyLabel" to "통화",
    "inKindDescriptionLabel" to "제공하실 수 있는 내용을 설명해 주세요",
    "messageLabel" to "메시지",
    "sendOfferBtn" to "제안 보내기",
    "sponsorOfferSent" to "제안이 전송되었습니다! 구조자가 세부 사항을 조율하기 위해 연락드릴 것입니다.",
    "sponsorshipOffersReceived" to "후원 제안",
    "noSponsorshipOffers" to "아직 후원 제안이 없습니다.",
    "generalFundLabel" to "일반 기금",
    "offeringLabel" to "제공 내용",
    "sponsorshipStatusSent" to "신규",
    "sponsorshipStatusRead" to "읽음",
    "markAsReadBtn" to "읽음으로 표시",
    "mySponsorshipOffers" to "내 후원 제안",
    "noSponsorshipOffersYet" to "아직 후원 제안을 보내지 않았습니다.",
    "howToSponsorHint" to "후원하려면 구조자 페이지(일반 지원) 또는 반려동물 페이지(해당 동물 비용)를 열고 제안을 보내세요.",
    "browsePetsLink" to "반려동물 보기",
    "yourMessage" to "회원님의 메시지",
    "newPassword" to "새 비밀번호",
    "passkeyDesc" to "기기 간 간편 로그인을 위해 계정에 추가 패스키를 등록하세요.",
    "passkeyName" to "패스키 이름",
    "pers" to "사람",
    "petFoodDescription" to "반려동물에게 안전한 음식, 해로운 음식, 독성이 있는 음식을 알아보세요",
    "petFoodGuide" to "반려동물 먹이 가이드",
    "foodInformation" to "먹이 정보",
    "petsForAdoption" to "입양 대상 반려동물",
    "chooseCountryPrompt" to "새로운 가장 친한 친구를 찾아보세요",
    "chooseCountryPromptHint" to "위에서 국가를 선택하여 근처에서 입양 가능한 반려동물을 둘러보세요.",
    "noPetsFound" to "반려동물을 찾을 수 없습니다",
    "noPetsFoundHint" to "다른 필터를 사용해 보시거나 잠시 후 다시 확인해 주세요 — 새로운 반려동물이 정기적으로 등록됩니다.",
    "loadingMorePets" to "반려동물을 더 불러오는 중…",
    "photos" to "사진(최대 12장)",
    "profilePhotographerRole" to "사진작가 - 사진 촬영 서비스 제공",
    "profileRescuerRole" to "구조자 - 입양 대상 반려동물 게시",
    "profileTemporalHomeRole" to "임시보호처 - 반려동물에게 임시 거처 제공",
    "provideShelter" to "보호소 - 동물 보호소 운영",
    "provideSterilization" to "중성화 서비스 - 중성화 시술 서비스 제공",
    "requestEmailChange" to "이메일 변경 요청",
    "rescueDate" to "구조 날짜",
    "rescueLocation" to "구조 장소",
    "savePassword" to "비밀번호 저장",
    "selectEnergy" to "활동 수준 선택",
    "selectSize" to "크기 선택",
    "sex" to "성별",
    "shelterSettings" to "보호소 설정",
    "size" to "크기",
    "small" to "소형",
    "specialNeeds" to "특별한 돌봄 필요사항",
    "sterilizationSettings" to "중성화 시술 설정",
    "sterilized" to "중성화됨",
    "temporalHomeSettings" to "임시보호처 설정",
    "temperament" to "성격",
    "type" to "종류 *",
    "urgentAdoption" to "긴급 입양 필요",
    "vaccinations" to "예방접종",
    "vetDisclaimer" to "식단을 변경하기 전에 수의사와 상담하세요.",
    "weight" to "체중(kg)",
    "weightLabel" to "체중",
    "website" to "웹사이트",
    "securitySettings" to "보안 설정",
    "emailVerifiedTitle" to "이메일 인증 완료!",
    "emailVerifiedMessage" to "이메일 인증이 완료되었습니다. 이제 가입 절차가 완료되었습니다.",
    "redirectingIn" to "다음 시간 후 메인 페이지로 이동합니다: ",
    "canAlsoLogin" to "다음을 이용할 수도 있습니다: ",
    "loginNow" to "지금 로그인",
    "emailVerificationFailed" to "인증 실패",
    "invalidOrExpiredToken" to "인증 링크가 유효하지 않거나 만료되었습니다.",
    "tryRegisteringAgain" to "다시 가입을 시도해 주세요.",
    "verificationEmailResent" to "인증 이메일이 만료되었습니다. 새 인증 이메일이 발송되었습니다. 받은편지함을 확인해 주세요.",
    "emailVerificationSent" to "인증 이메일이 발송되었습니다. 로그인하기 전에 받은편지함을 확인해 주세요.",
    "emailNotVerifiedLogin" to "로그인하기 전에 이메일을 인증해 주세요. 받은편지함에서 인증 링크를 확인하세요.",
    "invalidOrExpiredMagicLink" to "매직 링크가 유효하지 않거나 만료되었습니다. 새로 요청해 주세요.",
    "invalidToken" to "유효하지 않은 토큰입니다.",
    "accountBanned" to "회원님의 계정이 정지되었습니다.",
    "loginError" to "로그인 중 오류가 발생했습니다.",
    "profileUpdated" to "프로필이 업데이트되었습니다!",
    "displayNameRequired" to "표시 이름은 비워둘 수 없습니다",
    "saving" to "저장 중...",
    "passwordSet" to "비밀번호가 설정되어 있습니다",
    "noPassword" to "설정된 비밀번호가 없습니다",
    "passwordChangeSuccess" to "비밀번호가 변경되었습니다!",
    "passwordChangeFailed" to "비밀번호 변경에 실패했습니다",
    "passwordSetSuccess" to "비밀번호가 설정되었습니다!",
    "passwordSetFailed" to "비밀번호 설정에 실패했습니다",
    "passwordsDoNotMatch" to "비밀번호가 일치하지 않습니다",
    "currentPasswordRequired" to "현재 비밀번호를 입력해야 합니다",
    "emailChangeSent" to "새 이메일 주소로 인증 이메일이 발송되었습니다!",
    "emailChangeFailed" to "이메일 변경 요청에 실패했습니다",
    "emailRequired" to "이메일을 입력해야 합니다",
    "sendingEllipsis" to "전송 중...",
    "resetLinkSent" to "비밀번호 재설정 링크가 전송되었습니다! 이메일을 확인해 주세요.",
    "magicLinkSent" to "로그인 링크가 전송되었습니다! 이메일을 확인해 주세요.",
    "magicLinkSendFailed" to "로그인 링크 전송에 실패했습니다",
    "failedSendResetLink" to "재설정 링크 전송에 실패했습니다.",
    "invalidOrMissingToken" to "토큰이 유효하지 않거나 누락되었습니다.",
    "passwordTooShort" to "비밀번호는 8자 이상이어야 합니다.",
    "passwordNeedUppercase" to "비밀번호에는 대문자가 1자 이상 포함되어야 합니다.",
    "passwordNeedLowercase" to "비밀번호에는 소문자가 1자 이상 포함되어야 합니다.",
    "passwordNeedNumber" to "비밀번호에는 숫자가 1자 이상 포함되어야 합니다.",
    "passwordNeedSymbol" to "비밀번호에는 특수문자가 1자 이상 포함되어야 합니다.",
    "resettingPassword" to "비밀번호 재설정 중...",
    "passwordResetSuccess" to "비밀번호가 재설정되었습니다! 이제 로그인할 수 있습니다.",
    "failedResetPassword" to "비밀번호 재설정에 실패했습니다.",
    "loginSuccessRedirecting" to "로그인 성공! 이동 중...",
    "loginFailedExpiredLink" to "로그인에 실패했습니다. 링크가 유효하지 않거나 만료되었을 수 있습니다.",
    "loginFailed" to "로그인에 실패했습니다.",
    "emailChangedSuccess" to "이메일이 변경되었습니다!",
    "failedChangeEmailExpired" to "이메일 변경에 실패했습니다. 링크가 유효하지 않거나 만료되었을 수 있습니다.",
    "failedChangeEmail" to "이메일 변경에 실패했습니다.",
    "emailVerifiedSuccess" to "이메일 인증이 완료되었습니다!",
    "failedVerifyEmailExpired" to "이메일 인증에 실패했습니다. 링크가 유효하지 않거나 만료되었을 수 있습니다.",
    "failedVerifyEmail" to "이메일 인증에 실패했습니다.",
    "dropImagesHint" to "이미지를 여기에 끌어다 놓거나 클릭하여 찾아보세요",
    "forgotPasswordTitle" to "비밀번호 찾기",
    "forgotPasswordIntro" to "이메일을 입력하시면 비밀번호 재설정 링크를 보내드립니다.",
    "sendResetLink" to "재설정 링크 보내기",
    "backToLogin" to "로그인으로 돌아가기",
    "resetPasswordTitle" to "비밀번호 재설정",
    "resetPasswordIntro" to "아래에 새 비밀번호를 입력하세요.",
    "emailLinkLoginTitle" to "이메일 링크 로그인",
    "verifying" to "인증 중...",
    "emailChangeTitle" to "이메일 변경",
    "verifyContactEmailTitle" to "연락처 이메일 인증",
    "status" to "상태",
    "yes" to "예",
    "no" to "아니요",
    "noDescription" to "설명이 없습니다.",
    "urgentBadge" to "긴급 - 곧 새 가정이 필요해요!",
    "needsNewHome" to "이 반려동물은 긴급히 새 가정이 필요합니다",
    "promotedReasonLabel" to "사유",
    "selectReason" to "사유를 선택하세요",
    "promotedReasonMoving" to "주인이 이사함",
    "promotedReasonComplaints" to "민원 발생 / 더 이상 키울 수 없음",
    "promotedReasonPetConflict" to "다른 반려동물과의 갈등",
    "promotedReasonOther" to "기타",
    "promotedReasonDetailLabel" to "추가 세부사항(선택)",
    "promotedReasonRequired" to "사유를 선택해 주세요",
    "needsNewHomeBadge" to "새 가정 필요",
    "housingType" to "주거 형태",
    "preferNotToSay" to "답변하지 않음",
    "houseHousing" to "주택",
    "apartmentHousing" to "아파트",
    "hasYard" to "마당이 있음",
    "hasOtherPets" to "집에 다른 반려동물이 있음",
    "adoptionExperience" to "입양 경험",
    "firstTimeAdopter" to "첫 입양자",
    "experiencedAdopter" to "입양 경험자",
    "noAdoptionRequests" to "입양 신청이 없습니다",
    "noMessage" to "메시지 없음",
    "markUnderReview" to "검토 중으로 표시",
    "approve" to "승인",
    "reject" to "거절",
    "reviewNoteLabel" to "비공개 검토 메모",
    "saveNote" to "메모 저장",
    "adoptionStatusPending" to "대기 중",
    "adoptionStatusUnderReview" to "검토 중",
    "adoptionStatusApproved" to "승인됨",
    "adoptionStatusRejected" to "승인되지 않음",
    "myAdoptionRequests" to "내 입양 신청",
    "noAdoptionRequestsYet" to "아직 어떤 반려동물도 입양 신청을 하지 않았습니다.",
    "medicalHistory" to "의료 기록",
    "medicalSchedule" to "예방접종 및 구충 일정",
    "recordType" to "종류",
    "vaccination" to "예방접종",
    "deworming" to "구충",
    "recordName" to "이름",
    "dateGiven" to "접종일",
    "nextDueDate" to "다음 예정일(선택)",
    "notesOptional" to "메모(선택)",
    "addRecord" to "기록 추가",
    "noMedicalRecords" to "아직 예방접종 또는 구충 기록이 없습니다.",
    "nextDueLabel" to "다음 예정일",
    "givenLabel" to "접종일",
    "overdue" to "기한 초과",
    "dueSoon" to "곧 예정",
    "upToDate" to "최신 상태",
    "recordNameRequired" to "이름을 입력해야 합니다",
    "dateGivenRequired" to "접종일을 입력해야 합니다",
    "failedToAddRecord" to "기록 추가에 실패했습니다",
    "confirmDeleteRecord" to "이 기록을 삭제하시겠습니까?",
    "maxCapacity" to "동시 최대 보호 마리 수(선택)",
    "placePetHere" to "이곳에 반려동물 배치하기",
    "noAvailablePetsToPlace" to "현재 배치할 수 있는 반려동물이 없습니다.",
    "selectPet" to "반려동물 선택",
    "startPlacementBtn" to "배치 시작",
    "placementStarted" to "임시보호 배치가 시작되었습니다!",
    "petsCurrentlyWithMe" to "현재 나와 함께 있는 반려동물",
    "noPetsCurrentlyFostered" to "현재 회원님에게 배치된 반려동물이 없습니다.",
    "sinceLabel" to "시작일",
    "fosterPlacement" to "임시보호 배치",
    "endPlacement" to "배치 종료",
    "currentlyWithLabel" to "현재 함께 있는 곳",
    "notCurrentlyFostered" to "현재 임시보호 중이 아닙니다.",
    "confirmEndPlacement" to "이 임시보호 배치를 종료하시겠습니까? 반려동물은 회원님과 함께 있는 것으로 기록됩니다.",
    "aTemporalHome" to "임시보호처",
    "viewsLabel" to "조회수",
    "inquiriesLabel" to "문의",
    "conversionLabel" to "전환율",
    "messageOptional" to "메시지(선택)",
    "requestAdoption" to "입양 신청",
    "editPet" to "반려동물 정보 수정",
    "adoptionRequestSubmitted" to "입양 신청이 제출되었습니다!",
    "failedSubmitRequest" to "신청 제출에 실패했습니다",
    "noPhotosYet" to "아직 사진이 없습니다.",
    "rabbit" to "토끼",
    "safeFoods" to "안전한 음식",
    "harmfulFoods" to "해로운 음식",
    "cannotEatFoods" to "먹을 수 없는 음식",
    "legendSafe" to "안전",
    "legendHarmful" to "해로움",
    "legendToxic" to "독성",
    "food_dog_safe_chicken_name" to "닭고기",
    "food_dog_safe_chicken_detail" to "익힌 것, 뼈 제거",
    "food_dog_safe_chicken_desc" to "저지방 단백질, 소화가 쉬움",
    "food_dog_safe_rice_name" to "쌀",
    "food_dog_safe_rice_detail" to "백미 또는 현미",
    "food_dog_safe_rice_desc" to "좋은 탄수화물 공급원",
    "food_dog_safe_carrots_name" to "당근",
    "food_dog_safe_carrots_detail" to "생것 또는 익힌 것",
    "food_dog_safe_carrots_desc" to "저칼로리, 고식이섬유",
    "food_dog_safe_apples_name" to "사과",
    "food_dog_safe_apples_detail" to "씨 제거",
    "food_dog_safe_apples_desc" to "비타민이 풍부함",
    "food_dog_safe_peanutbutter_name" to "땅콩버터",
    "food_dog_safe_peanutbutter_detail" to "무염, 자일리톨 무첨가",
    "food_dog_safe_peanutbutter_desc" to "좋은 단백질 공급원",
    "food_dog_harmful_grapes_name" to "포도",
    "food_dog_harmful_grapes_detail" to "모든 종류",
    "food_dog_harmful_grapes_desc" to "신부전을 유발할 수 있음",
    "food_dog_harmful_onions_name" to "양파",
    "food_dog_harmful_onions_detail" to "모든 형태",
    "food_dog_harmful_onions_desc" to "적혈구를 손상시킴",
    "food_dog_harmful_chocolate_name" to "초콜릿",
    "food_dog_harmful_chocolate_detail" to "모든 종류",
    "food_dog_harmful_chocolate_desc" to "강아지에게 독성이 있음",
    "food_dog_harmful_garlic_name" to "마늘",
    "food_dog_harmful_garlic_detail" to "모든 형태",
    "food_dog_harmful_garlic_desc" to "빈혈을 유발함",
    "food_dog_cannot_xylitol_name" to "자일리톨",
    "food_dog_cannot_xylitol_detail" to "인공 감미료",
    "food_dog_cannot_xylitol_desc" to "매우 독성이 강함",
    "food_dog_cannot_macadamia_name" to "마카다미아 너트",
    "food_dog_cannot_macadamia_detail" to "모든 형태",
    "food_dog_cannot_macadamia_desc" to "무기력증을 유발함",
    "food_dog_cannot_avocado_name" to "아보카도",
    "food_dog_cannot_avocado_detail" to "씨, 껍질, 전체",
    "food_dog_cannot_avocado_desc" to "퍼신 성분 함유",
    "food_dog_cannot_alcohol_name" to "알코올",
    "food_dog_cannot_alcohol_detail" to "모든 형태",
    "food_dog_cannot_alcohol_desc" to "매우 독성이 강함",
    "food_cat_safe_cookedfish_name" to "익힌 생선",
    "food_cat_safe_cookedfish_detail" to "가시 제거",
    "food_cat_safe_cookedfish_desc" to "고단백",
    "food_cat_safe_chicken_name" to "닭고기",
    "food_cat_safe_chicken_detail" to "익힌 것, 양념 없이",
    "food_cat_safe_chicken_desc" to "좋은 단백질 공급원",
    "food_cat_safe_pumpkin_name" to "호박",
    "food_cat_safe_pumpkin_detail" to "양념 없이, 익힌 것",
    "food_cat_safe_pumpkin_desc" to "소화에 도움",
    "food_cat_safe_eggs_name" to "달걀",
    "food_cat_safe_eggs_detail" to "익힌 것",
    "food_cat_safe_eggs_desc" to "완전 단백질",
    "food_cat_harmful_raweggs_name" to "생 달걀",
    "food_cat_harmful_raweggs_detail" to "아비딘 함유",
    "food_cat_harmful_raweggs_desc" to "비오틴 흡수를 방해함",
    "food_cat_harmful_rawfish_name" to "생선회",
    "food_cat_harmful_rawfish_detail" to "티아미나아제 함유",
    "food_cat_harmful_rawfish_desc" to "비타민 B를 분해함",
    "food_cat_harmful_dogfood_name" to "강아지 사료",
    "food_cat_harmful_dogfood_detail" to "모든 종류",
    "food_cat_harmful_dogfood_desc" to "타우린 부족",
    "food_cat_harmful_milk_name" to "우유",
    "food_cat_harmful_milk_detail" to "대부분의 고양이",
    "food_cat_harmful_milk_desc" to "유당 불내증",
    "food_cat_cannot_chocolate_name" to "초콜릿",
    "food_cat_cannot_chocolate_detail" to "모든 종류",
    "food_cat_cannot_chocolate_desc" to "테오브로민 독성",
    "food_cat_cannot_onionsgarlic_name" to "양파/마늘",
    "food_cat_cannot_onionsgarlic_detail" to "모든 형태",
    "food_cat_cannot_onionsgarlic_desc" to "적혈구 손상",
    "food_cat_cannot_grapesraisins_name" to "포도/건포도",
    "food_cat_cannot_grapesraisins_detail" to "모든 종류",
    "food_cat_cannot_grapesraisins_desc" to "신장 손상",
    "food_cat_cannot_alcohol_name" to "알코올",
    "food_cat_cannot_alcohol_detail" to "모든 형태",
    "food_cat_cannot_alcohol_desc" to "매우 독성이 강함",
    "food_bird_safe_seeds_name" to "씨앗",
    "food_bird_safe_seeds_detail" to "혼합 종류",
    "food_bird_safe_seeds_desc" to "좋은 지방 공급원",
    "food_bird_safe_fruits_name" to "과일",
    "food_bird_safe_fruits_detail" to "대부분의 종류",
    "food_bird_safe_fruits_desc" to "비타민과 미네랄",
    "food_bird_safe_vegetables_name" to "채소",
    "food_bird_safe_vegetables_detail" to "잎채소",
    "food_bird_safe_vegetables_desc" to "필수 영양소",
    "food_bird_safe_pellets_name" to "펠릿 사료",
    "food_bird_safe_pellets_detail" to "고품질 배합 사료",
    "food_bird_safe_pellets_desc" to "완전 영양식",
    "food_bird_harmful_avocado_name" to "아보카도",
    "food_bird_harmful_avocado_detail" to "모든 부위",
    "food_bird_harmful_avocado_desc" to "퍼신 독성",
    "food_bird_harmful_fruitpits_name" to "과일 씨",
    "food_bird_harmful_fruitpits_detail" to "사과, 복숭아",
    "food_bird_harmful_fruitpits_desc" to "미량의 시안화물",
    "food_bird_harmful_salt_name" to "소금",
    "food_bird_harmful_salt_detail" to "모든 형태",
    "food_bird_harmful_salt_desc" to "새에게 독성이 있음",
    "food_bird_harmful_caffeine_name" to "카페인",
    "food_bird_harmful_caffeine_detail" to "커피, 차",
    "food_bird_harmful_caffeine_desc" to "심장 문제",
    "food_bird_cannot_chocolate_name" to "초콜릿",
    "food_bird_cannot_chocolate_detail" to "모든 종류",
    "food_bird_cannot_chocolate_desc" to "테오브로민 독성",
    "food_bird_cannot_onions_name" to "양파",
    "food_bird_cannot_onions_detail" to "모든 형태",
    "food_bird_cannot_onions_desc" to "적혈구 손상",
    "food_bird_cannot_garlic_name" to "마늘",
    "food_bird_cannot_garlic_detail" to "모든 형태",
    "food_bird_cannot_garlic_desc" to "독성이 있음",
    "food_bird_cannot_mushrooms_name" to "버섯",
    "food_bird_cannot_mushrooms_detail" to "야생 종류",
    "food_bird_cannot_mushrooms_desc" to "잠재적 독성",
    "food_fish_safe_flakes_name" to "플레이크 사료",
    "food_fish_safe_flakes_detail" to "고품질 브랜드",
    "food_fish_safe_flakes_desc" to "완전 어류 사료",
    "food_fish_safe_pellets_name" to "펠릿 사료",
    "food_fish_safe_pellets_detail" to "부상형/침강형",
    "food_fish_safe_pellets_desc" to "균형 잡힌 영양",
    "food_fish_safe_frozenfood_name" to "냉동 사료",
    "food_fish_safe_frozenfood_detail" to "붉은장구벌레, 브라인슈림프",
    "food_fish_safe_frozenfood_desc" to "단백질 공급원",
    "food_fish_safe_vegetables_name" to "채소",
    "food_fish_safe_vegetables_detail" to "데친 애호박",
    "food_fish_safe_vegetables_desc" to "식이섬유 공급원",
    "food_fish_harmful_bread_name" to "빵",
    "food_fish_harmful_bread_detail" to "모든 종류",
    "food_fish_harmful_bread_desc" to "영양가 없이 배만 채움",
    "food_fish_harmful_humanfood_name" to "사람 음식",
    "food_fish_harmful_humanfood_detail" to "조리된 식사",
    "food_fish_harmful_humanfood_desc" to "잘못된 영양",
    "food_fish_harmful_livefeed_name" to "살아있는 먹이",
    "food_fish_harmful_livefeed_detail" to "야생에서 잡은 곤충",
    "food_fish_harmful_livefeed_desc" to "기생충 위험",
    "food_fish_cannot_landinsect_name" to "육상 곤충",
    "food_fish_cannot_landinsect_detail" to "기어다니는 벌레",
    "food_fish_cannot_landinsect_desc" to "자연스러운 먹이가 아님",
    "food_fish_cannot_mammalmeat_name" to "포유류 고기",
    "food_fish_cannot_mammalmeat_detail" to "닭고기, 소고기",
    "food_fish_cannot_mammalmeat_desc" to "대부분의 물고기에 부적합",
    "food_fish_cannot_dairy_name" to "유제품",
    "food_fish_cannot_dairy_detail" to "모든 종류",
    "food_fish_cannot_dairy_desc" to "소화할 수 없음",
    "food_fish_cannot_breadcrumbs_name" to "빵가루",
    "food_fish_cannot_breadcrumbs_detail" to "마늘이 포함된 경우가 많음",
    "food_fish_cannot_breadcrumbs_desc" to "해로운 첨가물",
    "food_rabbit_safe_hay_name" to "건초",
    "food_rabbit_safe_hay_detail" to "티모시 그라스",
    "food_rabbit_safe_hay_desc" to "식단의 80%",
    "food_rabbit_safe_leafygreens_name" to "잎채소",
    "food_rabbit_safe_leafygreens_detail" to "로메인, 고수",
    "food_rabbit_safe_leafygreens_desc" to "매일 필요한 비타민",
    "food_rabbit_safe_pellets_name" to "펠릿 사료",
    "food_rabbit_safe_pellets_detail" to "티모시 기반",
    "food_rabbit_safe_pellets_desc" to "균형 잡힌 영양",
    "food_rabbit_safe_carrots_name" to "당근",
    "food_rabbit_safe_carrots_detail" to "적당량",
    "food_rabbit_safe_carrots_desc" to "간식",
    "food_rabbit_harmful_icebergletttuce_name" to "양상추",
    "food_rabbit_harmful_icebergletttuce_detail" to "영양가 없음",
    "food_rabbit_harmful_icebergletttuce_desc" to "위장 정체를 유발할 수 있음",
    "food_rabbit_harmful_beans_name" to "콩",
    "food_rabbit_harmful_beans_detail" to "모든 콩류",
    "food_rabbit_harmful_beans_desc" to "가스, 소화 문제",
    "food_rabbit_harmful_corn_name" to "옥수수",
    "food_rabbit_harmful_corn_detail" to "모든 형태",
    "food_rabbit_harmful_corn_desc" to "소화할 수 없음",
    "food_rabbit_harmful_potatoes_name" to "감자",
    "food_rabbit_harmful_potatoes_detail" to "모든 형태",
    "food_rabbit_harmful_potatoes_desc" to "잘못된 영양소",
    "food_rabbit_cannot_chocolate_name" to "초콜릿",
    "food_rabbit_cannot_chocolate_detail" to "모든 종류",
    "food_rabbit_cannot_chocolate_desc" to "테오브로민 독성",
    "food_rabbit_cannot_onions_name" to "양파",
    "food_rabbit_cannot_onions_detail" to "모든 형태",
    "food_rabbit_cannot_onions_desc" to "매우 독성이 강함",
    "food_rabbit_cannot_garlic_name" to "마늘",
    "food_rabbit_cannot_garlic_detail" to "모든 형태",
    "food_rabbit_cannot_garlic_desc" to "독성이 있음",
    "food_rabbit_cannot_avocado_name" to "아보카도",
    "food_rabbit_cannot_avocado_detail" to "모든 부위",
    "food_rabbit_cannot_avocado_desc" to "퍼신 성분 함유",
    "primary" to "기본",
    "passkeyRegisteredSuccess" to "패스키가 등록되었습니다!",
    "passkeyRegistrationFailed" to "패스키 등록에 실패했습니다",
    "passkeyNameRequired" to "패스키 이름을 입력해야 합니다",
    "registeringPasskey" to "패스키 등록 중...",
    "passkeyRegistered" to "패스키 등록됨",
    "noPasskey" to "등록된 패스키가 없습니다",
    "noPets" to "아직 반려동물이 없습니다.",
    "photographerCountryStateRequired" to "사진작가 서비스를 위해 국가와 주/도를 입력해 주세요",
    "temporalHomeAliasCountryCityRequired" to "임시보호처를 위해 별칭, 국가, 도시를 입력해 주세요",
    "shelterNameCountryCityAddressRequired" to "보호소를 위해 이름, 국가, 도시, 주소를 입력해 주세요",
    "sterilizationNameCountryCityAddressRequired" to "중성화 시술 장소를 위해 이름, 국가, 도시, 주소를 입력해 주세요",
    "country.afghanistan" to "아프가니스탄",
    "country.albania" to "알바니아",
    "country.algeria" to "알제리",
    "country.argentina" to "아르헨티나",
    "country.armenia" to "아르메니아",
    "country.australia" to "호주",
    "country.austria" to "오스트리아",
    "country.azerbaijan" to "아제르바이잔",
    "country.bangladesh" to "방글라데시",
    "country.belarus" to "벨라루스",
    "country.belgium" to "벨기에",
    "country.bolivia" to "볼리비아",
    "country.bosnia" to "보스니아 헤르체고비나",
    "country.brazil" to "브라질",
    "country.bulgaria" to "불가리아",
    "country.cambodia" to "캄보디아",
    "country.cameroon" to "카메룬",
    "country.canada" to "캐나다",
    "country.chile" to "칠레",
    "country.china" to "중국",
    "country.colombia" to "콜롬비아",
    "country.costaRica" to "코스타리카",
    "country.croatia" to "크로아티아",
    "country.cuba" to "쿠바",
    "country.czechia" to "체코",
    "country.denmark" to "덴마크",
    "country.dominicanRepublic" to "도미니카 공화국",
    "country.ecuador" to "에콰도르",
    "country.egypt" to "이집트",
    "country.elSalvador" to "엘살바도르",
    "country.estonia" to "에스토니아",
    "country.ethiopia" to "에티오피아",
    "country.finland" to "핀란드",
    "country.france" to "프랑스",
    "country.georgia" to "조지아",
    "country.germany" to "독일",
    "country.ghana" to "가나",
    "country.greece" to "그리스",
    "country.guatemala" to "과테말라",
    "country.haiti" to "아이티",
    "country.honduras" to "온두라스",
    "country.hungary" to "헝가리",
    "country.iceland" to "아이슬란드",
    "country.india" to "인도",
    "country.indonesia" to "인도네시아",
    "country.iran" to "이란",
    "country.iraq" to "이라크",
    "country.ireland" to "아일랜드",
    "country.israel" to "이스라엘",
    "country.italy" to "이탈리아",
    "country.jamaica" to "자메이카",
    "country.japan" to "일본",
    "country.jordan" to "요르단",
    "country.kazakhstan" to "카자흐스탄",
    "country.kenya" to "케냐",
    "country.kuwait" to "쿠웨이트",
    "country.latvia" to "라트비아",
    "country.lebanon" to "레바논",
    "country.libya" to "리비아",
    "country.lithuania" to "리투아니아",
    "country.luxembourg" to "룩셈부르크",
    "country.malaysia" to "말레이시아",
    "country.mexico" to "멕시코",
    "country.moldova" to "몰도바",
    "country.mongolia" to "몽골",
    "country.montenegro" to "몬테네그로",
    "country.morocco" to "모로코",
    "country.myanmar" to "미얀마",
    "country.nepal" to "네팔",
    "country.netherlands" to "네덜란드",
    "country.newZealand" to "뉴질랜드",
    "country.nicaragua" to "니카라과",
    "country.nigeria" to "나이지리아",
    "country.northKorea" to "북한",
    "country.norway" to "노르웨이",
    "country.pakistan" to "파키스탄",
    "country.panama" to "파나마",
    "country.paraguay" to "파라과이",
    "country.peru" to "페루",
    "country.philippines" to "필리핀",
    "country.poland" to "폴란드",
    "country.portugal" to "포르투갈",
    "country.puertoRico" to "푸에르토리코",
    "country.qatar" to "카타르",
    "country.romania" to "루마니아",
    "country.russia" to "러시아",
    "country.saudiArabia" to "사우디아라비아",
    "country.serbia" to "세르비아",
    "country.singapore" to "싱가포르",
    "country.slovakia" to "슬로바키아",
    "country.slovenia" to "슬로베니아",
    "country.southAfrica" to "남아프리카공화국",
    "country.southKorea" to "대한민국",
    "country.spain" to "스페인",
    "country.sriLanka" to "스리랑카",
    "country.sudan" to "수단",
    "country.sweden" to "스웨덴",
    "country.switzerland" to "스위스",
    "country.syria" to "시리아",
    "country.taiwan" to "대만",
    "country.thailand" to "태국",
    "country.tunisia" to "튀니지",
    "country.turkey" to "튀르키예",
    "country.ukraine" to "우크라이나",
    "country.uae" to "아랍에미리트",
    "country.uk" to "영국",
    "country.usa" to "미국",
    "country.uruguay" to "우루과이",
    "country.uzbekistan" to "우즈베키스탄",
    "country.venezuela" to "베네수엘라",
    "country.vietnam" to "베트남",
    "country.yemen" to "예멘",
    "loading" to "불러오는 중...",
    "countryRequired" to "검색하려면 국가를 선택해 주세요",
    "noSheltersFound" to "아직 보호소를 찾을 수 없습니다. 나중에 다시 확인해 주세요!",
    "visitWebsite" to "웹사이트 방문",
    "fiscalId" to "사업자등록번호",
    "donationInformation" to "후원 정보",
    "bankName" to "은행",
    "accountHolder" to "예금주",
    "accountNumber" to "계좌번호",
    "currency" to "통화",
    "errorLoadingShelters" to "보호소를 불러오는 중 오류가 발생했습니다",
    "pleaseSelectCountry" to "국가를 선택해 주세요",
    "actions" to "작업",
    "addShelter" to "보호소 추가",
    "addEditShelter" to "보호소 추가/편집",
    "allStates" to "모든 주/도",
    "confirmDelete" to "이 위치를 삭제하시겠습니까?",
    "confirmDeleteShelter" to "이 보호소를 삭제하시겠습니까?",
    "confirmUnbanUser" to "이 사용자의 정지를 해제하시겠습니까?",
    "confirmResetUserCredentials" to "이 작업은 {email}의 현재 비밀번호와 패스키를 무효화하고 재설정 링크를 이메일로 보냅니다. 계속하시겠습니까?",
    "confirmDeactivateUser" to "이 사용자를 비활성화하시겠습니까? 나중에 다시 활성화할 수 있습니다.",
    "confirmDeactivatePet" to "이 반려동물을 비활성화하시겠습니까? 공개 목록에서 숨겨지며 나중에 다시 활성화할 수 있습니다.",
    "confirmBlockRescuer" to "이 구조자가 추가 요청을 보내지 못하도록 차단하시겠습니까?",
    "confirmDeletePet" to "이 반려동물을 삭제하시겠습니까?",
    "confirmDeleteStorage" to "이 저장소를 삭제하시겠습니까?",
    "contact" to "연락처",
    "delete" to "삭제",
    "edit" to "수정",
    "errorDeletingShelter" to "보호소 삭제 중 오류가 발생했습니다",
    "errorLoadingLocations" to "위치를 불러오는 중 오류가 발생했습니다",
    "errorLoadingShelter" to "보호소를 불러오는 중 오류가 발생했습니다",
    "errorSaving" to "저장 중 오류가 발생했습니다",
    "errorSavingShelter" to "보호소 저장 중 오류가 발생했습니다",
    "location" to "위치",
    "noLocationsFound" to "위치를 찾을 수 없습니다",
    "selectCountry" to "국가를 선택하세요",
    "selectCountryToFilter" to "필터링하려면 국가를 선택해 주세요",
    "shelterAdded" to "보호소가 추가되었습니다",
    "shelterDeleted" to "보호소가 삭제되었습니다",
    "shelterUpdated" to "보호소 정보가 업데이트되었습니다",
    "updateShelter" to "보호소 정보 업데이트",
    "errorLoadingPhotographers" to "사진작가 정보를 불러오는 중 오류가 발생했습니다",
    "noPhotographersAvailable" to "이용 가능한 사진작가가 없습니다",
    "requestPhotoSession" to "사진 촬영 요청",
    "selectPhotographer" to "선택",
    "photographersSelected" to "{count}명 선택됨",
    "requestSelectedPhotographers" to "선택한 사진작가에게 요청",
    "clearSelection" to "선택 해제",
    "requestToMultiple" to "요청 대상",
    "messageRequired" to "사진작가에게 보낼 메시지를 작성해 주세요.",
    "requestsSentSuccessfully" to "요청이 성공적으로 전송되었습니다",
    "message" to "메시지",
    "sendRequest" to "요청 보내기",
    "requestSentSuccessfully" to "요청이 성공적으로 전송되었습니다!",
    "requestTo" to "요청 대상",
    "enterMessage" to "{name}에게 보낼 메시지를 입력하세요...",
    "reportUrgent" to "긴급",
    "topRescuers" to "우수 구조자",
    "urgentRescuerSettings" to "긴급 구조자 설정",
    "urgentRescueAlerts" to "긴급 구조 알림",
    "reportUrgentTitle" to "위험에 처한 반려동물 신고",
    "reportUrgentIntro" to "부상당하거나, 학대당하거나, 굶주리거나, 버려진 반려동물을 발견하여 즉각적인 도움이 필요하다면, 위치와 상황을 알려주세요 — 즉시 인근 긴급 구조자에게 알리겠습니다.",
    "dangerType" to "무엇이 문제인가요?",
    "dangerInjured" to "부상",
    "dangerAbused" to "학대 / 부당한 대우",
    "dangerStarving" to "굶주림",
    "dangerTooYoung" to "혼자 생존하기에 너무 어림",
    "dangerOther" to "기타 응급상황",
    "useMyLocation" to "현재 위치 사용",
    "confirmMyLocation" to "현재 위치 확인",
    "locationPermissionHint" to "브라우저가 위치 공유 권한을 요청합니다. 위치는 주변 신고 알림에만 사용됩니다.",
    "phoneOptional" to "전화번호(선택)",
    "submitReport" to "신고 제출",
    "urgentRescuerSettingsTitle" to "긴급 구조자 설정",
    "urgentRescuerSettingsIntro" to "담당 지역 근처에서 위험에 처한 반려동물이 신고되면 알림을 받으세요. 먼저 수락하는 사람이 해당 사례를 맡습니다.",
    "urgentRescuerActive" to "긴급 신고에 대응할 수 있습니다",
    "coverageAreaMode" to "담당 지역",
    "coverageArea" to "담당 지역",
    "coverageAreaHint" to "현재 위치를 확인하거나, 핀을 드래그하거나, 아래에 주소를 입력하세요 - 지도와 필드가 서로 동기화됩니다.",
    "useMyLocationAndRadius" to "내 위치 + 반경 사용",
    "pickAZone" to "대신 국가/주/도시 선택",
    "radiusKm" to "반경(km)",
    "urgentRescueAlertsTitle" to "긴급 구조 알림",
    "urgentRescuerLeaderboardTitle" to "최근 12개월 우수 긴급 구조자",
    "locating" to "위치 확인 중...",
    "geolocationUnsupported" to "브라우저가 위치 정보를 지원하지 않습니다",
    "locationCaptured" to "위치가 확인되었습니다",
    "locationDenied" to "위치를 가져올 수 없습니다 - 위치 접근을 허용하거나 도시를 직접 입력해 주세요",
    "street" to "도로명",
    "exteriorNumber" to "건물 번호",
    "referenceNotes" to "참고 사항(예: 건물 색상, 주변 랜드마크)",
    "concreteAddressHint" to "가능하다면 도로명과 번호를 추가해 주세요 - 이를 통해 구조자가 택시를 타거나 지도 앱을 열어 정확한 위치로 바로 이동할 수 있습니다.",
    "descriptionRequired" to "상황을 설명해 주세요",
    "locationRequired" to "위치를 제공해 주세요(현재 위치를 사용하거나 국가와 도시를 입력)",
    "submittingEllipsis" to "제출 중...",
    "reportSubmitted" to "신고가 제출되었습니다. 감사합니다 - 인근 구조자들에게 지금 알리고 있습니다.",
    "reportSubmitFailed" to "신고 제출에 실패했습니다",
    "phoneRequired" to "전화번호를 입력해 주세요",
    "savingEllipsis" to "저장 중...",
    "saved" to "저장됨",
    "saveFailed" to "저장에 실패했습니다",
    "noUrgentPages" to "현재 대기 중인 긴급 알림이 없습니다",
    "accept" to "수락",
    "alreadyAccepted" to "다른 사람이 이미 이 신고를 수락했습니다",
    "noLeaderboardData" to "최근 12개월간 수락된 구조 사례가 없습니다",
    "rescueAcceptedTitle" to "구조 수락됨",
    "rescueAcceptedMessage" to "감사합니다 - 잘 맡아주셨습니다. 가능한 한 빨리 신고된 위치로 이동해 주세요.",
    "rescueAcceptFailedTitle" to "수락할 수 없음",
    "rescueAcceptFailedMessage" to "이 신고는 이미 다른 구조자가 수락했거나, 링크가 유효하지 않거나 만료되었습니다.",
    "share" to "공유",
    "shareViaFacebook" to "Facebook에 공유",
    "shareViaX" to "X에 공유",
    "shareViaWhatsapp" to "WhatsApp에 공유",
    "shareViaEmail" to "이메일로 공유",
    "copyLink" to "링크 복사",
    "linkCopied" to "링크가 복사되었습니다!",
    "reportLostFoundTitle" to "잃어버리거나 발견한 반려동물 신고",
    "reportLostFoundIntro" to "반려동물을 잃어버리셨거나 유기동물을 발견하셨나요? 신고서를 작성하시면 인근의 다른 신고와 대조해 드립니다.",
    "browseLostFound" to "기존 실종/발견 신고 둘러보기",
    "kindLost" to "반려동물을 잃어버렸어요",
    "kindFound" to "반려동물을 발견했어요",
    "petType" to "반려동물 종류",
    "petTypeUnknown" to "잘 모르겠음",
    "lostFoundBrowseTitle" to "실종 & 발견 반려동물",
    "showing" to "표시 중",
    "kindLostPlural" to "실종된 반려동물",
    "kindFoundPlural" to "발견된 반려동물",
    "reportResolvedTitle" to "신고 해결됨",
    "reportResolvedMessage" to "알려주셔서 감사합니다 - 이 신고는 이제 종료되었습니다.",
    "reportResolveFailedTitle" to "해결할 수 없음",
    "reportResolveFailedMessage" to "이 신고는 이미 해결되었거나, 링크가 유효하지 않거나 만료되었습니다.",
    "reportLostFound" to "실종/\u200B발견",
    "noLostFoundReportsFound" to "실종/발견 신고를 찾을 수 없습니다",
    "errorLoadingReports" to "신고를 불러오는 중 오류가 발생했습니다",
    "yourEmail" to "회원님의 이메일",
    "contactReporter" to "신고자에게 연락",
    "contactFormIncomplete" to "이메일과 메시지를 입력해 주세요",
    "contactSent" to "메시지가 전송되었습니다",
    "contactFailed" to "메시지 전송에 실패했습니다",
    "lostFoundReportSubmitted" to "신고가 제출되었습니다. 감사합니다 - 인근의 다른 신고와 대조해 드리겠습니다.",
    "video" to "동영상(선택)",
    "removeVideo" to "동영상 삭제",
    "saveThisSearch" to "이 검색 저장하기",
    "savedSearchSaved" to "검색이 저장되었습니다! 새로운 일치 항목이 등록되면 이메일로 알려드립니다.",
    "savedSearchFailed" to "검색 저장에 실패했습니다",
    "favorite" to "즐겨찾기",
    "addToFavorites" to "즐겨찾기에 추가",
    "removeFromFavorites" to "즐겨찾기에서 제거",
    "myFavorites" to "내 즐겨찾기",
    "noFavoritesYet" to "아직 즐겨찾기가 없습니다",
    "mySavedSearches" to "내 저장된 검색",
    "noSavedSearchesYet" to "아직 저장된 검색이 없습니다",
    "donationPromptText" to "오늘 반려동물을 도와주셔서 감사합니다. 가능하시다면 Adopt-U를 모두에게 무료로 유지할 수 있도록 소액 후원을 고려해 주세요.",
    "donationPromptCta" to "후원하기",
    "dismiss" to "닫기",
)

private val japaneseTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "ログイン",
    "register" to "登録",
    "logout" to "セッションを終了",
    "profile" to "プロフィール",
    "myPets" to "マイペット",
    "addPet" to "ペットを追加",
    "save" to "保存",
    "cancel" to "キャンセル",
    "search" to "検索",
    "shelters" to "シェルター",
    "photographers" to "フォトグラファー",
    "temporalHomes" to "一時預かり先",
    "findTemporalHomes" to "一時預かり先を探す",
    "sterilizationLocations" to "去勢・避妊手術施設",
    "donate" to "寄付する",
    "countryLabel" to "国",
    "selectCountryToSearch" to "国を選択してください",
    "selectCountryFirst" to "これらのフィルターを使用するには国を選択してください",
    "state" to "都道府県/州",
    "city" to "市区町村",
    "zipCode" to "郵便番号",
    "neighborhood" to "地域",
    "name" to "名前",
    "email" to "メールアドレス",
    "phone" to "電話番号",
    "address" to "住所",
    "description" to "説明",
    "animalShelters" to "動物シェルター",
    "sheltersDescription" to "ペットを引き取ったり、事前にシェルターと合意のうえで預けたりできる動物シェルターを見つけましょう",
    "photographerDescription" to "ペットの撮影セッションを提供するプロのフォトグラファー",
    "sterilizationLocationsDescription" to "動物の去勢・避妊手術を受けられる施設を見つけましょう。",
    "privacyPolicy" to "プライバシーポリシー",
    "privacyLastUpdated" to "最終更新日:2025年4月",
    "privacyHighlight" to "Adopt-Uでは、データ収集を最小限に抑えることを信条としています。里親希望者と保護活動者間のコミュニケーションを可能にするため、メールアドレスのみを収集します。お客様の情報を広告に使用することはなく、マーケティング目的で第三者にデータを共有することも決してありません。",
    "privacyInfoCollectedH2" to "収集する情報",
    "privacyAccountRegH3" to "アカウント登録",
    "privacyAccountRegIntro" to "Adopt-Uでアカウントを作成する際、以下の情報を収集します。",
    "privacyEmailLabel" to "メールアドレス:",
    "privacyEmailDesc" to "アカウント確認、パスワードの再設定、および里親希望者と保護活動者間のコミュニケーションのために必要です",
    "privacyDisplayNameLabel" to "表示名:",
    "privacyDisplayNameDesc" to "プラットフォーム上でご自身を識別するために選択する名前です",
    "privacyLangPrefLabel" to "言語設定:",
    "privacyLangPrefDesc" to "ご希望の言語でプラットフォームを提供するために使用します",
    "privacyNoFullNameEtc" to "氏名、電話番号、住所、身分証明書、支払い情報など、上記以外の個人情報は収集しません。",
    "privacyOptionalProfileH3" to "任意のプロフィール情報",
    "privacyOptionalProfileIntro" to "ご利用の役割に応じて、以下の情報を任意でご提供いただけます。",
    "privacyAdopterLabel" to "里親希望者の場合:",
    "privacyAdopterDesc" to "追加情報の提供や収集は必要ありません",
    "privacyRescuerLabel" to "保護活動者の場合:",
    "privacyRescuerDesc" to "里親募集中のペットに関する所在地情報",
    "privacyPhotographerLabel" to "フォトグラファーの場合:",
    "privacyPhotographerDesc" to "サービス提供地域と連絡方法のご希望",
    "privacyTemporalHomeLabel" to "一時預かり先の場合:",
    "privacyTemporalHomeDesc" to "一時的な預かり先として提供できる所在地",
    "privacyLocationUsage" to "所在地情報はすべて、里親希望者と近隣の保護活動者・サービス提供者とのマッチングのみに使用されます。",
    "privacyPetInfoH3" to "ペットに関する情報",
    "privacyPetInfoIntro" to "保護活動者は、以下を含むペットに関する情報を掲載できます。",
    "privacyPetPhotos" to "ペットの写真",
    "privacyPetDescBreedAge" to "説明、犬種・猫種、年齢、特徴",
    "privacyPetHealth" to "健康状態およびワクチン接種状況",
    "privacyPetLocation" to "ペットの所在地",
    "privacyPetInfoNotVerified" to "この情報は保護活動者によって提供されたものであり、Adopt-Uが独自に検証したものではありません。",
    "privacyHowWeUseH2" to "情報の利用方法",
    "privacyPrimaryPurposeH3" to "主な目的:里親縁組のマッチング支援",
    "privacyEmailUsedFor" to "お客様のメールアドレスは、以下の目的にのみ使用されます。",
    "privacyUseVerification" to "アカウント作成時に確認用リンクを送信するため",
    "privacyUseRescuerContact" to "ご興味のあるペットについて保護活動者から連絡を受けられるようにするため",
    "privacyUseContactRescuer" to "掲載されているペットについて保護活動者に連絡できるようにするため",
    "privacyUseNotifications" to "重要なアカウントおよびセキュリティに関する通知を送信するため",
    "privacyOnceConnected" to "本プラットフォームを通じて他のユーザーとつながった後は、それ以降のやり取りはすべて当事者間で直接行われます。当社はその通信内容を監視、保存、または閲覧することはありません。",
    "privacyNeverDoH3" to "当社が決して行わないこと",
    "privacyCommitIntro" to "当社は以下を固くお約束します。",
    "privacyNoAdsLabel" to "広告を行いません:",
    "privacyNoAdsDesc" to "お客様の行動に基づくターゲティング広告や非ターゲティング広告を表示することはありません",
    "privacyNoMarketingLabel" to "マーケティング利用を行いません:",
    "privacyNoMarketingDesc" to "マーケティングや販促目的でお客様のメールアドレスを第三者と共有することはありません",
    "privacyNoSellingLabel" to "データ販売を行いません:",
    "privacyNoSellingDesc" to "お客様の個人情報を第三者に販売、貸与、その他の方法で譲渡することは決してありません",
    "privacyNoProfilingLabel" to "プロファイリングを行いません:",
    "privacyNoProfilingDesc" to "基本的なアカウント管理以外の目的でお客様のデータからプロフィールを作成することはありません",
    "privacyNoTrackingLabel" to "トラッキングを行いません:",
    "privacyNoTrackingDesc" to "トラッキングピクセル、広告用クッキー、その他類似の追跡技術を使用しません",
    "privacyDataStorageH2" to "データの保管とセキュリティ",
    "privacyDataStoredSecurely" to "お客様のデータは業界標準の暗号化技術を用いて安全に保管されます。当社は、不正アクセス、改ざん、漏えい、破壊からお客様の個人情報を保護するため、適切な技術的・組織的措置を講じています。",
    "privacyDataRetention" to "お客様のアカウント情報は、アカウントが有効である限り保持されます。いつでも当社にご連絡いただくことで、アカウントおよび関連データの削除を依頼できます。",
    "privacyYourRightsH2" to "お客様の権利",
    "privacyRightsIntro" to "お客様は、ご自身の個人データに関して以下の権利を有します。",
    "privacyRightAccess" to "アクセス権:当社が保有するお客様に関するすべての個人データの写しを請求できます",
    "privacyRightCorrection" to "訂正権:不正確な情報の訂正を請求できます",
    "privacyRightDeletion" to "削除権:アカウントおよび関連するすべてのデータの削除を請求できます",
    "privacyRightPortability" to "データポータビリティ権:一般的に使用される形式でデータの提供を請求できます",
    "privacyExerciseRights" to "これらの権利を行使するには、adopt-u@adopt-u.org までご連絡ください。",
    "privacyCookiesH2" to "クッキーについて",
    "privacyCookiesText" to "Adopt-Uでは、セッション管理や言語設定など、プラットフォームの機能に必要最小限のクッキーのみを使用しています。広告用クッキーや追跡用クッキーは使用しません。",
    "privacyThirdPartyH2" to "第三者サービス",
    "privacyThirdPartyIntro" to "当社は以下の第三者サービスを利用しており、これらがお客様のデータを処理する場合があります。",
    "privacyAws" to "AWS(Amazon Web Services):データの保管とメール配信のために使用しています。お客様のデータの処理には、AWSのプライバシーポリシーが適用されます。",
    "privacyWebauthn" to "WebAuthn/FIDO2:安全なパスキー認証のために使用しています。ブラウザ内で暗号処理が行われます。",
    "privacyThirdPartySelected" to "当社は、データプライバシーへの取り組みを共有できるサービス提供者を慎重に選定しています。",
    "privacyChildrensH2" to "児童のプライバシー",
    "privacyChildrensText" to "Adopt-Uは18歳未満の方の利用を想定していません。当社は未成年者から意図的に情報を収集することはありません。未成年者がアカウントを作成したと思われる場合は、削除のため当社までご連絡ください。",
    "privacyChangesH2" to "本ポリシーの変更",
    "privacyChangesText" to "当社は、実務や法的要件の変更を反映するため、本プライバシーポリシーを随時更新することがあります。重要な変更がある場合は、メールまたはプラットフォーム上の目立つ通知によりユーザーにお知らせします。",
    "privacyContactH2" to "お問い合わせ",
    "privacyContactText" to "本プライバシーポリシーに関するご質問やご懸念がある場合、またはデータに関する権利を行使される場合は、adopt-u@adopt-u.org までご連絡ください。",
    "termsConditions" to "利用規約",
    "installApp" to "アプリをインストール",
    "termsAcceptanceH2" to "規約への同意",
    "termsAcceptanceText" to "Adopt-Uにアクセスし利用することにより、お客様は本規約の条項に同意し、それに拘束されることを承諾したものとします。本規約に同意されない場合は、本プラットフォームをご利用にならないでください。",
    "termsNatureH2" to "本サービスの性質",
    "termsNatureText1" to "Adopt-Uは、ペットの保護活動者と里親希望者との連絡を仲介するプラットフォームです。当社はペットショップ、仲介業者、シェルター、里親斡旋機関のいずれでもありません。本プラットフォームに掲載されるペットの所有、保管、または仲介的な取り扱いは一切行いません。",
    "termsNatureText2" to "当社の役割は、当事者間の連絡手段を提供することに厳密に限定されています。里親縁組の交渉、契約、動物の引き渡しには一切関与しません。",
    "termsUserRespH2" to "ユーザーの責任",
    "termsForRescuersH3" to "保護活動者の方へ:",
    "termsRescuerResp1" to "保護しているペットについて、正確で真実な情報を提供すること",
    "termsRescuerResp2" to "掲載前にペットの健康状態を確認し、ワクチン接種および適切な獣医検査を済ませておくこと",
    "termsRescuerResp3" to "里親希望者に対して責任を持った審査を行うこと",
    "termsRescuerResp4" to "ペットが新しい家庭へ安全に引き渡されるよう手配すること",
    "termsRescuerResp5" to "提供するすべての情報の正確性について責任を負うこと",
    "termsRescuerResp6" to "ペットの里親縁組に関する現地の法令をすべて遵守すること",
    "termsRescuerResp7" to "本プラットフォームを商業的なペット販売やブリーディング目的で使用しないこと",
    "termsForAdoptersH3" to "里親希望者の方へ:",
    "termsAdopterResp1" to "ご自身の生活環境やペットの飼育経験について正確な情報を提供すること",
    "termsAdopterResp2" to "適切な世話ができることを示す準備をしておくこと",
    "termsAdopterResp3" to "里親縁組を取り消すことがペットに大きなストレスを与えることを理解すること",
    "termsAdopterResp4" to "保護活動者が里親申請を断る権利を尊重すること",
    "termsPetListingsH2" to "ペット掲載情報について",
    "termsPetListingsText1" to "Adopt-Uは、ペット掲載情報の正確性を検証、保証、または裏付けるものではありません。犬種・猫種、年齢、健康状態、性格、ワクチン接種記録などの情報は保護活動者によって提供されたものであり、独自の検証は行われていません。",
    "termsPetListingsText2" to "写真および説明文は保護活動者によって提供されています。故意の虚偽表示は禁止していますが、すべての掲載内容が動物の現在の状態を正確に反映していることを保証することはできません。",
    "termsPetListingsText3" to "里親縁組を最終決定する前に、質問をしたり、獣医の記録を求めたり、実際にペットと対面したりすることを推奨します。",
    "termsAdoptionProcessH2" to "里親縁組のプロセス",
    "termsAdoptionProcessIntro" to "保護活動者と里親希望者が里親縁組を進めることに合意した場合:",
    "termsAdoptionStep1" to "引き渡しの日時、場所、関連費用を含むすべての取り決めは、当事者間で直接交渉されます",
    "termsAdoptionStep2" to "Adopt-Uは、いかなる支払い取引の仲介や保証も行いません",
    "termsAdoptionStep3" to "本プラットフォームは里親縁組契約書を提供しません。契約に関する取り決めはすべて当事者の責任において行われます",
    "termsAdoptionStep4" to "すべての合意事項を書面で残すことを強く推奨します",
    "termsLiabilityH2" to "責任の制限",
    "termsLiabilityIntro" to "Adopt-Uは以下について責任を負いません。",
    "termsLiability1" to "保護活動者と里親希望者との間の紛争、意見の相違、対立",
    "termsLiability2" to "ペット掲載情報の正確性",
    "termsLiability3" to "里親縁組の前後を含む動物の健康状態、行動、または状態",
    "termsLiability4" to "本プラットフォームの利用に起因する金銭的損失、損害、または傷害",
    "termsLiability5" to "返却、健康上の問題、相性の問題などを含む里親縁組の結果",
    "termsLiability6" to "連絡が確立された後にユーザーがプラットフォーム外で行う行為",
    "termsLiabilityAck" to "ユーザーは、ペットを迎えることが重大な責任を伴う決断であることを認識し、その決定についてすべての責任を負うものとします。",
    "termsProhibitedH2" to "禁止事項",
    "termsProhibitedIntro" to "Adopt-Uでは、以下の行為を固く禁止しています。",
    "termsProhibited1" to "商業的なペット販売、ブリーディング事業、パピーミル",
    "termsProhibited2" to "自身が直接世話をしていないペットを掲載すること",
    "termsProhibited3" to "掲載情報やプロフィールにおける虚偽または誤解を招く情報",
    "termsProhibited4" to "他のユーザーへの嫌がらせ、差別、不適切な接触",
    "termsProhibited5" to "ペットの里親縁組と関係のない目的で個人情報を収集するために本プラットフォームを利用すること",
    "termsProhibited6" to "違法行為または動物福祉に関する法律の違反",
    "termsAccountTerminationH2" to "アカウントの停止・終了",
    "termsAccountTerminationText" to "Adopt-Uは、本規約に違反する、禁止行為を行う、またはプラットフォームの評判を損なうアカウントを停止または終了する権利を留保します。また、虚偽、誤解を招く、または不適切な内容を含む掲載情報を削除することがあります。",
    "termsModificationsH2" to "規約の変更",
    "termsModificationsText" to "当社は、本規約をいつでも変更する権利を留保します。変更後もプラットフォームの利用を継続した場合、変更後の規約に同意したものとみなされます。重要な変更がある場合は、メールまたはプラットフォーム上のお知らせによりユーザーにお知らせします。",
    "termsContactText" to "本利用規約に関するご質問がある場合は、adopt-u@adopt-u.org までご連絡ください。",
    "language" to "言語",
    "displayName" to "表示名",
    "saveProfile" to "プロフィールを保存",
    "accountSettings" to "アカウント設定",
    "yourRoles" to "あなたの役割:",
    "resources" to "リソース",
    "loginWithPasskey" to "パスキーでログイン",
    "signInWithPasskey" to "パスキーでサインイン",
    "resendVerificationEmail" to "確認メールを再送信",
    "usesFido" to "安全なパスワードレス認証のためにFIDO2/WebAuthnを使用します。",
    "loginWithMagicLink" to "メールリンクでログイン",
    "magicLinkDesc" to "ログイン用リンクをメールでお送りします(有効期限は5分間です)。",
    "sendMagicLink" to "ログインリンクを送信",
    "loginWithPassword" to "パスワードでログイン",
    "signIn" to "サインイン",
    "forgotPassword" to "パスワードをお忘れですか?",
    "registerNewAccount" to "アカウントを作成",
    "accountDetails" to "アカウント情報",
    "registrationMethod" to "登録方法",
    "selectAdditionalRoles" to "追加の役割を選択してください:",
    "adoptPet" to "ペットを引き取る",
    "publishPets" to "ペットを里親募集に掲載する",
    "offerPhotography" to "撮影サービスを提供する",
    "photographerSettings" to "フォトグラファー設定",
    "sessionFee" to "セッション料金",
    "provideTemporaryHome" to "ペットの一時預かり先を提供する",
    "passkeyNoPassword" to "パスキーはパスワードより安全で、お使いのすべてのデバイスで利用できます。",
    "registerWithPasskey" to "パスキーで登録",
    "passkeySettings" to "パスキー設定",
    "password" to "パスワード",
    "confirmPassword" to "パスワード(確認)",
    "passwordRequirements" to "パスワードは8文字以上で、大文字、小文字、数字、記号を含める必要があります",
    "securityInfo" to "セキュリティ情報",
    "passkeyMostSecure" to "パスキー:最も安全でおすすめです",
    "passkeyBrowserCompat" to "パスキーは一部のブラウザでは利用できない場合があります",
    "passwordLeastSecure" to "パスワード:安全性が低く、非推奨です",
    "selectLoginMethods" to "1つ以上のログイン方法を選択してください:",
    "registerPasskeyOption" to "パスキー(最も安全で、すべてのデバイスで利用可能)",
    "registerPasswordOption" to "パスワード(安全性は低いため、バックアップとしてのみ使用)",
    "howPasskeysWorkTitle" to "パスキーの仕組みは?",
    "howPasskeysWorkIntro" to "パスキーは、指紋認証、顔認証、画面ロックのPINなど、お使いのデバイスに組み込まれたセキュリティ機能でパスワードを置き換えます。",
    "howPasskeysWorkSecurity" to "お使いのデバイスにのみ保存され、フィッシング詐欺やデータ漏えいで盗まれることがなく、デバイス間で自動的に同期されます。",
    "howPasskeysWorkBackup" to "パスワードを追加することもできますが、パスキーだけでサインインでき、パスワードは不要です。",
    "gotIt" to "わかりました",
    "alreadyHaveAccount" to "すでにアカウントをお持ちですか?",
    "all" to "すべて",
    "filterDogs" to "犬",
    "filterCats" to "猫",
    "filterBirds" to "鳥",
    "filterFish" to "魚",
    "filterAllSexOption" to "すべての性別",
    "filterMaleOption" to "♂ オス",
    "filterFemaleOption" to "♀ メス",
    "age" to "年齢",
    "rescued" to "保護日",
    "years" to "歳",
    "months" to "ヶ月",
    "year" to "歳",
    "month" to "ヶ月",
    "loginToAdopt" to "里親申請にはログインが必要です",
    "requiredMarker" to "(必須)",
    "pageNotFoundTitle" to "ページが見つかりません",
    "pageNotFoundHint" to "リンクが古いか間違っている可能性があります。ペットたちはここにいます。",
    "browsePets" to "ペットを見る",
    "requestsFromRescuers" to "レスキュアーからのリクエスト",
    "signingOut" to "サインアウト中...",
    "adopter" to "里親希望者",
    "country" to "国",
    "zip" to "郵便番号",
    "viewPublic" to "公開ページを見る",
    "banUser" to "ユーザーをBANする",
    "banReason" to "理由（任意）",
    "myPhotoRequests" to "写真撮影リクエスト",
    "noPhotoRequestsYet" to "まだ撮影をリクエストしていません。",
    "photoRequestsReceived" to "受け取った撮影リクエスト",
    "noPhotoRequestsReceived" to "撮影リクエストはまだありません。",
    "photoStatusPending" to "保留中",
    "photoStatusApproved" to "承認済み",
    "photoStatusRejected" to "却下",
    "photoStatusCancelled" to "キャンセル",
    "photoStatusCompleted" to "完了",
    "cancelRequest" to "リクエストを取り消す",
    "registerConsentPrefix" to "アカウントを作成すると、次に同意したことになります：",
    "registerConsentTerms" to "利用規約",
    "registerConsentAnd" to "および",
    "registerConsentPrivacy" to "プライバシーポリシー",
    "petStatusAvailable" to "里親募集中",
    "petStatusAdopted" to "里親決定",
    "petStatusDisabled" to "非公開",
    "petStatusPending" to "保留中",
    "addEditLocation" to "所在地を追加/編集",
    "addLocation" to "所在地を追加",
    "manage" to "管理",
    "adminPanel" to "管理パネル",
    "adminSterilizationLocations" to "去勢・避妊手術施設を管理",
    "adopterRequired" to "里親希望者(必須)",
    "adoptionFee" to "里親縁組費用",
    "adoptionRequests" to "里親申請",
    "ageMonths" to "年齢(月)",
    "ageYears" to "年齢(歳)",
    "alias" to "名前",
    "bird" to "鳥",
    "breed" to "犬種/猫種",
    "medicalEventsOverview" to "医療イベントの概要",
    "back" to "戻る",
    "cat" to "猫",
    "color" to "毛色",
    "compatibility" to "相性",
    "currentPassword" to "現在のパスワード",
    "dog" to "犬",
    "emailChange" to "メールアドレスの変更",
    "emailChangeDesc" to "メールアドレスを更新します。新しいメールアドレスに確認用リンクが送信されます。",
    "energyLevel" to "活発さ",
    "female" to "メス",
    "findTemporalHome" to "一時預かり先を探す",
    "fish" to "魚",
    "goodWithCats" to "猫と相性が良い",
    "goodWithDogs" to "犬と相性が良い",
    "goodWithKids" to "子供と相性が良い",
    "high" to "高い",
    "houseTrained" to "しつけ済み",
    "large" to "大型",
    "low" to "低い",
    "male" to "オス",
    "managePets" to "ペットを管理",
    "managePetsBtn" to "ペットを追加・編集",
    "managePetsDescription" to "すべてのペットページを管理します。ペットの追加や削除ができます。",
    "manageShelters" to "シェルターを管理",
    "manageSheltersBtn" to "シェルターを追加・編集",
    "manageSheltersDescription" to "すべてのシェルターページを管理します。シェルターの追加や削除ができます。",
    "manageUsers" to "ユーザーを管理",
    "medical" to "医療情報",
    "medium" to "中型",
    "backToSearch" to "検索に戻る",
    "contactThisHome" to "この一時預かり先に連絡する",
    "loginAsRescuerToContact" to "この一時預かり先にリクエストを送るには、保護活動者としてログインしてください。",
    "memberSince" to "登録日",
    "microchipId" to "マイクロチップID",
    "microchipped" to "マイクロチップ装着済み",
    "myTemporalHome" to "マイ一時預かり先",
    "newEmail" to "新しいメールアドレス",
    "noTemporalHomes" to "一時預かり先が見つかりませんでした。",
    "requestSentToHome" to "リクエストを送信しました!",
    "sendRequestBtn" to "リクエストを送信",
    "temporalHomeNotFound" to "この一時預かり先が見つかりませんでした。",
    "viewDetails" to "詳細を見る",
    "rescuerDirectory" to "保護活動者",
    "noRescuersFound" to "保護活動者が見つかりませんでした。",
    "availablePetsCount" to "里親募集中のペット数",
    "availablePets" to "里親募集中のペット",
    "noAvailablePetsForRescuer" to "この保護活動者には現在里親募集中のペットがいません。",
    "rescuerNotFound" to "この保護活動者が見つかりませんでした。",
    "errorLoadingResults" to "結果の読み込み中にエラーが発生しました。",
    "volunteerForThisRescuer" to "このレスキュー団体のボランティアになる",
    "volunteerExplanation" to "承認されたボランティアは、このレスキュー団体のペット掲載情報の編集を提案できます。",
    "applyToVolunteerBtn" to "ボランティアに応募する",
    "loginToVolunteer" to "このレスキュー団体のボランティアに応募するにはログインしてください。",
    "volunteerApplicationSent" to "ボランティア応募を送信しました!",
    "myVolunteering" to "マイボランティア",
    "noVolunteerApplicationsYet" to "まだどのレスキュー団体にもボランティア応募をしていません。",
    "howToVolunteerHint" to "応募するには、ディレクトリからレスキュアーのページを開き、\"ボランティアに応募\"を押してください。",
    "volunteerStatusPending" to "審査中",
    "volunteerStatusActive" to "有効",
    "volunteerStatusRejected" to "却下",
    "volunteerApplications" to "ボランティア応募一覧",
    "noVolunteerApplications" to "ボランティア応募がありません。",
    "petEditSuggestions" to "ペット編集の提案",
    "noPetEditSuggestions" to "保留中の編集提案はありません。",
    "suggestedByLabel" to "提案者",
    "suggestAnEditTitle" to "編集を提案する",
    "suggestAnEditExplanation" to "承認されたボランティアとして、この掲載情報の変更を提案できます。公開前に保護活動者が確認します。",
    "suggestEditBtn" to "提案を送信",
    "editSuggestionSent" to "提案を保護活動者に送信し、確認を依頼しました!",
    "sponsorThisPetTitle" to "このペットをスポンサーする",
    "sponsorExplanation" to "引き取りは難しいけれど費用面で力になりたい方は、金銭または現物での支援を申し出て、保護活動者と直接つながることができます。",
    "sponsorThisRescuerTitle" to "この保護活動者をスポンサーする",
    "sponsorRescuerExplanation" to "金銭または現物支援で、この保護活動者の活動費用を支えましょう。",
    "sponsorTypeLabel" to "どのような支援ができますか?",
    "sponsorTypeMoney" to "お金",
    "sponsorTypeInKind" to "現物(フード、用品など)",
    "amountLabel" to "金額（毎月）",
    "currencyLabel" to "通貨",
    "inKindDescriptionLabel" to "提供できる内容を記入してください",
    "messageLabel" to "メッセージ",
    "sendOfferBtn" to "申し出を送信",
    "sponsorOfferSent" to "申し出を送信しました!詳細については保護活動者からご連絡します。",
    "sponsorshipOffersReceived" to "スポンサーの申し出",
    "noSponsorshipOffers" to "まだスポンサーの申し出がありません。",
    "generalFundLabel" to "一般支援",
    "offeringLabel" to "提供内容",
    "sponsorshipStatusSent" to "新着",
    "sponsorshipStatusRead" to "既読",
    "markAsReadBtn" to "既読にする",
    "mySponsorshipOffers" to "マイスポンサー申し出",
    "noSponsorshipOffersYet" to "まだスポンサーの申し出を送っていません。",
    "howToSponsorHint" to "支援するには、レスキュアーのページ（全般的な支援）またはペットのページ（そのペットの費用）を開いて申し出を送ってください。",
    "browsePetsLink" to "ペットを見る",
    "yourMessage" to "あなたのメッセージ",
    "newPassword" to "新しいパスワード",
    "passkeyDesc" to "アカウントにパスキーを追加すると、複数のデバイスから簡単にログインできます。",
    "passkeyName" to "パスキー名",
    "pers" to "人数",
    "petFoodDescription" to "ペットにとって安全な食べ物、有害な食べ物、危険な食べ物を確認しましょう",
    "petFoodGuide" to "ペットフードガイド",
    "foodInformation" to "食べ物に関する情報",
    "petsForAdoption" to "里親募集中のペット",
    "chooseCountryPrompt" to "新しい家族を見つけよう",
    "chooseCountryPromptHint" to "上の国を選択すると、お近くで里親募集中のペットを閲覧できます。",
    "noPetsFound" to "ペットが見つかりませんでした",
    "noPetsFoundHint" to "別のフィルターをお試しいただくか、しばらくしてから再度ご確認ください。新しいペットが随時追加されています。",
    "loadingMorePets" to "ペットを読み込んでいます…",
    "photos" to "写真(最大12枚)",
    "profilePhotographerRole" to "フォトグラファー - 撮影サービスを提供する",
    "profileRescuerRole" to "保護活動者 - ペットを里親募集に掲載する",
    "profileTemporalHomeRole" to "一時預かり先 - ペットの一時預かり先を提供する",
    "provideShelter" to "シェルター - 動物シェルターを運営する",
    "provideSterilization" to "去勢・避妊手術サービス - 去勢・避妊手術サービスを提供する",
    "requestEmailChange" to "メールアドレス変更を申請",
    "rescueDate" to "保護日",
    "rescueLocation" to "保護場所",
    "savePassword" to "パスワードを保存",
    "selectEnergy" to "活発さを選択",
    "selectSize" to "サイズを選択",
    "sex" to "性別",
    "shelterSettings" to "シェルター設定",
    "size" to "サイズ",
    "small" to "小型",
    "specialNeeds" to "特別なケアが必要",
    "sterilizationSettings" to "去勢・避妊手術施設設定",
    "sterilized" to "去勢・避妊手術済み",
    "temporalHomeSettings" to "一時預かり先設定",
    "temperament" to "性格",
    "type" to "種類 *",
    "urgentAdoption" to "緊急で里親募集中",
    "vaccinations" to "ワクチン接種",
    "vetDisclaimer" to "食事内容を変更する前に、必ず獣医にご相談ください。",
    "weight" to "体重(kg)",
    "weightLabel" to "体重",
    "website" to "ウェブサイト",
    "securitySettings" to "セキュリティ設定",
    "emailVerifiedTitle" to "メールアドレスの確認が完了しました!",
    "emailVerifiedMessage" to "メールアドレスが確認されました。登録が完了しました。",
    "redirectingIn" to "メインページへ自動的に移動します(残り",
    "canAlsoLogin" to "または、",
    "loginNow" to "今すぐログイン",
    "emailVerificationFailed" to "確認に失敗しました",
    "invalidOrExpiredToken" to "確認用リンクが無効か、有効期限が切れています。",
    "tryRegisteringAgain" to "もう一度登録をお試しください。",
    "verificationEmailResent" to "確認メールの有効期限が切れていたため、新しい確認メールを送信しました。受信トレイをご確認ください。",
    "emailVerificationSent" to "確認メールを送信しました。ログインする前に受信トレイをご確認ください。",
    "emailNotVerifiedLogin" to "ログインする前にメールアドレスの確認が必要です。受信トレイの確認用リンクをご確認ください。",
    "invalidOrExpiredMagicLink" to "ログインリンクが無効か、有効期限が切れています。新しいリンクをリクエストしてください。",
    "invalidToken" to "無効なトークンです。",
    "accountBanned" to "アカウントが停止されています。",
    "loginError" to "ログイン中にエラーが発生しました。",
    "profileUpdated" to "プロフィールを更新しました!",
    "displayNameRequired" to "表示名を入力してください",
    "saving" to "保存中...",
    "passwordSet" to "パスワード設定済み",
    "noPassword" to "パスワード未設定",
    "passwordChangeSuccess" to "パスワードを変更しました!",
    "passwordChangeFailed" to "パスワードの変更に失敗しました",
    "passwordSetSuccess" to "パスワードを設定しました!",
    "passwordSetFailed" to "パスワードの設定に失敗しました",
    "passwordsDoNotMatch" to "パスワードが一致しません",
    "currentPasswordRequired" to "現在のパスワードを入力してください",
    "emailChangeSent" to "新しいメールアドレスに確認メールを送信しました!",
    "emailChangeFailed" to "メールアドレス変更の申請に失敗しました",
    "emailRequired" to "メールアドレスを入力してください",
    "sendingEllipsis" to "送信中...",
    "resetLinkSent" to "パスワード再設定用リンクを送信しました!メールをご確認ください。",
    "magicLinkSent" to "ログインリンクを送信しました!メールをご確認ください。",
    "magicLinkSendFailed" to "ログインリンクの送信に失敗しました",
    "failedSendResetLink" to "再設定用リンクの送信に失敗しました。",
    "invalidOrMissingToken" to "トークンが無効か見つかりません。",
    "passwordTooShort" to "パスワードは8文字以上で入力してください。",
    "passwordNeedUppercase" to "パスワードには大文字を1文字以上含めてください。",
    "passwordNeedLowercase" to "パスワードには小文字を1文字以上含めてください。",
    "passwordNeedNumber" to "パスワードには数字を1文字以上含めてください。",
    "passwordNeedSymbol" to "パスワードには記号を1文字以上含めてください。",
    "resettingPassword" to "パスワードを再設定しています...",
    "passwordResetSuccess" to "パスワードを再設定しました!ログインできます。",
    "failedResetPassword" to "パスワードの再設定に失敗しました。",
    "loginSuccessRedirecting" to "ログインに成功しました!移動しています...",
    "loginFailedExpiredLink" to "ログインに失敗しました。リンクが無効か、有効期限が切れている可能性があります。",
    "loginFailed" to "ログインに失敗しました。",
    "emailChangedSuccess" to "メールアドレスを変更しました!",
    "failedChangeEmailExpired" to "メールアドレスの変更に失敗しました。リンクが無効か、有効期限が切れている可能性があります。",
    "failedChangeEmail" to "メールアドレスの変更に失敗しました。",
    "emailVerifiedSuccess" to "メールアドレスを確認しました!",
    "failedVerifyEmailExpired" to "メールアドレスの確認に失敗しました。リンクが無効か、有効期限が切れている可能性があります。",
    "failedVerifyEmail" to "メールアドレスの確認に失敗しました。",
    "dropImagesHint" to "ここに画像をドロップするか、クリックして選択してください",
    "forgotPasswordTitle" to "パスワードをお忘れですか",
    "forgotPasswordIntro" to "メールアドレスを入力すると、パスワード再設定用のリンクをお送りします。",
    "sendResetLink" to "再設定用リンクを送信",
    "backToLogin" to "ログインに戻る",
    "resetPasswordTitle" to "パスワードの再設定",
    "resetPasswordIntro" to "新しいパスワードを以下に入力してください。",
    "emailLinkLoginTitle" to "メールリンクログイン",
    "verifying" to "確認中...",
    "emailChangeTitle" to "メールアドレスの変更",
    "verifyContactEmailTitle" to "連絡先メールアドレスの確認",
    "status" to "ステータス",
    "yes" to "はい",
    "no" to "いいえ",
    "noDescription" to "説明はありません。",
    "urgentBadge" to "緊急 - すぐに新しい家が必要です!",
    "needsNewHome" to "このペットは緊急に新しい家庭を必要としています",
    "promotedReasonLabel" to "理由",
    "selectReason" to "理由を選択してください",
    "promotedReasonMoving" to "飼い主が引っ越すため",
    "promotedReasonComplaints" to "苦情がある/これ以上飼えない",
    "promotedReasonPetConflict" to "他のペットとの相性が悪い",
    "promotedReasonOther" to "その他",
    "promotedReasonDetailLabel" to "詳細(任意)",
    "promotedReasonRequired" to "理由を選択してください",
    "needsNewHomeBadge" to "新しい家庭を募集中",
    "housingType" to "住居の種類",
    "preferNotToSay" to "回答しない",
    "houseHousing" to "一戸建て",
    "apartmentHousing" to "集合住宅",
    "hasYard" to "庭がある",
    "hasOtherPets" to "他のペットと同居",
    "adoptionExperience" to "飼育経験",
    "firstTimeAdopter" to "初めて飼う",
    "experiencedAdopter" to "飼育経験あり",
    "noAdoptionRequests" to "里親申請はありません",
    "noMessage" to "メッセージなし",
    "markUnderReview" to "審査中にする",
    "approve" to "承認",
    "reject" to "却下",
    "reviewNoteLabel" to "非公開の審査メモ",
    "saveNote" to "メモを保存",
    "adoptionStatusPending" to "保留中",
    "adoptionStatusUnderReview" to "審査中",
    "adoptionStatusApproved" to "承認済み",
    "adoptionStatusRejected" to "不承認",
    "myAdoptionRequests" to "マイ里親申請",
    "noAdoptionRequestsYet" to "まだ里親申請をしていません。",
    "medicalHistory" to "医療履歴",
    "medicalSchedule" to "ワクチン接種・駆虫スケジュール",
    "recordType" to "種類",
    "vaccination" to "ワクチン接種",
    "deworming" to "駆虫",
    "recordName" to "名前",
    "dateGiven" to "実施日",
    "nextDueDate" to "次回予定日(任意)",
    "notesOptional" to "メモ(任意)",
    "addRecord" to "記録を追加",
    "noMedicalRecords" to "ワクチン接種・駆虫の記録はまだありません。",
    "nextDueLabel" to "次回予定日",
    "givenLabel" to "実施済み",
    "overdue" to "期限切れ",
    "dueSoon" to "まもなく期限",
    "upToDate" to "最新",
    "recordNameRequired" to "名前を入力してください",
    "dateGivenRequired" to "実施日を入力してください",
    "failedToAddRecord" to "記録の追加に失敗しました",
    "confirmDeleteRecord" to "この記録を削除しますか?",
    "maxCapacity" to "同時に預かれる最大数(任意)",
    "placePetHere" to "ここにペットを預ける",
    "noAvailablePetsToPlace" to "現在預けられるペットがいません。",
    "selectPet" to "ペットを選択",
    "startPlacementBtn" to "預かりを開始",
    "placementStarted" to "一時預かりを開始しました!",
    "petsCurrentlyWithMe" to "現在預かっているペット",
    "noPetsCurrentlyFostered" to "現在預かっているペットはいません。",
    "sinceLabel" to "開始日",
    "fosterPlacement" to "一時預かり",
    "endPlacement" to "預かりを終了",
    "currentlyWithLabel" to "現在の預かり先",
    "notCurrentlyFostered" to "現在一時預かり中ではありません。",
    "confirmEndPlacement" to "この一時預かりを終了しますか?ペットはあなたの元に戻ったものとして記録されます。",
    "aTemporalHome" to "一時預かり先",
    "viewsLabel" to "閲覧数",
    "inquiriesLabel" to "問い合わせ数",
    "conversionLabel" to "成約率",
    "messageOptional" to "メッセージ(任意)",
    "requestAdoption" to "里親を申請する",
    "editPet" to "ペット情報を編集",
    "adoptionRequestSubmitted" to "里親申請を送信しました!",
    "failedSubmitRequest" to "申請の送信に失敗しました",
    "noPhotosYet" to "まだ写真がありません。",
    "rabbit" to "うさぎ",
    "safeFoods" to "安全な食べ物",
    "harmfulFoods" to "有害な食べ物",
    "cannotEatFoods" to "食べさせてはいけない食べ物",
    "legendSafe" to "安全",
    "legendHarmful" to "有害",
    "legendToxic" to "危険",
    "food_dog_safe_chicken_name" to "鶏肉",
    "food_dog_safe_chicken_detail" to "調理済み、骨なし",
    "food_dog_safe_chicken_desc" to "低脂肪で消化しやすいタンパク質",
    "food_dog_safe_rice_name" to "米",
    "food_dog_safe_rice_detail" to "白米または玄米",
    "food_dog_safe_rice_desc" to "良質な炭水化物源",
    "food_dog_safe_carrots_name" to "にんじん",
    "food_dog_safe_carrots_detail" to "生または加熱調理",
    "food_dog_safe_carrots_desc" to "低カロリーで食物繊維が豊富",
    "food_dog_safe_apples_name" to "りんご",
    "food_dog_safe_apples_detail" to "種を除く",
    "food_dog_safe_apples_desc" to "ビタミンが豊富",
    "food_dog_safe_peanutbutter_name" to "ピーナッツバター",
    "food_dog_safe_peanutbutter_detail" to "無塩でキシリトール不使用のもの",
    "food_dog_safe_peanutbutter_desc" to "良質なタンパク質源",
    "food_dog_harmful_grapes_name" to "ぶどう",
    "food_dog_harmful_grapes_detail" to "すべての種類",
    "food_dog_harmful_grapes_desc" to "腎不全を引き起こす可能性があります",
    "food_dog_harmful_onions_name" to "玉ねぎ",
    "food_dog_harmful_onions_detail" to "すべての形態",
    "food_dog_harmful_onions_desc" to "赤血球を損傷します",
    "food_dog_harmful_chocolate_name" to "チョコレート",
    "food_dog_harmful_chocolate_detail" to "すべての種類",
    "food_dog_harmful_chocolate_desc" to "犬にとって有毒です",
    "food_dog_harmful_garlic_name" to "にんにく",
    "food_dog_harmful_garlic_detail" to "すべての形態",
    "food_dog_harmful_garlic_desc" to "貧血を引き起こします",
    "food_dog_cannot_xylitol_name" to "キシリトール",
    "food_dog_cannot_xylitol_detail" to "人工甘味料",
    "food_dog_cannot_xylitol_desc" to "強い毒性があります",
    "food_dog_cannot_macadamia_name" to "マカダミアナッツ",
    "food_dog_cannot_macadamia_detail" to "すべての形態",
    "food_dog_cannot_macadamia_desc" to "脱力症状を引き起こします",
    "food_dog_cannot_avocado_name" to "アボカド",
    "food_dog_cannot_avocado_detail" to "種、皮を含むすべての部分",
    "food_dog_cannot_avocado_desc" to "ペルシンを含みます",
    "food_dog_cannot_alcohol_name" to "アルコール",
    "food_dog_cannot_alcohol_detail" to "すべての形態",
    "food_dog_cannot_alcohol_desc" to "強い毒性があります",
    "food_cat_safe_cookedfish_name" to "加熱調理した魚",
    "food_cat_safe_cookedfish_detail" to "骨を除く",
    "food_cat_safe_cookedfish_desc" to "高タンパク",
    "food_cat_safe_chicken_name" to "鶏肉",
    "food_cat_safe_chicken_detail" to "調理済み、味付けなし",
    "food_cat_safe_chicken_desc" to "良質なタンパク質源",
    "food_cat_safe_pumpkin_name" to "かぼちゃ",
    "food_cat_safe_pumpkin_detail" to "味付けなし、加熱調理",
    "food_cat_safe_pumpkin_desc" to "消化を助けます",
    "food_cat_safe_eggs_name" to "卵",
    "food_cat_safe_eggs_detail" to "加熱調理",
    "food_cat_safe_eggs_desc" to "完全なタンパク質源",
    "food_cat_harmful_raweggs_name" to "生卵",
    "food_cat_harmful_raweggs_detail" to "アビジンを含む",
    "food_cat_harmful_raweggs_desc" to "ビオチンの吸収を妨げます",
    "food_cat_harmful_rawfish_name" to "生魚",
    "food_cat_harmful_rawfish_detail" to "チアミナーゼを含む",
    "food_cat_harmful_rawfish_desc" to "ビタミンB群を分解します",
    "food_cat_harmful_dogfood_name" to "ドッグフード",
    "food_cat_harmful_dogfood_detail" to "すべて",
    "food_cat_harmful_dogfood_desc" to "タウリンが不足しています",
    "food_cat_harmful_milk_name" to "牛乳",
    "food_cat_harmful_milk_detail" to "ほとんどの猫",
    "food_cat_harmful_milk_desc" to "乳糖不耐症の原因になります",
    "food_cat_cannot_chocolate_name" to "チョコレート",
    "food_cat_cannot_chocolate_detail" to "すべての種類",
    "food_cat_cannot_chocolate_desc" to "テオブロミンによる中毒",
    "food_cat_cannot_onionsgarlic_name" to "玉ねぎ/にんにく",
    "food_cat_cannot_onionsgarlic_detail" to "すべての形態",
    "food_cat_cannot_onionsgarlic_desc" to "赤血球を損傷します",
    "food_cat_cannot_grapesraisins_name" to "ぶどう/レーズン",
    "food_cat_cannot_grapesraisins_detail" to "すべての種類",
    "food_cat_cannot_grapesraisins_desc" to "腎臓に損傷を与えます",
    "food_cat_cannot_alcohol_name" to "アルコール",
    "food_cat_cannot_alcohol_detail" to "すべての形態",
    "food_cat_cannot_alcohol_desc" to "強い毒性があります",
    "food_bird_safe_seeds_name" to "種子",
    "food_bird_safe_seeds_detail" to "各種ミックス",
    "food_bird_safe_seeds_desc" to "良質な脂肪源",
    "food_bird_safe_fruits_name" to "果物",
    "food_bird_safe_fruits_detail" to "ほとんどの種類",
    "food_bird_safe_fruits_desc" to "ビタミンとミネラル",
    "food_bird_safe_vegetables_name" to "野菜",
    "food_bird_safe_vegetables_detail" to "葉物野菜",
    "food_bird_safe_vegetables_desc" to "必須栄養素",
    "food_bird_safe_pellets_name" to "ペレット",
    "food_bird_safe_pellets_detail" to "高品質に配合されたもの",
    "food_bird_safe_pellets_desc" to "完全栄養食",
    "food_bird_harmful_avocado_name" to "アボカド",
    "food_bird_harmful_avocado_detail" to "すべての部分",
    "food_bird_harmful_avocado_desc" to "ペルシンによる中毒",
    "food_bird_harmful_fruitpits_name" to "果物の種",
    "food_bird_harmful_fruitpits_detail" to "りんご、桃など",
    "food_bird_harmful_fruitpits_desc" to "微量のシアン化物を含みます",
    "food_bird_harmful_salt_name" to "塩",
    "food_bird_harmful_salt_detail" to "すべての形態",
    "food_bird_harmful_salt_desc" to "鳥にとって有毒です",
    "food_bird_harmful_caffeine_name" to "カフェイン",
    "food_bird_harmful_caffeine_detail" to "コーヒー、紅茶など",
    "food_bird_harmful_caffeine_desc" to "心臓に問題を引き起こします",
    "food_bird_cannot_chocolate_name" to "チョコレート",
    "food_bird_cannot_chocolate_detail" to "すべての種類",
    "food_bird_cannot_chocolate_desc" to "テオブロミンによる中毒",
    "food_bird_cannot_onions_name" to "玉ねぎ",
    "food_bird_cannot_onions_detail" to "すべての形態",
    "food_bird_cannot_onions_desc" to "赤血球を損傷します",
    "food_bird_cannot_garlic_name" to "にんにく",
    "food_bird_cannot_garlic_detail" to "すべての形態",
    "food_bird_cannot_garlic_desc" to "有毒です",
    "food_bird_cannot_mushrooms_name" to "きのこ",
    "food_bird_cannot_mushrooms_detail" to "野生種",
    "food_bird_cannot_mushrooms_desc" to "毒性がある可能性があります",
    "food_fish_safe_flakes_name" to "フレークフード",
    "food_fish_safe_flakes_detail" to "高品質なブランド",
    "food_fish_safe_flakes_desc" to "完全栄養の魚用フード",
    "food_fish_safe_pellets_name" to "ペレット",
    "food_fish_safe_pellets_detail" to "浮上性/沈下性",
    "food_fish_safe_pellets_desc" to "バランスの取れた栄養",
    "food_fish_safe_frozenfood_name" to "冷凍フード",
    "food_fish_safe_frozenfood_detail" to "アカムシ、ブラインシュリンプなど",
    "food_fish_safe_frozenfood_desc" to "タンパク質源",
    "food_fish_safe_vegetables_name" to "野菜",
    "food_fish_safe_vegetables_detail" to "湯通ししたズッキーニなど",
    "food_fish_safe_vegetables_desc" to "食物繊維源",
    "food_fish_harmful_bread_name" to "パン",
    "food_fish_harmful_bread_detail" to "すべての種類",
    "food_fish_harmful_bread_desc" to "栄養がなく満腹感だけを与えます",
    "food_fish_harmful_humanfood_name" to "人間の食べ物",
    "food_fish_harmful_humanfood_detail" to "調理済みの食事",
    "food_fish_harmful_humanfood_desc" to "栄養バランスが適していません",
    "food_fish_harmful_livefeed_name" to "生き餌",
    "food_fish_harmful_livefeed_detail" to "野生で捕獲した昆虫",
    "food_fish_harmful_livefeed_desc" to "寄生虫がいる可能性があります",
    "food_fish_cannot_landinsect_name" to "陸生昆虫",
    "food_fish_cannot_landinsect_detail" to "地面を這う虫",
    "food_fish_cannot_landinsect_desc" to "自然な食べ物ではありません",
    "food_fish_cannot_mammalmeat_name" to "哺乳類の肉",
    "food_fish_cannot_mammalmeat_detail" to "鶏肉、牛肉など",
    "food_fish_cannot_mammalmeat_desc" to "ほとんどの魚には適しません",
    "food_fish_cannot_dairy_name" to "乳製品",
    "food_fish_cannot_dairy_detail" to "すべて",
    "food_fish_cannot_dairy_desc" to "消化できません",
    "food_fish_cannot_breadcrumbs_name" to "パン粉",
    "food_fish_cannot_breadcrumbs_detail" to "にんにくを含むものが多い",
    "food_fish_cannot_breadcrumbs_desc" to "有害な添加物を含みます",
    "food_rabbit_safe_hay_name" to "牧草",
    "food_rabbit_safe_hay_detail" to "チモシー",
    "food_rabbit_safe_hay_desc" to "食事の80%を占めるべきもの",
    "food_rabbit_safe_leafygreens_name" to "葉物野菜",
    "food_rabbit_safe_leafygreens_detail" to "ロメインレタス、パクチーなど",
    "food_rabbit_safe_leafygreens_desc" to "毎日のビタミン補給に",
    "food_rabbit_safe_pellets_name" to "ペレット",
    "food_rabbit_safe_pellets_detail" to "チモシーベース",
    "food_rabbit_safe_pellets_desc" to "バランスの取れた栄養",
    "food_rabbit_safe_carrots_name" to "にんじん",
    "food_rabbit_safe_carrots_detail" to "適量を",
    "food_rabbit_safe_carrots_desc" to "おやつとして",
    "food_rabbit_harmful_icebergletttuce_name" to "レタス(玉レタス)",
    "food_rabbit_harmful_icebergletttuce_detail" to "栄養価が低い",
    "food_rabbit_harmful_icebergletttuce_desc" to "消化管うっ滞を引き起こす可能性があります",
    "food_rabbit_harmful_beans_name" to "豆類",
    "food_rabbit_harmful_beans_detail" to "すべてのマメ科植物",
    "food_rabbit_harmful_beans_desc" to "ガスや消化不良の原因になります",
    "food_rabbit_harmful_corn_name" to "とうもろこし",
    "food_rabbit_harmful_corn_detail" to "すべての形態",
    "food_rabbit_harmful_corn_desc" to "消化できません",
    "food_rabbit_harmful_potatoes_name" to "じゃがいも",
    "food_rabbit_harmful_potatoes_detail" to "すべての形態",
    "food_rabbit_harmful_potatoes_desc" to "栄養バランスが適していません",
    "food_rabbit_cannot_chocolate_name" to "チョコレート",
    "food_rabbit_cannot_chocolate_detail" to "すべての種類",
    "food_rabbit_cannot_chocolate_desc" to "テオブロミンによる中毒",
    "food_rabbit_cannot_onions_name" to "玉ねぎ",
    "food_rabbit_cannot_onions_detail" to "すべての形態",
    "food_rabbit_cannot_onions_desc" to "強い毒性があります",
    "food_rabbit_cannot_garlic_name" to "にんにく",
    "food_rabbit_cannot_garlic_detail" to "すべての形態",
    "food_rabbit_cannot_garlic_desc" to "有毒です",
    "food_rabbit_cannot_avocado_name" to "アボカド",
    "food_rabbit_cannot_avocado_detail" to "すべての部分",
    "food_rabbit_cannot_avocado_desc" to "ペルシンを含みます",
    "primary" to "メイン",
    "passkeyRegisteredSuccess" to "パスキーを登録しました!",
    "passkeyRegistrationFailed" to "パスキーの登録に失敗しました",
    "passkeyNameRequired" to "パスキー名を入力してください",
    "registeringPasskey" to "パスキーを登録しています...",
    "passkeyRegistered" to "パスキー登録済み",
    "noPasskey" to "パスキー未登録",
    "noPets" to "まだペットがいません。",
    "photographerCountryStateRequired" to "フォトグラファーサービスのため、国と都道府県/州を入力してください",
    "temporalHomeAliasCountryCityRequired" to "一時預かり先のため、名前、国、市区町村を入力してください",
    "shelterNameCountryCityAddressRequired" to "シェルターのため、名前、国、市区町村、住所を入力してください",
    "sterilizationNameCountryCityAddressRequired" to "去勢・避妊手術施設のため、名前、国、市区町村、住所を入力してください",
    "country.afghanistan" to "アフガニスタン",
    "country.albania" to "アルバニア",
    "country.algeria" to "アルジェリア",
    "country.argentina" to "アルゼンチン",
    "country.armenia" to "アルメニア",
    "country.australia" to "オーストラリア",
    "country.austria" to "オーストリア",
    "country.azerbaijan" to "アゼルバイジャン",
    "country.bangladesh" to "バングラデシュ",
    "country.belarus" to "ベラルーシ",
    "country.belgium" to "ベルギー",
    "country.bolivia" to "ボリビア",
    "country.bosnia" to "ボスニア・ヘルツェゴビナ",
    "country.brazil" to "ブラジル",
    "country.bulgaria" to "ブルガリア",
    "country.cambodia" to "カンボジア",
    "country.cameroon" to "カメルーン",
    "country.canada" to "カナダ",
    "country.chile" to "チリ",
    "country.china" to "中国",
    "country.colombia" to "コロンビア",
    "country.costaRica" to "コスタリカ",
    "country.croatia" to "クロアチア",
    "country.cuba" to "キューバ",
    "country.czechia" to "チェコ",
    "country.denmark" to "デンマーク",
    "country.dominicanRepublic" to "ドミニカ共和国",
    "country.ecuador" to "エクアドル",
    "country.egypt" to "エジプト",
    "country.elSalvador" to "エルサルバドル",
    "country.estonia" to "エストニア",
    "country.ethiopia" to "エチオピア",
    "country.finland" to "フィンランド",
    "country.france" to "フランス",
    "country.georgia" to "ジョージア",
    "country.germany" to "ドイツ",
    "country.ghana" to "ガーナ",
    "country.greece" to "ギリシャ",
    "country.guatemala" to "グアテマラ",
    "country.haiti" to "ハイチ",
    "country.honduras" to "ホンジュラス",
    "country.hungary" to "ハンガリー",
    "country.iceland" to "アイスランド",
    "country.india" to "インド",
    "country.indonesia" to "インドネシア",
    "country.iran" to "イラン",
    "country.iraq" to "イラク",
    "country.ireland" to "アイルランド",
    "country.israel" to "イスラエル",
    "country.italy" to "イタリア",
    "country.jamaica" to "ジャマイカ",
    "country.japan" to "日本",
    "country.jordan" to "ヨルダン",
    "country.kazakhstan" to "カザフスタン",
    "country.kenya" to "ケニア",
    "country.kuwait" to "クウェート",
    "country.latvia" to "ラトビア",
    "country.lebanon" to "レバノン",
    "country.libya" to "リビア",
    "country.lithuania" to "リトアニア",
    "country.luxembourg" to "ルクセンブルク",
    "country.malaysia" to "マレーシア",
    "country.mexico" to "メキシコ",
    "country.moldova" to "モルドバ",
    "country.mongolia" to "モンゴル",
    "country.montenegro" to "モンテネグロ",
    "country.morocco" to "モロッコ",
    "country.myanmar" to "ミャンマー",
    "country.nepal" to "ネパール",
    "country.netherlands" to "オランダ",
    "country.newZealand" to "ニュージーランド",
    "country.nicaragua" to "ニカラグア",
    "country.nigeria" to "ナイジェリア",
    "country.northKorea" to "北朝鮮",
    "country.norway" to "ノルウェー",
    "country.pakistan" to "パキスタン",
    "country.panama" to "パナマ",
    "country.paraguay" to "パラグアイ",
    "country.peru" to "ペルー",
    "country.philippines" to "フィリピン",
    "country.poland" to "ポーランド",
    "country.portugal" to "ポルトガル",
    "country.puertoRico" to "プエルトリコ",
    "country.qatar" to "カタール",
    "country.romania" to "ルーマニア",
    "country.russia" to "ロシア",
    "country.saudiArabia" to "サウジアラビア",
    "country.serbia" to "セルビア",
    "country.singapore" to "シンガポール",
    "country.slovakia" to "スロバキア",
    "country.slovenia" to "スロベニア",
    "country.southAfrica" to "南アフリカ",
    "country.southKorea" to "韓国",
    "country.spain" to "スペイン",
    "country.sriLanka" to "スリランカ",
    "country.sudan" to "スーダン",
    "country.sweden" to "スウェーデン",
    "country.switzerland" to "スイス",
    "country.syria" to "シリア",
    "country.taiwan" to "台湾",
    "country.thailand" to "タイ",
    "country.tunisia" to "チュニジア",
    "country.turkey" to "トルコ",
    "country.ukraine" to "ウクライナ",
    "country.uae" to "アラブ首長国連邦",
    "country.uk" to "イギリス",
    "country.usa" to "アメリカ合衆国",
    "country.uruguay" to "ウルグアイ",
    "country.uzbekistan" to "ウズベキスタン",
    "country.venezuela" to "ベネズエラ",
    "country.vietnam" to "ベトナム",
    "country.yemen" to "イエメン",
    "loading" to "読み込み中...",
    "countryRequired" to "検索する国を選択してください",
    "noSheltersFound" to "シェルターがまだ見つかりません。また後でご確認ください!",
    "visitWebsite" to "ウェブサイトを見る",
    "fiscalId" to "法人番号",
    "donationInformation" to "寄付情報",
    "bankName" to "銀行名",
    "accountHolder" to "口座名義人",
    "accountNumber" to "口座番号",
    "currency" to "通貨",
    "errorLoadingShelters" to "シェルターの読み込み中にエラーが発生しました",
    "pleaseSelectCountry" to "国を選択してください",
    "actions" to "操作",
    "addShelter" to "シェルターを追加",
    "addEditShelter" to "シェルターの追加・編集",
    "allStates" to "すべての都道府県/州",
    "confirmDelete" to "この所在地を削除してもよろしいですか?",
    "confirmDeleteShelter" to "このシェルターを削除してもよろしいですか?",
    "confirmUnbanUser" to "このユーザーの停止を解除しますか?",
    "confirmResetUserCredentials" to "{email} の現在のパスワードとパスキーを無効化し、再設定用リンクをメールで送信します。続行しますか?",
    "confirmDeactivateUser" to "このユーザーを無効化しますか?後で再度有効化できます。",
    "confirmDeactivatePet" to "このペットを非公開にしますか?公開一覧から非表示になり、後で再度公開できます。",
    "confirmBlockRescuer" to "この保護活動者からのリクエストを今後受け取らないようにブロックしますか?",
    "confirmDeletePet" to "このペットを削除しますか?",
    "confirmDeleteStorage" to "このストレージを削除しますか?",
    "contact" to "連絡先",
    "delete" to "削除",
    "edit" to "編集",
    "errorDeletingShelter" to "シェルターの削除中にエラーが発生しました",
    "errorLoadingLocations" to "所在地の読み込み中にエラーが発生しました",
    "errorLoadingShelter" to "シェルターの読み込み中にエラーが発生しました",
    "errorSaving" to "保存中にエラーが発生しました",
    "errorSavingShelter" to "シェルターの保存中にエラーが発生しました",
    "location" to "所在地",
    "noLocationsFound" to "所在地が見つかりませんでした",
    "selectCountry" to "国を選択してください",
    "selectCountryToFilter" to "絞り込む国を選択してください",
    "shelterAdded" to "シェルターを追加しました",
    "shelterDeleted" to "シェルターを削除しました",
    "shelterUpdated" to "シェルターを更新しました",
    "updateShelter" to "シェルターを更新",
    "errorLoadingPhotographers" to "フォトグラファーの読み込み中にエラーが発生しました",
    "noPhotographersAvailable" to "フォトグラファーが見つかりません",
    "requestPhotoSession" to "撮影セッションをリクエスト",
    "selectPhotographer" to "選択",
    "photographersSelected" to "{count}人選択中",
    "requestSelectedPhotographers" to "選択したフォトグラファーにリクエスト",
    "clearSelection" to "選択を解除",
    "requestToMultiple" to "リクエスト先",
    "messageRequired" to "フォトグラファーへのメッセージを入力してください。",
    "requestsSentSuccessfully" to "リクエストが正常に送信されました",
    "message" to "メッセージ",
    "sendRequest" to "リクエストを送信",
    "requestSentSuccessfully" to "リクエストを送信しました!",
    "requestTo" to "リクエスト送信先",
    "enterMessage" to "{name} へのメッセージを入力してください...",
    "reportUrgent" to "緊急",
    "topRescuers" to "トップ保護活動者",
    "urgentRescuerSettings" to "緊急レスキュー設定",
    "urgentRescueAlerts" to "緊急レスキューアラート",
    "reportUrgentTitle" to "危険な状態のペットを報告",
    "reportUrgentIntro" to "怪我をしている、虐待を受けている、飢えている、または捨てられているなど、すぐに助けが必要なペットを見つけた場合は、場所と状況を教えてください。近隣の緊急レスキュー団体にすぐにお知らせします。",
    "dangerType" to "どのような状況ですか?",
    "dangerInjured" to "怪我をしている",
    "dangerAbused" to "虐待/酷使されている",
    "dangerStarving" to "飢えている",
    "dangerTooYoung" to "一匹では生きられないほど幼い",
    "dangerOther" to "その他の緊急事態",
    "useMyLocation" to "現在地を使用",
    "confirmMyLocation" to "現在地を確認",
    "locationPermissionHint" to "ブラウザが位置情報の共有許可を求めます。位置情報は近くの通報をお知らせするためにのみ使用されます。",
    "phoneOptional" to "電話番号(任意)",
    "submitReport" to "報告を送信",
    "urgentRescuerSettingsTitle" to "緊急レスキュー設定",
    "urgentRescuerSettingsIntro" to "対応エリア内で危険な状態のペットが報告されると通知が届きます。最初に対応を承諾した方がそのケースを担当します。",
    "urgentRescuerActive" to "緊急報告に対応可能です",
    "coverageAreaMode" to "対応エリア",
    "coverageArea" to "対応エリア",
    "coverageAreaHint" to "現在地を確認するか、ピンをドラッグするか、下に住所を入力してください。地図と項目は連動します。",
    "useMyLocationAndRadius" to "現在地と半径を使用",
    "pickAZone" to "代わりに国/都道府県/市区町村を指定",
    "radiusKm" to "半径(km)",
    "urgentRescueAlertsTitle" to "緊急レスキューアラート",
    "urgentRescuerLeaderboardTitle" to "トップ緊急レスキュー団体(直近12ヶ月)",
    "locating" to "位置情報を取得中...",
    "geolocationUnsupported" to "お使いのブラウザは位置情報の取得に対応していません",
    "locationCaptured" to "位置情報を取得しました",
    "locationDenied" to "位置情報を取得できませんでした。位置情報へのアクセスを許可するか、代わりに市区町村を入力してください",
    "street" to "番地/通り名",
    "exteriorNumber" to "建物番号",
    "referenceNotes" to "目印(例:家の色、近くの目印など)",
    "concreteAddressHint" to "できれば番地や通り名を入力してください。保護活動者がタクシーや地図アプリで直接現場に向かえるようになります。",
    "descriptionRequired" to "状況を説明してください",
    "locationRequired" to "位置情報を入力してください(現在地を使用するか、国と市区町村を入力してください)",
    "submittingEllipsis" to "送信中...",
    "reportSubmitted" to "報告を送信しました。ありがとうございます。近隣の保護活動者に今すぐ通知しています。",
    "reportSubmitFailed" to "報告の送信に失敗しました",
    "phoneRequired" to "電話番号を入力してください",
    "savingEllipsis" to "保存中...",
    "saved" to "保存しました",
    "saveFailed" to "保存に失敗しました",
    "noUrgentPages" to "現在保留中の緊急アラートはありません",
    "accept" to "引き受ける",
    "alreadyAccepted" to "この報告はすでに他の方が引き受けています",
    "noLeaderboardData" to "直近12ヶ月に受理された救助はまだありません",
    "rescueAcceptedTitle" to "救助を引き受けました",
    "rescueAcceptedMessage" to "ありがとうございます。対応をお願いします。できるだけ早く報告された場所に向かってください。",
    "rescueAcceptFailedTitle" to "引き受けできませんでした",
    "rescueAcceptFailedMessage" to "この報告はすでに他の保護活動者が引き受けているか、リンクが無効か有効期限切れです。",
    "share" to "共有",
    "shareViaFacebook" to "Facebookで共有",
    "shareViaX" to "Xで共有",
    "shareViaWhatsapp" to "WhatsAppで共有",
    "shareViaEmail" to "メールで共有",
    "copyLink" to "リンクをコピー",
    "linkCopied" to "リンクをコピーしました！",
    "reportLostFoundTitle" to "迷子・保護したペットを報告",
    "reportLostFoundIntro" to "ペットが迷子になった、または野良の動物を保護しましたか?報告を送信すると、近隣の他の報告と照合します。",
    "browseLostFound" to "既存の迷子・保護報告を見る",
    "kindLost" to "ペットが迷子になった",
    "kindFound" to "ペットを保護した",
    "petType" to "ペットの種類",
    "petTypeUnknown" to "わからない",
    "lostFoundBrowseTitle" to "迷子・保護動物情報",
    "showing" to "表示中",
    "kindLostPlural" to "迷子のペット",
    "kindFoundPlural" to "保護されたペット",
    "reportResolvedTitle" to "報告を解決済みにしました",
    "reportResolvedMessage" to "更新ありがとうございます。この報告はクローズされました。",
    "reportResolveFailedTitle" to "解決できませんでした",
    "reportResolveFailedMessage" to "この報告はすでに解決済みか、リンクが無効か有効期限切れです。",
    "reportLostFound" to "迷子/\u200B保護",
    "noLostFoundReportsFound" to "迷子・保護報告が見つかりませんでした",
    "errorLoadingReports" to "報告の読み込み中にエラーが発生しました",
    "yourEmail" to "あなたのメールアドレス",
    "contactReporter" to "報告者に連絡する",
    "contactFormIncomplete" to "メールアドレスとメッセージを入力してください",
    "contactSent" to "メッセージを送信しました",
    "contactFailed" to "メッセージの送信に失敗しました",
    "lostFoundReportSubmitted" to "報告を送信しました。ありがとうございます。近隣の他の報告と照合します。",
    "video" to "動画(任意)",
    "removeVideo" to "動画を削除",
    "saveThisSearch" to "この検索条件を保存",
    "savedSearchSaved" to "検索条件を保存しました!新しく該当するペットが登録されたらメールでお知らせします。",
    "savedSearchFailed" to "検索条件の保存に失敗しました",
    "favorite" to "お気に入り",
    "addToFavorites" to "お気に入りに追加",
    "removeFromFavorites" to "お気に入りから削除",
    "myFavorites" to "マイお気に入り",
    "noFavoritesYet" to "まだお気に入りがありません",
    "mySavedSearches" to "マイ保存検索",
    "noSavedSearchesYet" to "まだ保存された検索条件がありません",
    "donationPromptText" to "本日はペットを助けていただきありがとうございます。よろしければ、Adopt-Uを誰もが無料で利用できるように、少額の寄付をご検討ください。",
    "donationPromptCta" to "寄付する",
    "dismiss" to "閉じる",
)

private val swahiliTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Ingia",
    "register" to "Jisajili",
    "logout" to "Toka",
    "profile" to "Wasifu",
    "myPets" to "Wanyama Wangu",
    "addPet" to "Ongeza Mnyama",
    "save" to "Hifadhi",
    "cancel" to "Ghairi",
    "search" to "Tafuta",
    "shelters" to "Makazi",
    "photographers" to "Wapiga Picha",
    "temporalHomes" to "Makazi ya Muda",
    "findTemporalHomes" to "Tafuta Makazi ya Muda",
    "sterilizationLocations" to "Maeneo ya Kuhasi",
    "donate" to "Changia",
    "countryLabel" to "Nchi",
    "selectCountryToSearch" to "Chagua nchi",
    "selectCountryFirst" to "Chagua nchi ili kuwezesha vichujio hivi",
    "state" to "Jimbo",
    "city" to "Jiji",
    "zipCode" to "Postikodi",
    "neighborhood" to "Mtaa",
    "name" to "Jina",
    "email" to "Barua pepe",
    "phone" to "Simu",
    "address" to "Anwani",
    "description" to "Maelezo",
    "animalShelters" to "Makazi ya Wanyama",
    "sheltersDescription" to "Tafuta makazi ya wanyama ambapo unaweza kupata wanyama wa kuasili au kuwaacha kwa makubaliano ya awali na makazi hayo",
    "photographerDescription" to "Wapiga picha wataalamu wanaotoa vikao vya kupiga picha za wanyama kipenzi",
    "sterilizationLocationsDescription" to "Tafuta maeneo unapoweza kupeleka wanyama kuhasiwa.",
    "privacyPolicy" to "Sera ya Faragha",
    "privacyLastUpdated" to "Ilisasishwa mwisho: Aprili 2025",
    "privacyHighlight" to "Katika Adopt-U, tunaamini katika ukusanyaji mdogo wa data. Tunakusanya tu anwani yako ya barua pepe ili kuwezesha mawasiliano kati ya waasiliaji na waokoaji. Hatutumii taarifa zako kwa matangazo, na hatutashiriki kamwe data yako na wahusika wengine kwa madhumuni ya uuzaji.",
    "privacyInfoCollectedH2" to "Taarifa Tunazokusanya",
    "privacyAccountRegH3" to "Usajili wa Akaunti",
    "privacyAccountRegIntro" to "Ili kuunda akaunti kwenye Adopt-U, tunakusanya taarifa zifuatazo:",
    "privacyEmailLabel" to "Anwani ya barua pepe: ",
    "privacyEmailDesc" to "Inahitajika kwa uthibitishaji wa akaunti, urejeshaji wa nenosiri, na mawasiliano kati ya waasiliaji na waokoaji",
    "privacyDisplayNameLabel" to "Jina la kuonyesha: ",
    "privacyDisplayNameDesc" to "Jina unalochagua kutambulika nalo kwenye jukwaa",
    "privacyLangPrefLabel" to "Upendeleo wa lugha: ",
    "privacyLangPrefDesc" to "Ili kutoa jukwaa kwa lugha unayopendelea",
    "privacyNoFullNameEtc" to "Hatukusanyi jina lako kamili, nambari ya simu, anwani halisi, hati za utambulisho, taarifa za malipo, au data nyingine yoyote ya kibinafsi zaidi ya ile iliyoorodheshwa hapo juu.",
    "privacyOptionalProfileH3" to "Taarifa za Hiari za Wasifu",
    "privacyOptionalProfileIntro" to "Kutegemea nafasi yako, unaweza kutoa kwa hiari:",
    "privacyAdopterLabel" to "Kama Muasiliaji: ",
    "privacyAdopterDesc" to "Hakuna taarifa za ziada zinazohitajika au kukusanywa",
    "privacyRescuerLabel" to "Kama Muokoaji: ",
    "privacyRescuerDesc" to "Taarifa za eneo kwa wanyama unaowapatia kwa ajili ya kuasiliwa",
    "privacyPhotographerLabel" to "Kama Mpiga Picha: ",
    "privacyPhotographerDesc" to "Eneo la huduma na mapendeleo ya mawasiliano",
    "privacyTemporalHomeLabel" to "Kama Makazi ya Muda: ",
    "privacyTemporalHomeDesc" to "Eneo unapoweza kutoa makazi ya muda",
    "privacyLocationUsage" to "Taarifa zote za eneo hutumika tu kuwaunganisha waasiliaji na waokoaji na watoa huduma walio karibu.",
    "privacyPetInfoH3" to "Taarifa za Mnyama",
    "privacyPetInfoIntro" to "Waokoaji wanaweza kuchapisha taarifa kuhusu wanyama, ikiwa ni pamoja na:",
    "privacyPetPhotos" to "Picha za mnyama",
    "privacyPetDescBreedAge" to "Maelezo, aina, umri, na sifa",
    "privacyPetHealth" to "Hali ya afya na chanjo",
    "privacyPetLocation" to "Eneo ambapo mnyama yupo",
    "privacyPetInfoNotVerified" to "Taarifa hii inatolewa na waokoaji na haijathibitishwa kwa kujitegemea na Adopt-U.",
    "privacyHowWeUseH2" to "Jinsi Tunavyotumia Taarifa Zako",
    "privacyPrimaryPurposeH3" to "Lengo Kuu: Kuwezesha Miunganisho ya Kuasili",
    "privacyEmailUsedFor" to "Anwani yako ya barua pepe hutumika pekee kwa madhumuni yafuatayo:",
    "privacyUseVerification" to "Kukutumia kiungo cha uthibitishaji unapounda akaunti",
    "privacyUseRescuerContact" to "Kuwaruhusu waokoaji kukuwasiliana kuhusu wanyama unaowapenda",
    "privacyUseContactRescuer" to "Kukuruhusu kuwasiliana na waokoaji kuhusu wanyama walioorodhesha",
    "privacyUseNotifications" to "Kukutumia arifa muhimu za akaunti na usalama",
    "privacyOnceConnected" to "Mara tu unapounganishwa na mtumiaji mwingine kupitia jukwaa letu, mawasiliano yote yanayofuata hufanyika moja kwa moja kati yako na huyo mtu. Hatufuatilii, kuhifadhi, au kupata maudhui ya mawasiliano yako.",
    "privacyNeverDoH3" to "Kile Ambacho Hatutafanya Kamwe",
    "privacyCommitIntro" to "Tunajitolea kwa dhati kufuata yafuatayo:",
    "privacyNoAdsLabel" to "Hakuna matangazo: ",
    "privacyNoAdsDesc" to "Hatutaonyesha kamwe matangazo yaliyolengwa au yasiyolengwa kulingana na shughuli zako",
    "privacyNoMarketingLabel" to "Hakuna uuzaji: ",
    "privacyNoMarketingDesc" to "Hatutashiriki kamwe barua pepe yako na wahusika wengine kwa madhumuni ya uuzaji au utangazaji",
    "privacyNoSellingLabel" to "Hakuna kuuza data: ",
    "privacyNoSellingDesc" to "Hatutauza, kukodisha, au kuhamisha kwa namna nyingine taarifa zako za kibinafsi kwa mhusika yeyote wa tatu",
    "privacyNoProfilingLabel" to "Hakuna uchambuzi wa wasifu: ",
    "privacyNoProfilingDesc" to "Hatutatumia data yako kujenga wasifu kwa madhumuni yoyote zaidi ya usimamizi wa msingi wa akaunti",
    "privacyNoTrackingLabel" to "Hakuna ufuatiliaji: ",
    "privacyNoTrackingDesc" to "Hatutumii pikseli za ufuatiliaji, vidakuzi vya matangazo, au teknolojia nyingine zozote za ufuatiliaji",
    "privacyDataStorageH2" to "Uhifadhi na Usalama wa Data",
    "privacyDataStoredSecurely" to "Data yako inahifadhiwa kwa usalama kwa kutumia usimbaji fiche wa kiwango cha sekta. Tunatumia hatua zinazofaa za kiufundi na kiutawala kulinda taarifa zako za kibinafsi dhidi ya ufikiaji usioidhinishwa, mabadiliko, ufichuzi, au uharibifu.",
    "privacyDataRetention" to "Tunahifadhi taarifa za akaunti yako kwa muda ambao akaunti yako inabaki hai. Unaweza kuomba ufutaji wa akaunti yako na data inayohusiana wakati wowote kwa kuwasiliana nasi.",
    "privacyYourRightsH2" to "Haki Zako",
    "privacyRightsIntro" to "Una haki zifuatazo kuhusu data yako ya kibinafsi:",
    "privacyRightAccess" to "Upatikanaji: Unaweza kuomba nakala ya data zote za kibinafsi tunazokuwa nazo kukuhusu",
    "privacyRightCorrection" to "Marekebisho: Unaweza kuomba tusahihishe taarifa yoyote isiyo sahihi",
    "privacyRightDeletion" to "Ufutaji: Unaweza kuomba tufute akaunti yako na data zote zinazohusiana",
    "privacyRightPortability" to "Uhamishaji: Unaweza kuomba tukupe data yako katika muundo unaotumika kwa kawaida",
    "privacyExerciseRights" to "Ili kutumia haki yoyote kati ya hizi, tafadhali wasiliana nasi kupitia adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Vidakuzi",
    "privacyCookiesText" to "Adopt-U hutumia vidakuzi vichache muhimu kwa utendaji wa jukwaa, ikiwa ni pamoja na usimamizi wa kipindi na mapendeleo ya lugha. Hatutumii vidakuzi vya matangazo au vidakuzi vya ufuatiliaji.",
    "privacyThirdPartyH2" to "Huduma za Wahusika Wengine",
    "privacyThirdPartyIntro" to "Tunatumia huduma zifuatazo za wahusika wengine, ambazo zinaweza kuchakata data yako:",
    "privacyAws" to "AWS (Amazon Web Services): Kwa uhifadhi wa data na uwasilishaji wa barua pepe. Sera zao za faragha zinatumika kwa uchakataji wao wa data yako.",
    "privacyWebauthn" to "WebAuthn/FIDO2: Kwa uthibitishaji salama wa ufunguo wa siri (passkey). Hii inahusisha shughuli za kriptografia kwenye kivinjari chako.",
    "privacyThirdPartySelected" to "Tumewachagua kwa makini watoa huduma wanaoshiriki dhamira yetu ya faragha ya data.",
    "privacyChildrensH2" to "Faragha ya Watoto",
    "privacyChildrensText" to "Adopt-U haikusudiwi kwa watumiaji walio chini ya umri wa miaka 18. Hatukusanyi kwa makusudi taarifa kutoka kwa watoto wadogo. Ikiwa unaamini mtoto mdogo ameunda akaunti, tafadhali wasiliana nasi ili iondolewe.",
    "privacyChangesH2" to "Mabadiliko kwa Sera Hii",
    "privacyChangesText" to "Tunaweza kusasisha Sera hii ya Faragha mara kwa mara ili kuakisi mabadiliko katika taratibu zetu au mahitaji ya kisheria. Tutawajulisha watumiaji kuhusu mabadiliko makubwa kupitia barua pepe au taarifa dhahiri kwenye jukwaa.",
    "privacyContactH2" to "Wasiliana Nasi",
    "privacyContactText" to "Ikiwa una maswali au wasiwasi wowote kuhusu Sera hii ya Faragha, au kutumia haki zako za data, tafadhali wasiliana nasi kupitia adopt-u@adopt-u.org.",
    "termsConditions" to "Vigezo na Masharti",
    "installApp" to "Sakinisha Programu",
    "termsAcceptanceH2" to "Kukubali Masharti",
    "termsAcceptanceText" to "Kwa kufikia na kutumia Adopt-U, unakubali na kukubaliana kufungwa na masharti na vipengele vya makubaliano haya. Ikiwa hukubaliani na masharti haya, tafadhali usitumie jukwaa letu.",
    "termsNatureH2" to "Asili ya Huduma Yetu",
    "termsNatureText1" to "Adopt-U ni jukwaa la kuunganisha linaloweleza mawasiliano kati ya waokoaji wa wanyama na waasiliaji watarajiwa. Sisi si duka la wanyama, dalali, makazi, au wakala wa kuasili. Hatumiliki, kulinda, au kushughulikia kwa njia ya kati wanyama wowote walioorodheshwa kwenye jukwaa letu.",
    "termsNatureText2" to "Jukumu letu limewekewa mipaka kabisa kwa kutoa njia ya mawasiliano kati ya pande husika. Hatushiriki katika mazungumzo ya kuasili, mikataba, au uhamishaji halisi wa wanyama.",
    "termsUserRespH2" to "Wajibu wa Mtumiaji",
    "termsForRescuersH3" to "Kwa Waokoaji:",
    "termsRescuerResp1" to "Toa taarifa sahihi na za kweli kuhusu wanyama walio chini ya uangalizi wako",
    "termsRescuerResp2" to "Hakikisha wanyama wana afya njema, wamechanjwa, na wamechunguzwa ipasavyo kabla ya kuorodheshwa",
    "termsRescuerResp3" to "Fanya uchunguzi wenye uwajibikaji wa waasiliaji watarajiwa",
    "termsRescuerResp4" to "Panga uhamishaji salama wa wanyama kwenda makazi yao mapya",
    "termsRescuerResp5" to "Kubali wajibu wa usahihi wa taarifa zote zilizotolewa",
    "termsRescuerResp6" to "Zingatia sheria na kanuni zote za mahali kuhusu kuasili wanyama",
    "termsRescuerResp7" to "Usitumie kamwe jukwaa kwa mauzo ya kibiashara ya wanyama au madhumuni ya ufugaji wa kuzalisha",
    "termsForAdoptersH3" to "Kwa Waasiliaji:",
    "termsAdopterResp1" to "Toa taarifa sahihi kuhusu hali yako ya maisha na uzoefu wako na wanyama kipenzi",
    "termsAdopterResp2" to "Kuwa tayari kuonyesha uwezo wako wa kutoa uangalizi ufaao",
    "termsAdopterResp3" to "Elewa kwamba kubatilisha uasiliaji wa mnyama husababisha msongo mkubwa kwa mnyama huyo",
    "termsAdopterResp4" to "Heshimu haki ya muokoaji kukataa ombi lako la kuasili",
    "termsPetListingsH2" to "Orodha na Taarifa za Wanyama",
    "termsPetListingsText1" to "Adopt-U haithibitishi, kuidhinisha, au kuhakikisha usahihi wa taarifa zilizotolewa katika orodha za wanyama. Taarifa ikiwa ni pamoja na lakini si tu aina, umri, hali ya afya, tabia, na rekodi za chanjo hutolewa na waokoaji na hazijathibitishwa kwa kujitegemea.",
    "termsPetListingsText2" to "Picha na maelezo hutolewa na waokoaji. Ingawa tunakataza upotoshaji wa makusudi, hatuwezi kuhakikisha kwamba orodha zote zinawakilisha kwa usahihi hali ya sasa ya mnyama.",
    "termsPetListingsText3" to "Watumiaji wanahimizwa kuuliza maswali, kuomba rekodi za daktari wa mifugo, na kukutana na wanyama kabla ya kukamilisha mpango wowote wa kuasili.",
    "termsAdoptionProcessH2" to "Mchakato wa Kuasili",
    "termsAdoptionProcessIntro" to "Mara muokoaji na muasiliaji wanapokubaliana kuendelea na uasiliaji:",
    "termsAdoptionStep1" to "Mipango yote, ikiwa ni pamoja na tarehe ya uhamishaji, eneo, na gharama zozote zinazohusiana, hujadiliwa moja kwa moja kati ya pande husika",
    "termsAdoptionStep2" to "Adopt-U haiwezeshi wala kuhakikisha miamala yoyote ya malipo",
    "termsAdoptionStep3" to "Jukwaa halitoi mikataba ya kuasili; mipango yoyote ya kimkataba ni wajibu pekee wa pande zinazohusika",
    "termsAdoptionStep4" to "Tunapendekeza sana kuandika makubaliano yote kwa maandishi",
    "termsLiabilityH2" to "Kikomo cha Dhima",
    "termsLiabilityIntro" to "Adopt-U haitawajibika kwa:",
    "termsLiability1" to "Mizozo yoyote, kutokubaliana, au migogoro kati ya waokoaji na waasiliaji",
    "termsLiability2" to "Usahihi wa taarifa zilizotolewa katika orodha za wanyama",
    "termsLiability3" to "Afya, tabia, au hali ya mnyama yeyote kabla, wakati, au baada ya kuasiliwa",
    "termsLiability4" to "Hasara zozote za kifedha, uharibifu, au majeraha yanayotokana na matumizi ya jukwaa",
    "termsLiability5" to "Matokeo ya uasiliaji wowote, ikiwa ni pamoja na lakini si tu marejesho, matatizo ya afya, au matatizo ya upatanifu",
    "termsLiability6" to "Vitendo vyovyote vinavyofanywa na watumiaji nje ya jukwaa baada ya mawasiliano kuanzishwa",
    "termsLiabilityAck" to "Watumiaji wanakiri kwamba kuasili mnyama ni dhamira kubwa na kwamba wanachukua wajibu kamili wa uamuzi wao wa kuasili.",
    "termsProhibitedH2" to "Shughuli Zilizokatazwa",
    "termsProhibitedIntro" to "Shughuli zifuatazo zimekatazwa vikali kwenye Adopt-U:",
    "termsProhibited1" to "Mauzo ya kibiashara ya wanyama, shughuli za ufugaji wa kuzalisha, au viwanda vya kuzalisha mbwa",
    "termsProhibited2" to "Kuorodhesha wanyama usiowaangalia moja kwa moja",
    "termsProhibited3" to "Taarifa za uongo au za kupotosha katika orodha au wasifu",
    "termsProhibited4" to "Unyanyasaji, ubaguzi, au mawasiliano yasiyofaa na watumiaji wengine",
    "termsProhibited5" to "Kutumia jukwaa kukusanya taarifa za kibinafsi kwa madhumuni yasiyohusiana na kuasili wanyama",
    "termsProhibited6" to "Shughuli zozote haramu au ukiukaji wa sheria za ustawi wa wanyama",
    "termsAccountTerminationH2" to "Kusitisha Akaunti",
    "termsAccountTerminationText" to "Adopt-U inahifadhi haki ya kusimamisha au kusitisha akaunti zinazokiuka masharti haya, kushiriki katika shughuli zilizokatazwa, au kuharibu sifa ya jukwaa. Tunaweza pia kuondoa orodha zenye maudhui ya uongo, ya kupotosha, au yasiyofaa.",
    "termsModificationsH2" to "Mabadiliko ya Masharti",
    "termsModificationsText" to "Tunahifadhi haki ya kubadilisha masharti haya wakati wowote. Kuendelea kutumia jukwaa baada ya mabadiliko kunamaanisha kukubali masharti yaliyobadilishwa. Tutawajulisha watumiaji kuhusu mabadiliko makubwa kupitia barua pepe au matangazo ya jukwaa.",
    "termsContactText" to "Ikiwa una maswali yoyote kuhusu Vigezo na Masharti haya, tafadhali wasiliana nasi kupitia adopt-u@adopt-u.org.",
    "language" to "Lugha",
    "displayName" to "Jina la Kuonyesha",
    "saveProfile" to "Hifadhi Wasifu",
    "accountSettings" to "Mipangilio ya Akaunti",
    "yourRoles" to "Majukumu Yako:",
    "resources" to "Rasilimali",
    "loginWithPasskey" to "Ingia kwa Passkey",
    "signInWithPasskey" to "Ingia kwa Passkey",
    "resendVerificationEmail" to "Tuma Tena Barua Pepe ya Uthibitishaji",
    "usesFido" to "Inatumia FIDO2 / WebAuthn kwa uthibitishaji salama bila nenosiri.",
    "loginWithMagicLink" to "Ingia kwa Kiungo cha Barua Pepe",
    "magicLinkDesc" to "Tutakutumia kiungo cha kuingia kwenye barua pepe yako (kinasalia halali kwa dakika 5).",
    "sendMagicLink" to "Tuma Kiungo cha Kuingia",
    "loginWithPassword" to "Ingia kwa Nenosiri",
    "signIn" to "Ingia",
    "forgotPassword" to "Umesahau Nenosiri?",
    "registerNewAccount" to "Fungua Akaunti",
    "accountDetails" to "Maelezo ya Akaunti",
    "registrationMethod" to "Njia ya Usajili",
    "selectAdditionalRoles" to "Chagua majukumu ya ziada:",
    "adoptPet" to "Asili mnyama",
    "publishPets" to "Chapisha wanyama kwa ajili ya kuasiliwa",
    "offerPhotography" to "Toa huduma za upigaji picha",
    "photographerSettings" to "Mipangilio ya Mpiga Picha",
    "sessionFee" to "Ada ya Kikao",
    "provideTemporaryHome" to "Toa makazi ya muda kwa wanyama",
    "passkeyNoPassword" to "Vifunguo vya siri (passkeys) ni salama zaidi kuliko manenosiri na hufanya kazi katika vifaa vyako vyote.",
    "registerWithPasskey" to "Jisajili kwa Passkey",
    "passkeySettings" to "Mipangilio ya Passkey",
    "password" to "Nenosiri",
    "confirmPassword" to "Thibitisha Nenosiri",
    "passwordRequirements" to "Nenosiri lazima liwe na angalau herufi 8 na liwe na herufi kubwa, herufi ndogo, namba, na alama",
    "securityInfo" to "Taarifa za Usalama",
    "passkeyMostSecure" to "Passkey: Salama zaidi, inapendekezwa",
    "passkeyBrowserCompat" to "Passkey huenda isifanye kazi kwenye vivinjari vyote",
    "passwordLeastSecure" to "Nenosiri: Salama kidogo, halipendekezwi",
    "selectLoginMethods" to "Chagua njia moja au zaidi za kuingia:",
    "registerPasskeyOption" to "Passkey (salama zaidi, hufanya kazi kwenye vifaa vyako vyote)",
    "registerPasswordOption" to "Nenosiri (salama kidogo, tumia kama hifadhi mbadala tu)",
    "howPasskeysWorkTitle" to "Passkey inafanyaje kazi?",
    "howPasskeysWorkIntro" to "Passkey inachukua nafasi ya nenosiri lako kwa usalama uliojengwa ndani ya kifaa chako — alama ya kidole, utambuzi wa uso, au PIN ya kufunga skrini.",
    "howPasskeysWorkSecurity" to "Inahifadhiwa tu kwenye kifaa chako, haiwezi kuibiwa kwa udanganyifu au kuvuja katika uvunjaji wa data, na inasawazishwa kiotomatiki kwenye vifaa vyako.",
    "howPasskeysWorkBackup" to "Unaweza pia kuongeza nenosiri, lakini passkey pekee inatosha kuingia — hakuna nenosiri linalohitajika.",
    "gotIt" to "Nimeelewa",
    "alreadyHaveAccount" to "Tayari una akaunti?",
    "all" to "Zote",
    "filterDogs" to "Mbwa",
    "filterCats" to "Paka",
    "filterBirds" to "Ndege",
    "filterFish" to "Samaki",
    "filterAllSexOption" to "Jinsia Zote",
    "filterMaleOption" to "♂ Dume",
    "filterFemaleOption" to "♀ Jike",
    "age" to "Umri",
    "rescued" to "Ameokolewa",
    "years" to "miaka",
    "months" to "miezi",
    "year" to "mwaka",
    "month" to "mwezi",
    "loginToAdopt" to "Ingia ili kuomba kuasili",
    "requiredMarker" to "(inahitajika)",
    "pageNotFoundTitle" to "Ukurasa haupatikani",
    "pageNotFoundHint" to "Kiungo kinaweza kuwa cha zamani au kimeandikwa vibaya. Wanyama bado wapo hapa.",
    "browsePets" to "Tazama wanyama",
    "requestsFromRescuers" to "Maombi kutoka kwa waokoaji",
    "signingOut" to "Inatoka...",
    "adopter" to "Muasiliaji",
    "country" to "Nchi",
    "zip" to "Msimbo wa posta",
    "viewPublic" to "Tazama ukurasa wa umma",
    "banUser" to "Piga marufuku mtumiaji",
    "banReason" to "Sababu (hiari)",
    "myPhotoRequests" to "Maombi yangu ya kipindi cha picha",
    "noPhotoRequestsYet" to "Bado hujaomba kipindi cha picha.",
    "photoRequestsReceived" to "Maombi ya kipindi cha picha yaliyopokelewa",
    "noPhotoRequestsReceived" to "Hakuna maombi ya kipindi cha picha bado.",
    "photoStatusPending" to "Inasubiri",
    "photoStatusApproved" to "Imekubaliwa",
    "photoStatusRejected" to "Imekataliwa",
    "photoStatusCancelled" to "Imeghairiwa",
    "photoStatusCompleted" to "Imekamilika",
    "cancelRequest" to "Ghairi ombi",
    "registerConsentPrefix" to "Kwa kuunda akaunti unakubali",
    "registerConsentTerms" to "Vigezo na Masharti",
    "registerConsentAnd" to "na",
    "registerConsentPrivacy" to "Sera ya Faragha",
    "petStatusAvailable" to "Yupo",
    "petStatusAdopted" to "Ameasiliwa",
    "petStatusDisabled" to "Amezimwa",
    "petStatusPending" to "Inasubiri",
    "addEditLocation" to "Ongeza/Hariri Eneo",
    "addLocation" to "Ongeza Eneo",
    "manage" to "Simamia",
    "adminPanel" to "Dashibodi ya Msimamizi",
    "adminSterilizationLocations" to "Simamia Maeneo ya Kuhasi",
    "adopterRequired" to "Muasiliaji (inahitajika)",
    "adoptionFee" to "Ada ya Kuasili",
    "adoptionRequests" to "Maombi ya Kuasili",
    "ageMonths" to "Umri (miezi)",
    "ageYears" to "Umri (miaka)",
    "alias" to "Jina la utani",
    "bird" to "Ndege",
    "breed" to "Aina",
    "medicalEventsOverview" to "Muhtasari wa Matukio ya Kiafya",
    "back" to "Rudi",
    "cat" to "Paka",
    "color" to "Rangi",
    "compatibility" to "Upatanifu",
    "currentPassword" to "Nenosiri la Sasa",
    "dog" to "Mbwa",
    "emailChange" to "Badilisha Barua Pepe",
    "emailChangeDesc" to "Sasisha anwani yako ya barua pepe. Kiungo cha uthibitishaji kitatumwa kwenye barua pepe mpya.",
    "energyLevel" to "Kiwango cha Nguvu",
    "female" to "Jike",
    "findTemporalHome" to "Tafuta Makazi ya Muda",
    "fish" to "Samaki",
    "goodWithCats" to "Anapatana vizuri na paka",
    "goodWithDogs" to "Anapatana vizuri na mbwa",
    "goodWithKids" to "Anapatana vizuri na watoto",
    "high" to "Juu",
    "houseTrained" to "Amefunzwa kutumia choo nyumbani",
    "large" to "Kubwa",
    "low" to "Chini",
    "male" to "Dume",
    "managePets" to "Simamia Wanyama",
    "managePetsBtn" to "Ongeza au Hariri Wanyama",
    "managePetsDescription" to "Simamia kurasa zote za wanyama. Ongeza au ondoa wanyama.",
    "manageShelters" to "Simamia Makazi",
    "manageSheltersBtn" to "Ongeza au Hariri Makazi",
    "manageSheltersDescription" to "Simamia kurasa zote za makazi. Ongeza au ondoa makazi.",
    "manageUsers" to "Simamia Watumiaji",
    "medical" to "Matibabu",
    "medium" to "Wastani",
    "backToSearch" to "Rudi kwenye Utafutaji",
    "contactThisHome" to "Wasiliana na Makazi Haya ya Muda",
    "loginAsRescuerToContact" to "Ingia kama muokoaji ili kutuma ombi kwa makazi haya ya muda.",
    "memberSince" to "Mwanachama tangu",
    "microchipId" to "Nambari ya Chip",
    "microchipped" to "Amewekwa Chip",
    "myTemporalHome" to "Makazi Yangu ya Muda",
    "newEmail" to "Barua Pepe Mpya",
    "noTemporalHomes" to "Hakuna makazi ya muda yaliyopatikana.",
    "requestSentToHome" to "Ombi lako limetumwa!",
    "sendRequestBtn" to "Tuma Ombi",
    "temporalHomeNotFound" to "Makazi haya ya muda hayakupatikana.",
    "viewDetails" to "Angalia Maelezo",
    "rescuerDirectory" to "Waokoaji",
    "noRescuersFound" to "Hakuna waokoaji waliopatikana.",
    "availablePetsCount" to "Wanyama waliopo",
    "availablePets" to "Wanyama Waliopo",
    "noAvailablePetsForRescuer" to "Muokoaji huyu hana wanyama waliopo kwa sasa.",
    "rescuerNotFound" to "Muokoaji huyu hakupatikana.",
    "errorLoadingResults" to "Hitilafu katika kupakia matokeo.",
    "volunteerForThisRescuer" to "Jitolee kwa Muokoaji Huyu",
    "volunteerExplanation" to "Wajitolea walioidhinishwa wanaweza kupendekeza mabadiliko kwenye orodha za wanyama za muokoaji huyu.",
    "applyToVolunteerBtn" to "Omba Kujitolea",
    "loginToVolunteer" to "Ingia ili kuomba kujitolea kwa muokoaji huyu.",
    "volunteerApplicationSent" to "Ombi lako la kujitolea limetumwa!",
    "myVolunteering" to "Kujitolea Kwangu",
    "noVolunteerApplicationsYet" to "Bado hujaomba kujitolea kwa muokoaji yeyote.",
    "howToVolunteerHint" to "Ili kuomba, fungua ukurasa wa mwokoaji kutoka kwenye orodha kisha bonyeza \"Omba Kujitolea\".",
    "volunteerStatusPending" to "Inasubiri",
    "volunteerStatusActive" to "Hai",
    "volunteerStatusRejected" to "Imekataliwa",
    "volunteerApplications" to "Maombi ya Kujitolea",
    "noVolunteerApplications" to "Hakuna maombi ya kujitolea.",
    "petEditSuggestions" to "Mapendekezo ya Kuhariri Mnyama",
    "noPetEditSuggestions" to "Hakuna mapendekezo ya kuhariri yanayosubiri.",
    "suggestedByLabel" to "imependekezwa na",
    "suggestAnEditTitle" to "Pendekeza Marekebisho",
    "suggestAnEditExplanation" to "Kama mjitolea aliyeidhinishwa, unaweza kupendekeza mabadiliko kwenye orodha hii. Muokoaji atakagua kabla ya kuchapishwa.",
    "suggestEditBtn" to "Tuma Pendekezo",
    "editSuggestionSent" to "Pendekezo lako limetumwa kwa muokoaji kwa ajili ya kukaguliwa!",
    "sponsorThisPetTitle" to "Dhamini Mnyama Huyu",
    "sponsorExplanation" to "Huwezi kuasili lakini unataka kusaidia gharama? Toa msaada wa fedha au vitu na uwasiliane moja kwa moja na muokoaji.",
    "sponsorThisRescuerTitle" to "Dhamini Muokoaji Huyu",
    "sponsorRescuerExplanation" to "Saidia gharama za jumla za muokoaji huyu kwa fedha au msaada wa vitu.",
    "sponsorTypeLabel" to "Unaweza kutoa nini?",
    "sponsorTypeMoney" to "Fedha",
    "sponsorTypeInKind" to "Vitu (chakula, vifaa, n.k.)",
    "amountLabel" to "Kiasi (kila mwezi)",
    "currencyLabel" to "Sarafu",
    "inKindDescriptionLabel" to "Eleza unachoweza kutoa",
    "messageLabel" to "Ujumbe",
    "sendOfferBtn" to "Tuma Ofa",
    "sponsorOfferSent" to "Ofa yako imetumwa! Muokoaji atawasiliana nawe kupanga maelezo.",
    "sponsorshipOffersReceived" to "Ofa za Udhamini",
    "noSponsorshipOffers" to "Hakuna ofa za udhamini bado.",
    "generalFundLabel" to "Mfuko wa jumla",
    "offeringLabel" to "Anachotoa",
    "sponsorshipStatusSent" to "Mpya",
    "sponsorshipStatusRead" to "Imesomwa",
    "markAsReadBtn" to "Weka Alama Imesomwa",
    "mySponsorshipOffers" to "Ofa Zangu za Udhamini",
    "noSponsorshipOffersYet" to "Bado hujatuma ofa yoyote ya udhamini.",
    "howToSponsorHint" to "Ili kudhamini, fungua ukurasa wa mwokoaji (msaada wa jumla) au ukurasa wa mnyama (gharama za mnyama huyo) kisha tuma ofa.",
    "browsePetsLink" to "Tazama wanyama",
    "yourMessage" to "Ujumbe wako",
    "newPassword" to "Nenosiri Jipya",
    "passkeyDesc" to "Ongeza vifunguo vya siri vya ziada kwenye akaunti yako kwa kuingia kwa urahisi katika vifaa mbalimbali.",
    "passkeyName" to "Jina la Passkey",
    "pers" to "Watu",
    "petFoodDescription" to "Jifunze ni vyakula gani salama, hatari, au sumu kwa wanyama wako kipenzi",
    "petFoodGuide" to "Mwongozo wa Chakula cha Wanyama",
    "foodInformation" to "Taarifa za Chakula",
    "petsForAdoption" to "Wanyama wa Kuasili",
    "chooseCountryPrompt" to "Tafuta rafiki yako mpya bora",
    "chooseCountryPromptHint" to "Chagua nchi hapo juu ili kuvinjari wanyama waliopo kwa ajili ya kuasiliwa karibu nawe.",
    "noPetsFound" to "Hakuna wanyama waliopatikana",
    "noPetsFoundHint" to "Jaribu kichujio kingine au rudi hivi karibuni — wanyama wapya wanaongezwa mara kwa mara.",
    "loadingMorePets" to "Inapakia wanyama zaidi…",
    "photos" to "Picha (upeo 12)",
    "profilePhotographerRole" to "Mpiga Picha - Toa huduma za upigaji picha",
    "profileRescuerRole" to "Muokoaji - Chapisha wanyama kwa kuasiliwa",
    "profileTemporalHomeRole" to "Makazi ya Muda - Toa makazi ya muda kwa wanyama",
    "provideShelter" to "Makazi - Simamia makazi ya wanyama",
    "provideSterilization" to "Huduma ya Kuhasi - Toa huduma za kuhasi",
    "requestEmailChange" to "Omba Kubadilisha Barua Pepe",
    "rescueDate" to "Tarehe ya Kuokolewa",
    "rescueLocation" to "Eneo la Kuokolewa",
    "savePassword" to "Hifadhi Nenosiri",
    "selectEnergy" to "Chagua nguvu",
    "selectSize" to "Chagua ukubwa",
    "sex" to "Jinsia",
    "shelterSettings" to "Mipangilio ya Makazi",
    "size" to "Ukubwa",
    "small" to "Ndogo",
    "specialNeeds" to "Mahitaji Maalum",
    "sterilizationSettings" to "Mipangilio ya Kuhasi",
    "sterilized" to "Amehasiwa",
    "temporalHomeSettings" to "Mipangilio ya Makazi ya Muda",
    "temperament" to "Tabia",
    "type" to "Aina *",
    "urgentAdoption" to "Uasiliaji wa haraka unahitajika",
    "vaccinations" to "Chanjo",
    "vetDisclaimer" to "Wasiliana na daktari wako wa mifugo kabla ya kufanya mabadiliko ya lishe.",
    "weight" to "Uzito (kg)",
    "weightLabel" to "Uzito",
    "website" to "Tovuti",
    "securitySettings" to "Mipangilio ya Usalama",
    "emailVerifiedTitle" to "Barua Pepe Imethibitishwa Kikamilifu!",
    "emailVerifiedMessage" to "Barua pepe yako imethibitishwa. Usajili wako sasa umekamilika.",
    "redirectingIn" to "Unaelekezwa kwenye ukurasa mkuu baada ya ",
    "canAlsoLogin" to "Unaweza pia ",
    "loginNow" to "Ingia Sasa",
    "emailVerificationFailed" to "Uthibitishaji Umeshindwa",
    "invalidOrExpiredToken" to "Kiungo cha uthibitishaji si sahihi au kimeisha muda wake.",
    "tryRegisteringAgain" to "Tafadhali jaribu kujisajili tena.",
    "verificationEmailResent" to "Barua pepe ya uthibitishaji ilikuwa imeisha muda wake. Barua pepe mpya ya uthibitishaji imetumwa. Tafadhali angalia sanduku lako la barua.",
    "emailVerificationSent" to "Barua pepe ya uthibitishaji imetumwa. Tafadhali angalia sanduku lako la barua kabla ya kuingia.",
    "emailNotVerifiedLogin" to "Tafadhali thibitisha barua pepe yako kabla ya kuingia. Angalia sanduku lako la barua kwa kiungo cha uthibitishaji.",
    "invalidOrExpiredMagicLink" to "Kiungo cha uchawi si sahihi au kimeisha muda wake. Tafadhali omba kingine kipya.",
    "invalidToken" to "Tokeni si sahihi.",
    "accountBanned" to "Akaunti yako imepigwa marufuku.",
    "loginError" to "Hitilafu ya kuingia imetokea.",
    "profileUpdated" to "Wasifu umesasishwa!",
    "displayNameRequired" to "Jina la kuonyesha haliwezi kuwa tupu",
    "saving" to "Inahifadhi...",
    "passwordSet" to "Nenosiri limewekwa",
    "noPassword" to "Hakuna nenosiri lililowekwa",
    "passwordChangeSuccess" to "Nenosiri limebadilishwa kwa mafanikio!",
    "passwordChangeFailed" to "Imeshindwa kubadilisha nenosiri",
    "passwordSetSuccess" to "Nenosiri limewekwa kwa mafanikio!",
    "passwordSetFailed" to "Imeshindwa kuweka nenosiri",
    "passwordsDoNotMatch" to "Manenosiri hayafanani",
    "currentPasswordRequired" to "Nenosiri la sasa linahitajika",
    "emailChangeSent" to "Barua pepe ya uthibitishaji imetumwa kwenye anwani yako mpya!",
    "emailChangeFailed" to "Imeshindwa kuomba kubadilisha barua pepe",
    "emailRequired" to "Barua pepe inahitajika",
    "sendingEllipsis" to "Inatuma...",
    "resetLinkSent" to "Kiungo cha kuweka upya nenosiri kimetumwa! Angalia barua pepe yako.",
    "magicLinkSent" to "Kiungo cha kuingia kimetumwa! Angalia barua pepe yako.",
    "magicLinkSendFailed" to "Imeshindwa kutuma kiungo cha kuingia",
    "failedSendResetLink" to "Imeshindwa kutuma kiungo cha kuweka upya.",
    "invalidOrMissingToken" to "Tokeni si sahihi au haipo.",
    "passwordTooShort" to "Nenosiri lazima liwe na angalau herufi 8.",
    "passwordNeedUppercase" to "Nenosiri linahitaji angalau herufi kubwa 1.",
    "passwordNeedLowercase" to "Nenosiri linahitaji angalau herufi ndogo 1.",
    "passwordNeedNumber" to "Nenosiri linahitaji angalau namba 1.",
    "passwordNeedSymbol" to "Nenosiri linahitaji angalau alama 1.",
    "resettingPassword" to "Inaweka upya nenosiri...",
    "passwordResetSuccess" to "Nenosiri limewekwa upya kwa mafanikio! Sasa unaweza kuingia.",
    "failedResetPassword" to "Imeshindwa kuweka upya nenosiri.",
    "loginSuccessRedirecting" to "Kuingia kumefanikiwa! Unaelekezwa...",
    "loginFailedExpiredLink" to "Kuingia kumeshindikana. Kiungo huenda si sahihi au kimeisha muda wake.",
    "loginFailed" to "Kuingia kumeshindikana.",
    "emailChangedSuccess" to "Barua pepe imebadilishwa kwa mafanikio!",
    "failedChangeEmailExpired" to "Imeshindwa kubadilisha barua pepe. Kiungo huenda si sahihi au kimeisha muda wake.",
    "failedChangeEmail" to "Imeshindwa kubadilisha barua pepe.",
    "emailVerifiedSuccess" to "Barua pepe imethibitishwa kwa mafanikio!",
    "failedVerifyEmailExpired" to "Imeshindwa kuthibitisha barua pepe. Kiungo huenda si sahihi au kimeisha muda wake.",
    "failedVerifyEmail" to "Imeshindwa kuthibitisha barua pepe.",
    "dropImagesHint" to "Dondosha picha hapa au bofya kuvinjari",
    "forgotPasswordTitle" to "Umesahau Nenosiri",
    "forgotPasswordIntro" to "Weka barua pepe yako na tutakutumia kiungo cha kuweka upya nenosiri lako.",
    "sendResetLink" to "Tuma Kiungo cha Kuweka Upya",
    "backToLogin" to "Rudi kwenye Kuingia",
    "resetPasswordTitle" to "Weka Upya Nenosiri",
    "resetPasswordIntro" to "Weka nenosiri lako jipya hapa chini.",
    "emailLinkLoginTitle" to "Kuingia kwa Kiungo cha Barua Pepe",
    "verifying" to "Inathibitisha...",
    "emailChangeTitle" to "Kubadilisha Barua Pepe",
    "verifyContactEmailTitle" to "Thibitisha Barua Pepe ya Mawasiliano",
    "status" to "Hali",
    "yes" to "Ndiyo",
    "no" to "Hapana",
    "noDescription" to "Hakuna maelezo.",
    "urgentBadge" to "HARAKA - Anahitaji makazi hivi karibuni!",
    "needsNewHome" to "Mnyama huyu anahitaji makazi mapya kwa haraka",
    "promotedReasonLabel" to "Sababu",
    "selectReason" to "Chagua sababu",
    "promotedReasonMoving" to "Mmiliki anahamia",
    "promotedReasonComplaints" to "Malalamiko / hawezi kuweka wanyama zaidi",
    "promotedReasonPetConflict" to "Mgogoro na mnyama mwingine",
    "promotedReasonOther" to "Nyingine",
    "promotedReasonDetailLabel" to "Maelezo zaidi (si lazima)",
    "promotedReasonRequired" to "Tafadhali chagua sababu",
    "needsNewHomeBadge" to "Anahitaji makazi mapya",
    "housingType" to "Aina ya makazi",
    "preferNotToSay" to "Napendelea kutosema",
    "houseHousing" to "Nyumba",
    "apartmentHousing" to "Ghorofa",
    "hasYard" to "Ana ua",
    "hasOtherPets" to "Wanyama wengine nyumbani",
    "adoptionExperience" to "Uzoefu wa kuasili",
    "firstTimeAdopter" to "Muasiliaji wa mara ya kwanza",
    "experiencedAdopter" to "Muasiliaji mwenye uzoefu",
    "noAdoptionRequests" to "Hakuna maombi ya kuasili",
    "noMessage" to "Hakuna ujumbe",
    "markUnderReview" to "Weka Alama Inakaguliwa",
    "approve" to "Idhinisha",
    "reject" to "Kataa",
    "reviewNoteLabel" to "Ujumbe wa faragha wa ukaguzi",
    "saveNote" to "Hifadhi Ujumbe",
    "adoptionStatusPending" to "Inasubiri",
    "adoptionStatusUnderReview" to "Inakaguliwa",
    "adoptionStatusApproved" to "Imeidhinishwa",
    "adoptionStatusRejected" to "Haijaidhinishwa",
    "myAdoptionRequests" to "Maombi Yangu ya Kuasili",
    "noAdoptionRequestsYet" to "Bado hujaomba kuasili mnyama yeyote.",
    "medicalHistory" to "Historia ya Afya",
    "medicalSchedule" to "Ratiba ya Chanjo na Udhibiti wa Minyoo",
    "recordType" to "Aina",
    "vaccination" to "Chanjo",
    "deworming" to "Udhibiti wa Minyoo",
    "recordName" to "Jina",
    "dateGiven" to "Tarehe ilipopewa",
    "nextDueDate" to "Tarehe inayofuata (si lazima)",
    "notesOptional" to "Maelezo (si lazima)",
    "addRecord" to "Ongeza Rekodi",
    "noMedicalRecords" to "Bado hakuna rekodi za chanjo au udhibiti wa minyoo.",
    "nextDueLabel" to "Inayofuata",
    "givenLabel" to "Ilipewa",
    "overdue" to "Imechelewa",
    "dueSoon" to "Inakaribia",
    "upToDate" to "Iko sawa",
    "recordNameRequired" to "Jina linahitajika",
    "dateGivenRequired" to "Tarehe ilipopewa inahitajika",
    "failedToAddRecord" to "Imeshindwa kuongeza rekodi",
    "confirmDeleteRecord" to "Futa rekodi hii?",
    "maxCapacity" to "Idadi ya juu ya wanyama kwa wakati mmoja (si lazima)",
    "placePetHere" to "Weka Mnyama Hapa",
    "noAvailablePetsToPlace" to "Huna wanyama waliopo wa kuweka kwa sasa.",
    "selectPet" to "Chagua mnyama",
    "startPlacementBtn" to "Anza Uwekaji",
    "placementStarted" to "Uwekaji wa muda umeanza!",
    "petsCurrentlyWithMe" to "Wanyama Walioko Nami Sasa",
    "noPetsCurrentlyFostered" to "Hakuna wanyama walioko chini ya uangalizi wako kwa sasa.",
    "sinceLabel" to "tangu",
    "fosterPlacement" to "Uwekaji wa Muda",
    "endPlacement" to "Maliza Uwekaji",
    "currentlyWithLabel" to "Sasa yuko na",
    "notCurrentlyFostered" to "Hayupo chini ya uangalizi wa muda kwa sasa.",
    "confirmEndPlacement" to "Maliza uwekaji huu wa muda? Mnyama ataandikwa kuwa amerudi kwako.",
    "aTemporalHome" to "makazi ya muda",
    "viewsLabel" to "Mionekano",
    "inquiriesLabel" to "Maswali",
    "conversionLabel" to "Ubadilishaji",
    "messageOptional" to "Ujumbe (si lazima)",
    "requestAdoption" to "Omba Kuasili",
    "editPet" to "Hariri Mnyama",
    "adoptionRequestSubmitted" to "Ombi la kuasili limewasilishwa!",
    "failedSubmitRequest" to "Imeshindwa kuwasilisha ombi",
    "noPhotosYet" to "Hakuna picha bado.",
    "rabbit" to "Sungura",
    "safeFoods" to "Vyakula Salama",
    "harmfulFoods" to "Vyakula Hatari",
    "cannotEatFoods" to "Havili Kuliwa",
    "legendSafe" to "Salama",
    "legendHarmful" to "Hatari",
    "legendToxic" to "Sumu",
    "food_dog_safe_chicken_name" to "Kuku",
    "food_dog_safe_chicken_detail" to "Kilichopikwa, bila mifupa",
    "food_dog_safe_chicken_desc" to "Protini nyembamba, rahisi kusaga",
    "food_dog_safe_rice_name" to "Mchele",
    "food_dog_safe_rice_detail" to "Mweupe au wa kahawia",
    "food_dog_safe_rice_desc" to "Chanzo kizuri cha wanga",
    "food_dog_safe_carrots_name" to "Karoti",
    "food_dog_safe_carrots_detail" to "Mbichi au iliyopikwa",
    "food_dog_safe_carrots_desc" to "Kalori chache, nyuzinyuzi nyingi",
    "food_dog_safe_apples_name" to "Tofaa",
    "food_dog_safe_apples_detail" to "Bila mbegu",
    "food_dog_safe_apples_desc" to "Yenye vitamini nyingi",
    "food_dog_safe_peanutbutter_name" to "Siagi ya Karanga",
    "food_dog_safe_peanutbutter_detail" to "Bila chumvi, bila xylitol",
    "food_dog_safe_peanutbutter_desc" to "Chanzo kizuri cha protini",
    "food_dog_harmful_grapes_name" to "Zabibu",
    "food_dog_harmful_grapes_detail" to "Aina zote",
    "food_dog_harmful_grapes_desc" to "Zinaweza kusababisha kushindwa kwa figo",
    "food_dog_harmful_onions_name" to "Vitunguu",
    "food_dog_harmful_onions_detail" to "Aina zote",
    "food_dog_harmful_onions_desc" to "Huharibu chembe nyekundu za damu",
    "food_dog_harmful_chocolate_name" to "Chokoleti",
    "food_dog_harmful_chocolate_detail" to "Aina zote",
    "food_dog_harmful_chocolate_desc" to "Ni sumu kwa mbwa",
    "food_dog_harmful_garlic_name" to "Kitunguu Saumu",
    "food_dog_harmful_garlic_detail" to "Aina zote",
    "food_dog_harmful_garlic_desc" to "Husababisha upungufu wa damu",
    "food_dog_cannot_xylitol_name" to "Xylitol",
    "food_dog_cannot_xylitol_detail" to "Kitamu bandia",
    "food_dog_cannot_xylitol_desc" to "Sumu kali",
    "food_dog_cannot_macadamia_name" to "Njugu za Macadamia",
    "food_dog_cannot_macadamia_detail" to "Aina zote",
    "food_dog_cannot_macadamia_desc" to "Husababisha udhaifu",
    "food_dog_cannot_avocado_name" to "Parachichi",
    "food_dog_cannot_avocado_detail" to "Kokwa, ganda, vyote",
    "food_dog_cannot_avocado_desc" to "Ina persin",
    "food_dog_cannot_alcohol_name" to "Pombe",
    "food_dog_cannot_alcohol_detail" to "Aina zote",
    "food_dog_cannot_alcohol_desc" to "Sumu kali",
    "food_cat_safe_cookedfish_name" to "Samaki Aliyepikwa",
    "food_cat_safe_cookedfish_detail" to "Bila mifupa",
    "food_cat_safe_cookedfish_desc" to "Protini nyingi",
    "food_cat_safe_chicken_name" to "Kuku",
    "food_cat_safe_chicken_detail" to "Aliyepikwa, wa kawaida",
    "food_cat_safe_chicken_desc" to "Chanzo kizuri cha protini",
    "food_cat_safe_pumpkin_name" to "Boga",
    "food_cat_safe_pumpkin_detail" to "Wa kawaida, alizopikwa",
    "food_cat_safe_pumpkin_desc" to "Husaidia usagaji chakula",
    "food_cat_safe_eggs_name" to "Mayai",
    "food_cat_safe_eggs_detail" to "Yaliyopikwa",
    "food_cat_safe_eggs_desc" to "Protini kamili",
    "food_cat_harmful_raweggs_name" to "Mayai Mabichi",
    "food_cat_harmful_raweggs_detail" to "Yenye avidin",
    "food_cat_harmful_raweggs_desc" to "Huathiri biotin",
    "food_cat_harmful_rawfish_name" to "Samaki Mbichi",
    "food_cat_harmful_rawfish_detail" to "Ina thiaminase",
    "food_cat_harmful_rawfish_desc" to "Huvunja vitamini B",
    "food_cat_harmful_dogfood_name" to "Chakula cha Mbwa",
    "food_cat_harmful_dogfood_detail" to "Chochote",
    "food_cat_harmful_dogfood_desc" to "Hakina taurine",
    "food_cat_harmful_milk_name" to "Maziwa",
    "food_cat_harmful_milk_detail" to "Paka wengi",
    "food_cat_harmful_milk_desc" to "Kutoyavumilia lactose",
    "food_cat_cannot_chocolate_name" to "Chokoleti",
    "food_cat_cannot_chocolate_detail" to "Aina zote",
    "food_cat_cannot_chocolate_desc" to "Sumu ya theobromine",
    "food_cat_cannot_onionsgarlic_name" to "Vitunguu/Kitunguu Saumu",
    "food_cat_cannot_onionsgarlic_detail" to "Aina zote",
    "food_cat_cannot_onionsgarlic_desc" to "Huharibu chembe nyekundu za damu",
    "food_cat_cannot_grapesraisins_name" to "Zabibu/Zabibu Kavu",
    "food_cat_cannot_grapesraisins_detail" to "Aina zote",
    "food_cat_cannot_grapesraisins_desc" to "Uharibifu wa figo",
    "food_cat_cannot_alcohol_name" to "Pombe",
    "food_cat_cannot_alcohol_detail" to "Aina zote",
    "food_cat_cannot_alcohol_desc" to "Sumu sana",
    "food_bird_safe_seeds_name" to "Mbegu",
    "food_bird_safe_seeds_detail" to "Mchanganyiko wa aina",
    "food_bird_safe_seeds_desc" to "Chanzo kizuri cha mafuta",
    "food_bird_safe_fruits_name" to "Matunda",
    "food_bird_safe_fruits_detail" to "Aina nyingi",
    "food_bird_safe_fruits_desc" to "Vitamini na madini",
    "food_bird_safe_vegetables_name" to "Mboga",
    "food_bird_safe_vegetables_detail" to "Majani ya kijani",
    "food_bird_safe_vegetables_desc" to "Virutubisho muhimu",
    "food_bird_safe_pellets_name" to "Chembechembe za Chakula",
    "food_bird_safe_pellets_detail" to "Zilizotengenezwa kwa ubora",
    "food_bird_safe_pellets_desc" to "Lishe kamili",
    "food_bird_harmful_avocado_name" to "Parachichi",
    "food_bird_harmful_avocado_detail" to "Sehemu zote",
    "food_bird_harmful_avocado_desc" to "Sumu ya persin",
    "food_bird_harmful_fruitpits_name" to "Kokwa za Matunda",
    "food_bird_harmful_fruitpits_detail" to "Tofaa, pichi",
    "food_bird_harmful_fruitpits_desc" to "Alama za sumu ya cyanide",
    "food_bird_harmful_salt_name" to "Chumvi",
    "food_bird_harmful_salt_detail" to "Aina yoyote",
    "food_bird_harmful_salt_desc" to "Sumu kwa ndege",
    "food_bird_harmful_caffeine_name" to "Kafeini",
    "food_bird_harmful_caffeine_detail" to "Kahawa, chai",
    "food_bird_harmful_caffeine_desc" to "Matatizo ya moyo",
    "food_bird_cannot_chocolate_name" to "Chokoleti",
    "food_bird_cannot_chocolate_detail" to "Aina zote",
    "food_bird_cannot_chocolate_desc" to "Sumu ya theobromine",
    "food_bird_cannot_onions_name" to "Vitunguu",
    "food_bird_cannot_onions_detail" to "Aina zote",
    "food_bird_cannot_onions_desc" to "Huharibu chembe nyekundu za damu",
    "food_bird_cannot_garlic_name" to "Kitunguu Saumu",
    "food_bird_cannot_garlic_detail" to "Aina zote",
    "food_bird_cannot_garlic_desc" to "Sumu",
    "food_bird_cannot_mushrooms_name" to "Uyoga",
    "food_bird_cannot_mushrooms_detail" to "Aina za porini",
    "food_bird_cannot_mushrooms_desc" to "Sumu zinazowezekana",
    "food_fish_safe_flakes_name" to "Vipande Vidogo",
    "food_fish_safe_flakes_detail" to "Chapa bora",
    "food_fish_safe_flakes_desc" to "Chakula kamili cha samaki",
    "food_fish_safe_pellets_name" to "Chembechembe za Chakula",
    "food_fish_safe_pellets_detail" to "Zinazoelea/zinazozama",
    "food_fish_safe_pellets_desc" to "Lishe iliyosawazishwa",
    "food_fish_safe_frozenfood_name" to "Chakula Kilichogandishwa",
    "food_fish_safe_frozenfood_detail" to "Minyoo ya damu, brine shrimp",
    "food_fish_safe_frozenfood_desc" to "Chanzo cha protini",
    "food_fish_safe_vegetables_name" to "Mboga",
    "food_fish_safe_vegetables_detail" to "Zucchini iliyochemshwa kidogo",
    "food_fish_safe_vegetables_desc" to "Chanzo cha nyuzinyuzi",
    "food_fish_harmful_bread_name" to "Mkate",
    "food_fish_harmful_bread_detail" to "Aina yoyote",
    "food_fish_harmful_bread_desc" to "Hauna lishe, hujaza tumbo",
    "food_fish_harmful_humanfood_name" to "Chakula cha Binadamu",
    "food_fish_harmful_humanfood_detail" to "Milo iliyopikwa",
    "food_fish_harmful_humanfood_desc" to "Lishe isiyofaa",
    "food_fish_harmful_livefeed_name" to "Chakula Hai",
    "food_fish_harmful_livefeed_detail" to "Wadudu waliokamatwa porini",
    "food_fish_harmful_livefeed_desc" to "Vimelea vinavyowezekana",
    "food_fish_cannot_landinsect_name" to "Wadudu wa Nchi Kavu",
    "food_fish_cannot_landinsect_detail" to "Wadudu watambaao",
    "food_fish_cannot_landinsect_desc" to "Si chakula cha asili",
    "food_fish_cannot_mammalmeat_name" to "Nyama ya Mamalia",
    "food_fish_cannot_mammalmeat_detail" to "Kuku, nyama ya ng'ombe",
    "food_fish_cannot_mammalmeat_desc" to "Haifai kwa samaki wengi",
    "food_fish_cannot_dairy_name" to "Maziwa na Mazao Yake",
    "food_fish_cannot_dairy_detail" to "Chochote",
    "food_fish_cannot_dairy_desc" to "Hawawezi kusaga",
    "food_fish_cannot_breadcrumbs_name" to "Unga wa Mkate",
    "food_fish_cannot_breadcrumbs_detail" to "Nyingi zina kitunguu saumu",
    "food_fish_cannot_breadcrumbs_desc" to "Viongezeo hatari",
    "food_rabbit_safe_hay_name" to "Nyasi Kavu",
    "food_rabbit_safe_hay_detail" to "Nyasi ya Timothy",
    "food_rabbit_safe_hay_desc" to "80% ya lishe",
    "food_rabbit_safe_leafygreens_name" to "Majani ya Kijani",
    "food_rabbit_safe_leafygreens_detail" to "Romaine, dania",
    "food_rabbit_safe_leafygreens_desc" to "Vitamini vya kila siku",
    "food_rabbit_safe_pellets_name" to "Chembechembe za Chakula",
    "food_rabbit_safe_pellets_detail" to "Zenye msingi wa Timothy",
    "food_rabbit_safe_pellets_desc" to "Lishe iliyosawazishwa",
    "food_rabbit_safe_carrots_name" to "Karoti",
    "food_rabbit_safe_carrots_detail" to "Kwa kiasi",
    "food_rabbit_safe_carrots_desc" to "Zawadi ndogo",
    "food_rabbit_harmful_icebergletttuce_name" to "Letusi ya Iceberg",
    "food_rabbit_harmful_icebergletttuce_detail" to "Haina lishe",
    "food_rabbit_harmful_icebergletttuce_desc" to "Inaweza kusababisha kusimama kwa mmeng'enyo",
    "food_rabbit_harmful_beans_name" to "Maharage",
    "food_rabbit_harmful_beans_detail" to "Mikunde yote",
    "food_rabbit_harmful_beans_desc" to "Gesi, matatizo ya mmeng'enyo",
    "food_rabbit_harmful_corn_name" to "Mahindi",
    "food_rabbit_harmful_corn_detail" to "Aina yoyote",
    "food_rabbit_harmful_corn_desc" to "Hawawezi kusaga",
    "food_rabbit_harmful_potatoes_name" to "Viazi",
    "food_rabbit_harmful_potatoes_detail" to "Aina yoyote",
    "food_rabbit_harmful_potatoes_desc" to "Virutubisho visivyofaa",
    "food_rabbit_cannot_chocolate_name" to "Chokoleti",
    "food_rabbit_cannot_chocolate_detail" to "Aina zote",
    "food_rabbit_cannot_chocolate_desc" to "Sumu ya theobromine",
    "food_rabbit_cannot_onions_name" to "Vitunguu",
    "food_rabbit_cannot_onions_detail" to "Aina zote",
    "food_rabbit_cannot_onions_desc" to "Sumu sana",
    "food_rabbit_cannot_garlic_name" to "Kitunguu Saumu",
    "food_rabbit_cannot_garlic_detail" to "Aina zote",
    "food_rabbit_cannot_garlic_desc" to "Sumu",
    "food_rabbit_cannot_avocado_name" to "Parachichi",
    "food_rabbit_cannot_avocado_detail" to "Sehemu zote",
    "food_rabbit_cannot_avocado_desc" to "Ina persin",
    "primary" to "Msingi",
    "passkeyRegisteredSuccess" to "Passkey imesajiliwa kwa mafanikio!",
    "passkeyRegistrationFailed" to "Usajili wa Passkey umeshindikana",
    "passkeyNameRequired" to "Jina la passkey linahitajika",
    "registeringPasskey" to "Inasajili passkey...",
    "passkeyRegistered" to "Passkey imesajiliwa",
    "noPasskey" to "Hakuna passkey iliyosajiliwa",
    "noPets" to "Bado hakuna wanyama.",
    "photographerCountryStateRequired" to "Tafadhali jaza nchi na jimbo kwa huduma za upigaji picha",
    "temporalHomeAliasCountryCityRequired" to "Tafadhali jaza jina la utani, nchi na jiji kwa makazi ya muda",
    "shelterNameCountryCityAddressRequired" to "Tafadhali jaza jina, nchi, jiji na anwani kwa makazi",
    "sterilizationNameCountryCityAddressRequired" to "Tafadhali jaza jina, nchi, jiji na anwani kwa eneo la kuhasi",
    "country.afghanistan" to "Afghanistan",
    "country.albania" to "Albania",
    "country.algeria" to "Algeria",
    "country.argentina" to "Argentina",
    "country.armenia" to "Armenia",
    "country.australia" to "Australia",
    "country.austria" to "Austria",
    "country.azerbaijan" to "Azerbaijan",
    "country.bangladesh" to "Bangladesh",
    "country.belarus" to "Belarusi",
    "country.belgium" to "Ubelgiji",
    "country.bolivia" to "Bolivia",
    "country.bosnia" to "Bosnia na Hezegovina",
    "country.brazil" to "Brazili",
    "country.bulgaria" to "Bulgaria",
    "country.cambodia" to "Kambodia",
    "country.cameroon" to "Kameruni",
    "country.canada" to "Kanada",
    "country.chile" to "Chile",
    "country.china" to "Uchina",
    "country.colombia" to "Kolombia",
    "country.costaRica" to "Kostarika",
    "country.croatia" to "Kroeshia",
    "country.cuba" to "Kuba",
    "country.czechia" to "Jamhuri ya Cheki",
    "country.denmark" to "Denmaki",
    "country.dominicanRepublic" to "Jamhuri ya Dominika",
    "country.ecuador" to "Ekuado",
    "country.egypt" to "Misri",
    "country.elSalvador" to "El Salvador",
    "country.estonia" to "Estonia",
    "country.ethiopia" to "Ethiopia",
    "country.finland" to "Ufini",
    "country.france" to "Ufaransa",
    "country.georgia" to "Jojia",
    "country.germany" to "Ujerumani",
    "country.ghana" to "Ghana",
    "country.greece" to "Ugiriki",
    "country.guatemala" to "Guatemala",
    "country.haiti" to "Haiti",
    "country.honduras" to "Hondurasi",
    "country.hungary" to "Hungaria",
    "country.iceland" to "Aislandi",
    "country.india" to "India",
    "country.indonesia" to "Indonesia",
    "country.iran" to "Iran",
    "country.iraq" to "Iraki",
    "country.ireland" to "Ayalandi",
    "country.israel" to "Israeli",
    "country.italy" to "Italia",
    "country.jamaica" to "Jamaika",
    "country.japan" to "Japani",
    "country.jordan" to "Yordani",
    "country.kazakhstan" to "Kazakistani",
    "country.kenya" to "Kenya",
    "country.kuwait" to "Kuwaiti",
    "country.latvia" to "Lativia",
    "country.lebanon" to "Lebanoni",
    "country.libya" to "Libya",
    "country.lithuania" to "Lithuania",
    "country.luxembourg" to "Luxembourg",
    "country.malaysia" to "Malaysia",
    "country.mexico" to "Meksiko",
    "country.moldova" to "Moldova",
    "country.mongolia" to "Mongolia",
    "country.montenegro" to "Montenegro",
    "country.morocco" to "Moroko",
    "country.myanmar" to "Myanmar",
    "country.nepal" to "Nepal",
    "country.netherlands" to "Uholanzi",
    "country.newZealand" to "New Zealand",
    "country.nicaragua" to "Nikaragua",
    "country.nigeria" to "Nijeria",
    "country.northKorea" to "Korea Kaskazini",
    "country.norway" to "Norwe",
    "country.pakistan" to "Pakistani",
    "country.panama" to "Panama",
    "country.paraguay" to "Paragwai",
    "country.peru" to "Peru",
    "country.philippines" to "Ufilipino",
    "country.poland" to "Polandi",
    "country.portugal" to "Ureno",
    "country.puertoRico" to "Puerto Rico",
    "country.qatar" to "Katari",
    "country.romania" to "Romania",
    "country.russia" to "Urusi",
    "country.saudiArabia" to "Saudi Arabia",
    "country.serbia" to "Serbia",
    "country.singapore" to "Singapoo",
    "country.slovakia" to "Slovakia",
    "country.slovenia" to "Slovenia",
    "country.southAfrica" to "Afrika Kusini",
    "country.southKorea" to "Korea Kusini",
    "country.spain" to "Hispania",
    "country.sriLanka" to "Sri Lanka",
    "country.sudan" to "Sudan",
    "country.sweden" to "Uswidi",
    "country.switzerland" to "Uswisi",
    "country.syria" to "Siria",
    "country.taiwan" to "Taiwan",
    "country.thailand" to "Tailandi",
    "country.tunisia" to "Tunisia",
    "country.turkey" to "Uturuki",
    "country.ukraine" to "Ukraini",
    "country.uae" to "Falme za Kiarabu",
    "country.uk" to "Uingereza",
    "country.usa" to "Marekani",
    "country.uruguay" to "Urugwai",
    "country.uzbekistan" to "Uzbekistan",
    "country.venezuela" to "Venezuela",
    "country.vietnam" to "Vietnamu",
    "country.yemen" to "Yemeni",
    "loading" to "Inapakia...",
    "countryRequired" to "Tafadhali chagua nchi kutafuta",
    "noSheltersFound" to "Hakuna makazi yaliyopatikana bado. Rudi tena baadaye!",
    "visitWebsite" to "Tembelea Tovuti",
    "fiscalId" to "Nambari ya Kodi",
    "donationInformation" to "Taarifa za Uchangiaji",
    "bankName" to "Benki",
    "accountHolder" to "Mmiliki wa Akaunti",
    "accountNumber" to "Nambari ya Akaunti",
    "currency" to "Sarafu",
    "errorLoadingShelters" to "Hitilafu katika kupakia makazi",
    "pleaseSelectCountry" to "Tafadhali chagua nchi",
    "actions" to "Vitendo",
    "addShelter" to "Ongeza Makazi",
    "addEditShelter" to "Ongeza / Hariri Makazi",
    "allStates" to "Majimbo Yote",
    "confirmDelete" to "Una uhakika unataka kufuta eneo hili?",
    "confirmDeleteShelter" to "Una uhakika unataka kufuta makazi haya?",
    "confirmUnbanUser" to "Ondoa marufuku kwa mtumiaji huyu?",
    "confirmResetUserCredentials" to "Hii itabatilisha nenosiri na vifunguo vya siri vya sasa vya {email} na kuwatumia kiungo cha kuweka upya kwa barua pepe. Endelea?",
    "confirmDeactivateUser" to "Zima akaunti ya mtumiaji huyu? Wanaweza kuwezeshwa tena baadaye.",
    "confirmDeactivatePet" to "Zima mnyama huyu? Ataondolewa kwenye orodha za umma na anaweza kuwezeshwa tena baadaye.",
    "confirmBlockRescuer" to "Zuia muokoaji huyu kukutumia maombi zaidi?",
    "confirmDeletePet" to "Futa mnyama huyu?",
    "confirmDeleteStorage" to "Futa hifadhi hii?",
    "contact" to "Wasiliana",
    "delete" to "Futa",
    "edit" to "Hariri",
    "errorDeletingShelter" to "Hitilafu katika kufuta makazi",
    "errorLoadingLocations" to "Hitilafu katika kupakia maeneo",
    "errorLoadingShelter" to "Hitilafu katika kupakia makazi",
    "errorSaving" to "Hitilafu katika kuhifadhi",
    "errorSavingShelter" to "Hitilafu katika kuhifadhi makazi",
    "location" to "Eneo",
    "noLocationsFound" to "Hakuna maeneo yaliyopatikana",
    "selectCountry" to "Chagua nchi",
    "selectCountryToFilter" to "Tafadhali chagua nchi ili kuchuja",
    "shelterAdded" to "Makazi yameongezwa kwa mafanikio",
    "shelterDeleted" to "Makazi yamefutwa kwa mafanikio",
    "shelterUpdated" to "Makazi yamesasishwa kwa mafanikio",
    "updateShelter" to "Sasisha Makazi",
    "errorLoadingPhotographers" to "Hitilafu katika kupakia wapiga picha",
    "noPhotographersAvailable" to "Hakuna wapiga picha waliopo",
    "requestPhotoSession" to "Omba Kikao cha Kupiga Picha",
    "selectPhotographer" to "Chagua",
    "photographersSelected" to "{count} wamechaguliwa",
    "requestSelectedPhotographers" to "Omba wapiga picha waliochaguliwa",
    "clearSelection" to "Futa uteuzi",
    "requestToMultiple" to "Omba kwa",
    "messageRequired" to "Tafadhali andika ujumbe kwa wapiga picha.",
    "requestsSentSuccessfully" to "Maombi yametumwa kwa mafanikio",
    "message" to "Ujumbe",
    "sendRequest" to "Tuma Ombi",
    "requestSentSuccessfully" to "Ombi limetumwa kwa mafanikio!",
    "requestTo" to "Ombi kwa",
    "enterMessage" to "Weka ujumbe kwa {name}...",
    "reportUrgent" to "Dharura",
    "topRescuers" to "Waokoaji Bora",
    "urgentRescuerSettings" to "Mipangilio ya Muokoaji wa Dharura",
    "urgentRescueAlerts" to "Arifa za Uokoaji wa Dharura",
    "reportUrgentTitle" to "Ripoti Mnyama Aliye Hatarini",
    "reportUrgentIntro" to "Ikiwa umempata mnyama aliyeumia, anayenyanyaswa, mwenye njaa, au aliyeachwa anayehitaji msaada wa haraka, tuambie yuko wapi na tatizo ni nini - tutawajulisha mara moja waokoaji wa dharura walio karibu.",
    "dangerType" to "Tatizo ni nini?",
    "dangerInjured" to "Ameumia",
    "dangerAbused" to "Ananyanyaswa / Anatendewa vibaya",
    "dangerStarving" to "Ana njaa",
    "dangerTooYoung" to "Ni mdogo mno kuishi peke yake",
    "dangerOther" to "Dharura nyingine",
    "useMyLocation" to "Tumia eneo langu la sasa",
    "confirmMyLocation" to "Thibitisha eneo langu la sasa",
    "locationPermissionHint" to "Kivinjari chako kitaomba ruhusa ya kushiriki eneo lako; linatumika tu kukuarifu kuhusu ripoti zilizo karibu nawe.",
    "phoneOptional" to "Simu yako (si lazima)",
    "submitReport" to "Wasilisha Ripoti",
    "urgentRescuerSettingsTitle" to "Mipangilio ya Muokoaji wa Dharura",
    "urgentRescuerSettingsIntro" to "Pokea taarifa mnyama aliye hatarini anaporipotiwa karibu na eneo lako la huduma. Wa kwanza kukubali anachukua kesi hiyo.",
    "urgentRescuerActive" to "Nipo tayari kujibu ripoti za dharura",
    "coverageAreaMode" to "Eneo la huduma",
    "coverageArea" to "Eneo la huduma",
    "coverageAreaHint" to "Thibitisha eneo lako la sasa, buruta pini, au andika anwani hapa chini - ramani na sehemu zitasalia sawa.",
    "useMyLocationAndRadius" to "Tumia eneo langu + eneo la mzunguko",
    "pickAZone" to "Chagua nchi/jimbo/jiji badala yake",
    "radiusKm" to "Eneo la mzunguko (km)",
    "urgentRescueAlertsTitle" to "Arifa za Uokoaji wa Dharura",
    "urgentRescuerLeaderboardTitle" to "Waokoaji Bora wa Dharura (miezi 12 iliyopita)",
    "locating" to "Inatafuta eneo...",
    "geolocationUnsupported" to "Utambuzi wa eneo hauhudumiwi na kivinjari chako",
    "locationCaptured" to "Eneo limepatikana",
    "locationDenied" to "Imeshindwa kupata eneo lako - tafadhali ruhusu ufikiaji wa eneo au weka jiji badala yake",
    "street" to "Barabara",
    "exteriorNumber" to "Nambari ya nyumba",
    "referenceNotes" to "Maelezo ya rejea (mfano rangi ya nyumba, alama karibu)",
    "concreteAddressHint" to "Ongeza barabara na nambari ikiwezekana - hii ndiyo humruhusu muokoaji kupanda teksi au kufungua programu ya ramani moja kwa moja hadi mahali husika.",
    "descriptionRequired" to "Tafadhali eleza hali hiyo",
    "locationRequired" to "Tafadhali toa eneo (tumia eneo lako la sasa au weka nchi na jiji)",
    "submittingEllipsis" to "Inawasilisha...",
    "reportSubmitted" to "Ripoti imewasilishwa. Asante - waokoaji walio karibu wanajulishwa sasa.",
    "reportSubmitFailed" to "Imeshindwa kuwasilisha ripoti",
    "phoneRequired" to "Tafadhali weka nambari ya simu",
    "savingEllipsis" to "Inahifadhi...",
    "saved" to "Imehifadhiwa",
    "saveFailed" to "Imeshindwa kuhifadhi",
    "noUrgentPages" to "Hakuna arifa za dharura zinazosubiri kwa sasa",
    "accept" to "Kubali",
    "alreadyAccepted" to "Mtu mwingine tayari amekubali ripoti hii",
    "noLeaderboardData" to "Hakuna uokoaji ulioidhinishwa katika miezi 12 iliyopita",
    "rescueAcceptedTitle" to "Uokoaji Umekubaliwa",
    "rescueAcceptedMessage" to "Asante - unashughulikia. Elekea eneo lililoripotiwa haraka uwezavyo.",
    "rescueAcceptFailedTitle" to "Imeshindwa Kukubali",
    "rescueAcceptFailedMessage" to "Ripoti hii tayari imekubaliwa na muokoaji mwingine, au kiungo si sahihi au kimeisha muda wake.",
    "share" to "Shiriki",
    "shareViaFacebook" to "Shiriki kwenye Facebook",
    "shareViaX" to "Shiriki kwenye X",
    "shareViaWhatsapp" to "Shiriki kwenye WhatsApp",
    "shareViaEmail" to "Shiriki kwa Barua pepe",
    "copyLink" to "Nakili Kiungo",
    "linkCopied" to "Kiungo kimenakiliwa!",
    "reportLostFoundTitle" to "Ripoti Mnyama Aliyepotea au Aliyepatikana",
    "reportLostFoundIntro" to "Umempoteza mnyama wako, au umempata mnyama aliyepotea? Wasilisha ripoti na tutailinganisha na ripoti nyingine za karibu.",
    "browseLostFound" to "Vinjari ripoti zilizopo za waliopotea na waliopatikana",
    "kindLost" to "Nimepoteza mnyama wangu",
    "kindFound" to "Nimempata mnyama",
    "petType" to "Aina ya mnyama",
    "petTypeUnknown" to "Sijui hakika",
    "lostFoundBrowseTitle" to "Wanyama Waliopotea na Waliopatikana",
    "showing" to "Inaonyesha",
    "kindLostPlural" to "Wanyama waliopotea",
    "kindFoundPlural" to "Wanyama waliopatikana",
    "reportResolvedTitle" to "Ripoti Imetatuliwa",
    "reportResolvedMessage" to "Asante kwa taarifa - ripoti hii sasa imefungwa.",
    "reportResolveFailedTitle" to "Imeshindwa Kutatua",
    "reportResolveFailedMessage" to "Ripoti hii tayari imetatuliwa, au kiungo si sahihi au kimeisha muda wake.",
    "reportLostFound" to "Waliopotea/\u200BWaliopatikana",
    "noLostFoundReportsFound" to "Hakuna ripoti za waliopotea au waliopatikana zilizopatikana",
    "errorLoadingReports" to "Hitilafu katika kupakia ripoti",
    "yourEmail" to "Barua pepe yako",
    "contactReporter" to "Wasiliana na Aliyeripoti",
    "contactFormIncomplete" to "Tafadhali weka barua pepe yako na ujumbe",
    "contactSent" to "Ujumbe umetumwa",
    "contactFailed" to "Imeshindwa kutuma ujumbe",
    "lostFoundReportSubmitted" to "Ripoti imewasilishwa. Asante - tutailinganisha na ripoti nyingine za karibu.",
    "video" to "Video (si lazima)",
    "removeVideo" to "Ondoa video",
    "saveThisSearch" to "Hifadhi Utafutaji Huu",
    "savedSearchSaved" to "Utafutaji umehifadhiwa! Tutakutumia barua pepe kunapoorodheshwa mnyama anayelingana.",
    "savedSearchFailed" to "Imeshindwa kuhifadhi utafutaji",
    "favorite" to "Kipendwa",
    "addToFavorites" to "Ongeza kwenye Vipendwa",
    "removeFromFavorites" to "Ondoa kwenye Vipendwa",
    "myFavorites" to "Vipendwa Vyangu",
    "noFavoritesYet" to "Bado hakuna vipendwa",
    "mySavedSearches" to "Utafutaji Wangu Uliohifadhiwa",
    "noSavedSearchesYet" to "Bado hakuna utafutaji uliohifadhiwa",
    "donationPromptText" to "Asante kwa kumsaidia mnyama leo. Ikiwezekana, fikiria kutoa mchango mdogo ili Adopt-U iendelee kuwa bure kwa kila mtu.",
    "donationPromptCta" to "Changia",
    "dismiss" to "Funga",
)

private val hausaTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Shiga",
    "register" to "Yi rijista",
    "logout" to "Rufe zama",
    "profile" to "Bayanan sirri",
    "myPets" to "Dabbobina",
    "addPet" to "Ƙara Dabba",
    "save" to "Ajiye",
    "cancel" to "Soke",
    "search" to "Bincike",
    "shelters" to "Matsugunai",
    "photographers" to "Masu Daukar Hoto",
    "temporalHomes" to "Gidajen Wucin Gadi",
    "findTemporalHomes" to "Nemo Gidajen Wucin Gadi",
    "sterilizationLocations" to "Wuraren Bakarar Dabbobi",
    "donate" to "Ba da Gudummawa",
    "countryLabel" to "Kasa",
    "selectCountryToSearch" to "Zaɓi kasa",
    "selectCountryFirst" to "Zaɓi kasa domin kunna wadannan tacewa",
    "state" to "Jiha",
    "city" to "Birni",
    "zipCode" to "Lambar Gidan Waya",
    "neighborhood" to "Unguwa",
    "name" to "Suna",
    "email" to "Imel",
    "phone" to "Waya",
    "address" to "Adireshi",
    "description" to "Bayani",
    "animalShelters" to "Matsugunan Dabbobi",
    "sheltersDescription" to "Nemo matsugunan dabbobi inda za ka sami dabbobi don karba ko ka bar su bisa yarjejeniya da matsugunin",
    "photographerDescription" to "Kwararrun masu daukar hoto suna bayar da hidimar daukar hoton dabbobi",
    "sterilizationLocationsDescription" to "Nemo wuraren da za ka kai dabbobi domin a yi musu bakarar.",
    "privacyPolicy" to "Manufar Sirri",
    "privacyLastUpdated" to "An sabunta na karshe: Afrilu 2025",
    "privacyHighlight" to "A Adopt-U, mun yi imani da tattara bayanai kadan. Muna tattara adireshin imel dinka kawai domin ba da damar sadarwa tsakanin masu karbar dabbobi da masu ceto su. Ba ma amfani da bayananka domin talla, kuma ba za mu taba raba bayananka da wasu kamfanoni domin dalilan talla ba.",
    "privacyInfoCollectedH2" to "Bayanan da Muke Tattarawa",
    "privacyAccountRegH3" to "Rijistar Asusu",
    "privacyAccountRegIntro" to "Domin kirkirar asusu a Adopt-U, muna tattara bayanan da ke gaba:",
    "privacyEmailLabel" to "Adireshin imel: ",
    "privacyEmailDesc" to "Ana bukatarsa domin tabbatar da asusu, dawo da kalmar sirri, da sadarwa tsakanin masu karbar dabbobi da masu ceto su",
    "privacyDisplayNameLabel" to "Sunan da za a nuna: ",
    "privacyDisplayNameDesc" to "Sunan da ka zaɓa domin a gane ka da shi a dandalin",
    "privacyLangPrefLabel" to "Zaɓin harshe: ",
    "privacyLangPrefDesc" to "Domin bayar da dandalin a harshen da ka fi so",
    "privacyNoFullNameEtc" to "Ba ma tattara cikakken sunanka, lambar wayarka, adireshin gidanka, takardun shaida, bayanan biya, ko wani bayani na sirri fiye da abin da aka lissafa a sama.",
    "privacyOptionalProfileH3" to "Bayanan Bayanan Sirri na Zaɓi",
    "privacyOptionalProfileIntro" to "Dangane da matsayinka, za ka iya bayarwa idan ka ga dama:",
    "privacyAdopterLabel" to "A matsayin Mai Karba: ",
    "privacyAdopterDesc" to "Ba a bukatar ko tattara wani karin bayani",
    "privacyRescuerLabel" to "A matsayin Mai Ceto: ",
    "privacyRescuerDesc" to "Bayanin wuri na dabbobin da kake da su domin karba",
    "privacyPhotographerLabel" to "A matsayin Mai Daukar Hoto: ",
    "privacyPhotographerDesc" to "Wurin hidima da zaɓin tuntuɓa",
    "privacyTemporalHomeLabel" to "A matsayin Gida na Wucin Gadi: ",
    "privacyTemporalHomeDesc" to "Wurin da za ka iya bayar da matsuguni na wucin gadi",
    "privacyLocationUsage" to "Ana amfani da duk bayanan wuri kawai domin hada masu karbar dabbobi da masu ceto ko masu bayar da hidima kusa da su.",
    "privacyPetInfoH3" to "Bayanin Dabba",
    "privacyPetInfoIntro" to "Masu ceto dabbobi na iya buga bayani game da dabbobi, wanda ya hada da:",
    "privacyPetPhotos" to "Hotunan dabbar",
    "privacyPetDescBreedAge" to "Bayani, nau'i, shekaru, da halaye",
    "privacyPetHealth" to "Lafiya da matsayin allurar rigakafi",
    "privacyPetLocation" to "Wurin da dabbar take samuwa",
    "privacyPetInfoNotVerified" to "Wannan bayanin masu ceto dabbobi ne suka bayar kuma Adopt-U ba ta tabbatar da shi ba daban.",
    "privacyHowWeUseH2" to "Yadda Muke Amfani da Bayaninka",
    "privacyPrimaryPurposeH3" to "Babban Manufa: Sauƙaƙe Hulɗar Karba",
    "privacyEmailUsedFor" to "Ana amfani da adireshin imel dinka kawai domin dalilan da ke gaba:",
    "privacyUseVerification" to "Aika maka hanyar tabbatarwa lokacin da ka kirkiri asusu",
    "privacyUseRescuerContact" to "Bayar da damar masu ceto su tuntuɓe ka game da dabbobin da kake sha'awa",
    "privacyUseContactRescuer" to "Bayar da damar ka tuntuɓi masu ceto game da dabbobin da suka jera",
    "privacyUseNotifications" to "Aika maka sanarwar asusu da tsaro masu muhimmanci",
    "privacyOnceConnected" to "Da zarar ka hadu da wani mai amfani ta hanyar dandalinmu, duk wata sadarwa mai zuwa ana gudanar da ita kai tsaye tsakaninka da wancan bangare. Ba mu sa ido, ajiye, ko samun damar shiga cikin abin da ke cikin sadarwarku ba.",
    "privacyNeverDoH3" to "Abin da Ba Za Mu Taba Yi Ba",
    "privacyCommitIntro" to "Mun dauki alkawari na gaskiya kan abin da ke gaba:",
    "privacyNoAdsLabel" to "Babu talla: ",
    "privacyNoAdsDesc" to "Ba za mu taba nuna tallace-tallace na musamman ko na gaba ɗaya bisa aikinka ba",
    "privacyNoMarketingLabel" to "Babu tallace-tallace: ",
    "privacyNoMarketingDesc" to "Ba za mu taba raba imel dinka da wasu kamfanoni domin dalilan tallace-tallace ba",
    "privacyNoSellingLabel" to "Babu sayar da bayanai: ",
    "privacyNoSellingDesc" to "Ba za mu taba sayarwa, hayar, ko mika bayaninka na sirri ga wani kamfani ba",
    "privacyNoProfilingLabel" to "Babu tsara bayanan mutum: ",
    "privacyNoProfilingDesc" to "Ba za mu yi amfani da bayaninka domin gina bayanan mutum domin wani dalili fiye da gudanar da asusu na yau da kullum ba",
    "privacyNoTrackingLabel" to "Babu bibiya: ",
    "privacyNoTrackingDesc" to "Ba ma amfani da alamun bibiya, cookies domin talla, ko wata dabarar bibiya makamanciyar haka",
    "privacyDataStorageH2" to "Ajiye Bayanai da Tsaro",
    "privacyDataStoredSecurely" to "Ana ajiye bayaninka lafiya ta amfani da tsarin sirri na zamani. Muna amfani da matakan fasaha da na gudanarwa masu dacewa domin kare bayaninka na sirri daga shiga ba tare da izini ba, gyarawa, bayyanawa, ko lalacewa.",
    "privacyDataRetention" to "Muna ajiye bayanin asusunka muddin asusunka na aiki. Za ka iya neman a share asusunka da bayanan da suka hade da shi a kowane lokaci ta hanyar tuntuɓarmu.",
    "privacyYourRightsH2" to "Hakkinka",
    "privacyRightsIntro" to "Kana da hakkoki masu zuwa game da bayaninka na sirri:",
    "privacyRightAccess" to "Shiga: Za ka iya neman kwafin duk bayanan sirri da muke da su game da kai",
    "privacyRightCorrection" to "Gyarawa: Za ka iya neman mu gyara duk wani bayani mara daidai",
    "privacyRightDeletion" to "Sharewa: Za ka iya neman mu share asusunka da duk bayanan da suka hade da shi",
    "privacyRightPortability" to "Kaiwa: Za ka iya neman mu bayar da bayaninka a tsari mai amfani gama gari",
    "privacyExerciseRights" to "Domin amfani da wani daga cikin wadannan hakkoki, don Allah ka tuntuɓe mu ta adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Cookies",
    "privacyCookiesText" to "Adopt-U tana amfani da cookies kadan da suka zama dole domin aikin dandalin, wanda ya hada da gudanar da zama da zaɓin harshe. Ba ma amfani da cookies na talla ko bibiya.",
    "privacyThirdPartyH2" to "Hidimomin Kamfanoni na Uku",
    "privacyThirdPartyIntro" to "Muna amfani da hidimomin kamfanoni na uku masu zuwa, wadanda za su iya sarrafa bayaninka:",
    "privacyAws" to "AWS (Amazon Web Services): Domin ajiye bayanai da isar da imel. Manufofin sirrinsu na aiki akan sarrafa bayaninka.",
    "privacyWebauthn" to "WebAuthn/FIDO2: Domin amintaccen shaidar shiga ba tare da kalmar sirri ba. Wannan ya kunshi ayyukan kimiyyar sirri a cikin burauzarka.",
    "privacyThirdPartySelected" to "Mun zaɓi masu bayar da hidima cikin hankali wadanda suke raba mana burin kiyaye sirrin bayanai.",
    "privacyChildrensH2" to "Sirrin Yara",
    "privacyChildrensText" to "Adopt-U ba a nufe ta ne domin masu amfani da suka kasa shekaru 18 ba. Ba mu tattara bayanin karami da sane ba. Idan ka yi imani wani karami ya kirkiri asusu, don Allah ka tuntuɓe mu domin cirewa.",
    "privacyChangesH2" to "Canje-canje ga Wannan Manufa",
    "privacyChangesText" to "Za mu iya sabunta wannan Manufar Sirri lokaci zuwa lokaci domin nuna canje-canje a hanyoyinmu ko bukatun doka. Za mu sanar da masu amfani game da manyan canje-canje ta imel ko sanarwa mai bayyana a dandalin.",
    "privacyContactH2" to "Tuntuɓe Mu",
    "privacyContactText" to "Idan kana da wata tambaya ko damuwa game da wannan Manufar Sirri, ko domin amfani da hakkokin bayaninka, don Allah ka tuntuɓe mu ta adopt-u@adopt-u.org.",
    "termsConditions" to "Sharudda da Ka'idoji",
    "installApp" to "Shigar da App",
    "termsAcceptanceH2" to "Amincewa da Sharudda",
    "termsAcceptanceText" to "Ta hanyar shiga da amfani da Adopt-U, ka amince ka kuma yarda ka daure da sharudda da tanade-tanaden wannan yarjejeniya. Idan ba ka amince da wadannan sharudda ba, don Allah kada ka yi amfani da dandalinmu.",
    "termsNatureH2" to "Yanayin Hidimarmu",
    "termsNatureText1" to "Adopt-U dandali ne na hada-hada wanda ke sauƙaƙe tuntuɓa tsakanin masu ceto dabbobi da masu yiwuwar karban dabbobi. Ba mu kasuwar dabbobi ba, dillali, matsuguni, ko hukumar karba. Ba mu mallaka, kula, ko sarrafa kai tsaye wata dabba da aka jera a dandalinmu.",
    "termsNatureText2" to "Rawar da muke takawa tana iyakance sosai zuwa bayar da hanyar sadarwa tsakanin bangarori. Ba mu shiga cikin tattaunawar karba, kwangila, ko mika dabbobi ta jiki ba.",
    "termsUserRespH2" to "Nauyin Mai Amfani",
    "termsForRescuersH3" to "Domin Masu Ceto:",
    "termsRescuerResp1" to "Bayar da bayani na gaskiya da daidai game da dabbobin da kake kulawa",
    "termsRescuerResp2" to "Tabbatar da cewa dabbobi lafiyayyu ne, an yi musu allurar rigakafi, kuma an bincike su yadda ya kamata kafin a jera su",
    "termsRescuerResp3" to "Gudanar da bincike mai nauyi na masu yiwuwar karba",
    "termsRescuerResp4" to "Shirya mika dabbobi lafiya zuwa sabbin gidajensu",
    "termsRescuerResp5" to "Karɓi nauyin daidaiton duk bayanan da aka bayar",
    "termsRescuerResp6" to "Bin duk dokoki da ka'idojin gari game da karban dabbobi",
    "termsRescuerResp7" to "Kada ka taba amfani da dandalin domin sayar da dabbobi ko kiwo domin kasuwanci",
    "termsForAdoptersH3" to "Domin Masu Karba:",
    "termsAdopterResp1" to "Bayar da bayani na daidai game da yanayin zamanka da kwarewarka da dabbobi",
    "termsAdopterResp2" to "Kasance a shirye ka nuna iyawarka na bayar da kulawa da ta dace",
    "termsAdopterResp3" to "Fahimci cewa dakatar da karban dabba na kawo babban tashin hankali ga dabbar",
    "termsAdopterResp4" to "Girmama hakkin mai ceto na ki amincewa da bukatarka ta karba",
    "termsPetListingsH2" to "Jerin Dabbobi da Bayani",
    "termsPetListingsText1" to "Adopt-U ba ta tabbatar, amincewa, ko tabbatar da daidaiton bayanin da aka bayar a jerin dabbobi ba. Bayani wanda ya hada amma bai iyakance ga nau'i, shekaru, matsayin lafiya, hali, da bayanan allurar rigakafi masu ceto ne suka bayar kuma ba a tabbatar da shi daban ba.",
    "termsPetListingsText2" to "Masu ceto ne suka bayar da hotuna da bayanai. Duk da cewa mun haramta bayar da bayanan karya da gangan, ba za mu iya tabbatar da cewa duk jerin sun nuna yanayin dabbar na yanzu daidai ba.",
    "termsPetListingsText3" to "Ana karfafa masu amfani su yi tambayoyi, su nemi bayanan likitan dabbobi, kuma su hadu da dabbobi kafin su kammala wata yarjejeniyar karba.",
    "termsAdoptionProcessH2" to "Tsarin Karba",
    "termsAdoptionProcessIntro" to "Da zarar mai ceto da mai karba sun amince su ci gaba da karba:",
    "termsAdoptionStep1" to "Duk shirye-shirye, wanda ya hada ranar mika, wuri, da duk wani kudin da ya hade, ana tattaunawa kai tsaye tsakanin bangarorin",
    "termsAdoptionStep2" to "Adopt-U ba ta sauƙaƙe ko tabbatar da wata hulɗar biya ba",
    "termsAdoptionStep3" to "Dandalin ba ya bayar da kwangilar karba; duk wata hulɗar kwangila nauyin bangarorin ne kadai",
    "termsAdoptionStep4" to "Muna ba da shawara sosai a rubuta duk yarjejeniyoyi",
    "termsLiabilityH2" to "Iyakancewar Nauyi",
    "termsLiabilityIntro" to "Adopt-U ba za a hore ta da alhaki domin:",
    "termsLiability1" to "Duk wata sabani, rashin jituwa, ko takaddama tsakanin masu ceto da masu karba",
    "termsLiability2" to "Daidaiton bayanin da aka bayar a jerin dabbobi",
    "termsLiability3" to "Lafiya, hali, ko yanayin wata dabba kafin, lokacin, ko bayan karba",
    "termsLiability4" to "Duk wata asarar kudi, lalacewa, ko rauni da ke fitowa daga amfani da dandalin",
    "termsLiability5" to "Sakamakon wata karba, wanda ya hada amma bai iyakance ga mayarwa, matsalolin lafiya, ko matsalolin dacewa",
    "termsLiability6" to "Duk wani aiki da masu amfani suka yi a wajen dandalin bayan an kafa tuntuɓa",
    "termsLiabilityAck" to "Masu amfani sun yarda cewa karban dabba babban alkawari ne kuma sun karɓi cikakken nauyin shawararsu ta karba.",
    "termsProhibitedH2" to "Ayyukan Haramtattu",
    "termsProhibitedIntro" to "Ayyukan da ke gaba an haramta su gaba daya a Adopt-U:",
    "termsProhibited1" to "Sayar da dabbobi domin kasuwanci, ayyukan kiwo, ko masana'antar 'yan kwikwiyo",
    "termsProhibited2" to "Jera dabbobin da ba ka kula da su kai tsaye ba",
    "termsProhibited3" to "Bayani na karya ko yaudara a jeri ko bayanan sirri",
    "termsProhibited4" to "Cin zarafi, wariya, ko tuntuɓa mara dacewa da wasu masu amfani",
    "termsProhibited5" to "Amfani da dandalin domin tattara bayanan sirri domin dalilan da ba su da alaka da karban dabbobi",
    "termsProhibited6" to "Duk wani aiki na haram ko keta dokokin walwalar dabbobi",
    "termsAccountTerminationH2" to "Kawo Karshen Asusu",
    "termsAccountTerminationText" to "Adopt-U tana da hakkin dakatar ko kawo karshen asusun da suka keta wadannan sharudda, shiga ayyuka haramtattu, ko kawo dandalin cikin mummunar suna. Hakanan za mu iya cire jerin da ke dauke da abun ciki na karya, yaudara, ko mara dacewa.",
    "termsModificationsH2" to "Canje-canje ga Sharudda",
    "termsModificationsText" to "Muna da hakkin gyara wadannan sharudda a kowane lokaci. Ci gaba da amfani da dandalin bayan canje-canje na nuna amincewa da sabuwar sharuddar. Za mu sanar da masu amfani game da manyan canje-canje ta imel ko sanarwar dandali.",
    "termsContactText" to "Idan kana da wata tambaya game da wadannan Sharudda da Ka'idoji, don Allah ka tuntuɓe mu ta adopt-u@adopt-u.org.",
    "language" to "Harshe",
    "displayName" to "Sunan da za a Nuna",
    "saveProfile" to "Ajiye Bayanan Sirri",
    "accountSettings" to "Saitunan Asusu",
    "yourRoles" to "Matsayinka:",
    "resources" to "Albarkatu",
    "loginWithPasskey" to "Shiga da Passkey",
    "signInWithPasskey" to "Shiga da Passkey",
    "resendVerificationEmail" to "Sake Aika Imel na Tabbatarwa",
    "usesFido" to "Yana amfani da FIDO2 / WebAuthn domin shaidar shiga amintacce ba tare da kalmar sirri ba.",
    "loginWithMagicLink" to "Shiga da Hanyar Imel",
    "magicLinkDesc" to "Za mu aika maka hanyar shiga zuwa imel dinka (tana aiki na tsawon minti 5).",
    "sendMagicLink" to "Aika Hanyar Shiga",
    "loginWithPassword" to "Shiga da Kalmar Sirri",
    "signIn" to "Shiga",
    "forgotPassword" to "Ka manta kalmar sirri?",
    "registerNewAccount" to "Kirkiri Asusu",
    "accountDetails" to "Bayanan Asusu",
    "registrationMethod" to "Hanyar Rijista",
    "selectAdditionalRoles" to "Zaɓi karin matsayi:",
    "adoptPet" to "Karbi dabba",
    "publishPets" to "Buga dabbobi domin karba",
    "offerPhotography" to "Bayar da hidimar daukar hoto",
    "photographerSettings" to "Saitunan Mai Daukar Hoto",
    "sessionFee" to "Kudin Zaman",
    "provideTemporaryHome" to "Bayar da gida na wucin gadi domin dabbobi",
    "passkeyNoPassword" to "Passkeys sun fi kalmomin sirri tsaro kuma suna aiki a duk na'urorinka.",
    "registerWithPasskey" to "Yi Rijista da Passkey",
    "passkeySettings" to "Saitunan Passkey",
    "password" to "Kalmar Sirri",
    "confirmPassword" to "Tabbatar da Kalmar Sirri",
    "passwordRequirements" to "Kalmar sirri dole ta zama akalla haruffa 8 kuma ta kunshi babban harafi, karamin harafi, lamba, da alama",
    "securityInfo" to "Bayanin Tsaro",
    "passkeyMostSecure" to "Passkey: Mafi tsaro, ana ba da shawara",
    "passkeyBrowserCompat" to "Passkey na iya kasa aiki a wasu burauzoji",
    "passwordLeastSecure" to "Kalmar Sirri: Karancin tsaro, ba a ba da shawara ba",
    "selectLoginMethods" to "Zaɓi hanya daya ko fiye na shiga:",
    "registerPasskeyOption" to "Passkey (mafi tsaro, yana aiki a duk na'urorinka)",
    "registerPasswordOption" to "Kalmar Sirri (karancin tsaro, yi amfani da ita a matsayin madadi kawai)",
    "howPasskeysWorkTitle" to "Yaya passkey ke aiki?",
    "howPasskeysWorkIntro" to "Passkey na maye gurbin kalmar sirrinka da tsaron da aka gina a na'urarka - alamar yatsa, gane fuska, ko lambar kulle na'ura.",
    "howPasskeysWorkSecurity" to "Ana ajiye ta ne kawai a na'urarka, ba za a iya sata ta ba ta hanyar yaudara ko a tona ta a wani hatsarin bayanai, kuma tana daidaitawa kai tsaye a duk na'urorinka.",
    "howPasskeysWorkBackup" to "Za ka iya ƙara kalmar sirri ma, amma passkey shi kadai ya isa domin shiga - ba a bukatar kalmar sirri.",
    "gotIt" to "Na gane",
    "alreadyHaveAccount" to "Kana da asusu tuni?",
    "all" to "Duka",
    "filterDogs" to "Karnuka",
    "filterCats" to "Kyanwoyi",
    "filterBirds" to "Tsuntsaye",
    "filterFish" to "Kifi",
    "filterAllSexOption" to "Kowane Jinsi",
    "filterMaleOption" to "♂ Namiji",
    "filterFemaleOption" to "♀ Mace",
    "age" to "Shekaru",
    "rescued" to "An Ceto",
    "years" to "shekaru",
    "months" to "watanni",
    "year" to "shekara",
    "month" to "wata",
    "loginToAdopt" to "Shiga don neman karɓa",
    "requiredMarker" to "(dole)",
    "pageNotFoundTitle" to "Ba a sami shafin ba",
    "pageNotFoundHint" to "Mai yiwuwa hanyar haɗin ta tsufa ko an rubuta ta kuskure. Dabbobin har yanzu suna nan.",
    "browsePets" to "Duba dabbobi",
    "requestsFromRescuers" to "Buƙatun masu ceto",
    "signingOut" to "Ana fita...",
    "adopter" to "Mai karba",
    "country" to "Ƙasa",
    "zip" to "Lambar gidan waya",
    "viewPublic" to "Duba shafin jama'a",
    "banUser" to "Hana mai amfani",
    "banReason" to "Dalili (na zaɓi)",
    "myPhotoRequests" to "Buƙatuna na ɗaukar hoto",
    "noPhotoRequestsYet" to "Ba ka nemi zaman ɗaukar hoto ba tukuna.",
    "photoRequestsReceived" to "Buƙatun ɗaukar hoto da aka karɓa",
    "noPhotoRequestsReceived" to "Babu buƙatun ɗaukar hoto tukuna.",
    "photoStatusPending" to "Ana jira",
    "photoStatusApproved" to "An amince",
    "photoStatusRejected" to "An ƙi",
    "photoStatusCancelled" to "An soke",
    "photoStatusCompleted" to "An kammala",
    "cancelRequest" to "Soke buƙata",
    "registerConsentPrefix" to "Ta hanyar buɗe asusu ka amince da",
    "registerConsentTerms" to "Sharuɗɗa da Ka'idoji",
    "registerConsentAnd" to "da",
    "registerConsentPrivacy" to "Manufar Sirri",
    "petStatusAvailable" to "Akwai",
    "petStatusAdopted" to "An Karba",
    "petStatusDisabled" to "An Dakatar",
    "petStatusPending" to "Ana Jira",
    "addEditLocation" to "Ƙara/Gyara Wuri",
    "addLocation" to "Ƙara Wuri",
    "manage" to "Sarrafa",
    "adminPanel" to "Fanel na Gudanarwa",
    "adminSterilizationLocations" to "Sarrafa Wuraren Bakarar Dabbobi",
    "adopterRequired" to "Mai Karba (dole)",
    "adoptionFee" to "Kudin Karba",
    "adoptionRequests" to "Bukatun Karba",
    "ageMonths" to "Shekaru (watanni)",
    "ageYears" to "Shekaru (shekaru)",
    "alias" to "Laƙabi",
    "bird" to "Tsuntsu",
    "breed" to "Nau'i",
    "medicalEventsOverview" to "Bayyani na Abubuwan Kiwon Lafiya",
    "back" to "Koma Baya",
    "cat" to "Kyanwa",
    "color" to "Launi",
    "compatibility" to "Dacewa",
    "currentPassword" to "Kalmar Sirri ta Yanzu",
    "dog" to "Kare",
    "emailChange" to "Canza Imel",
    "emailChangeDesc" to "Sabunta adireshin imel dinka. Za a aika hanyar tabbatarwa zuwa sabon imel.",
    "energyLevel" to "Matakin Kuzari",
    "female" to "Mace",
    "findTemporalHome" to "Nemo Gida na Wucin Gadi",
    "fish" to "Kifi",
    "goodWithCats" to "Yana da kyau da kyanwoyi",
    "goodWithDogs" to "Yana da kyau da karnuka",
    "goodWithKids" to "Yana da kyau da yara",
    "high" to "Babba",
    "houseTrained" to "An horar da shi a gida",
    "large" to "Babba",
    "low" to "Kadan",
    "male" to "Namiji",
    "managePets" to "Sarrafa Dabbobi",
    "managePetsBtn" to "Ƙara ko Gyara Dabbobi",
    "managePetsDescription" to "Sarrafa duk shafukan dabbobi. Ƙara ko cire dabbobi.",
    "manageShelters" to "Sarrafa Matsugunai",
    "manageSheltersBtn" to "Ƙara ko Gyara Mafaka",
    "manageSheltersDescription" to "Sarrafa duk shafukan matsugunai. Ƙara ko cire matsugunai.",
    "manageUsers" to "Sarrafa Masu Amfani",
    "medical" to "Likita",
    "medium" to "Matsakaici",
    "backToSearch" to "Koma zuwa bincike",
    "contactThisHome" to "Tuntuɓi Wannan Gida na Wucin Gadi",
    "loginAsRescuerToContact" to "Shiga a matsayin mai ceto domin aika bukata zuwa wannan gida na wucin gadi.",
    "memberSince" to "Memba tun",
    "microchipId" to "Lambar Microchip",
    "microchipped" to "Da Microchip",
    "myTemporalHome" to "Gidana na Wucin Gadi",
    "newEmail" to "Sabon Imel",
    "noTemporalHomes" to "Ba a sami gidajen wucin gadi ba.",
    "requestSentToHome" to "An aika bukatarka!",
    "sendRequestBtn" to "Aika Bukata",
    "temporalHomeNotFound" to "Ba a sami wannan gida na wucin gadi ba.",
    "viewDetails" to "Duba Bayanai",
    "rescuerDirectory" to "Masu Ceto",
    "noRescuersFound" to "Ba a sami masu ceto ba.",
    "availablePetsCount" to "Dabbobin da akwai",
    "availablePets" to "Dabbobin da Akwai",
    "noAvailablePetsForRescuer" to "Wannan mai ceto ba shi da dabbobi a yanzu.",
    "rescuerNotFound" to "Ba a sami wannan mai ceto ba.",
    "errorLoadingResults" to "Kuskure wajen loda sakamako.",
    "volunteerForThisRescuer" to "Yi Aikin Sa Kai ga Wannan Mai Ceto",
    "volunteerExplanation" to "Masu aikin sa kai da aka amince za su iya bayar da shawarar gyara jerin dabbobin wannan mai ceto.",
    "applyToVolunteerBtn" to "Nemi Aikin Sa Kai",
    "loginToVolunteer" to "Shiga domin nema aikin sa kai ga wannan mai ceto.",
    "volunteerApplicationSent" to "An aika bukatar aikin sa kai naka!",
    "myVolunteering" to "Aikin Sa Kai Na",
    "noVolunteerApplicationsYet" to "Ba ka nema aikin sa kai ga wani mai ceto ba tukuna.",
    "howToVolunteerHint" to "Don neman, buɗe shafin mai ceto daga jerin sannan ka danna \"Nemi Zama Mai Sa Kai\".",
    "volunteerStatusPending" to "Ana Jira",
    "volunteerStatusActive" to "Yana Aiki",
    "volunteerStatusRejected" to "An Ki",
    "volunteerApplications" to "Bukatun Aikin Sa Kai",
    "noVolunteerApplications" to "Babu bukatun aikin sa kai.",
    "petEditSuggestions" to "Shawarwarin Gyara Dabba",
    "noPetEditSuggestions" to "Babu shawarwarin gyara da ake jira.",
    "suggestedByLabel" to "an bayar da shawara daga",
    "suggestAnEditTitle" to "Bayar da Shawarar Gyara",
    "suggestAnEditExplanation" to "A matsayin mai aikin sa kai da aka amince, za ka iya bayar da shawarar canje-canje ga wannan jerin. Mai ceto zai duba kafin ya fito.",
    "suggestEditBtn" to "Aika Shawara",
    "editSuggestionSent" to "An aika shawararka zuwa mai ceto domin duba!",
    "sponsorThisPetTitle" to "Dauki Nauyin Wannan Dabba",
    "sponsorExplanation" to "Ba za ka iya karba ba amma kana son taimakawa da kudade? Bayar da kudi ko taimako na kayayyaki kuma ka hadu kai tsaye da mai ceto.",
    "sponsorThisRescuerTitle" to "Dauki Nauyin Wannan Mai Ceto",
    "sponsorRescuerExplanation" to "Taimaka wa kudaden gaba daya na wannan mai ceto da kudi ko taimako na kayayyaki.",
    "sponsorTypeLabel" to "Me za ka iya bayarwa?",
    "sponsorTypeMoney" to "Kudi",
    "sponsorTypeInKind" to "Kayayyaki (abinci, kayan aiki, da sauransu)",
    "amountLabel" to "Adadi (kowane wata)",
    "currencyLabel" to "Kudin Kasa",
    "inKindDescriptionLabel" to "Bayyana abin da za ka iya bayarwa",
    "messageLabel" to "Sako",
    "sendOfferBtn" to "Aika Tayi",
    "sponsorOfferSent" to "An aika tayin ka! Mai ceto zai tuntuɓe ka domin shirya bayanai.",
    "sponsorshipOffersReceived" to "Tayin Daukar Nauyi",
    "noSponsorshipOffers" to "Babu tayin daukar nauyi tukuna.",
    "generalFundLabel" to "Asusun gaba daya",
    "offeringLabel" to "Tayi",
    "sponsorshipStatusSent" to "Sabo",
    "sponsorshipStatusRead" to "An Karanta",
    "markAsReadBtn" to "Yi alama a matsayin An Karanta",
    "mySponsorshipOffers" to "Tayin Daukar Nauyi Na",
    "noSponsorshipOffersYet" to "Ba ka aika wani tayin daukar nauyi ba tukuna.",
    "howToSponsorHint" to "Don ɗaukar nauyi, buɗe shafin mai ceto (tallafi na gaba ɗaya) ko shafin dabba (kuɗin wannan dabba) sannan ka aika tayi.",
    "browsePetsLink" to "Duba dabbobi",
    "yourMessage" to "Sakonka",
    "newPassword" to "Sabuwar Kalmar Sirri",
    "passkeyDesc" to "Ƙara passkeys na karin ga asusunka domin shiga cikin sauki a duk na'urori.",
    "passkeyName" to "Sunan Passkey",
    "pers" to "Mutane",
    "petFoodDescription" to "Koyi wane abinci ne mai aminci, mai illa, ko mai guba ga dabbobinka",
    "petFoodGuide" to "Jagorar Abincin Dabbobi",
    "foodInformation" to "Bayanin Abinci",
    "petsForAdoption" to "Dabbobi domin Karba",
    "chooseCountryPrompt" to "Nemo sabon babban abokinka",
    "chooseCountryPromptHint" to "Zaɓi kasa a sama domin bincika dabbobin da ake iya karba kusa da kai.",
    "noPetsFound" to "Ba a sami dabbobi ba",
    "noPetsFoundHint" to "Gwada wata tacewa dabam ko dawo daga baya - ana ƙara sabbin dabbobi kullum.",
    "loadingMorePets" to "Ana loda karin dabbobi…",
    "photos" to "Hotuna (mafi yawa 12)",
    "profilePhotographerRole" to "Mai Daukar Hoto - Bayar da hidimar daukar hoto",
    "profileRescuerRole" to "Mai Ceto - Buga dabbobi domin karba",
    "profileTemporalHomeRole" to "Gida na Wucin Gadi - Bayar da gida na wucin gadi domin dabbobi",
    "provideShelter" to "Matsuguni - Sarrafa matsugunin dabbobi",
    "provideSterilization" to "Hidimar Bakarar Dabbobi - Bayar da hidimar bakarar dabbobi",
    "requestEmailChange" to "Nemi Canza Imel",
    "rescueDate" to "Ranar Ceto",
    "rescueLocation" to "Wurin Ceto",
    "savePassword" to "Ajiye Kalmar Sirri",
    "selectEnergy" to "Zaɓi kuzari",
    "selectSize" to "Zaɓi girma",
    "sex" to "Jinsi",
    "shelterSettings" to "Saitunan Matsuguni",
    "size" to "Girma",
    "small" to "Karami",
    "specialNeeds" to "Bukatu na Musamman",
    "sterilizationSettings" to "Saitunan Bakarar Dabbobi",
    "sterilized" to "An Yi Bakarar",
    "temporalHomeSettings" to "Saitunan Gida na Wucin Gadi",
    "temperament" to "Hali",
    "type" to "Nau'i *",
    "urgentAdoption" to "Ana bukatar karba cikin gaggawa",
    "vaccinations" to "Allurar Rigakafi",
    "vetDisclaimer" to "Tuntuɓi likitan dabbobinka kafin canza abinci.",
    "weight" to "Nauyi (kg)",
    "weightLabel" to "Nauyi",
    "website" to "Yanar Gizo",
    "securitySettings" to "Saitunan Tsaro",
    "emailVerifiedTitle" to "An Tabbatar da Imel Cikin Nasara!",
    "emailVerifiedMessage" to "An tabbatar da imel dinka. Rijistarka ta kammala yanzu.",
    "redirectingIn" to "Ana tura zuwa babban shafi cikin ",
    "canAlsoLogin" to "Hakanan za ka iya ",
    "loginNow" to "Shiga Yanzu",
    "emailVerificationFailed" to "Tabbatarwa Ta Kasa",
    "invalidOrExpiredToken" to "Hanyar tabbatarwa ba ta da inganci ko ta kare.",
    "tryRegisteringAgain" to "Don Allah gwada yin rijista sake.",
    "verificationEmailResent" to "Imel na tabbatarwa ya kare. An aika sabon imel na tabbatarwa. Don Allah duba akwatin sakonka.",
    "emailVerificationSent" to "An aika imel na tabbatarwa. Don Allah duba akwatin sakonka kafin shiga.",
    "emailNotVerifiedLogin" to "Don Allah tabbatar da imel dinka kafin shiga. Duba akwatin sakonka domin hanyar tabbatarwa.",
    "invalidOrExpiredMagicLink" to "Hanyar sihiri ba ta da inganci ko ta kare. Don Allah nemi sabuwa.",
    "invalidToken" to "Alamar ba ta da inganci.",
    "accountBanned" to "An dakatar da asusunka.",
    "loginError" to "An sami kuskure wajen shiga.",
    "profileUpdated" to "An sabunta bayanan sirri!",
    "displayNameRequired" to "Sunan da za a nuna ba zai zama fanko ba",
    "saving" to "Ana ajiyewa...",
    "passwordSet" to "An saita kalmar sirri",
    "noPassword" to "Ba a saita kalmar sirri ba",
    "passwordChangeSuccess" to "An canza kalmar sirri cikin nasara!",
    "passwordChangeFailed" to "An kasa canza kalmar sirri",
    "passwordSetSuccess" to "An saita kalmar sirri cikin nasara!",
    "passwordSetFailed" to "An kasa saita kalmar sirri",
    "passwordsDoNotMatch" to "Kalmomin sirri ba su dace da juna ba",
    "currentPasswordRequired" to "Ana bukatar kalmar sirri ta yanzu",
    "emailChangeSent" to "An aika imel na tabbatarwa zuwa sabon adireshin imel dinka!",
    "emailChangeFailed" to "An kasa neman canza imel",
    "emailRequired" to "Ana bukatar imel",
    "sendingEllipsis" to "Ana aikawa...",
    "resetLinkSent" to "An aika hanyar sake saita kalmar sirri! Duba imel dinka.",
    "magicLinkSent" to "An aika hanyar shiga! Duba imel dinka.",
    "magicLinkSendFailed" to "An kasa aika hanyar shiga",
    "failedSendResetLink" to "An kasa aika hanyar sake saitawa.",
    "invalidOrMissingToken" to "Alamar ba ta da inganci ko ba ta nan.",
    "passwordTooShort" to "Kalmar sirri dole ta zama akalla haruffa 8.",
    "passwordNeedUppercase" to "Kalmar sirri na bukatar akalla babban harafi 1.",
    "passwordNeedLowercase" to "Kalmar sirri na bukatar akalla karamin harafi 1.",
    "passwordNeedNumber" to "Kalmar sirri na bukatar akalla lamba 1.",
    "passwordNeedSymbol" to "Kalmar sirri na bukatar akalla alama 1.",
    "resettingPassword" to "Ana sake saita kalmar sirri...",
    "passwordResetSuccess" to "An sake saita kalmar sirri cikin nasara! Yanzu za ka iya shiga.",
    "failedResetPassword" to "An kasa sake saita kalmar sirri.",
    "loginSuccessRedirecting" to "Shiga ya yi nasara! Ana turawa...",
    "loginFailedExpiredLink" to "Shiga ya kasa. Hanyar na iya zama mara inganci ko ta kare.",
    "loginFailed" to "Shiga ya kasa.",
    "emailChangedSuccess" to "An canza imel cikin nasara!",
    "failedChangeEmailExpired" to "An kasa canza imel. Hanyar na iya zama mara inganci ko ta kare.",
    "failedChangeEmail" to "An kasa canza imel.",
    "emailVerifiedSuccess" to "An tabbatar da imel cikin nasara!",
    "failedVerifyEmailExpired" to "An kasa tabbatar da imel. Hanyar na iya zama mara inganci ko ta kare.",
    "failedVerifyEmail" to "An kasa tabbatar da imel.",
    "dropImagesHint" to "Sauke hotuna anan ko danna domin bincika",
    "forgotPasswordTitle" to "Ka Manta Kalmar Sirri",
    "forgotPasswordIntro" to "Shigar da imel dinka kuma za mu aika maka hanyar sake saita kalmar sirrinka.",
    "sendResetLink" to "Aika Hanyar Sake Saitawa",
    "backToLogin" to "Koma zuwa Shiga",
    "resetPasswordTitle" to "Sake Saita Kalmar Sirri",
    "resetPasswordIntro" to "Shigar da sabuwar kalmar sirrinka a kasa.",
    "emailLinkLoginTitle" to "Shiga da Hanyar Imel",
    "verifying" to "Ana tabbatarwa...",
    "emailChangeTitle" to "Canza Imel",
    "verifyContactEmailTitle" to "Tabbatar da Imel na Tuntuɓa",
    "status" to "Matsayi",
    "yes" to "Eh",
    "no" to "A'a",
    "noDescription" to "Babu bayani.",
    "urgentBadge" to "GAGGAWA - Ana bukatar gida cikin sauri!",
    "needsNewHome" to "Wannan dabba tana bukatar sabon gida cikin gaggawa",
    "promotedReasonLabel" to "Dalili",
    "selectReason" to "Zaɓi dalili",
    "promotedReasonMoving" to "Mai mallakar tana canza wuri",
    "promotedReasonComplaints" to "Korafi / ba za a iya ci gaba da kula da karin dabbobi ba",
    "promotedReasonPetConflict" to "Sabani da wata dabba",
    "promotedReasonOther" to "Wani dalili",
    "promotedReasonDetailLabel" to "Karin bayani (na zaɓi)",
    "promotedReasonRequired" to "Don Allah zaɓi dalili",
    "needsNewHomeBadge" to "Tana bukatar sabon gida",
    "housingType" to "Nau'in gida",
    "preferNotToSay" to "Na fi son kada in fada",
    "houseHousing" to "Gida",
    "apartmentHousing" to "Ɗakin Haya",
    "hasYard" to "Yana da filin waje",
    "hasOtherPets" to "Wasu dabbobi a gida",
    "adoptionExperience" to "Kwarewar karba",
    "firstTimeAdopter" to "Mai karba na farko",
    "experiencedAdopter" to "Mai karba mai kwarewa",
    "noAdoptionRequests" to "Babu bukatun karba",
    "noMessage" to "Babu sako",
    "markUnderReview" to "Yi alama a matsayin Ana Duba",
    "approve" to "Amince",
    "reject" to "Ki",
    "reviewNoteLabel" to "Bayanin duba na sirri",
    "saveNote" to "Ajiye Bayani",
    "adoptionStatusPending" to "Ana Jira",
    "adoptionStatusUnderReview" to "Ana Duba",
    "adoptionStatusApproved" to "An Amince",
    "adoptionStatusRejected" to "Ba a Amince ba",
    "myAdoptionRequests" to "Bukatun Karba Na",
    "noAdoptionRequestsYet" to "Ba ka nemi karban wata dabba ba tukuna.",
    "medicalHistory" to "Tarihin Lafiya",
    "medicalSchedule" to "Jadawalin Allurar Rigakafi da Fitar da Tsutsotsi",
    "recordType" to "Nau'i",
    "vaccination" to "Allurar Rigakafi",
    "deworming" to "Fitar da Tsutsotsi",
    "recordName" to "Suna",
    "dateGiven" to "Ranar da aka bayar",
    "nextDueDate" to "Na gaba (na zaɓi)",
    "notesOptional" to "Bayanai (na zaɓi)",
    "addRecord" to "Ƙara Rikodi",
    "noMedicalRecords" to "Babu rikodin allurar rigakafi ko fitar da tsutsotsi tukuna.",
    "nextDueLabel" to "Na gaba",
    "givenLabel" to "An Bayar",
    "overdue" to "Ya Wuce Lokaci",
    "dueSoon" to "Zai Kai Lokaci Nan Ba Da Jimawa Ba",
    "upToDate" to "A Sabuntacce",
    "recordNameRequired" to "Ana bukatar suna",
    "dateGivenRequired" to "Ana bukatar ranar da aka bayar",
    "failedToAddRecord" to "An kasa ƙara rikodi",
    "confirmDeleteRecord" to "Share wannan rikodin?",
    "maxCapacity" to "Mafi yawan dabbobi a lokaci daya (na zaɓi)",
    "placePetHere" to "Sanya Dabba Anan",
    "noAvailablePetsToPlace" to "Ba ka da wata dabba da za a iya sanyawa a yanzu.",
    "selectPet" to "Zaɓi dabba",
    "startPlacementBtn" to "Fara Sanyawa",
    "placementStarted" to "An fara sanya dabba a matsugunin wucin gadi!",
    "petsCurrentlyWithMe" to "Dabbobin da Suke Tare Da Ni Yanzu",
    "noPetsCurrentlyFostered" to "Babu dabbobin da aka sanya tare da kai a yanzu.",
    "sinceLabel" to "tun",
    "fosterPlacement" to "Sanya a Matsugunin Wucin Gadi",
    "endPlacement" to "Kawo Karshen Sanyawa",
    "currentlyWithLabel" to "A yanzu tare da",
    "notCurrentlyFostered" to "Ba a sanya a matsugunin wucin gadi a yanzu ba.",
    "confirmEndPlacement" to "Kawo karshen wannan sanyawar wucin gadi? Za a rubuta dabbar a matsayin ta dawo tare da kai.",
    "aTemporalHome" to "gida na wucin gadi",
    "viewsLabel" to "Kallo",
    "inquiriesLabel" to "Tambayoyi",
    "conversionLabel" to "Sauyawa",
    "messageOptional" to "Sako (na zaɓi)",
    "requestAdoption" to "Nemi Karba",
    "editPet" to "Gyara Dabba",
    "adoptionRequestSubmitted" to "An gabatar da bukatar karba!",
    "failedSubmitRequest" to "An kasa gabatar da bukata",
    "noPhotosYet" to "Babu hotuna tukuna.",
    "rabbit" to "Zomo",
    "safeFoods" to "Abinci Mai Aminci",
    "harmfulFoods" to "Abinci Mai Illa",
    "cannotEatFoods" to "Ba za a Iya Ci ba",
    "legendSafe" to "Mai Aminci",
    "legendHarmful" to "Mai Illa",
    "legendToxic" to "Mai Guba",
    "food_dog_safe_chicken_name" to "Kaza",
    "food_dog_safe_chicken_detail" to "An dafa, ba kashi",
    "food_dog_safe_chicken_desc" to "Furotin mara mai, sauki a narke",
    "food_dog_safe_rice_name" to "Shinkafa",
    "food_dog_safe_rice_detail" to "Fari ko launin ruwan kasa",
    "food_dog_safe_rice_desc" to "Tushen carbohydrate mai kyau",
    "food_dog_safe_carrots_name" to "Karas",
    "food_dog_safe_carrots_detail" to "Danye ko dafaffe",
    "food_dog_safe_carrots_desc" to "Kalori kadan, fiber mai yawa",
    "food_dog_safe_apples_name" to "Apple",
    "food_dog_safe_apples_detail" to "Ba tare da iri ba",
    "food_dog_safe_apples_desc" to "Mai yawan bitamin",
    "food_dog_safe_peanutbutter_name" to "Man Gyada",
    "food_dog_safe_peanutbutter_detail" to "Mara gishiri, ba xylitol",
    "food_dog_safe_peanutbutter_desc" to "Tushen furotin mai kyau",
    "food_dog_harmful_grapes_name" to "Inabi",
    "food_dog_harmful_grapes_detail" to "Duk nau'i",
    "food_dog_harmful_grapes_desc" to "Zai iya haifar da gazawar koda",
    "food_dog_harmful_onions_name" to "Albasa",
    "food_dog_harmful_onions_detail" to "Duk nau'i",
    "food_dog_harmful_onions_desc" to "Yana lalata jajayen kwayoyin jini",
    "food_dog_harmful_chocolate_name" to "Cakulan",
    "food_dog_harmful_chocolate_detail" to "Duk nau'i",
    "food_dog_harmful_chocolate_desc" to "Mai guba ga karnuka",
    "food_dog_harmful_garlic_name" to "Tafarnuwa",
    "food_dog_harmful_garlic_detail" to "Duk nau'i",
    "food_dog_harmful_garlic_desc" to "Yana haifar da karancin jini",
    "food_dog_cannot_xylitol_name" to "Xylitol",
    "food_dog_cannot_xylitol_detail" to "Mai zaki na wucin gadi",
    "food_dog_cannot_xylitol_desc" to "Mai matukar guba",
    "food_dog_cannot_macadamia_name" to "Goro na Macadamia",
    "food_dog_cannot_macadamia_detail" to "Duk nau'i",
    "food_dog_cannot_macadamia_desc" to "Yana haifar da rauni",
    "food_dog_cannot_avocado_name" to "Avocado",
    "food_dog_cannot_avocado_detail" to "Iri, fata, duka",
    "food_dog_cannot_avocado_desc" to "Yana dauke da persin",
    "food_dog_cannot_alcohol_name" to "Barasa",
    "food_dog_cannot_alcohol_detail" to "Duk nau'i",
    "food_dog_cannot_alcohol_desc" to "Mai matukar guba",
    "food_cat_safe_cookedfish_name" to "Kifi Dafaffe",
    "food_cat_safe_cookedfish_detail" to "Ba tare da kashi ba",
    "food_cat_safe_cookedfish_desc" to "Furotin mai yawa",
    "food_cat_safe_chicken_name" to "Kaza",
    "food_cat_safe_chicken_detail" to "An dafa, na kai tsaye",
    "food_cat_safe_chicken_desc" to "Tushen furotin mai kyau",
    "food_cat_safe_pumpkin_name" to "Kabewa",
    "food_cat_safe_pumpkin_detail" to "Na kai tsaye, dafaffe",
    "food_cat_safe_pumpkin_desc" to "Yana taimaka narkewa",
    "food_cat_safe_eggs_name" to "Kwai",
    "food_cat_safe_eggs_detail" to "Dafaffe",
    "food_cat_safe_eggs_desc" to "Cikakken furotin",
    "food_cat_harmful_raweggs_name" to "Kwai Danye",
    "food_cat_harmful_raweggs_detail" to "Da avidin",
    "food_cat_harmful_raweggs_desc" to "Yana tsangwama biotin",
    "food_cat_harmful_rawfish_name" to "Kifi Danye",
    "food_cat_harmful_rawfish_detail" to "Yana dauke da thiaminase",
    "food_cat_harmful_rawfish_desc" to "Yana lalata bitamin B",
    "food_cat_harmful_dogfood_name" to "Abincin Kare",
    "food_cat_harmful_dogfood_detail" to "Kowanne",
    "food_cat_harmful_dogfood_desc" to "Bai da taurine",
    "food_cat_harmful_milk_name" to "Madara",
    "food_cat_harmful_milk_detail" to "Yawancin kyanwoyi",
    "food_cat_harmful_milk_desc" to "Rashin iya narke lactose",
    "food_cat_cannot_chocolate_name" to "Cakulan",
    "food_cat_cannot_chocolate_detail" to "Duk nau'i",
    "food_cat_cannot_chocolate_desc" to "Theobromine mai guba",
    "food_cat_cannot_onionsgarlic_name" to "Albasa/Tafarnuwa",
    "food_cat_cannot_onionsgarlic_detail" to "Duk nau'i",
    "food_cat_cannot_onionsgarlic_desc" to "Yana lalata jajayen kwayoyin jini",
    "food_cat_cannot_grapesraisins_name" to "Inabi/Zabib",
    "food_cat_cannot_grapesraisins_detail" to "Duk nau'i",
    "food_cat_cannot_grapesraisins_desc" to "Lalacewar koda",
    "food_cat_cannot_alcohol_name" to "Barasa",
    "food_cat_cannot_alcohol_detail" to "Duk nau'i",
    "food_cat_cannot_alcohol_desc" to "Mai matukar guba",
    "food_bird_safe_seeds_name" to "Iri",
    "food_bird_safe_seeds_detail" to "Cakuda iri iri",
    "food_bird_safe_seeds_desc" to "Tushen mai mai kyau",
    "food_bird_safe_fruits_name" to "'Ya'yan Itace",
    "food_bird_safe_fruits_detail" to "Yawancin nau'i",
    "food_bird_safe_fruits_desc" to "Bitamin da ma'adanai",
    "food_bird_safe_vegetables_name" to "Kayan Lambu",
    "food_bird_safe_vegetables_detail" to "Ganyayen ganye",
    "food_bird_safe_vegetables_desc" to "Sinadaran gina jiki masu muhimmanci",
    "food_bird_safe_pellets_name" to "Pellets",
    "food_bird_safe_pellets_detail" to "An tsara da inganci",
    "food_bird_safe_pellets_desc" to "Cikakkiyar abinci mai gina jiki",
    "food_bird_harmful_avocado_name" to "Avocado",
    "food_bird_harmful_avocado_detail" to "Duk sassa",
    "food_bird_harmful_avocado_desc" to "Persin mai guba",
    "food_bird_harmful_fruitpits_name" to "Iri na 'Ya'yan Itace",
    "food_bird_harmful_fruitpits_detail" to "Apple, peach",
    "food_bird_harmful_fruitpits_desc" to "Alamun cyanide",
    "food_bird_harmful_salt_name" to "Gishiri",
    "food_bird_harmful_salt_detail" to "Kowane nau'i",
    "food_bird_harmful_salt_desc" to "Mai guba ga tsuntsaye",
    "food_bird_harmful_caffeine_name" to "Caffeine",
    "food_bird_harmful_caffeine_detail" to "Kofi, shayi",
    "food_bird_harmful_caffeine_desc" to "Matsalolin zuciya",
    "food_bird_cannot_chocolate_name" to "Cakulan",
    "food_bird_cannot_chocolate_detail" to "Duk nau'i",
    "food_bird_cannot_chocolate_desc" to "Theobromine mai guba",
    "food_bird_cannot_onions_name" to "Albasa",
    "food_bird_cannot_onions_detail" to "Duk nau'i",
    "food_bird_cannot_onions_desc" to "Yana lalata jajayen kwayoyin jini",
    "food_bird_cannot_garlic_name" to "Tafarnuwa",
    "food_bird_cannot_garlic_detail" to "Duk nau'i",
    "food_bird_cannot_garlic_desc" to "Mai guba",
    "food_bird_cannot_mushrooms_name" to "Namomin Kaza",
    "food_bird_cannot_mushrooms_detail" to "Nau'in daji",
    "food_bird_cannot_mushrooms_desc" to "Yiwuwar guba",
    "food_fish_safe_flakes_name" to "Flakes",
    "food_fish_safe_flakes_detail" to "Nau'i masu inganci",
    "food_fish_safe_flakes_desc" to "Cikakkiyar abincin kifi",
    "food_fish_safe_pellets_name" to "Pellets",
    "food_fish_safe_pellets_detail" to "Yana yawo/nutsewa",
    "food_fish_safe_pellets_desc" to "Abinci mai gina jiki daidaitacce",
    "food_fish_safe_frozenfood_name" to "Abinci Daskararre",
    "food_fish_safe_frozenfood_detail" to "Bloodworms, brine",
    "food_fish_safe_frozenfood_desc" to "Tushen furotin",
    "food_fish_safe_vegetables_name" to "Kayan Lambu",
    "food_fish_safe_vegetables_detail" to "Zucchini da aka dafa kadan",
    "food_fish_safe_vegetables_desc" to "Tushen fiber",
    "food_fish_harmful_bread_name" to "Gurasa",
    "food_fish_harmful_bread_detail" to "Kowane nau'i",
    "food_fish_harmful_bread_desc" to "Babu sinadaran gina jiki, yana cika ciki",
    "food_fish_harmful_humanfood_name" to "Abincin Mutum",
    "food_fish_harmful_humanfood_detail" to "Abinci dafaffe",
    "food_fish_harmful_humanfood_desc" to "Kuskuren sinadaran gina jiki",
    "food_fish_harmful_livefeed_name" to "Abincin Rai",
    "food_fish_harmful_livefeed_detail" to "Kwarin da aka kama daji",
    "food_fish_harmful_livefeed_desc" to "Yiwuwar tsutsotsi",
    "food_fish_cannot_landinsect_name" to "Kwarin Kasa",
    "food_fish_cannot_landinsect_detail" to "Kwaruruka masu rarrafe",
    "food_fish_cannot_landinsect_desc" to "Ba abinci na dabi'a ba",
    "food_fish_cannot_mammalmeat_name" to "Naman Dabbobi Masu Shayarwa",
    "food_fish_cannot_mammalmeat_detail" to "Kaza, naman sa",
    "food_fish_cannot_mammalmeat_desc" to "Ba daidai ba ga yawancin kifi",
    "food_fish_cannot_dairy_name" to "Kayan Kiwo",
    "food_fish_cannot_dairy_detail" to "Kowanne",
    "food_fish_cannot_dairy_desc" to "Ba za a iya narke ba",
    "food_fish_cannot_breadcrumbs_name" to "Gutsattsarin Gurasa",
    "food_fish_cannot_breadcrumbs_detail" to "Yawancinsu suna dauke da tafarnuwa",
    "food_fish_cannot_breadcrumbs_desc" to "Abubuwa masu illa",
    "food_rabbit_safe_hay_name" to "Ciyawar Bushe",
    "food_rabbit_safe_hay_detail" to "Ciyawar Timothy",
    "food_rabbit_safe_hay_desc" to "Kaso 80% na abinci",
    "food_rabbit_safe_leafygreens_name" to "Ganyayen Ganye",
    "food_rabbit_safe_leafygreens_detail" to "Romaine, cilantro",
    "food_rabbit_safe_leafygreens_desc" to "Bitamin na yau da kullum",
    "food_rabbit_safe_pellets_name" to "Pellets",
    "food_rabbit_safe_pellets_detail" to "Na tushen Timothy",
    "food_rabbit_safe_pellets_desc" to "Abinci mai gina jiki daidaitacce",
    "food_rabbit_safe_carrots_name" to "Karas",
    "food_rabbit_safe_carrots_detail" to "Cikin iyaka",
    "food_rabbit_safe_carrots_desc" to "Kyauta",
    "food_rabbit_harmful_icebergletttuce_name" to "Lettuce na Iceberg",
    "food_rabbit_harmful_icebergletttuce_detail" to "Babu sinadaran gina jiki",
    "food_rabbit_harmful_icebergletttuce_desc" to "Zai iya haifar da tsaiwar hanji",
    "food_rabbit_harmful_beans_name" to "Wake",
    "food_rabbit_harmful_beans_detail" to "Duk irin wake",
    "food_rabbit_harmful_beans_desc" to "Iska, matsalolin narkewa",
    "food_rabbit_harmful_corn_name" to "Masara",
    "food_rabbit_harmful_corn_detail" to "Kowane nau'i",
    "food_rabbit_harmful_corn_desc" to "Ba za a iya narke ba",
    "food_rabbit_harmful_potatoes_name" to "Dankali",
    "food_rabbit_harmful_potatoes_detail" to "Kowane nau'i",
    "food_rabbit_harmful_potatoes_desc" to "Kuskuren sinadaran gina jiki",
    "food_rabbit_cannot_chocolate_name" to "Cakulan",
    "food_rabbit_cannot_chocolate_detail" to "Duk nau'i",
    "food_rabbit_cannot_chocolate_desc" to "Theobromine mai guba",
    "food_rabbit_cannot_onions_name" to "Albasa",
    "food_rabbit_cannot_onions_detail" to "Duk nau'i",
    "food_rabbit_cannot_onions_desc" to "Mai matukar guba",
    "food_rabbit_cannot_garlic_name" to "Tafarnuwa",
    "food_rabbit_cannot_garlic_detail" to "Duk nau'i",
    "food_rabbit_cannot_garlic_desc" to "Mai guba",
    "food_rabbit_cannot_avocado_name" to "Avocado",
    "food_rabbit_cannot_avocado_detail" to "Duk sassa",
    "food_rabbit_cannot_avocado_desc" to "Yana dauke da persin",
    "primary" to "Na Farko",
    "passkeyRegisteredSuccess" to "An yi rijistar passkey cikin nasara!",
    "passkeyRegistrationFailed" to "Rijistar passkey ta kasa",
    "passkeyNameRequired" to "Ana bukatar sunan passkey",
    "registeringPasskey" to "Ana yin rijistar passkey...",
    "passkeyRegistered" to "An yi rijistar passkey",
    "noPasskey" to "Babu passkey da aka yi rijista",
    "noPets" to "Babu dabbobi tukuna.",
    "photographerCountryStateRequired" to "Don Allah cika kasa da jiha domin hidimar daukar hoto",
    "temporalHomeAliasCountryCityRequired" to "Don Allah cika laƙabi, kasa da birni domin gida na wucin gadi",
    "shelterNameCountryCityAddressRequired" to "Don Allah cika suna, kasa, birni da adireshi domin matsuguni",
    "sterilizationNameCountryCityAddressRequired" to "Don Allah cika suna, kasa, birni da adireshi domin wurin bakarar dabbobi",
    "country.afghanistan" to "Afghanistan",
    "country.albania" to "Albaniya",
    "country.algeria" to "Aljeriya",
    "country.argentina" to "Argentina",
    "country.armenia" to "Armeniya",
    "country.australia" to "Ostiraliya",
    "country.austria" to "Ostiriya",
    "country.azerbaijan" to "Azerbaijan",
    "country.bangladesh" to "Bangladesh",
    "country.belarus" to "Belarus",
    "country.belgium" to "Belgium",
    "country.bolivia" to "Bolivia",
    "country.bosnia" to "Bosnia da Herzegovina",
    "country.brazil" to "Brazil",
    "country.bulgaria" to "Bulgaria",
    "country.cambodia" to "Cambodia",
    "country.cameroon" to "Kamaru",
    "country.canada" to "Kanada",
    "country.chile" to "Chile",
    "country.china" to "Sin",
    "country.colombia" to "Colombia",
    "country.costaRica" to "Costa Rica",
    "country.croatia" to "Croatia",
    "country.cuba" to "Cuba",
    "country.czechia" to "Jamhuriyar Czech",
    "country.denmark" to "Denmark",
    "country.dominicanRepublic" to "Jamhuriyar Dominican",
    "country.ecuador" to "Ecuador",
    "country.egypt" to "Masar",
    "country.elSalvador" to "El Salvador",
    "country.estonia" to "Estonia",
    "country.ethiopia" to "Habasha",
    "country.finland" to "Finland",
    "country.france" to "Faransa",
    "country.georgia" to "Georgia",
    "country.germany" to "Jamus",
    "country.ghana" to "Ghana",
    "country.greece" to "Girka",
    "country.guatemala" to "Guatemala",
    "country.haiti" to "Haiti",
    "country.honduras" to "Honduras",
    "country.hungary" to "Hungary",
    "country.iceland" to "Iceland",
    "country.india" to "Indiya",
    "country.indonesia" to "Indonesia",
    "country.iran" to "Iran",
    "country.iraq" to "Iraq",
    "country.ireland" to "Ireland",
    "country.israel" to "Isra'ila",
    "country.italy" to "Italiya",
    "country.jamaica" to "Jamaica",
    "country.japan" to "Japan",
    "country.jordan" to "Jordan",
    "country.kazakhstan" to "Kazakhstan",
    "country.kenya" to "Kenya",
    "country.kuwait" to "Kuwait",
    "country.latvia" to "Latvia",
    "country.lebanon" to "Lebanon",
    "country.libya" to "Libya",
    "country.lithuania" to "Lithuania",
    "country.luxembourg" to "Luxembourg",
    "country.malaysia" to "Malaysia",
    "country.mexico" to "Mexico",
    "country.moldova" to "Moldova",
    "country.mongolia" to "Mongolia",
    "country.montenegro" to "Montenegro",
    "country.morocco" to "Maroko",
    "country.myanmar" to "Myanmar",
    "country.nepal" to "Nepal",
    "country.netherlands" to "Netherlands",
    "country.newZealand" to "New Zealand",
    "country.nicaragua" to "Nicaragua",
    "country.nigeria" to "Najeriya",
    "country.northKorea" to "Koriya ta Arewa",
    "country.norway" to "Norway",
    "country.pakistan" to "Pakistan",
    "country.panama" to "Panama",
    "country.paraguay" to "Paraguay",
    "country.peru" to "Peru",
    "country.philippines" to "Philippines",
    "country.poland" to "Poland",
    "country.portugal" to "Portugal",
    "country.puertoRico" to "Puerto Rico",
    "country.qatar" to "Qatar",
    "country.romania" to "Romania",
    "country.russia" to "Rasha",
    "country.saudiArabia" to "Saudi Arabiya",
    "country.serbia" to "Serbia",
    "country.singapore" to "Singapore",
    "country.slovakia" to "Slovakia",
    "country.slovenia" to "Slovenia",
    "country.southAfrica" to "Afirka ta Kudu",
    "country.southKorea" to "Koriya ta Kudu",
    "country.spain" to "Spain",
    "country.sriLanka" to "Sri Lanka",
    "country.sudan" to "Sudan",
    "country.sweden" to "Sweden",
    "country.switzerland" to "Switzerland",
    "country.syria" to "Syria",
    "country.taiwan" to "Taiwan",
    "country.thailand" to "Thailand",
    "country.tunisia" to "Tunisia",
    "country.turkey" to "Turkiyya",
    "country.ukraine" to "Ukraine",
    "country.uae" to "Hadaddiyar Daular Larabawa",
    "country.uk" to "Birtaniya",
    "country.usa" to "Amurka",
    "country.uruguay" to "Uruguay",
    "country.uzbekistan" to "Uzbekistan",
    "country.venezuela" to "Venezuela",
    "country.vietnam" to "Vietnam",
    "country.yemen" to "Yemen",
    "loading" to "Ana loda...",
    "countryRequired" to "Don Allah zaɓi kasa domin bincike",
    "noSheltersFound" to "Ba a sami matsugunai ba tukuna. Dawo daga baya!",
    "visitWebsite" to "Ziyarci Yanar Gizo",
    "fiscalId" to "Lambar Haraji",
    "donationInformation" to "Bayanin Gudummawa",
    "bankName" to "Banki",
    "accountHolder" to "Mai Asusu",
    "accountNumber" to "Lambar Asusu",
    "currency" to "Kudin Kasa",
    "errorLoadingShelters" to "Kuskure wajen loda matsugunai",
    "pleaseSelectCountry" to "Don Allah zaɓi kasa",
    "actions" to "Ayyuka",
    "addShelter" to "Ƙara Matsuguni",
    "addEditShelter" to "Ƙara / Gyara Matsuguni",
    "allStates" to "Duk Jihohi",
    "confirmDelete" to "Ka tabbata kana son share wannan wurin?",
    "confirmDeleteShelter" to "Ka tabbata kana son share wannan matsugunin?",
    "confirmUnbanUser" to "Cire dakatarwa daga wannan mai amfani?",
    "confirmResetUserCredentials" to "Wannan zai soke kalmar sirri ta yanzu da passkeys na {email} sannan a aika masa hanyar sake saitawa ta imel. Ci gaba?",
    "confirmDeactivateUser" to "Dakatar da wannan mai amfani? Za a iya sake kunna shi daga baya.",
    "confirmDeactivatePet" to "Dakatar da wannan dabbar? Za a boye ta daga jerin jama'a kuma za a iya sake kunna ta daga baya.",
    "confirmBlockRescuer" to "Toshe wannan mai ceto daga aika maka karin bukatu?",
    "confirmDeletePet" to "Share wannan dabbar?",
    "confirmDeleteStorage" to "Share wannan ma'ajiyar?",
    "contact" to "Tuntuɓa",
    "delete" to "Share",
    "edit" to "Gyara",
    "errorDeletingShelter" to "Kuskure wajen share matsuguni",
    "errorLoadingLocations" to "Kuskure wajen loda wurare",
    "errorLoadingShelter" to "Kuskure wajen loda matsuguni",
    "errorSaving" to "Kuskure wajen ajiyewa",
    "errorSavingShelter" to "Kuskure wajen ajiye matsuguni",
    "location" to "Wuri",
    "noLocationsFound" to "Ba a sami wurare ba",
    "selectCountry" to "Zaɓi kasa",
    "selectCountryToFilter" to "Don Allah zaɓi kasa domin tacewa",
    "shelterAdded" to "An ƙara matsuguni cikin nasara",
    "shelterDeleted" to "An share matsuguni cikin nasara",
    "shelterUpdated" to "An sabunta matsuguni cikin nasara",
    "updateShelter" to "Sabunta Matsuguni",
    "errorLoadingPhotographers" to "Kuskure wajen loda masu daukar hoto",
    "noPhotographersAvailable" to "Babu masu daukar hoto a yanzu",
    "requestPhotoSession" to "Nemi Zaman Daukar Hoto",
    "selectPhotographer" to "Zaɓa",
    "photographersSelected" to "An zaɓi {count}",
    "requestSelectedPhotographers" to "Nemi masu daukar hoto da aka zaɓa",
    "clearSelection" to "Share zaɓi",
    "requestToMultiple" to "Nemi zuwa",
    "messageRequired" to "Da fatan za a rubuta saƙo ga masu daukar hoto.",
    "requestsSentSuccessfully" to "An aika buƙatun cikin nasara",
    "message" to "Sako",
    "sendRequest" to "Aika Bukata",
    "requestSentSuccessfully" to "An aika bukata cikin nasara!",
    "requestTo" to "Bukata zuwa",
    "enterMessage" to "Shigar da sako ga {name}...",
    "reportUrgent" to "Gaggawa",
    "topRescuers" to "Manyan Masu Ceto",
    "urgentRescuerSettings" to "Saitunan Mai Ceto na Gaggawa",
    "urgentRescueAlerts" to "Sanarwar Ceto na Gaggawa",
    "reportUrgentTitle" to "Bayar da Rahoton Dabba Cikin Hatsari",
    "reportUrgentIntro" to "Idan ka sami dabbar da ta ji rauni, aka wulakanta ta, tana fama da yunwa, ko aka watsar da ita wadda take bukatar taimako nan take, gaya mana ina kuma menene matsalar - za mu sanar da masu ceto na gaggawa kusa da ku nan take.",
    "dangerType" to "Menene matsalar?",
    "dangerInjured" to "Ta ji rauni",
    "dangerAbused" to "Ana wulakanta ta / rashin kyautatawa",
    "dangerStarving" to "Tana fama da yunwa",
    "dangerTooYoung" to "Karama kwarai domin ta rayu ita kadai",
    "dangerOther" to "Wani hatsari",
    "useMyLocation" to "Yi amfani da wurina na yanzu",
    "confirmMyLocation" to "Tabbatar da wurina na yanzu",
    "locationPermissionHint" to "Burauzarka za ta nemi izini don raba wurinka; ana amfani da shi ne kawai don sanar da kai rahotanni na kusa da kai.",
    "phoneOptional" to "Wayarka (na zaɓi)",
    "submitReport" to "Gabatar da Rahoto",
    "urgentRescuerSettingsTitle" to "Saitunan Mai Ceto na Gaggawa",
    "urgentRescuerSettingsIntro" to "Za a kira ka lokacin da aka bayar da rahoton dabba cikin hatsari kusa da yankinka. Wanda ya fara amincewa ne zai dauki al'amarin.",
    "urgentRescuerActive" to "Ina samuwa domin amsawa ga rahotannin gaggawa",
    "coverageAreaMode" to "Yankin da za ka rufe",
    "coverageArea" to "Yankin da za ka rufe",
    "coverageAreaHint" to "Tabbatar da wurin da kake yanzu, ja fil ɗin, ko rubuta adireshi a ƙasa - taswira da fagage za su kasance daidai.",
    "useMyLocationAndRadius" to "Yi amfani da wurina + zango",
    "pickAZone" to "Zaɓi kasa/jiha/birni maimakon",
    "radiusKm" to "Zango (km)",
    "urgentRescueAlertsTitle" to "Sanarwar Ceto na Gaggawa",
    "urgentRescuerLeaderboardTitle" to "Manyan Masu Ceto na Gaggawa (watanni 12 na karshe)",
    "locating" to "Ana neman wuri...",
    "geolocationUnsupported" to "Burauzarka ba ta tallafa wa neman wuri ba",
    "locationCaptured" to "An sami wuri",
    "locationDenied" to "Ba za mu iya samun wurinka ba - don Allah ka bar shiga wurin ko ka shigar da birni maimakon",
    "street" to "Titi",
    "exteriorNumber" to "Lambar waje",
    "referenceNotes" to "Bayanan tuntuɓa (misali, launin gida, alamar kusa)",
    "concreteAddressHint" to "Ƙara titi da lambar idan za ka iya - wannan shi ne zai bai wa mai ceto damar hawa tasi ko bude manhajar taswira kai tsaye zuwa wurin.",
    "descriptionRequired" to "Don Allah bayyana yanayin",
    "locationRequired" to "Don Allah bayar da wuri (yi amfani da wurinka na yanzu ko shigar da kasa da birni)",
    "submittingEllipsis" to "Ana gabatarwa...",
    "reportSubmitted" to "An gabatar da rahoto. Na gode - ana sanar da masu ceto kusa da ku yanzu.",
    "reportSubmitFailed" to "An kasa gabatar da rahoto",
    "phoneRequired" to "Don Allah shigar da lambar waya",
    "savingEllipsis" to "Ana ajiyewa...",
    "saved" to "An Ajiye",
    "saveFailed" to "An kasa ajiyewa",
    "noUrgentPages" to "Babu sanarwar gaggawa da ake jira a yanzu",
    "accept" to "Amince",
    "alreadyAccepted" to "Wani ya riga ya amince da wannan rahoton",
    "noLeaderboardData" to "Babu ceto da aka amince a watanni 12 na karshe tukuna",
    "rescueAcceptedTitle" to "An Amince da Ceto",
    "rescueAcceptedMessage" to "Na gode - kana kansa. Je zuwa wurin da aka bayar da rahoto da wuri-wuri.",
    "rescueAcceptFailedTitle" to "An Kasa Amincewa",
    "rescueAcceptFailedMessage" to "Wani mai ceto ya riga ya amince da wannan rahoton, ko hanyar ba ta da inganci ko ta kare.",
    "share" to "Raba",
    "shareViaFacebook" to "Raba akan Facebook",
    "shareViaX" to "Raba akan X",
    "shareViaWhatsapp" to "Raba akan WhatsApp",
    "shareViaEmail" to "Raba ta Imel",
    "copyLink" to "Kwafi Hanyar Haɗi",
    "linkCopied" to "An kwafi hanyar haɗi!",
    "reportLostFoundTitle" to "Bayar da Rahoton Dabba da Ta Bace ko Aka Samu",
    "reportLostFoundIntro" to "Dabbarka ta bace, ko ka sami wata dabbar da ta bata? Gabatar da rahoto kuma za mu duba shi da sauran rahotannin kusa da ku.",
    "browseLostFound" to "Bincika rahotannin dabbobin da suka bace ko aka samu",
    "kindLost" to "Dabbata ta bace",
    "kindFound" to "Na sami dabba",
    "petType" to "Nau'in dabba",
    "petTypeUnknown" to "Ban tabbata ba",
    "lostFoundBrowseTitle" to "Dabbobin da Suka Bace da aka Samu",
    "showing" to "Ana nunawa",
    "kindLostPlural" to "Dabbobin da suka bace",
    "kindFoundPlural" to "Dabbobin da aka samu",
    "reportResolvedTitle" to "An Warware Rahoto",
    "reportResolvedMessage" to "Na gode da sabuntawa - wannan rahoton yanzu an rufe shi.",
    "reportResolveFailedTitle" to "An Kasa Warwarewa",
    "reportResolveFailedMessage" to "An riga an warware wannan rahoton, ko hanyar ba ta da inganci ko ta kare.",
    "reportLostFound" to "Bace/\u200BAka Samu",
    "noLostFoundReportsFound" to "Ba a sami rahotannin dabbobin da suka bace ko aka samu ba",
    "errorLoadingReports" to "Kuskure wajen loda rahotanni",
    "yourEmail" to "Imel dinka",
    "contactReporter" to "Tuntuɓi wanda ya bayar da rahoto",
    "contactFormIncomplete" to "Don Allah shigar da imel dinka da sako",
    "contactSent" to "An aika sako",
    "contactFailed" to "An kasa aika sako",
    "lostFoundReportSubmitted" to "An gabatar da rahoto. Na gode - za mu duba shi da sauran rahotannin kusa da ku.",
    "video" to "Bidiyo (na zaɓi)",
    "removeVideo" to "Cire bidiyo",
    "saveThisSearch" to "Ajiye wannan binciken",
    "savedSearchSaved" to "An ajiye bincike! Za mu aika maka imel idan an jera sabon abin da ya dace.",
    "savedSearchFailed" to "An kasa ajiye bincike",
    "favorite" to "Abin so",
    "addToFavorites" to "Ƙara zuwa Abubuwan So",
    "removeFromFavorites" to "Cire daga Abubuwan So",
    "myFavorites" to "Abubuwan So Na",
    "noFavoritesYet" to "Babu abubuwan so tukuna",
    "mySavedSearches" to "Bincikena da Aka Ajiye",
    "noSavedSearchesYet" to "Babu bincike da aka ajiye tukuna",
    "donationPromptText" to "Na gode da ka taimaka wa dabba yau. Idan za ka iya, ka yi la'akari da bayar da karamin gudummawa domin Adopt-U ta ci gaba da zama kyauta ga kowa.",
    "donationPromptCta" to "Ba da Gudummawa",
    "dismiss" to "Kore",
)

private val turkishTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Giriş Yap",
    "register" to "Kayıt Ol",
    "logout" to "Oturumu Kapat",
    "profile" to "Profil",
    "myPets" to "Evcil Hayvanlarım",
    "addPet" to "Evcil Hayvan Ekle",
    "save" to "Kaydet",
    "cancel" to "İptal",
    "search" to "Ara",
    "shelters" to "Barınaklar",
    "photographers" to "Fotoğrafçılar",
    "temporalHomes" to "Geçici Yuvalar",
    "findTemporalHomes" to "Geçici Yuva Bul",
    "sterilizationLocations" to "Kısırlaştırma Merkezleri",
    "donate" to "Bağış Yap",
    "countryLabel" to "Ülke",
    "selectCountryToSearch" to "Bir ülke seçin",
    "selectCountryFirst" to "Bu filtreleri etkinleştirmek için bir ülke seçin",
    "state" to "Eyalet/İl",
    "city" to "Şehir",
    "zipCode" to "Posta Kodu",
    "neighborhood" to "Mahalle",
    "name" to "Ad",
    "email" to "E-posta",
    "phone" to "Telefon",
    "address" to "Adres",
    "description" to "Açıklama",
    "animalShelters" to "Hayvan Barınakları",
    "sheltersDescription" to "Evlat edinmek için hayvan bulabileceğiniz veya barınakla önceden anlaşarak hayvan bırakabileceğiniz hayvan barınaklarını bulun",
    "photographerDescription" to "Evcil hayvan fotoğraf çekimleri sunan profesyonel fotoğrafçılar",
    "sterilizationLocationsDescription" to "Hayvanları kısırlaştırma için götürebileceğiniz yerleri bulun.",
    "privacyPolicy" to "Gizlilik Politikası",
    "privacyLastUpdated" to "Son güncelleme: Nisan 2025",
    "privacyHighlight" to "Adopt-U olarak, minimum veri toplamaya inanıyoruz. Evlat edinenler ile kurtarıcılar arasında iletişimi sağlamak için yalnızca e-posta adresinizi topluyoruz. Bilgilerinizi reklam amacıyla kullanmıyoruz ve verilerinizi pazarlama amacıyla asla üçüncü taraflarla paylaşmayacağız.",
    "privacyInfoCollectedH2" to "Topladığımız Bilgiler",
    "privacyAccountRegH3" to "Hesap Kaydı",
    "privacyAccountRegIntro" to "Adopt-U üzerinde bir hesap oluşturmak için aşağıdaki bilgileri topluyoruz:",
    "privacyEmailLabel" to "E-posta adresi: ",
    "privacyEmailDesc" to "Hesap doğrulama, şifre kurtarma ve evlat edinenler ile kurtarıcılar arasındaki iletişim için gereklidir",
    "privacyDisplayNameLabel" to "Görünen ad: ",
    "privacyDisplayNameDesc" to "Platformda tanınmak için seçtiğiniz bir ad",
    "privacyLangPrefLabel" to "Dil tercihi: ",
    "privacyLangPrefDesc" to "Platformu tercih ettiğiniz dilde sunmak için",
    "privacyNoFullNameEtc" to "Tam adınızı, telefon numaranızı, fiziksel adresinizi, kimlik belgelerinizi, ödeme bilgilerinizi veya yukarıda belirtilenlerin dışında herhangi bir kişisel veriyi toplamıyoruz.",
    "privacyOptionalProfileH3" to "İsteğe Bağlı Profil Bilgileri",
    "privacyOptionalProfileIntro" to "Rolünüze bağlı olarak, isteğe bağlı olarak şunları sağlayabilirsiniz:",
    "privacyAdopterLabel" to "Evlat Edinen olarak: ",
    "privacyAdopterDesc" to "Ek bilgi gerekmez veya toplanmaz",
    "privacyRescuerLabel" to "Kurtarıcı olarak: ",
    "privacyRescuerDesc" to "Evlat edindirmeye hazır olan evcil hayvanlarınız için konum bilgisi",
    "privacyPhotographerLabel" to "Fotoğrafçı olarak: ",
    "privacyPhotographerDesc" to "Hizmet konumu ve iletişim tercihleri",
    "privacyTemporalHomeLabel" to "Geçici Yuva olarak: ",
    "privacyTemporalHomeDesc" to "Geçici barınma sağlayabileceğiniz konum",
    "privacyLocationUsage" to "Tüm konum bilgileri yalnızca evlat edinenleri yakındaki kurtarıcılar ve hizmet sağlayıcılarla eşleştirmek için kullanılır.",
    "privacyPetInfoH3" to "Evcil Hayvan Bilgileri",
    "privacyPetInfoIntro" to "Kurtarıcılar, evcil hayvanlar hakkında aşağıdakiler dahil bilgiler paylaşabilir:",
    "privacyPetPhotos" to "Evcil hayvanın fotoğrafları",
    "privacyPetDescBreedAge" to "Açıklama, cins, yaş ve özellikler",
    "privacyPetHealth" to "Sağlık ve aşı durumu",
    "privacyPetLocation" to "Evcil hayvanın bulunduğu konum",
    "privacyPetInfoNotVerified" to "Bu bilgiler kurtarıcılar tarafından sağlanır ve Adopt-U tarafından bağımsız olarak doğrulanmaz.",
    "privacyHowWeUseH2" to "Bilgilerinizi Nasıl Kullanıyoruz",
    "privacyPrimaryPurposeH3" to "Temel Amaç: Evlat Edindirme Bağlantılarını Kolaylaştırmak",
    "privacyEmailUsedFor" to "E-posta adresiniz yalnızca aşağıdaki amaçlar için kullanılır:",
    "privacyUseVerification" to "Hesap oluşturduğunuzda size bir doğrulama bağlantısı göndermek",
    "privacyUseRescuerContact" to "Kurtarıcıların ilgilendiğiniz evcil hayvanlar hakkında sizinle iletişime geçmesine izin vermek",
    "privacyUseContactRescuer" to "İlan verdikleri evcil hayvanlar hakkında kurtarıcılarla iletişime geçmenize izin vermek",
    "privacyUseNotifications" to "Size önemli hesap ve güvenlik bildirimleri göndermek",
    "privacyOnceConnected" to "Platformumuz aracılığıyla başka bir kullanıcıyla bağlantı kurduğunuzda, sonraki tüm iletişim doğrudan sizinle o kişi arasında gerçekleşir. İletişimlerinizin içeriğini izlemiyor, saklamıyor veya bu içeriğe erişimimiz bulunmuyor.",
    "privacyNeverDoH3" to "Asla Yapmayacaklarımız",
    "privacyCommitIntro" to "Aşağıdakileri kesin olarak taahhüt ediyoruz:",
    "privacyNoAdsLabel" to "Reklam yok: ",
    "privacyNoAdsDesc" to "Etkinliğinize dayalı hedefli veya hedefsiz reklamları asla göstermeyeceğiz",
    "privacyNoMarketingLabel" to "Pazarlama yok: ",
    "privacyNoMarketingDesc" to "E-postanızı pazarlama veya tanıtım amacıyla asla üçüncü taraflarla paylaşmayacağız",
    "privacyNoSellingLabel" to "Veri satışı yok: ",
    "privacyNoSellingDesc" to "Kişisel bilgilerinizi asla üçüncü bir tarafa satmayacak, kiralamayacak veya başka bir şekilde devretmeyeceğiz",
    "privacyNoProfilingLabel" to "Profilleme yok: ",
    "privacyNoProfilingDesc" to "Verilerinizi temel hesap yönetimi dışında herhangi bir amaçla profil oluşturmak için kullanmayacağız",
    "privacyNoTrackingLabel" to "Takip yok: ",
    "privacyNoTrackingDesc" to "Takip pikselleri, reklam amaçlı çerezler veya benzeri takip teknolojileri kullanmıyoruz",
    "privacyDataStorageH2" to "Veri Depolama ve Güvenlik",
    "privacyDataStoredSecurely" to "Verileriniz, endüstri standardı şifreleme kullanılarak güvenli bir şekilde saklanır. Kişisel bilgilerinizi yetkisiz erişime, değişikliğe, ifşaya veya imhaya karşı korumak için uygun teknik ve organizasyonel önlemler alıyoruz.",
    "privacyDataRetention" to "Hesap bilgilerinizi hesabınız aktif kaldığı sürece saklarız. Bizimle iletişime geçerek istediğiniz zaman hesabınızın ve ilişkili verilerin silinmesini talep edebilirsiniz.",
    "privacyYourRightsH2" to "Haklarınız",
    "privacyRightsIntro" to "Kişisel verilerinizle ilgili olarak aşağıdaki haklara sahipsiniz:",
    "privacyRightAccess" to "Erişim: Hakkınızda tuttuğumuz tüm kişisel verilerin bir kopyasını talep edebilirsiniz",
    "privacyRightCorrection" to "Düzeltme: Herhangi bir yanlış bilgiyi düzeltmemizi talep edebilirsiniz",
    "privacyRightDeletion" to "Silme: Hesabınızın ve ilişkili tüm verilerin silinmesini talep edebilirsiniz",
    "privacyRightPortability" to "Taşınabilirlik: Verilerinizi yaygın olarak kullanılan bir formatta sağlamamızı talep edebilirsiniz",
    "privacyExerciseRights" to "Bu haklardan herhangi birini kullanmak için lütfen adopt-u@adopt-u.org adresinden bizimle iletişime geçin.",
    "privacyCookiesH2" to "Çerezler",
    "privacyCookiesText" to "Adopt-U, oturum yönetimi ve dil tercihleri dahil olmak üzere platform işlevselliği için gerekli minimum düzeyde çerez kullanır. Reklam çerezleri veya takip çerezleri kullanmıyoruz.",
    "privacyThirdPartyH2" to "Üçüncü Taraf Hizmetleri",
    "privacyThirdPartyIntro" to "Verilerinizi işleyebilecek aşağıdaki üçüncü taraf hizmetlerini kullanıyoruz:",
    "privacyAws" to "AWS (Amazon Web Services): Veri depolama ve e-posta gönderimi için. Verilerinizin işlenmesine kendi gizlilik politikaları uygulanır.",
    "privacyWebauthn" to "WebAuthn/FIDO2: Güvenli passkey kimlik doğrulaması için. Bu, tarayıcınızda kriptografik işlemler içerir.",
    "privacyThirdPartySelected" to "Veri gizliliğine olan bağlılığımızı paylaşan hizmet sağlayıcıları özenle seçtik.",
    "privacyChildrensH2" to "Çocukların Gizliliği",
    "privacyChildrensText" to "Adopt-U, 18 yaşın altındaki kullanıcılar için tasarlanmamıştır. Reşit olmayanlardan bilerek bilgi toplamıyoruz. Reşit olmayan birinin hesap oluşturduğunu düşünüyorsanız, kaldırılması için lütfen bizimle iletişime geçin.",
    "privacyChangesH2" to "Bu Politikadaki Değişiklikler",
    "privacyChangesText" to "Uygulamalarımızdaki değişiklikleri veya yasal gereklilikleri yansıtmak için bu Gizlilik Politikasını zaman zaman güncelleyebiliriz. Önemli değişiklikleri kullanıcılara e-posta yoluyla veya platformda göze çarpan bir bildirimle duyuracağız.",
    "privacyContactH2" to "Bize Ulaşın",
    "privacyContactText" to "Bu Gizlilik Politikası hakkında herhangi bir sorunuz veya endişeniz varsa ya da veri haklarınızı kullanmak istiyorsanız, lütfen adopt-u@adopt-u.org adresinden bizimle iletişime geçin.",
    "termsConditions" to "Şartlar ve Koşullar",
    "installApp" to "Uygulamayı Yükle",
    "termsAcceptanceH2" to "Şartların Kabulü",
    "termsAcceptanceText" to "Adopt-U'ya erişerek ve kullanarak, bu sözleşmenin şart ve hükümlerine bağlı kalmayı kabul ve taahhüt edersiniz. Bu şartları kabul etmiyorsanız, lütfen platformumuzu kullanmayın.",
    "termsNatureH2" to "Hizmetimizin Niteliği",
    "termsNatureText1" to "Adopt-U, evcil hayvan kurtarıcıları ile potansiyel evlat edinenler arasında iletişimi kolaylaştıran bir bağlantı platformudur. Bir evcil hayvan mağazası, aracı, barınak veya evlat edindirme kurumu değiliz. Platformumuzda listelenen hiçbir evcil hayvanın sahibi, gözetimcisi veya aracısı değiliz.",
    "termsNatureText2" to "Rolümüz, taraflar arasında bir iletişim kanalı sağlamakla kesinlikle sınırlıdır. Evlat edindirme müzakerelerine, sözleşmelere veya hayvanların fiziksel transferine katılmıyoruz.",
    "termsUserRespH2" to "Kullanıcı Sorumlulukları",
    "termsForRescuersH3" to "Kurtarıcılar için:",
    "termsRescuerResp1" to "Bakımınızdaki evcil hayvanlar hakkında doğru ve gerçek bilgiler sağlayın",
    "termsRescuerResp2" to "İlan vermeden önce evcil hayvanların sağlıklı, aşılı ve uygun şekilde kontrolden geçmiş olduğundan emin olun",
    "termsRescuerResp3" to "Potansiyel evlat edinenler için sorumlu bir tarama yapın",
    "termsRescuerResp4" to "Evcil hayvanların yeni yuvalarına güvenli bir şekilde teslimini organize edin",
    "termsRescuerResp5" to "Sağlanan tüm bilgilerin doğruluğu için sorumluluk kabul edin",
    "termsRescuerResp6" to "Evcil hayvan evlat edindirmeyle ilgili tüm yerel yasa ve düzenlemelere uyun",
    "termsRescuerResp7" to "Platformu asla ticari evcil hayvan satışı veya üretim amacıyla kullanmayın",
    "termsForAdoptersH3" to "Evlat Edinenler için:",
    "termsAdopterResp1" to "Yaşam koşullarınız ve evcil hayvan deneyiminiz hakkında doğru bilgi sağlayın",
    "termsAdopterResp2" to "Uygun bakım sağlama yeteneğinizi gösterebilmeye hazır olun",
    "termsAdopterResp3" to "Bir evcil hayvanın evlat edindirilmesinden vazgeçmenin hayvana ciddi stres yarattığını anlayın",
    "termsAdopterResp4" to "Kurtarıcının evlat edindirme talebinizi reddetme hakkına saygı gösterin",
    "termsPetListingsH2" to "Evcil Hayvan İlanları ve Bilgiler",
    "termsPetListingsText1" to "Adopt-U, evcil hayvan ilanlarında sağlanan bilgilerin doğruluğunu doğrulamaz, onaylamaz veya garanti etmez. Cins, yaş, sağlık durumu, mizaç ve aşı kayıtları dahil ancak bunlarla sınırlı olmayan bilgiler kurtarıcılar tarafından sağlanır ve bağımsız olarak doğrulanmamıştır.",
    "termsPetListingsText2" to "Fotoğraflar ve açıklamalar kurtarıcılar tarafından sağlanır. Kasıtlı yanlış beyanı yasaklasak da, tüm ilanların hayvanın mevcut durumunu doğru şekilde yansıttığını garanti edemeyiz.",
    "termsPetListingsText3" to "Kullanıcıların, herhangi bir evlat edindirme düzenlemesini sonuçlandırmadan önce soru sormaları, veteriner kayıtları istemeleri ve evcil hayvanlarla tanışmaları teşvik edilir.",
    "termsAdoptionProcessH2" to "Evlat Edindirme Süreci",
    "termsAdoptionProcessIntro" to "Bir kurtarıcı ve evlat edinen bir evlat edindirmeye devam etmeyi kabul ettiğinde:",
    "termsAdoptionStep1" to "Transfer tarihi, konumu ve ilgili maliyetler dahil tüm düzenlemeler doğrudan taraflar arasında müzakere edilir",
    "termsAdoptionStep2" to "Adopt-U, herhangi bir ödeme işlemini kolaylaştırmaz veya garanti etmez",
    "termsAdoptionStep3" to "Platform, evlat edindirme sözleşmeleri sağlamaz; her türlü sözleşmeye dayalı düzenleme yalnızca ilgili tarafların sorumluluğundadır",
    "termsAdoptionStep4" to "Tüm anlaşmaları yazılı olarak belgelemenizi şiddetle tavsiye ederiz",
    "termsLiabilityH2" to "Sorumluluk Sınırlaması",
    "termsLiabilityIntro" to "Adopt-U aşağıdakilerden sorumlu tutulamaz:",
    "termsLiability1" to "Kurtarıcılar ve evlat edinenler arasındaki her türlü anlaşmazlık, uyuşmazlık veya çatışma",
    "termsLiability2" to "Evcil hayvan ilanlarında sağlanan bilgilerin doğruluğu",
    "termsLiability3" to "Herhangi bir hayvanın evlat edindirme öncesinde, sırasında veya sonrasındaki sağlığı, davranışı veya durumu",
    "termsLiability4" to "Platformun kullanımından kaynaklanan her türlü mali kayıp, zarar veya yaralanma",
    "termsLiability5" to "İadeler, sağlık sorunları veya uyumluluk sorunları dahil ancak bunlarla sınırlı olmayan her türlü evlat edindirme sonucu",
    "termsLiability6" to "İletişim kurulduktan sonra kullanıcıların platform dışında gerçekleştirdiği her türlü eylem",
    "termsLiabilityAck" to "Kullanıcılar, bir evcil hayvan evlat edinmenin önemli bir taahhüt olduğunu ve evlat edinme kararlarının tüm sorumluluğunu üstlendiklerini kabul ederler.",
    "termsProhibitedH2" to "Yasaklanmış Faaliyetler",
    "termsProhibitedIntro" to "Aşağıdaki faaliyetler Adopt-U'da kesinlikle yasaktır:",
    "termsProhibited1" to "Ticari evcil hayvan satışı, üretim faaliyetleri veya yavru fabrikaları",
    "termsProhibited2" to "Doğrudan bakmadığınız evcil hayvanları ilana koymak",
    "termsProhibited3" to "İlanlarda veya profillerde yanlış veya yanıltıcı bilgi",
    "termsProhibited4" to "Diğer kullanıcılara taciz, ayrımcılık veya uygunsuz temas",
    "termsProhibited5" to "Platformu, evcil hayvan evlat edindirmeyle ilgisi olmayan amaçlarla kişisel bilgi toplamak için kullanmak",
    "termsProhibited6" to "Her türlü yasa dışı faaliyet veya hayvan refahı yasalarının ihlali",
    "termsAccountTerminationH2" to "Hesap Feshi",
    "termsAccountTerminationText" to "Adopt-U, bu şartları ihlal eden, yasaklanmış faaliyetlerde bulunan veya platformu itibarsızlaştıran hesapları askıya alma veya feshetme hakkını saklı tutar. Ayrıca yanlış, yanıltıcı veya uygunsuz içerik barındıran ilanları da kaldırabiliriz.",
    "termsModificationsH2" to "Şartlarda Değişiklikler",
    "termsModificationsText" to "Bu şartları herhangi bir zamanda değiştirme hakkını saklı tutarız. Değişikliklerden sonra platformun kullanılmaya devam edilmesi, değiştirilmiş şartların kabulü anlamına gelir. Önemli değişiklikleri kullanıcılara e-posta veya platform duyuruları yoluyla bildireceğiz.",
    "termsContactText" to "Bu Şartlar ve Koşullar hakkında herhangi bir sorunuz varsa, lütfen adopt-u@adopt-u.org adresinden bizimle iletişime geçin.",
    "language" to "Dil",
    "displayName" to "Görünen Ad",
    "saveProfile" to "Profili Kaydet",
    "accountSettings" to "Hesap Ayarları",
    "yourRoles" to "Rolleriniz:",
    "resources" to "Kaynaklar",
    "loginWithPasskey" to "Passkey ile Giriş Yap",
    "signInWithPasskey" to "Passkey ile Oturum Aç",
    "resendVerificationEmail" to "Doğrulama E-postasını Yeniden Gönder",
    "usesFido" to "Güvenli parolasız kimlik doğrulama için FIDO2 / WebAuthn kullanır.",
    "loginWithMagicLink" to "E-posta Bağlantısıyla Giriş Yap",
    "magicLinkDesc" to "Size e-postanıza bir giriş bağlantısı göndereceğiz (5 dakika geçerlidir).",
    "sendMagicLink" to "Giriş Bağlantısı Gönder",
    "loginWithPassword" to "Şifre ile Giriş Yap",
    "signIn" to "Giriş Yap",
    "forgotPassword" to "Şifrenizi mi Unuttunuz?",
    "registerNewAccount" to "Hesap Oluştur",
    "accountDetails" to "Hesap Bilgileri",
    "registrationMethod" to "Kayıt Yöntemi",
    "selectAdditionalRoles" to "Ek roller seçin:",
    "adoptPet" to "Evcil hayvan evlat edin",
    "publishPets" to "Evlat edindirmek için evcil hayvan yayınla",
    "offerPhotography" to "Fotoğrafçılık hizmeti sun",
    "photographerSettings" to "Fotoğrafçı Ayarları",
    "sessionFee" to "Çekim Ücreti",
    "provideTemporaryHome" to "Evcil hayvanlar için geçici yuva sağla",
    "passkeyNoPassword" to "Passkey'ler şifrelerden daha güvenlidir ve tüm cihazlarınızda çalışır.",
    "registerWithPasskey" to "Passkey ile Kaydol",
    "passkeySettings" to "Passkey Ayarları",
    "password" to "Şifre",
    "confirmPassword" to "Şifreyi Onayla",
    "passwordRequirements" to "Şifre en az 8 karakter olmalı ve büyük harf, küçük harf, rakam ve sembol içermelidir",
    "securityInfo" to "Güvenlik Bilgisi",
    "passkeyMostSecure" to "Passkey: En güvenli, önerilir",
    "passkeyBrowserCompat" to "Passkey tüm tarayıcılarda çalışmayabilir",
    "passwordLeastSecure" to "Şifre: Daha az güvenli, önerilmez",
    "selectLoginMethods" to "Bir veya daha fazla giriş yöntemi seçin:",
    "registerPasskeyOption" to "Passkey (en güvenli, tüm cihazlarınızda çalışır)",
    "registerPasswordOption" to "Şifre (daha az güvenli, yalnızca yedek olarak kullanın)",
    "howPasskeysWorkTitle" to "Passkey nasıl çalışır?",
    "howPasskeysWorkIntro" to "Bir passkey, şifrenizin yerini cihazınızın yerleşik güvenlik özelliğiyle alır - parmak izi, yüz tanıma veya ekran kilidi PIN'i.",
    "howPasskeysWorkSecurity" to "Yalnızca cihazınızda saklanır, kimlik avıyla ele geçirilemez veya bir veri ihlalinde sızdırılamaz ve cihazlarınız arasında otomatik olarak senkronize edilir.",
    "howPasskeysWorkBackup" to "Bir şifre de ekleyebilirsiniz, ancak giriş yapmak için tek başına bir passkey yeterlidir - şifreye gerek yoktur.",
    "gotIt" to "Anladım",
    "alreadyHaveAccount" to "Zaten bir hesabınız var mı?",
    "all" to "Tümü",
    "filterDogs" to "Köpekler",
    "filterCats" to "Kediler",
    "filterBirds" to "Kuşlar",
    "filterFish" to "Balıklar",
    "filterAllSexOption" to "Tüm Cinsiyetler",
    "filterMaleOption" to "♂ Erkek",
    "filterFemaleOption" to "♀ Dişi",
    "age" to "Yaş",
    "rescued" to "Kurtarıldı",
    "years" to "yıl",
    "months" to "ay",
    "year" to "yıl",
    "month" to "ay",
    "loginToAdopt" to "Sahiplenme talebi için giriş yapın",
    "requiredMarker" to "(gerekli)",
    "pageNotFoundTitle" to "Sayfa bulunamadı",
    "pageNotFoundHint" to "Bağlantı eski veya hatalı yazılmış olabilir. Hayvanlar hâlâ burada.",
    "browsePets" to "Hayvanlara göz at",
    "requestsFromRescuers" to "Kurtarıcılardan gelen talepler",
    "signingOut" to "Çıkış yapılıyor...",
    "adopter" to "Evlat edinen",
    "country" to "Ülke",
    "zip" to "Posta kodu",
    "viewPublic" to "Herkese açık sayfayı gör",
    "banUser" to "Kullanıcıyı yasakla",
    "banReason" to "Gerekçe (isteğe bağlı)",
    "myPhotoRequests" to "Fotoğraf çekimi taleplerim",
    "noPhotoRequestsYet" to "Henüz fotoğraf çekimi talep etmediniz.",
    "photoRequestsReceived" to "Alınan fotoğraf çekimi talepleri",
    "noPhotoRequestsReceived" to "Henüz fotoğraf çekimi talebi yok.",
    "photoStatusPending" to "Beklemede",
    "photoStatusApproved" to "Onaylandı",
    "photoStatusRejected" to "Reddedildi",
    "photoStatusCancelled" to "İptal edildi",
    "photoStatusCompleted" to "Tamamlandı",
    "cancelRequest" to "Talebi iptal et",
    "registerConsentPrefix" to "Hesap oluşturarak şunları kabul etmiş olursunuz:",
    "registerConsentTerms" to "Şartlar ve Koşullar",
    "registerConsentAnd" to "ve",
    "registerConsentPrivacy" to "Gizlilik Politikası",
    "petStatusAvailable" to "Mevcut",
    "petStatusAdopted" to "Evlat Edinildi",
    "petStatusDisabled" to "Devre Dışı",
    "petStatusPending" to "Beklemede",
    "addEditLocation" to "Konum Ekle/Düzenle",
    "addLocation" to "Konum Ekle",
    "manage" to "Yönet",
    "adminPanel" to "Yönetici Paneli",
    "adminSterilizationLocations" to "Kısırlaştırma Merkezlerini Yönet",
    "adopterRequired" to "Evlat Edinen (gerekli)",
    "adoptionFee" to "Evlat Edindirme Ücreti",
    "adoptionRequests" to "Evlat Edindirme Talepleri",
    "ageMonths" to "Yaş (ay)",
    "ageYears" to "Yaş (yıl)",
    "alias" to "Takma Ad",
    "bird" to "Kuş",
    "breed" to "Cins",
    "medicalEventsOverview" to "Tıbbi Olaylar Özeti",
    "back" to "Geri",
    "cat" to "Kedi",
    "color" to "Renk",
    "compatibility" to "Uyumluluk",
    "currentPassword" to "Mevcut Şifre",
    "dog" to "Köpek",
    "emailChange" to "E-postayı Değiştir",
    "emailChangeDesc" to "E-posta adresinizi güncelleyin. Yeni e-postaya bir doğrulama bağlantısı gönderilecektir.",
    "energyLevel" to "Enerji Seviyesi",
    "female" to "Dişi",
    "findTemporalHome" to "Geçici Yuva Bul",
    "fish" to "Balık",
    "goodWithCats" to "Kedilerle iyi geçinir",
    "goodWithDogs" to "Köpeklerle iyi geçinir",
    "goodWithKids" to "Çocuklarla iyi geçinir",
    "high" to "Yüksek",
    "houseTrained" to "Ev eğitimi almış",
    "large" to "Büyük",
    "low" to "Düşük",
    "male" to "Erkek",
    "managePets" to "Evcil Hayvanları Yönet",
    "managePetsBtn" to "Evcil Hayvan Ekle veya Düzenle",
    "managePetsDescription" to "Tüm evcil hayvan sayfalarını yönetin. Evcil hayvan ekleyin veya kaldırın.",
    "manageShelters" to "Barınakları Yönet",
    "manageSheltersBtn" to "Barınak Ekle veya Düzenle",
    "manageSheltersDescription" to "Tüm barınak sayfalarını yönetin. Barınak ekleyin veya kaldırın.",
    "manageUsers" to "Kullanıcıları Yönet",
    "medical" to "Tıbbi",
    "medium" to "Orta",
    "backToSearch" to "Aramaya dön",
    "contactThisHome" to "Bu Geçici Yuva ile İletişime Geç",
    "loginAsRescuerToContact" to "Bu geçici yuvaya talep göndermek için kurtarıcı olarak giriş yapın.",
    "memberSince" to "Üyelik başlangıcı",
    "microchipId" to "Mikroçip Kimliği",
    "microchipped" to "Mikroçipli",
    "myTemporalHome" to "Geçici Yuvam",
    "newEmail" to "Yeni E-posta",
    "noTemporalHomes" to "Geçici yuva bulunamadı.",
    "requestSentToHome" to "Talebiniz gönderildi!",
    "sendRequestBtn" to "Talep Gönder",
    "temporalHomeNotFound" to "Bu geçici yuva bulunamadı.",
    "viewDetails" to "Detayları Görüntüle",
    "rescuerDirectory" to "Kurtarıcılar",
    "noRescuersFound" to "Kurtarıcı bulunamadı.",
    "availablePetsCount" to "Mevcut evcil hayvanlar",
    "availablePets" to "Mevcut Evcil Hayvanlar",
    "noAvailablePetsForRescuer" to "Bu kurtarıcının şu anda mevcut evcil hayvanı yok.",
    "rescuerNotFound" to "Bu kurtarıcı bulunamadı.",
    "errorLoadingResults" to "Sonuçlar yüklenirken hata oluştu.",
    "volunteerForThisRescuer" to "Bu Kurtarıcı için Gönüllü Ol",
    "volunteerExplanation" to "Onaylanmış gönüllüler bu kurtarıcının evcil hayvan ilanlarında düzenleme önerebilir.",
    "applyToVolunteerBtn" to "Gönüllü Olmak için Başvur",
    "loginToVolunteer" to "Bu kurtarıcı için gönüllü olmaya başvurmak üzere giriş yapın.",
    "volunteerApplicationSent" to "Gönüllülük başvurunuz gönderildi!",
    "myVolunteering" to "Gönüllülüklerim",
    "noVolunteerApplicationsYet" to "Henüz hiçbir kurtarıcı için gönüllü olmaya başvurmadınız.",
    "howToVolunteerHint" to "Başvurmak için dizinden bir kurtarıcının sayfasını açın ve \"Gönüllü Olarak Başvur\"a dokunun.",
    "volunteerStatusPending" to "Beklemede",
    "volunteerStatusActive" to "Aktif",
    "volunteerStatusRejected" to "Reddedildi",
    "volunteerApplications" to "Gönüllü Başvuruları",
    "noVolunteerApplications" to "Gönüllü başvurusu yok.",
    "petEditSuggestions" to "Evcil Hayvan Düzenleme Önerileri",
    "noPetEditSuggestions" to "Bekleyen düzenleme önerisi yok.",
    "suggestedByLabel" to "öneren",
    "suggestAnEditTitle" to "Bir Düzenleme Öner",
    "suggestAnEditExplanation" to "Onaylanmış bir gönüllü olarak, bu ilanda değişiklik önerebilirsiniz. Kurtarıcı, yayınlanmadan önce inceleyecektir.",
    "suggestEditBtn" to "Öneri Gönder",
    "editSuggestionSent" to "Öneriniz inceleme için kurtarıcıya gönderildi!",
    "sponsorThisPetTitle" to "Bu Evcil Hayvana Destek Ol",
    "sponsorExplanation" to "Evlat edinemiyor ama masraflara yardımcı olmak mı istiyorsunuz? Para veya ayni destek sunun ve doğrudan kurtarıcıyla bağlantı kurun.",
    "sponsorThisRescuerTitle" to "Bu Kurtarıcıya Destek Ol",
    "sponsorRescuerExplanation" to "Bu kurtarıcının genel masraflarını para veya ayni yardımla destekleyin.",
    "sponsorTypeLabel" to "Ne sunabilirsiniz?",
    "sponsorTypeMoney" to "Para",
    "sponsorTypeInKind" to "Ayni (yiyecek, malzeme vb.)",
    "amountLabel" to "Miktar (aylık)",
    "currencyLabel" to "Para Birimi",
    "inKindDescriptionLabel" to "Sunabileceğiniz şeyi açıklayın",
    "messageLabel" to "Mesaj",
    "sendOfferBtn" to "Teklif Gönder",
    "sponsorOfferSent" to "Teklifiniz gönderildi! Kurtarıcı, ayrıntıları düzenlemek için sizinle iletişime geçecektir.",
    "sponsorshipOffersReceived" to "Destek Teklifleri",
    "noSponsorshipOffers" to "Henüz destek teklifi yok.",
    "generalFundLabel" to "Genel fon",
    "offeringLabel" to "Sunulan",
    "sponsorshipStatusSent" to "Yeni",
    "sponsorshipStatusRead" to "Okundu",
    "markAsReadBtn" to "Okundu Olarak İşaretle",
    "mySponsorshipOffers" to "Destek Tekliflerim",
    "noSponsorshipOffersYet" to "Henüz hiçbir destek teklifi göndermediniz.",
    "howToSponsorHint" to "Sponsor olmak için bir kurtarıcının sayfasını (genel destek) veya bir hayvanın sayfasını (o hayvanın masrafları) açın ve bir teklif gönderin.",
    "browsePetsLink" to "Hayvanlara göz at",
    "yourMessage" to "Mesajınız",
    "newPassword" to "Yeni Şifre",
    "passkeyDesc" to "Cihazlar arasında kolay giriş için hesabınıza ek passkey'ler ekleyin.",
    "passkeyName" to "Passkey Adı",
    "pers" to "Kişiler",
    "petFoodDescription" to "Hangi yiyeceklerin evcil hayvanlarınız için güvenli, zararlı veya toksik olduğunu öğrenin",
    "petFoodGuide" to "Evcil Hayvan Beslenme Rehberi",
    "foodInformation" to "Yiyecek Bilgisi",
    "petsForAdoption" to "Evlat Edindirilecek Evcil Hayvanlar",
    "chooseCountryPrompt" to "Yeni en iyi arkadaşınızı bulun",
    "chooseCountryPromptHint" to "Yakınınızdaki evlat edindirilecek evcil hayvanlara göz atmak için yukarıdan bir ülke seçin.",
    "noPetsFound" to "Evcil hayvan bulunamadı",
    "noPetsFoundHint" to "Farklı bir filtre deneyin veya yakında tekrar kontrol edin - düzenli olarak yeni evcil hayvanlar ekleniyor.",
    "loadingMorePets" to "Daha fazla evcil hayvan yükleniyor…",
    "photos" to "Fotoğraflar (en fazla 12)",
    "profilePhotographerRole" to "Fotoğrafçı - Fotoğrafçılık hizmeti sun",
    "profileRescuerRole" to "Kurtarıcı - Evlat edindirmek için evcil hayvan yayınla",
    "profileTemporalHomeRole" to "Geçici Yuva - Evcil hayvanlar için geçici yuva sağla",
    "provideShelter" to "Barınak - Bir hayvan barınağını yönet",
    "provideSterilization" to "Kısırlaştırma Hizmeti - Kısırlaştırma hizmetleri sun",
    "requestEmailChange" to "E-posta Değişikliği Talep Et",
    "rescueDate" to "Kurtarılma Tarihi",
    "rescueLocation" to "Kurtarılma Konumu",
    "savePassword" to "Şifreyi Kaydet",
    "selectEnergy" to "Enerji seviyesi seçin",
    "selectSize" to "Boyut seçin",
    "sex" to "Cinsiyet",
    "shelterSettings" to "Barınak Ayarları",
    "size" to "Boyut",
    "small" to "Küçük",
    "specialNeeds" to "Özel İhtiyaçlar",
    "sterilizationSettings" to "Kısırlaştırma Ayarları",
    "sterilized" to "Kısırlaştırılmış",
    "temporalHomeSettings" to "Geçici Yuva Ayarları",
    "temperament" to "Mizaç",
    "type" to "Tür *",
    "urgentAdoption" to "Acil evlat edindirme gerekiyor",
    "vaccinations" to "Aşılar",
    "vetDisclaimer" to "Beslenme değişikliği yapmadan önce veterinerinize danışın.",
    "weight" to "Ağırlık (kg)",
    "weightLabel" to "Ağırlık",
    "website" to "Web Sitesi",
    "securitySettings" to "Güvenlik Ayarları",
    "emailVerifiedTitle" to "E-posta Başarıyla Doğrulandı!",
    "emailVerifiedMessage" to "E-postanız doğrulandı. Kaydınız artık tamamlandı.",
    "redirectingIn" to "Ana sayfaya yönlendiriliyorsunuz: ",
    "canAlsoLogin" to "Ayrıca ",
    "loginNow" to "Şimdi Giriş Yap",
    "emailVerificationFailed" to "Doğrulama Başarısız",
    "invalidOrExpiredToken" to "Doğrulama bağlantısı geçersiz veya süresi dolmuş.",
    "tryRegisteringAgain" to "Lütfen tekrar kayıt olmayı deneyin.",
    "verificationEmailResent" to "Doğrulama e-postasının süresi dolmuştu. Yeni bir doğrulama e-postası gönderildi. Lütfen gelen kutunuzu kontrol edin.",
    "emailVerificationSent" to "Bir doğrulama e-postası gönderildi. Giriş yapmadan önce lütfen gelen kutunuzu kontrol edin.",
    "emailNotVerifiedLogin" to "Giriş yapmadan önce lütfen e-postanızı doğrulayın. Doğrulama bağlantısı için gelen kutunuzu kontrol edin.",
    "invalidOrExpiredMagicLink" to "Geçersiz veya süresi dolmuş sihirli bağlantı. Lütfen yeni bir tane isteyin.",
    "invalidToken" to "Geçersiz jeton.",
    "accountBanned" to "Hesabınız yasaklandı.",
    "loginError" to "Giriş hatası oluştu.",
    "profileUpdated" to "Profil güncellendi!",
    "displayNameRequired" to "Görünen ad boş olamaz",
    "saving" to "Kaydediliyor...",
    "passwordSet" to "Şifre ayarlandı",
    "noPassword" to "Şifre ayarlanmadı",
    "passwordChangeSuccess" to "Şifre başarıyla değiştirildi!",
    "passwordChangeFailed" to "Şifre değiştirilemedi",
    "passwordSetSuccess" to "Şifre başarıyla ayarlandı!",
    "passwordSetFailed" to "Şifre ayarlanamadı",
    "passwordsDoNotMatch" to "Şifreler eşleşmiyor",
    "currentPasswordRequired" to "Mevcut şifre gereklidir",
    "emailChangeSent" to "Yeni e-posta adresinize doğrulama e-postası gönderildi!",
    "emailChangeFailed" to "E-posta değişikliği talep edilemedi",
    "emailRequired" to "E-posta gereklidir",
    "sendingEllipsis" to "Gönderiliyor...",
    "resetLinkSent" to "Şifre sıfırlama bağlantısı gönderildi! E-postanızı kontrol edin.",
    "magicLinkSent" to "Giriş bağlantısı gönderildi! E-postanızı kontrol edin.",
    "magicLinkSendFailed" to "Giriş bağlantısı gönderilemedi",
    "failedSendResetLink" to "Sıfırlama bağlantısı gönderilemedi.",
    "invalidOrMissingToken" to "Geçersiz veya eksik jeton.",
    "passwordTooShort" to "Şifre en az 8 karakter olmalıdır.",
    "passwordNeedUppercase" to "Şifre en az 1 büyük harf içermelidir.",
    "passwordNeedLowercase" to "Şifre en az 1 küçük harf içermelidir.",
    "passwordNeedNumber" to "Şifre en az 1 rakam içermelidir.",
    "passwordNeedSymbol" to "Şifre en az 1 sembol içermelidir.",
    "resettingPassword" to "Şifre sıfırlanıyor...",
    "passwordResetSuccess" to "Şifre başarıyla sıfırlandı! Artık giriş yapabilirsiniz.",
    "failedResetPassword" to "Şifre sıfırlanamadı.",
    "loginSuccessRedirecting" to "Giriş başarılı! Yönlendiriliyor...",
    "loginFailedExpiredLink" to "Giriş başarısız. Bağlantı geçersiz veya süresi dolmuş olabilir.",
    "loginFailed" to "Giriş başarısız.",
    "emailChangedSuccess" to "E-posta başarıyla değiştirildi!",
    "failedChangeEmailExpired" to "E-posta değiştirilemedi. Bağlantı geçersiz veya süresi dolmuş olabilir.",
    "failedChangeEmail" to "E-posta değiştirilemedi.",
    "emailVerifiedSuccess" to "E-posta başarıyla doğrulandı!",
    "failedVerifyEmailExpired" to "E-posta doğrulanamadı. Bağlantı geçersiz veya süresi dolmuş olabilir.",
    "failedVerifyEmail" to "E-posta doğrulanamadı.",
    "dropImagesHint" to "Görselleri buraya bırakın veya göz atmak için tıklayın",
    "forgotPasswordTitle" to "Şifremi Unuttum",
    "forgotPasswordIntro" to "E-postanızı girin, size şifrenizi sıfırlamak için bir bağlantı gönderelim.",
    "sendResetLink" to "Sıfırlama Bağlantısı Gönder",
    "backToLogin" to "Girişe Dön",
    "resetPasswordTitle" to "Şifreyi Sıfırla",
    "resetPasswordIntro" to "Yeni şifrenizi aşağıya girin.",
    "emailLinkLoginTitle" to "E-posta Bağlantısıyla Giriş",
    "verifying" to "Doğrulanıyor...",
    "emailChangeTitle" to "E-posta Değişikliği",
    "verifyContactEmailTitle" to "İletişim E-postasını Doğrula",
    "status" to "Durum",
    "yes" to "Evet",
    "no" to "Hayır",
    "noDescription" to "Açıklama yok.",
    "urgentBadge" to "ACİL - Yakında yuvaya ihtiyacı var!",
    "needsNewHome" to "Bu evcil hayvanın acilen yeni bir yuvaya ihtiyacı var",
    "promotedReasonLabel" to "Sebep",
    "selectReason" to "Bir sebep seçin",
    "promotedReasonMoving" to "Sahibi taşınıyor",
    "promotedReasonComplaints" to "Şikayetler / daha fazla evcil hayvan bakamıyor",
    "promotedReasonPetConflict" to "Başka bir evcil hayvanla çatışma",
    "promotedReasonOther" to "Diğer",
    "promotedReasonDetailLabel" to "Ek ayrıntılar (isteğe bağlı)",
    "promotedReasonRequired" to "Lütfen bir sebep seçin",
    "needsNewHomeBadge" to "Yeni yuvaya ihtiyacı var",
    "housingType" to "Konut türü",
    "preferNotToSay" to "Belirtmek istemiyorum",
    "houseHousing" to "Ev",
    "apartmentHousing" to "Apartman",
    "hasYard" to "Bahçesi var",
    "hasOtherPets" to "Evde başka evcil hayvanlar var",
    "adoptionExperience" to "Evlat edinme deneyimi",
    "firstTimeAdopter" to "İlk kez evlat edinen",
    "experiencedAdopter" to "Deneyimli evlat edinen",
    "noAdoptionRequests" to "Evlat edindirme talebi yok",
    "noMessage" to "Mesaj yok",
    "markUnderReview" to "İnceleniyor Olarak İşaretle",
    "approve" to "Onayla",
    "reject" to "Reddet",
    "reviewNoteLabel" to "Özel inceleme notu",
    "saveNote" to "Notu Kaydet",
    "adoptionStatusPending" to "Beklemede",
    "adoptionStatusUnderReview" to "İnceleniyor",
    "adoptionStatusApproved" to "Onaylandı",
    "adoptionStatusRejected" to "Onaylanmadı",
    "myAdoptionRequests" to "Evlat Edindirme Taleplerim",
    "noAdoptionRequestsYet" to "Henüz hiçbir evcil hayvanı evlat edinmek için talepte bulunmadınız.",
    "medicalHistory" to "Tıbbi Geçmiş",
    "medicalSchedule" to "Aşı ve Kurt İlacı Takvimi",
    "recordType" to "Tür",
    "vaccination" to "Aşı",
    "deworming" to "Kurt İlacı",
    "recordName" to "Ad",
    "dateGiven" to "Uygulama tarihi",
    "nextDueDate" to "Sonraki tarih (isteğe bağlı)",
    "notesOptional" to "Notlar (isteğe bağlı)",
    "addRecord" to "Kayıt Ekle",
    "noMedicalRecords" to "Henüz aşı veya kurt ilacı kaydı yok.",
    "nextDueLabel" to "Sonraki tarih",
    "givenLabel" to "Uygulandı",
    "overdue" to "Gecikmiş",
    "dueSoon" to "Yaklaşıyor",
    "upToDate" to "Güncel",
    "recordNameRequired" to "Ad gereklidir",
    "dateGivenRequired" to "Uygulama tarihi gereklidir",
    "failedToAddRecord" to "Kayıt eklenemedi",
    "confirmDeleteRecord" to "Bu kayıt silinsin mi?",
    "maxCapacity" to "Aynı anda maksimum evcil hayvan sayısı (isteğe bağlı)",
    "placePetHere" to "Buraya Bir Evcil Hayvan Yerleştir",
    "noAvailablePetsToPlace" to "Şu anda yerleştirebileceğiniz mevcut bir evcil hayvanınız yok.",
    "selectPet" to "Evcil hayvan seçin",
    "startPlacementBtn" to "Yerleştirmeyi Başlat",
    "placementStarted" to "Geçici bakım yerleştirmesi başlatıldı!",
    "petsCurrentlyWithMe" to "Şu Anda Yanımdaki Evcil Hayvanlar",
    "noPetsCurrentlyFostered" to "Şu anda sizinle yerleştirilmiş evcil hayvan yok.",
    "sinceLabel" to "başlangıç",
    "fosterPlacement" to "Geçici Bakım Yerleştirmesi",
    "endPlacement" to "Yerleştirmeyi Sonlandır",
    "currentlyWithLabel" to "Şu anda kiminle",
    "notCurrentlyFostered" to "Şu anda geçici bakımda değil.",
    "confirmEndPlacement" to "Bu geçici bakım yerleştirmesi sonlandırılsın mı? Evcil hayvan sizinle geri döndü olarak kaydedilecek.",
    "aTemporalHome" to "bir geçici yuva",
    "viewsLabel" to "Görüntülenme",
    "inquiriesLabel" to "Sorgular",
    "conversionLabel" to "Dönüşüm",
    "messageOptional" to "Mesaj (isteğe bağlı)",
    "requestAdoption" to "Evlat Edindirme Talep Et",
    "editPet" to "Evcil Hayvanı Düzenle",
    "adoptionRequestSubmitted" to "Evlat edindirme talebi gönderildi!",
    "failedSubmitRequest" to "Talep gönderilemedi",
    "noPhotosYet" to "Henüz fotoğraf yok.",
    "rabbit" to "Tavşan",
    "safeFoods" to "Güvenli Yiyecekler",
    "harmfulFoods" to "Zararlı Yiyecekler",
    "cannotEatFoods" to "Yenilemez",
    "legendSafe" to "Güvenli",
    "legendHarmful" to "Zararlı",
    "legendToxic" to "Toksik",
    "food_dog_safe_chicken_name" to "Tavuk",
    "food_dog_safe_chicken_detail" to "Pişmiş, kemiksiz",
    "food_dog_safe_chicken_desc" to "Yağsız protein, sindirimi kolay",
    "food_dog_safe_rice_name" to "Pirinç",
    "food_dog_safe_rice_detail" to "Beyaz veya esmer",
    "food_dog_safe_rice_desc" to "İyi bir karbonhidrat kaynağı",
    "food_dog_safe_carrots_name" to "Havuç",
    "food_dog_safe_carrots_detail" to "Çiğ veya pişmiş",
    "food_dog_safe_carrots_desc" to "Düşük kalorili, yüksek lifli",
    "food_dog_safe_apples_name" to "Elma",
    "food_dog_safe_apples_detail" to "Çekirdeksiz",
    "food_dog_safe_apples_desc" to "Vitamin açısından zengin",
    "food_dog_safe_peanutbutter_name" to "Fıstık Ezmesi",
    "food_dog_safe_peanutbutter_detail" to "Tuzsuz, ksilitolsuz",
    "food_dog_safe_peanutbutter_desc" to "İyi bir protein kaynağı",
    "food_dog_harmful_grapes_name" to "Üzüm",
    "food_dog_harmful_grapes_detail" to "Tüm çeşitler",
    "food_dog_harmful_grapes_desc" to "Böbrek yetmezliğine neden olabilir",
    "food_dog_harmful_onions_name" to "Soğan",
    "food_dog_harmful_onions_detail" to "Tüm formlar",
    "food_dog_harmful_onions_desc" to "Kırmızı kan hücrelerine zarar verir",
    "food_dog_harmful_chocolate_name" to "Çikolata",
    "food_dog_harmful_chocolate_detail" to "Tüm türler",
    "food_dog_harmful_chocolate_desc" to "Köpekler için toksiktir",
    "food_dog_harmful_garlic_name" to "Sarımsak",
    "food_dog_harmful_garlic_detail" to "Tüm formlar",
    "food_dog_harmful_garlic_desc" to "Anemiye neden olur",
    "food_dog_cannot_xylitol_name" to "Ksilitol",
    "food_dog_cannot_xylitol_detail" to "Yapay tatlandırıcı",
    "food_dog_cannot_xylitol_desc" to "Yüksek derecede toksik",
    "food_dog_cannot_macadamia_name" to "Makademya Fıstığı",
    "food_dog_cannot_macadamia_detail" to "Tüm formlar",
    "food_dog_cannot_macadamia_desc" to "Halsizliğe neden olur",
    "food_dog_cannot_avocado_name" to "Avokado",
    "food_dog_cannot_avocado_detail" to "Çekirdek, kabuk, hepsi",
    "food_dog_cannot_avocado_desc" to "Persin içerir",
    "food_dog_cannot_alcohol_name" to "Alkol",
    "food_dog_cannot_alcohol_detail" to "Tüm formlar",
    "food_dog_cannot_alcohol_desc" to "Yüksek derecede toksik",
    "food_cat_safe_cookedfish_name" to "Pişmiş Balık",
    "food_cat_safe_cookedfish_detail" to "Kılçıksız",
    "food_cat_safe_cookedfish_desc" to "Yüksek protein",
    "food_cat_safe_chicken_name" to "Tavuk",
    "food_cat_safe_chicken_detail" to "Pişmiş, sade",
    "food_cat_safe_chicken_desc" to "İyi bir protein kaynağı",
    "food_cat_safe_pumpkin_name" to "Balkabağı",
    "food_cat_safe_pumpkin_detail" to "Sade, pişmiş",
    "food_cat_safe_pumpkin_desc" to "Sindirime yardımcı olur",
    "food_cat_safe_eggs_name" to "Yumurta",
    "food_cat_safe_eggs_detail" to "Pişmiş",
    "food_cat_safe_eggs_desc" to "Tam protein",
    "food_cat_harmful_raweggs_name" to "Çiğ Yumurta",
    "food_cat_harmful_raweggs_detail" to "Avidin içerir",
    "food_cat_harmful_raweggs_desc" to "Biyotini engeller",
    "food_cat_harmful_rawfish_name" to "Çiğ Balık",
    "food_cat_harmful_rawfish_detail" to "Tiaminaz içerir",
    "food_cat_harmful_rawfish_desc" to "B vitaminlerini parçalar",
    "food_cat_harmful_dogfood_name" to "Köpek Maması",
    "food_cat_harmful_dogfood_detail" to "Herhangi biri",
    "food_cat_harmful_dogfood_desc" to "Taurin eksikliği vardır",
    "food_cat_harmful_milk_name" to "Süt",
    "food_cat_harmful_milk_detail" to "Çoğu kedi",
    "food_cat_harmful_milk_desc" to "Laktoz intoleransı",
    "food_cat_cannot_chocolate_name" to "Çikolata",
    "food_cat_cannot_chocolate_detail" to "Tüm türler",
    "food_cat_cannot_chocolate_desc" to "Teobromin toksiktir",
    "food_cat_cannot_onionsgarlic_name" to "Soğan/Sarımsak",
    "food_cat_cannot_onionsgarlic_detail" to "Tüm formlar",
    "food_cat_cannot_onionsgarlic_desc" to "Kırmızı kan hücrelerine zarar verir",
    "food_cat_cannot_grapesraisins_name" to "Üzüm/Kuru Üzüm",
    "food_cat_cannot_grapesraisins_detail" to "Tüm çeşitler",
    "food_cat_cannot_grapesraisins_desc" to "Böbrek hasarı",
    "food_cat_cannot_alcohol_name" to "Alkol",
    "food_cat_cannot_alcohol_detail" to "Tüm formlar",
    "food_cat_cannot_alcohol_desc" to "Çok toksik",
    "food_bird_safe_seeds_name" to "Tohumlar",
    "food_bird_safe_seeds_detail" to "Çeşit karışımı",
    "food_bird_safe_seeds_desc" to "İyi bir yağ kaynağı",
    "food_bird_safe_fruits_name" to "Meyveler",
    "food_bird_safe_fruits_detail" to "Çoğu çeşit",
    "food_bird_safe_fruits_desc" to "Vitamin ve mineraller",
    "food_bird_safe_vegetables_name" to "Sebzeler",
    "food_bird_safe_vegetables_detail" to "Yapraklı yeşillikler",
    "food_bird_safe_vegetables_desc" to "Temel besinler",
    "food_bird_safe_pellets_name" to "Pelet",
    "food_bird_safe_pellets_detail" to "Kaliteli formülasyon",
    "food_bird_safe_pellets_desc" to "Tam beslenme",
    "food_bird_harmful_avocado_name" to "Avokado",
    "food_bird_harmful_avocado_detail" to "Tüm kısımlar",
    "food_bird_harmful_avocado_desc" to "Persin toksiktir",
    "food_bird_harmful_fruitpits_name" to "Meyve Çekirdekleri",
    "food_bird_harmful_fruitpits_detail" to "Elma, şeftali",
    "food_bird_harmful_fruitpits_desc" to "İz miktarda siyanür",
    "food_bird_harmful_salt_name" to "Tuz",
    "food_bird_harmful_salt_detail" to "Her türlü form",
    "food_bird_harmful_salt_desc" to "Kuşlar için toksiktir",
    "food_bird_harmful_caffeine_name" to "Kafein",
    "food_bird_harmful_caffeine_detail" to "Kahve, çay",
    "food_bird_harmful_caffeine_desc" to "Kalp sorunları",
    "food_bird_cannot_chocolate_name" to "Çikolata",
    "food_bird_cannot_chocolate_detail" to "Tüm türler",
    "food_bird_cannot_chocolate_desc" to "Teobromin toksiktir",
    "food_bird_cannot_onions_name" to "Soğan",
    "food_bird_cannot_onions_detail" to "Tüm formlar",
    "food_bird_cannot_onions_desc" to "Kırmızı kan hücrelerine zarar verir",
    "food_bird_cannot_garlic_name" to "Sarımsak",
    "food_bird_cannot_garlic_detail" to "Tüm formlar",
    "food_bird_cannot_garlic_desc" to "Toksik",
    "food_bird_cannot_mushrooms_name" to "Mantarlar",
    "food_bird_cannot_mushrooms_detail" to "Yabani çeşitler",
    "food_bird_cannot_mushrooms_desc" to "Olası toksinler",
    "food_fish_safe_flakes_name" to "Pul Yem",
    "food_fish_safe_flakes_detail" to "Kaliteli markalar",
    "food_fish_safe_flakes_desc" to "Tam balık maması",
    "food_fish_safe_pellets_name" to "Pelet",
    "food_fish_safe_pellets_detail" to "Yüzen/batan",
    "food_fish_safe_pellets_desc" to "Dengeli beslenme",
    "food_fish_safe_frozenfood_name" to "Dondurulmuş Yem",
    "food_fish_safe_frozenfood_detail" to "Kan kurdu, tuzlu su karidesi",
    "food_fish_safe_frozenfood_desc" to "Protein kaynağı",
    "food_fish_safe_vegetables_name" to "Sebzeler",
    "food_fish_safe_vegetables_detail" to "Haşlanmış kabak",
    "food_fish_safe_vegetables_desc" to "Lif kaynağı",
    "food_fish_harmful_bread_name" to "Ekmek",
    "food_fish_harmful_bread_detail" to "Her türlü",
    "food_fish_harmful_bread_desc" to "Besin değeri yok, doyurur",
    "food_fish_harmful_humanfood_name" to "İnsan Yiyeceği",
    "food_fish_harmful_humanfood_detail" to "Pişmiş yemekler",
    "food_fish_harmful_humanfood_desc" to "Yanlış beslenme",
    "food_fish_harmful_livefeed_name" to "Canlı Yem",
    "food_fish_harmful_livefeed_detail" to "Vahşi yakalanmış böcekler",
    "food_fish_harmful_livefeed_desc" to "Olası parazitler",
    "food_fish_cannot_landinsect_name" to "Kara Böceği",
    "food_fish_cannot_landinsect_detail" to "Sürünen böcekler",
    "food_fish_cannot_landinsect_desc" to "Doğal bir besin değil",
    "food_fish_cannot_mammalmeat_name" to "Memeli Eti",
    "food_fish_cannot_mammalmeat_detail" to "Tavuk, dana",
    "food_fish_cannot_mammalmeat_desc" to "Çoğu balık için yanlış",
    "food_fish_cannot_dairy_name" to "Süt Ürünleri",
    "food_fish_cannot_dairy_detail" to "Herhangi biri",
    "food_fish_cannot_dairy_desc" to "Sindiremez",
    "food_fish_cannot_breadcrumbs_name" to "Galeta Unu",
    "food_fish_cannot_breadcrumbs_detail" to "Çoğu sarımsak içerir",
    "food_fish_cannot_breadcrumbs_desc" to "Zararlı katkı maddeleri",
    "food_rabbit_safe_hay_name" to "Ot",
    "food_rabbit_safe_hay_detail" to "Timothy otu",
    "food_rabbit_safe_hay_desc" to "Beslenmenin %80'i",
    "food_rabbit_safe_leafygreens_name" to "Yapraklı Yeşillikler",
    "food_rabbit_safe_leafygreens_detail" to "Marul, kişniş",
    "food_rabbit_safe_leafygreens_desc" to "Günlük vitaminler",
    "food_rabbit_safe_pellets_name" to "Pelet",
    "food_rabbit_safe_pellets_detail" to "Timothy otu bazlı",
    "food_rabbit_safe_pellets_desc" to "Dengeli beslenme",
    "food_rabbit_safe_carrots_name" to "Havuç",
    "food_rabbit_safe_carrots_detail" to "Ölçülü miktarda",
    "food_rabbit_safe_carrots_desc" to "Ödül",
    "food_rabbit_harmful_icebergletttuce_name" to "Aysberg Marul",
    "food_rabbit_harmful_icebergletttuce_detail" to "Besin değeri yok",
    "food_rabbit_harmful_icebergletttuce_desc" to "Sindirim durmasına neden olabilir",
    "food_rabbit_harmful_beans_name" to "Fasulye",
    "food_rabbit_harmful_beans_detail" to "Tüm baklagiller",
    "food_rabbit_harmful_beans_desc" to "Gaz, sindirim sorunları",
    "food_rabbit_harmful_corn_name" to "Mısır",
    "food_rabbit_harmful_corn_detail" to "Her türlü form",
    "food_rabbit_harmful_corn_desc" to "Sindiremez",
    "food_rabbit_harmful_potatoes_name" to "Patates",
    "food_rabbit_harmful_potatoes_detail" to "Her türlü form",
    "food_rabbit_harmful_potatoes_desc" to "Yanlış besinler",
    "food_rabbit_cannot_chocolate_name" to "Çikolata",
    "food_rabbit_cannot_chocolate_detail" to "Tüm türler",
    "food_rabbit_cannot_chocolate_desc" to "Teobromin toksiktir",
    "food_rabbit_cannot_onions_name" to "Soğan",
    "food_rabbit_cannot_onions_detail" to "Tüm formlar",
    "food_rabbit_cannot_onions_desc" to "Çok toksik",
    "food_rabbit_cannot_garlic_name" to "Sarımsak",
    "food_rabbit_cannot_garlic_detail" to "Tüm formlar",
    "food_rabbit_cannot_garlic_desc" to "Toksik",
    "food_rabbit_cannot_avocado_name" to "Avokado",
    "food_rabbit_cannot_avocado_detail" to "Tüm kısımlar",
    "food_rabbit_cannot_avocado_desc" to "Persin içerir",
    "primary" to "Birincil",
    "passkeyRegisteredSuccess" to "Passkey başarıyla kaydedildi!",
    "passkeyRegistrationFailed" to "Passkey kaydı başarısız oldu",
    "passkeyNameRequired" to "Passkey adı gereklidir",
    "registeringPasskey" to "Passkey kaydediliyor...",
    "passkeyRegistered" to "Passkey kaydedildi",
    "noPasskey" to "Kayıtlı passkey yok",
    "noPets" to "Henüz evcil hayvan yok.",
    "photographerCountryStateRequired" to "Fotoğrafçılık hizmetleri için lütfen ülke ve eyalet/il bilgilerini doldurun",
        "temporalHomeAliasCountryCityRequired" to "Geçici yuva için lütfen takma ad, ülke ve şehir bilgilerini doldurun",
        "shelterNameCountryCityAddressRequired" to "Barınak için lütfen ad, ülke, şehir ve adres bilgilerini doldurun",
        "sterilizationNameCountryCityAddressRequired" to "Kısırlaştırma merkezi için lütfen ad, ülke, şehir ve adres bilgilerini doldurun",
        "country.afghanistan" to "Afganistan",
        "country.albania" to "Arnavutluk",
        "country.algeria" to "Cezayir",
        "country.argentina" to "Arjantin",
        "country.armenia" to "Ermenistan",
        "country.australia" to "Avustralya",
        "country.austria" to "Avusturya",
        "country.azerbaijan" to "Azerbaycan",
        "country.bangladesh" to "Bangladeş",
        "country.belarus" to "Belarus",
        "country.belgium" to "Belçika",
        "country.bolivia" to "Bolivya",
        "country.bosnia" to "Bosna Hersek",
        "country.brazil" to "Brezilya",
        "country.bulgaria" to "Bulgaristan",
        "country.cambodia" to "Kamboçya",
        "country.cameroon" to "Kamerun",
        "country.canada" to "Kanada",
        "country.chile" to "Şili",
        "country.china" to "Çin",
        "country.colombia" to "Kolombiya",
        "country.costaRica" to "Kosta Rika",
        "country.croatia" to "Hırvatistan",
        "country.cuba" to "Küba",
        "country.czechia" to "Çekya",
        "country.denmark" to "Danimarka",
        "country.dominicanRepublic" to "Dominik Cumhuriyeti",
        "country.ecuador" to "Ekvador",
        "country.egypt" to "Mısır",
        "country.elSalvador" to "El Salvador",
        "country.estonia" to "Estonya",
        "country.ethiopia" to "Etiyopya",
        "country.finland" to "Finlandiya",
        "country.france" to "Fransa",
        "country.georgia" to "Gürcistan",
        "country.germany" to "Almanya",
        "country.ghana" to "Gana",
        "country.greece" to "Yunanistan",
        "country.guatemala" to "Guatemala",
        "country.haiti" to "Haiti",
        "country.honduras" to "Honduras",
        "country.hungary" to "Macaristan",
        "country.iceland" to "İzlanda",
        "country.india" to "Hindistan",
        "country.indonesia" to "Endonezya",
        "country.iran" to "İran",
        "country.iraq" to "Irak",
        "country.ireland" to "İrlanda",
        "country.israel" to "İsrail",
        "country.italy" to "İtalya",
        "country.jamaica" to "Jamaika",
        "country.japan" to "Japonya",
        "country.jordan" to "Ürdün",
        "country.kazakhstan" to "Kazakistan",
        "country.kenya" to "Kenya",
        "country.kuwait" to "Kuveyt",
        "country.latvia" to "Letonya",
        "country.lebanon" to "Lübnan",
        "country.libya" to "Libya",
        "country.lithuania" to "Litvanya",
        "country.luxembourg" to "Lüksemburg",
        "country.malaysia" to "Malezya",
        "country.mexico" to "Meksika",
        "country.moldova" to "Moldova",
        "country.mongolia" to "Moğolistan",
        "country.montenegro" to "Karadağ",
        "country.morocco" to "Fas",
        "country.myanmar" to "Myanmar",
        "country.nepal" to "Nepal",
        "country.netherlands" to "Hollanda",
        "country.newZealand" to "Yeni Zelanda",
        "country.nicaragua" to "Nikaragua",
        "country.nigeria" to "Nijerya",
        "country.northKorea" to "Kuzey Kore",
        "country.norway" to "Norveç",
        "country.pakistan" to "Pakistan",
        "country.panama" to "Panama",
        "country.paraguay" to "Paraguay",
        "country.peru" to "Peru",
        "country.philippines" to "Filipinler",
        "country.poland" to "Polonya",
        "country.portugal" to "Portekiz",
        "country.puertoRico" to "Porto Riko",
        "country.qatar" to "Katar",
        "country.romania" to "Romanya",
        "country.russia" to "Rusya",
        "country.saudiArabia" to "Suudi Arabistan",
        "country.serbia" to "Sırbistan",
        "country.singapore" to "Singapur",
        "country.slovakia" to "Slovakya",
        "country.slovenia" to "Slovenya",
        "country.southAfrica" to "Güney Afrika",
        "country.southKorea" to "Güney Kore",
        "country.spain" to "İspanya",
        "country.sriLanka" to "Sri Lanka",
        "country.sudan" to "Sudan",
        "country.sweden" to "İsveç",
        "country.switzerland" to "İsviçre",
        "country.syria" to "Suriye",
        "country.taiwan" to "Tayvan",
        "country.thailand" to "Tayland",
        "country.tunisia" to "Tunus",
        "country.turkey" to "Türkiye",
        "country.ukraine" to "Ukrayna",
        "country.uae" to "Birleşik Arap Emirlikleri",
        "country.uk" to "Birleşik Krallık",
        "country.usa" to "Amerika Birleşik Devletleri",
        "country.uruguay" to "Uruguay",
        "country.uzbekistan" to "Özbekistan",
        "country.venezuela" to "Venezuela",
        "country.vietnam" to "Vietnam",
        "country.yemen" to "Yemen",
        "loading" to "Yükleniyor...",
        "countryRequired" to "Aramak için lütfen bir ülke seçin",
        "noSheltersFound" to "Henüz barınak bulunamadı. Daha sonra tekrar kontrol edin!",
        "visitWebsite" to "Web Sitesini Ziyaret Et",
        "fiscalId" to "Vergi Kimlik No",
        "donationInformation" to "Bağış Bilgileri",
        "bankName" to "Banka",
        "accountHolder" to "Hesap Sahibi",
        "accountNumber" to "Hesap Numarası",
        "currency" to "Para Birimi",
        "errorLoadingShelters" to "Barınaklar yüklenirken hata oluştu",
        "pleaseSelectCountry" to "Lütfen bir ülke seçin",
        "actions" to "İşlemler",
        "addShelter" to "Barınak Ekle",
        "addEditShelter" to "Barınak Ekle / Düzenle",
        "allStates" to "Tüm Eyaletler/İller",
        "confirmDelete" to "Bu konumu silmek istediğinizden emin misiniz?",
        "confirmDeleteShelter" to "Bu barınağı silmek istediğinizden emin misiniz?",
        "confirmUnbanUser" to "Bu kullanıcının yasağı kaldırılsın mı?",
        "confirmResetUserCredentials" to "Bu, {email} kullanıcısının mevcut şifresini ve passkey'lerini geçersiz kılacak ve kendisine bir sıfırlama bağlantısı e-postayla gönderecektir. Devam edilsin mi?",
        "confirmDeactivateUser" to "Bu kullanıcı devre dışı bırakılsın mı? Daha sonra yeniden etkinleştirilebilir.",
        "confirmDeactivatePet" to "Bu evcil hayvan devre dışı bırakılsın mı? Genel ilanlardan gizlenecek ve daha sonra yeniden etkinleştirilebilir.",
        "confirmBlockRescuer" to "Bu kurtarıcının size daha fazla talep göndermesi engellensin mi?",
        "confirmDeletePet" to "Bu evcil hayvan silinsin mi?",
        "confirmDeleteStorage" to "Bu depolama silinsin mi?",
        "contact" to "İletişim",
        "delete" to "Sil",
        "edit" to "Düzenle",
        "errorDeletingShelter" to "Barınak silinirken hata oluştu",
        "errorLoadingLocations" to "Konumlar yüklenirken hata oluştu",
        "errorLoadingShelter" to "Barınak yüklenirken hata oluştu",
        "errorSaving" to "Kaydedilirken hata oluştu",
        "errorSavingShelter" to "Barınak kaydedilirken hata oluştu",
        "location" to "Konum",
        "noLocationsFound" to "Konum bulunamadı",
        "selectCountry" to "Bir ülke seçin",
        "selectCountryToFilter" to "Filtrelemek için lütfen bir ülke seçin",
        "shelterAdded" to "Barınak başarıyla eklendi",
        "shelterDeleted" to "Barınak başarıyla silindi",
        "shelterUpdated" to "Barınak başarıyla güncellendi",
        "updateShelter" to "Barınağı Güncelle",
        "errorLoadingPhotographers" to "Fotoğrafçılar yüklenirken hata oluştu",
        "noPhotographersAvailable" to "Mevcut fotoğrafçı yok",
        "requestPhotoSession" to "Fotoğraf Çekimi Talep Et",
        "selectPhotographer" to "Seç",
        "photographersSelected" to "{count} seçildi",
        "requestSelectedPhotographers" to "Seçili fotoğrafçıları talep et",
        "clearSelection" to "Seçimi temizle",
        "requestToMultiple" to "Şuna talep gönder",
        "messageRequired" to "Lütfen fotoğrafçılar için bir mesaj yazın.",
        "requestsSentSuccessfully" to "Talepler başarıyla gönderildi",
        "message" to "Mesaj",
        "sendRequest" to "Talep Gönder",
        "requestSentSuccessfully" to "Talep başarıyla gönderildi!",
        "requestTo" to "Talep alıcısı",
        "enterMessage" to "{name} için bir mesaj girin...",
        "reportUrgent" to "Acil",
        "topRescuers" to "En İyi Kurtarıcılar",
        "urgentRescuerSettings" to "Acil Kurtarıcı Ayarları",
        "urgentRescueAlerts" to "Acil Kurtarma Uyarıları",
        "reportUrgentTitle" to "Tehlikedeki Bir Evcil Hayvanı Bildir",
        "reportUrgentIntro" to "Acil yardıma ihtiyacı olan yaralı, kötü muamele gören, aç veya terk edilmiş bir evcil hayvan bulduysanız, bize nerede olduğunu ve sorunu bildirin - yakındaki acil kurtarıcıları hemen uyaracağız.",
        "dangerType" to "Sorun ne?",
        "dangerInjured" to "Yaralı",
        "dangerAbused" to "Kötü muamele görüyor / istismar ediliyor",
        "dangerStarving" to "Aç",
        "dangerTooYoung" to "Tek başına hayatta kalamayacak kadar küçük",
        "dangerOther" to "Diğer acil durum",
        "useMyLocation" to "Mevcut konumumu kullan",
        "confirmMyLocation" to "Mevcut konumumu onayla",
        "locationPermissionHint" to "Tarayıcınız konumunuzu paylaşmak için izin isteyecek; konum yalnızca yakınınızdaki bildirimler için kullanılır.",
        "phoneOptional" to "Telefonunuz (isteğe bağlı)",
        "submitReport" to "Bildirimi Gönder",
        "urgentRescuerSettingsTitle" to "Acil Kurtarıcı Ayarları",
        "urgentRescuerSettingsIntro" to "Kapsama alanınızın yakınında tehlikedeki bir evcil hayvan bildirildiğinde çağrı alın. İlk kabul eden vakayı üstlenir.",
        "urgentRescuerActive" to "Acil bildirimlere yanıt vermeye hazırım",
        "coverageAreaMode" to "Kapsama alanı",
        "coverageArea" to "Kapsama alanı",
        "coverageAreaHint" to "Mevcut konumunu onayla, iğneyi sürükle veya aşağıya bir adres yaz - harita ve alanlar birbiriyle eşzamanlı kalır.",
        "useMyLocationAndRadius" to "Konumumu + bir yarıçapı kullan",
        "pickAZone" to "Bunun yerine bir ülke/eyalet/şehir seç",
        "radiusKm" to "Yarıçap (km)",
        "urgentRescueAlertsTitle" to "Acil Kurtarma Uyarıları",
        "urgentRescuerLeaderboardTitle" to "En İyi Acil Kurtarıcılar (son 12 ay)",
        "locating" to "Konum belirleniyor...",
        "geolocationUnsupported" to "Konum belirleme tarayıcınız tarafından desteklenmiyor",
        "locationCaptured" to "Konum alındı",
        "locationDenied" to "Konumunuz alınamadı - lütfen konum erişimine izin verin veya bunun yerine bir şehir girin",
        "street" to "Sokak",
        "exteriorNumber" to "Dış kapı numarası",
        "referenceNotes" to "Referans notları (örn. ev rengi, yakındaki dikkat çekici nokta)",
        "concreteAddressHint" to "Mümkünse bir sokak ve numara ekleyin - bu, bir kurtarıcının taksiye binip veya bir harita uygulamasını doğrudan o noktaya açmasını sağlar.",
        "descriptionRequired" to "Lütfen durumu açıklayın",
        "locationRequired" to "Lütfen bir konum belirtin (mevcut konumunuzu kullanın veya bir ülke ve şehir girin)",
        "submittingEllipsis" to "Gönderiliyor...",
        "reportSubmitted" to "Bildirim gönderildi. Teşekkür ederiz - yakındaki kurtarıcılar şu anda uyarılıyor.",
        "reportSubmitFailed" to "Bildirim gönderilemedi",
        "phoneRequired" to "Lütfen bir telefon numarası girin",
        "savingEllipsis" to "Kaydediliyor...",
        "saved" to "Kaydedildi",
        "saveFailed" to "Kaydedilemedi",
        "noUrgentPages" to "Şu anda bekleyen acil uyarı yok",
        "accept" to "Kabul Et",
        "alreadyAccepted" to "Bu bildirimi başka biri zaten kabul etti",
        "noLeaderboardData" to "Son 12 ayda kabul edilmiş kurtarma yok",
        "rescueAcceptedTitle" to "Kurtarma Kabul Edildi",
        "rescueAcceptedMessage" to "Teşekkür ederiz - ilgileniyorsunuz. Mümkün olan en kısa sürede bildirilen konuma gidin.",
        "rescueAcceptFailedTitle" to "Kabul Edilemedi",
        "rescueAcceptFailedMessage" to "Bu bildirim başka bir kurtarıcı tarafından zaten kabul edildi veya bağlantı geçersiz ya da süresi dolmuş.",
        "share" to "Paylaş",
        "shareViaFacebook" to "Facebook'ta paylaş",
        "shareViaX" to "X'te paylaş",
        "shareViaWhatsapp" to "WhatsApp'ta paylaş",
        "shareViaEmail" to "E-posta ile paylaş",
        "copyLink" to "Bağlantıyı kopyala",
        "linkCopied" to "Bağlantı kopyalandı!",
        "reportLostFoundTitle" to "Kayıp veya Bulunan Bir Evcil Hayvanı Bildir",
        "reportLostFoundIntro" to "Evcil hayvanınızı mı kaybettiniz, yoksa sahipsiz bir hayvan mı buldunuz? Bir bildirim yapın, yakındaki diğer bildirimlerle karşılaştıralım.",
        "browseLostFound" to "Mevcut kayıp ve bulunan bildirimlerine göz at",
        "kindLost" to "Evcil hayvanımı kaybettim",
        "kindFound" to "Bir evcil hayvan buldum",
        "petType" to "Evcil hayvan türü",
        "petTypeUnknown" to "Emin değilim",
        "lostFoundBrowseTitle" to "Kayıp ve Bulunan Evcil Hayvanlar",
        "showing" to "Gösteriliyor",
        "kindLostPlural" to "Kayıp evcil hayvanlar",
        "kindFoundPlural" to "Bulunan evcil hayvanlar",
        "reportResolvedTitle" to "Bildirim Çözüldü",
        "reportResolvedMessage" to "Güncelleme için teşekkürler - bu bildirim artık kapatıldı.",
        "reportResolveFailedTitle" to "Çözülemedi",
        "reportResolveFailedMessage" to "Bu bildirim zaten çözüldü veya bağlantı geçersiz ya da süresi dolmuş.",
        "reportLostFound" to "Kayıp/\u200BBulunan",
        "noLostFoundReportsFound" to "Kayıp veya bulunan bildirim bulunamadı",
        "errorLoadingReports" to "Bildirimler yüklenirken hata oluştu",
        "yourEmail" to "E-postanız",
        "contactReporter" to "Bildiren kişiyle iletişime geç",
        "contactFormIncomplete" to "Lütfen e-postanızı ve bir mesaj girin",
        "contactSent" to "Mesaj gönderildi",
        "contactFailed" to "Mesaj gönderilemedi",
        "lostFoundReportSubmitted" to "Bildirim gönderildi. Teşekkür ederiz - yakındaki diğer bildirimlerle karşılaştıracağız.",
        "video" to "Video (isteğe bağlı)",
        "removeVideo" to "Videoyu kaldır",
        "saveThisSearch" to "Bu aramayı kaydet",
        "savedSearchSaved" to "Arama kaydedildi! Yeni bir eşleşme listelendiğinde size e-posta göndereceğiz.",
        "savedSearchFailed" to "Arama kaydedilemedi",
        "favorite" to "Favori",
        "addToFavorites" to "Favorilere Ekle",
        "removeFromFavorites" to "Favorilerden Kaldır",
        "myFavorites" to "Favorilerim",
        "noFavoritesYet" to "Henüz favori yok",
        "mySavedSearches" to "Kaydedilmiş Aramalarım",
        "noSavedSearchesYet" to "Henüz kaydedilmiş arama yok",
        "donationPromptText" to "Bugün bir evcil hayvana yardım ettiğiniz için teşekkür ederiz. Yapabiliyorsanız, Adopt-U'yu herkes için ücretsiz tutmaya devam etmek için küçük bir bağış yapmayı düşünün.",
        "donationPromptCta" to "Bağış Yap",
        "dismiss" to "Kapat",
    )

private val romanianTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "Autentificare",
    "register" to "Înregistrare",
    "logout" to "Deconectare",
    "profile" to "Profil",
    "myPets" to "Animalele mele",
    "addPet" to "Adaugă animal",
    "save" to "Salvează",
    "cancel" to "Anulează",
    "search" to "Caută",
    "shelters" to "Adăposturi",
    "photographers" to "Fotografi",
    "temporalHomes" to "Case temporare",
    "findTemporalHomes" to "Găsește case temporare",
    "sterilizationLocations" to "Locații de sterilizare",
    "donate" to "Donează",
    "countryLabel" to "Țara",
    "selectCountryToSearch" to "Selectează o țară",
    "selectCountryFirst" to "Selectează o țară pentru a activa aceste filtre",
    "state" to "Stat/Județ",
    "city" to "Oraș",
    "zipCode" to "Cod poștal",
    "neighborhood" to "Cartier",
    "name" to "Nume",
    "email" to "E-mail",
    "phone" to "Telefon",
    "address" to "Adresă",
    "description" to "Descriere",
    "animalShelters" to "Adăposturi pentru animale",
    "sheltersDescription" to "Găsește adăposturi pentru animale unde poți găsi animale de adoptat sau le poți lăsa cu acordul prealabil al adăpostului",
    "photographerDescription" to "Fotografi profesioniști care oferă sesiuni foto pentru animale de companie",
    "sterilizationLocationsDescription" to "Găsește locuri unde poți duce animalele pentru a fi sterilizate.",
    "privacyPolicy" to "Politica de confidențialitate",
    "privacyLastUpdated" to "Ultima actualizare: aprilie 2025",
    "privacyHighlight" to "La Adopt-U, credem în colectarea minimă de date. Colectăm doar adresa ta de e-mail pentru a permite comunicarea între adoptatori și salvatori. Nu folosim informațiile tale în scopuri publicitare și nu vom împărtăși niciodată datele tale cu terți în scopuri de marketing.",
    "privacyInfoCollectedH2" to "Informațiile pe care le colectăm",
    "privacyAccountRegH3" to "Înregistrarea contului",
    "privacyAccountRegIntro" to "Pentru a crea un cont pe Adopt-U, colectăm următoarele informații:",
    "privacyEmailLabel" to "Adresă de e-mail: ",
    "privacyEmailDesc" to "Necesară pentru verificarea contului, recuperarea parolei și comunicarea între adoptatori și salvatori",
    "privacyDisplayNameLabel" to "Nume afișat: ",
    "privacyDisplayNameDesc" to "Un nume pe care îl alegi pentru a fi identificat pe platformă",
    "privacyLangPrefLabel" to "Preferință de limbă: ",
    "privacyLangPrefDesc" to "Pentru a oferi platforma în limba preferată de tine",
    "privacyNoFullNameEtc" to "Nu colectăm numele tău complet, numărul de telefon, adresa fizică, documentele de identitate, informațiile de plată sau orice alte date personale în afara celor menționate mai sus.",
    "privacyOptionalProfileH3" to "Informații opționale despre profil",
    "privacyOptionalProfileIntro" to "În funcție de rolul tău, poți furniza opțional:",
    "privacyAdopterLabel" to "Ca Adoptator: ",
    "privacyAdopterDesc" to "Nu este necesară sau colectată nicio informație suplimentară",
    "privacyRescuerLabel" to "Ca Salvator: ",
    "privacyRescuerDesc" to "Informații despre locație pentru animalele disponibile pentru adopție",
    "privacyPhotographerLabel" to "Ca Fotograf: ",
    "privacyPhotographerDesc" to "Locația serviciului și preferințele de contact",
    "privacyTemporalHomeLabel" to "Ca Casă temporară: ",
    "privacyTemporalHomeDesc" to "Locația unde poți oferi cazare temporară",
    "privacyLocationUsage" to "Toate informațiile despre locație sunt folosite exclusiv pentru a asocia adoptatorii cu salvatorii și furnizorii de servicii din apropiere.",
    "privacyPetInfoH3" to "Informații despre animal",
    "privacyPetInfoIntro" to "Salvatorii pot publica informații despre animale, inclusiv:",
    "privacyPetPhotos" to "Fotografii ale animalului",
    "privacyPetDescBreedAge" to "Descriere, rasă, vârstă și caracteristici",
    "privacyPetHealth" to "Starea de sănătate și de vaccinare",
    "privacyPetLocation" to "Locația unde este disponibil animalul",
    "privacyPetInfoNotVerified" to "Aceste informații sunt furnizate de salvatori și nu sunt verificate independent de Adopt-U.",
    "privacyHowWeUseH2" to "Cum folosim informațiile tale",
    "privacyPrimaryPurposeH3" to "Scop principal: Facilitarea conexiunilor de adopție",
    "privacyEmailUsedFor" to "Adresa ta de e-mail este utilizată exclusiv pentru următoarele scopuri:",
    "privacyUseVerification" to "Trimiterea unui link de verificare atunci când îți creezi un cont",
    "privacyUseRescuerContact" to "Permiterea salvatorilor să te contacteze despre animalele de care ești interesat",
    "privacyUseContactRescuer" to "Permiterea ta de a contacta salvatorii despre animalele listate de aceștia",
    "privacyUseNotifications" to "Trimiterea de notificări importante privind contul și securitatea",
    "privacyOnceConnected" to "Odată ce te conectezi cu un alt utilizator prin platforma noastră, orice comunicare ulterioară se desfășoară direct între tine și acea persoană. Nu monitorizăm, nu stocăm și nu avem acces la conținutul comunicărilor tale.",
    "privacyNeverDoH3" to "Ce nu vom face niciodată",
    "privacyCommitIntro" to "Ne angajăm solemn la următoarele:",
    "privacyNoAdsLabel" to "Fără publicitate: ",
    "privacyNoAdsDesc" to "Nu vom afișa niciodată reclame direcționate sau nedirecționate pe baza activității tale",
    "privacyNoMarketingLabel" to "Fără marketing: ",
    "privacyNoMarketingDesc" to "Nu vom împărtăși niciodată e-mailul tău cu terți în scopuri de marketing sau promoționale",
    "privacyNoSellingLabel" to "Fără vânzarea datelor: ",
    "privacyNoSellingDesc" to "Nu vom vinde, închiria sau transfera în niciun alt mod informațiile tale personale către vreun terț",
    "privacyNoProfilingLabel" to "Fără profilare: ",
    "privacyNoProfilingDesc" to "Nu vom folosi datele tale pentru a construi profiluri în niciun alt scop decât administrarea de bază a contului",
    "privacyNoTrackingLabel" to "Fără urmărire: ",
    "privacyNoTrackingDesc" to "Nu folosim pixeli de urmărire, cookie-uri publicitare sau tehnologii de urmărire similare",
    "privacyDataStorageH2" to "Stocarea și securitatea datelor",
    "privacyDataStoredSecurely" to "Datele tale sunt stocate în siguranță folosind criptare la standarde din industrie. Aplicăm măsuri tehnice și organizatorice adecvate pentru a proteja informațiile tale personale împotriva accesului neautorizat, modificării, divulgării sau distrugerii.",
    "privacyDataRetention" to "Păstrăm informațiile contului tău atât timp cât contul tău rămâne activ. Poți solicita ștergerea contului tău și a datelor asociate în orice moment, contactându-ne.",
    "privacyYourRightsH2" to "Drepturile tale",
    "privacyRightsIntro" to "Ai următoarele drepturi cu privire la datele tale personale:",
    "privacyRightAccess" to "Acces: Poți solicita o copie a tuturor datelor personale pe care le deținem despre tine",
    "privacyRightCorrection" to "Rectificare: Poți solicita să corectăm orice informație inexactă",
    "privacyRightDeletion" to "Ștergere: Poți solicita să ștergem contul tău și toate datele asociate",
    "privacyRightPortability" to "Portabilitate: Poți solicita să îți furnizăm datele într-un format utilizat în mod obișnuit",
    "privacyExerciseRights" to "Pentru a exercita oricare dintre aceste drepturi, te rugăm să ne contactezi la adopt-u@adopt-u.org.",
    "privacyCookiesH2" to "Cookie-uri",
    "privacyCookiesText" to "Adopt-U folosește un minim de cookie-uri necesare pentru funcționarea platformei, inclusiv gestionarea sesiunii și preferințele de limbă. Nu folosim cookie-uri publicitare sau de urmărire.",
    "privacyThirdPartyH2" to "Servicii terțe",
    "privacyThirdPartyIntro" to "Folosim următoarele servicii terțe, care pot procesa datele tale:",
    "privacyAws" to "AWS (Amazon Web Services): Pentru stocarea datelor și livrarea e-mailurilor. Politicile lor de confidențialitate se aplică prelucrării datelor tale de către aceștia.",
    "privacyWebauthn" to "WebAuthn/FIDO2: Pentru autentificare securizată prin passkey. Aceasta implică operațiuni criptografice în browserul tău.",
    "privacyThirdPartySelected" to "Am selectat cu grijă furnizorii de servicii care împărtășesc angajamentul nostru față de confidențialitatea datelor.",
    "privacyChildrensH2" to "Confidențialitatea copiilor",
    "privacyChildrensText" to "Adopt-U nu este destinat utilizatorilor sub 18 ani. Nu colectăm în mod conștient informații de la minori. Dacă crezi că un minor și-a creat un cont, te rugăm să ne contactezi pentru a-l elimina.",
    "privacyChangesH2" to "Modificări ale acestei politici",
    "privacyChangesText" to "Putem actualiza această Politică de confidențialitate din când în când pentru a reflecta modificări în practicile noastre sau cerințele legale. Vom notifica utilizatorii cu privire la modificările semnificative prin e-mail sau printr-un anunț vizibil pe platformă.",
    "privacyContactH2" to "Contactează-ne",
    "privacyContactText" to "Dacă ai întrebări sau nelămuriri legate de această Politică de confidențialitate sau dorești să îți exerciți drepturile privind datele, te rugăm să ne contactezi la adopt-u@adopt-u.org.",
    "termsConditions" to "Termeni și condiții",
    "installApp" to "Instalează aplicația",
    "termsAcceptanceH2" to "Acceptarea termenilor",
    "termsAcceptanceText" to "Accesând și utilizând Adopt-U, accepți și ești de acord să respecți termenii și prevederile acestui acord. Dacă nu ești de acord cu acești termeni, te rugăm să nu folosești platforma noastră.",
    "termsNatureH2" to "Natura serviciului nostru",
    "termsNatureText1" to "Adopt-U este o platformă de conectare care facilitează contactul dintre salvatorii de animale și potențialii adoptatori. Nu suntem un magazin de animale, un broker, un adăpost sau o agenție de adopții. Nu deținem, nu custodiem și nu gestionăm în mod intermediar niciun animal listat pe platforma noastră.",
    "termsNatureText2" to "Rolul nostru se limitează strict la furnizarea unui canal de comunicare între părți. Nu participăm la negocierile de adopție, la contracte sau la transferul fizic al animalelor.",
    "termsUserRespH2" to "Responsabilitățile utilizatorilor",
    "termsForRescuersH3" to "Pentru salvatori:",
    "termsRescuerResp1" to "Furnizează informații corecte și adevărate despre animalele aflate în grija ta",
    "termsRescuerResp2" to "Asigură-te că animalele sunt sănătoase, vaccinate și verificate corespunzător înainte de listare",
    "termsRescuerResp3" to "Efectuează o verificare responsabilă a potențialilor adoptatori",
    "termsRescuerResp4" to "Organizează transferul în siguranță al animalelor către noile lor locuințe",
    "termsRescuerResp5" to "Acceptă responsabilitatea pentru acuratețea tuturor informațiilor furnizate",
    "termsRescuerResp6" to "Respectă toate legile și reglementările locale privind adopția animalelor",
    "termsRescuerResp7" to "Nu folosi niciodată platforma pentru vânzarea comercială de animale sau în scopuri de reproducere",
    "termsForAdoptersH3" to "Pentru adoptatori:",
    "termsAdopterResp1" to "Furnizează informații corecte despre situația ta locativă și experiența cu animale",
    "termsAdopterResp2" to "Fii pregătit să demonstrezi capacitatea ta de a oferi îngrijire corespunzătoare",
    "termsAdopterResp3" to "Înțelege că renunțarea la adopția unui animal provoacă un stres semnificativ animalului",
    "termsAdopterResp4" to "Respectă dreptul salvatorului de a refuza cererea ta de adopție",
    "termsPetListingsH2" to "Anunțuri și informații despre animale",
    "termsPetListingsText1" to "Adopt-U nu verifică, nu susține și nu garantează acuratețea informațiilor furnizate în anunțurile despre animale. Informațiile, incluzând dar fără a se limita la rasă, vârstă, stare de sănătate, temperament și evidența vaccinărilor, sunt furnizate de salvatori și nu au fost verificate independent.",
    "termsPetListingsText2" to "Fotografiile și descrierile sunt furnizate de salvatori. Deși interzicem denaturarea deliberată, nu putem garanta că toate anunțurile reprezintă cu acuratețe starea actuală a animalului.",
    "termsPetListingsText3" to "Utilizatorii sunt încurajați să pună întrebări, să solicite fișele veterinare și să întâlnească animalele înainte de a finaliza orice aranjament de adopție.",
    "termsAdoptionProcessH2" to "Procesul de adopție",
    "termsAdoptionProcessIntro" to "Odată ce un salvator și un adoptator convin să continue cu o adopție:",
    "termsAdoptionStep1" to "Toate aranjamentele, inclusiv data transferului, locația și orice costuri asociate, sunt negociate direct între părți",
    "termsAdoptionStep2" to "Adopt-U nu facilitează și nu garantează nicio tranzacție de plată",
    "termsAdoptionStep3" to "Platforma nu oferă contracte de adopție; orice aranjamente contractuale sunt responsabilitatea exclusivă a părților implicate",
    "termsAdoptionStep4" to "Recomandăm cu tărie documentarea în scris a tuturor acordurilor",
    "termsLiabilityH2" to "Limitarea răspunderii",
    "termsLiabilityIntro" to "Adopt-U nu va fi ținut răspunzător pentru:",
    "termsLiability1" to "Orice dispute, neînțelegeri sau conflicte între salvatori și adoptatori",
    "termsLiability2" to "Acuratețea informațiilor furnizate în anunțurile despre animale",
    "termsLiability3" to "Starea de sănătate, comportamentul sau condiția oricărui animal înainte, în timpul sau după adopție",
    "termsLiability4" to "Orice pierderi financiare, daune sau vătămări rezultate din utilizarea platformei",
    "termsLiability5" to "Rezultatul oricărei adopții, incluzând dar fără a se limita la retururi, probleme de sănătate sau probleme de compatibilitate",
    "termsLiability6" to "Orice acțiuni întreprinse de utilizatori în afara platformei după ce contactul a fost stabilit",
    "termsLiabilityAck" to "Utilizatorii recunosc că adopția unui animal este un angajament semnificativ și că își asumă întreaga responsabilitate pentru decizia lor de a adopta.",
    "termsProhibitedH2" to "Activități interzise",
    "termsProhibitedIntro" to "Următoarele activități sunt strict interzise pe Adopt-U:",
    "termsProhibited1" to "Vânzări comerciale de animale, operațiuni de reproducere sau crescătorii tip \"puppy mill\"",
    "termsProhibited2" to "Listarea unor animale de care nu te ocupi direct",
    "termsProhibited3" to "Informații false sau înșelătoare în anunțuri sau profiluri",
    "termsProhibited4" to "Hărțuirea, discriminarea sau contactul necorespunzător cu alți utilizatori",
    "termsProhibited5" to "Utilizarea platformei pentru a colecta informații personale în scopuri fără legătură cu adopția animalelor",
    "termsProhibited6" to "Orice activități ilegale sau încălcări ale legilor privind bunăstarea animalelor",
    "termsAccountTerminationH2" to "Închiderea contului",
    "termsAccountTerminationText" to "Adopt-U își rezervă dreptul de a suspenda sau închide conturile care încalcă acești termeni, se implică în activități interzise sau aduc prejudicii reputației platformei. De asemenea, putem elimina anunțuri care conțin conținut fals, înșelător sau necorespunzător.",
    "termsModificationsH2" to "Modificări ale termenilor",
    "termsModificationsText" to "Ne rezervăm dreptul de a modifica acești termeni în orice moment. Utilizarea în continuare a platformei după modificări constituie acceptarea termenilor modificați. Vom notifica utilizatorii cu privire la modificările semnificative prin e-mail sau anunțuri pe platformă.",
    "termsContactText" to "Dacă ai întrebări legate de acești Termeni și condiții, te rugăm să ne contactezi la adopt-u@adopt-u.org.",
    "language" to "Limbă",
    "displayName" to "Nume afișat",
    "saveProfile" to "Salvează profilul",
    "accountSettings" to "Setări cont",
    "yourRoles" to "Rolurile tale:",
    "resources" to "Resurse",
    "loginWithPasskey" to "Autentificare cu Passkey",
    "signInWithPasskey" to "Conectează-te cu Passkey",
    "resendVerificationEmail" to "Retrimite e-mailul de verificare",
    "usesFido" to "Folosește FIDO2 / WebAuthn pentru autentificare sigură fără parolă.",
    "loginWithMagicLink" to "Autentificare cu link pe e-mail",
    "magicLinkDesc" to "Îți vom trimite un link de autentificare pe e-mail (valabil 5 minute).",
    "sendMagicLink" to "Trimite link de autentificare",
    "loginWithPassword" to "Autentificare cu parolă",
    "signIn" to "Conectare",
    "forgotPassword" to "Ai uitat parola?",
    "registerNewAccount" to "Creează cont",
    "accountDetails" to "Detalii cont",
    "registrationMethod" to "Metodă de înregistrare",
    "selectAdditionalRoles" to "Selectează roluri suplimentare:",
    "adoptPet" to "Adoptă un animal",
    "publishPets" to "Publică animale pentru adopție",
    "offerPhotography" to "Oferă servicii de fotografie",
    "photographerSettings" to "Setări fotograf",
    "sessionFee" to "Tarif sesiune",
    "provideTemporaryHome" to "Oferă cazare temporară pentru animale",
    "passkeyNoPassword" to "Passkey-urile sunt mai sigure decât parolele și funcționează pe toate dispozitivele tale.",
    "registerWithPasskey" to "Înregistrează-te cu Passkey",
    "passkeySettings" to "Setări Passkey",
    "password" to "Parolă",
    "confirmPassword" to "Confirmă parola",
    "passwordRequirements" to "Parola trebuie să aibă cel puțin 8 caractere și să conțină literă mare, literă mică, cifră și simbol",
    "securityInfo" to "Informații de securitate",
    "passkeyMostSecure" to "Passkey: Cel mai sigur, recomandat",
    "passkeyBrowserCompat" to "Passkey poate să nu funcționeze pe toate browserele",
    "passwordLeastSecure" to "Parolă: Mai puțin sigură, nu este recomandată",
    "selectLoginMethods" to "Selectează una sau mai multe metode de autentificare:",
    "registerPasskeyOption" to "Passkey (cel mai sigur, funcționează pe toate dispozitivele tale)",
    "registerPasswordOption" to "Parolă (mai puțin sigură, folosește doar ca rezervă)",
    "howPasskeysWorkTitle" to "Cum funcționează un passkey?",
    "howPasskeysWorkIntro" to "Un passkey înlocuiește parola ta cu securitatea integrată a dispozitivului tău — amprentă, recunoaștere facială sau codul PIN de blocare a ecranului.",
    "howPasskeysWorkSecurity" to "Este stocat doar pe dispozitivul tău, nu poate fi phishing-uit sau scurs într-o breșă de date și se sincronizează automat pe toate dispozitivele tale.",
    "howPasskeysWorkBackup" to "Poți adăuga și o parolă, dar un passkey este suficient pentru a te conecta — fără a fi nevoie de parolă.",
    "gotIt" to "Am înțeles",
    "alreadyHaveAccount" to "Ai deja un cont?",
    "all" to "Toate",
    "filterDogs" to "Câini",
    "filterCats" to "Pisici",
    "filterBirds" to "Păsări",
    "filterFish" to "Pești",
    "filterAllSexOption" to "Toate sexele",
    "filterMaleOption" to "♂ Mascul",
    "filterFemaleOption" to "♀ Femelă",
    "age" to "Vârstă",
    "rescued" to "Salvat",
    "years" to "ani",
    "months" to "luni",
    "year" to "an",
    "month" to "lună",
    "loginToAdopt" to "Conectează-te pentru a solicita adopția",
    "requiredMarker" to "(obligatoriu)",
    "pageNotFoundTitle" to "Pagina nu a fost găsită",
    "pageNotFoundHint" to "Linkul poate fi vechi sau greșit. Animalele sunt încă aici.",
    "browsePets" to "Vezi animalele",
    "requestsFromRescuers" to "Cereri de la salvatori",
    "signingOut" to "Se deconectează...",
    "adopter" to "Adoptator",
    "country" to "Țară",
    "zip" to "Cod poștal",
    "viewPublic" to "Vezi pagina publică",
    "banUser" to "Blochează utilizatorul",
    "banReason" to "Motiv (opțional)",
    "myPhotoRequests" to "Cererile mele de ședință foto",
    "noPhotoRequestsYet" to "Nu ai solicitat încă o ședință foto.",
    "photoRequestsReceived" to "Cereri de ședință foto primite",
    "noPhotoRequestsReceived" to "Nu există încă cereri de ședință foto.",
    "photoStatusPending" to "În așteptare",
    "photoStatusApproved" to "Aprobată",
    "photoStatusRejected" to "Respinsă",
    "photoStatusCancelled" to "Anulată",
    "photoStatusCompleted" to "Finalizată",
    "cancelRequest" to "Anulează cererea",
    "registerConsentPrefix" to "Prin crearea unui cont accepți",
    "registerConsentTerms" to "Termenii și condițiile",
    "registerConsentAnd" to "și",
    "registerConsentPrivacy" to "Politica de confidențialitate",
    "petStatusAvailable" to "Disponibil",
    "petStatusAdopted" to "Adoptat",
    "petStatusDisabled" to "Dezactivat",
    "petStatusPending" to "În așteptare",
    "addEditLocation" to "Adaugă/Editează locația",
    "addLocation" to "Adaugă locație",
    "manage" to "Administrare",
    "adminPanel" to "Panou de administrare",
    "adminSterilizationLocations" to "Gestionează locațiile de sterilizare",
    "adopterRequired" to "Adoptator (obligatoriu)",
    "adoptionFee" to "Taxă de adopție",
    "adoptionRequests" to "Cereri de adopție",
    "ageMonths" to "Vârstă (luni)",
    "ageYears" to "Vârstă (ani)",
    "alias" to "Alias",
    "bird" to "Pasăre",
    "breed" to "Rasă",
    "medicalEventsOverview" to "Prezentare generală a evenimentelor medicale",
    "back" to "Înapoi",
    "cat" to "Pisică",
    "color" to "Culoare",
    "compatibility" to "Compatibilitate",
    "currentPassword" to "Parola curentă",
    "dog" to "Câine",
    "emailChange" to "Schimbă e-mailul",
    "emailChangeDesc" to "Actualizează-ți adresa de e-mail. Un link de verificare va fi trimis la noua adresă de e-mail.",
    "energyLevel" to "Nivel de energie",
    "female" to "Femelă",
    "findTemporalHome" to "Găsește o casă temporară",
    "fish" to "Pește",
    "goodWithCats" to "Se înțelege bine cu pisicile",
    "goodWithDogs" to "Se înțelege bine cu câinii",
    "goodWithKids" to "Se înțelege bine cu copiii",
    "high" to "Ridicat",
    "houseTrained" to "Educat în casă",
    "large" to "Mare",
    "low" to "Scăzut",
    "male" to "Mascul",
    "managePets" to "Gestionează animalele",
    "managePetsBtn" to "Adaugă sau editează animale",
    "managePetsDescription" to "Gestionează toate paginile de animale. Adaugă sau elimină animale.",
    "manageShelters" to "Gestionează adăposturile",
    "manageSheltersBtn" to "Adaugă sau editează adăposturi",
    "manageSheltersDescription" to "Gestionează toate paginile de adăposturi. Adaugă sau elimină adăposturi.",
    "manageUsers" to "Gestionează utilizatorii",
    "medical" to "Medical",
    "medium" to "Mediu",
    "backToSearch" to "Înapoi la căutare",
    "contactThisHome" to "Contactează această casă temporară",
    "loginAsRescuerToContact" to "Conectează-te ca salvator pentru a trimite o cerere către această casă temporară.",
    "memberSince" to "Membru din",
    "microchipId" to "ID microcip",
    "microchipped" to "Microcipat",
    "myTemporalHome" to "Casa mea temporară",
    "newEmail" to "E-mail nou",
    "noTemporalHomes" to "Nu au fost găsite case temporare.",
    "requestSentToHome" to "Cererea ta a fost trimisă!",
    "sendRequestBtn" to "Trimite cererea",
    "temporalHomeNotFound" to "Această casă temporară nu a putut fi găsită.",
    "viewDetails" to "Vezi detalii",
    "rescuerDirectory" to "Salvatori",
    "noRescuersFound" to "Nu au fost găsiți salvatori.",
    "availablePetsCount" to "Animale disponibile",
    "availablePets" to "Animale disponibile",
    "noAvailablePetsForRescuer" to "Acest salvator nu are animale disponibile momentan.",
    "rescuerNotFound" to "Acest salvator nu a putut fi găsit.",
    "errorLoadingResults" to "Eroare la încărcarea rezultatelor.",
    "volunteerForThisRescuer" to "Voluntariat pentru acest salvator",
    "volunteerExplanation" to "Voluntarii aprobați pot sugera modificări la anunțurile de animale ale acestui salvator.",
    "applyToVolunteerBtn" to "Aplică pentru voluntariat",
    "loginToVolunteer" to "Conectează-te pentru a aplica pentru voluntariat la acest salvator.",
    "volunteerApplicationSent" to "Cererea ta de voluntariat a fost trimisă!",
    "myVolunteering" to "Voluntariatul meu",
    "noVolunteerApplicationsYet" to "Nu ai aplicat încă pentru voluntariat la niciun salvator.",
    "howToVolunteerHint" to "Pentru a aplica, deschide pagina unui salvator din director și apasă \"Aplică ca voluntar\".",
    "volunteerStatusPending" to "În așteptare",
    "volunteerStatusActive" to "Activ",
    "volunteerStatusRejected" to "Respins",
    "volunteerApplications" to "Cereri de voluntariat",
    "noVolunteerApplications" to "Nu există cereri de voluntariat.",
    "petEditSuggestions" to "Sugestii de editare a animalelor",
    "noPetEditSuggestions" to "Nu există sugestii de editare în așteptare.",
    "suggestedByLabel" to "sugerat de",
    "suggestAnEditTitle" to "Sugerează o editare",
    "suggestAnEditExplanation" to "Ca voluntar aprobat, poți sugera modificări la acest anunț. Salvatorul le va analiza înainte ca acestea să devină publice.",
    "suggestEditBtn" to "Trimite sugestia",
    "editSuggestionSent" to "Sugestia ta a fost trimisă salvatorului spre analiză!",
    "sponsorThisPetTitle" to "Sponsorizează acest animal",
    "sponsorExplanation" to "Nu poți adopta, dar vrei să ajuți cu cheltuielile? Oferă bani sau ajutor în natură și conectează-te direct cu salvatorul.",
    "sponsorThisRescuerTitle" to "Sponsorizează acest salvator",
    "sponsorRescuerExplanation" to "Susține costurile generale ale acestui salvator cu bani sau ajutor în natură.",
    "sponsorTypeLabel" to "Ce poți oferi?",
    "sponsorTypeMoney" to "Bani",
    "sponsorTypeInKind" to "În natură (hrană, provizii etc.)",
    "amountLabel" to "Sumă (lunară)",
    "currencyLabel" to "Monedă",
    "inKindDescriptionLabel" to "Descrie ce poți oferi",
    "messageLabel" to "Mesaj",
    "sendOfferBtn" to "Trimite oferta",
    "sponsorOfferSent" to "Oferta ta a fost trimisă! Salvatorul te va contacta pentru a stabili detaliile.",
    "sponsorshipOffersReceived" to "Oferte de sponsorizare",
    "noSponsorshipOffers" to "Nu există încă oferte de sponsorizare.",
    "generalFundLabel" to "Fond general",
    "offeringLabel" to "Oferă",
    "sponsorshipStatusSent" to "Nou",
    "sponsorshipStatusRead" to "Citit",
    "markAsReadBtn" to "Marchează ca citit",
    "mySponsorshipOffers" to "Ofertele mele de sponsorizare",
    "noSponsorshipOffersYet" to "Nu ai trimis încă nicio ofertă de sponsorizare.",
    "howToSponsorHint" to "Pentru a sponsoriza, deschide pagina unui salvator (sprijin general) sau a unui animal (costurile acelui animal) și trimite o ofertă.",
    "browsePetsLink" to "Vezi animalele",
    "yourMessage" to "Mesajul tău",
    "newPassword" to "Parolă nouă",
    "passkeyDesc" to "Adaugă passkey-uri suplimentare la contul tău pentru autentificare ușoară pe mai multe dispozitive.",
    "passkeyName" to "Nume passkey",
    "pers" to "Persoane",
    "petFoodDescription" to "Află ce alimente sunt sigure, dăunătoare sau toxice pentru animalele tale de companie",
    "petFoodGuide" to "Ghid alimentar pentru animale",
    "foodInformation" to "Informații despre hrană",
    "petsForAdoption" to "Animale pentru adopție",
    "chooseCountryPrompt" to "Găsește-ți noul cel mai bun prieten",
    "chooseCountryPromptHint" to "Alege o țară mai sus pentru a răsfoi animalele disponibile pentru adopție în apropierea ta.",
    "noPetsFound" to "Nu au fost găsite animale",
    "noPetsFoundHint" to "Încearcă un alt filtru sau revino mai târziu — se adaugă animale noi în mod regulat.",
    "loadingMorePets" to "Se încarcă mai multe animale…",
    "photos" to "Fotografii (max 12)",
    "profilePhotographerRole" to "Fotograf - Oferă servicii de fotografie",
    "profileRescuerRole" to "Salvator - Publică animale pentru adopție",
    "profileTemporalHomeRole" to "Casă temporară - Oferă cazare temporară pentru animale",
    "provideShelter" to "Adăpost - Gestionează un adăpost pentru animale",
    "provideSterilization" to "Serviciu de sterilizare - Oferă servicii de sterilizare",
    "requestEmailChange" to "Solicită schimbarea e-mailului",
    "rescueDate" to "Data salvării",
    "rescueLocation" to "Locația salvării",
    "savePassword" to "Salvează parola",
    "selectEnergy" to "Selectează nivelul de energie",
    "selectSize" to "Selectează mărimea",
    "sex" to "Sex",
    "shelterSettings" to "Setări adăpost",
    "size" to "Mărime",
    "small" to "Mic",
    "specialNeeds" to "Nevoi speciale",
    "sterilizationSettings" to "Setări sterilizare",
    "sterilized" to "Sterilizat",
    "temporalHomeSettings" to "Setări casă temporară",
    "temperament" to "Temperament",
    "type" to "Tip *",
    "urgentAdoption" to "Adopție urgentă necesară",
    "vaccinations" to "Vaccinări",
    "vetDisclaimer" to "Consultă medicul veterinar înainte de a face modificări în dietă.",
    "weight" to "Greutate (kg)",
    "weightLabel" to "Greutate",
    "website" to "Website",
    "securitySettings" to "Setări de securitate",
    "emailVerifiedTitle" to "E-mail verificat cu succes!",
    "emailVerifiedMessage" to "E-mailul tău a fost verificat. Înregistrarea ta este acum completă.",
    "redirectingIn" to "Redirecționare către pagina principală în ",
    "canAlsoLogin" to "De asemenea, te poți ",
    "loginNow" to "Autentifică-te acum",
    "emailVerificationFailed" to "Verificare eșuată",
    "invalidOrExpiredToken" to "Linkul de verificare este invalid sau a expirat.",
    "tryRegisteringAgain" to "Te rugăm să încerci să te înregistrezi din nou.",
    "verificationEmailResent" to "E-mailul de verificare a expirat. A fost trimis un nou e-mail de verificare. Te rugăm să verifici căsuța de e-mail.",
    "emailVerificationSent" to "A fost trimis un e-mail de verificare. Te rugăm să verifici căsuța de e-mail înainte de a te autentifica.",
    "emailNotVerifiedLogin" to "Te rugăm să îți verifici e-mailul înainte de a te autentifica. Verifică-ți căsuța de e-mail pentru linkul de verificare.",
    "invalidOrExpiredMagicLink" to "Link magic invalid sau expirat. Te rugăm să soliciți unul nou.",
    "invalidToken" to "Token invalid.",
    "accountBanned" to "Contul tău a fost interzis.",
    "loginError" to "A apărut o eroare la autentificare.",
    "profileUpdated" to "Profil actualizat!",
    "displayNameRequired" to "Numele afișat nu poate fi gol",
    "saving" to "Se salvează...",
    "passwordSet" to "Parola este setată",
    "noPassword" to "Nicio parolă setată",
    "passwordChangeSuccess" to "Parola a fost schimbată cu succes!",
    "passwordChangeFailed" to "Schimbarea parolei a eșuat",
    "passwordSetSuccess" to "Parola a fost setată cu succes!",
    "passwordSetFailed" to "Setarea parolei a eșuat",
    "passwordsDoNotMatch" to "Parolele nu se potrivesc",
    "currentPasswordRequired" to "Parola curentă este obligatorie",
    "emailChangeSent" to "E-mail de verificare trimis la noua ta adresă de e-mail!",
    "emailChangeFailed" to "Solicitarea de schimbare a e-mailului a eșuat",
    "emailRequired" to "E-mailul este obligatoriu",
    "sendingEllipsis" to "Se trimite...",
    "resetLinkSent" to "Link de resetare a parolei trimis! Verifică-ți e-mailul.",
    "magicLinkSent" to "Link de autentificare trimis! Verifică-ți e-mailul.",
    "magicLinkSendFailed" to "Trimiterea linkului de autentificare a eșuat",
    "failedSendResetLink" to "Trimiterea linkului de resetare a eșuat.",
    "invalidOrMissingToken" to "Token invalid sau lipsă.",
    "passwordTooShort" to "Parola trebuie să aibă cel puțin 8 caractere.",
    "passwordNeedUppercase" to "Parola necesită cel puțin o literă mare.",
    "passwordNeedLowercase" to "Parola necesită cel puțin o literă mică.",
    "passwordNeedNumber" to "Parola necesită cel puțin o cifră.",
    "passwordNeedSymbol" to "Parola necesită cel puțin un simbol.",
    "resettingPassword" to "Se resetează parola...",
    "passwordResetSuccess" to "Parola a fost resetată cu succes! Te poți autentifica acum.",
    "failedResetPassword" to "Resetarea parolei a eșuat.",
    "loginSuccessRedirecting" to "Autentificare reușită! Se redirecționează...",
    "loginFailedExpiredLink" to "Autentificare eșuată. Linkul poate fi invalid sau expirat.",
    "loginFailed" to "Autentificare eșuată.",
    "emailChangedSuccess" to "E-mailul a fost schimbat cu succes!",
    "failedChangeEmailExpired" to "Schimbarea e-mailului a eșuat. Linkul poate fi invalid sau expirat.",
    "failedChangeEmail" to "Schimbarea e-mailului a eșuat.",
    "emailVerifiedSuccess" to "E-mail verificat cu succes!",
    "failedVerifyEmailExpired" to "Verificarea e-mailului a eșuat. Linkul poate fi invalid sau expirat.",
    "failedVerifyEmail" to "Verificarea e-mailului a eșuat.",
    "dropImagesHint" to "Plasează imagini aici sau apasă pentru a răsfoi",
    "forgotPasswordTitle" to "Ai uitat parola",
    "forgotPasswordIntro" to "Introdu adresa ta de e-mail și îți vom trimite un link pentru a-ți reseta parola.",
    "sendResetLink" to "Trimite link de resetare",
    "backToLogin" to "Înapoi la autentificare",
    "resetPasswordTitle" to "Resetează parola",
    "resetPasswordIntro" to "Introdu noua ta parolă mai jos.",
    "emailLinkLoginTitle" to "Autentificare prin link pe e-mail",
    "verifying" to "Se verifică...",
    "emailChangeTitle" to "Schimbarea e-mailului",
    "verifyContactEmailTitle" to "Verifică e-mailul de contact",
    "status" to "Stare",
    "yes" to "Da",
    "no" to "Nu",
    "noDescription" to "Fără descriere.",
    "urgentBadge" to "URGENT - Are nevoie de o casă curând!",
    "needsNewHome" to "Acest animal are nevoie urgentă de o casă nouă",
    "promotedReasonLabel" to "Motiv",
    "selectReason" to "Selectează un motiv",
    "promotedReasonMoving" to "Proprietarul se mută",
    "promotedReasonComplaints" to "Plângeri / nu mai poate ține alte animale",
    "promotedReasonPetConflict" to "Conflict cu un alt animal",
    "promotedReasonOther" to "Altul",
    "promotedReasonDetailLabel" to "Detalii suplimentare (opțional)",
    "promotedReasonRequired" to "Te rugăm să selectezi un motiv",
    "needsNewHomeBadge" to "Are nevoie de o casă nouă",
    "housingType" to "Tip de locuință",
    "preferNotToSay" to "Prefer să nu spun",
    "houseHousing" to "Casă",
    "apartmentHousing" to "Apartament",
    "hasYard" to "Are curte",
    "hasOtherPets" to "Alte animale în casă",
    "adoptionExperience" to "Experiență cu adopția",
    "firstTimeAdopter" to "Adoptator pentru prima dată",
    "experiencedAdopter" to "Adoptator cu experiență",
    "noAdoptionRequests" to "Nicio cerere de adopție",
    "noMessage" to "Niciun mesaj",
    "markUnderReview" to "Marchează în curs de analiză",
    "approve" to "Aprobă",
    "reject" to "Respinge",
    "reviewNoteLabel" to "Notă privată de analiză",
    "saveNote" to "Salvează nota",
    "adoptionStatusPending" to "În așteptare",
    "adoptionStatusUnderReview" to "În curs de analiză",
    "adoptionStatusApproved" to "Aprobat",
    "adoptionStatusRejected" to "Neaprobat",
    "myAdoptionRequests" to "Cererile mele de adopție",
    "noAdoptionRequestsYet" to "Nu ai solicitat încă adoptarea niciunui animal.",
    "medicalHistory" to "Istoric medical",
    "medicalSchedule" to "Program de vaccinare și deparazitare",
    "recordType" to "Tip",
    "vaccination" to "Vaccinare",
    "deworming" to "Deparazitare",
    "recordName" to "Nume",
    "dateGiven" to "Data administrării",
    "nextDueDate" to "Următoarea dată (opțional)",
    "notesOptional" to "Note (opțional)",
    "addRecord" to "Adaugă înregistrare",
    "noMedicalRecords" to "Nu există încă înregistrări de vaccinare sau deparazitare.",
    "nextDueLabel" to "Următoarea dată",
    "givenLabel" to "Administrat",
    "overdue" to "Întârziat",
    "dueSoon" to "În curând scadent",
    "upToDate" to "La zi",
    "recordNameRequired" to "Numele este obligatoriu",
    "dateGivenRequired" to "Data administrării este obligatorie",
    "failedToAddRecord" to "Adăugarea înregistrării a eșuat",
    "confirmDeleteRecord" to "Ștergi această înregistrare?",
    "maxCapacity" to "Numărul maxim de animale simultan (opțional)",
    "placePetHere" to "Plasează un animal aici",
    "noAvailablePetsToPlace" to "Nu ai animale disponibile pentru plasare momentan.",
    "selectPet" to "Selectează animalul",
    "startPlacementBtn" to "Începe plasarea",
    "placementStarted" to "Plasarea temporară a început!",
    "petsCurrentlyWithMe" to "Animale aflate în prezent la mine",
    "noPetsCurrentlyFostered" to "Niciun animal nu este plasat în prezent la tine.",
    "sinceLabel" to "din",
    "fosterPlacement" to "Plasament temporar",
    "endPlacement" to "Încheie plasarea",
    "currentlyWithLabel" to "În prezent la",
    "notCurrentlyFostered" to "Nu se află în prezent în plasament temporar.",
    "confirmEndPlacement" to "Încheiezi acest plasament temporar? Animalul va fi înregistrat ca fiind din nou la tine.",
    "aTemporalHome" to "o casă temporară",
    "viewsLabel" to "Vizualizări",
    "inquiriesLabel" to "Solicitări",
    "conversionLabel" to "Conversie",
    "messageOptional" to "Mesaj (opțional)",
    "requestAdoption" to "Solicită adopția",
    "editPet" to "Editează animalul",
    "adoptionRequestSubmitted" to "Cererea de adopție a fost trimisă!",
    "failedSubmitRequest" to "Trimiterea cererii a eșuat",
    "noPhotosYet" to "Nicio fotografie încă.",
    "rabbit" to "Iepure",
    "safeFoods" to "Alimente sigure",
    "harmfulFoods" to "Alimente dăunătoare",
    "cannotEatFoods" to "Nu poate mânca",
    "legendSafe" to "Sigur",
    "legendHarmful" to "Dăunător",
    "legendToxic" to "Toxic",
    "food_dog_safe_chicken_name" to "Pui",
    "food_dog_safe_chicken_detail" to "Gătit, fără oase",
    "food_dog_safe_chicken_desc" to "Proteină slabă, ușor de digerat",
    "food_dog_safe_rice_name" to "Orez",
    "food_dog_safe_rice_detail" to "Alb sau brun",
    "food_dog_safe_rice_desc" to "Sursă bună de carbohidrați",
    "food_dog_safe_carrots_name" to "Morcovi",
    "food_dog_safe_carrots_detail" to "Crud sau gătit",
    "food_dog_safe_carrots_desc" to "Sărac în calorii, bogat în fibre",
    "food_dog_safe_apples_name" to "Mere",
    "food_dog_safe_apples_detail" to "Fără sâmburi",
    "food_dog_safe_apples_desc" to "Bogate în vitamine",
    "food_dog_safe_peanutbutter_name" to "Unt de arahide",
    "food_dog_safe_peanutbutter_detail" to "Nesărat, fără xilitol",
    "food_dog_safe_peanutbutter_desc" to "Sursă bună de proteine",
    "food_dog_harmful_grapes_name" to "Struguri",
    "food_dog_harmful_grapes_detail" to "Toate soiurile",
    "food_dog_harmful_grapes_desc" to "Poate provoca insuficiență renală",
    "food_dog_harmful_onions_name" to "Ceapă",
    "food_dog_harmful_onions_detail" to "Toate formele",
    "food_dog_harmful_onions_desc" to "Afectează globulele roșii",
    "food_dog_harmful_chocolate_name" to "Ciocolată",
    "food_dog_harmful_chocolate_detail" to "Toate tipurile",
    "food_dog_harmful_chocolate_desc" to "Toxică pentru câini",
    "food_dog_harmful_garlic_name" to "Usturoi",
    "food_dog_harmful_garlic_detail" to "Toate formele",
    "food_dog_harmful_garlic_desc" to "Provoacă anemie",
    "food_dog_cannot_xylitol_name" to "Xilitol",
    "food_dog_cannot_xylitol_detail" to "Îndulcitor artificial",
    "food_dog_cannot_xylitol_desc" to "Foarte toxic",
    "food_dog_cannot_macadamia_name" to "Nuci de macadamia",
    "food_dog_cannot_macadamia_detail" to "Toate formele",
    "food_dog_cannot_macadamia_desc" to "Provoacă slăbiciune",
    "food_dog_cannot_avocado_name" to "Avocado",
    "food_dog_cannot_avocado_detail" to "Sâmbure, coajă, tot",
    "food_dog_cannot_avocado_desc" to "Conține persină",
    "food_dog_cannot_alcohol_name" to "Alcool",
    "food_dog_cannot_alcohol_detail" to "Toate formele",
    "food_dog_cannot_alcohol_desc" to "Foarte toxic",
    "food_cat_safe_cookedfish_name" to "Pește gătit",
    "food_cat_safe_cookedfish_detail" to "Fără oase",
    "food_cat_safe_cookedfish_desc" to "Bogat în proteine",
    "food_cat_safe_chicken_name" to "Pui",
    "food_cat_safe_chicken_detail" to "Gătit, simplu",
    "food_cat_safe_chicken_desc" to "Sursă bună de proteine",
    "food_cat_safe_pumpkin_name" to "Dovleac",
    "food_cat_safe_pumpkin_detail" to "Simplu, gătit",
    "food_cat_safe_pumpkin_desc" to "Ajută digestia",
    "food_cat_safe_eggs_name" to "Ouă",
    "food_cat_safe_eggs_detail" to "Gătite",
    "food_cat_safe_eggs_desc" to "Proteină completă",
    "food_cat_harmful_raweggs_name" to "Ouă crude",
    "food_cat_harmful_raweggs_detail" to "Conțin avidină",
    "food_cat_harmful_raweggs_desc" to "Interferează cu biotina",
    "food_cat_harmful_rawfish_name" to "Pește crud",
    "food_cat_harmful_rawfish_detail" to "Conține tiaminază",
    "food_cat_harmful_rawfish_desc" to "Descompune vitaminele B",
    "food_cat_harmful_dogfood_name" to "Hrană pentru câini",
    "food_cat_harmful_dogfood_detail" to "Orice",
    "food_cat_harmful_dogfood_desc" to "Lipsită de taurină",
    "food_cat_harmful_milk_name" to "Lapte",
    "food_cat_harmful_milk_detail" to "Majoritatea pisicilor",
    "food_cat_harmful_milk_desc" to "Intoleranță la lactoză",
    "food_cat_cannot_chocolate_name" to "Ciocolată",
    "food_cat_cannot_chocolate_detail" to "Toate tipurile",
    "food_cat_cannot_chocolate_desc" to "Teobromină toxică",
    "food_cat_cannot_onionsgarlic_name" to "Ceapă/Usturoi",
    "food_cat_cannot_onionsgarlic_detail" to "Toate formele",
    "food_cat_cannot_onionsgarlic_desc" to "Afectează globulele roșii",
    "food_cat_cannot_grapesraisins_name" to "Struguri/Stafide",
    "food_cat_cannot_grapesraisins_detail" to "Toate soiurile",
    "food_cat_cannot_grapesraisins_desc" to "Afectare renală",
    "food_cat_cannot_alcohol_name" to "Alcool",
    "food_cat_cannot_alcohol_detail" to "Toate formele",
    "food_cat_cannot_alcohol_desc" to "Foarte toxic",
    "food_bird_safe_seeds_name" to "Semințe",
    "food_bird_safe_seeds_detail" to "Amestec variat",
    "food_bird_safe_seeds_desc" to "Sursă bună de grăsimi",
    "food_bird_safe_fruits_name" to "Fructe",
    "food_bird_safe_fruits_detail" to "Majoritatea soiurilor",
    "food_bird_safe_fruits_desc" to "Vitamine și minerale",
    "food_bird_safe_vegetables_name" to "Legume",
    "food_bird_safe_vegetables_detail" to "Verdețuri cu frunze",
    "food_bird_safe_vegetables_desc" to "Nutrienți esențiali",
    "food_bird_safe_pellets_name" to "Granule",
    "food_bird_safe_pellets_detail" to "Formulate de calitate",
    "food_bird_safe_pellets_desc" to "Nutriție completă",
    "food_bird_harmful_avocado_name" to "Avocado",
    "food_bird_harmful_avocado_detail" to "Toate părțile",
    "food_bird_harmful_avocado_desc" to "Persină toxică",
    "food_bird_harmful_fruitpits_name" to "Sâmburi de fructe",
    "food_bird_harmful_fruitpits_detail" to "Măr, piersică",
    "food_bird_harmful_fruitpits_desc" to "Urme de cianură",
    "food_bird_harmful_salt_name" to "Sare",
    "food_bird_harmful_salt_detail" to "Orice formă",
    "food_bird_harmful_salt_desc" to "Toxică pentru păsări",
    "food_bird_harmful_caffeine_name" to "Cofeină",
    "food_bird_harmful_caffeine_detail" to "Cafea, ceai",
    "food_bird_harmful_caffeine_desc" to "Probleme cardiace",
    "food_bird_cannot_chocolate_name" to "Ciocolată",
    "food_bird_cannot_chocolate_detail" to "Toate tipurile",
    "food_bird_cannot_chocolate_desc" to "Teobromină toxică",
    "food_bird_cannot_onions_name" to "Ceapă",
    "food_bird_cannot_onions_detail" to "Toate formele",
    "food_bird_cannot_onions_desc" to "Afectează globulele roșii",
    "food_bird_cannot_garlic_name" to "Usturoi",
    "food_bird_cannot_garlic_detail" to "Toate formele",
    "food_bird_cannot_garlic_desc" to "Toxic",
    "food_bird_cannot_mushrooms_name" to "Ciuperci",
    "food_bird_cannot_mushrooms_detail" to "Soiuri sălbatice",
    "food_bird_cannot_mushrooms_desc" to "Toxine potențiale",
    "food_fish_safe_flakes_name" to "Fulgi",
    "food_fish_safe_flakes_detail" to "Mărci de calitate",
    "food_fish_safe_flakes_desc" to "Hrană completă pentru pești",
    "food_fish_safe_pellets_name" to "Granule",
    "food_fish_safe_pellets_detail" to "Plutitoare/scufundătoare",
    "food_fish_safe_pellets_desc" to "Nutriție echilibrată",
    "food_fish_safe_frozenfood_name" to "Hrană congelată",
    "food_fish_safe_frozenfood_detail" to "Larve de viermi, artemia",
    "food_fish_safe_frozenfood_desc" to "Sursă de proteine",
    "food_fish_safe_vegetables_name" to "Legume",
    "food_fish_safe_vegetables_detail" to "Dovlecel opărit",
    "food_fish_safe_vegetables_desc" to "Sursă de fibre",
    "food_fish_harmful_bread_name" to "Pâine",
    "food_fish_harmful_bread_detail" to "Orice tip",
    "food_fish_harmful_bread_desc" to "Fără nutriție, umple stomacul",
    "food_fish_harmful_humanfood_name" to "Hrană pentru oameni",
    "food_fish_harmful_humanfood_detail" to "Mese gătite",
    "food_fish_harmful_humanfood_desc" to "Nutriție necorespunzătoare",
    "food_fish_harmful_livefeed_name" to "Hrană vie",
    "food_fish_harmful_livefeed_detail" to "Insecte capturate din sălbăticie",
    "food_fish_harmful_livefeed_desc" to "Paraziți potențiali",
    "food_fish_cannot_landinsect_name" to "Insecte terestre",
    "food_fish_cannot_landinsect_detail" to "Insecte care se târăsc",
    "food_fish_cannot_landinsect_desc" to "Nu este hrană naturală",
    "food_fish_cannot_mammalmeat_name" to "Carne de mamifere",
    "food_fish_cannot_mammalmeat_detail" to "Pui, vită",
    "food_fish_cannot_mammalmeat_desc" to "Nepotrivită pentru majoritatea peștilor",
    "food_fish_cannot_dairy_name" to "Lactate",
    "food_fish_cannot_dairy_detail" to "Orice",
    "food_fish_cannot_dairy_desc" to "Nu pot digera",
    "food_fish_cannot_breadcrumbs_name" to "Pesmet",
    "food_fish_cannot_breadcrumbs_detail" to "Multe conțin usturoi",
    "food_fish_cannot_breadcrumbs_desc" to "Aditivi dăunători",
    "food_rabbit_safe_hay_name" to "Fân",
    "food_rabbit_safe_hay_detail" to "Iarbă Timothy",
    "food_rabbit_safe_hay_desc" to "80% din dietă",
    "food_rabbit_safe_leafygreens_name" to "Verdețuri cu frunze",
    "food_rabbit_safe_leafygreens_detail" to "Salată romană, coriandru",
    "food_rabbit_safe_leafygreens_desc" to "Vitamine zilnice",
    "food_rabbit_safe_pellets_name" to "Granule",
    "food_rabbit_safe_pellets_detail" to "Pe bază de Timothy",
    "food_rabbit_safe_pellets_desc" to "Nutriție echilibrată",
    "food_rabbit_safe_carrots_name" to "Morcovi",
    "food_rabbit_safe_carrots_detail" to "Cu moderație",
    "food_rabbit_safe_carrots_desc" to "Recompensă",
    "food_rabbit_harmful_icebergletttuce_name" to "Salată iceberg",
    "food_rabbit_harmful_icebergletttuce_detail" to "Fără valoare nutritivă",
    "food_rabbit_harmful_icebergletttuce_desc" to "Poate provoca stază gastrointestinală",
    "food_rabbit_harmful_beans_name" to "Fasole",
    "food_rabbit_harmful_beans_detail" to "Toate leguminoasele",
    "food_rabbit_harmful_beans_desc" to "Balonare, probleme digestive",
    "food_rabbit_harmful_corn_name" to "Porumb",
    "food_rabbit_harmful_corn_detail" to "Orice formă",
    "food_rabbit_harmful_corn_desc" to "Nu poate fi digerat",
    "food_rabbit_harmful_potatoes_name" to "Cartofi",
    "food_rabbit_harmful_potatoes_detail" to "Orice formă",
    "food_rabbit_harmful_potatoes_desc" to "Nutrienți nepotriviți",
    "food_rabbit_cannot_chocolate_name" to "Ciocolată",
    "food_rabbit_cannot_chocolate_detail" to "Toate tipurile",
    "food_rabbit_cannot_chocolate_desc" to "Teobromină toxică",
    "food_rabbit_cannot_onions_name" to "Ceapă",
    "food_rabbit_cannot_onions_detail" to "Toate formele",
    "food_rabbit_cannot_onions_desc" to "Foarte toxică",
    "food_rabbit_cannot_garlic_name" to "Usturoi",
    "food_rabbit_cannot_garlic_detail" to "Toate formele",
    "food_rabbit_cannot_garlic_desc" to "Toxic",
    "food_rabbit_cannot_avocado_name" to "Avocado",
    "food_rabbit_cannot_avocado_detail" to "Toate părțile",
    "food_rabbit_cannot_avocado_desc" to "Conține persină",
    "primary" to "Principal",
    "passkeyRegisteredSuccess" to "Passkey înregistrat cu succes!",
    "passkeyRegistrationFailed" to "Înregistrarea passkey-ului a eșuat",
    "passkeyNameRequired" to "Numele passkey-ului este obligatoriu",
    "registeringPasskey" to "Se înregistrează passkey-ul...",
    "passkeyRegistered" to "Passkey înregistrat",
    "noPasskey" to "Niciun passkey înregistrat",
    "noPets" to "Niciun animal încă.",
    "photographerCountryStateRequired" to "Te rugăm să completezi țara și statul/județul pentru serviciile de fotografie",
    "temporalHomeAliasCountryCityRequired" to "Te rugăm să completezi aliasul, țara și orașul pentru casa temporară",
    "shelterNameCountryCityAddressRequired" to "Te rugăm să completezi numele, țara, orașul și adresa pentru adăpost",
    "sterilizationNameCountryCityAddressRequired" to "Te rugăm să completezi numele, țara, orașul și adresa pentru locația de sterilizare",
    "country.afghanistan" to "Afganistan",
    "country.albania" to "Albania",
    "country.algeria" to "Algeria",
    "country.argentina" to "Argentina",
    "country.armenia" to "Armenia",
    "country.australia" to "Australia",
    "country.austria" to "Austria",
    "country.azerbaijan" to "Azerbaidjan",
    "country.bangladesh" to "Bangladesh",
    "country.belarus" to "Belarus",
    "country.belgium" to "Belgia",
    "country.bolivia" to "Bolivia",
    "country.bosnia" to "Bosnia și Herțegovina",
    "country.brazil" to "Brazilia",
    "country.bulgaria" to "Bulgaria",
    "country.cambodia" to "Cambodgia",
    "country.cameroon" to "Camerun",
    "country.canada" to "Canada",
    "country.chile" to "Chile",
    "country.china" to "China",
    "country.colombia" to "Columbia",
    "country.costaRica" to "Costa Rica",
    "country.croatia" to "Croația",
    "country.cuba" to "Cuba",
    "country.czechia" to "Cehia",
    "country.denmark" to "Danemarca",
    "country.dominicanRepublic" to "Republica Dominicană",
    "country.ecuador" to "Ecuador",
    "country.egypt" to "Egipt",
    "country.elSalvador" to "El Salvador",
    "country.estonia" to "Estonia",
    "country.ethiopia" to "Etiopia",
    "country.finland" to "Finlanda",
    "country.france" to "Franța",
    "country.georgia" to "Georgia",
    "country.germany" to "Germania",
    "country.ghana" to "Ghana",
    "country.greece" to "Grecia",
    "country.guatemala" to "Guatemala",
    "country.haiti" to "Haiti",
    "country.honduras" to "Honduras",
    "country.hungary" to "Ungaria",
    "country.iceland" to "Islanda",
    "country.india" to "India",
    "country.indonesia" to "Indonezia",
    "country.iran" to "Iran",
    "country.iraq" to "Irak",
    "country.ireland" to "Irlanda",
    "country.israel" to "Israel",
    "country.italy" to "Italia",
    "country.jamaica" to "Jamaica",
    "country.japan" to "Japonia",
    "country.jordan" to "Iordania",
    "country.kazakhstan" to "Kazahstan",
    "country.kenya" to "Kenya",
    "country.kuwait" to "Kuweit",
    "country.latvia" to "Letonia",
    "country.lebanon" to "Liban",
    "country.libya" to "Libia",
    "country.lithuania" to "Lituania",
    "country.luxembourg" to "Luxemburg",
    "country.malaysia" to "Malaysia",
    "country.mexico" to "Mexic",
    "country.moldova" to "Moldova",
    "country.mongolia" to "Mongolia",
    "country.montenegro" to "Muntenegru",
    "country.morocco" to "Maroc",
    "country.myanmar" to "Myanmar",
    "country.nepal" to "Nepal",
    "country.netherlands" to "Țările de Jos",
    "country.newZealand" to "Noua Zeelandă",
    "country.nicaragua" to "Nicaragua",
    "country.nigeria" to "Nigeria",
    "country.northKorea" to "Coreea de Nord",
    "country.norway" to "Norvegia",
    "country.pakistan" to "Pakistan",
    "country.panama" to "Panama",
    "country.paraguay" to "Paraguay",
    "country.peru" to "Peru",
    "country.philippines" to "Filipine",
    "country.poland" to "Polonia",
    "country.portugal" to "Portugalia",
    "country.puertoRico" to "Puerto Rico",
    "country.qatar" to "Qatar",
    "country.romania" to "România",
    "country.russia" to "Rusia",
    "country.saudiArabia" to "Arabia Saudită",
    "country.serbia" to "Serbia",
    "country.singapore" to "Singapore",
    "country.slovakia" to "Slovacia",
    "country.slovenia" to "Slovenia",
    "country.southAfrica" to "Africa de Sud",
    "country.southKorea" to "Coreea de Sud",
    "country.spain" to "Spania",
    "country.sriLanka" to "Sri Lanka",
    "country.sudan" to "Sudan",
    "country.sweden" to "Suedia",
    "country.switzerland" to "Elveția",
    "country.syria" to "Siria",
    "country.taiwan" to "Taiwan",
    "country.thailand" to "Thailanda",
    "country.tunisia" to "Tunisia",
    "country.turkey" to "Turcia",
    "country.ukraine" to "Ucraina",
    "country.uae" to "Emiratele Arabe Unite",
    "country.uk" to "Regatul Unit",
    "country.usa" to "Statele Unite",
    "country.uruguay" to "Uruguay",
    "country.uzbekistan" to "Uzbekistan",
    "country.venezuela" to "Venezuela",
    "country.vietnam" to "Vietnam",
    "country.yemen" to "Yemen",
    "loading" to "Se încarcă...",
    "countryRequired" to "Te rugăm să selectezi o țară pentru a căuta",
    "noSheltersFound" to "Niciun adăpost găsit încă. Revino mai târziu!",
    "visitWebsite" to "Vizitează website-ul",
    "fiscalId" to "CIF/CUI",
    "donationInformation" to "Informații despre donații",
    "bankName" to "Bancă",
    "accountHolder" to "Titular cont",
    "accountNumber" to "Număr de cont",
    "currency" to "Monedă",
    "errorLoadingShelters" to "Eroare la încărcarea adăposturilor",
    "pleaseSelectCountry" to "Te rugăm să selectezi o țară",
    "actions" to "Acțiuni",
    "addShelter" to "Adaugă adăpost",
    "addEditShelter" to "Adaugă / Editează adăpost",
    "allStates" to "Toate statele/județele",
    "confirmDelete" to "Ești sigur că vrei să ștergi această locație?",
    "confirmDeleteShelter" to "Ești sigur că vrei să ștergi acest adăpost?",
    "confirmUnbanUser" to "Deblochezi acest utilizator?",
    "confirmResetUserCredentials" to "Aceasta va invalida parola și passkey-urile curente ale utilizatorului {email} și îi va trimite un link de resetare prin e-mail. Continui?",
    "confirmDeactivateUser" to "Dezactivezi acest utilizator? Poate fi reactivat ulterior.",
    "confirmDeactivatePet" to "Dezactivezi acest animal? Va fi ascuns din listările publice și poate fi reactivat ulterior.",
    "confirmBlockRescuer" to "Blochezi acest salvator să îți mai trimită cereri?",
    "confirmDeletePet" to "Ștergi acest animal?",
    "confirmDeleteStorage" to "Ștergi acest spațiu de stocare?",
    "contact" to "Contact",
    "delete" to "Șterge",
    "edit" to "Editează",
    "errorDeletingShelter" to "Eroare la ștergerea adăpostului",
    "errorLoadingLocations" to "Eroare la încărcarea locațiilor",
    "errorLoadingShelter" to "Eroare la încărcarea adăpostului",
    "errorSaving" to "Eroare la salvare",
    "errorSavingShelter" to "Eroare la salvarea adăpostului",
    "location" to "Locație",
    "noLocationsFound" to "Nicio locație găsită",
    "selectCountry" to "Selectează o țară",
    "selectCountryToFilter" to "Te rugăm să selectezi o țară pentru a filtra",
    "shelterAdded" to "Adăpost adăugat cu succes",
    "shelterDeleted" to "Adăpost șters cu succes",
    "shelterUpdated" to "Adăpost actualizat cu succes",
    "updateShelter" to "Actualizează adăpostul",
    "errorLoadingPhotographers" to "Eroare la încărcarea fotografilor",
    "noPhotographersAvailable" to "Niciun fotograf disponibil",
    "requestPhotoSession" to "Solicită o sesiune foto",
    "selectPhotographer" to "Selectează",
    "photographersSelected" to "{count} selectați",
    "requestSelectedPhotographers" to "Solicită fotografii selectați",
    "clearSelection" to "Șterge selecția",
    "requestToMultiple" to "Solicită către",
    "messageRequired" to "Vă rugăm să scrieți un mesaj pentru fotografi.",
    "requestsSentSuccessfully" to "Cererile au fost trimise cu succes",
    "message" to "Mesaj",
    "sendRequest" to "Trimite cererea",
    "requestSentSuccessfully" to "Cererea a fost trimisă cu succes!",
    "requestTo" to "Cerere către",
    "enterMessage" to "Introdu un mesaj pentru {name}...",
    "reportUrgent" to "Urgență",
    "topRescuers" to "Cei mai buni salvatori",
    "urgentRescuerSettings" to "Setări salvator de urgență",
    "urgentRescueAlerts" to "Alerte de salvare urgentă",
    "reportUrgentTitle" to "Raportează un animal în pericol",
    "reportUrgentIntro" to "Dacă ai găsit un animal rănit, maltratat, înfometat sau abandonat care are nevoie de ajutor imediat, spune-ne unde și ce este în neregulă — vom alerta imediat salvatorii de urgență din apropiere.",
    "dangerType" to "Ce este în neregulă?",
    "dangerInjured" to "Rănit",
    "dangerAbused" to "Este maltratat / abuzat",
    "dangerStarving" to "Înfometat",
    "dangerTooYoung" to "Prea tânăr pentru a supraviețui singur",
    "dangerOther" to "Altă urgență",
    "useMyLocation" to "Folosește locația mea curentă",
    "confirmMyLocation" to "Confirmă locația mea curentă",
    "locationPermissionHint" to "Browserul îți va cere permisiunea de a partaja locația; este folosită doar pentru a te anunța despre raportările din apropiere.",
    "phoneOptional" to "Telefonul tău (opțional)",
    "submitReport" to "Trimite raportul",
    "urgentRescuerSettingsTitle" to "Setări salvator de urgență",
    "urgentRescuerSettingsIntro" to "Fii înștiințat când este raportat un animal în pericol în apropierea zonei tale de acoperire. Primul care acceptă preia cazul.",
    "urgentRescuerActive" to "Sunt disponibil să răspund la rapoartele urgente",
    "coverageAreaMode" to "Zonă de acoperire",
    "coverageArea" to "Zonă de acoperire",
    "coverageAreaHint" to "Confirmă locația actuală, trage pinul sau scrie o adresă mai jos - harta și câmpurile rămân sincronizate.",
    "useMyLocationAndRadius" to "Folosește locația mea + o rază",
    "pickAZone" to "Alege în schimb o țară/stat/oraș",
    "radiusKm" to "Rază (km)",
    "urgentRescueAlertsTitle" to "Alerte de salvare urgentă",
    "urgentRescuerLeaderboardTitle" to "Cei mai buni salvatori de urgență (ultimele 12 luni)",
    "locating" to "Se localizează...",
    "geolocationUnsupported" to "Geolocalizarea nu este acceptată de browserul tău",
    "locationCaptured" to "Locație capturată",
    "locationDenied" to "Nu am putut obține locația ta - te rugăm să permiți accesul la locație sau introdu un oraș",
    "street" to "Stradă",
    "exteriorNumber" to "Număr exterior",
    "referenceNotes" to "Note de referință (ex. culoarea casei, reper din apropiere)",
    "concreteAddressHint" to "Adaugă o stradă și un număr dacă poți - acest lucru îi permite unui salvator să ia un taxi sau să deschidă o aplicație de hărți direct către locație.",
    "descriptionRequired" to "Te rugăm să descrii situația",
    "locationRequired" to "Te rugăm să oferi o locație (folosește locația curentă sau introdu o țară și un oraș)",
    "submittingEllipsis" to "Se trimite...",
    "reportSubmitted" to "Raport trimis. Mulțumim - salvatorii din apropiere sunt alertați acum.",
    "reportSubmitFailed" to "Trimiterea raportului a eșuat",
    "phoneRequired" to "Te rugăm să introduci un număr de telefon",
    "savingEllipsis" to "Se salvează...",
    "saved" to "Salvat",
    "saveFailed" to "Salvarea a eșuat",
    "noUrgentPages" to "Nicio alertă urgentă în așteptare momentan",
    "accept" to "Acceptă",
    "alreadyAccepted" to "Altcineva a acceptat deja acest raport",
    "noLeaderboardData" to "Nicio salvare acceptată în ultimele 12 luni încă",
    "rescueAcceptedTitle" to "Salvare acceptată",
    "rescueAcceptedMessage" to "Mulțumim - te ocupi de asta. Îndreaptă-te spre locația raportată cât mai curând posibil.",
    "rescueAcceptFailedTitle" to "Nu s-a putut accepta",
    "rescueAcceptFailedMessage" to "Acest raport a fost deja acceptat de un alt salvator, sau linkul este invalid sau a expirat.",
    "share" to "Distribuie",
    "shareViaFacebook" to "Distribuie pe Facebook",
    "shareViaX" to "Distribuie pe X",
    "shareViaWhatsapp" to "Distribuie pe WhatsApp",
    "shareViaEmail" to "Distribuie prin e-mail",
    "copyLink" to "Copiază linkul",
    "linkCopied" to "Link copiat!",
    "reportLostFoundTitle" to "Raportează un animal pierdut sau găsit",
    "reportLostFoundIntro" to "Ți-ai pierdut animalul sau ai găsit unul fără stăpân? Completează un raport și îl vom compara cu alte rapoarte din apropiere.",
    "browseLostFound" to "Răsfoiește rapoartele existente de animale pierdute și găsite",
    "kindLost" to "Mi-am pierdut animalul",
    "kindFound" to "Am găsit un animal",
    "petType" to "Tipul animalului",
    "petTypeUnknown" to "Nu sunt sigur",
    "lostFoundBrowseTitle" to "Animale pierdute și găsite",
    "showing" to "Se afișează",
    "kindLostPlural" to "Animale pierdute",
    "kindFoundPlural" to "Animale găsite",
    "reportResolvedTitle" to "Raport rezolvat",
    "reportResolvedMessage" to "Mulțumim pentru actualizare - acest raport este acum închis.",
    "reportResolveFailedTitle" to "Nu s-a putut rezolva",
    "reportResolveFailedMessage" to "Acest raport a fost deja rezolvat, sau linkul este invalid sau a expirat.",
    "reportLostFound" to "Pierdut/\u200BGăsit",
    "noLostFoundReportsFound" to "Niciun raport de animale pierdute sau găsite găsit",
    "errorLoadingReports" to "Eroare la încărcarea rapoartelor",
    "yourEmail" to "E-mailul tău",
    "contactReporter" to "Contactează raportorul",
    "contactFormIncomplete" to "Te rugăm să introduci e-mailul tău și un mesaj",
    "contactSent" to "Mesaj trimis",
    "contactFailed" to "Trimiterea mesajului a eșuat",
    "lostFoundReportSubmitted" to "Raport trimis. Mulțumim - îl vom compara cu alte rapoarte din apropiere.",
    "video" to "Video (opțional)",
    "removeVideo" to "Elimină videoclipul",
    "saveThisSearch" to "Salvează această căutare",
    "savedSearchSaved" to "Căutare salvată! Îți vom trimite un e-mail când este listată o nouă potrivire.",
    "savedSearchFailed" to "Salvarea căutării a eșuat",
    "favorite" to "Favorit",
    "addToFavorites" to "Adaugă la favorite",
    "removeFromFavorites" to "Elimină din favorite",
    "myFavorites" to "Favoritele mele",
    "noFavoritesYet" to "Niciun favorit încă",
    "mySavedSearches" to "Căutările mele salvate",
    "noSavedSearchesYet" to "Nicio căutare salvată încă",
    "donationPromptText" to "Îți mulțumim că ai ajutat un animal astăzi. Dacă poți, ia în considerare o mică donație pentru a menține Adopt-U gratuit pentru toată lumea.",
    "donationPromptCta" to "Donează",
    "dismiss" to "Închide",
)

private val hindiTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "लॉगिन",
    "register" to "रजिस्टर करें",
    "logout" to "सत्र बंद करें",
    "profile" to "प्रोफ़ाइल",
    "myPets" to "मेरे पालतू जानवर",
    "addPet" to "पालतू जानवर जोड़ें",
    "save" to "सहेजें",
    "cancel" to "रद्द करें",
    "search" to "खोजें",
    "shelters" to "आश्रय स्थल",
    "photographers" to "फ़ोटोग्राफर",
    "temporalHomes" to "अस्थायी घर",
    "findTemporalHomes" to "अस्थायी घर खोजें",
    "sterilizationLocations" to "नसबंदी केंद्र",
    "donate" to "दान करें",
    "countryLabel" to "देश",
    "selectCountryToSearch" to "एक देश चुनें",
    "selectCountryFirst" to "इन फ़िल्टर को सक्षम करने के लिए एक देश चुनें",
    "state" to "राज्य",
    "city" to "शहर",
    "zipCode" to "पिन कोड",
    "neighborhood" to "मोहल्ला",
    "name" to "नाम",
    "email" to "ईमेल",
    "phone" to "फ़ोन",
    "address" to "पता",
    "description" to "विवरण",
    "animalShelters" to "पशु आश्रय स्थल",
    "sheltersDescription" to "ऐसे पशु आश्रय स्थल खोजें जहाँ आप गोद लेने के लिए पालतू जानवर पा सकते हैं या आश्रय स्थल के साथ पूर्व सहमति से उन्हें वहाँ छोड़ सकते हैं",
    "photographerDescription" to "पेशेवर फ़ोटोग्राफर जो पालतू जानवरों के फ़ोटो सेशन प्रदान करते हैं",
    "sterilizationLocationsDescription" to "ऐसे स्थान खोजें जहाँ आप जानवरों की नसबंदी करवा सकते हैं।",
    "privacyPolicy" to "गोपनीयता नीति",
    "privacyLastUpdated" to "अंतिम अद्यतन: अप्रैल 2025",
    "privacyHighlight" to "Adopt-U में, हम न्यूनतम डेटा संग्रह में विश्वास करते हैं। हम गोद लेने वालों और बचावकर्ताओं के बीच संचार सक्षम करने के लिए केवल आपका ईमेल पता एकत्र करते हैं। हम आपकी जानकारी का उपयोग विज्ञापन के लिए नहीं करते हैं, और हम विपणन उद्देश्यों के लिए कभी भी आपका डेटा तीसरे पक्षों के साथ साझा नहीं करेंगे।",
    "privacyInfoCollectedH2" to "हम जो जानकारी एकत्र करते हैं",
    "privacyAccountRegH3" to "खाता पंजीकरण",
    "privacyAccountRegIntro" to "Adopt-U पर खाता बनाने के लिए, हम निम्नलिखित जानकारी एकत्र करते हैं:",
    "privacyEmailLabel" to "ईमेल पता: ",
    "privacyEmailDesc" to "खाता सत्यापन, पासवर्ड पुनर्प्राप्ति, और गोद लेने वालों तथा बचावकर्ताओं के बीच संचार के लिए आवश्यक",
    "privacyDisplayNameLabel" to "प्रदर्शन नाम: ",
    "privacyDisplayNameDesc" to "एक नाम जिसे आप प्लेटफ़ॉर्म पर पहचाने जाने के लिए चुनते हैं",
    "privacyLangPrefLabel" to "भाषा प्राथमिकता: ",
    "privacyLangPrefDesc" to "प्लेटफ़ॉर्म को आपकी पसंदीदा भाषा में प्रदान करने के लिए",
    "privacyNoFullNameEtc" to "हम आपका पूरा नाम, फ़ोन नंबर, भौतिक पता, पहचान दस्तावेज़, भुगतान जानकारी, या ऊपर सूचीबद्ध के अलावा कोई अन्य व्यक्तिगत डेटा एकत्र नहीं करते हैं।",
    "privacyOptionalProfileH3" to "वैकल्पिक प्रोफ़ाइल जानकारी",
    "privacyOptionalProfileIntro" to "आपकी भूमिका के आधार पर, आप वैकल्पिक रूप से प्रदान कर सकते हैं:",
    "privacyAdopterLabel" to "गोद लेने वाले के रूप में: ",
    "privacyAdopterDesc" to "कोई अतिरिक्त जानकारी आवश्यक या एकत्रित नहीं की जाती",
    "privacyRescuerLabel" to "बचावकर्ता के रूप में: ",
    "privacyRescuerDesc" to "उन पालतू जानवरों के लिए स्थान जानकारी जो आपके पास गोद लेने हेतु उपलब्ध हैं",
    "privacyPhotographerLabel" to "फ़ोटोग्राफर के रूप में: ",
    "privacyPhotographerDesc" to "सेवा स्थान और संपर्क प्राथमिकताएँ",
    "privacyTemporalHomeLabel" to "अस्थायी घर के रूप में: ",
    "privacyTemporalHomeDesc" to "वह स्थान जहाँ आप अस्थायी आवास प्रदान कर सकते हैं",
    "privacyLocationUsage" to "सभी स्थान जानकारी का उपयोग केवल गोद लेने वालों को नज़दीकी बचावकर्ताओं और सेवा प्रदाताओं से मिलाने के लिए किया जाता है।",
    "privacyPetInfoH3" to "पालतू जानवर की जानकारी",
    "privacyPetInfoIntro" to "बचावकर्ता पालतू जानवरों के बारे में जानकारी पोस्ट कर सकते हैं, जिसमें शामिल है:",
    "privacyPetPhotos" to "पालतू जानवर की तस्वीरें",
    "privacyPetDescBreedAge" to "विवरण, नस्ल, उम्र, और विशेषताएँ",
    "privacyPetHealth" to "स्वास्थ्य और टीकाकरण की स्थिति",
    "privacyPetLocation" to "वह स्थान जहाँ पालतू जानवर उपलब्ध है",
    "privacyPetInfoNotVerified" to "यह जानकारी बचावकर्ताओं द्वारा प्रदान की जाती है और Adopt-U द्वारा स्वतंत्र रूप से सत्यापित नहीं की जाती है।",
    "privacyHowWeUseH2" to "हम आपकी जानकारी का उपयोग कैसे करते हैं",
    "privacyPrimaryPurposeH3" to "मुख्य उद्देश्य: गोद लेने के संबंध स्थापित करना",
    "privacyEmailUsedFor" to "आपका ईमेल पता विशेष रूप से निम्नलिखित उद्देश्यों के लिए उपयोग किया जाता है:",
    "privacyUseVerification" to "जब आप खाता बनाते हैं तो आपको एक सत्यापन लिंक भेजना",
    "privacyUseRescuerContact" to "बचावकर्ताओं को उन पालतू जानवरों के बारे में आपसे संपर्क करने की अनुमति देना जिनमें आपकी रुचि है",
    "privacyUseContactRescuer" to "आपको उनके सूचीबद्ध पालतू जानवरों के बारे में बचावकर्ताओं से संपर्क करने की अनुमति देना",
    "privacyUseNotifications" to "आपको महत्वपूर्ण खाता और सुरक्षा सूचनाएँ भेजना",
    "privacyOnceConnected" to "एक बार जब आप हमारे प्लेटफ़ॉर्म के माध्यम से किसी अन्य उपयोगकर्ता से जुड़ जाते हैं, तो आगे का सारा संचार सीधे आपके और उस पक्ष के बीच होता है। हम आपके संचार की सामग्री की निगरानी, संग्रहण या उस तक पहुँच नहीं रखते हैं।",
    "privacyNeverDoH3" to "हम कभी क्या नहीं करेंगे",
    "privacyCommitIntro" to "हम गंभीरता से निम्नलिखित के प्रति प्रतिबद्ध हैं:",
    "privacyNoAdsLabel" to "कोई विज्ञापन नहीं: ",
    "privacyNoAdsDesc" to "हम आपकी गतिविधि के आधार पर कभी भी लक्षित या गैर-लक्षित विज्ञापन प्रदर्शित नहीं करेंगे",
    "privacyNoMarketingLabel" to "कोई विपणन नहीं: ",
    "privacyNoMarketingDesc" to "हम विपणन या प्रचार उद्देश्यों के लिए आपका ईमेल कभी भी तीसरे पक्षों के साथ साझा नहीं करेंगे",
    "privacyNoSellingLabel" to "कोई डेटा बिक्री नहीं: ",
    "privacyNoSellingDesc" to "हम आपकी व्यक्तिगत जानकारी को किसी भी तीसरे पक्ष को कभी भी बेचेंगे, किराए पर नहीं देंगे, या अन्यथा स्थानांतरित नहीं करेंगे",
    "privacyNoProfilingLabel" to "कोई प्रोफ़ाइलिंग नहीं: ",
    "privacyNoProfilingDesc" to "हम बुनियादी खाता प्रबंधन से परे किसी भी उद्देश्य के लिए प्रोफ़ाइल बनाने हेतु आपके डेटा का उपयोग नहीं करेंगे",
    "privacyNoTrackingLabel" to "कोई ट्रैकिंग नहीं: ",
    "privacyNoTrackingDesc" to "हम ट्रैकिंग पिक्सेल, विज्ञापन के लिए कुकीज़, या किसी भी समान ट्रैकिंग तकनीक का उपयोग नहीं करते हैं",
    "privacyDataStorageH2" to "डेटा भंडारण और सुरक्षा",
    "privacyDataStoredSecurely" to "आपका डेटा उद्योग-मानक एन्क्रिप्शन का उपयोग करके सुरक्षित रूप से संग्रहीत किया जाता है। हम आपकी व्यक्तिगत जानकारी को अनधिकृत पहुँच, परिवर्तन, प्रकटीकरण, या विनाश से बचाने के लिए उपयुक्त तकनीकी और संगठनात्मक उपाय अपनाते हैं।",
    "privacyDataRetention" to "हम आपके खाते की जानकारी तब तक बनाए रखते हैं जब तक आपका खाता सक्रिय रहता है। आप हमसे संपर्क करके किसी भी समय अपने खाते और संबंधित डेटा को हटाने का अनुरोध कर सकते हैं।",
    "privacyYourRightsH2" to "आपके अधिकार",
    "privacyRightsIntro" to "आपके व्यक्तिगत डेटा के संबंध में आपके पास निम्नलिखित अधिकार हैं:",
    "privacyRightAccess" to "पहुँच: आप हमारे पास मौजूद आपके सभी व्यक्तिगत डेटा की एक प्रति का अनुरोध कर सकते हैं",
    "privacyRightCorrection" to "सुधार: आप अनुरोध कर सकते हैं कि हम किसी भी गलत जानकारी को सुधारें",
    "privacyRightDeletion" to "विलोपन: आप अनुरोध कर सकते हैं कि हम आपका खाता और सभी संबंधित डेटा हटा दें",
    "privacyRightPortability" to "पोर्टेबिलिटी: आप अनुरोध कर सकते हैं कि हम आपका डेटा एक सामान्य रूप से उपयोग किए जाने वाले प्रारूप में प्रदान करें",
    "privacyExerciseRights" to "इनमें से किसी भी अधिकार का प्रयोग करने के लिए, कृपया हमसे adopt-u@adopt-u.org पर संपर्क करें।",
    "privacyCookiesH2" to "कुकीज़",
    "privacyCookiesText" to "Adopt-U प्लेटफ़ॉर्म कार्यक्षमता के लिए आवश्यक न्यूनतम कुकीज़ का उपयोग करता है, जिसमें सत्र प्रबंधन और भाषा प्राथमिकताएँ शामिल हैं। हम विज्ञापन कुकीज़ या ट्रैकिंग कुकीज़ का उपयोग नहीं करते हैं।",
    "privacyThirdPartyH2" to "तृतीय-पक्ष सेवाएँ",
    "privacyThirdPartyIntro" to "हम निम्नलिखित तृतीय-पक्ष सेवाओं का उपयोग करते हैं, जो आपके डेटा को प्रोसेस कर सकती हैं:",
    "privacyAws" to "AWS (Amazon Web Services): डेटा भंडारण और ईमेल डिलीवरी के लिए। आपके डेटा के उनके प्रसंस्करण पर उनकी गोपनीयता नीतियाँ लागू होती हैं।",
    "privacyWebauthn" to "WebAuthn/FIDO2: सुरक्षित पासकी प्रमाणीकरण के लिए। इसमें आपके ब्राउज़र में क्रिप्टोग्राफ़िक ऑपरेशन शामिल हैं।",
    "privacyThirdPartySelected" to "हमने ऐसे सेवा प्रदाताओं का सावधानीपूर्वक चयन किया है जो डेटा गोपनीयता के प्रति हमारी प्रतिबद्धता साझा करते हैं।",
    "privacyChildrensH2" to "बच्चों की गोपनीयता",
    "privacyChildrensText" to "Adopt-U 18 वर्ष से कम आयु के उपयोगकर्ताओं के लिए नहीं है। हम जानबूझकर नाबालिगों से जानकारी एकत्र नहीं करते हैं। यदि आपको लगता है कि किसी नाबालिग ने खाता बनाया है, तो कृपया उसे हटवाने के लिए हमसे संपर्क करें।",
    "privacyChangesH2" to "इस नीति में परिवर्तन",
    "privacyChangesText" to "हम अपनी प्रथाओं या कानूनी आवश्यकताओं में बदलाव को दर्शाने के लिए समय-समय पर इस गोपनीयता नीति को अद्यतन कर सकते हैं। हम महत्वपूर्ण परिवर्तनों के बारे में उपयोगकर्ताओं को ईमेल या प्लेटफ़ॉर्म पर प्रमुख सूचना के माध्यम से सूचित करेंगे।",
    "privacyContactH2" to "हमसे संपर्क करें",
    "privacyContactText" to "यदि इस गोपनीयता नीति के बारे में आपके कोई प्रश्न या चिंताएँ हैं, या अपने डेटा अधिकारों का प्रयोग करना चाहते हैं, तो कृपया हमसे adopt-u@adopt-u.org पर संपर्क करें।",
    "termsConditions" to "नियम और शर्तें",
    "installApp" to "ऐप इंस्टॉल करें",
    "termsAcceptanceH2" to "शर्तों की स्वीकृति",
    "termsAcceptanceText" to "Adopt-U तक पहुँच बनाकर और उसका उपयोग करके, आप इस समझौते की शर्तों और प्रावधानों से बंधे रहने के लिए स्वीकार करते हैं और सहमत होते हैं। यदि आप इन शर्तों से सहमत नहीं हैं, तो कृपया हमारे प्लेटफ़ॉर्म का उपयोग न करें।",
    "termsNatureH2" to "हमारी सेवा की प्रकृति",
    "termsNatureText1" to "Adopt-U एक संपर्क प्लेटफ़ॉर्म है जो पालतू जानवरों के बचावकर्ताओं और संभावित गोद लेने वालों के बीच संपर्क को सुविधाजनक बनाता है। हम कोई पालतू जानवर की दुकान, दलाल, आश्रय स्थल, या गोद लेने की एजेंसी नहीं हैं। हम अपने प्लेटफ़ॉर्म पर सूचीबद्ध किसी भी पालतू जानवर के मालिक नहीं हैं, उसकी अभिरक्षा नहीं रखते, और न ही उसे बीच में संभालते हैं।",
    "termsNatureText2" to "हमारी भूमिका पक्षों के बीच एक संचार माध्यम प्रदान करने तक ही सीमित है। हम गोद लेने की बातचीत, अनुबंधों, या जानवरों के भौतिक हस्तांतरण में भाग नहीं लेते हैं।",
    "termsUserRespH2" to "उपयोगकर्ता की जिम्मेदारियाँ",
    "termsForRescuersH3" to "बचावकर्ताओं के लिए:",
    "termsRescuerResp1" to "अपनी देखरेख में मौजूद पालतू जानवरों के बारे में सटीक, सच्ची जानकारी प्रदान करें",
    "termsRescuerResp2" to "सूची में डालने से पहले सुनिश्चित करें कि पालतू जानवर स्वस्थ, टीकाकृत, और उचित रूप से जाँचे गए हैं",
    "termsRescuerResp3" to "संभावित गोद लेने वालों की जिम्मेदार जाँच करें",
    "termsRescuerResp4" to "पालतू जानवरों को उनके नए घरों में सुरक्षित रूप से पहुँचाने की व्यवस्था करें",
    "termsRescuerResp5" to "प्रदान की गई सभी जानकारी की सटीकता की जिम्मेदारी स्वीकार करें",
    "termsRescuerResp6" to "पालतू जानवर को गोद लेने से संबंधित सभी स्थानीय कानूनों और विनियमों का पालन करें",
    "termsRescuerResp7" to "व्यावसायिक पालतू जानवर बिक्री या प्रजनन उद्देश्यों के लिए प्लेटफ़ॉर्म का कभी उपयोग न करें",
    "termsForAdoptersH3" to "गोद लेने वालों के लिए:",
    "termsAdopterResp1" to "अपनी रहने की स्थिति और पालतू जानवरों के साथ अनुभव के बारे में सटीक जानकारी प्रदान करें",
    "termsAdopterResp2" to "उचित देखभाल प्रदान करने की अपनी क्षमता प्रदर्शित करने के लिए तैयार रहें",
    "termsAdopterResp3" to "समझें कि किसी पालतू जानवर को गोद लेने को रद्द करने से जानवर को काफ़ी तनाव होता है",
    "termsAdopterResp4" to "आपके गोद लेने के अनुरोध को अस्वीकार करने के बचावकर्ता के अधिकार का सम्मान करें",
    "termsPetListingsH2" to "पालतू जानवरों की सूची और जानकारी",
    "termsPetListingsText1" to "Adopt-U पालतू जानवरों की सूचियों में दी गई जानकारी की सटीकता को सत्यापित, समर्थन, या गारंटी नहीं देता है। नस्ल, उम्र, स्वास्थ्य स्थिति, स्वभाव, और टीकाकरण रिकॉर्ड सहित लेकिन इन्हीं तक सीमित नहीं जानकारी बचावकर्ताओं द्वारा प्रदान की जाती है और इसे स्वतंत्र रूप से सत्यापित नहीं किया गया है।",
    "termsPetListingsText2" to "तस्वीरें और विवरण बचावकर्ताओं द्वारा प्रदान किए जाते हैं। जबकि हम जानबूझकर गलत प्रस्तुति को प्रतिबंधित करते हैं, हम यह गारंटी नहीं दे सकते कि सभी सूचियाँ जानवर की वर्तमान स्थिति को सटीक रूप से दर्शाती हैं।",
    "termsPetListingsText3" to "उपयोगकर्ताओं को किसी भी गोद लेने की व्यवस्था को अंतिम रूप देने से पहले प्रश्न पूछने, पशु चिकित्सा रिकॉर्ड का अनुरोध करने, और पालतू जानवरों से मिलने के लिए प्रोत्साहित किया जाता है।",
    "termsAdoptionProcessH2" to "गोद लेने की प्रक्रिया",
    "termsAdoptionProcessIntro" to "एक बार जब बचावकर्ता और गोद लेने वाला गोद लेने की प्रक्रिया आगे बढ़ाने पर सहमत हो जाते हैं:",
    "termsAdoptionStep1" to "स्थानांतरण की तारीख, स्थान, और किसी भी संबंधित लागत सहित सभी व्यवस्थाएँ पक्षों के बीच सीधे बातचीत से तय की जाती हैं",
    "termsAdoptionStep2" to "Adopt-U किसी भी भुगतान लेनदेन को सुविधाजनक नहीं बनाता या इसकी गारंटी नहीं देता",
    "termsAdoptionStep3" to "प्लेटफ़ॉर्म गोद लेने के अनुबंध प्रदान नहीं करता है; कोई भी संविदात्मक व्यवस्था शामिल पक्षों की एकमात्र जिम्मेदारी है",
    "termsAdoptionStep4" to "हम दृढ़ता से सलाह देते हैं कि सभी समझौतों को लिखित रूप में दर्ज करें",
    "termsLiabilityH2" to "दायित्व की सीमा",
    "termsLiabilityIntro" to "Adopt-U को निम्नलिखित के लिए जिम्मेदार नहीं ठहराया जाएगा:",
    "termsLiability1" to "बचावकर्ताओं और गोद लेने वालों के बीच कोई भी विवाद, मतभेद, या संघर्ष",
    "termsLiability2" to "पालतू जानवरों की सूचियों में दी गई जानकारी की सटीकता",
    "termsLiability3" to "गोद लेने से पहले, दौरान, या बाद में किसी भी जानवर का स्वास्थ्य, व्यवहार, या स्थिति",
    "termsLiability4" to "प्लेटफ़ॉर्म के उपयोग से उत्पन्न होने वाली कोई भी वित्तीय हानि, क्षति, या चोट",
    "termsLiability5" to "किसी भी गोद लेने का परिणाम, जिसमें वापसी, स्वास्थ्य समस्याएँ, या अनुकूलता संबंधी समस्याएँ शामिल हैं लेकिन इन्हीं तक सीमित नहीं",
    "termsLiability6" to "संपर्क स्थापित होने के बाद प्लेटफ़ॉर्म के बाहर उपयोगकर्ताओं द्वारा की गई कोई भी कार्रवाई",
    "termsLiabilityAck" to "उपयोगकर्ता स्वीकार करते हैं कि पालतू जानवर को गोद लेना एक महत्वपूर्ण प्रतिबद्धता है और वे गोद लेने के अपने निर्णय की पूरी जिम्मेदारी लेते हैं।",
    "termsProhibitedH2" to "निषिद्ध गतिविधियाँ",
    "termsProhibitedIntro" to "Adopt-U पर निम्नलिखित गतिविधियाँ सख्ती से निषिद्ध हैं:",
    "termsProhibited1" to "व्यावसायिक पालतू जानवर बिक्री, प्रजनन संचालन, या पपी मिल",
    "termsProhibited2" to "ऐसे पालतू जानवरों को सूचीबद्ध करना जिनकी आप सीधे देखभाल नहीं करते",
    "termsProhibited3" to "सूचियों या प्रोफ़ाइलों में गलत या भ्रामक जानकारी",
    "termsProhibited4" to "अन्य उपयोगकर्ताओं के साथ उत्पीड़न, भेदभाव, या अनुचित संपर्क",
    "termsProhibited5" to "पालतू जानवर को गोद लेने से असंबंधित उद्देश्यों के लिए व्यक्तिगत जानकारी एकत्र करने हेतु प्लेटफ़ॉर्म का उपयोग करना",
    "termsProhibited6" to "कोई भी अवैध गतिविधियाँ या पशु कल्याण कानूनों का उल्लंघन",
    "termsAccountTerminationH2" to "खाता समाप्ति",
    "termsAccountTerminationText" to "Adopt-U उन खातों को निलंबित या समाप्त करने का अधिकार सुरक्षित रखता है जो इन शर्तों का उल्लंघन करते हैं, निषिद्ध गतिविधियों में संलग्न होते हैं, या प्लेटफ़ॉर्म को बदनाम करते हैं। हम ऐसी सूचियों को भी हटा सकते हैं जिनमें गलत, भ्रामक, या अनुचित सामग्री हो।",
    "termsModificationsH2" to "शर्तों में संशोधन",
    "termsModificationsText" to "हम किसी भी समय इन शर्तों को संशोधित करने का अधिकार सुरक्षित रखते हैं। परिवर्तनों के बाद प्लेटफ़ॉर्म का निरंतर उपयोग संशोधित शर्तों की स्वीकृति माना जाएगा। हम महत्वपूर्ण परिवर्तनों के बारे में उपयोगकर्ताओं को ईमेल या प्लेटफ़ॉर्म घोषणाओं के माध्यम से सूचित करेंगे।",
    "termsContactText" to "यदि इन नियमों और शर्तों के बारे में आपके कोई प्रश्न हैं, तो कृपया हमसे adopt-u@adopt-u.org पर संपर्क करें।",
    "language" to "भाषा",
    "displayName" to "प्रदर्शन नाम",
    "saveProfile" to "प्रोफ़ाइल सहेजें",
    "accountSettings" to "खाता सेटिंग्स",
    "yourRoles" to "आपकी भूमिकाएँ:",
    "resources" to "संसाधन",
    "loginWithPasskey" to "पासकी से लॉगिन करें",
    "signInWithPasskey" to "पासकी से साइन इन करें",
    "resendVerificationEmail" to "सत्यापन ईमेल पुनः भेजें",
    "usesFido" to "सुरक्षित पासवर्ड-रहित प्रमाणीकरण के लिए FIDO2 / WebAuthn का उपयोग करता है।",
    "loginWithMagicLink" to "ईमेल लिंक से लॉगिन करें",
    "magicLinkDesc" to "हम आपके ईमेल पर एक लॉगिन लिंक भेजेंगे (5 मिनट के लिए मान्य)।",
    "sendMagicLink" to "लॉगिन लिंक भेजें",
    "loginWithPassword" to "पासवर्ड से लॉगिन करें",
    "signIn" to "साइन इन करें",
    "forgotPassword" to "पासवर्ड भूल गए?",
    "registerNewAccount" to "खाता बनाएँ",
    "accountDetails" to "खाता विवरण",
    "registrationMethod" to "पंजीकरण विधि",
    "selectAdditionalRoles" to "अतिरिक्त भूमिकाएँ चुनें:",
    "adoptPet" to "एक पालतू जानवर गोद लें",
    "publishPets" to "गोद लेने के लिए पालतू जानवर प्रकाशित करें",
    "offerPhotography" to "फ़ोटोग्राफी सेवाएँ प्रदान करें",
    "photographerSettings" to "फ़ोटोग्राफर सेटिंग्स",
    "sessionFee" to "सत्र शुल्क",
    "provideTemporaryHome" to "पालतू जानवरों के लिए अस्थायी घर प्रदान करें",
    "passkeyNoPassword" to "पासकी पासवर्ड की तुलना में अधिक सुरक्षित हैं और आपके सभी डिवाइस पर काम करती हैं।",
    "registerWithPasskey" to "पासकी से पंजीकरण करें",
    "passkeySettings" to "पासकी सेटिंग्स",
    "password" to "पासवर्ड",
    "confirmPassword" to "पासवर्ड की पुष्टि करें",
    "passwordRequirements" to "पासवर्ड कम से कम 8 अक्षरों का होना चाहिए और इसमें बड़े अक्षर, छोटे अक्षर, संख्या, और प्रतीक शामिल होने चाहिए",
    "securityInfo" to "सुरक्षा जानकारी",
    "passkeyMostSecure" to "पासकी: सबसे सुरक्षित, अनुशंसित",
    "passkeyBrowserCompat" to "पासकी सभी ब्राउज़र पर काम नहीं कर सकती",
    "passwordLeastSecure" to "पासवर्ड: कम सुरक्षित, अनुशंसित नहीं",
    "selectLoginMethods" to "एक या अधिक लॉगिन विधियाँ चुनें:",
    "registerPasskeyOption" to "पासकी (सबसे सुरक्षित, आपके सभी डिवाइस पर काम करती है)",
    "registerPasswordOption" to "पासवर्ड (कम सुरक्षित, केवल बैकअप के रूप में उपयोग करें)",
    "howPasskeysWorkTitle" to "पासकी कैसे काम करती है?",
    "howPasskeysWorkIntro" to "एक पासकी आपके पासवर्ड को आपके डिवाइस की अंतर्निहित सुरक्षा से बदल देती है — फ़िंगरप्रिंट, चेहरा पहचान, या स्क्रीन लॉक पिन।",
    "howPasskeysWorkSecurity" to "यह केवल आपके डिवाइस पर संग्रहीत होती है, इसे फ़िश नहीं किया जा सकता या डेटा उल्लंघन में लीक नहीं किया जा सकता, और यह आपके डिवाइस में स्वचालित रूप से सिंक हो जाती है।",
    "howPasskeysWorkBackup" to "आप एक पासवर्ड भी जोड़ सकते हैं, लेकिन साइन इन करने के लिए अकेली पासकी ही पर्याप्त है — किसी पासवर्ड की आवश्यकता नहीं।",
    "gotIt" to "समझ गया",
    "alreadyHaveAccount" to "पहले से खाता है?",
    "all" to "सभी",
    "filterDogs" to "कुत्ते",
    "filterCats" to "बिल्लियाँ",
    "filterBirds" to "पक्षी",
    "filterFish" to "मछलियाँ",
    "filterAllSexOption" to "सभी लिंग",
    "filterMaleOption" to "♂ नर",
    "filterFemaleOption" to "♀ मादा",
    "age" to "उम्र",
    "rescued" to "बचाया गया",
    "years" to "वर्ष",
    "months" to "महीने",
    "year" to "वर्ष",
    "month" to "महीना",
    "loginToAdopt" to "गोद लेने का अनुरोध करने के लिए लॉग इन करें",
    "requiredMarker" to "(आवश्यक)",
    "pageNotFoundTitle" to "पृष्ठ नहीं मिला",
    "pageNotFoundHint" to "लिंक पुराना या गलत हो सकता है। पालतू जानवर अभी भी यहाँ हैं।",
    "browsePets" to "पालतू जानवर देखें",
    "requestsFromRescuers" to "बचावकर्ताओं से अनुरोध",
    "signingOut" to "साइन आउट हो रहा है...",
    "adopter" to "गोद लेने वाला",
    "country" to "देश",
    "zip" to "पिन कोड",
    "viewPublic" to "सार्वजनिक पृष्ठ देखें",
    "banUser" to "उपयोगकर्ता को प्रतिबंधित करें",
    "banReason" to "कारण (वैकल्पिक)",
    "myPhotoRequests" to "मेरे फोटो सेशन अनुरोध",
    "noPhotoRequestsYet" to "आपने अभी तक फोटो सेशन का अनुरोध नहीं किया है।",
    "photoRequestsReceived" to "प्राप्त फोटो सेशन अनुरोध",
    "noPhotoRequestsReceived" to "अभी तक कोई फोटो सेशन अनुरोध नहीं।",
    "photoStatusPending" to "लंबित",
    "photoStatusApproved" to "स्वीकृत",
    "photoStatusRejected" to "अस्वीकृत",
    "photoStatusCancelled" to "रद्द",
    "photoStatusCompleted" to "पूर्ण",
    "cancelRequest" to "अनुरोध रद्द करें",
    "registerConsentPrefix" to "खाता बनाकर आप इन्हें स्वीकार करते हैं:",
    "registerConsentTerms" to "नियम और शर्तें",
    "registerConsentAnd" to "और",
    "registerConsentPrivacy" to "गोपनीयता नीति",
    "petStatusAvailable" to "उपलब्ध",
    "petStatusAdopted" to "गोद लिया गया",
    "petStatusDisabled" to "अक्षम",
    "petStatusPending" to "लंबित",
    "addEditLocation" to "स्थान जोड़ें/संपादित करें",
    "addLocation" to "स्थान जोड़ें",
    "manage" to "प्रबंधन",
    "adminPanel" to "व्यवस्थापक पैनल",
    "adminSterilizationLocations" to "नसबंदी केंद्रों का प्रबंधन करें",
    "adopterRequired" to "गोद लेने वाला (आवश्यक)",
    "adoptionFee" to "गोद लेने का शुल्क",
    "adoptionRequests" to "गोद लेने के अनुरोध",
    "ageMonths" to "उम्र (महीने)",
    "ageYears" to "उम्र (वर्ष)",
    "alias" to "उपनाम",
    "bird" to "पक्षी",
    "breed" to "नस्ल",
    "medicalEventsOverview" to "चिकित्सा घटनाओं का अवलोकन",
    "back" to "वापस",
    "cat" to "बिल्ली",
    "color" to "रंग",
    "compatibility" to "अनुकूलता",
    "currentPassword" to "वर्तमान पासवर्ड",
    "dog" to "कुत्ता",
    "emailChange" to "ईमेल बदलें",
    "emailChangeDesc" to "अपना ईमेल पता अपडेट करें। नए ईमेल पर एक सत्यापन लिंक भेजा जाएगा।",
    "energyLevel" to "ऊर्जा स्तर",
    "female" to "मादा",
    "findTemporalHome" to "अस्थायी घर खोजें",
    "fish" to "मछली",
    "goodWithCats" to "बिल्लियों के साथ अच्छा",
    "goodWithDogs" to "कुत्तों के साथ अच्छा",
    "goodWithKids" to "बच्चों के साथ अच्छा",
    "high" to "उच्च",
    "houseTrained" to "घर-प्रशिक्षित",
    "large" to "बड़ा",
    "low" to "कम",
    "male" to "नर",
    "managePets" to "पालतू जानवरों का प्रबंधन करें",
    "managePetsBtn" to "पालतू जोड़ें या संपादित करें",
    "managePetsDescription" to "सभी पालतू जानवर पृष्ठों का प्रबंधन करें। पालतू जानवर जोड़ें या हटाएँ।",
    "manageShelters" to "आश्रय स्थलों का प्रबंधन करें",
    "manageSheltersBtn" to "आश्रय जोड़ें या संपादित करें",
    "manageSheltersDescription" to "सभी आश्रय स्थल पृष्ठों का प्रबंधन करें। आश्रय स्थल जोड़ें या हटाएँ।",
    "manageUsers" to "उपयोगकर्ताओं का प्रबंधन करें",
    "medical" to "चिकित्सा",
    "medium" to "मध्यम",
    "backToSearch" to "खोज पर वापस जाएँ",
    "contactThisHome" to "इस अस्थायी घर से संपर्क करें",
    "loginAsRescuerToContact" to "इस अस्थायी घर को अनुरोध भेजने के लिए बचावकर्ता के रूप में लॉगिन करें।",
    "memberSince" to "सदस्य बने",
    "microchipId" to "माइक्रोचिप आईडी",
    "microchipped" to "माइक्रोचिप लगी हुई",
    "myTemporalHome" to "मेरा अस्थायी घर",
    "newEmail" to "नया ईमेल",
    "noTemporalHomes" to "कोई अस्थायी घर नहीं मिला।",
    "requestSentToHome" to "आपका अनुरोध भेज दिया गया है!",
    "sendRequestBtn" to "अनुरोध भेजें",
    "temporalHomeNotFound" to "यह अस्थायी घर नहीं मिल सका।",
    "viewDetails" to "विवरण देखें",
    "rescuerDirectory" to "बचावकर्ता",
    "noRescuersFound" to "कोई बचावकर्ता नहीं मिला।",
    "availablePetsCount" to "उपलब्ध पालतू जानवर",
    "availablePets" to "उपलब्ध पालतू जानवर",
    "noAvailablePetsForRescuer" to "इस बचावकर्ता के पास अभी कोई उपलब्ध पालतू जानवर नहीं है।",
    "rescuerNotFound" to "यह बचावकर्ता नहीं मिल सका।",
    "errorLoadingResults" to "परिणाम लोड करने में त्रुटि।",
    "volunteerForThisRescuer" to "इस बचावकर्ता के लिए स्वयंसेवा करें",
    "volunteerExplanation" to "स्वीकृत स्वयंसेवक इस बचावकर्ता की पालतू जानवर सूचियों में संपादन सुझा सकते हैं।",
    "applyToVolunteerBtn" to "स्वयंसेवा के लिए आवेदन करें",
    "loginToVolunteer" to "इस बचावकर्ता के लिए स्वयंसेवा हेतु आवेदन करने के लिए लॉगिन करें।",
    "volunteerApplicationSent" to "आपका स्वयंसेवक आवेदन भेज दिया गया है!",
    "myVolunteering" to "मेरी स्वयंसेवा",
    "noVolunteerApplicationsYet" to "आपने अभी तक किसी भी बचावकर्ता के लिए स्वयंसेवा हेतु आवेदन नहीं किया है।",
    "howToVolunteerHint" to "आवेदन करने के लिए, निर्देशिका से किसी रेस्क्यूअर का पेज खोलें और \"स्वयंसेवक के लिए आवेदन करें\" दबाएं।",
    "volunteerStatusPending" to "लंबित",
    "volunteerStatusActive" to "सक्रिय",
    "volunteerStatusRejected" to "अस्वीकृत",
    "volunteerApplications" to "स्वयंसेवक आवेदन",
    "noVolunteerApplications" to "कोई स्वयंसेवक आवेदन नहीं।",
    "petEditSuggestions" to "पालतू जानवर संपादन सुझाव",
    "noPetEditSuggestions" to "कोई लंबित संपादन सुझाव नहीं।",
    "suggestedByLabel" to "द्वारा सुझाया गया",
    "suggestAnEditTitle" to "संपादन सुझाएँ",
    "suggestAnEditExplanation" to "एक स्वीकृत स्वयंसेवक के रूप में, आप इस सूची में परिवर्तन सुझा सकते हैं। लाइव होने से पहले बचावकर्ता इसकी समीक्षा करेगा।",
    "suggestEditBtn" to "सुझाव भेजें",
    "editSuggestionSent" to "आपका सुझाव समीक्षा हेतु बचावकर्ता को भेज दिया गया है!",
    "sponsorThisPetTitle" to "इस पालतू जानवर को प्रायोजित करें",
    "sponsorExplanation" to "गोद नहीं ले सकते लेकिन खर्चों में मदद करना चाहते हैं? पैसे या वस्तु के रूप में सहायता प्रदान करें और सीधे बचावकर्ता से जुड़ें।",
    "sponsorThisRescuerTitle" to "इस बचावकर्ता को प्रायोजित करें",
    "sponsorRescuerExplanation" to "पैसे या वस्तु सहायता के साथ इस बचावकर्ता की सामान्य लागतों का समर्थन करें।",
    "sponsorTypeLabel" to "आप क्या दे सकते हैं?",
    "sponsorTypeMoney" to "पैसा",
    "sponsorTypeInKind" to "वस्तु के रूप में (भोजन, आपूर्ति, आदि)",
    "amountLabel" to "राशि (मासिक)",
    "currencyLabel" to "मुद्रा",
    "inKindDescriptionLabel" to "बताएँ कि आप क्या दे सकते हैं",
    "messageLabel" to "संदेश",
    "sendOfferBtn" to "प्रस्ताव भेजें",
    "sponsorOfferSent" to "आपका प्रस्ताव भेज दिया गया है! बचावकर्ता विवरण तय करने के लिए संपर्क करेगा।",
    "sponsorshipOffersReceived" to "प्रायोजन प्रस्ताव",
    "noSponsorshipOffers" to "अभी तक कोई प्रायोजन प्रस्ताव नहीं।",
    "generalFundLabel" to "सामान्य कोष",
    "offeringLabel" to "प्रस्तावित",
    "sponsorshipStatusSent" to "नया",
    "sponsorshipStatusRead" to "पढ़ा गया",
    "markAsReadBtn" to "पढ़ा हुआ चिह्नित करें",
    "mySponsorshipOffers" to "मेरे प्रायोजन प्रस्ताव",
    "noSponsorshipOffersYet" to "आपने अभी तक कोई प्रायोजन प्रस्ताव नहीं भेजा है।",
    "howToSponsorHint" to "प्रायोजित करने के लिए, किसी रेस्क्यूअर का पेज (सामान्य सहायता) या किसी पालतू का पेज (उस पालतू के खर्च) खोलें और एक प्रस्ताव भेजें।",
    "browsePetsLink" to "पालतू जानवर देखें",
    "yourMessage" to "आपका संदेश",
    "newPassword" to "नया पासवर्ड",
    "passkeyDesc" to "डिवाइस पर आसान लॉगिन के लिए अपने खाते में अतिरिक्त पासकी जोड़ें।",
    "passkeyName" to "पासकी नाम",
    "pers" to "लोग",
    "petFoodDescription" to "जानें कि कौन से खाद्य पदार्थ आपके पालतू जानवरों के लिए सुरक्षित, हानिकारक, या विषैले हैं",
    "petFoodGuide" to "पालतू भोजन गाइड",
    "foodInformation" to "भोजन जानकारी",
    "petsForAdoption" to "गोद लेने हेतु पालतू जानवर",
    "chooseCountryPrompt" to "अपना नया सबसे अच्छा दोस्त खोजें",
    "chooseCountryPromptHint" to "अपने आस-पास गोद लेने हेतु उपलब्ध पालतू जानवरों को देखने के लिए ऊपर एक देश चुनें।",
    "noPetsFound" to "कोई पालतू जानवर नहीं मिला",
    "noPetsFoundHint" to "कोई भिन्न फ़िल्टर आज़माएँ या जल्द ही फिर से देखें — नए पालतू जानवर नियमित रूप से जोड़े जाते हैं।",
    "loadingMorePets" to "और पालतू जानवर लोड हो रहे हैं…",
    "photos" to "तस्वीरें (अधिकतम 12)",
    "profilePhotographerRole" to "फ़ोटोग्राफर - फ़ोटोग्राफी सेवाएँ प्रदान करें",
    "profileRescuerRole" to "बचावकर्ता - गोद लेने के लिए पालतू जानवर प्रकाशित करें",
    "profileTemporalHomeRole" to "अस्थायी घर - पालतू जानवरों के लिए अस्थायी घर प्रदान करें",
    "provideShelter" to "आश्रय स्थल - एक पशु आश्रय स्थल का प्रबंधन करें",
    "provideSterilization" to "नसबंदी सेवा - नसबंदी सेवाएँ प्रदान करें",
    "requestEmailChange" to "ईमेल परिवर्तन का अनुरोध करें",
    "rescueDate" to "बचाव तिथि",
    "rescueLocation" to "बचाव स्थान",
    "savePassword" to "पासवर्ड सहेजें",
    "selectEnergy" to "ऊर्जा चुनें",
    "selectSize" to "आकार चुनें",
    "sex" to "लिंग",
    "shelterSettings" to "आश्रय स्थल सेटिंग्स",
    "size" to "आकार",
    "small" to "छोटा",
    "specialNeeds" to "विशेष आवश्यकताएँ",
    "sterilizationSettings" to "नसबंदी सेटिंग्स",
    "sterilized" to "नसबंदी की गई",
    "temporalHomeSettings" to "अस्थायी घर सेटिंग्स",
    "temperament" to "स्वभाव",
    "type" to "प्रकार *",
    "urgentAdoption" to "तत्काल गोद लेने की आवश्यकता है",
    "vaccinations" to "टीकाकरण",
    "vetDisclaimer" to "आहार में बदलाव करने से पहले अपने पशु चिकित्सक से सलाह लें।",
    "weight" to "वज़न (किग्रा)",
    "weightLabel" to "वज़न",
    "website" to "वेबसाइट",
    "securitySettings" to "सुरक्षा सेटिंग्स",
    "emailVerifiedTitle" to "ईमेल सफलतापूर्वक सत्यापित हुआ!",
    "emailVerifiedMessage" to "आपका ईमेल सत्यापित हो गया है। आपका पंजीकरण अब पूर्ण हो गया है।",
    "redirectingIn" to "मुख्य पृष्ठ पर पुनर्निर्देशित हो रहा है ",
    "canAlsoLogin" to "आप यह भी कर सकते हैं ",
    "loginNow" to "अभी लॉगिन करें",
    "emailVerificationFailed" to "सत्यापन विफल",
    "invalidOrExpiredToken" to "सत्यापन लिंक अमान्य है या समाप्त हो गया है।",
    "tryRegisteringAgain" to "कृपया फिर से पंजीकरण करने का प्रयास करें।",
    "verificationEmailResent" to "सत्यापन ईमेल समाप्त हो गया था। एक नया सत्यापन ईमेल भेज दिया गया है। कृपया अपना इनबॉक्स जाँचें।",
    "emailVerificationSent" to "एक सत्यापन ईमेल भेज दिया गया है। लॉगिन करने से पहले कृपया अपना इनबॉक्स जाँचें।",
    "emailNotVerifiedLogin" to "लॉगिन करने से पहले कृपया अपना ईमेल सत्यापित करें। सत्यापन लिंक के लिए अपना इनबॉक्स जाँचें।",
    "invalidOrExpiredMagicLink" to "अमान्य या समाप्त हो चुका लिंक। कृपया एक नया अनुरोध करें।",
    "invalidToken" to "अमान्य टोकन।",
    "accountBanned" to "आपका खाता प्रतिबंधित कर दिया गया है।",
    "loginError" to "लॉगिन में त्रुटि हुई।",
    "profileUpdated" to "प्रोफ़ाइल अपडेट हो गई!",
    "displayNameRequired" to "प्रदर्शन नाम खाली नहीं हो सकता",
    "saving" to "सहेजा जा रहा है...",
    "passwordSet" to "पासवर्ड सेट है",
    "noPassword" to "कोई पासवर्ड सेट नहीं है",
    "passwordChangeSuccess" to "पासवर्ड सफलतापूर्वक बदल दिया गया!",
    "passwordChangeFailed" to "पासवर्ड बदलने में विफल",
    "passwordSetSuccess" to "पासवर्ड सफलतापूर्वक सेट हो गया!",
    "passwordSetFailed" to "पासवर्ड सेट करने में विफल",
    "passwordsDoNotMatch" to "पासवर्ड मेल नहीं खाते",
    "currentPasswordRequired" to "वर्तमान पासवर्ड आवश्यक है",
    "emailChangeSent" to "आपके नए ईमेल पते पर सत्यापन ईमेल भेज दिया गया!",
    "emailChangeFailed" to "ईमेल परिवर्तन का अनुरोध करने में विफल",
    "emailRequired" to "ईमेल आवश्यक है",
    "sendingEllipsis" to "भेजा जा रहा है...",
    "resetLinkSent" to "पासवर्ड रीसेट लिंक भेज दिया गया! अपना ईमेल जाँचें।",
    "magicLinkSent" to "लॉगिन लिंक भेज दिया गया! अपना ईमेल जाँचें।",
    "magicLinkSendFailed" to "लॉगिन लिंक भेजने में विफल",
    "failedSendResetLink" to "रीसेट लिंक भेजने में विफल।",
    "invalidOrMissingToken" to "अमान्य या अनुपस्थित टोकन।",
    "passwordTooShort" to "पासवर्ड कम से कम 8 अक्षरों का होना चाहिए।",
    "passwordNeedUppercase" to "पासवर्ड में कम से कम 1 बड़ा अक्षर होना चाहिए।",
    "passwordNeedLowercase" to "पासवर्ड में कम से कम 1 छोटा अक्षर होना चाहिए।",
    "passwordNeedNumber" to "पासवर्ड में कम से कम 1 संख्या होनी चाहिए।",
    "passwordNeedSymbol" to "पासवर्ड में कम से कम 1 प्रतीक होना चाहिए।",
    "resettingPassword" to "पासवर्ड रीसेट किया जा रहा है...",
    "passwordResetSuccess" to "पासवर्ड सफलतापूर्वक रीसेट हो गया! अब आप लॉगिन कर सकते हैं।",
    "failedResetPassword" to "पासवर्ड रीसेट करने में विफल।",
    "loginSuccessRedirecting" to "लॉगिन सफल! पुनर्निर्देशित किया जा रहा है...",
    "loginFailedExpiredLink" to "लॉगिन विफल। लिंक अमान्य या समाप्त हो सकता है।",
    "loginFailed" to "लॉगिन विफल।",
    "emailChangedSuccess" to "ईमेल सफलतापूर्वक बदल दिया गया!",
    "failedChangeEmailExpired" to "ईमेल बदलने में विफल। लिंक अमान्य या समाप्त हो सकता है।",
    "failedChangeEmail" to "ईमेल बदलने में विफल।",
    "emailVerifiedSuccess" to "ईमेल सफलतापूर्वक सत्यापित हो गया!",
    "failedVerifyEmailExpired" to "ईमेल सत्यापित करने में विफल। लिंक अमान्य या समाप्त हो सकता है।",
    "failedVerifyEmail" to "ईमेल सत्यापित करने में विफल।",
    "dropImagesHint" to "छवियाँ यहाँ छोड़ें या ब्राउज़ करने के लिए क्लिक करें",
    "forgotPasswordTitle" to "पासवर्ड भूल गए",
    "forgotPasswordIntro" to "अपना ईमेल दर्ज करें और हम आपको पासवर्ड रीसेट करने के लिए एक लिंक भेजेंगे।",
    "sendResetLink" to "रीसेट लिंक भेजें",
    "backToLogin" to "लॉगिन पर वापस जाएँ",
    "resetPasswordTitle" to "पासवर्ड रीसेट करें",
    "resetPasswordIntro" to "नीचे अपना नया पासवर्ड दर्ज करें।",
    "emailLinkLoginTitle" to "ईमेल लिंक लॉगिन",
    "verifying" to "सत्यापित किया जा रहा है...",
    "emailChangeTitle" to "ईमेल परिवर्तन",
    "verifyContactEmailTitle" to "संपर्क ईमेल सत्यापित करें",
    "status" to "स्थिति",
    "yes" to "हाँ",
    "no" to "नहीं",
    "noDescription" to "कोई विवरण नहीं।",
    "urgentBadge" to "तत्काल - जल्द घर चाहिए!",
    "needsNewHome" to "इस पालतू जानवर को तत्काल एक नए घर की आवश्यकता है",
    "promotedReasonLabel" to "कारण",
    "selectReason" to "कारण चुनें",
    "promotedReasonMoving" to "मालिक स्थानांतरित हो रहा है",
    "promotedReasonComplaints" to "शिकायतें / अधिक पालतू जानवर नहीं रख सकते",
    "promotedReasonPetConflict" to "किसी अन्य पालतू जानवर के साथ टकराव",
    "promotedReasonOther" to "अन्य",
    "promotedReasonDetailLabel" to "अतिरिक्त विवरण (वैकल्पिक)",
    "promotedReasonRequired" to "कृपया एक कारण चुनें",
    "needsNewHomeBadge" to "नए घर की आवश्यकता है",
    "housingType" to "आवास प्रकार",
    "preferNotToSay" to "बताना नहीं चाहते",
    "houseHousing" to "घर",
    "apartmentHousing" to "अपार्टमेंट",
    "hasYard" to "आँगन है",
    "hasOtherPets" to "घर में अन्य पालतू जानवर",
    "adoptionExperience" to "गोद लेने का अनुभव",
    "firstTimeAdopter" to "पहली बार गोद लेने वाला",
    "experiencedAdopter" to "अनुभवी गोद लेने वाला",
    "noAdoptionRequests" to "कोई गोद लेने का अनुरोध नहीं",
    "noMessage" to "कोई संदेश नहीं",
    "markUnderReview" to "समीक्षाधीन चिह्नित करें",
    "approve" to "स्वीकृत करें",
    "reject" to "अस्वीकार करें",
    "reviewNoteLabel" to "निजी समीक्षा टिप्पणी",
    "saveNote" to "टिप्पणी सहेजें",
    "adoptionStatusPending" to "लंबित",
    "adoptionStatusUnderReview" to "समीक्षाधीन",
    "adoptionStatusApproved" to "स्वीकृत",
    "adoptionStatusRejected" to "स्वीकृत नहीं",
    "myAdoptionRequests" to "मेरे गोद लेने के अनुरोध",
    "noAdoptionRequestsYet" to "आपने अभी तक किसी भी पालतू जानवर को गोद लेने का अनुरोध नहीं किया है।",
    "medicalHistory" to "चिकित्सा इतिहास",
    "medicalSchedule" to "टीकाकरण और कृमि उपचार अनुसूची",
    "recordType" to "प्रकार",
    "vaccination" to "टीकाकरण",
    "deworming" to "कृमि उपचार",
    "recordName" to "नाम",
    "dateGiven" to "दी गई तारीख",
    "nextDueDate" to "अगली नियत तारीख (वैकल्पिक)",
    "notesOptional" to "टिप्पणियाँ (वैकल्पिक)",
    "addRecord" to "रिकॉर्ड जोड़ें",
    "noMedicalRecords" to "अभी तक कोई टीकाकरण या कृमि उपचार रिकॉर्ड नहीं।",
    "nextDueLabel" to "अगली नियत तारीख",
    "givenLabel" to "दी गई",
    "overdue" to "अतिदेय",
    "dueSoon" to "जल्द ही देय",
    "upToDate" to "अद्यतित",
    "recordNameRequired" to "नाम आवश्यक है",
    "dateGivenRequired" to "दी गई तारीख आवश्यक है",
    "failedToAddRecord" to "रिकॉर्ड जोड़ने में विफल",
    "confirmDeleteRecord" to "इस रिकॉर्ड को हटाएँ?",
    "maxCapacity" to "एक बार में अधिकतम पालतू जानवर (वैकल्पिक)",
    "placePetHere" to "यहाँ एक पालतू जानवर रखें",
    "noAvailablePetsToPlace" to "अभी आपके पास रखने के लिए कोई उपलब्ध पालतू जानवर नहीं है।",
    "selectPet" to "पालतू जानवर चुनें",
    "startPlacementBtn" to "नियुक्ति शुरू करें",
    "placementStarted" to "पालक नियुक्ति शुरू हो गई!",
    "petsCurrentlyWithMe" to "मेरे पास वर्तमान में मौजूद पालतू जानवर",
    "noPetsCurrentlyFostered" to "वर्तमान में आपके पास कोई पालतू जानवर नहीं रखा गया है।",
    "sinceLabel" to "से",
    "fosterPlacement" to "पालक नियुक्ति",
    "endPlacement" to "नियुक्ति समाप्त करें",
    "currentlyWithLabel" to "वर्तमान में इनके पास",
    "notCurrentlyFostered" to "वर्तमान में पालक देखभाल में नहीं।",
    "confirmEndPlacement" to "इस पालक नियुक्ति को समाप्त करें? पालतू जानवर को आपके पास वापस दर्ज किया जाएगा।",
    "aTemporalHome" to "एक अस्थायी घर",
    "viewsLabel" to "दृश्य",
    "inquiriesLabel" to "पूछताछ",
    "conversionLabel" to "रूपांतरण",
    "messageOptional" to "संदेश (वैकल्पिक)",
    "requestAdoption" to "गोद लेने का अनुरोध करें",
    "editPet" to "पालतू जानवर संपादित करें",
    "adoptionRequestSubmitted" to "गोद लेने का अनुरोध सबमिट हो गया!",
    "failedSubmitRequest" to "अनुरोध सबमिट करने में विफल",
    "noPhotosYet" to "अभी तक कोई तस्वीरें नहीं।",
    "rabbit" to "खरगोश",
    "safeFoods" to "सुरक्षित खाद्य पदार्थ",
    "harmfulFoods" to "हानिकारक खाद्य पदार्थ",
    "cannotEatFoods" to "न खाएँ",
    "legendSafe" to "सुरक्षित",
    "legendHarmful" to "हानिकारक",
    "legendToxic" to "विषैला",
    "food_dog_safe_chicken_name" to "चिकन",
    "food_dog_safe_chicken_detail" to "पका हुआ, बिना हड्डी का",
    "food_dog_safe_chicken_desc" to "कम वसा वाला प्रोटीन, पचाने में आसान",
    "food_dog_safe_rice_name" to "चावल",
    "food_dog_safe_rice_detail" to "सफ़ेद या भूरा",
    "food_dog_safe_rice_desc" to "कार्बोहाइड्रेट का अच्छा स्रोत",
    "food_dog_safe_carrots_name" to "गाजर",
    "food_dog_safe_carrots_detail" to "कच्चा या पका हुआ",
    "food_dog_safe_carrots_desc" to "कम कैलोरी, अधिक फ़ाइबर",
    "food_dog_safe_apples_name" to "सेब",
    "food_dog_safe_apples_detail" to "बीज रहित",
    "food_dog_safe_apples_desc" to "विटामिन से भरपूर",
    "food_dog_safe_peanutbutter_name" to "मूँगफली का मक्खन",
    "food_dog_safe_peanutbutter_detail" to "बिना नमक, बिना ज़ाइलिटोल",
    "food_dog_safe_peanutbutter_desc" to "अच्छा प्रोटीन स्रोत",
    "food_dog_harmful_grapes_name" to "अंगूर",
    "food_dog_harmful_grapes_detail" to "सभी किस्में",
    "food_dog_harmful_grapes_desc" to "गुर्दे की विफलता का कारण बन सकता है",
    "food_dog_harmful_onions_name" to "प्याज़",
    "food_dog_harmful_onions_detail" to "सभी रूप",
    "food_dog_harmful_onions_desc" to "लाल रक्त कोशिकाओं को नुकसान पहुँचाता है",
    "food_dog_harmful_chocolate_name" to "चॉकलेट",
    "food_dog_harmful_chocolate_detail" to "सभी प्रकार",
    "food_dog_harmful_chocolate_desc" to "कुत्तों के लिए विषैला",
    "food_dog_harmful_garlic_name" to "लहसुन",
    "food_dog_harmful_garlic_detail" to "सभी रूप",
    "food_dog_harmful_garlic_desc" to "एनीमिया का कारण बनता है",
    "food_dog_cannot_xylitol_name" to "ज़ाइलिटोल",
    "food_dog_cannot_xylitol_detail" to "कृत्रिम मिठास",
    "food_dog_cannot_xylitol_desc" to "अत्यंत विषैला",
    "food_dog_cannot_macadamia_name" to "मैकाडामिया नट्स",
    "food_dog_cannot_macadamia_detail" to "सभी रूप",
    "food_dog_cannot_macadamia_desc" to "कमज़ोरी का कारण बनता है",
    "food_dog_cannot_avocado_name" to "एवोकाडो",
    "food_dog_cannot_avocado_detail" to "गुठली, छिलका, सब कुछ",
    "food_dog_cannot_avocado_desc" to "पर्सिन होता है",
    "food_dog_cannot_alcohol_name" to "शराब",
    "food_dog_cannot_alcohol_detail" to "सभी रूप",
    "food_dog_cannot_alcohol_desc" to "अत्यंत विषैला",
    "food_cat_safe_cookedfish_name" to "पकी हुई मछली",
    "food_cat_safe_cookedfish_detail" to "बिना हड्डी के",
    "food_cat_safe_cookedfish_desc" to "प्रोटीन में उच्च",
    "food_cat_safe_chicken_name" to "चिकन",
    "food_cat_safe_chicken_detail" to "पका हुआ, सादा",
    "food_cat_safe_chicken_desc" to "अच्छा प्रोटीन स्रोत",
    "food_cat_safe_pumpkin_name" to "कद्दू",
    "food_cat_safe_pumpkin_detail" to "सादा, पका हुआ",
    "food_cat_safe_pumpkin_desc" to "पाचन में मदद करता है",
    "food_cat_safe_eggs_name" to "अंडे",
    "food_cat_safe_eggs_detail" to "पका हुआ",
    "food_cat_safe_eggs_desc" to "पूर्ण प्रोटीन",
    "food_cat_harmful_raweggs_name" to "कच्चे अंडे",
    "food_cat_harmful_raweggs_detail" to "एविडिन युक्त",
    "food_cat_harmful_raweggs_desc" to "बायोटिन में बाधा डालता है",
    "food_cat_harmful_rawfish_name" to "कच्ची मछली",
    "food_cat_harmful_rawfish_detail" to "थायमिनेज़ होता है",
    "food_cat_harmful_rawfish_desc" to "बी विटामिन को तोड़ देता है",
    "food_cat_harmful_dogfood_name" to "कुत्ते का भोजन",
    "food_cat_harmful_dogfood_detail" to "कोई भी",
    "food_cat_harmful_dogfood_desc" to "टॉरिन की कमी",
    "food_cat_harmful_milk_name" to "दूध",
    "food_cat_harmful_milk_detail" to "अधिकांश बिल्लियाँ",
    "food_cat_harmful_milk_desc" to "लैक्टोज़ असहिष्णुता",
    "food_cat_cannot_chocolate_name" to "चॉकलेट",
    "food_cat_cannot_chocolate_detail" to "सभी प्रकार",
    "food_cat_cannot_chocolate_desc" to "थियोब्रोमिन विषैला",
    "food_cat_cannot_onionsgarlic_name" to "प्याज़/लहसुन",
    "food_cat_cannot_onionsgarlic_detail" to "सभी रूप",
    "food_cat_cannot_onionsgarlic_desc" to "लाल रक्त कोशिकाओं को नुकसान",
    "food_cat_cannot_grapesraisins_name" to "अंगूर/किशमिश",
    "food_cat_cannot_grapesraisins_detail" to "सभी किस्में",
    "food_cat_cannot_grapesraisins_desc" to "गुर्दे को नुकसान",
    "food_cat_cannot_alcohol_name" to "शराब",
    "food_cat_cannot_alcohol_detail" to "सभी रूप",
    "food_cat_cannot_alcohol_desc" to "अत्यंत विषैला",
    "food_bird_safe_seeds_name" to "बीज",
    "food_bird_safe_seeds_detail" to "मिश्रित किस्में",
    "food_bird_safe_seeds_desc" to "अच्छा वसा स्रोत",
    "food_bird_safe_fruits_name" to "फल",
    "food_bird_safe_fruits_detail" to "अधिकांश किस्में",
    "food_bird_safe_fruits_desc" to "विटामिन और खनिज",
    "food_bird_safe_vegetables_name" to "सब्ज़ियाँ",
    "food_bird_safe_vegetables_detail" to "हरी पत्तेदार सब्ज़ियाँ",
    "food_bird_safe_vegetables_desc" to "आवश्यक पोषक तत्व",
    "food_bird_safe_pellets_name" to "पेलेट्स",
    "food_bird_safe_pellets_detail" to "गुणवत्तापूर्ण फ़ॉर्मूला",
    "food_bird_safe_pellets_desc" to "पूर्ण पोषण",
    "food_bird_harmful_avocado_name" to "एवोकाडो",
    "food_bird_harmful_avocado_detail" to "सभी भाग",
    "food_bird_harmful_avocado_desc" to "पर्सिन विषैला",
    "food_bird_harmful_fruitpits_name" to "फल की गुठलियाँ",
    "food_bird_harmful_fruitpits_detail" to "सेब, आड़ू",
    "food_bird_harmful_fruitpits_desc" to "साइनाइड के अंश",
    "food_bird_harmful_salt_name" to "नमक",
    "food_bird_harmful_salt_detail" to "कोई भी रूप",
    "food_bird_harmful_salt_desc" to "पक्षियों के लिए विषैला",
    "food_bird_harmful_caffeine_name" to "कैफ़ीन",
    "food_bird_harmful_caffeine_detail" to "कॉफ़ी, चाय",
    "food_bird_harmful_caffeine_desc" to "हृदय संबंधी समस्याएँ",
    "food_bird_cannot_chocolate_name" to "चॉकलेट",
    "food_bird_cannot_chocolate_detail" to "सभी प्रकार",
    "food_bird_cannot_chocolate_desc" to "थियोब्रोमिन विषैला",
    "food_bird_cannot_onions_name" to "प्याज़",
    "food_bird_cannot_onions_detail" to "सभी रूप",
    "food_bird_cannot_onions_desc" to "लाल रक्त कोशिकाओं को नुकसान",
    "food_bird_cannot_garlic_name" to "लहसुन",
    "food_bird_cannot_garlic_detail" to "सभी रूप",
    "food_bird_cannot_garlic_desc" to "विषैला",
    "food_bird_cannot_mushrooms_name" to "मशरूम",
    "food_bird_cannot_mushrooms_detail" to "जंगली किस्में",
    "food_bird_cannot_mushrooms_desc" to "संभावित विषाक्त पदार्थ",
    "food_fish_safe_flakes_name" to "फ्लेक्स",
    "food_fish_safe_flakes_detail" to "गुणवत्तापूर्ण ब्रांड",
    "food_fish_safe_flakes_desc" to "पूर्ण मछली भोजन",
    "food_fish_safe_pellets_name" to "पेलेट्स",
    "food_fish_safe_pellets_detail" to "तैरने वाला/डूबने वाला",
    "food_fish_safe_pellets_desc" to "संतुलित पोषण",
    "food_fish_safe_frozenfood_name" to "जमा हुआ भोजन",
    "food_fish_safe_frozenfood_detail" to "ब्लडवर्म, ब्राइन झींगा",
    "food_fish_safe_frozenfood_desc" to "प्रोटीन स्रोत",
    "food_fish_safe_vegetables_name" to "सब्ज़ियाँ",
    "food_fish_safe_vegetables_detail" to "उबली हुई तोरी",
    "food_fish_safe_vegetables_desc" to "फ़ाइबर स्रोत",
    "food_fish_harmful_bread_name" to "ब्रेड",
    "food_fish_harmful_bread_detail" to "कोई भी प्रकार",
    "food_fish_harmful_bread_desc" to "कोई पोषण नहीं, पेट भर देता है",
    "food_fish_harmful_humanfood_name" to "इंसानी भोजन",
    "food_fish_harmful_humanfood_detail" to "पके हुए भोजन",
    "food_fish_harmful_humanfood_desc" to "गलत पोषण",
    "food_fish_harmful_livefeed_name" to "जीवित आहार",
    "food_fish_harmful_livefeed_detail" to "जंगली पकड़े गए कीड़े",
    "food_fish_harmful_livefeed_desc" to "संभावित परजीवी",
    "food_fish_cannot_landinsect_name" to "ज़मीनी कीड़ा",
    "food_fish_cannot_landinsect_detail" to "रेंगने वाले कीड़े",
    "food_fish_cannot_landinsect_desc" to "प्राकृतिक भोजन नहीं",
    "food_fish_cannot_mammalmeat_name" to "स्तनपायी का मांस",
    "food_fish_cannot_mammalmeat_detail" to "चिकन, बीफ़",
    "food_fish_cannot_mammalmeat_desc" to "अधिकांश मछलियों के लिए अनुपयुक्त",
    "food_fish_cannot_dairy_name" to "डेयरी",
    "food_fish_cannot_dairy_detail" to "कोई भी",
    "food_fish_cannot_dairy_desc" to "पचा नहीं सकते",
    "food_fish_cannot_breadcrumbs_name" to "ब्रेडक्रम्ब्स",
    "food_fish_cannot_breadcrumbs_detail" to "कई में लहसुन होता है",
    "food_fish_cannot_breadcrumbs_desc" to "हानिकारक योजक",
    "food_rabbit_safe_hay_name" to "घास",
    "food_rabbit_safe_hay_detail" to "टिमोथी घास",
    "food_rabbit_safe_hay_desc" to "आहार का 80%",
    "food_rabbit_safe_leafygreens_name" to "हरी पत्तेदार सब्ज़ियाँ",
    "food_rabbit_safe_leafygreens_detail" to "रोमेन, धनिया",
    "food_rabbit_safe_leafygreens_desc" to "दैनिक विटामिन",
    "food_rabbit_safe_pellets_name" to "पेलेट्स",
    "food_rabbit_safe_pellets_detail" to "टिमोथी आधारित",
    "food_rabbit_safe_pellets_desc" to "संतुलित पोषण",
    "food_rabbit_safe_carrots_name" to "गाजर",
    "food_rabbit_safe_carrots_detail" to "सीमित मात्रा में",
    "food_rabbit_safe_carrots_desc" to "उपहार स्वरूप भोजन",
    "food_rabbit_harmful_icebergletttuce_name" to "आइसबर्ग लेट्यूस",
    "food_rabbit_harmful_icebergletttuce_detail" to "कोई पोषण नहीं",
    "food_rabbit_harmful_icebergletttuce_desc" to "जठरांत्र स्थिरता का कारण बन सकता है",
    "food_rabbit_harmful_beans_name" to "फलियाँ",
    "food_rabbit_harmful_beans_detail" to "सभी दालें",
    "food_rabbit_harmful_beans_desc" to "गैस, पाचन समस्याएँ",
    "food_rabbit_harmful_corn_name" to "मक्का",
    "food_rabbit_harmful_corn_detail" to "कोई भी रूप",
    "food_rabbit_harmful_corn_desc" to "पचा नहीं सकते",
    "food_rabbit_harmful_potatoes_name" to "आलू",
    "food_rabbit_harmful_potatoes_detail" to "कोई भी रूप",
    "food_rabbit_harmful_potatoes_desc" to "गलत पोषक तत्व",
    "food_rabbit_cannot_chocolate_name" to "चॉकलेट",
    "food_rabbit_cannot_chocolate_detail" to "सभी प्रकार",
    "food_rabbit_cannot_chocolate_desc" to "थियोब्रोमिन विषैला",
    "food_rabbit_cannot_onions_name" to "प्याज़",
    "food_rabbit_cannot_onions_detail" to "सभी रूप",
    "food_rabbit_cannot_onions_desc" to "अत्यंत विषैला",
    "food_rabbit_cannot_garlic_name" to "लहसुन",
    "food_rabbit_cannot_garlic_detail" to "सभी रूप",
    "food_rabbit_cannot_garlic_desc" to "विषैला",
    "food_rabbit_cannot_avocado_name" to "एवोकाडो",
    "food_rabbit_cannot_avocado_detail" to "सभी भाग",
    "food_rabbit_cannot_avocado_desc" to "पर्सिन होता है",
    "primary" to "प्राथमिक",
    "passkeyRegisteredSuccess" to "पासकी सफलतापूर्वक पंजीकृत हुई!",
    "passkeyRegistrationFailed" to "पासकी पंजीकरण विफल",
    "passkeyNameRequired" to "पासकी नाम आवश्यक है",
    "registeringPasskey" to "पासकी पंजीकृत की जा रही है...",
    "passkeyRegistered" to "पासकी पंजीकृत",
    "noPasskey" to "कोई पासकी पंजीकृत नहीं",
    "noPets" to "अभी तक कोई पालतू जानवर नहीं।",
    "photographerCountryStateRequired" to "कृपया फ़ोटोग्राफर सेवाओं के लिए देश और राज्य भरें",
        "temporalHomeAliasCountryCityRequired" to "कृपया अस्थायी घर के लिए उपनाम, देश और शहर भरें",
        "shelterNameCountryCityAddressRequired" to "कृपया आश्रय स्थल के लिए नाम, देश, शहर और पता भरें",
        "sterilizationNameCountryCityAddressRequired" to "कृपया नसबंदी केंद्र के लिए नाम, देश, शहर और पता भरें",
        "country.afghanistan" to "अफ़गानिस्तान",
        "country.albania" to "अल्बानिया",
        "country.algeria" to "अल्जीरिया",
        "country.argentina" to "अर्जेंटीना",
        "country.armenia" to "आर्मेनिया",
        "country.australia" to "ऑस्ट्रेलिया",
        "country.austria" to "ऑस्ट्रिया",
        "country.azerbaijan" to "अज़रबैजान",
        "country.bangladesh" to "बांग्लादेश",
        "country.belarus" to "बेलारूस",
        "country.belgium" to "बेल्जियम",
        "country.bolivia" to "बोलीविया",
        "country.bosnia" to "बोस्निया और हर्ज़ेगोविना",
        "country.brazil" to "ब्राज़ील",
        "country.bulgaria" to "बुल्गारिया",
        "country.cambodia" to "कंबोडिया",
        "country.cameroon" to "कैमरून",
        "country.canada" to "कनाडा",
        "country.chile" to "चिली",
        "country.china" to "चीन",
        "country.colombia" to "कोलंबिया",
        "country.costaRica" to "कोस्टा रिका",
        "country.croatia" to "क्रोएशिया",
        "country.cuba" to "क्यूबा",
        "country.czechia" to "चेक गणराज्य",
        "country.denmark" to "डेनमार्क",
        "country.dominicanRepublic" to "डोमिनिकन गणराज्य",
        "country.ecuador" to "इक्वाडोर",
        "country.egypt" to "मिस्र",
        "country.elSalvador" to "एल साल्वाडोर",
        "country.estonia" to "एस्टोनिया",
        "country.ethiopia" to "इथियोपिया",
        "country.finland" to "फ़िनलैंड",
        "country.france" to "फ़्रांस",
        "country.georgia" to "जॉर्जिया",
        "country.germany" to "जर्मनी",
        "country.ghana" to "घाना",
        "country.greece" to "यूनान",
        "country.guatemala" to "ग्वाटेमाला",
        "country.haiti" to "हैती",
        "country.honduras" to "होंडुरास",
        "country.hungary" to "हंगरी",
        "country.iceland" to "आइसलैंड",
        "country.india" to "भारत",
        "country.indonesia" to "इंडोनेशिया",
        "country.iran" to "ईरान",
        "country.iraq" to "इराक़",
        "country.ireland" to "आयरलैंड",
        "country.israel" to "इज़राइल",
        "country.italy" to "इटली",
        "country.jamaica" to "जमैका",
        "country.japan" to "जापान",
        "country.jordan" to "जॉर्डन",
        "country.kazakhstan" to "कज़ाख़स्तान",
        "country.kenya" to "केन्या",
        "country.kuwait" to "कुवैत",
        "country.latvia" to "लातविया",
        "country.lebanon" to "लेबनान",
        "country.libya" to "लीबिया",
        "country.lithuania" to "लिथुआनिया",
        "country.luxembourg" to "लक्ज़मबर्ग",
        "country.malaysia" to "मलेशिया",
        "country.mexico" to "मेक्सिको",
        "country.moldova" to "मोल्दोवा",
        "country.mongolia" to "मंगोलिया",
        "country.montenegro" to "मॉन्टेनेग्रो",
        "country.morocco" to "मोरक्को",
        "country.myanmar" to "म्यांमार",
        "country.nepal" to "नेपाल",
        "country.netherlands" to "नीदरलैंड",
        "country.newZealand" to "न्यूज़ीलैंड",
        "country.nicaragua" to "निकारागुआ",
        "country.nigeria" to "नाइजीरिया",
        "country.northKorea" to "उत्तर कोरिया",
        "country.norway" to "नॉर्वे",
        "country.pakistan" to "पाकिस्तान",
        "country.panama" to "पनामा",
        "country.paraguay" to "पैराग्वे",
        "country.peru" to "पेरू",
        "country.philippines" to "फ़िलीपींस",
        "country.poland" to "पोलैंड",
        "country.portugal" to "पुर्तगाल",
        "country.puertoRico" to "प्वेर्तो रिको",
        "country.qatar" to "क़तर",
        "country.romania" to "रोमानिया",
        "country.russia" to "रूस",
        "country.saudiArabia" to "सऊदी अरब",
        "country.serbia" to "सर्बिया",
        "country.singapore" to "सिंगापुर",
        "country.slovakia" to "स्लोवाकिया",
        "country.slovenia" to "स्लोवेनिया",
        "country.southAfrica" to "दक्षिण अफ़्रीका",
        "country.southKorea" to "दक्षिण कोरिया",
        "country.spain" to "स्पेन",
        "country.sriLanka" to "श्रीलंका",
        "country.sudan" to "सूडान",
        "country.sweden" to "स्वीडन",
        "country.switzerland" to "स्विट्ज़रलैंड",
        "country.syria" to "सीरिया",
        "country.taiwan" to "ताइवान",
        "country.thailand" to "थाईलैंड",
        "country.tunisia" to "ट्यूनीशिया",
        "country.turkey" to "तुर्की",
        "country.ukraine" to "यूक्रेन",
        "country.uae" to "संयुक्त अरब अमीरात",
        "country.uk" to "यूनाइटेड किंगडम",
        "country.usa" to "संयुक्त राज्य अमेरिका",
        "country.uruguay" to "उरुग्वे",
        "country.uzbekistan" to "उज़्बेकिस्तान",
        "country.venezuela" to "वेनेज़ुएला",
        "country.vietnam" to "वियतनाम",
        "country.yemen" to "यमन",
        "loading" to "लोड हो रहा है...",
        "countryRequired" to "खोजने के लिए कृपया एक देश चुनें",
        "noSheltersFound" to "अभी तक कोई आश्रय स्थल नहीं मिला। बाद में फिर से देखें!",
        "visitWebsite" to "वेबसाइट पर जाएँ",
        "fiscalId" to "कर पहचान संख्या",
        "donationInformation" to "दान जानकारी",
        "bankName" to "बैंक",
        "accountHolder" to "खाताधारक",
        "accountNumber" to "खाता संख्या",
        "currency" to "मुद्रा",
        "errorLoadingShelters" to "आश्रय स्थल लोड करने में त्रुटि",
        "pleaseSelectCountry" to "कृपया एक देश चुनें",
        "actions" to "कार्रवाइयाँ",
        "addShelter" to "आश्रय स्थल जोड़ें",
        "addEditShelter" to "आश्रय स्थल जोड़ें / संपादित करें",
        "allStates" to "सभी राज्य",
        "confirmDelete" to "क्या आप वाकई इस स्थान को हटाना चाहते हैं?",
        "confirmDeleteShelter" to "क्या आप वाकई इस आश्रय स्थल को हटाना चाहते हैं?",
        "confirmUnbanUser" to "इस उपयोगकर्ता से प्रतिबंध हटाएँ?",
        "confirmResetUserCredentials" to "इससे {email} का वर्तमान पासवर्ड और पासकी अमान्य हो जाएँगे और उन्हें एक रीसेट लिंक ईमेल किया जाएगा। जारी रखें?",
        "confirmDeactivateUser" to "इस उपयोगकर्ता को निष्क्रिय करें? इन्हें बाद में फिर से सक्रिय किया जा सकता है।",
        "confirmDeactivatePet" to "इस पालतू जानवर को निष्क्रिय करें? यह सार्वजनिक सूचियों से छिप जाएगा और बाद में फिर से सक्रिय किया जा सकता है।",
        "confirmBlockRescuer" to "इस बचावकर्ता को आपको और अनुरोध भेजने से रोकें?",
        "confirmDeletePet" to "इस पालतू जानवर को हटाएँ?",
        "confirmDeleteStorage" to "इस संग्रहण को हटाएँ?",
        "contact" to "संपर्क",
        "delete" to "हटाएँ",
        "edit" to "संपादित करें",
        "errorDeletingShelter" to "आश्रय स्थल हटाने में त्रुटि",
        "errorLoadingLocations" to "स्थान लोड करने में त्रुटि",
        "errorLoadingShelter" to "आश्रय स्थल लोड करने में त्रुटि",
        "errorSaving" to "सहेजने में त्रुटि",
        "errorSavingShelter" to "आश्रय स्थल सहेजने में त्रुटि",
        "location" to "स्थान",
        "noLocationsFound" to "कोई स्थान नहीं मिला",
        "selectCountry" to "एक देश चुनें",
        "selectCountryToFilter" to "फ़िल्टर करने के लिए कृपया एक देश चुनें",
        "shelterAdded" to "आश्रय स्थल सफलतापूर्वक जोड़ा गया",
        "shelterDeleted" to "आश्रय स्थल सफलतापूर्वक हटाया गया",
        "shelterUpdated" to "आश्रय स्थल सफलतापूर्वक अपडेट किया गया",
        "updateShelter" to "आश्रय स्थल अपडेट करें",
        "errorLoadingPhotographers" to "फ़ोटोग्राफर लोड करने में त्रुटि",
        "noPhotographersAvailable" to "कोई फ़ोटोग्राफर उपलब्ध नहीं",
        "requestPhotoSession" to "फ़ोटो सेशन का अनुरोध करें",
        "selectPhotographer" to "चुनें",
        "photographersSelected" to "{count} चयनित",
        "requestSelectedPhotographers" to "चयनित फ़ोटोग्राफ़रों से अनुरोध करें",
        "clearSelection" to "चयन साफ़ करें",
        "requestToMultiple" to "अनुरोध करें",
        "messageRequired" to "कृपया फ़ोटोग्राफ़रों के लिए एक संदेश लिखें।",
        "requestsSentSuccessfully" to "अनुरोध सफलतापूर्वक भेजे गए",
        "message" to "संदेश",
        "sendRequest" to "अनुरोध भेजें",
        "requestSentSuccessfully" to "अनुरोध सफलतापूर्वक भेज दिया गया!",
        "requestTo" to "अनुरोध प्राप्तकर्ता",
        "enterMessage" to "{name} के लिए एक संदेश दर्ज करें...",
        "reportUrgent" to "तत्काल",
        "topRescuers" to "शीर्ष बचावकर्ता",
        "urgentRescuerSettings" to "तत्काल बचावकर्ता सेटिंग्स",
        "urgentRescueAlerts" to "तत्काल बचाव अलर्ट",
        "reportUrgentTitle" to "ख़तरे में पालतू जानवर की रिपोर्ट करें",
        "reportUrgentIntro" to "यदि आपको कोई घायल, प्रताड़ित, भूखा, या परित्यक्त पालतू जानवर मिला है जिसे तत्काल मदद चाहिए, तो हमें बताएँ कि वह कहाँ है और क्या गड़बड़ है - हम तुरंत नज़दीकी तत्काल बचावकर्ताओं को सतर्क करेंगे।",
        "dangerType" to "क्या गड़बड़ है?",
        "dangerInjured" to "घायल",
        "dangerAbused" to "प्रताड़ित / दुर्व्यवहार किया जा रहा है",
        "dangerStarving" to "भूखा",
        "dangerTooYoung" to "अकेले जीवित रहने के लिए बहुत छोटा",
        "dangerOther" to "अन्य आपातकाल",
        "useMyLocation" to "मेरा वर्तमान स्थान उपयोग करें",
        "confirmMyLocation" to "मेरा वर्तमान स्थान पुष्टि करें",
        "locationPermissionHint" to "आपका ब्राउज़र स्थान साझा करने की अनुमति मांगेगा; इसका उपयोग केवल आपके आस-पास की रिपोर्ट की सूचना देने के लिए होता है।",
        "phoneOptional" to "आपका फ़ोन (वैकल्पिक)",
        "submitReport" to "रिपोर्ट सबमिट करें",
        "urgentRescuerSettingsTitle" to "तत्काल बचावकर्ता सेटिंग्स",
        "urgentRescuerSettingsIntro" to "जब आपके कवरेज क्षेत्र के पास ख़तरे में पालतू जानवर की रिपोर्ट की जाती है तो आपको सूचित किया जाएगा। सबसे पहले स्वीकार करने वाले को केस मिलेगा।",
        "urgentRescuerActive" to "मैं तत्काल रिपोर्ट का जवाब देने के लिए उपलब्ध हूँ",
        "coverageAreaMode" to "कवरेज क्षेत्र",
        "coverageArea" to "कवरेज क्षेत्र",
        "coverageAreaHint" to "अपना वर्तमान स्थान पुष्टि करें, पिन को खींचें, या नीचे एक पता टाइप करें - मानचित्र और फ़ील्ड समन्वित रहते हैं।",
        "useMyLocationAndRadius" to "मेरा स्थान + एक त्रिज्या उपयोग करें",
        "pickAZone" to "इसके बजाय एक देश/राज्य/शहर चुनें",
        "radiusKm" to "त्रिज्या (किमी)",
        "urgentRescueAlertsTitle" to "तत्काल बचाव अलर्ट",
        "urgentRescuerLeaderboardTitle" to "शीर्ष तत्काल बचावकर्ता (पिछले 12 महीने)",
        "locating" to "स्थान खोजा जा रहा है...",
        "geolocationUnsupported" to "आपका ब्राउज़र जियोलोकेशन का समर्थन नहीं करता",
        "locationCaptured" to "स्थान प्राप्त हो गया",
        "locationDenied" to "आपका स्थान प्राप्त नहीं हो सका - कृपया स्थान एक्सेस की अनुमति दें या इसके बजाय एक शहर दर्ज करें",
        "street" to "गली",
        "exteriorNumber" to "बाहरी नंबर",
        "referenceNotes" to "संदर्भ टिप्पणियाँ (जैसे घर का रंग, नज़दीकी पहचान चिह्न)",
        "concreteAddressHint" to "यदि संभव हो तो एक गली और नंबर जोड़ें - इससे बचावकर्ता टैक्सी ले सकता है या मैप्स ऐप सीधे उस स्थान तक खोल सकता है।",
        "descriptionRequired" to "कृपया स्थिति का वर्णन करें",
        "locationRequired" to "कृपया एक स्थान प्रदान करें (अपना वर्तमान स्थान उपयोग करें या एक देश और शहर दर्ज करें)",
        "submittingEllipsis" to "सबमिट किया जा रहा है...",
        "reportSubmitted" to "रिपोर्ट सबमिट हो गई। धन्यवाद - नज़दीकी बचावकर्ताओं को अभी सतर्क किया जा रहा है।",
        "reportSubmitFailed" to "रिपोर्ट सबमिट करने में विफल",
        "phoneRequired" to "कृपया एक फ़ोन नंबर दर्ज करें",
        "savingEllipsis" to "सहेजा जा रहा है...",
        "saved" to "सहेजा गया",
        "saveFailed" to "सहेजने में विफल",
        "noUrgentPages" to "अभी कोई लंबित तत्काल अलर्ट नहीं",
        "accept" to "स्वीकार करें",
        "alreadyAccepted" to "किसी और ने पहले ही इस रिपोर्ट को स्वीकार कर लिया है",
        "noLeaderboardData" to "पिछले 12 महीनों में अभी तक कोई स्वीकृत बचाव नहीं",
        "rescueAcceptedTitle" to "बचाव स्वीकृत",
        "rescueAcceptedMessage" to "धन्यवाद - आप इस पर काम कर रहे हैं। जितनी जल्दी हो सके रिपोर्ट किए गए स्थान पर जाएँ।",
        "rescueAcceptFailedTitle" to "स्वीकार नहीं किया जा सका",
        "rescueAcceptFailedMessage" to "इस रिपोर्ट को पहले ही किसी अन्य बचावकर्ता द्वारा स्वीकार किया जा चुका है, या लिंक अमान्य या समाप्त हो चुका है।",
        "share" to "साझा करें",
        "shareViaFacebook" to "Facebook पर साझा करें",
        "shareViaX" to "X पर साझा करें",
        "shareViaWhatsapp" to "WhatsApp पर साझा करें",
        "shareViaEmail" to "ईमेल से साझा करें",
        "copyLink" to "लिंक कॉपी करें",
        "linkCopied" to "लिंक कॉपी हो गया!",
        "reportLostFoundTitle" to "खोए या मिले पालतू जानवर की रिपोर्ट करें",
        "reportLostFoundIntro" to "अपना पालतू जानवर खो दिया, या कोई आवारा जानवर मिला? एक रिपोर्ट दर्ज करें और हम इसे आस-पास की अन्य रिपोर्टों से मिलाकर जाँचेंगे।",
        "browseLostFound" to "मौजूदा खोया और मिला रिपोर्ट देखें",
        "kindLost" to "मैंने अपना पालतू जानवर खो दिया",
        "kindFound" to "मुझे एक पालतू जानवर मिला",
        "petType" to "पालतू जानवर का प्रकार",
        "petTypeUnknown" to "निश्चित नहीं",
        "lostFoundBrowseTitle" to "खोए और मिले पालतू जानवर",
        "showing" to "दिखाया जा रहा है",
        "kindLostPlural" to "खोए हुए पालतू जानवर",
        "kindFoundPlural" to "मिले हुए पालतू जानवर",
        "reportResolvedTitle" to "रिपोर्ट हल हो गई",
        "reportResolvedMessage" to "अपडेट के लिए धन्यवाद - यह रिपोर्ट अब बंद कर दी गई है।",
        "reportResolveFailedTitle" to "हल नहीं किया जा सका",
        "reportResolveFailedMessage" to "यह रिपोर्ट पहले ही हल हो चुकी है, या लिंक अमान्य या समाप्त हो चुका है।",
        "reportLostFound" to "खोया/\u200Bमिला",
        "noLostFoundReportsFound" to "कोई खोया और मिला रिपोर्ट नहीं मिली",
        "errorLoadingReports" to "रिपोर्ट लोड करने में त्रुटि",
        "yourEmail" to "आपका ईमेल",
        "contactReporter" to "रिपोर्टर से संपर्क करें",
        "contactFormIncomplete" to "कृपया अपना ईमेल और एक संदेश दर्ज करें",
        "contactSent" to "संदेश भेज दिया गया",
        "contactFailed" to "संदेश भेजने में विफल",
        "lostFoundReportSubmitted" to "रिपोर्ट सबमिट हो गई। धन्यवाद - हम इसे आस-पास की अन्य रिपोर्टों से मिलाकर जाँचेंगे।",
        "video" to "वीडियो (वैकल्पिक)",
        "removeVideo" to "वीडियो हटाएँ",
        "saveThisSearch" to "यह खोज सहेजें",
        "savedSearchSaved" to "खोज सहेज ली गई! जब कोई नया मिलान सूचीबद्ध होगा तो हम आपको ईमेल करेंगे।",
        "savedSearchFailed" to "खोज सहेजने में विफल",
        "favorite" to "पसंदीदा",
        "addToFavorites" to "पसंदीदा में जोड़ें",
        "removeFromFavorites" to "पसंदीदा से हटाएँ",
        "myFavorites" to "मेरे पसंदीदा",
        "noFavoritesYet" to "अभी तक कोई पसंदीदा नहीं",
        "mySavedSearches" to "मेरी सहेजी गई खोजें",
        "noSavedSearchesYet" to "अभी तक कोई सहेजी गई खोज नहीं",
        "donationPromptText" to "आज एक पालतू जानवर की मदद करने के लिए धन्यवाद। यदि आप कर सकते हैं, तो Adopt-U को सभी के लिए मुफ़्त रखने हेतु एक छोटा दान देने पर विचार करें।",
        "donationPromptCta" to "दान करें",
        "dismiss" to "ख़ारिज करें",
    )

private val bengaliTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "লগইন",
    "register" to "নিবন্ধন করুন",
    "logout" to "সেশন বন্ধ করুন",
    "profile" to "প্রোফাইল",
    "myPets" to "আমার পোষা প্রাণী",
    "addPet" to "পোষা প্রাণী যোগ করুন",
    "save" to "সংরক্ষণ করুন",
    "cancel" to "বাতিল করুন",
    "search" to "অনুসন্ধান",
    "shelters" to "আশ্রয়কেন্দ্র",
    "photographers" to "ফটোগ্রাফার",
    "temporalHomes" to "সাময়িক আবাস",
    "findTemporalHomes" to "সাময়িক আবাস খুঁজুন",
    "sterilizationLocations" to "বন্ধ্যাকরণ কেন্দ্র",
    "donate" to "দান করুন",
    "countryLabel" to "দেশ",
    "selectCountryToSearch" to "একটি দেশ নির্বাচন করুন",
    "selectCountryFirst" to "এই ফিল্টারগুলি সক্রিয় করতে একটি দেশ নির্বাচন করুন",
    "state" to "রাজ্য",
    "city" to "শহর",
    "zipCode" to "জিপ কোড",
    "neighborhood" to "পাড়া",
    "name" to "নাম",
    "email" to "ইমেইল",
    "phone" to "ফোন",
    "address" to "ঠিকানা",
    "description" to "বিবরণ",
    "animalShelters" to "প্রাণী আশ্রয়কেন্দ্র",
    "sheltersDescription" to "এমন প্রাণী আশ্রয়কেন্দ্র খুঁজুন যেখানে আপনি দত্তক নেওয়ার জন্য পোষা প্রাণী পেতে পারেন অথবা আশ্রয়কেন্দ্রের সাথে পূর্ব সমঝোতার মাধ্যমে তাদের রেখে যেতে পারেন",
    "photographerDescription" to "পেশাদার ফটোগ্রাফার যারা পোষা প্রাণীর ফটো সেশন প্রদান করেন",
    "sterilizationLocationsDescription" to "এমন স্থান খুঁজুন যেখানে আপনি প্রাণীদের বন্ধ্যাকরণের জন্য নিয়ে যেতে পারেন।",
    "privacyPolicy" to "গোপনীয়তা নীতি",
    "privacyLastUpdated" to "সর্বশেষ আপডেট: এপ্রিল ২০২৫",
    "privacyHighlight" to "Adopt-U-তে, আমরা ন্যূনতম তথ্য সংগ্রহে বিশ্বাসী। দত্তক গ্রহণকারী ও উদ্ধারকারীদের মধ্যে যোগাযোগ সক্ষম করতে আমরা কেবল আপনার ইমেইল ঠিকানা সংগ্রহ করি। আমরা আপনার তথ্য বিজ্ঞাপনের জন্য ব্যবহার করি না, এবং বিপণনের উদ্দেশ্যে আমরা কখনও তৃতীয় পক্ষের সাথে আপনার তথ্য শেয়ার করব না।",
    "privacyInfoCollectedH2" to "আমরা যে তথ্য সংগ্রহ করি",
    "privacyAccountRegH3" to "অ্যাকাউন্ট নিবন্ধন",
    "privacyAccountRegIntro" to "Adopt-U-তে একটি অ্যাকাউন্ট তৈরি করতে, আমরা নিম্নলিখিত তথ্য সংগ্রহ করি:",
    "privacyEmailLabel" to "ইমেইল ঠিকানা: ",
    "privacyEmailDesc" to "অ্যাকাউন্ট যাচাইকরণ, পাসওয়ার্ড পুনরুদ্ধার এবং দত্তক গ্রহণকারী ও উদ্ধারকারীদের মধ্যে যোগাযোগের জন্য প্রয়োজনীয়",
    "privacyDisplayNameLabel" to "প্রদর্শন নাম: ",
    "privacyDisplayNameDesc" to "প্ল্যাটফর্মে আপনাকে চেনার জন্য আপনি যে নাম বেছে নেন",
    "privacyLangPrefLabel" to "ভাষার পছন্দ: ",
    "privacyLangPrefDesc" to "আপনার পছন্দের ভাষায় প্ল্যাটফর্মটি প্রদান করতে",
    "privacyNoFullNameEtc" to "আমরা আপনার পূর্ণ নাম, ফোন নম্বর, ভৌত ঠিকানা, পরিচয়পত্র, অর্থপ্রদানের তথ্য, অথবা উপরে উল্লেখিত তথ্যের বাইরে অন্য কোনো ব্যক্তিগত তথ্য সংগ্রহ করি না।",
    "privacyOptionalProfileH3" to "ঐচ্ছিক প্রোফাইল তথ্য",
    "privacyOptionalProfileIntro" to "আপনার ভূমিকার উপর নির্ভর করে, আপনি ঐচ্ছিকভাবে প্রদান করতে পারেন:",
    "privacyAdopterLabel" to "দত্তক গ্রহণকারী হিসেবে: ",
    "privacyAdopterDesc" to "অতিরিক্ত কোনো তথ্য প্রয়োজন বা সংগ্রহ করা হয় না",
    "privacyRescuerLabel" to "উদ্ধারকারী হিসেবে: ",
    "privacyRescuerDesc" to "দত্তকের জন্য উপলব্ধ পোষা প্রাণীর অবস্থান সংক্রান্ত তথ্য",
    "privacyPhotographerLabel" to "ফটোগ্রাফার হিসেবে: ",
    "privacyPhotographerDesc" to "সেবার অবস্থান ও যোগাযোগের পছন্দ",
    "privacyTemporalHomeLabel" to "সাময়িক আবাস হিসেবে: ",
    "privacyTemporalHomeDesc" to "যেখানে আপনি সাময়িক আবাসন প্রদান করতে পারেন সেই অবস্থান",
    "privacyLocationUsage" to "সমস্ত অবস্থান তথ্য শুধুমাত্র দত্তক গ্রহণকারীদের কাছাকাছি উদ্ধারকারী ও সেবা প্রদানকারীদের সাথে মেলানোর জন্য ব্যবহার করা হয়।",
    "privacyPetInfoH3" to "পোষা প্রাণীর তথ্য",
    "privacyPetInfoIntro" to "উদ্ধারকারীরা পোষা প্রাণী সম্পর্কে তথ্য পোস্ট করতে পারেন, যার মধ্যে রয়েছে:",
    "privacyPetPhotos" to "পোষা প্রাণীর ছবি",
    "privacyPetDescBreedAge" to "বিবরণ, জাত, বয়স এবং বৈশিষ্ট্য",
    "privacyPetHealth" to "স্বাস্থ্য ও টিকাদানের অবস্থা",
    "privacyPetLocation" to "পোষা প্রাণীটি যেখানে উপলব্ধ সেই অবস্থান",
    "privacyPetInfoNotVerified" to "এই তথ্য উদ্ধারকারীদের দ্বারা প্রদান করা হয় এবং Adopt-U দ্বারা স্বাধীনভাবে যাচাই করা হয় না।",
    "privacyHowWeUseH2" to "আমরা কীভাবে আপনার তথ্য ব্যবহার করি",
    "privacyPrimaryPurposeH3" to "মূল উদ্দেশ্য: দত্তক সংযোগ সহজতর করা",
    "privacyEmailUsedFor" to "আপনার ইমেইল ঠিকানা শুধুমাত্র নিম্নলিখিত উদ্দেশ্যে ব্যবহার করা হয়:",
    "privacyUseVerification" to "আপনি অ্যাকাউন্ট তৈরি করলে আপনাকে একটি যাচাইকরণ লিংক পাঠানো",
    "privacyUseRescuerContact" to "আপনি যে পোষা প্রাণীতে আগ্রহী তা নিয়ে উদ্ধারকারীদের আপনার সাথে যোগাযোগ করতে দেওয়া",
    "privacyUseContactRescuer" to "উদ্ধারকারীদের তালিকাভুক্ত পোষা প্রাণী সম্পর্কে তাদের সাথে যোগাযোগ করতে আপনাকে সক্ষম করা",
    "privacyUseNotifications" to "আপনাকে গুরুত্বপূর্ণ অ্যাকাউন্ট ও নিরাপত্তা বিজ্ঞপ্তি পাঠানো",
    "privacyOnceConnected" to "একবার আপনি আমাদের প্ল্যাটফর্মের মাধ্যমে অন্য ব্যবহারকারীর সাথে সংযুক্ত হলে, পরবর্তী সমস্ত যোগাযোগ সরাসরি আপনার ও সেই পক্ষের মধ্যে পরিচালিত হয়। আমরা আপনার যোগাযোগের বিষয়বস্তু পর্যবেক্ষণ, সংরক্ষণ বা অ্যাক্সেস করি না।",
    "privacyNeverDoH3" to "আমরা কখনও যা করব না",
    "privacyCommitIntro" to "আমরা নিম্নলিখিত বিষয়ে দৃঢ় প্রতিশ্রুতিবদ্ধ:",
    "privacyNoAdsLabel" to "কোনো বিজ্ঞাপন নয়: ",
    "privacyNoAdsDesc" to "আমরা কখনও আপনার কার্যকলাপের ভিত্তিতে লক্ষ্যযুক্ত বা অ-লক্ষ্যযুক্ত বিজ্ঞাপন প্রদর্শন করব না",
    "privacyNoMarketingLabel" to "কোনো বিপণন নয়: ",
    "privacyNoMarketingDesc" to "আমরা কখনও বিপণন বা প্রচারমূলক উদ্দেশ্যে তৃতীয় পক্ষের সাথে আপনার ইমেইল শেয়ার করব না",
    "privacyNoSellingLabel" to "কোনো তথ্য বিক্রি নয়: ",
    "privacyNoSellingDesc" to "আমরা কখনও আপনার ব্যক্তিগত তথ্য কোনো তৃতীয় পক্ষের কাছে বিক্রি, ভাড়া বা অন্য কোনোভাবে স্থানান্তর করব না",
    "privacyNoProfilingLabel" to "কোনো প্রোফাইলিং নয়: ",
    "privacyNoProfilingDesc" to "মৌলিক অ্যাকাউন্ট ব্যবস্থাপনার বাইরে অন্য কোনো উদ্দেশ্যে প্রোফাইল তৈরি করতে আমরা আপনার তথ্য ব্যবহার করব না",
    "privacyNoTrackingLabel" to "কোনো ট্র্যাকিং নয়: ",
    "privacyNoTrackingDesc" to "আমরা ট্র্যাকিং পিক্সেল, বিজ্ঞাপনের জন্য কুকি, বা অনুরূপ কোনো ট্র্যাকিং প্রযুক্তি ব্যবহার করি না",
    "privacyDataStorageH2" to "তথ্য সংরক্ষণ ও নিরাপত্তা",
    "privacyDataStoredSecurely" to "আপনার তথ্য শিল্প-মানের এনক্রিপশন ব্যবহার করে নিরাপদে সংরক্ষণ করা হয়। অননুমোদিত অ্যাক্সেস, পরিবর্তন, প্রকাশ বা ধ্বংস থেকে আপনার ব্যক্তিগত তথ্য রক্ষা করতে আমরা উপযুক্ত প্রযুক্তিগত ও প্রাতিষ্ঠানিক ব্যবস্থা গ্রহণ করি।",
    "privacyDataRetention" to "আপনার অ্যাকাউন্ট সক্রিয় থাকা পর্যন্ত আমরা আপনার অ্যাকাউন্ট তথ্য সংরক্ষণ করি। আপনি যেকোনো সময় আমাদের সাথে যোগাযোগ করে আপনার অ্যাকাউন্ট ও সংশ্লিষ্ট তথ্য মুছে ফেলার অনুরোধ করতে পারেন।",
    "privacyYourRightsH2" to "আপনার অধিকার",
    "privacyRightsIntro" to "আপনার ব্যক্তিগত তথ্য সম্পর্কে আপনার নিম্নলিখিত অধিকার রয়েছে:",
    "privacyRightAccess" to "অ্যাক্সেস: আমরা আপনার সম্পর্কে যে সমস্ত ব্যক্তিগত তথ্য রাখি তার একটি কপি আপনি অনুরোধ করতে পারেন",
    "privacyRightCorrection" to "সংশোধন: কোনো ভুল তথ্য সংশোধনের জন্য আপনি অনুরোধ করতে পারেন",
    "privacyRightDeletion" to "মুছে ফেলা: আপনার অ্যাকাউন্ট ও সংশ্লিষ্ট সমস্ত তথ্য মুছে ফেলার জন্য আপনি অনুরোধ করতে পারেন",
    "privacyRightPortability" to "বহনযোগ্যতা: সাধারণভাবে ব্যবহৃত ফরম্যাটে আপনার তথ্য প্রদানের জন্য আপনি অনুরোধ করতে পারেন",
    "privacyExerciseRights" to "এই অধিকারগুলির যেকোনো একটি প্রয়োগ করতে, অনুগ্রহ করে adopt-u@adopt-u.org-এ আমাদের সাথে যোগাযোগ করুন।",
    "privacyCookiesH2" to "কুকি",
    "privacyCookiesText" to "Adopt-U প্ল্যাটফর্মের কার্যকারিতার জন্য প্রয়োজনীয় ন্যূনতম কুকি ব্যবহার করে, যার মধ্যে রয়েছে সেশন ব্যবস্থাপনা ও ভাষার পছন্দ। আমরা বিজ্ঞাপন কুকি বা ট্র্যাকিং কুকি ব্যবহার করি না।",
    "privacyThirdPartyH2" to "তৃতীয় পক্ষের সেবা",
    "privacyThirdPartyIntro" to "আমরা নিম্নলিখিত তৃতীয় পক্ষের সেবা ব্যবহার করি, যা আপনার তথ্য প্রক্রিয়া করতে পারে:",
    "privacyAws" to "AWS (Amazon Web Services): তথ্য সংরক্ষণ ও ইমেইল প্রেরণের জন্য। আপনার তথ্য প্রক্রিয়াকরণের ক্ষেত্রে তাদের গোপনীয়তা নীতি প্রযোজ্য।",
    "privacyWebauthn" to "WebAuthn/FIDO2: নিরাপদ পাসকী প্রমাণীকরণের জন্য। এতে আপনার ব্রাউজারে ক্রিপ্টোগ্রাফিক অপারেশন জড়িত।",
    "privacyThirdPartySelected" to "আমরা সাবধানে এমন সেবা প্রদানকারী নির্বাচন করেছি যারা তথ্য গোপনীয়তার প্রতি আমাদের প্রতিশ্রুতি ভাগ করে নেয়।",
    "privacyChildrensH2" to "শিশুদের গোপনীয়তা",
    "privacyChildrensText" to "Adopt-U ১৮ বছরের কম বয়সী ব্যবহারকারীদের জন্য নয়। আমরা জ্ঞাতসারে অপ্রাপ্তবয়স্কদের কাছ থেকে তথ্য সংগ্রহ করি না। যদি আপনি মনে করেন কোনো অপ্রাপ্তবয়স্ক ব্যক্তি অ্যাকাউন্ট তৈরি করেছে, তবে তা অপসারণের জন্য অনুগ্রহ করে আমাদের সাথে যোগাযোগ করুন।",
    "privacyChangesH2" to "এই নীতিতে পরিবর্তন",
    "privacyChangesText" to "আমাদের অনুশীলন বা আইনি প্রয়োজনীয়তার পরিবর্তন প্রতিফলিত করতে আমরা সময়ে সময়ে এই গোপনীয়তা নীতি আপডেট করতে পারি। উল্লেখযোগ্য পরিবর্তনের বিষয়ে আমরা ইমেইল বা প্ল্যাটফর্মে সুস্পষ্ট নোটিশের মাধ্যমে ব্যবহারকারীদের জানাব।",
    "privacyContactH2" to "আমাদের সাথে যোগাযোগ করুন",
    "privacyContactText" to "এই গোপনীয়তা নীতি সম্পর্কে আপনার কোনো প্রশ্ন বা উদ্বেগ থাকলে, অথবা আপনার তথ্য অধিকার প্রয়োগ করতে, অনুগ্রহ করে adopt-u@adopt-u.org-এ আমাদের সাথে যোগাযোগ করুন।",
    "termsConditions" to "শর্তাবলী",
    "installApp" to "অ্যাপ ইনস্টল করুন",
    "termsAcceptanceH2" to "শর্তাবলীর গ্রহণযোগ্যতা",
    "termsAcceptanceText" to "Adopt-U অ্যাক্সেস ও ব্যবহার করার মাধ্যমে, আপনি এই চুক্তির শর্তাবলী ও বিধান দ্বারা আবদ্ধ থাকতে সম্মত হন। আপনি যদি এই শর্তাবলীতে সম্মত না হন, তবে অনুগ্রহ করে আমাদের প্ল্যাটফর্ম ব্যবহার করবেন না।",
    "termsNatureH2" to "আমাদের সেবার প্রকৃতি",
    "termsNatureText1" to "Adopt-U একটি সংযোগ প্ল্যাটফর্ম যা পোষা প্রাণী উদ্ধারকারী ও সম্ভাব্য দত্তক গ্রহণকারীদের মধ্যে যোগাযোগ সহজতর করে। আমরা কোনো পোষা প্রাণীর দোকান, দালাল, আশ্রয়কেন্দ্র বা দত্তক সংস্থা নই। আমাদের প্ল্যাটফর্মে তালিকাভুক্ত কোনো পোষা প্রাণীর আমরা মালিকানা, হেফাজত বা মধ্যস্থতা করি না।",
    "termsNatureText2" to "আমাদের ভূমিকা কঠোরভাবে পক্ষগুলির মধ্যে একটি যোগাযোগ মাধ্যম প্রদানে সীমাবদ্ধ। আমরা দত্তক আলোচনা, চুক্তি বা প্রাণীর শারীরিক হস্তান্তরে অংশগ্রহণ করি না।",
    "termsUserRespH2" to "ব্যবহারকারীর দায়িত্ব",
    "termsForRescuersH3" to "উদ্ধারকারীদের জন্য:",
    "termsRescuerResp1" to "আপনার যত্নে থাকা পোষা প্রাণী সম্পর্কে সঠিক ও সত্য তথ্য প্রদান করুন",
    "termsRescuerResp2" to "তালিকাভুক্ত করার আগে পোষা প্রাণী সুস্থ, টিকাপ্রাপ্ত এবং যথাযথভাবে পরীক্ষিত কিনা তা নিশ্চিত করুন",
    "termsRescuerResp3" to "সম্ভাব্য দত্তক গ্রহণকারীদের দায়িত্বশীল যাচাই পরিচালনা করুন",
    "termsRescuerResp4" to "পোষা প্রাণীদের নতুন বাড়িতে নিরাপদ হস্তান্তরের ব্যবস্থা করুন",
    "termsRescuerResp5" to "প্রদত্ত সমস্ত তথ্যের নির্ভুলতার দায়িত্ব গ্রহণ করুন",
    "termsRescuerResp6" to "পোষা প্রাণী দত্তক সংক্রান্ত সমস্ত স্থানীয় আইন ও প্রবিধান মেনে চলুন",
    "termsRescuerResp7" to "বাণিজ্যিক পোষা প্রাণী বিক্রয় বা প্রজননের উদ্দেশ্যে কখনও প্ল্যাটফর্ম ব্যবহার করবেন না",
    "termsForAdoptersH3" to "দত্তক গ্রহণকারীদের জন্য:",
    "termsAdopterResp1" to "আপনার বসবাসের পরিস্থিতি ও পোষা প্রাণীর সাথে অভিজ্ঞতা সম্পর্কে সঠিক তথ্য প্রদান করুন",
    "termsAdopterResp2" to "যথাযথ যত্ন প্রদানের সক্ষমতা প্রদর্শনের জন্য প্রস্তুত থাকুন",
    "termsAdopterResp3" to "বুঝুন যে পোষা প্রাণীর দত্তক বাতিল করলে প্রাণীটির উল্লেখযোগ্য মানসিক চাপ হয়",
    "termsAdopterResp4" to "আপনার দত্তক অনুরোধ প্রত্যাখ্যান করার উদ্ধারকারীর অধিকারকে সম্মান করুন",
    "termsPetListingsH2" to "পোষা প্রাণীর তালিকা ও তথ্য",
    "termsPetListingsText1" to "Adopt-U পোষা প্রাণীর তালিকায় প্রদত্ত তথ্যের নির্ভুলতা যাচাই, অনুমোদন বা নিশ্চয়তা দেয় না। জাত, বয়স, স্বাস্থ্যের অবস্থা, স্বভাব এবং টিকাদানের রেকর্ড সহ কিন্তু এতে সীমাবদ্ধ নয় এমন তথ্য উদ্ধারকারীদের দ্বারা প্রদান করা হয় এবং স্বাধীনভাবে যাচাই করা হয়নি।",
    "termsPetListingsText2" to "ছবি ও বিবরণ উদ্ধারকারীদের দ্বারা প্রদান করা হয়। যদিও আমরা ইচ্ছাকৃত ভুল উপস্থাপনা নিষিদ্ধ করি, আমরা নিশ্চয়তা দিতে পারি না যে সমস্ত তালিকা প্রাণীর বর্তমান অবস্থা সঠিকভাবে উপস্থাপন করে।",
    "termsPetListingsText3" to "কোনো দত্তক ব্যবস্থা চূড়ান্ত করার আগে প্রশ্ন জিজ্ঞাসা করতে, পশুচিকিৎসা রেকর্ড অনুরোধ করতে এবং পোষা প্রাণীর সাথে সাক্ষাৎ করতে ব্যবহারকারীদের উৎসাহিত করা হয়।",
    "termsAdoptionProcessH2" to "দত্তক প্রক্রিয়া",
    "termsAdoptionProcessIntro" to "একবার উদ্ধারকারী ও দত্তক গ্রহণকারী দত্তক নিয়ে এগিয়ে যেতে সম্মত হলে:",
    "termsAdoptionStep1" to "হস্তান্তরের তারিখ, স্থান এবং যেকোনো সংশ্লিষ্ট খরচ সহ সমস্ত ব্যবস্থা সরাসরি পক্ষগুলির মধ্যে আলোচনা করা হয়",
    "termsAdoptionStep2" to "Adopt-U কোনো অর্থপ্রদান লেনদেন সহজতর বা নিশ্চিত করে না",
    "termsAdoptionStep3" to "প্ল্যাটফর্ম দত্তক চুক্তি প্রদান করে না; যেকোনো চুক্তিভিত্তিক ব্যবস্থা সংশ্লিষ্ট পক্ষগুলির একমাত্র দায়িত্ব",
    "termsAdoptionStep4" to "আমরা জোরালোভাবে সুপারিশ করি যে সমস্ত চুক্তি লিখিতভাবে নথিভুক্ত করা হোক",
    "termsLiabilityH2" to "দায় সীমাবদ্ধতা",
    "termsLiabilityIntro" to "Adopt-U নিম্নলিখিত বিষয়ে দায়ী থাকবে না:",
    "termsLiability1" to "উদ্ধারকারী ও দত্তক গ্রহণকারীদের মধ্যে যেকোনো বিরোধ, মতানৈক্য বা দ্বন্দ্ব",
    "termsLiability2" to "পোষা প্রাণীর তালিকায় প্রদত্ত তথ্যের নির্ভুলতা",
    "termsLiability3" to "দত্তকের আগে, চলাকালীন বা পরে যেকোনো প্রাণীর স্বাস্থ্য, আচরণ বা অবস্থা",
    "termsLiability4" to "প্ল্যাটফর্ম ব্যবহারের ফলে উদ্ভূত যেকোনো আর্থিক ক্ষতি, ক্ষয়ক্ষতি বা আঘাত",
    "termsLiability5" to "ফেরত, স্বাস্থ্য সমস্যা বা সামঞ্জস্যের সমস্যা সহ কিন্তু এতে সীমাবদ্ধ নয় এমন যেকোনো দত্তকের ফলাফল",
    "termsLiability6" to "যোগাযোগ স্থাপিত হওয়ার পর প্ল্যাটফর্মের বাইরে ব্যবহারকারীদের দ্বারা গৃহীত যেকোনো পদক্ষেপ",
    "termsLiabilityAck" to "ব্যবহারকারীরা স্বীকার করেন যে পোষা প্রাণী দত্তক নেওয়া একটি গুরুত্বপূর্ণ প্রতিশ্রুতি এবং তারা দত্তক নেওয়ার সিদ্ধান্তের সম্পূর্ণ দায়িত্ব গ্রহণ করেন।",
    "termsProhibitedH2" to "নিষিদ্ধ কার্যক্রম",
    "termsProhibitedIntro" to "Adopt-U-তে নিম্নলিখিত কার্যক্রম কঠোরভাবে নিষিদ্ধ:",
    "termsProhibited1" to "বাণিজ্যিক পোষা প্রাণী বিক্রয়, প্রজনন কার্যক্রম বা পাপি মিল",
    "termsProhibited2" to "আপনি সরাসরি যত্ন নেন না এমন পোষা প্রাণী তালিকাভুক্ত করা",
    "termsProhibited3" to "তালিকা বা প্রোফাইলে মিথ্যা বা বিভ্রান্তিকর তথ্য",
    "termsProhibited4" to "অন্যান্য ব্যবহারকারীদের সাথে হয়রানি, বৈষম্য বা অনুপযুক্ত যোগাযোগ",
    "termsProhibited5" to "পোষা প্রাণী দত্তকের সাথে অসম্পর্কিত উদ্দেশ্যে ব্যক্তিগত তথ্য সংগ্রহের জন্য প্ল্যাটফর্ম ব্যবহার করা",
    "termsProhibited6" to "যেকোনো অবৈধ কার্যক্রম বা প্রাণী কল্যাণ আইনের লঙ্ঘন",
    "termsAccountTerminationH2" to "অ্যাকাউন্ট বন্ধকরণ",
    "termsAccountTerminationText" to "Adopt-U এমন অ্যাকাউন্ট স্থগিত বা বন্ধ করার অধিকার সংরক্ষণ করে যা এই শর্তাবলী লঙ্ঘন করে, নিষিদ্ধ কার্যক্রমে জড়িত থাকে, বা প্ল্যাটফর্মকে বদনামের মধ্যে ফেলে। আমরা মিথ্যা, বিভ্রান্তিকর বা অনুপযুক্ত বিষয়বস্তুযুক্ত তালিকাও অপসারণ করতে পারি।",
    "termsModificationsH2" to "শর্তাবলীতে পরিবর্তন",
    "termsModificationsText" to "আমরা যেকোনো সময় এই শর্তাবলী পরিবর্তন করার অধিকার সংরক্ষণ করি। পরিবর্তনের পরে প্ল্যাটফর্মের ধারাবাহিক ব্যবহার সংশোধিত শর্তাবলীর গ্রহণযোগ্যতা নির্দেশ করে। উল্লেখযোগ্য পরিবর্তনের বিষয়ে আমরা ইমেইল বা প্ল্যাটফর্ম ঘোষণার মাধ্যমে ব্যবহারকারীদের জানাব।",
    "termsContactText" to "এই শর্তাবলী সম্পর্কে আপনার কোনো প্রশ্ন থাকলে, অনুগ্রহ করে adopt-u@adopt-u.org-এ আমাদের সাথে যোগাযোগ করুন।",
    "language" to "ভাষা",
    "displayName" to "প্রদর্শন নাম",
    "saveProfile" to "প্রোফাইল সংরক্ষণ করুন",
    "accountSettings" to "অ্যাকাউন্ট সেটিংস",
    "yourRoles" to "আপনার ভূমিকা:",
    "resources" to "সম্পদ",
    "loginWithPasskey" to "পাসকী দিয়ে লগইন করুন",
    "signInWithPasskey" to "পাসকী দিয়ে সাইন ইন করুন",
    "resendVerificationEmail" to "যাচাইকরণ ইমেইল পুনরায় পাঠান",
    "usesFido" to "নিরাপদ পাসওয়ার্ডবিহীন প্রমাণীকরণের জন্য FIDO2 / WebAuthn ব্যবহার করে।",
    "loginWithMagicLink" to "ইমেইল লিংক দিয়ে লগইন করুন",
    "magicLinkDesc" to "আমরা আপনার ইমেইলে একটি লগইন লিংক পাঠাব (৫ মিনিটের জন্য বৈধ)।",
    "sendMagicLink" to "লগইন লিংক পাঠান",
    "loginWithPassword" to "পাসওয়ার্ড দিয়ে লগইন করুন",
    "signIn" to "সাইন ইন",
    "forgotPassword" to "পাসওয়ার্ড ভুলে গেছেন?",
    "registerNewAccount" to "অ্যাকাউন্ট তৈরি করুন",
    "accountDetails" to "অ্যাকাউন্টের বিবরণ",
    "registrationMethod" to "নিবন্ধন পদ্ধতি",
    "selectAdditionalRoles" to "অতিরিক্ত ভূমিকা নির্বাচন করুন:",
    "adoptPet" to "একটি পোষা প্রাণী দত্তক নিন",
    "publishPets" to "দত্তকের জন্য পোষা প্রাণী প্রকাশ করুন",
    "offerPhotography" to "ফটোগ্রাফি সেবা প্রদান করুন",
    "photographerSettings" to "ফটোগ্রাফার সেটিংস",
    "sessionFee" to "সেশন ফি",
    "provideTemporaryHome" to "পোষা প্রাণীর জন্য সাময়িক আবাস প্রদান করুন",
    "passkeyNoPassword" to "পাসকী পাসওয়ার্ডের চেয়ে বেশি নিরাপদ এবং আপনার সমস্ত ডিভাইসে কাজ করে।",
    "registerWithPasskey" to "পাসকী দিয়ে নিবন্ধন করুন",
    "passkeySettings" to "পাসকী সেটিংস",
    "password" to "পাসওয়ার্ড",
    "confirmPassword" to "পাসওয়ার্ড নিশ্চিত করুন",
    "passwordRequirements" to "পাসওয়ার্ড অবশ্যই কমপক্ষে ৮ অক্ষরের হতে হবে এবং বড় হাতের অক্ষর, ছোট হাতের অক্ষর, সংখ্যা ও প্রতীক থাকতে হবে",
    "securityInfo" to "নিরাপত্তা তথ্য",
    "passkeyMostSecure" to "পাসকী: সবচেয়ে নিরাপদ, সুপারিশকৃত",
    "passkeyBrowserCompat" to "পাসকী সব ব্রাউজারে কাজ নাও করতে পারে",
    "passwordLeastSecure" to "পাসওয়ার্ড: কম নিরাপদ, সুপারিশ করা হয় না",
    "selectLoginMethods" to "এক বা একাধিক লগইন পদ্ধতি নির্বাচন করুন:",
    "registerPasskeyOption" to "পাসকী (সবচেয়ে নিরাপদ, আপনার সমস্ত ডিভাইসে কাজ করে)",
    "registerPasswordOption" to "পাসওয়ার্ড (কম নিরাপদ, শুধুমাত্র ব্যাকআপ হিসেবে ব্যবহার করুন)",
    "howPasskeysWorkTitle" to "পাসকী কীভাবে কাজ করে?",
    "howPasskeysWorkIntro" to "একটি পাসকী আপনার পাসওয়ার্ডকে আপনার ডিভাইসের অন্তর্নির্মিত নিরাপত্তা দিয়ে প্রতিস্থাপন করে — আঙুলের ছাপ, মুখ শনাক্তকরণ, অথবা স্ক্রিন লক পিন।",
    "howPasskeysWorkSecurity" to "এটি শুধুমাত্র আপনার ডিভাইসে সংরক্ষিত থাকে, ফিশিং বা তথ্য ফাঁসের মাধ্যমে চুরি করা যায় না, এবং স্বয়ংক্রিয়ভাবে আপনার ডিভাইসগুলির মধ্যে সিঙ্ক হয়।",
    "howPasskeysWorkBackup" to "আপনি একটি পাসওয়ার্ডও যোগ করতে পারেন, তবে সাইন ইন করার জন্য শুধুমাত্র একটি পাসকীই যথেষ্ট — কোনো পাসওয়ার্ডের প্রয়োজন নেই।",
    "gotIt" to "বুঝেছি",
    "alreadyHaveAccount" to "ইতিমধ্যে একটি অ্যাকাউন্ট আছে?",
    "all" to "সব",
    "filterDogs" to "কুকুর",
    "filterCats" to "বিড়াল",
    "filterBirds" to "পাখি",
    "filterFish" to "মাছ",
    "filterAllSexOption" to "সব লিঙ্গ",
    "filterMaleOption" to "♂ পুরুষ",
    "filterFemaleOption" to "♀ মহিলা",
    "age" to "বয়স",
    "rescued" to "উদ্ধারকৃত",
    "years" to "বছর",
    "months" to "মাস",
    "year" to "বছর",
    "month" to "মাস",
    "loginToAdopt" to "দত্তক নেওয়ার অনুরোধ করতে লগ ইন করুন",
    "requiredMarker" to "(আবশ্যক)",
    "pageNotFoundTitle" to "পৃষ্ঠা পাওয়া যায়নি",
    "pageNotFoundHint" to "লিঙ্কটি পুরনো বা ভুল হতে পারে। পোষা প্রাণীরা এখনও এখানে আছে।",
    "browsePets" to "পোষা প্রাণী দেখুন",
    "requestsFromRescuers" to "উদ্ধারকারীদের অনুরোধ",
    "signingOut" to "সাইন আউট হচ্ছে...",
    "adopter" to "দত্তক গ্রহণকারী",
    "country" to "দেশ",
    "zip" to "পোস্ট কোড",
    "viewPublic" to "পাবলিক পৃষ্ঠা দেখুন",
    "banUser" to "ব্যবহারকারীকে নিষিদ্ধ করুন",
    "banReason" to "কারণ (ঐচ্ছিক)",
    "myPhotoRequests" to "আমার ফটো সেশন অনুরোধ",
    "noPhotoRequestsYet" to "আপনি এখনও ফটো সেশনের অনুরোধ করেননি।",
    "photoRequestsReceived" to "প্রাপ্ত ফটো সেশন অনুরোধ",
    "noPhotoRequestsReceived" to "এখনও কোনো ফটো সেশন অনুরোধ নেই।",
    "photoStatusPending" to "অপেক্ষমাণ",
    "photoStatusApproved" to "অনুমোদিত",
    "photoStatusRejected" to "প্রত্যাখ্যাত",
    "photoStatusCancelled" to "বাতিল",
    "photoStatusCompleted" to "সম্পন্ন",
    "cancelRequest" to "অনুরোধ বাতিল করুন",
    "registerConsentPrefix" to "অ্যাকাউন্ট তৈরি করলে আপনি মেনে নিচ্ছেন:",
    "registerConsentTerms" to "শর্তাবলী",
    "registerConsentAnd" to "এবং",
    "registerConsentPrivacy" to "গোপনীয়তা নীতি",
    "petStatusAvailable" to "উপলব্ধ",
    "petStatusAdopted" to "দত্তক নেওয়া হয়েছে",
    "petStatusDisabled" to "নিষ্ক্রিয়",
    "petStatusPending" to "মুলতুবি",
    "addEditLocation" to "অবস্থান যোগ/সম্পাদনা করুন",
    "addLocation" to "অবস্থান যোগ করুন",
    "manage" to "পরিচালনা",
    "adminPanel" to "অ্যাডমিন প্যানেল",
    "adminSterilizationLocations" to "বন্ধ্যাকরণ কেন্দ্র পরিচালনা করুন",
    "adopterRequired" to "দত্তক গ্রহণকারী (আবশ্যক)",
    "adoptionFee" to "দত্তক ফি",
    "adoptionRequests" to "দত্তক অনুরোধ",
    "ageMonths" to "বয়স (মাস)",
    "ageYears" to "বয়স (বছর)",
    "alias" to "ডাকনাম",
    "bird" to "পাখি",
    "breed" to "জাত",
    "medicalEventsOverview" to "চিকিৎসা ইভেন্টের সংক্ষিপ্ত বিবরণ",
    "back" to "পিছনে",
    "cat" to "বিড়াল",
    "color" to "রঙ",
    "compatibility" to "সামঞ্জস্য",
    "currentPassword" to "বর্তমান পাসওয়ার্ড",
    "dog" to "কুকুর",
    "emailChange" to "ইমেইল পরিবর্তন করুন",
    "emailChangeDesc" to "আপনার ইমেইল ঠিকানা আপডেট করুন। নতুন ইমেইলে একটি যাচাইকরণ লিংক পাঠানো হবে।",
    "energyLevel" to "শক্তির মাত্রা",
    "female" to "মহিলা",
    "findTemporalHome" to "সাময়িক আবাস খুঁজুন",
    "fish" to "মাছ",
    "goodWithCats" to "বিড়ালের সাথে ভালো",
    "goodWithDogs" to "কুকুরের সাথে ভালো",
    "goodWithKids" to "শিশুদের সাথে ভালো",
    "high" to "উচ্চ",
    "houseTrained" to "ঘরে প্রশিক্ষিত",
    "large" to "বড়",
    "low" to "কম",
    "male" to "পুরুষ",
    "managePets" to "পোষা প্রাণী পরিচালনা করুন",
    "managePetsBtn" to "পোষা প্রাণী যোগ বা সম্পাদনা করুন",
    "managePetsDescription" to "সমস্ত পোষা প্রাণীর পাতা পরিচালনা করুন। পোষা প্রাণী যোগ বা অপসারণ করুন।",
    "manageShelters" to "আশ্রয়কেন্দ্র পরিচালনা করুন",
    "manageSheltersBtn" to "আশ্রয় যোগ বা সম্পাদনা করুন",
    "manageSheltersDescription" to "সমস্ত আশ্রয়কেন্দ্র পাতা পরিচালনা করুন। আশ্রয়কেন্দ্র যোগ বা অপসারণ করুন।",
    "manageUsers" to "ব্যবহারকারী পরিচালনা করুন",
    "medical" to "চিকিৎসা",
    "medium" to "মাঝারি",
    "backToSearch" to "অনুসন্ধানে ফিরে যান",
    "contactThisHome" to "এই সাময়িক আবাসের সাথে যোগাযোগ করুন",
    "loginAsRescuerToContact" to "এই সাময়িক আবাসে অনুরোধ পাঠাতে উদ্ধারকারী হিসেবে লগইন করুন।",
    "memberSince" to "সদস্য যখন থেকে",
    "microchipId" to "মাইক্রোচিপ আইডি",
    "microchipped" to "মাইক্রোচিপযুক্ত",
    "myTemporalHome" to "আমার সাময়িক আবাস",
    "newEmail" to "নতুন ইমেইল",
    "noTemporalHomes" to "কোনো সাময়িক আবাস পাওয়া যায়নি।",
    "requestSentToHome" to "আপনার অনুরোধ পাঠানো হয়েছে!",
    "sendRequestBtn" to "অনুরোধ পাঠান",
    "temporalHomeNotFound" to "এই সাময়িক আবাসটি খুঁজে পাওয়া যায়নি।",
    "viewDetails" to "বিস্তারিত দেখুন",
    "rescuerDirectory" to "উদ্ধারকারী",
    "noRescuersFound" to "কোনো উদ্ধারকারী পাওয়া যায়নি।",
    "availablePetsCount" to "উপলব্ধ পোষা প্রাণী",
    "availablePets" to "উপলব্ধ পোষা প্রাণী",
    "noAvailablePetsForRescuer" to "এই উদ্ধারকারীর কাছে এখন কোনো উপলব্ধ পোষা প্রাণী নেই।",
    "rescuerNotFound" to "এই উদ্ধারকারীকে খুঁজে পাওয়া যায়নি।",
    "errorLoadingResults" to "ফলাফল লোড করতে ত্রুটি হয়েছে।",
    "volunteerForThisRescuer" to "এই উদ্ধারকারীর জন্য স্বেচ্ছাসেবক হন",
    "volunteerExplanation" to "অনুমোদিত স্বেচ্ছাসেবকরা এই উদ্ধারকারীর পোষা প্রাণীর তালিকায় সম্পাদনার পরামর্শ দিতে পারেন।",
    "applyToVolunteerBtn" to "স্বেচ্ছাসেবক হতে আবেদন করুন",
    "loginToVolunteer" to "এই উদ্ধারকারীর জন্য স্বেচ্ছাসেবক হতে আবেদন করতে লগইন করুন।",
    "volunteerApplicationSent" to "আপনার স্বেচ্ছাসেবক আবেদন পাঠানো হয়েছে!",
    "myVolunteering" to "আমার স্বেচ্ছাসেবা",
    "noVolunteerApplicationsYet" to "আপনি এখনও কোনো উদ্ধারকারীর জন্য স্বেচ্ছাসেবক হতে আবেদন করেননি।",
    "howToVolunteerHint" to "আবেদন করতে, ডিরেক্টরি থেকে একজন উদ্ধারকারীর পেজ খুলুন এবং \"স্বেচ্ছাসেবক হতে আবেদন করুন\" চাপুন।",
    "volunteerStatusPending" to "মুলতুবি",
    "volunteerStatusActive" to "সক্রিয়",
    "volunteerStatusRejected" to "প্রত্যাখ্যাত",
    "volunteerApplications" to "স্বেচ্ছাসেবক আবেদন",
    "noVolunteerApplications" to "কোনো স্বেচ্ছাসেবক আবেদন নেই।",
    "petEditSuggestions" to "পোষা প্রাণী সম্পাদনার পরামর্শ",
    "noPetEditSuggestions" to "কোনো মুলতুবি সম্পাদনার পরামর্শ নেই।",
    "suggestedByLabel" to "পরামর্শ দিয়েছেন",
    "suggestAnEditTitle" to "একটি সম্পাদনার পরামর্শ দিন",
    "suggestAnEditExplanation" to "একজন অনুমোদিত স্বেচ্ছাসেবক হিসেবে, আপনি এই তালিকায় পরিবর্তনের পরামর্শ দিতে পারেন। প্রকাশের আগে উদ্ধারকারী তা পর্যালোচনা করবেন।",
    "suggestEditBtn" to "পরামর্শ পাঠান",
    "editSuggestionSent" to "আপনার পরামর্শ পর্যালোচনার জন্য উদ্ধারকারীর কাছে পাঠানো হয়েছে!",
    "sponsorThisPetTitle" to "এই পোষা প্রাণীকে স্পনসর করুন",
    "sponsorExplanation" to "দত্তক নিতে পারছেন না কিন্তু খরচে সাহায্য করতে চান? অর্থ বা প্রকারভেদে সহায়তা প্রদান করুন এবং সরাসরি উদ্ধারকারীর সাথে যোগাযোগ করুন।",
    "sponsorThisRescuerTitle" to "এই উদ্ধারকারীকে স্পনসর করুন",
    "sponsorRescuerExplanation" to "অর্থ বা প্রকারভেদে সহায়তার মাধ্যমে এই উদ্ধারকারীর সাধারণ খরচে সহায়তা করুন।",
    "sponsorTypeLabel" to "আপনি কী প্রদান করতে পারেন?",
    "sponsorTypeMoney" to "অর্থ",
    "sponsorTypeInKind" to "প্রকারভেদে (খাবার, সরবরাহ ইত্যাদি)",
    "amountLabel" to "পরিমাণ (মাসিক)",
    "currencyLabel" to "মুদ্রা",
    "inKindDescriptionLabel" to "আপনি কী প্রদান করতে পারেন তা বর্ণনা করুন",
    "messageLabel" to "বার্তা",
    "sendOfferBtn" to "প্রস্তাব পাঠান",
    "sponsorOfferSent" to "আপনার প্রস্তাব পাঠানো হয়েছে! উদ্ধারকারী বিস্তারিত ব্যবস্থার জন্য যোগাযোগ করবেন।",
    "sponsorshipOffersReceived" to "স্পনসরশিপ প্রস্তাব",
    "noSponsorshipOffers" to "এখনও কোনো স্পনসরশিপ প্রস্তাব নেই।",
    "generalFundLabel" to "সাধারণ তহবিল",
    "offeringLabel" to "প্রদান করছেন",
    "sponsorshipStatusSent" to "নতুন",
    "sponsorshipStatusRead" to "পঠিত",
    "markAsReadBtn" to "পঠিত হিসেবে চিহ্নিত করুন",
    "mySponsorshipOffers" to "আমার স্পনসরশিপ প্রস্তাব",
    "noSponsorshipOffersYet" to "আপনি এখনও কোনো স্পনসরশিপ প্রস্তাব পাঠাননি।",
    "howToSponsorHint" to "স্পনসর করতে, একজন উদ্ধারকারীর পেজ (সাধারণ সহায়তা) বা একটি পোষা প্রাণীর পেজ (সেই প্রাণীর খরচ) খুলুন এবং একটি প্রস্তাব পাঠান।",
    "browsePetsLink" to "পোষা প্রাণী দেখুন",
    "yourMessage" to "আপনার বার্তা",
    "newPassword" to "নতুন পাসওয়ার্ড",
    "passkeyDesc" to "ডিভাইসগুলিতে সহজ লগইনের জন্য আপনার অ্যাকাউন্টে অতিরিক্ত পাসকী যোগ করুন।",
    "passkeyName" to "পাসকীর নাম",
    "pers" to "মানুষ",
    "petFoodDescription" to "আপনার পোষা প্রাণীর জন্য কোন খাবার নিরাপদ, ক্ষতিকর, বা বিষাক্ত তা জানুন",
    "petFoodGuide" to "পোষা প্রাণীর খাদ্য নির্দেশিকা",
    "foodInformation" to "খাদ্য তথ্য",
    "petsForAdoption" to "দত্তকের জন্য পোষা প্রাণী",
    "chooseCountryPrompt" to "আপনার নতুন সেরা বন্ধু খুঁজুন",
    "chooseCountryPromptHint" to "আপনার কাছাকাছি দত্তকের জন্য উপলব্ধ পোষা প্রাণী দেখতে উপরে একটি দেশ নির্বাচন করুন।",
    "noPetsFound" to "কোনো পোষা প্রাণী পাওয়া যায়নি",
    "noPetsFoundHint" to "ভিন্ন একটি ফিল্টার চেষ্টা করুন অথবা শীঘ্রই আবার দেখুন — নিয়মিত নতুন পোষা প্রাণী যোগ করা হয়।",
    "loadingMorePets" to "আরও পোষা প্রাণী লোড হচ্ছে…",
    "photos" to "ছবি (সর্বোচ্চ ১২টি)",
    "profilePhotographerRole" to "ফটোগ্রাফার - ফটোগ্রাফি সেবা প্রদান করুন",
    "profileRescuerRole" to "উদ্ধারকারী - দত্তকের জন্য পোষা প্রাণী প্রকাশ করুন",
    "profileTemporalHomeRole" to "সাময়িক আবাস - পোষা প্রাণীর জন্য সাময়িক আবাস প্রদান করুন",
    "provideShelter" to "আশ্রয়কেন্দ্র - একটি প্রাণী আশ্রয়কেন্দ্র পরিচালনা করুন",
    "provideSterilization" to "বন্ধ্যাকরণ সেবা - বন্ধ্যাকরণ সেবা প্রদান করুন",
    "requestEmailChange" to "ইমেইল পরিবর্তনের অনুরোধ করুন",
    "rescueDate" to "উদ্ধারের তারিখ",
    "rescueLocation" to "উদ্ধারের স্থান",
    "savePassword" to "পাসওয়ার্ড সংরক্ষণ করুন",
    "selectEnergy" to "শক্তি নির্বাচন করুন",
    "selectSize" to "আকার নির্বাচন করুন",
    "sex" to "লিঙ্গ",
    "shelterSettings" to "আশ্রয়কেন্দ্র সেটিংস",
    "size" to "আকার",
    "small" to "ছোট",
    "specialNeeds" to "বিশেষ চাহিদা",
    "sterilizationSettings" to "বন্ধ্যাকরণ সেটিংস",
    "sterilized" to "বন্ধ্যাকৃত",
    "temporalHomeSettings" to "সাময়িক আবাস সেটিংস",
    "temperament" to "স্বভাব",
    "type" to "ধরন *",
    "urgentAdoption" to "জরুরি দত্তক প্রয়োজন",
    "vaccinations" to "টিকাদান",
    "vetDisclaimer" to "খাদ্যাভ্যাস পরিবর্তনের আগে আপনার পশুচিকিৎসকের পরামর্শ নিন।",
    "weight" to "ওজন (কেজি)",
    "weightLabel" to "ওজন",
    "website" to "ওয়েবসাইট",
    "securitySettings" to "নিরাপত্তা সেটিংস",
    "emailVerifiedTitle" to "ইমেইল সফলভাবে যাচাই হয়েছে!",
    "emailVerifiedMessage" to "আপনার ইমেইল যাচাই করা হয়েছে। আপনার নিবন্ধন এখন সম্পূর্ণ।",
    "redirectingIn" to "মূল পাতায় পুনর্নির্দেশিত হচ্ছে ",
    "canAlsoLogin" to "আপনি এটিও করতে পারেন ",
    "loginNow" to "এখনই লগইন করুন",
    "emailVerificationFailed" to "যাচাইকরণ ব্যর্থ হয়েছে",
    "invalidOrExpiredToken" to "যাচাইকরণ লিংকটি অবৈধ বা মেয়াদোত্তীর্ণ হয়েছে।",
    "tryRegisteringAgain" to "অনুগ্রহ করে আবার নিবন্ধন করার চেষ্টা করুন।",
    "verificationEmailResent" to "যাচাইকরণ ইমেইলের মেয়াদ শেষ হয়ে গিয়েছিল। একটি নতুন যাচাইকরণ ইমেইল পাঠানো হয়েছে। অনুগ্রহ করে আপনার ইনবক্স পরীক্ষা করুন।",
    "emailVerificationSent" to "একটি যাচাইকরণ ইমেইল পাঠানো হয়েছে। লগইন করার আগে অনুগ্রহ করে আপনার ইনবক্স পরীক্ষা করুন।",
    "emailNotVerifiedLogin" to "লগইন করার আগে অনুগ্রহ করে আপনার ইমেইল যাচাই করুন। যাচাইকরণ লিংকের জন্য আপনার ইনবক্স পরীক্ষা করুন।",
    "invalidOrExpiredMagicLink" to "অবৈধ বা মেয়াদোত্তীর্ণ ম্যাজিক লিংক। অনুগ্রহ করে একটি নতুন অনুরোধ করুন।",
    "invalidToken" to "অবৈধ টোকেন।",
    "accountBanned" to "আপনার অ্যাকাউন্ট নিষিদ্ধ করা হয়েছে।",
    "loginError" to "লগইন ত্রুটি ঘটেছে।",
    "profileUpdated" to "প্রোফাইল আপডেট হয়েছে!",
    "displayNameRequired" to "প্রদর্শন নাম খালি রাখা যাবে না",
    "saving" to "সংরক্ষণ হচ্ছে...",
    "passwordSet" to "পাসওয়ার্ড সেট করা আছে",
    "noPassword" to "কোনো পাসওয়ার্ড সেট করা নেই",
    "passwordChangeSuccess" to "পাসওয়ার্ড সফলভাবে পরিবর্তন হয়েছে!",
    "passwordChangeFailed" to "পাসওয়ার্ড পরিবর্তন করতে ব্যর্থ হয়েছে",
    "passwordSetSuccess" to "পাসওয়ার্ড সফলভাবে সেট হয়েছে!",
    "passwordSetFailed" to "পাসওয়ার্ড সেট করতে ব্যর্থ হয়েছে",
    "passwordsDoNotMatch" to "পাসওয়ার্ড মিলছে না",
    "currentPasswordRequired" to "বর্তমান পাসওয়ার্ড প্রয়োজন",
    "emailChangeSent" to "আপনার নতুন ইমেইল ঠিকানায় যাচাইকরণ ইমেইল পাঠানো হয়েছে!",
    "emailChangeFailed" to "ইমেইল পরিবর্তনের অনুরোধ ব্যর্থ হয়েছে",
    "emailRequired" to "ইমেইল প্রয়োজন",
    "sendingEllipsis" to "পাঠানো হচ্ছে...",
    "resetLinkSent" to "পাসওয়ার্ড রিসেট লিংক পাঠানো হয়েছে! আপনার ইমেইল পরীক্ষা করুন।",
    "magicLinkSent" to "লগইন লিংক পাঠানো হয়েছে! আপনার ইমেইল পরীক্ষা করুন।",
    "magicLinkSendFailed" to "লগইন লিংক পাঠাতে ব্যর্থ হয়েছে",
    "failedSendResetLink" to "রিসেট লিংক পাঠাতে ব্যর্থ হয়েছে।",
    "invalidOrMissingToken" to "অবৈধ বা অনুপস্থিত টোকেন।",
    "passwordTooShort" to "পাসওয়ার্ড অবশ্যই কমপক্ষে ৮ অক্ষরের হতে হবে।",
    "passwordNeedUppercase" to "পাসওয়ার্ডে কমপক্ষে ১টি বড় হাতের অক্ষর থাকতে হবে।",
    "passwordNeedLowercase" to "পাসওয়ার্ডে কমপক্ষে ১টি ছোট হাতের অক্ষর থাকতে হবে।",
    "passwordNeedNumber" to "পাসওয়ার্ডে কমপক্ষে ১টি সংখ্যা থাকতে হবে।",
    "passwordNeedSymbol" to "পাসওয়ার্ডে কমপক্ষে ১টি প্রতীক থাকতে হবে।",
    "resettingPassword" to "পাসওয়ার্ড রিসেট হচ্ছে...",
    "passwordResetSuccess" to "পাসওয়ার্ড সফলভাবে রিসেট হয়েছে! আপনি এখন লগইন করতে পারেন।",
    "failedResetPassword" to "পাসওয়ার্ড রিসেট করতে ব্যর্থ হয়েছে।",
    "loginSuccessRedirecting" to "লগইন সফল হয়েছে! পুনর্নির্দেশিত হচ্ছে...",
    "loginFailedExpiredLink" to "লগইন ব্যর্থ হয়েছে। লিংকটি অবৈধ বা মেয়াদোত্তীর্ণ হতে পারে।",
    "loginFailed" to "লগইন ব্যর্থ হয়েছে।",
    "emailChangedSuccess" to "ইমেইল সফলভাবে পরিবর্তন হয়েছে!",
    "failedChangeEmailExpired" to "ইমেইল পরিবর্তন করতে ব্যর্থ হয়েছে। লিংকটি অবৈধ বা মেয়াদোত্তীর্ণ হতে পারে।",
    "failedChangeEmail" to "ইমেইল পরিবর্তন করতে ব্যর্থ হয়েছে।",
    "emailVerifiedSuccess" to "ইমেইল সফলভাবে যাচাই হয়েছে!",
    "failedVerifyEmailExpired" to "ইমেইল যাচাই করতে ব্যর্থ হয়েছে। লিংকটি অবৈধ বা মেয়াদোত্তীর্ণ হতে পারে।",
    "failedVerifyEmail" to "ইমেইল যাচাই করতে ব্যর্থ হয়েছে।",
    "dropImagesHint" to "এখানে ছবি ড্রপ করুন অথবা ব্রাউজ করতে ক্লিক করুন",
    "forgotPasswordTitle" to "পাসওয়ার্ড ভুলে গেছেন",
    "forgotPasswordIntro" to "আপনার ইমেইল লিখুন এবং আমরা আপনার পাসওয়ার্ড রিসেট করার জন্য একটি লিংক পাঠাব।",
    "sendResetLink" to "রিসেট লিংক পাঠান",
    "backToLogin" to "লগইনে ফিরে যান",
    "resetPasswordTitle" to "পাসওয়ার্ড রিসেট করুন",
    "resetPasswordIntro" to "নিচে আপনার নতুন পাসওয়ার্ড লিখুন।",
    "emailLinkLoginTitle" to "ইমেইল লিংক লগইন",
    "verifying" to "যাচাই করা হচ্ছে...",
    "emailChangeTitle" to "ইমেইল পরিবর্তন",
    "verifyContactEmailTitle" to "যোগাযোগের ইমেইল যাচাই করুন",
    "status" to "অবস্থা",
    "yes" to "হ্যাঁ",
    "no" to "না",
    "noDescription" to "কোনো বিবরণ নেই।",
    "urgentBadge" to "জরুরি - শীঘ্রই বাড়ি প্রয়োজন!",
    "needsNewHome" to "এই পোষা প্রাণীটির জরুরিভাবে একটি নতুন বাড়ি প্রয়োজন",
    "promotedReasonLabel" to "কারণ",
    "selectReason" to "একটি কারণ নির্বাচন করুন",
    "promotedReasonMoving" to "মালিক স্থানান্তরিত হচ্ছেন",
    "promotedReasonComplaints" to "অভিযোগ / আর পোষা প্রাণী রাখতে পারছেন না",
    "promotedReasonPetConflict" to "অন্য একটি পোষা প্রাণীর সাথে দ্বন্দ্ব",
    "promotedReasonOther" to "অন্যান্য",
    "promotedReasonDetailLabel" to "অতিরিক্ত বিবরণ (ঐচ্ছিক)",
    "promotedReasonRequired" to "অনুগ্রহ করে একটি কারণ নির্বাচন করুন",
    "needsNewHomeBadge" to "নতুন বাড়ি প্রয়োজন",
    "housingType" to "আবাসনের ধরন",
    "preferNotToSay" to "বলতে চাই না",
    "houseHousing" to "বাড়ি",
    "apartmentHousing" to "অ্যাপার্টমেন্ট",
    "hasYard" to "উঠান আছে",
    "hasOtherPets" to "বাড়িতে অন্যান্য পোষা প্রাণী",
    "adoptionExperience" to "দত্তকের অভিজ্ঞতা",
    "firstTimeAdopter" to "প্রথমবার দত্তক গ্রহণকারী",
    "experiencedAdopter" to "অভিজ্ঞ দত্তক গ্রহণকারী",
    "noAdoptionRequests" to "কোনো দত্তক অনুরোধ নেই",
    "noMessage" to "কোনো বার্তা নেই",
    "markUnderReview" to "পর্যালোচনাধীন হিসেবে চিহ্নিত করুন",
    "approve" to "অনুমোদন করুন",
    "reject" to "প্রত্যাখ্যান করুন",
    "reviewNoteLabel" to "ব্যক্তিগত পর্যালোচনা নোট",
    "saveNote" to "নোট সংরক্ষণ করুন",
    "adoptionStatusPending" to "মুলতুবি",
    "adoptionStatusUnderReview" to "পর্যালোচনাধীন",
    "adoptionStatusApproved" to "অনুমোদিত",
    "adoptionStatusRejected" to "অনুমোদিত নয়",
    "myAdoptionRequests" to "আমার দত্তক অনুরোধ",
    "noAdoptionRequestsYet" to "আপনি এখনও কোনো পোষা প্রাণী দত্তক নেওয়ার অনুরোধ করেননি।",
    "medicalHistory" to "চিকিৎসা ইতিহাস",
    "medicalSchedule" to "টিকাদান ও কৃমিনাশক সময়সূচি",
    "recordType" to "ধরন",
    "vaccination" to "টিকাদান",
    "deworming" to "কৃমিনাশক",
    "recordName" to "নাম",
    "dateGiven" to "প্রদানের তারিখ",
    "nextDueDate" to "পরবর্তী নির্ধারিত তারিখ (ঐচ্ছিক)",
    "notesOptional" to "নোট (ঐচ্ছিক)",
    "addRecord" to "রেকর্ড যোগ করুন",
    "noMedicalRecords" to "এখনও কোনো টিকাদান বা কৃমিনাশক রেকর্ড নেই।",
    "nextDueLabel" to "পরবর্তী নির্ধারিত",
    "givenLabel" to "প্রদত্ত",
    "overdue" to "মেয়াদোত্তীর্ণ",
    "dueSoon" to "শীঘ্রই নির্ধারিত",
    "upToDate" to "হালনাগাদ",
    "recordNameRequired" to "নাম প্রয়োজন",
    "dateGivenRequired" to "প্রদানের তারিখ প্রয়োজন",
    "failedToAddRecord" to "রেকর্ড যোগ করতে ব্যর্থ হয়েছে",
    "confirmDeleteRecord" to "এই রেকর্ডটি মুছে ফেলবেন?",
    "maxCapacity" to "একসাথে সর্বোচ্চ পোষা প্রাণী (ঐচ্ছিক)",
    "placePetHere" to "এখানে একটি পোষা প্রাণী রাখুন",
    "noAvailablePetsToPlace" to "এখন রাখার জন্য আপনার কোনো উপলব্ধ পোষা প্রাণী নেই।",
    "selectPet" to "পোষা প্রাণী নির্বাচন করুন",
    "startPlacementBtn" to "স্থাপন শুরু করুন",
    "placementStarted" to "ফস্টার স্থাপন শুরু হয়েছে!",
    "petsCurrentlyWithMe" to "বর্তমানে আমার সাথে থাকা পোষা প্রাণী",
    "noPetsCurrentlyFostered" to "বর্তমানে আপনার সাথে কোনো পোষা প্রাণী স্থাপন করা নেই।",
    "sinceLabel" to "থেকে",
    "fosterPlacement" to "ফস্টার স্থাপন",
    "endPlacement" to "স্থাপন শেষ করুন",
    "currentlyWithLabel" to "বর্তমানে যার সাথে",
    "notCurrentlyFostered" to "বর্তমানে ফস্টার যত্নে নেই।",
    "confirmEndPlacement" to "এই ফস্টার স্থাপনটি শেষ করবেন? পোষা প্রাণীটি আপনার কাছে ফিরে এসেছে বলে রেকর্ড করা হবে।",
    "aTemporalHome" to "একটি সাময়িক আবাস",
    "viewsLabel" to "দর্শন",
    "inquiriesLabel" to "অনুসন্ধান",
    "conversionLabel" to "রূপান্তর",
    "messageOptional" to "বার্তা (ঐচ্ছিক)",
    "requestAdoption" to "দত্তকের অনুরোধ করুন",
    "editPet" to "পোষা প্রাণী সম্পাদনা করুন",
    "adoptionRequestSubmitted" to "দত্তক অনুরোধ জমা দেওয়া হয়েছে!",
    "failedSubmitRequest" to "অনুরোধ জমা দিতে ব্যর্থ হয়েছে",
    "noPhotosYet" to "এখনও কোনো ছবি নেই।",
    "rabbit" to "খরগোশ",
    "safeFoods" to "নিরাপদ খাবার",
    "harmfulFoods" to "ক্ষতিকর খাবার",
    "cannotEatFoods" to "খাওয়া যাবে না",
    "legendSafe" to "নিরাপদ",
    "legendHarmful" to "ক্ষতিকর",
    "legendToxic" to "বিষাক্ত",
    "food_dog_safe_chicken_name" to "মুরগি",
    "food_dog_safe_chicken_detail" to "রান্না করা, হাড়বিহীন",
    "food_dog_safe_chicken_desc" to "চর্বিহীন প্রোটিন, সহজে হজমযোগ্য",
    "food_dog_safe_rice_name" to "ভাত",
    "food_dog_safe_rice_detail" to "সাদা বা বাদামি",
    "food_dog_safe_rice_desc" to "শর্করার ভালো উৎস",
    "food_dog_safe_carrots_name" to "গাজর",
    "food_dog_safe_carrots_detail" to "কাঁচা বা রান্না করা",
    "food_dog_safe_carrots_desc" to "কম ক্যালোরি, বেশি ফাইবার",
    "food_dog_safe_apples_name" to "আপেল",
    "food_dog_safe_apples_detail" to "বীজ ছাড়া",
    "food_dog_safe_apples_desc" to "ভিটামিনে সমৃদ্ধ",
    "food_dog_safe_peanutbutter_name" to "পিনাট বাটার",
    "food_dog_safe_peanutbutter_detail" to "লবণবিহীন, জাইলিটল ছাড়া",
    "food_dog_safe_peanutbutter_desc" to "প্রোটিনের ভালো উৎস",
    "food_dog_harmful_grapes_name" to "আঙুর",
    "food_dog_harmful_grapes_detail" to "সব জাত",
    "food_dog_harmful_grapes_desc" to "কিডনি বিকল হতে পারে",
    "food_dog_harmful_onions_name" to "পেঁয়াজ",
    "food_dog_harmful_onions_detail" to "সব রূপ",
    "food_dog_harmful_onions_desc" to "লোহিত রক্তকণিকা নষ্ট করে",
    "food_dog_harmful_chocolate_name" to "চকোলেট",
    "food_dog_harmful_chocolate_detail" to "সব ধরনের",
    "food_dog_harmful_chocolate_desc" to "কুকুরের জন্য বিষাক্ত",
    "food_dog_harmful_garlic_name" to "রসুন",
    "food_dog_harmful_garlic_detail" to "সব রূপ",
    "food_dog_harmful_garlic_desc" to "রক্তস্বল্পতা সৃষ্টি করে",
    "food_dog_cannot_xylitol_name" to "জাইলিটল",
    "food_dog_cannot_xylitol_detail" to "কৃত্রিম মিষ্টিকারক",
    "food_dog_cannot_xylitol_desc" to "অত্যন্ত বিষাক্ত",
    "food_dog_cannot_macadamia_name" to "ম্যাকাডামিয়া বাদাম",
    "food_dog_cannot_macadamia_detail" to "সব রূপ",
    "food_dog_cannot_macadamia_desc" to "দুর্বলতা সৃষ্টি করে",
    "food_dog_cannot_avocado_name" to "অ্যাভোকাডো",
    "food_dog_cannot_avocado_detail" to "বীজ, খোসা, সবকিছু",
    "food_dog_cannot_avocado_desc" to "পার্সিন থাকে",
    "food_dog_cannot_alcohol_name" to "অ্যালকোহল",
    "food_dog_cannot_alcohol_detail" to "সব রূপ",
    "food_dog_cannot_alcohol_desc" to "অত্যন্ত বিষাক্ত",
    "food_cat_safe_cookedfish_name" to "রান্না করা মাছ",
    "food_cat_safe_cookedfish_detail" to "হাড় ছাড়া",
    "food_cat_safe_cookedfish_desc" to "প্রোটিনে সমৃদ্ধ",
    "food_cat_safe_chicken_name" to "মুরগি",
    "food_cat_safe_chicken_detail" to "রান্না করা, সাদামাটা",
    "food_cat_safe_chicken_desc" to "প্রোটিনের ভালো উৎস",
    "food_cat_safe_pumpkin_name" to "কুমড়া",
    "food_cat_safe_pumpkin_detail" to "সাদামাটা, রান্না করা",
    "food_cat_safe_pumpkin_desc" to "হজমে সাহায্য করে",
    "food_cat_safe_eggs_name" to "ডিম",
    "food_cat_safe_eggs_detail" to "রান্না করা",
    "food_cat_safe_eggs_desc" to "সম্পূর্ণ প্রোটিন",
    "food_cat_harmful_raweggs_name" to "কাঁচা ডিম",
    "food_cat_harmful_raweggs_detail" to "অ্যাভিডিনযুক্ত",
    "food_cat_harmful_raweggs_desc" to "বায়োটিনে বাধা দেয়",
    "food_cat_harmful_rawfish_name" to "কাঁচা মাছ",
    "food_cat_harmful_rawfish_detail" to "থায়ামিনেজ থাকে",
    "food_cat_harmful_rawfish_desc" to "বি ভিটামিন ভেঙে দেয়",
    "food_cat_harmful_dogfood_name" to "কুকুরের খাবার",
    "food_cat_harmful_dogfood_detail" to "যেকোনো",
    "food_cat_harmful_dogfood_desc" to "টরিনের অভাব",
    "food_cat_harmful_milk_name" to "দুধ",
    "food_cat_harmful_milk_detail" to "বেশিরভাগ বিড়াল",
    "food_cat_harmful_milk_desc" to "ল্যাকটোজ অসহিষ্ণুতা",
    "food_cat_cannot_chocolate_name" to "চকোলেট",
    "food_cat_cannot_chocolate_detail" to "সব ধরনের",
    "food_cat_cannot_chocolate_desc" to "থিওব্রোমিন বিষাক্ত",
    "food_cat_cannot_onionsgarlic_name" to "পেঁয়াজ/রসুন",
    "food_cat_cannot_onionsgarlic_detail" to "সব রূপ",
    "food_cat_cannot_onionsgarlic_desc" to "লোহিত রক্তকণিকা নষ্ট করে",
    "food_cat_cannot_grapesraisins_name" to "আঙুর/কিশমিশ",
    "food_cat_cannot_grapesraisins_detail" to "সব জাত",
    "food_cat_cannot_grapesraisins_desc" to "কিডনির ক্ষতি",
    "food_cat_cannot_alcohol_name" to "অ্যালকোহল",
    "food_cat_cannot_alcohol_detail" to "সব রূপ",
    "food_cat_cannot_alcohol_desc" to "অত্যন্ত বিষাক্ত",
    "food_bird_safe_seeds_name" to "বীজ",
    "food_bird_safe_seeds_detail" to "বিভিন্ন মিশ্রণ",
    "food_bird_safe_seeds_desc" to "চর্বির ভালো উৎস",
    "food_bird_safe_fruits_name" to "ফল",
    "food_bird_safe_fruits_detail" to "বেশিরভাগ জাত",
    "food_bird_safe_fruits_desc" to "ভিটামিন ও খনিজ",
    "food_bird_safe_vegetables_name" to "শাকসবজি",
    "food_bird_safe_vegetables_detail" to "সবুজ শাক",
    "food_bird_safe_vegetables_desc" to "প্রয়োজনীয় পুষ্টি",
    "food_bird_safe_pellets_name" to "পেলেট",
    "food_bird_safe_pellets_detail" to "মানসম্পন্ন প্রস্তুত",
    "food_bird_safe_pellets_desc" to "সম্পূর্ণ পুষ্টি",
    "food_bird_harmful_avocado_name" to "অ্যাভোকাডো",
    "food_bird_harmful_avocado_detail" to "সব অংশ",
    "food_bird_harmful_avocado_desc" to "পার্সিন বিষাক্ত",
    "food_bird_harmful_fruitpits_name" to "ফলের বিচি",
    "food_bird_harmful_fruitpits_detail" to "আপেল, পীচ",
    "food_bird_harmful_fruitpits_desc" to "সায়ানাইডের চিহ্ন",
    "food_bird_harmful_salt_name" to "লবণ",
    "food_bird_harmful_salt_detail" to "যেকোনো রূপ",
    "food_bird_harmful_salt_desc" to "পাখির জন্য বিষাক্ত",
    "food_bird_harmful_caffeine_name" to "ক্যাফেইন",
    "food_bird_harmful_caffeine_detail" to "কফি, চা",
    "food_bird_harmful_caffeine_desc" to "হৃদযন্ত্রের সমস্যা",
    "food_bird_cannot_chocolate_name" to "চকোলেট",
    "food_bird_cannot_chocolate_detail" to "সব ধরনের",
    "food_bird_cannot_chocolate_desc" to "থিওব্রোমিন বিষাক্ত",
    "food_bird_cannot_onions_name" to "পেঁয়াজ",
    "food_bird_cannot_onions_detail" to "সব রূপ",
    "food_bird_cannot_onions_desc" to "লোহিত রক্তকণিকা নষ্ট করে",
    "food_bird_cannot_garlic_name" to "রসুন",
    "food_bird_cannot_garlic_detail" to "সব রূপ",
    "food_bird_cannot_garlic_desc" to "বিষাক্ত",
    "food_bird_cannot_mushrooms_name" to "মাশরুম",
    "food_bird_cannot_mushrooms_detail" to "বন্য জাত",
    "food_bird_cannot_mushrooms_desc" to "সম্ভাব্য বিষাক্ত পদার্থ",
    "food_fish_safe_flakes_name" to "ফ্লেক্স",
    "food_fish_safe_flakes_detail" to "মানসম্পন্ন ব্র্যান্ড",
    "food_fish_safe_flakes_desc" to "সম্পূর্ণ মাছের খাবার",
    "food_fish_safe_pellets_name" to "পেলেট",
    "food_fish_safe_pellets_detail" to "ভাসমান/ডুবন্ত",
    "food_fish_safe_pellets_desc" to "সুষম পুষ্টি",
    "food_fish_safe_frozenfood_name" to "হিমায়িত খাবার",
    "food_fish_safe_frozenfood_detail" to "ব্লাডওয়ার্ম, ব্রাইন শ্রিম্প",
    "food_fish_safe_frozenfood_desc" to "প্রোটিনের উৎস",
    "food_fish_safe_vegetables_name" to "শাকসবজি",
    "food_fish_safe_vegetables_detail" to "ব্লাঞ্চ করা জুকিনি",
    "food_fish_safe_vegetables_desc" to "ফাইবারের উৎস",
    "food_fish_harmful_bread_name" to "রুটি",
    "food_fish_harmful_bread_detail" to "যেকোনো ধরনের",
    "food_fish_harmful_bread_desc" to "কোনো পুষ্টি নেই, পেট ভরিয়ে দেয়",
    "food_fish_harmful_humanfood_name" to "মানুষের খাবার",
    "food_fish_harmful_humanfood_detail" to "রান্না করা খাবার",
    "food_fish_harmful_humanfood_desc" to "ভুল পুষ্টি",
    "food_fish_harmful_livefeed_name" to "জীবন্ত খাদ্য",
    "food_fish_harmful_livefeed_detail" to "বন্য ধৃত পোকামাকড়",
    "food_fish_harmful_livefeed_desc" to "সম্ভাব্য পরজীবী",
    "food_fish_cannot_landinsect_name" to "স্থলজ পোকা",
    "food_fish_cannot_landinsect_detail" to "হামাগুড়ি দেওয়া পোকা",
    "food_fish_cannot_landinsect_desc" to "স্বাভাবিক খাবার নয়",
    "food_fish_cannot_mammalmeat_name" to "স্তন্যপায়ীর মাংস",
    "food_fish_cannot_mammalmeat_detail" to "মুরগি, গরুর মাংস",
    "food_fish_cannot_mammalmeat_desc" to "বেশিরভাগ মাছের জন্য অনুপযুক্ত",
    "food_fish_cannot_dairy_name" to "দুগ্ধজাত",
    "food_fish_cannot_dairy_detail" to "যেকোনো",
    "food_fish_cannot_dairy_desc" to "হজম করতে পারে না",
    "food_fish_cannot_breadcrumbs_name" to "রুটির গুঁড়া",
    "food_fish_cannot_breadcrumbs_detail" to "অনেকগুলিতে রসুন থাকে",
    "food_fish_cannot_breadcrumbs_desc" to "ক্ষতিকর সংযোজন",
    "food_rabbit_safe_hay_name" to "খড়",
    "food_rabbit_safe_hay_detail" to "টিমোথি ঘাস",
    "food_rabbit_safe_hay_desc" to "খাদ্যের ৮০%",
    "food_rabbit_safe_leafygreens_name" to "সবুজ শাক",
    "food_rabbit_safe_leafygreens_detail" to "রোমেইন, ধনেপাতা",
    "food_rabbit_safe_leafygreens_desc" to "দৈনিক ভিটামিন",
    "food_rabbit_safe_pellets_name" to "পেলেট",
    "food_rabbit_safe_pellets_detail" to "টিমোথি-ভিত্তিক",
    "food_rabbit_safe_pellets_desc" to "সুষম পুষ্টি",
    "food_rabbit_safe_carrots_name" to "গাজর",
    "food_rabbit_safe_carrots_detail" to "পরিমিতভাবে",
    "food_rabbit_safe_carrots_desc" to "খাবারের পুরস্কার",
    "food_rabbit_harmful_icebergletttuce_name" to "আইসবার্গ লেটুস",
    "food_rabbit_harmful_icebergletttuce_detail" to "কোনো পুষ্টি নেই",
    "food_rabbit_harmful_icebergletttuce_desc" to "পাচনতন্ত্র স্থবিরতা সৃষ্টি করতে পারে",
    "food_rabbit_harmful_beans_name" to "শিম",
    "food_rabbit_harmful_beans_detail" to "সব ডালজাতীয়",
    "food_rabbit_harmful_beans_desc" to "গ্যাস, পাচন সমস্যা",
    "food_rabbit_harmful_corn_name" to "ভুট্টা",
    "food_rabbit_harmful_corn_detail" to "যেকোনো রূপ",
    "food_rabbit_harmful_corn_desc" to "হজম করতে পারে না",
    "food_rabbit_harmful_potatoes_name" to "আলু",
    "food_rabbit_harmful_potatoes_detail" to "যেকোনো রূপ",
    "food_rabbit_harmful_potatoes_desc" to "ভুল পুষ্টি উপাদান",
    "food_rabbit_cannot_chocolate_name" to "চকোলেট",
    "food_rabbit_cannot_chocolate_detail" to "সব ধরনের",
    "food_rabbit_cannot_chocolate_desc" to "থিওব্রোমিন বিষাক্ত",
    "food_rabbit_cannot_onions_name" to "পেঁয়াজ",
    "food_rabbit_cannot_onions_detail" to "সব রূপ",
    "food_rabbit_cannot_onions_desc" to "অত্যন্ত বিষাক্ত",
    "food_rabbit_cannot_garlic_name" to "রসুন",
    "food_rabbit_cannot_garlic_detail" to "সব রূপ",
    "food_rabbit_cannot_garlic_desc" to "বিষাক্ত",
    "food_rabbit_cannot_avocado_name" to "অ্যাভোকাডো",
    "food_rabbit_cannot_avocado_detail" to "সব অংশ",
    "food_rabbit_cannot_avocado_desc" to "পার্সিন থাকে",
    "primary" to "প্রধান",
    "passkeyRegisteredSuccess" to "পাসকী সফলভাবে নিবন্ধিত হয়েছে!",
    "passkeyRegistrationFailed" to "পাসকী নিবন্ধন ব্যর্থ হয়েছে",
    "passkeyNameRequired" to "পাসকীর নাম প্রয়োজন",
    "registeringPasskey" to "পাসকী নিবন্ধন করা হচ্ছে...",
    "passkeyRegistered" to "পাসকী নিবন্ধিত",
    "noPasskey" to "কোনো পাসকী নিবন্ধিত নেই",
    "noPets" to "এখনও কোনো পোষা প্রাণী নেই।",
    "photographerCountryStateRequired" to "ফটোগ্রাফার সেবার জন্য দেশ ও রাজ্য পূরণ করুন",
    "temporalHomeAliasCountryCityRequired" to "সাময়িক আবাসের জন্য ডাকনাম, দেশ ও শহর পূরণ করুন",
    "shelterNameCountryCityAddressRequired" to "আশ্রয়কেন্দ্রের জন্য নাম, দেশ, শহর ও ঠিকানা পূরণ করুন",
    "sterilizationNameCountryCityAddressRequired" to "বন্ধ্যাকরণ কেন্দ্রের জন্য নাম, দেশ, শহর ও ঠিকানা পূরণ করুন",
    "country.afghanistan" to "আফগানিস্তান",
    "country.albania" to "আলবেনিয়া",
    "country.algeria" to "আলজেরিয়া",
    "country.argentina" to "আর্জেন্টিনা",
    "country.armenia" to "আর্মেনিয়া",
    "country.australia" to "অস্ট্রেলিয়া",
    "country.austria" to "অস্ট্রিয়া",
    "country.azerbaijan" to "আজারবাইজান",
    "country.bangladesh" to "বাংলাদেশ",
    "country.belarus" to "বেলারুশ",
    "country.belgium" to "বেলজিয়াম",
    "country.bolivia" to "বলিভিয়া",
    "country.bosnia" to "বসনিয়া ও হার্জেগোভিনা",
    "country.brazil" to "ব্রাজিল",
    "country.bulgaria" to "বুলগেরিয়া",
    "country.cambodia" to "কম্বোডিয়া",
    "country.cameroon" to "ক্যামেরুন",
    "country.canada" to "কানাডা",
    "country.chile" to "চিলি",
    "country.china" to "চীন",
    "country.colombia" to "কলম্বিয়া",
    "country.costaRica" to "কোস্টা রিকা",
    "country.croatia" to "ক্রোয়েশিয়া",
    "country.cuba" to "কিউবা",
    "country.czechia" to "চেক প্রজাতন্ত্র",
    "country.denmark" to "ডেনমার্ক",
    "country.dominicanRepublic" to "ডোমিনিকান প্রজাতন্ত্র",
    "country.ecuador" to "ইকুয়েডর",
    "country.egypt" to "মিশর",
    "country.elSalvador" to "এল সালভাদর",
    "country.estonia" to "এস্তোনিয়া",
    "country.ethiopia" to "ইথিওপিয়া",
    "country.finland" to "ফিনল্যান্ড",
    "country.france" to "ফ্রান্স",
    "country.georgia" to "জর্জিয়া",
    "country.germany" to "জার্মানি",
    "country.ghana" to "ঘানা",
    "country.greece" to "গ্রিস",
    "country.guatemala" to "গুয়াতেমালা",
    "country.haiti" to "হাইতি",
    "country.honduras" to "হন্ডুরাস",
    "country.hungary" to "হাঙ্গেরি",
    "country.iceland" to "আইসল্যান্ড",
    "country.india" to "ভারত",
    "country.indonesia" to "ইন্দোনেশিয়া",
    "country.iran" to "ইরান",
    "country.iraq" to "ইরাক",
    "country.ireland" to "আয়ারল্যান্ড",
    "country.israel" to "ইসরায়েল",
    "country.italy" to "ইতালি",
    "country.jamaica" to "জ্যামাইকা",
    "country.japan" to "জাপান",
    "country.jordan" to "জর্ডান",
    "country.kazakhstan" to "কাজাখস্তান",
    "country.kenya" to "কেনিয়া",
    "country.kuwait" to "কুয়েত",
    "country.latvia" to "লাটভিয়া",
    "country.lebanon" to "লেবানন",
    "country.libya" to "লিবিয়া",
    "country.lithuania" to "লিথুয়ানিয়া",
    "country.luxembourg" to "লুক্সেমবার্গ",
    "country.malaysia" to "মালয়েশিয়া",
    "country.mexico" to "মেক্সিকো",
    "country.moldova" to "মলদোভা",
    "country.mongolia" to "মঙ্গোলিয়া",
    "country.montenegro" to "মন্টিনিগ্রো",
    "country.morocco" to "মরক্কো",
    "country.myanmar" to "মিয়ানমার",
    "country.nepal" to "নেপাল",
    "country.netherlands" to "নেদারল্যান্ডস",
    "country.newZealand" to "নিউজিল্যান্ড",
    "country.nicaragua" to "নিকারাগুয়া",
    "country.nigeria" to "নাইজেরিয়া",
    "country.northKorea" to "উত্তর কোরিয়া",
    "country.norway" to "নরওয়ে",
    "country.pakistan" to "পাকিস্তান",
    "country.panama" to "পানামা",
    "country.paraguay" to "প্যারাগুয়ে",
    "country.peru" to "পেরু",
    "country.philippines" to "ফিলিপাইন",
    "country.poland" to "পোল্যান্ড",
    "country.portugal" to "পর্তুগাল",
    "country.puertoRico" to "পুয়ের্তো রিকো",
    "country.qatar" to "কাতার",
    "country.romania" to "রোমানিয়া",
    "country.russia" to "রাশিয়া",
    "country.saudiArabia" to "সৌদি আরব",
    "country.serbia" to "সার্বিয়া",
    "country.singapore" to "সিঙ্গাপুর",
    "country.slovakia" to "স্লোভাকিয়া",
    "country.slovenia" to "স্লোভেনিয়া",
    "country.southAfrica" to "দক্ষিণ আফ্রিকা",
    "country.southKorea" to "দক্ষিণ কোরিয়া",
    "country.spain" to "স্পেন",
    "country.sriLanka" to "শ্রীলঙ্কা",
    "country.sudan" to "সুদান",
    "country.sweden" to "সুইডেন",
    "country.switzerland" to "সুইজারল্যান্ড",
    "country.syria" to "সিরিয়া",
    "country.taiwan" to "তাইওয়ান",
    "country.thailand" to "থাইল্যান্ড",
    "country.tunisia" to "তিউনিসিয়া",
    "country.turkey" to "তুরস্ক",
    "country.ukraine" to "ইউক্রেন",
    "country.uae" to "সংযুক্ত আরব আমিরাত",
    "country.uk" to "যুক্তরাজ্য",
    "country.usa" to "যুক্তরাষ্ট্র",
    "country.uruguay" to "উরুগুয়ে",
    "country.uzbekistan" to "উজবেকিস্তান",
    "country.venezuela" to "ভেনেজুয়েলা",
    "country.vietnam" to "ভিয়েতনাম",
    "country.yemen" to "ইয়েমেন",
    "loading" to "লোড হচ্ছে...",
    "countryRequired" to "অনুসন্ধান করতে একটি দেশ নির্বাচন করুন",
    "noSheltersFound" to "এখনও কোনো আশ্রয়কেন্দ্র পাওয়া যায়নি। পরে আবার দেখুন!",
    "visitWebsite" to "ওয়েবসাইট দেখুন",
    "fiscalId" to "কর শনাক্তকরণ নম্বর",
    "donationInformation" to "দান সংক্রান্ত তথ্য",
    "bankName" to "ব্যাংক",
    "accountHolder" to "অ্যাকাউন্ট ধারক",
    "accountNumber" to "অ্যাকাউন্ট নম্বর",
    "currency" to "মুদ্রা",
    "errorLoadingShelters" to "আশ্রয়কেন্দ্র লোড করতে ত্রুটি হয়েছে",
    "pleaseSelectCountry" to "একটি দেশ নির্বাচন করুন",
    "actions" to "কার্যক্রম",
    "addShelter" to "আশ্রয়কেন্দ্র যোগ করুন",
    "addEditShelter" to "আশ্রয়কেন্দ্র যোগ করুন / সম্পাদনা করুন",
    "allStates" to "সব রাজ্য",
    "confirmDelete" to "আপনি কি নিশ্চিত যে এই অবস্থানটি মুছে ফেলতে চান?",
    "confirmDeleteShelter" to "আপনি কি নিশ্চিত যে এই আশ্রয়কেন্দ্রটি মুছে ফেলতে চান?",
    "confirmUnbanUser" to "এই ব্যবহারকারীর নিষেধাজ্ঞা প্রত্যাহার করবেন?",
    "confirmResetUserCredentials" to "এটি {email}-এর বর্তমান পাসওয়ার্ড ও পাসকী অবৈধ করে দেবে এবং তাদের একটি রিসেট লিংক ইমেইল করবে। চালিয়ে যাবেন?",
    "confirmDeactivateUser" to "এই ব্যবহারকারীকে নিষ্ক্রিয় করবেন? পরে তাদের পুনরায় সক্রিয় করা যাবে।",
    "confirmDeactivatePet" to "এই পোষা প্রাণীকে নিষ্ক্রিয় করবেন? এটি সর্বজনীন তালিকা থেকে লুকানো থাকবে এবং পরে পুনরায় সক্রিয় করা যাবে।",
    "confirmBlockRescuer" to "এই উদ্ধারকারীকে আপনাকে আরও অনুরোধ পাঠানো থেকে ব্লক করবেন?",
    "confirmDeletePet" to "এই পোষা প্রাণীটি মুছে ফেলবেন?",
    "confirmDeleteStorage" to "এই স্টোরেজটি মুছে ফেলবেন?",
    "contact" to "যোগাযোগ",
    "delete" to "মুছে ফেলুন",
    "edit" to "সম্পাদনা করুন",
    "errorDeletingShelter" to "আশ্রয়কেন্দ্র মুছতে ত্রুটি হয়েছে",
    "errorLoadingLocations" to "অবস্থান লোড করতে ত্রুটি হয়েছে",
    "errorLoadingShelter" to "আশ্রয়কেন্দ্র লোড করতে ত্রুটি হয়েছে",
    "errorSaving" to "সংরক্ষণ করতে ত্রুটি হয়েছে",
    "errorSavingShelter" to "আশ্রয়কেন্দ্র সংরক্ষণ করতে ত্রুটি হয়েছে",
    "location" to "অবস্থান",
    "noLocationsFound" to "কোনো অবস্থান পাওয়া যায়নি",
    "selectCountry" to "একটি দেশ নির্বাচন করুন",
    "selectCountryToFilter" to "ফিল্টার করতে একটি দেশ নির্বাচন করুন",
    "shelterAdded" to "আশ্রয়কেন্দ্র সফলভাবে যোগ করা হয়েছে",
    "shelterDeleted" to "আশ্রয়কেন্দ্র সফলভাবে মুছে ফেলা হয়েছে",
    "shelterUpdated" to "আশ্রয়কেন্দ্র সফলভাবে আপডেট করা হয়েছে",
    "updateShelter" to "আশ্রয়কেন্দ্র আপডেট করুন",
    "errorLoadingPhotographers" to "ফটোগ্রাফার লোড করতে ত্রুটি হয়েছে",
    "noPhotographersAvailable" to "কোনো ফটোগ্রাফার উপলব্ধ নেই",
    "requestPhotoSession" to "ফটো সেশনের অনুরোধ করুন",
    "selectPhotographer" to "নির্বাচন করুন",
    "photographersSelected" to "{count} জন নির্বাচিত",
    "requestSelectedPhotographers" to "নির্বাচিত ফটোগ্রাফারদের অনুরোধ করুন",
    "clearSelection" to "নির্বাচন সাফ করুন",
    "requestToMultiple" to "অনুরোধ করুন",
    "messageRequired" to "ফটোগ্রাফারদের জন্য একটি বার্তা লিখুন।",
    "requestsSentSuccessfully" to "অনুরোধ সফলভাবে পাঠানো হয়েছে",
    "message" to "বার্তা",
    "sendRequest" to "অনুরোধ পাঠান",
    "requestSentSuccessfully" to "অনুরোধ সফলভাবে পাঠানো হয়েছে!",
    "requestTo" to "অনুরোধ প্রাপক",
    "enterMessage" to "{name}-এর জন্য একটি বার্তা লিখুন...",
    "reportUrgent" to "জরুরি",
    "topRescuers" to "শীর্ষ উদ্ধারকারী",
    "urgentRescuerSettings" to "জরুরি উদ্ধারকারী সেটিংস",
    "urgentRescueAlerts" to "জরুরি উদ্ধার সতর্কতা",
    "reportUrgentTitle" to "বিপদে থাকা পোষা প্রাণী রিপোর্ট করুন",
    "reportUrgentIntro" to "আপনি যদি আহত, নির্যাতিত, ক্ষুধার্ত, বা পরিত্যক্ত কোনো পোষা প্রাণী খুঁজে পান যার তাৎক্ষণিক সাহায্য প্রয়োজন, আমাদের বলুন কোথায় এবং কী সমস্যা - আমরা সাথে সাথে কাছাকাছি জরুরি উদ্ধারকারীদের সতর্ক করব।",
    "dangerType" to "কী সমস্যা?",
    "dangerInjured" to "আহত",
    "dangerAbused" to "নির্যাতিত / দুর্ব্যবহারের শিকার",
    "dangerStarving" to "ক্ষুধার্ত",
    "dangerTooYoung" to "একা বেঁচে থাকার জন্য খুব ছোট",
    "dangerOther" to "অন্যান্য জরুরি অবস্থা",
    "useMyLocation" to "আমার বর্তমান অবস্থান ব্যবহার করুন",
    "confirmMyLocation" to "আমার বর্তমান অবস্থান নিশ্চিত করুন",
    "locationPermissionHint" to "আপনার ব্রাউজার অবস্থান শেয়ার করার অনুমতি চাইবে; এটি শুধুমাত্র আপনার কাছাকাছি রিপোর্ট জানাতে ব্যবহৃত হয়।",
    "phoneOptional" to "আপনার ফোন (ঐচ্ছিক)",
    "submitReport" to "রিপোর্ট জমা দিন",
    "urgentRescuerSettingsTitle" to "জরুরি উদ্ধারকারী সেটিংস",
    "urgentRescuerSettingsIntro" to "আপনার কভারেজ এলাকার কাছে বিপদে থাকা পোষা প্রাণীর রিপোর্ট হলে আপনাকে জানানো হবে। প্রথমে যিনি গ্রহণ করবেন তিনিই কেসটি পাবেন।",
    "urgentRescuerActive" to "আমি জরুরি রিপোর্টে সাড়া দিতে উপলব্ধ",
    "coverageAreaMode" to "কভারেজ এলাকা",
    "coverageArea" to "কভারেজ এলাকা",
    "coverageAreaHint" to "আপনার বর্তমান অবস্থান নিশ্চিত করুন, পিনটি টেনে আনুন, অথবা নিচে একটি ঠিকানা টাইপ করুন - মানচিত্র এবং ফিল্ডগুলি সিঙ্ক থাকবে।",
    "useMyLocationAndRadius" to "আমার অবস্থান + একটি ব্যাসার্ধ ব্যবহার করুন",
    "pickAZone" to "পরিবর্তে একটি দেশ/রাজ্য/শহর নির্বাচন করুন",
    "radiusKm" to "ব্যাসার্ধ (কিমি)",
    "urgentRescueAlertsTitle" to "জরুরি উদ্ধার সতর্কতা",
    "urgentRescuerLeaderboardTitle" to "শীর্ষ জরুরি উদ্ধারকারী (গত ১২ মাস)",
    "locating" to "অবস্থান নির্ণয় করা হচ্ছে...",
    "geolocationUnsupported" to "আপনার ব্রাউজার জিওলোকেশন সমর্থন করে না",
    "locationCaptured" to "অবস্থান নেওয়া হয়েছে",
    "locationDenied" to "আপনার অবস্থান পাওয়া যায়নি - অনুগ্রহ করে অবস্থান অ্যাক্সেসের অনুমতি দিন অথবা পরিবর্তে একটি শহর লিখুন",
    "street" to "রাস্তা",
    "exteriorNumber" to "বাড়ির নম্বর",
    "referenceNotes" to "রেফারেন্স নোট (যেমন বাড়ির রঙ, কাছাকাছি ল্যান্ডমার্ক)",
    "concreteAddressHint" to "সম্ভব হলে একটি রাস্তা ও নম্বর যোগ করুন - এটিই উদ্ধারকারীকে ট্যাক্সি নিতে বা সরাসরি সেই স্থানে ম্যাপ অ্যাপ খুলতে সাহায্য করে।",
    "descriptionRequired" to "অনুগ্রহ করে পরিস্থিতি বর্ণনা করুন",
    "locationRequired" to "অনুগ্রহ করে একটি অবস্থান প্রদান করুন (আপনার বর্তমান অবস্থান ব্যবহার করুন অথবা একটি দেশ ও শহর লিখুন)",
    "submittingEllipsis" to "জমা দেওয়া হচ্ছে...",
    "reportSubmitted" to "রিপোর্ট জমা দেওয়া হয়েছে। ধন্যবাদ - কাছাকাছি উদ্ধারকারীদের এখন সতর্ক করা হচ্ছে।",
    "reportSubmitFailed" to "রিপোর্ট জমা দিতে ব্যর্থ হয়েছে",
    "phoneRequired" to "অনুগ্রহ করে একটি ফোন নম্বর লিখুন",
    "savingEllipsis" to "সংরক্ষণ হচ্ছে...",
    "saved" to "সংরক্ষিত",
    "saveFailed" to "সংরক্ষণ করতে ব্যর্থ হয়েছে",
    "noUrgentPages" to "এখন কোনো মুলতুবি জরুরি সতর্কতা নেই",
    "accept" to "গ্রহণ করুন",
    "alreadyAccepted" to "অন্য কেউ ইতিমধ্যে এই রিপোর্টটি গ্রহণ করেছেন",
    "noLeaderboardData" to "গত ১২ মাসে এখনও কোনো গৃহীত উদ্ধার নেই",
    "rescueAcceptedTitle" to "উদ্ধার গৃহীত হয়েছে",
    "rescueAcceptedMessage" to "ধন্যবাদ - আপনি দায়িত্ব নিয়েছেন। যত তাড়াতাড়ি সম্ভব রিপোর্ট করা স্থানে যান।",
    "rescueAcceptFailedTitle" to "গ্রহণ করা যায়নি",
    "rescueAcceptFailedMessage" to "এই রিপোর্টটি ইতিমধ্যে অন্য একজন উদ্ধারকারী গ্রহণ করেছেন, অথবা লিংকটি অবৈধ বা মেয়াদোত্তীর্ণ।",
    "share" to "শেয়ার করুন",
    "shareViaFacebook" to "Facebook-এ শেয়ার করুন",
    "shareViaX" to "X-এ শেয়ার করুন",
    "shareViaWhatsapp" to "WhatsApp-এ শেয়ার করুন",
    "shareViaEmail" to "ইমেইলের মাধ্যমে শেয়ার করুন",
    "copyLink" to "লিংক কপি করুন",
    "linkCopied" to "লিংক কপি হয়েছে!",
    "reportLostFoundTitle" to "হারানো বা পাওয়া পোষা প্রাণী রিপোর্ট করুন",
    "reportLostFoundIntro" to "আপনার পোষা প্রাণী হারিয়েছে, নাকি একটি বেওয়ারিশ প্রাণী পেয়েছেন? একটি রিপোর্ট দাখিল করুন এবং আমরা কাছাকাছি অন্যান্য রিপোর্টের সাথে তা মিলিয়ে দেখব।",
    "browseLostFound" to "বিদ্যমান হারানো ও পাওয়া রিপোর্ট ব্রাউজ করুন",
    "kindLost" to "আমি আমার পোষা প্রাণী হারিয়েছি",
    "kindFound" to "আমি একটি পোষা প্রাণী পেয়েছি",
    "petType" to "পোষা প্রাণীর ধরন",
    "petTypeUnknown" to "নিশ্চিত নই",
    "lostFoundBrowseTitle" to "হারানো ও পাওয়া পোষা প্রাণী",
    "showing" to "দেখানো হচ্ছে",
    "kindLostPlural" to "হারানো পোষা প্রাণী",
    "kindFoundPlural" to "পাওয়া পোষা প্রাণী",
    "reportResolvedTitle" to "রিপোর্ট সমাধান হয়েছে",
    "reportResolvedMessage" to "আপডেটের জন্য ধন্যবাদ - এই রিপোর্টটি এখন বন্ধ করা হয়েছে।",
    "reportResolveFailedTitle" to "সমাধান করা যায়নি",
    "reportResolveFailedMessage" to "এই রিপোর্টটি ইতিমধ্যে সমাধান করা হয়েছে, অথবা লিংকটি অবৈধ বা মেয়াদোত্তীর্ণ।",
    "reportLostFound" to "হারানো/\u200Bপাওয়া",
    "noLostFoundReportsFound" to "কোনো হারানো ও পাওয়া রিপোর্ট পাওয়া যায়নি",
    "errorLoadingReports" to "রিপোর্ট লোড করতে ত্রুটি হয়েছে",
    "yourEmail" to "আপনার ইমেইল",
    "contactReporter" to "রিপোর্টকারীর সাথে যোগাযোগ করুন",
    "contactFormIncomplete" to "অনুগ্রহ করে আপনার ইমেইল ও একটি বার্তা লিখুন",
    "contactSent" to "বার্তা পাঠানো হয়েছে",
    "contactFailed" to "বার্তা পাঠাতে ব্যর্থ হয়েছে",
    "lostFoundReportSubmitted" to "রিপোর্ট জমা দেওয়া হয়েছে। ধন্যবাদ - আমরা কাছাকাছি অন্যান্য রিপোর্টের সাথে তা মিলিয়ে দেখব।",
    "video" to "ভিডিও (ঐচ্ছিক)",
    "removeVideo" to "ভিডিও সরান",
    "saveThisSearch" to "এই অনুসন্ধান সংরক্ষণ করুন",
    "savedSearchSaved" to "অনুসন্ধান সংরক্ষিত হয়েছে! নতুন মিল তালিকাভুক্ত হলে আমরা আপনাকে ইমেইল করব।",
    "savedSearchFailed" to "অনুসন্ধান সংরক্ষণ করতে ব্যর্থ হয়েছে",
    "favorite" to "প্রিয়",
    "addToFavorites" to "প্রিয়তে যোগ করুন",
    "removeFromFavorites" to "প্রিয় থেকে সরান",
    "myFavorites" to "আমার প্রিয়",
    "noFavoritesYet" to "এখনও কোনো প্রিয় নেই",
    "mySavedSearches" to "আমার সংরক্ষিত অনুসন্ধান",
    "noSavedSearchesYet" to "এখনও কোনো সংরক্ষিত অনুসন্ধান নেই",
    "donationPromptText" to "আজ একটি পোষা প্রাণীকে সাহায্য করার জন্য ধন্যবাদ। যদি পারেন, Adopt-U সবার জন্য বিনামূল্যে রাখতে একটি ছোট দান বিবেচনা করুন।",
    "donationPromptCta" to "দান করুন",
    "dismiss" to "খারিজ করুন",
)

private val marathiTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "लॉगिन",
    "register" to "नोंदणी करा",
    "logout" to "सत्र बंद करा",
    "profile" to "प्रोफाइल",
    "myPets" to "माझे पाळीव प्राणी",
    "addPet" to "पाळीव प्राणी जोडा",
    "save" to "जतन करा",
    "cancel" to "रद्द करा",
    "search" to "शोधा",
    "shelters" to "आश्रयस्थाने",
    "photographers" to "छायाचित्रकार",
    "temporalHomes" to "तात्पुरती घरे",
    "findTemporalHomes" to "तात्पुरती घरे शोधा",
    "sterilizationLocations" to "नसबंदी केंद्रे",
    "donate" to "देणगी द्या",
    "countryLabel" to "देश",
    "selectCountryToSearch" to "एक देश निवडा",
    "selectCountryFirst" to "हे फिल्टर सक्षम करण्यासाठी देश निवडा",
    "state" to "राज्य",
    "city" to "शहर",
    "zipCode" to "पिन कोड",
    "neighborhood" to "परिसर",
    "name" to "नाव",
    "email" to "ईमेल",
    "phone" to "फोन",
    "address" to "पत्ता",
    "description" to "वर्णन",
    "animalShelters" to "प्राणी आश्रयस्थाने",
    "sheltersDescription" to "दत्तक घेण्यासाठी पाळीव प्राणी मिळू शकतील किंवा आश्रयस्थानाशी पूर्वसंमतीने त्यांना सोडता येईल अशी प्राणी आश्रयस्थाने शोधा",
    "photographerDescription" to "पाळीव प्राण्यांच्या फोटो सत्रांची सेवा देणारे व्यावसायिक छायाचित्रकार",
    "sterilizationLocationsDescription" to "प्राण्यांची नसबंदी करून घेता येईल अशी ठिकाणे शोधा.",
    "privacyPolicy" to "गोपनीयता धोरण",
    "privacyLastUpdated" to "शेवटचे अद्यतन: एप्रिल २०२५",
    "privacyHighlight" to "Adopt-U मध्ये, आम्ही किमान माहिती संकलनावर विश्वास ठेवतो. दत्तक घेणारे आणि बचावकर्ते यांच्यातील संवाद शक्य करण्यासाठी आम्ही फक्त तुमचा ईमेल पत्ता संकलित करतो. आम्ही तुमच्या माहितीचा वापर जाहिरातीसाठी करत नाही आणि विपणनाच्या उद्देशाने तुमचा डेटा तृतीय पक्षांसोबत कधीही सामायिक करणार नाही.",
    "privacyInfoCollectedH2" to "आम्ही कोणती माहिती संकलित करतो",
    "privacyAccountRegH3" to "खाते नोंदणी",
    "privacyAccountRegIntro" to "Adopt-U वर खाते तयार करण्यासाठी, आम्ही खालील माहिती संकलित करतो:",
    "privacyEmailLabel" to "ईमेल पत्ता: ",
    "privacyEmailDesc" to "खाते पडताळणी, पासवर्ड पुनर्प्राप्ती आणि दत्तक घेणारे व बचावकर्ते यांच्यातील संवादासाठी आवश्यक",
    "privacyDisplayNameLabel" to "प्रदर्शित नाव: ",
    "privacyDisplayNameDesc" to "प्लॅटफॉर्मवर तुम्ही ज्या नावाने ओळखले जाऊ इच्छिता ते नाव",
    "privacyLangPrefLabel" to "भाषा प्राधान्य: ",
    "privacyLangPrefDesc" to "प्लॅटफॉर्म तुमच्या पसंतीच्या भाषेत उपलब्ध करून देण्यासाठी",
    "privacyNoFullNameEtc" to "वर नमूद केलेल्या माहितीव्यतिरिक्त आम्ही तुमचे पूर्ण नाव, फोन नंबर, भौतिक पत्ता, ओळखपत्रे, देयक माहिती किंवा इतर कोणताही वैयक्तिक डेटा संकलित करत नाही.",
    "privacyOptionalProfileH3" to "ऐच्छिक प्रोफाइल माहिती",
    "privacyOptionalProfileIntro" to "तुमच्या भूमिकेनुसार, तुम्ही ऐच्छिकपणे खालील माहिती देऊ शकता:",
    "privacyAdopterLabel" to "दत्तक घेणारा म्हणून: ",
    "privacyAdopterDesc" to "कोणतीही अतिरिक्त माहिती आवश्यक किंवा संकलित केली जात नाही",
    "privacyRescuerLabel" to "बचावकर्ता म्हणून: ",
    "privacyRescuerDesc" to "दत्तक घेण्यासाठी उपलब्ध असलेल्या पाळीव प्राण्यांच्या स्थानाची माहिती",
    "privacyPhotographerLabel" to "छायाचित्रकार म्हणून: ",
    "privacyPhotographerDesc" to "सेवा स्थान आणि संपर्क प्राधान्ये",
    "privacyTemporalHomeLabel" to "तात्पुरते घर म्हणून: ",
    "privacyTemporalHomeDesc" to "तुम्ही तात्पुरता निवारा देऊ शकता ते स्थान",
    "privacyLocationUsage" to "सर्व स्थान माहितीचा वापर फक्त दत्तक घेणाऱ्यांना जवळपासच्या बचावकर्त्यांशी आणि सेवा पुरवठादारांशी जोडण्यासाठी केला जातो.",
    "privacyPetInfoH3" to "पाळीव प्राण्याची माहिती",
    "privacyPetInfoIntro" to "बचावकर्ते पाळीव प्राण्यांबद्दल माहिती पोस्ट करू शकतात, यामध्ये समाविष्ट आहे:",
    "privacyPetPhotos" to "पाळीव प्राण्याचे फोटो",
    "privacyPetDescBreedAge" to "वर्णन, जात, वय आणि वैशिष्ट्ये",
    "privacyPetHealth" to "आरोग्य आणि लसीकरण स्थिती",
    "privacyPetLocation" to "पाळीव प्राणी उपलब्ध असलेले स्थान",
    "privacyPetInfoNotVerified" to "ही माहिती बचावकर्त्यांनी दिलेली आहे आणि Adopt-U द्वारे स्वतंत्रपणे पडताळलेली नाही.",
    "privacyHowWeUseH2" to "आम्ही तुमची माहिती कशी वापरतो",
    "privacyPrimaryPurposeH3" to "मुख्य उद्देश: दत्तक जोडण्या सुलभ करणे",
    "privacyEmailUsedFor" to "तुमचा ईमेल पत्ता केवळ खालील उद्देशांसाठी वापरला जातो:",
    "privacyUseVerification" to "तुम्ही खाते तयार करता तेव्हा तुम्हाला पडताळणी दुवा पाठवणे",
    "privacyUseRescuerContact" to "तुम्हाला स्वारस्य असलेल्या पाळीव प्राण्यांबद्दल बचावकर्त्यांना तुमच्याशी संपर्क साधण्याची परवानगी देणे",
    "privacyUseContactRescuer" to "बचावकर्त्यांच्या सूचीबद्ध पाळीव प्राण्यांबद्दल तुम्हाला त्यांच्याशी संपर्क साधण्याची परवानगी देणे",
    "privacyUseNotifications" to "तुम्हाला महत्त्वाच्या खाते आणि सुरक्षा सूचना पाठवणे",
    "privacyOnceConnected" to "आमच्या प्लॅटफॉर्मद्वारे तुम्ही दुसऱ्या वापरकर्त्याशी जोडले गेल्यानंतर, पुढील सर्व संवाद तुमच्या आणि त्या पक्षामध्ये थेट होतो. आम्ही तुमच्या संवादाच्या सामग्रीचे निरीक्षण, साठवण किंवा त्यामध्ये प्रवेश करत नाही.",
    "privacyNeverDoH3" to "आम्ही कधीही काय करणार नाही",
    "privacyCommitIntro" to "आम्ही खालील गोष्टींसाठी गांभीर्याने वचनबद्ध आहोत:",
    "privacyNoAdsLabel" to "कोणतीही जाहिरात नाही: ",
    "privacyNoAdsDesc" to "आम्ही तुमच्या क्रियाकलापावर आधारित लक्ष्यित किंवा गैर-लक्ष्यित जाहिराती कधीही दाखवणार नाही",
    "privacyNoMarketingLabel" to "कोणतेही विपणन नाही: ",
    "privacyNoMarketingDesc" to "विपणन किंवा प्रचारात्मक उद्देशांसाठी आम्ही तुमचा ईमेल तृतीय पक्षांसोबत कधीही सामायिक करणार नाही",
    "privacyNoSellingLabel" to "डेटा विक्री नाही: ",
    "privacyNoSellingDesc" to "आम्ही तुमची वैयक्तिक माहिती कोणत्याही तृतीय पक्षाला कधीही विकणार नाही, भाड्याने देणार नाही किंवा हस्तांतरित करणार नाही",
    "privacyNoProfilingLabel" to "कोणतेही प्रोफाइलिंग नाही: ",
    "privacyNoProfilingDesc" to "मूलभूत खाते व्यवस्थापनाशिवाय इतर कोणत्याही उद्देशासाठी प्रोफाइल तयार करण्यासाठी आम्ही तुमचा डेटा वापरणार नाही",
    "privacyNoTrackingLabel" to "कोणतेही ट्रॅकिंग नाही: ",
    "privacyNoTrackingDesc" to "आम्ही ट्रॅकिंग पिक्सेल, जाहिरातींसाठी कुकीज किंवा तत्सम ट्रॅकिंग तंत्रज्ञान वापरत नाही",
    "privacyDataStorageH2" to "डेटा साठवण आणि सुरक्षा",
    "privacyDataStoredSecurely" to "तुमचा डेटा उद्योग-मानक एन्क्रिप्शन वापरून सुरक्षितपणे साठवला जातो. अनधिकृत प्रवेश, बदल, प्रकटीकरण किंवा नाशापासून तुमच्या वैयक्तिक माहितीचे संरक्षण करण्यासाठी आम्ही योग्य तांत्रिक आणि संघटनात्मक उपाययोजना करतो.",
    "privacyDataRetention" to "तुमचे खाते सक्रिय असेपर्यंत आम्ही तुमची खाते माहिती जतन करतो. तुम्ही कधीही आमच्याशी संपर्क साधून तुमचे खाते आणि संबंधित डेटा हटवण्याची विनंती करू शकता.",
    "privacyYourRightsH2" to "तुमचे हक्क",
    "privacyRightsIntro" to "तुमच्या वैयक्तिक डेटाबाबत तुम्हाला खालील हक्क आहेत:",
    "privacyRightAccess" to "प्रवेश: आम्ही तुमच्याबद्दल ठेवलेल्या सर्व वैयक्तिक डेटाची प्रत तुम्ही मागू शकता",
    "privacyRightCorrection" to "दुरुस्ती: कोणतीही चुकीची माहिती आम्ही दुरुस्त करावी अशी विनंती तुम्ही करू शकता",
    "privacyRightDeletion" to "हटवणे: तुमचे खाते आणि संबंधित सर्व डेटा हटवण्याची विनंती तुम्ही करू शकता",
    "privacyRightPortability" to "पोर्टेबिलिटी: तुमचा डेटा सामान्यतः वापरल्या जाणाऱ्या स्वरूपात द्यावा अशी विनंती तुम्ही करू शकता",
    "privacyExerciseRights" to "यापैकी कोणताही हक्क वापरण्यासाठी, कृपया आमच्याशी adopt-u@adopt-u.org वर संपर्क साधा.",
    "privacyCookiesH2" to "कुकीज",
    "privacyCookiesText" to "Adopt-U सत्र व्यवस्थापन आणि भाषा प्राधान्यांसह प्लॅटफॉर्मच्या कार्यक्षमतेसाठी आवश्यक असलेल्या किमान कुकीज वापरते. आम्ही जाहिरात कुकीज किंवा ट्रॅकिंग कुकीज वापरत नाही.",
    "privacyThirdPartyH2" to "तृतीय-पक्ष सेवा",
    "privacyThirdPartyIntro" to "आम्ही खालील तृतीय-पक्ष सेवा वापरतो, ज्या तुमचा डेटा प्रक्रिया करू शकतात:",
    "privacyAws" to "AWS (Amazon Web Services): डेटा साठवण आणि ईमेल वितरणासाठी. तुमच्या डेटाच्या प्रक्रियेसाठी त्यांचे गोपनीयता धोरण लागू होते.",
    "privacyWebauthn" to "WebAuthn/FIDO2: सुरक्षित पासकी प्रमाणीकरणासाठी. यात तुमच्या ब्राउझरमध्ये क्रिप्टोग्राफिक प्रक्रिया समाविष्ट असतात.",
    "privacyThirdPartySelected" to "आम्ही डेटा गोपनीयतेप्रती आमची बांधिलकी सामायिक करणारे सेवा पुरवठादार काळजीपूर्वक निवडले आहेत.",
    "privacyChildrensH2" to "मुलांची गोपनीयता",
    "privacyChildrensText" to "Adopt-U १८ वर्षांखालील वापरकर्त्यांसाठी नाही. आम्ही जाणूनबुजून अल्पवयीन मुलांकडून माहिती संकलित करत नाही. एखाद्या अल्पवयीन व्यक्तीने खाते तयार केले आहे असे तुम्हाला वाटत असल्यास, ते काढून टाकण्यासाठी कृपया आमच्याशी संपर्क साधा.",
    "privacyChangesH2" to "या धोरणातील बदल",
    "privacyChangesText" to "आमच्या पद्धती किंवा कायदेशीर आवश्यकतांमधील बदल प्रतिबिंबित करण्यासाठी आम्ही वेळोवेळी हे गोपनीयता धोरण अद्यतनित करू शकतो. महत्त्वाच्या बदलांबद्दल आम्ही वापरकर्त्यांना ईमेलद्वारे किंवा प्लॅटफॉर्मवरील ठळक सूचनेद्वारे कळवू.",
    "privacyContactH2" to "आमच्याशी संपर्क साधा",
    "privacyContactText" to "या गोपनीयता धोरणाबद्दल तुम्हाला काही प्रश्न किंवा चिंता असल्यास, किंवा तुमचे डेटा हक्क वापरण्यासाठी, कृपया आमच्याशी adopt-u@adopt-u.org वर संपर्क साधा.",
    "termsConditions" to "अटी आणि शर्ती",
    "installApp" to "अ‍ॅप इंस्टॉल करा",
    "termsAcceptanceH2" to "अटींची स्वीकृती",
    "termsAcceptanceText" to "Adopt-U मध्ये प्रवेश करून आणि वापरून, तुम्ही या कराराच्या अटी व तरतुदींनी बांधील राहण्यास स्वीकृती देता आणि सहमती दर्शवता. तुम्ही या अटींशी सहमत नसल्यास, कृपया आमचे प्लॅटफॉर्म वापरू नका.",
    "termsNatureH2" to "आमच्या सेवेचे स्वरूप",
    "termsNatureText1" to "Adopt-U हे एक जोडणी प्लॅटफॉर्म आहे जे पाळीव प्राणी बचावकर्ते आणि संभाव्य दत्तक घेणारे यांच्यातील संपर्क सुलभ करते. आम्ही पाळीव प्राण्यांचे दुकान, दलाल, आश्रयस्थान किंवा दत्तक संस्था नाही. आमच्या प्लॅटफॉर्मवर सूचीबद्ध केलेल्या कोणत्याही पाळीव प्राण्यांची मालकी, ताबा किंवा मध्यस्थ हाताळणी आमच्याकडे नसते.",
    "termsNatureText2" to "आमची भूमिका केवळ पक्षांमध्ये संवाद माध्यम पुरवण्यापुरती मर्यादित आहे. आम्ही दत्तक वाटाघाटी, करार किंवा प्राण्यांच्या भौतिक हस्तांतरणात सहभागी होत नाही.",
    "termsUserRespH2" to "वापरकर्त्याच्या जबाबदाऱ्या",
    "termsForRescuersH3" to "बचावकर्त्यांसाठी:",
    "termsRescuerResp1" to "तुमच्या काळजीतील पाळीव प्राण्यांबद्दल अचूक, सत्य माहिती द्या",
    "termsRescuerResp2" to "सूचीबद्ध करण्यापूर्वी पाळीव प्राणी निरोगी, लसीकरण केलेले आणि योग्यरित्या तपासलेले असल्याची खात्री करा",
    "termsRescuerResp3" to "संभाव्य दत्तक घेणाऱ्यांची जबाबदार पडताळणी करा",
    "termsRescuerResp4" to "पाळीव प्राण्यांचे त्यांच्या नवीन घरी सुरक्षित हस्तांतरण व्यवस्थापित करा",
    "termsRescuerResp5" to "दिलेल्या सर्व माहितीच्या अचूकतेची जबाबदारी स्वीकारा",
    "termsRescuerResp6" to "पाळीव प्राणी दत्तक घेण्याबाबत सर्व स्थानिक कायदे आणि नियमांचे पालन करा",
    "termsRescuerResp7" to "व्यावसायिक पाळीव प्राणी विक्री किंवा प्रजननाच्या उद्देशांसाठी प्लॅटफॉर्मचा कधीही वापर करू नका",
    "termsForAdoptersH3" to "दत्तक घेणाऱ्यांसाठी:",
    "termsAdopterResp1" to "तुमची राहण्याची परिस्थिती आणि पाळीव प्राण्यांबद्दलचा अनुभव याबद्दल अचूक माहिती द्या",
    "termsAdopterResp2" to "योग्य काळजी देण्याची तुमची क्षमता दाखवण्यास तयार रहा",
    "termsAdopterResp3" to "पाळीव प्राण्याचे दत्तक रद्द केल्याने प्राण्याला मोठा त्रास होतो हे समजून घ्या",
    "termsAdopterResp4" to "तुमची दत्तक विनंती नाकारण्याचा बचावकर्त्याचा हक्क मान्य करा",
    "termsPetListingsH2" to "पाळीव प्राण्यांच्या याद्या आणि माहिती",
    "termsPetListingsText1" to "पाळीव प्राण्यांच्या याद्यांमध्ये दिलेल्या माहितीची अचूकता Adopt-U पडताळत नाही, समर्थन देत नाही किंवा त्याची हमी देत नाही. जात, वय, आरोग्य स्थिती, स्वभाव आणि लसीकरण नोंदींसह परंतु त्यापुरती मर्यादित नसलेली माहिती बचावकर्त्यांनी दिलेली आहे आणि ती स्वतंत्रपणे पडताळलेली नाही.",
    "termsPetListingsText2" to "फोटो आणि वर्णने बचावकर्त्यांनी दिलेली आहेत. आम्ही जाणूनबुजून चुकीचे सादरीकरण करण्यास मनाई करत असलो तरी, सर्व याद्या प्राण्याच्या सध्याच्या स्थितीचे अचूक प्रतिनिधित्व करतात याची आम्ही हमी देऊ शकत नाही.",
    "termsPetListingsText3" to "कोणतीही दत्तक व्यवस्था अंतिम करण्यापूर्वी वापरकर्त्यांना प्रश्न विचारण्यास, पशुवैद्यकीय नोंदी मागण्यास आणि पाळीव प्राण्यांना भेटण्यास प्रोत्साहित केले जाते.",
    "termsAdoptionProcessH2" to "दत्तक प्रक्रिया",
    "termsAdoptionProcessIntro" to "एकदा बचावकर्ता आणि दत्तक घेणारा दत्तक प्रक्रियेसह पुढे जाण्यास सहमत झाल्यावर:",
    "termsAdoptionStep1" to "हस्तांतरण तारीख, स्थान आणि कोणताही संबंधित खर्च यासह सर्व व्यवस्था पक्षांमध्ये थेट वाटाघाटी केल्या जातात",
    "termsAdoptionStep2" to "Adopt-U कोणतेही देयक व्यवहार सुलभ करत नाही किंवा त्याची हमी देत नाही",
    "termsAdoptionStep3" to "प्लॅटफॉर्म दत्तक करार पुरवत नाही; कोणतीही करारात्मक व्यवस्था संबंधित पक्षांची एकमेव जबाबदारी आहे",
    "termsAdoptionStep4" to "सर्व करार लेखी स्वरूपात नोंदवण्याची आम्ही जोरदार शिफारस करतो",
    "termsLiabilityH2" to "दायित्वाची मर्यादा",
    "termsLiabilityIntro" to "Adopt-U खालील गोष्टींसाठी जबाबदार धरले जाणार नाही:",
    "termsLiability1" to "बचावकर्ते आणि दत्तक घेणारे यांच्यातील कोणतेही वाद, मतभेद किंवा संघर्ष",
    "termsLiability2" to "पाळीव प्राण्यांच्या याद्यांमध्ये दिलेल्या माहितीची अचूकता",
    "termsLiability3" to "दत्तक घेण्यापूर्वी, दरम्यान किंवा नंतर कोणत्याही प्राण्याचे आरोग्य, वर्तन किंवा स्थिती",
    "termsLiability4" to "प्लॅटफॉर्मच्या वापरामुळे उद्भवणारे कोणतेही आर्थिक नुकसान, हानी किंवा दुखापती",
    "termsLiability5" to "परतावा, आरोग्य समस्या किंवा सुसंगतता समस्यांसह परंतु त्यापुरते मर्यादित नसलेल्या कोणत्याही दत्तक प्रक्रियेचा परिणाम",
    "termsLiability6" to "संपर्क प्रस्थापित झाल्यानंतर प्लॅटफॉर्मबाहेर वापरकर्त्यांनी केलेल्या कोणत्याही कृती",
    "termsLiabilityAck" to "पाळीव प्राणी दत्तक घेणे ही एक महत्त्वाची वचनबद्धता आहे आणि दत्तक घेण्याच्या त्यांच्या निर्णयाची संपूर्ण जबाबदारी ते स्वीकारतात हे वापरकर्ते मान्य करतात.",
    "termsProhibitedH2" to "प्रतिबंधित क्रियाकलाप",
    "termsProhibitedIntro" to "Adopt-U वर खालील क्रियाकलाप कठोरपणे प्रतिबंधित आहेत:",
    "termsProhibited1" to "व्यावसायिक पाळीव प्राणी विक्री, प्रजनन कार्ये किंवा पिल्ले उत्पादन कारखाने",
    "termsProhibited2" to "तुम्ही थेट काळजी घेत नसलेल्या पाळीव प्राण्यांची यादी करणे",
    "termsProhibited3" to "याद्या किंवा प्रोफाइलमध्ये खोटी किंवा दिशाभूल करणारी माहिती",
    "termsProhibited4" to "इतर वापरकर्त्यांशी छळ, भेदभाव किंवा अनुचित संपर्क",
    "termsProhibited5" to "पाळीव प्राणी दत्तक घेण्याशी संबंधित नसलेल्या उद्देशांसाठी वैयक्तिक माहिती संकलित करण्यासाठी प्लॅटफॉर्मचा वापर करणे",
    "termsProhibited6" to "कोणतीही बेकायदेशीर कृती किंवा प्राणी कल्याण कायद्यांचे उल्लंघन",
    "termsAccountTerminationH2" to "खाते समाप्ती",
    "termsAccountTerminationText" to "या अटींचे उल्लंघन करणारी, प्रतिबंधित क्रियाकलापांमध्ये सहभागी होणारी किंवा प्लॅटफॉर्मला बदनाम करणारी खाती स्थगित किंवा समाप्त करण्याचा अधिकार Adopt-U राखून ठेवते. आम्ही खोटी, दिशाभूल करणारी किंवा अनुचित सामग्री असलेल्या याद्याही काढून टाकू शकतो.",
    "termsModificationsH2" to "अटींमध्ये सुधारणा",
    "termsModificationsText" to "या अटी कधीही सुधारण्याचा अधिकार आम्ही राखून ठेवतो. बदलांनंतर प्लॅटफॉर्मचा सतत वापर सुधारित अटींच्या स्वीकृतीस सूचित करतो. महत्त्वाच्या बदलांबद्दल आम्ही वापरकर्त्यांना ईमेल किंवा प्लॅटफॉर्म घोषणांद्वारे कळवू.",
    "termsContactText" to "या अटी व शर्तींबद्दल तुम्हाला काही प्रश्न असल्यास, कृपया आमच्याशी adopt-u@adopt-u.org वर संपर्क साधा.",
    "language" to "भाषा",
    "displayName" to "प्रदर्शित नाव",
    "saveProfile" to "प्रोफाइल जतन करा",
    "accountSettings" to "खाते सेटिंग्ज",
    "yourRoles" to "तुमच्या भूमिका:",
    "resources" to "संसाधने",
    "loginWithPasskey" to "पासकीने लॉगिन करा",
    "signInWithPasskey" to "पासकीने साइन इन करा",
    "resendVerificationEmail" to "पडताळणी ईमेल पुन्हा पाठवा",
    "usesFido" to "सुरक्षित पासवर्डविरहित प्रमाणीकरणासाठी FIDO2 / WebAuthn वापरते.",
    "loginWithMagicLink" to "ईमेल लिंकने लॉगिन करा",
    "magicLinkDesc" to "आम्ही तुमच्या ईमेलवर लॉगिन लिंक पाठवू (५ मिनिटांसाठी वैध).",
    "sendMagicLink" to "लॉगिन लिंक पाठवा",
    "loginWithPassword" to "पासवर्डने लॉगिन करा",
    "signIn" to "साइन इन करा",
    "forgotPassword" to "पासवर्ड विसरलात?",
    "registerNewAccount" to "खाते तयार करा",
    "accountDetails" to "खाते तपशील",
    "registrationMethod" to "नोंदणी पद्धत",
    "selectAdditionalRoles" to "अतिरिक्त भूमिका निवडा:",
    "adoptPet" to "पाळीव प्राणी दत्तक घ्या",
    "publishPets" to "दत्तकसाठी पाळीव प्राणी प्रकाशित करा",
    "offerPhotography" to "छायाचित्रण सेवा द्या",
    "photographerSettings" to "छायाचित्रकार सेटिंग्ज",
    "sessionFee" to "सत्र शुल्क",
    "provideTemporaryHome" to "पाळीव प्राण्यांसाठी तात्पुरते घर द्या",
    "passkeyNoPassword" to "पासकी पासवर्डपेक्षा अधिक सुरक्षित आहेत आणि तुमच्या सर्व उपकरणांवर कार्य करतात.",
    "registerWithPasskey" to "पासकीने नोंदणी करा",
    "passkeySettings" to "पासकी सेटिंग्ज",
    "password" to "पासवर्ड",
    "confirmPassword" to "पासवर्डची पुष्टी करा",
    "passwordRequirements" to "पासवर्ड किमान ८ अक्षरांचा असावा आणि त्यात मोठे अक्षर, लहान अक्षर, अंक आणि चिन्ह असावे",
    "securityInfo" to "सुरक्षा माहिती",
    "passkeyMostSecure" to "पासकी: सर्वात सुरक्षित, शिफारस केलेली",
    "passkeyBrowserCompat" to "पासकी सर्व ब्राउझरवर कार्य करू शकत नाही",
    "passwordLeastSecure" to "पासवर्ड: कमी सुरक्षित, शिफारस केलेली नाही",
    "selectLoginMethods" to "एक किंवा अधिक लॉगिन पद्धती निवडा:",
    "registerPasskeyOption" to "पासकी (सर्वात सुरक्षित, तुमच्या सर्व उपकरणांवर कार्य करते)",
    "registerPasswordOption" to "पासवर्ड (कमी सुरक्षित, फक्त बॅकअप म्हणून वापरा)",
    "howPasskeysWorkTitle" to "पासकी कशी कार्य करते?",
    "howPasskeysWorkIntro" to "पासकी तुमच्या पासवर्डची जागा तुमच्या उपकरणाच्या अंगभूत सुरक्षेने घेते — फिंगरप्रिंट, चेहरा ओळख किंवा स्क्रीन लॉक पिन.",
    "howPasskeysWorkSecurity" to "ती फक्त तुमच्या उपकरणावर साठवली जाते, फिश केली जाऊ शकत नाही किंवा डेटा उल्लंघनात लीक होऊ शकत नाही आणि तुमच्या उपकरणांवर आपोआप सिंक होते.",
    "howPasskeysWorkBackup" to "तुम्ही पासवर्डही जोडू शकता, पण साइन इन करण्यासाठी एकटी पासकी पुरेशी आहे — पासवर्डची गरज नाही.",
    "gotIt" to "समजले",
    "alreadyHaveAccount" to "आधीच खाते आहे?",
    "all" to "सर्व",
    "filterDogs" to "कुत्रे",
    "filterCats" to "मांजरी",
    "filterBirds" to "पक्षी",
    "filterFish" to "मासे",
    "filterAllSexOption" to "सर्व लिंग",
    "filterMaleOption" to "♂ नर",
    "filterFemaleOption" to "♀ मादी",
    "age" to "वय",
    "rescued" to "बचावलेले",
    "years" to "वर्षे",
    "months" to "महिने",
    "year" to "वर्ष",
    "month" to "महिना",
    "loginToAdopt" to "दत्तक विनंतीसाठी लॉग इन करा",
    "requiredMarker" to "(आवश्यक)",
    "pageNotFoundTitle" to "पृष्ठ सापडले नाही",
    "pageNotFoundHint" to "लिंक जुनी किंवा चुकीची असू शकते. पाळीव प्राणी अजूनही इथे आहेत.",
    "browsePets" to "पाळीव प्राणी पहा",
    "requestsFromRescuers" to "बचावकर्त्यांकडून विनंत्या",
    "signingOut" to "साइन आउट होत आहे...",
    "adopter" to "दत्तक घेणारा",
    "country" to "देश",
    "zip" to "पिन कोड",
    "viewPublic" to "सार्वजनिक पृष्ठ पहा",
    "banUser" to "वापरकर्त्यावर बंदी घाला",
    "banReason" to "कारण (ऐच्छिक)",
    "myPhotoRequests" to "माझ्या फोटो सेशन विनंत्या",
    "noPhotoRequestsYet" to "तुम्ही अजून फोटो सेशनची विनंती केलेली नाही.",
    "photoRequestsReceived" to "प्राप्त फोटो सेशन विनंत्या",
    "noPhotoRequestsReceived" to "अजून फोटो सेशन विनंत्या नाहीत.",
    "photoStatusPending" to "प्रलंबित",
    "photoStatusApproved" to "मंजूर",
    "photoStatusRejected" to "नाकारले",
    "photoStatusCancelled" to "रद्द",
    "photoStatusCompleted" to "पूर्ण",
    "cancelRequest" to "विनंती रद्द करा",
    "registerConsentPrefix" to "खाते तयार केल्यास तुम्ही स्वीकारता:",
    "registerConsentTerms" to "अटी व शर्ती",
    "registerConsentAnd" to "आणि",
    "registerConsentPrivacy" to "गोपनीयता धोरण",
    "petStatusAvailable" to "उपलब्ध",
    "petStatusAdopted" to "दत्तक घेतलेले",
    "petStatusDisabled" to "अक्षम",
    "petStatusPending" to "प्रलंबित",
    "addEditLocation" to "स्थान जोडा/संपादित करा",
    "addLocation" to "स्थान जोडा",
    "manage" to "व्यवस्थापन",
    "adminPanel" to "प्रशासक पॅनेल",
    "adminSterilizationLocations" to "नसबंदी केंद्रे व्यवस्थापित करा",
    "adopterRequired" to "दत्तक घेणारा (आवश्यक)",
    "adoptionFee" to "दत्तक शुल्क",
    "adoptionRequests" to "दत्तक विनंत्या",
    "ageMonths" to "वय (महिने)",
    "ageYears" to "वय (वर्षे)",
    "alias" to "टोपणनाव",
    "bird" to "पक्षी",
    "breed" to "जात",
    "medicalEventsOverview" to "वैद्यकीय घटनांचा आढावा",
    "back" to "मागे",
    "cat" to "मांजर",
    "color" to "रंग",
    "compatibility" to "सुसंगतता",
    "currentPassword" to "सध्याचा पासवर्ड",
    "dog" to "कुत्रा",
    "emailChange" to "ईमेल बदला",
    "emailChangeDesc" to "तुमचा ईमेल पत्ता अद्यतनित करा. नवीन ईमेलवर पडताळणी दुवा पाठवला जाईल.",
    "energyLevel" to "ऊर्जा पातळी",
    "female" to "मादी",
    "findTemporalHome" to "तात्पुरते घर शोधा",
    "fish" to "मासा",
    "goodWithCats" to "मांजरींसोबत चांगला",
    "goodWithDogs" to "कुत्र्यांसोबत चांगला",
    "goodWithKids" to "मुलांसोबत चांगला",
    "high" to "उच्च",
    "houseTrained" to "घरात प्रशिक्षित",
    "large" to "मोठा",
    "low" to "कमी",
    "male" to "नर",
    "managePets" to "पाळीव प्राणी व्यवस्थापित करा",
    "managePetsBtn" to "पाळीव प्राणी जोडा किंवा संपादित करा",
    "managePetsDescription" to "सर्व पाळीव प्राण्यांची पाने व्यवस्थापित करा. पाळीव प्राणी जोडा किंवा काढा.",
    "manageShelters" to "आश्रयस्थाने व्यवस्थापित करा",
    "manageSheltersBtn" to "आश्रयस्थान जोडा किंवा संपादित करा",
    "manageSheltersDescription" to "सर्व आश्रयस्थान पाने व्यवस्थापित करा. आश्रयस्थान जोडा किंवा काढा.",
    "manageUsers" to "वापरकर्ते व्यवस्थापित करा",
    "medical" to "वैद्यकीय",
    "medium" to "मध्यम",
    "backToSearch" to "शोधाकडे परत जा",
    "contactThisHome" to "या तात्पुरत्या घराशी संपर्क साधा",
    "loginAsRescuerToContact" to "या तात्पुरत्या घराला विनंती पाठवण्यासाठी बचावकर्ता म्हणून लॉगिन करा.",
    "memberSince" to "सदस्यत्वाची तारीख",
    "microchipId" to "मायक्रोचिप आयडी",
    "microchipped" to "मायक्रोचिप केलेले",
    "myTemporalHome" to "माझे तात्पुरते घर",
    "newEmail" to "नवीन ईमेल",
    "noTemporalHomes" to "कोणतीही तात्पुरती घरे आढळली नाहीत.",
    "requestSentToHome" to "तुमची विनंती पाठवली आहे!",
    "sendRequestBtn" to "विनंती पाठवा",
    "temporalHomeNotFound" to "हे तात्पुरते घर सापडले नाही.",
    "viewDetails" to "तपशील पहा",
    "rescuerDirectory" to "बचावकर्ते",
    "noRescuersFound" to "कोणतेही बचावकर्ते आढळले नाहीत.",
    "availablePetsCount" to "उपलब्ध पाळीव प्राणी",
    "availablePets" to "उपलब्ध पाळीव प्राणी",
    "noAvailablePetsForRescuer" to "या बचावकर्त्याकडे सध्या कोणतेही उपलब्ध पाळीव प्राणी नाहीत.",
    "rescuerNotFound" to "हा बचावकर्ता सापडला नाही.",
    "errorLoadingResults" to "निकाल लोड करताना त्रुटी.",
    "volunteerForThisRescuer" to "या बचावकर्त्यासाठी स्वयंसेवक व्हा",
    "volunteerExplanation" to "मंजूर स्वयंसेवक या बचावकर्त्याच्या पाळीव प्राण्यांच्या याद्यांमध्ये संपादनाची सूचना देऊ शकतात.",
    "applyToVolunteerBtn" to "स्वयंसेवकासाठी अर्ज करा",
    "loginToVolunteer" to "या बचावकर्त्यासाठी स्वयंसेवक होण्यासाठी अर्ज करण्यास लॉगिन करा.",
    "volunteerApplicationSent" to "तुमचा स्वयंसेवक अर्ज पाठवला आहे!",
    "myVolunteering" to "माझे स्वयंसेवा कार्य",
    "noVolunteerApplicationsYet" to "तुम्ही अद्याप कोणत्याही बचावकर्त्यासाठी स्वयंसेवक होण्यासाठी अर्ज केलेला नाही.",
    "howToVolunteerHint" to "अर्ज करण्यासाठी, निर्देशिकेतून एखाद्या रेस्क्यूअरचे पेज उघडा आणि \"स्वयंसेवक म्हणून अर्ज करा\" दाबा.",
    "volunteerStatusPending" to "प्रलंबित",
    "volunteerStatusActive" to "सक्रिय",
    "volunteerStatusRejected" to "नाकारले",
    "volunteerApplications" to "स्वयंसेवक अर्ज",
    "noVolunteerApplications" to "कोणतेही स्वयंसेवक अर्ज नाहीत.",
    "petEditSuggestions" to "पाळीव प्राणी संपादन सूचना",
    "noPetEditSuggestions" to "कोणत्याही प्रलंबित संपादन सूचना नाहीत.",
    "suggestedByLabel" to "यांनी सुचवले",
    "suggestAnEditTitle" to "संपादन सुचवा",
    "suggestAnEditExplanation" to "मंजूर स्वयंसेवक म्हणून, तुम्ही या यादीत बदल सुचवू शकता. प्रकाशित होण्यापूर्वी बचावकर्ता त्याचे पुनरावलोकन करेल.",
    "suggestEditBtn" to "सूचना पाठवा",
    "editSuggestionSent" to "तुमची सूचना पुनरावलोकनासाठी बचावकर्त्याला पाठवली आहे!",
    "sponsorThisPetTitle" to "या पाळीव प्राण्याला प्रायोजित करा",
    "sponsorExplanation" to "दत्तक घेऊ शकत नाही पण खर्चात मदत करू इच्छिता? पैसे किंवा वस्तू स्वरूपात मदत द्या आणि थेट बचावकर्त्याशी जोडले जा.",
    "sponsorThisRescuerTitle" to "या बचावकर्त्याला प्रायोजित करा",
    "sponsorRescuerExplanation" to "पैसे किंवा वस्तू स्वरूपात मदतीने या बचावकर्त्याच्या सर्वसाधारण खर्चाला पाठिंबा द्या.",
    "sponsorTypeLabel" to "तुम्ही काय देऊ शकता?",
    "sponsorTypeMoney" to "पैसे",
    "sponsorTypeInKind" to "वस्तू स्वरूपात (अन्न, साहित्य इ.)",
    "amountLabel" to "रक्कम (मासिक)",
    "currencyLabel" to "चलन",
    "inKindDescriptionLabel" to "तुम्ही काय देऊ शकता याचे वर्णन करा",
    "messageLabel" to "संदेश",
    "sendOfferBtn" to "प्रस्ताव पाठवा",
    "sponsorOfferSent" to "तुमचा प्रस्ताव पाठवला आहे! तपशील ठरवण्यासाठी बचावकर्ता संपर्क साधेल.",
    "sponsorshipOffersReceived" to "प्रायोजकत्व प्रस्ताव",
    "noSponsorshipOffers" to "अद्याप कोणतेही प्रायोजकत्व प्रस्ताव नाहीत.",
    "generalFundLabel" to "सर्वसाधारण निधी",
    "offeringLabel" to "देत आहे",
    "sponsorshipStatusSent" to "नवीन",
    "sponsorshipStatusRead" to "वाचले",
    "markAsReadBtn" to "वाचले म्हणून चिन्हांकित करा",
    "mySponsorshipOffers" to "माझे प्रायोजकत्व प्रस्ताव",
    "noSponsorshipOffersYet" to "तुम्ही अद्याप कोणतेही प्रायोजकत्व प्रस्ताव पाठवलेले नाहीत.",
    "howToSponsorHint" to "प्रायोजित करण्यासाठी, एखाद्या रेस्क्यूअरचे पेज (सामान्य मदत) किंवा एखाद्या पाळीव प्राण्याचे पेज (त्या प्राण्याचा खर्च) उघडा आणि ऑफर पाठवा.",
    "browsePetsLink" to "पाळीव प्राणी पहा",
    "yourMessage" to "तुमचा संदेश",
    "newPassword" to "नवीन पासवर्ड",
    "passkeyDesc" to "उपकरणांवर सहज लॉगिनसाठी तुमच्या खात्यात अतिरिक्त पासकी जोडा.",
    "passkeyName" to "पासकी नाव",
    "pers" to "लोक",
    "petFoodDescription" to "तुमच्या पाळीव प्राण्यांसाठी कोणते अन्न सुरक्षित, हानिकारक किंवा विषारी आहे ते जाणून घ्या",
    "petFoodGuide" to "पाळीव प्राणी अन्न मार्गदर्शक",
    "foodInformation" to "अन्न माहिती",
    "petsForAdoption" to "दत्तकसाठी पाळीव प्राणी",
    "chooseCountryPrompt" to "तुमचा नवीन जिवलग मित्र शोधा",
    "chooseCountryPromptHint" to "तुमच्या जवळ दत्तकसाठी उपलब्ध पाळीव प्राणी पाहण्यासाठी वरील देश निवडा.",
    "noPetsFound" to "कोणतेही पाळीव प्राणी आढळले नाहीत",
    "noPetsFoundHint" to "वेगळे फिल्टर वापरून पहा किंवा लवकरच पुन्हा तपासा — नवीन पाळीव प्राणी नियमितपणे जोडले जातात.",
    "loadingMorePets" to "अधिक पाळीव प्राणी लोड करत आहे…",
    "photos" to "फोटो (कमाल १२)",
    "profilePhotographerRole" to "छायाचित्रकार - छायाचित्रण सेवा द्या",
    "profileRescuerRole" to "बचावकर्ता - दत्तकसाठी पाळीव प्राणी प्रकाशित करा",
    "profileTemporalHomeRole" to "तात्पुरते घर - पाळीव प्राण्यांसाठी तात्पुरते घर द्या",
    "provideShelter" to "आश्रयस्थान - प्राणी आश्रयस्थान व्यवस्थापित करा",
    "provideSterilization" to "नसबंदी सेवा - नसबंदी सेवा पुरवा",
    "requestEmailChange" to "ईमेल बदलण्याची विनंती करा",
    "rescueDate" to "बचाव तारीख",
    "rescueLocation" to "बचाव स्थान",
    "savePassword" to "पासवर्ड जतन करा",
    "selectEnergy" to "ऊर्जा निवडा",
    "selectSize" to "आकार निवडा",
    "sex" to "लिंग",
    "shelterSettings" to "आश्रयस्थान सेटिंग्ज",
    "size" to "आकार",
    "small" to "लहान",
    "specialNeeds" to "विशेष गरजा",
    "sterilizationSettings" to "नसबंदी सेटिंग्ज",
    "sterilized" to "नसबंदी केलेले",
    "temporalHomeSettings" to "तात्पुरते घर सेटिंग्ज",
    "temperament" to "स्वभाव",
    "type" to "प्रकार *",
    "urgentAdoption" to "तातडीने दत्तक आवश्यक",
    "vaccinations" to "लसीकरणे",
    "vetDisclaimer" to "आहारात बदल करण्यापूर्वी तुमच्या पशुवैद्यकाचा सल्ला घ्या.",
    "weight" to "वजन (किलो)",
    "weightLabel" to "वजन",
    "website" to "वेबसाइट",
    "securitySettings" to "सुरक्षा सेटिंग्ज",
    "emailVerifiedTitle" to "ईमेल यशस्वीरित्या पडताळला!",
    "emailVerifiedMessage" to "तुमचा ईमेल पडताळला गेला आहे. तुमची नोंदणी आता पूर्ण झाली आहे.",
    "redirectingIn" to "मुख्य पानावर पुनर्निर्देशित करत आहे ",
    "canAlsoLogin" to "तुम्ही असेही करू शकता ",
    "loginNow" to "आता लॉगिन करा",
    "emailVerificationFailed" to "पडताळणी अयशस्वी",
    "invalidOrExpiredToken" to "पडताळणी दुवा अवैध आहे किंवा त्याची मुदत संपली आहे.",
    "tryRegisteringAgain" to "कृपया पुन्हा नोंदणी करण्याचा प्रयत्न करा.",
    "verificationEmailResent" to "पडताळणी ईमेलची मुदत संपली होती. नवीन पडताळणी ईमेल पाठवला आहे. कृपया तुमचा इनबॉक्स तपासा.",
    "emailVerificationSent" to "पडताळणी ईमेल पाठवला आहे. लॉगिन करण्यापूर्वी कृपया तुमचा इनबॉक्स तपासा.",
    "emailNotVerifiedLogin" to "लॉगिन करण्यापूर्वी कृपया तुमचा ईमेल पडताळा. पडताळणी दुव्यासाठी तुमचा इनबॉक्स तपासा.",
    "invalidOrExpiredMagicLink" to "अवैध किंवा कालबाह्य मॅजिक लिंक. कृपया नवीन विनंती करा.",
    "invalidToken" to "अवैध टोकन.",
    "accountBanned" to "तुमचे खाते बंदी घातले गेले आहे.",
    "loginError" to "लॉगिन त्रुटी आली.",
    "profileUpdated" to "प्रोफाइल अद्यतनित केले!",
    "displayNameRequired" to "प्रदर्शित नाव रिकामे असू शकत नाही",
    "saving" to "जतन करत आहे...",
    "passwordSet" to "पासवर्ड सेट केला आहे",
    "noPassword" to "कोणताही पासवर्ड सेट केलेला नाही",
    "passwordChangeSuccess" to "पासवर्ड यशस्वीरित्या बदलला!",
    "passwordChangeFailed" to "पासवर्ड बदलण्यात अयशस्वी",
    "passwordSetSuccess" to "पासवर्ड यशस्वीरित्या सेट केला!",
    "passwordSetFailed" to "पासवर्ड सेट करण्यात अयशस्वी",
    "passwordsDoNotMatch" to "पासवर्ड जुळत नाहीत",
    "currentPasswordRequired" to "सध्याचा पासवर्ड आवश्यक आहे",
    "emailChangeSent" to "तुमच्या नवीन ईमेल पत्त्यावर पडताळणी ईमेल पाठवला आहे!",
    "emailChangeFailed" to "ईमेल बदलण्याची विनंती करण्यात अयशस्वी",
    "emailRequired" to "ईमेल आवश्यक आहे",
    "sendingEllipsis" to "पाठवत आहे...",
    "resetLinkSent" to "पासवर्ड रीसेट दुवा पाठवला! तुमचा ईमेल तपासा.",
    "magicLinkSent" to "लॉगिन दुवा पाठवला! तुमचा ईमेल तपासा.",
    "magicLinkSendFailed" to "लॉगिन दुवा पाठवण्यात अयशस्वी",
    "failedSendResetLink" to "रीसेट दुवा पाठवण्यात अयशस्वी.",
    "invalidOrMissingToken" to "अवैध किंवा गहाळ टोकन.",
    "passwordTooShort" to "पासवर्ड किमान ८ अक्षरांचा असावा.",
    "passwordNeedUppercase" to "पासवर्डमध्ये किमान १ मोठे अक्षर असणे आवश्यक आहे.",
    "passwordNeedLowercase" to "पासवर्डमध्ये किमान १ लहान अक्षर असणे आवश्यक आहे.",
    "passwordNeedNumber" to "पासवर्डमध्ये किमान १ अंक असणे आवश्यक आहे.",
    "passwordNeedSymbol" to "पासवर्डमध्ये किमान १ चिन्ह असणे आवश्यक आहे.",
    "resettingPassword" to "पासवर्ड रीसेट करत आहे...",
    "passwordResetSuccess" to "पासवर्ड यशस्वीरित्या रीसेट झाला! तुम्ही आता लॉगिन करू शकता.",
    "failedResetPassword" to "पासवर्ड रीसेट करण्यात अयशस्वी.",
    "loginSuccessRedirecting" to "लॉगिन यशस्वी! पुनर्निर्देशित करत आहे...",
    "loginFailedExpiredLink" to "लॉगिन अयशस्वी. दुवा अवैध किंवा कालबाह्य असू शकतो.",
    "loginFailed" to "लॉगिन अयशस्वी.",
    "emailChangedSuccess" to "ईमेल यशस्वीरित्या बदलला!",
    "failedChangeEmailExpired" to "ईमेल बदलण्यात अयशस्वी. दुवा अवैध किंवा कालबाह्य असू शकतो.",
    "failedChangeEmail" to "ईमेल बदलण्यात अयशस्वी.",
    "emailVerifiedSuccess" to "ईमेल यशस्वीरित्या पडताळला!",
    "failedVerifyEmailExpired" to "ईमेल पडताळण्यात अयशस्वी. दुवा अवैध किंवा कालबाह्य असू शकतो.",
    "failedVerifyEmail" to "ईमेल पडताळण्यात अयशस्वी.",
    "dropImagesHint" to "प्रतिमा येथे टाका किंवा ब्राउझ करण्यासाठी क्लिक करा",
    "forgotPasswordTitle" to "पासवर्ड विसरलात",
    "forgotPasswordIntro" to "तुमचा ईमेल टाका आणि आम्ही तुम्हाला तुमचा पासवर्ड रीसेट करण्यासाठी दुवा पाठवू.",
    "sendResetLink" to "रीसेट दुवा पाठवा",
    "backToLogin" to "लॉगिनकडे परत जा",
    "resetPasswordTitle" to "पासवर्ड रीसेट करा",
    "resetPasswordIntro" to "खाली तुमचा नवीन पासवर्ड टाका.",
    "emailLinkLoginTitle" to "ईमेल लिंक लॉगिन",
    "verifying" to "पडताळत आहे...",
    "emailChangeTitle" to "ईमेल बदल",
    "verifyContactEmailTitle" to "संपर्क ईमेल पडताळा",
    "status" to "स्थिती",
    "yes" to "होय",
    "no" to "नाही",
    "noDescription" to "कोणतेही वर्णन नाही.",
    "urgentBadge" to "तातडीचे - लवकरच घर हवे!",
    "needsNewHome" to "या पाळीव प्राण्याला तातडीने नवीन घराची गरज आहे",
    "promotedReasonLabel" to "कारण",
    "selectReason" to "एक कारण निवडा",
    "promotedReasonMoving" to "मालक स्थलांतर करत आहे",
    "promotedReasonComplaints" to "तक्रारी / अधिक पाळीव प्राणी ठेवू शकत नाही",
    "promotedReasonPetConflict" to "दुसऱ्या पाळीव प्राण्याशी संघर्ष",
    "promotedReasonOther" to "इतर",
    "promotedReasonDetailLabel" to "अतिरिक्त तपशील (ऐच्छिक)",
    "promotedReasonRequired" to "कृपया एक कारण निवडा",
    "needsNewHomeBadge" to "नवीन घर हवे",
    "housingType" to "घराचा प्रकार",
    "preferNotToSay" to "सांगू इच्छित नाही",
    "houseHousing" to "घर",
    "apartmentHousing" to "अपार्टमेंट",
    "hasYard" to "अंगण आहे",
    "hasOtherPets" to "घरी इतर पाळीव प्राणी आहेत",
    "adoptionExperience" to "दत्तक अनुभव",
    "firstTimeAdopter" to "प्रथमच दत्तक घेणारा",
    "experiencedAdopter" to "अनुभवी दत्तक घेणारा",
    "noAdoptionRequests" to "कोणत्याही दत्तक विनंत्या नाहीत",
    "noMessage" to "कोणताही संदेश नाही",
    "markUnderReview" to "पुनरावलोकनाधीन म्हणून चिन्हांकित करा",
    "approve" to "मंजूर करा",
    "reject" to "नाकारा",
    "reviewNoteLabel" to "खाजगी पुनरावलोकन टीप",
    "saveNote" to "टीप जतन करा",
    "adoptionStatusPending" to "प्रलंबित",
    "adoptionStatusUnderReview" to "पुनरावलोकनाधीन",
    "adoptionStatusApproved" to "मंजूर",
    "adoptionStatusRejected" to "मंजूर नाही",
    "myAdoptionRequests" to "माझ्या दत्तक विनंत्या",
    "noAdoptionRequestsYet" to "तुम्ही अद्याप कोणताही पाळीव प्राणी दत्तक घेण्याची विनंती केलेली नाही.",
    "medicalHistory" to "वैद्यकीय इतिहास",
    "medicalSchedule" to "लसीकरण आणि जंतनाशक वेळापत्रक",
    "recordType" to "प्रकार",
    "vaccination" to "लसीकरण",
    "deworming" to "जंतनाशक",
    "recordName" to "नाव",
    "dateGiven" to "दिलेली तारीख",
    "nextDueDate" to "पुढील देय (ऐच्छिक)",
    "notesOptional" to "टिपा (ऐच्छिक)",
    "addRecord" to "नोंद जोडा",
    "noMedicalRecords" to "अद्याप लसीकरण किंवा जंतनाशक नोंदी नाहीत.",
    "nextDueLabel" to "पुढील देय",
    "givenLabel" to "दिलेले",
    "overdue" to "मुदत उलटलेली",
    "dueSoon" to "लवकरच देय",
    "upToDate" to "अद्ययावत",
    "recordNameRequired" to "नाव आवश्यक आहे",
    "dateGivenRequired" to "दिलेली तारीख आवश्यक आहे",
    "failedToAddRecord" to "नोंद जोडण्यात अयशस्वी",
    "confirmDeleteRecord" to "ही नोंद हटवायची?",
    "maxCapacity" to "एका वेळी कमाल पाळीव प्राणी (ऐच्छिक)",
    "placePetHere" to "येथे पाळीव प्राणी ठेवा",
    "noAvailablePetsToPlace" to "सध्या ठेवण्यासाठी तुमच्याकडे कोणतेही उपलब्ध पाळीव प्राणी नाहीत.",
    "selectPet" to "पाळीव प्राणी निवडा",
    "startPlacementBtn" to "प्लेसमेंट सुरू करा",
    "placementStarted" to "फॉस्टर प्लेसमेंट सुरू झाले!",
    "petsCurrentlyWithMe" to "सध्या माझ्याकडे असलेले पाळीव प्राणी",
    "noPetsCurrentlyFostered" to "सध्या तुमच्याकडे कोणतेही पाळीव प्राणी ठेवलेले नाहीत.",
    "sinceLabel" to "पासून",
    "fosterPlacement" to "फॉस्टर प्लेसमेंट",
    "endPlacement" to "प्लेसमेंट समाप्त करा",
    "currentlyWithLabel" to "सध्या यांच्याकडे",
    "notCurrentlyFostered" to "सध्या फॉस्टर केअरमध्ये नाही.",
    "confirmEndPlacement" to "ही फॉस्टर प्लेसमेंट समाप्त करायची? पाळीव प्राणी तुमच्याकडे परत आले असे नोंदवले जाईल.",
    "aTemporalHome" to "एक तात्पुरते घर",
    "viewsLabel" to "पाहिले",
    "inquiriesLabel" to "चौकशी",
    "conversionLabel" to "रूपांतरण",
    "messageOptional" to "संदेश (ऐच्छिक)",
    "requestAdoption" to "दत्तक विनंती करा",
    "editPet" to "पाळीव प्राणी संपादित करा",
    "adoptionRequestSubmitted" to "दत्तक विनंती सादर केली!",
    "failedSubmitRequest" to "विनंती सादर करण्यात अयशस्वी",
    "noPhotosYet" to "अद्याप कोणतेही फोटो नाहीत.",
    "rabbit" to "ससा",
    "safeFoods" to "सुरक्षित अन्न",
    "harmfulFoods" to "हानिकारक अन्न",
    "cannotEatFoods" to "खाऊ शकत नाही",
    "legendSafe" to "सुरक्षित",
    "legendHarmful" to "हानिकारक",
    "legendToxic" to "विषारी",
    "food_dog_safe_chicken_name" to "चिकन",
    "food_dog_safe_chicken_detail" to "शिजवलेले, हाडविरहित",
    "food_dog_safe_chicken_desc" to "पातळ प्रथिने, पचायला सोपे",
    "food_dog_safe_rice_name" to "भात",
    "food_dog_safe_rice_detail" to "पांढरा किंवा तपकिरी",
    "food_dog_safe_rice_desc" to "कर्बोदकांचा चांगला स्रोत",
    "food_dog_safe_carrots_name" to "गाजर",
    "food_dog_safe_carrots_detail" to "कच्चे किंवा शिजवलेले",
    "food_dog_safe_carrots_desc" to "कमी कॅलरी, जास्त तंतू",
    "food_dog_safe_apples_name" to "सफरचंद",
    "food_dog_safe_apples_detail" to "बिया काढून",
    "food_dog_safe_apples_desc" to "जीवनसत्त्वांनी समृद्ध",
    "food_dog_safe_peanutbutter_name" to "शेंगदाणा लोणी",
    "food_dog_safe_peanutbutter_detail" to "मीठविरहित, झायलिटॉलविरहित",
    "food_dog_safe_peanutbutter_desc" to "प्रथिनांचा चांगला स्रोत",
    "food_dog_harmful_grapes_name" to "द्राक्षे",
    "food_dog_harmful_grapes_detail" to "सर्व प्रकार",
    "food_dog_harmful_grapes_desc" to "मूत्रपिंड निकामी होऊ शकते",
    "food_dog_harmful_onions_name" to "कांदे",
    "food_dog_harmful_onions_detail" to "सर्व रूपे",
    "food_dog_harmful_onions_desc" to "लाल रक्तपेशींना हानी पोहोचवते",
    "food_dog_harmful_chocolate_name" to "चॉकलेट",
    "food_dog_harmful_chocolate_detail" to "सर्व प्रकार",
    "food_dog_harmful_chocolate_desc" to "कुत्र्यांसाठी विषारी",
    "food_dog_harmful_garlic_name" to "लसूण",
    "food_dog_harmful_garlic_detail" to "सर्व रूपे",
    "food_dog_harmful_garlic_desc" to "अशक्तपणा निर्माण करतो",
    "food_dog_cannot_xylitol_name" to "झायलिटॉल",
    "food_dog_cannot_xylitol_detail" to "कृत्रिम गोडी करणारे पदार्थ",
    "food_dog_cannot_xylitol_desc" to "अत्यंत विषारी",
    "food_dog_cannot_macadamia_name" to "मॅकाडॅमिया काजू",
    "food_dog_cannot_macadamia_detail" to "सर्व रूपे",
    "food_dog_cannot_macadamia_desc" to "अशक्तपणा निर्माण करतो",
    "food_dog_cannot_avocado_name" to "अॅव्होकॅडो",
    "food_dog_cannot_avocado_detail" to "बी, साल, सर्व",
    "food_dog_cannot_avocado_desc" to "पर्सिन असते",
    "food_dog_cannot_alcohol_name" to "मद्य",
    "food_dog_cannot_alcohol_detail" to "सर्व रूपे",
    "food_dog_cannot_alcohol_desc" to "अत्यंत विषारी",
    "food_cat_safe_cookedfish_name" to "शिजवलेला मासा",
    "food_cat_safe_cookedfish_detail" to "काटे नसलेला",
    "food_cat_safe_cookedfish_desc" to "उच्च प्रथिनयुक्त",
    "food_cat_safe_chicken_name" to "चिकन",
    "food_cat_safe_chicken_detail" to "शिजवलेले, साधे",
    "food_cat_safe_chicken_desc" to "प्रथिनांचा चांगला स्रोत",
    "food_cat_safe_pumpkin_name" to "भोपळा",
    "food_cat_safe_pumpkin_detail" to "साधा, शिजवलेला",
    "food_cat_safe_pumpkin_desc" to "पचनास मदत करतो",
    "food_cat_safe_eggs_name" to "अंडी",
    "food_cat_safe_eggs_detail" to "शिजवलेली",
    "food_cat_safe_eggs_desc" to "संपूर्ण प्रथिन",
    "food_cat_harmful_raweggs_name" to "कच्ची अंडी",
    "food_cat_harmful_raweggs_detail" to "अॅव्हिडिनसह",
    "food_cat_harmful_raweggs_desc" to "बायोटिनमध्ये अडथळा आणते",
    "food_cat_harmful_rawfish_name" to "कच्चा मासा",
    "food_cat_harmful_rawfish_detail" to "थायमिनेज असते",
    "food_cat_harmful_rawfish_desc" to "बी जीवनसत्त्वे नष्ट करतो",
    "food_cat_harmful_dogfood_name" to "कुत्र्याचे अन्न",
    "food_cat_harmful_dogfood_detail" to "कोणतेही",
    "food_cat_harmful_dogfood_desc" to "टॉरिनची कमतरता",
    "food_cat_harmful_milk_name" to "दूध",
    "food_cat_harmful_milk_detail" to "बहुतेक मांजरी",
    "food_cat_harmful_milk_desc" to "लॅक्टोज असहिष्णुता",
    "food_cat_cannot_chocolate_name" to "चॉकलेट",
    "food_cat_cannot_chocolate_detail" to "सर्व प्रकार",
    "food_cat_cannot_chocolate_desc" to "थिओब्रोमिन विषारी",
    "food_cat_cannot_onionsgarlic_name" to "कांदा/लसूण",
    "food_cat_cannot_onionsgarlic_detail" to "सर्व रूपे",
    "food_cat_cannot_onionsgarlic_desc" to "लाल रक्तपेशींना हानी पोहोचवते",
    "food_cat_cannot_grapesraisins_name" to "द्राक्षे/मनुका",
    "food_cat_cannot_grapesraisins_detail" to "सर्व प्रकार",
    "food_cat_cannot_grapesraisins_desc" to "मूत्रपिंडाचे नुकसान",
    "food_cat_cannot_alcohol_name" to "मद्य",
    "food_cat_cannot_alcohol_detail" to "सर्व रूपे",
    "food_cat_cannot_alcohol_desc" to "अत्यंत विषारी",
    "food_bird_safe_seeds_name" to "बिया",
    "food_bird_safe_seeds_detail" to "विविध मिश्रण",
    "food_bird_safe_seeds_desc" to "चांगला स्निग्धांश स्रोत",
    "food_bird_safe_fruits_name" to "फळे",
    "food_bird_safe_fruits_detail" to "बहुतेक प्रकार",
    "food_bird_safe_fruits_desc" to "जीवनसत्त्वे आणि खनिजे",
    "food_bird_safe_vegetables_name" to "भाज्या",
    "food_bird_safe_vegetables_detail" to "पालेभाज्या",
    "food_bird_safe_vegetables_desc" to "आवश्यक पोषक तत्त्वे",
    "food_bird_safe_pellets_name" to "पेलेट्स",
    "food_bird_safe_pellets_detail" to "दर्जेदार फॉर्म्युलेटेड",
    "food_bird_safe_pellets_desc" to "संपूर्ण पोषण",
    "food_bird_harmful_avocado_name" to "अॅव्होकॅडो",
    "food_bird_harmful_avocado_detail" to "सर्व भाग",
    "food_bird_harmful_avocado_desc" to "पर्सिन विषारी",
    "food_bird_harmful_fruitpits_name" to "फळांच्या बिया",
    "food_bird_harmful_fruitpits_detail" to "सफरचंद, पीच",
    "food_bird_harmful_fruitpits_desc" to "सायनाइडचे अंश",
    "food_bird_harmful_salt_name" to "मीठ",
    "food_bird_harmful_salt_detail" to "कोणत्याही स्वरूपात",
    "food_bird_harmful_salt_desc" to "पक्ष्यांसाठी विषारी",
    "food_bird_harmful_caffeine_name" to "कॅफिन",
    "food_bird_harmful_caffeine_detail" to "कॉफी, चहा",
    "food_bird_harmful_caffeine_desc" to "हृदयाच्या समस्या",
    "food_bird_cannot_chocolate_name" to "चॉकलेट",
    "food_bird_cannot_chocolate_detail" to "सर्व प्रकार",
    "food_bird_cannot_chocolate_desc" to "थिओब्रोमिन विषारी",
    "food_bird_cannot_onions_name" to "कांदे",
    "food_bird_cannot_onions_detail" to "सर्व रूपे",
    "food_bird_cannot_onions_desc" to "लाल रक्तपेशींना हानी पोहोचवते",
    "food_bird_cannot_garlic_name" to "लसूण",
    "food_bird_cannot_garlic_detail" to "सर्व रूपे",
    "food_bird_cannot_garlic_desc" to "विषारी",
    "food_bird_cannot_mushrooms_name" to "मशरूम",
    "food_bird_cannot_mushrooms_detail" to "जंगली प्रकार",
    "food_bird_cannot_mushrooms_desc" to "संभाव्य विषारी पदार्थ",
    "food_fish_safe_flakes_name" to "फ्लेक्स",
    "food_fish_safe_flakes_detail" to "दर्जेदार ब्रँड",
    "food_fish_safe_flakes_desc" to "संपूर्ण मासे अन्न",
    "food_fish_safe_pellets_name" to "पेलेट्स",
    "food_fish_safe_pellets_detail" to "तरंगणारे/बुडणारे",
    "food_fish_safe_pellets_desc" to "संतुलित पोषण",
    "food_fish_safe_frozenfood_name" to "गोठवलेले अन्न",
    "food_fish_safe_frozenfood_detail" to "ब्लडवर्म्स, ब्राइन",
    "food_fish_safe_frozenfood_desc" to "प्रथिन स्रोत",
    "food_fish_safe_vegetables_name" to "भाज्या",
    "food_fish_safe_vegetables_detail" to "वाफवलेले झुकिनी",
    "food_fish_safe_vegetables_desc" to "तंतूंचा स्रोत",
    "food_fish_harmful_bread_name" to "ब्रेड",
    "food_fish_harmful_bread_detail" to "कोणताही प्रकार",
    "food_fish_harmful_bread_desc" to "पोषण नाही, पोट भरते",
    "food_fish_harmful_humanfood_name" to "मानवी अन्न",
    "food_fish_harmful_humanfood_detail" to "शिजवलेले जेवण",
    "food_fish_harmful_humanfood_desc" to "चुकीचे पोषण",
    "food_fish_harmful_livefeed_name" to "जिवंत खाद्य",
    "food_fish_harmful_livefeed_detail" to "जंगली पकडलेले कीटक",
    "food_fish_harmful_livefeed_desc" to "संभाव्य परजीवी",
    "food_fish_cannot_landinsect_name" to "जमिनीवरील कीटक",
    "food_fish_cannot_landinsect_detail" to "रांगणारे कीटक",
    "food_fish_cannot_landinsect_desc" to "नैसर्गिक अन्न नाही",
    "food_fish_cannot_mammalmeat_name" to "सस्तन प्राण्याचे मांस",
    "food_fish_cannot_mammalmeat_detail" to "चिकन, गोमांस",
    "food_fish_cannot_mammalmeat_desc" to "बहुतेक माशांसाठी चुकीचे",
    "food_fish_cannot_dairy_name" to "दुग्धजन्य पदार्थ",
    "food_fish_cannot_dairy_detail" to "कोणतेही",
    "food_fish_cannot_dairy_desc" to "पचवू शकत नाही",
    "food_fish_cannot_breadcrumbs_name" to "ब्रेडक्रम्ब्स",
    "food_fish_cannot_breadcrumbs_detail" to "अनेकांमध्ये लसूण असतो",
    "food_fish_cannot_breadcrumbs_desc" to "हानिकारक पदार्थ",
    "food_rabbit_safe_hay_name" to "गवत",
    "food_rabbit_safe_hay_detail" to "टिमोथी गवत",
    "food_rabbit_safe_hay_desc" to "आहाराच्या ८०%",
    "food_rabbit_safe_leafygreens_name" to "पालेभाज्या",
    "food_rabbit_safe_leafygreens_detail" to "रोमेन, कोथिंबीर",
    "food_rabbit_safe_leafygreens_desc" to "दैनंदिन जीवनसत्त्वे",
    "food_rabbit_safe_pellets_name" to "पेलेट्स",
    "food_rabbit_safe_pellets_detail" to "टिमोथी-आधारित",
    "food_rabbit_safe_pellets_desc" to "संतुलित पोषण",
    "food_rabbit_safe_carrots_name" to "गाजर",
    "food_rabbit_safe_carrots_detail" to "मर्यादित प्रमाणात",
    "food_rabbit_safe_carrots_desc" to "बक्षीस",
    "food_rabbit_harmful_icebergletttuce_name" to "आइसबर्ग लेट्युस",
    "food_rabbit_harmful_icebergletttuce_detail" to "पोषण नाही",
    "food_rabbit_harmful_icebergletttuce_desc" to "जीआय स्टेसिस होऊ शकतो",
    "food_rabbit_harmful_beans_name" to "बीन्स",
    "food_rabbit_harmful_beans_detail" to "सर्व डाळी",
    "food_rabbit_harmful_beans_desc" to "गॅस, पचनाच्या समस्या",
    "food_rabbit_harmful_corn_name" to "मका",
    "food_rabbit_harmful_corn_detail" to "कोणत्याही स्वरूपात",
    "food_rabbit_harmful_corn_desc" to "पचवू शकत नाही",
    "food_rabbit_harmful_potatoes_name" to "बटाटे",
    "food_rabbit_harmful_potatoes_detail" to "कोणत्याही स्वरूपात",
    "food_rabbit_harmful_potatoes_desc" to "चुकीचे पोषक घटक",
    "food_rabbit_cannot_chocolate_name" to "चॉकलेट",
    "food_rabbit_cannot_chocolate_detail" to "सर्व प्रकार",
    "food_rabbit_cannot_chocolate_desc" to "थिओब्रोमिन विषारी",
    "food_rabbit_cannot_onions_name" to "कांदे",
    "food_rabbit_cannot_onions_detail" to "सर्व रूपे",
    "food_rabbit_cannot_onions_desc" to "अत्यंत विषारी",
    "food_rabbit_cannot_garlic_name" to "लसूण",
    "food_rabbit_cannot_garlic_detail" to "सर्व रूपे",
    "food_rabbit_cannot_garlic_desc" to "विषारी",
    "food_rabbit_cannot_avocado_name" to "अॅव्होकॅडो",
    "food_rabbit_cannot_avocado_detail" to "सर्व भाग",
    "food_rabbit_cannot_avocado_desc" to "पर्सिन असते",
    "primary" to "प्राथमिक",
    "passkeyRegisteredSuccess" to "पासकी यशस्वीरित्या नोंदणीकृत झाली!",
    "passkeyRegistrationFailed" to "पासकी नोंदणी अयशस्वी",
    "passkeyNameRequired" to "पासकी नाव आवश्यक आहे",
    "registeringPasskey" to "पासकी नोंदणी करत आहे...",
    "passkeyRegistered" to "पासकी नोंदणीकृत",
    "noPasskey" to "कोणतीही पासकी नोंदणीकृत नाही",
    "noPets" to "अद्याप कोणतेही पाळीव प्राणी नाहीत.",
    "photographerCountryStateRequired" to "कृपया छायाचित्रण सेवांसाठी देश आणि राज्य भरा",
        "temporalHomeAliasCountryCityRequired" to "कृपया तात्पुरत्या घरासाठी टोपणनाव, देश आणि शहर भरा",
        "shelterNameCountryCityAddressRequired" to "कृपया आश्रयस्थानासाठी नाव, देश, शहर आणि पत्ता भरा",
        "sterilizationNameCountryCityAddressRequired" to "कृपया नसबंदी स्थानासाठी नाव, देश, शहर आणि पत्ता भरा",
        "country.afghanistan" to "अफगाणिस्तान",
        "country.albania" to "अल्बानिया",
        "country.algeria" to "अल्जेरिया",
        "country.argentina" to "अर्जेंटिना",
        "country.armenia" to "आर्मेनिया",
        "country.australia" to "ऑस्ट्रेलिया",
        "country.austria" to "ऑस्ट्रिया",
        "country.azerbaijan" to "अझरबैजान",
        "country.bangladesh" to "बांगलादेश",
        "country.belarus" to "बेलारूस",
        "country.belgium" to "बेल्जियम",
        "country.bolivia" to "बोलिव्हिया",
        "country.bosnia" to "बोस्निया आणि हर्झेगोविना",
        "country.brazil" to "ब्राझील",
        "country.bulgaria" to "बल्गेरिया",
        "country.cambodia" to "कंबोडिया",
        "country.cameroon" to "कॅमेरून",
        "country.canada" to "कॅनडा",
        "country.chile" to "चिली",
        "country.china" to "चीन",
        "country.colombia" to "कोलंबिया",
        "country.costaRica" to "कोस्टा रिका",
        "country.croatia" to "क्रोएशिया",
        "country.cuba" to "क्युबा",
        "country.czechia" to "चेक प्रजासत्ताक",
        "country.denmark" to "डेन्मार्क",
        "country.dominicanRepublic" to "डॉमिनिकन रिपब्लिक",
        "country.ecuador" to "इक्वेडोर",
        "country.egypt" to "इजिप्त",
        "country.elSalvador" to "एल साल्वाडोर",
        "country.estonia" to "एस्टोनिया",
        "country.ethiopia" to "इथिओपिया",
        "country.finland" to "फिनलंड",
        "country.france" to "फ्रान्स",
        "country.georgia" to "जॉर्जिया",
        "country.germany" to "जर्मनी",
        "country.ghana" to "घाना",
        "country.greece" to "ग्रीस",
        "country.guatemala" to "ग्वाटेमाला",
        "country.haiti" to "हैती",
        "country.honduras" to "होंडुरास",
        "country.hungary" to "हंगेरी",
        "country.iceland" to "आइसलँड",
        "country.india" to "भारत",
        "country.indonesia" to "इंडोनेशिया",
        "country.iran" to "इराण",
        "country.iraq" to "इराक",
        "country.ireland" to "आयर्लंड",
        "country.israel" to "इस्रायल",
        "country.italy" to "इटली",
        "country.jamaica" to "जमैका",
        "country.japan" to "जपान",
        "country.jordan" to "जॉर्डन",
        "country.kazakhstan" to "कझाकस्तान",
        "country.kenya" to "केनिया",
        "country.kuwait" to "कुवेत",
        "country.latvia" to "लॅटव्हिया",
        "country.lebanon" to "लेबनॉन",
        "country.libya" to "लिबिया",
        "country.lithuania" to "लिथुआनिया",
        "country.luxembourg" to "लक्झेंबर्ग",
        "country.malaysia" to "मलेशिया",
        "country.mexico" to "मेक्सिको",
        "country.moldova" to "मोल्दोव्हा",
        "country.mongolia" to "मंगोलिया",
        "country.montenegro" to "माँटेनेग्रो",
        "country.morocco" to "मोरोक्को",
        "country.myanmar" to "म्यानमार",
        "country.nepal" to "नेपाळ",
        "country.netherlands" to "नेदरलँड्स",
        "country.newZealand" to "न्यूझीलंड",
        "country.nicaragua" to "निकाराग्वा",
        "country.nigeria" to "नायजेरिया",
        "country.northKorea" to "उत्तर कोरिया",
        "country.norway" to "नॉर्वे",
        "country.pakistan" to "पाकिस्तान",
        "country.panama" to "पनामा",
        "country.paraguay" to "पॅराग्वे",
        "country.peru" to "पेरू",
        "country.philippines" to "फिलिपिन्स",
        "country.poland" to "पोलंड",
        "country.portugal" to "पोर्तुगाल",
        "country.puertoRico" to "पोर्तो रिको",
        "country.qatar" to "कतार",
        "country.romania" to "रोमानिया",
        "country.russia" to "रशिया",
        "country.saudiArabia" to "सौदी अरेबिया",
        "country.serbia" to "सर्बिया",
        "country.singapore" to "सिंगापूर",
        "country.slovakia" to "स्लोव्हाकिया",
        "country.slovenia" to "स्लोव्हेनिया",
        "country.southAfrica" to "दक्षिण आफ्रिका",
        "country.southKorea" to "दक्षिण कोरिया",
        "country.spain" to "स्पेन",
        "country.sriLanka" to "श्रीलंका",
        "country.sudan" to "सुदान",
        "country.sweden" to "स्वीडन",
        "country.switzerland" to "स्वित्झर्लंड",
        "country.syria" to "सीरिया",
        "country.taiwan" to "तैवान",
        "country.thailand" to "थायलंड",
        "country.tunisia" to "ट्युनिशिया",
        "country.turkey" to "तुर्कस्तान",
        "country.ukraine" to "युक्रेन",
        "country.uae" to "संयुक्त अरब अमिराती",
        "country.uk" to "युनायटेड किंगडम",
        "country.usa" to "अमेरिकेची संयुक्त संस्थाने",
        "country.uruguay" to "उरुग्वे",
        "country.uzbekistan" to "उझबेकिस्तान",
        "country.venezuela" to "व्हेनेझुएला",
        "country.vietnam" to "व्हिएतनाम",
        "country.yemen" to "येमेन",
        "loading" to "लोड करत आहे...",
        "countryRequired" to "शोधण्यासाठी कृपया देश निवडा",
        "noSheltersFound" to "अद्याप कोणतेही आश्रयस्थान आढळले नाही. नंतर पुन्हा तपासा!",
        "visitWebsite" to "वेबसाइटला भेट द्या",
        "fiscalId" to "कर आयडी",
        "donationInformation" to "देणगी माहिती",
        "bankName" to "बँक",
        "accountHolder" to "खातेधारक",
        "accountNumber" to "खाते क्रमांक",
        "currency" to "चलन",
        "errorLoadingShelters" to "आश्रयस्थाने लोड करताना त्रुटी",
        "pleaseSelectCountry" to "कृपया एक देश निवडा",
        "actions" to "क्रिया",
        "addShelter" to "आश्रयस्थान जोडा",
        "addEditShelter" to "आश्रयस्थान जोडा / संपादित करा",
        "allStates" to "सर्व राज्ये",
        "confirmDelete" to "तुम्हाला खात्री आहे की तुम्ही हे स्थान हटवू इच्छिता?",
        "confirmDeleteShelter" to "तुम्हाला खात्री आहे की तुम्ही हे आश्रयस्थान हटवू इच्छिता?",
        "confirmUnbanUser" to "या वापरकर्त्यावरील बंदी उठवायची?",
        "confirmResetUserCredentials" to "यामुळे {email} चा सध्याचा पासवर्ड आणि पासकी अवैध होतील आणि त्यांना रीसेट दुवा ईमेल केला जाईल. सुरू ठेवायचे?",
        "confirmDeactivateUser" to "या वापरकर्त्याला निष्क्रिय करायचे? नंतर त्यांना पुन्हा सक्रिय करता येईल.",
        "confirmDeactivatePet" to "हा पाळीव प्राणी निष्क्रिय करायचा? तो सार्वजनिक याद्यांमधून लपवला जाईल आणि नंतर पुन्हा सक्रिय करता येईल.",
        "confirmBlockRescuer" to "या बचावकर्त्याला तुम्हाला आणखी विनंत्या पाठवण्यापासून रोखायचे?",
        "confirmDeletePet" to "हा पाळीव प्राणी हटवायचा?",
        "confirmDeleteStorage" to "हा स्टोरेज हटवायचा?",
        "contact" to "संपर्क",
        "delete" to "हटवा",
        "edit" to "संपादित करा",
        "errorDeletingShelter" to "आश्रयस्थान हटवताना त्रुटी",
        "errorLoadingLocations" to "स्थाने लोड करताना त्रुटी",
        "errorLoadingShelter" to "आश्रयस्थान लोड करताना त्रुटी",
        "errorSaving" to "जतन करताना त्रुटी",
        "errorSavingShelter" to "आश्रयस्थान जतन करताना त्रुटी",
        "location" to "स्थान",
        "noLocationsFound" to "कोणतीही स्थाने आढळली नाहीत",
        "selectCountry" to "एक देश निवडा",
        "selectCountryToFilter" to "फिल्टर करण्यासाठी कृपया देश निवडा",
        "shelterAdded" to "आश्रयस्थान यशस्वीरित्या जोडले",
        "shelterDeleted" to "आश्रयस्थान यशस्वीरित्या हटवले",
        "shelterUpdated" to "आश्रयस्थान यशस्वीरित्या अद्यतनित केले",
        "updateShelter" to "आश्रयस्थान अद्यतनित करा",
        "errorLoadingPhotographers" to "छायाचित्रकार लोड करताना त्रुटी",
        "noPhotographersAvailable" to "कोणतेही छायाचित्रकार उपलब्ध नाहीत",
        "requestPhotoSession" to "फोटो सत्राची विनंती करा",
        "selectPhotographer" to "निवडा",
        "photographersSelected" to "{count} निवडले",
        "requestSelectedPhotographers" to "निवडलेल्या छायाचित्रकारांना विनंती करा",
        "clearSelection" to "निवड साफ करा",
        "requestToMultiple" to "यांना विनंती करा",
        "messageRequired" to "कृपया छायाचित्रकारांसाठी संदेश लिहा.",
        "requestsSentSuccessfully" to "विनंत्या यशस्वीरित्या पाठवल्या",
        "message" to "संदेश",
        "sendRequest" to "विनंती पाठवा",
        "requestSentSuccessfully" to "विनंती यशस्वीरित्या पाठवली!",
        "requestTo" to "विनंती",
        "enterMessage" to "{name} साठी संदेश टाका...",
        "reportUrgent" to "तातडीचे",
        "topRescuers" to "अव्वल बचावकर्ते",
        "urgentRescuerSettings" to "तातडीचे बचावकर्ता सेटिंग्ज",
        "urgentRescueAlerts" to "तातडीचे बचाव सूचना",
        "reportUrgentTitle" to "धोक्यात असलेल्या पाळीव प्राण्याची तक्रार करा",
        "reportUrgentIntro" to "जर तुम्हाला जखमी, अत्याचारित, उपाशी किंवा सोडून दिलेला पाळीव प्राणी सापडला असेल ज्याला तातडीने मदतीची गरज आहे, तर आम्हाला कुठे आणि काय चूक आहे ते सांगा — आम्ही लगेच जवळपासच्या तातडीच्या बचावकर्त्यांना सूचित करू.",
        "dangerType" to "काय चूक आहे?",
        "dangerInjured" to "जखमी",
        "dangerAbused" to "अत्याचार / गैरवर्तन केले जात आहे",
        "dangerStarving" to "उपाशी",
        "dangerTooYoung" to "एकट्याने जगण्यासाठी खूप लहान",
        "dangerOther" to "इतर आणीबाणी",
        "useMyLocation" to "माझे सध्याचे स्थान वापरा",
        "confirmMyLocation" to "माझे सध्याचे स्थान निश्चित करा",
        "locationPermissionHint" to "तुमचा ब्राउझर स्थान शेअर करण्याची परवानगी मागेल; ते फक्त तुमच्या जवळच्या अहवालांची सूचना देण्यासाठी वापरले जाते.",
        "phoneOptional" to "तुमचा फोन (ऐच्छिक)",
        "submitReport" to "तक्रार सादर करा",
        "urgentRescuerSettingsTitle" to "तातडीचे बचावकर्ता सेटिंग्ज",
        "urgentRescuerSettingsIntro" to "तुमच्या कव्हरेज क्षेत्राजवळ धोक्यात असलेल्या पाळीव प्राण्याची तक्रार आल्यावर तुम्हाला सूचित केले जाईल. स्वीकारणारा पहिला व्यक्ती केस घेतो.",
        "urgentRescuerActive" to "मी तातडीच्या तक्रारींना प्रतिसाद देण्यासाठी उपलब्ध आहे",
        "coverageAreaMode" to "कव्हरेज क्षेत्र",
        "coverageArea" to "कव्हरेज क्षेत्र",
        "coverageAreaHint" to "तुमचे सध्याचे स्थान पुष्टी करा, पिन ड्रॅग करा, किंवा खाली पत्ता टाइप करा - नकाशा आणि फील्ड समक्रमित राहतील.",
        "useMyLocationAndRadius" to "माझे स्थान + त्रिज्या वापरा",
        "pickAZone" to "त्याऐवजी देश/राज्य/शहर निवडा",
        "radiusKm" to "त्रिज्या (किमी)",
        "urgentRescueAlertsTitle" to "तातडीचे बचाव सूचना",
        "urgentRescuerLeaderboardTitle" to "अव्वल तातडीचे बचावकर्ते (गेल्या १२ महिन्यांतील)",
        "locating" to "स्थान शोधत आहे...",
        "geolocationUnsupported" to "तुमच्या ब्राउझरद्वारे जिओलोकेशन समर्थित नाही",
        "locationCaptured" to "स्थान कॅप्चर केले",
        "locationDenied" to "तुमचे स्थान मिळू शकले नाही - कृपया स्थान प्रवेशास परवानगी द्या किंवा त्याऐवजी शहर टाका",
        "street" to "रस्ता",
        "exteriorNumber" to "बाह्य क्रमांक",
        "referenceNotes" to "संदर्भ टिपा (उदा. घराचा रंग, जवळची खूण)",
        "concreteAddressHint" to "शक्य असल्यास रस्ता आणि क्रमांक जोडा - यामुळेच बचावकर्ता टॅक्सी घेऊ शकतो किंवा थेट त्या ठिकाणी नकाशा अॅप उघडू शकतो.",
        "descriptionRequired" to "कृपया परिस्थितीचे वर्णन करा",
        "locationRequired" to "कृपया स्थान द्या (तुमचे सध्याचे स्थान वापरा किंवा देश आणि शहर टाका)",
        "submittingEllipsis" to "सादर करत आहे...",
        "reportSubmitted" to "तक्रार सादर केली. धन्यवाद - जवळपासच्या बचावकर्त्यांना आता सूचित केले जात आहे.",
        "reportSubmitFailed" to "तक्रार सादर करण्यात अयशस्वी",
        "phoneRequired" to "कृपया फोन नंबर टाका",
        "savingEllipsis" to "जतन करत आहे...",
        "saved" to "जतन केले",
        "saveFailed" to "जतन करण्यात अयशस्वी",
        "noUrgentPages" to "सध्या कोणत्याही प्रलंबित तातडीच्या सूचना नाहीत",
        "accept" to "स्वीकारा",
        "alreadyAccepted" to "या तक्रारीला आधीच दुसऱ्याने स्वीकारले आहे",
        "noLeaderboardData" to "गेल्या १२ महिन्यांत अद्याप कोणतेही स्वीकारलेले बचाव नाहीत",
        "rescueAcceptedTitle" to "बचाव स्वीकारला",
        "rescueAcceptedMessage" to "धन्यवाद - तुम्ही यावर काम करत आहात. शक्य तितक्या लवकर नोंदवलेल्या स्थानी जा.",
        "rescueAcceptFailedTitle" to "स्वीकारता आले नाही",
        "rescueAcceptFailedMessage" to "ही तक्रार आधीच दुसऱ्या बचावकर्त्याने स्वीकारली आहे, किंवा दुवा अवैध किंवा कालबाह्य आहे.",
        "share" to "सामायिक करा",
        "shareViaFacebook" to "Facebook वर शेअर करा",
        "shareViaX" to "X वर शेअर करा",
        "shareViaWhatsapp" to "WhatsApp वर शेअर करा",
        "shareViaEmail" to "ईमेलद्वारे शेअर करा",
        "copyLink" to "लिंक कॉपी करा",
        "linkCopied" to "लिंक कॉपी झाली!",
        "reportLostFoundTitle" to "हरवलेला किंवा सापडलेला पाळीव प्राणी कळवा",
        "reportLostFoundIntro" to "तुमचा पाळीव प्राणी हरवला आहे, की एखादा भटका सापडला आहे? तक्रार दाखल करा आणि आम्ही ती जवळपासच्या इतर तक्रारींशी जुळवून पाहू.",
        "browseLostFound" to "विद्यमान हरवलेले आणि सापडलेले अहवाल ब्राउझ करा",
        "kindLost" to "माझा पाळीव प्राणी हरवला",
        "kindFound" to "मला एक पाळीव प्राणी सापडला",
        "petType" to "पाळीव प्राण्याचा प्रकार",
        "petTypeUnknown" to "खात्री नाही",
        "lostFoundBrowseTitle" to "हरवलेले आणि सापडलेले पाळीव प्राणी",
        "showing" to "दाखवत आहे",
        "kindLostPlural" to "हरवलेले पाळीव प्राणी",
        "kindFoundPlural" to "सापडलेले पाळीव प्राणी",
        "reportResolvedTitle" to "तक्रार सोडवली",
        "reportResolvedMessage" to "अद्ययावतीबद्दल धन्यवाद - ही तक्रार आता बंद आहे.",
        "reportResolveFailedTitle" to "सोडवता आले नाही",
        "reportResolveFailedMessage" to "ही तक्रार आधीच सोडवली गेली आहे, किंवा दुवा अवैध किंवा कालबाह्य आहे.",
        "reportLostFound" to "हरवलेले/\u200Bसापडलेले",
        "noLostFoundReportsFound" to "कोणतेही हरवलेले आणि सापडलेले अहवाल आढळले नाहीत",
        "errorLoadingReports" to "अहवाल लोड करताना त्रुटी",
        "yourEmail" to "तुमचा ईमेल",
        "contactReporter" to "तक्रारदाराशी संपर्क साधा",
        "contactFormIncomplete" to "कृपया तुमचा ईमेल आणि एक संदेश टाका",
        "contactSent" to "संदेश पाठवला",
        "contactFailed" to "संदेश पाठवण्यात अयशस्वी",
        "lostFoundReportSubmitted" to "तक्रार सादर केली. धन्यवाद - आम्ही ती जवळपासच्या इतर तक्रारींशी जुळवून पाहू.",
        "video" to "व्हिडिओ (ऐच्छिक)",
        "removeVideo" to "व्हिडिओ काढा",
        "saveThisSearch" to "हा शोध जतन करा",
        "savedSearchSaved" to "शोध जतन केला! नवीन जुळणी सूचीबद्ध झाल्यावर आम्ही तुम्हाला ईमेल करू.",
        "savedSearchFailed" to "शोध जतन करण्यात अयशस्वी",
        "favorite" to "आवडते",
        "addToFavorites" to "आवडत्यामध्ये जोडा",
        "removeFromFavorites" to "आवडत्यामधून काढा",
        "myFavorites" to "माझे आवडते",
        "noFavoritesYet" to "अद्याप कोणतेही आवडते नाहीत",
        "mySavedSearches" to "माझे जतन केलेले शोध",
        "noSavedSearchesYet" to "अद्याप कोणतेही जतन केलेले शोध नाहीत",
        "donationPromptText" to "आज पाळीव प्राण्याला मदत केल्याबद्दल धन्यवाद. शक्य असल्यास, Adopt-U सर्वांसाठी मोफत ठेवण्यासाठी थोडी देणगी देण्याचा विचार करा.",
        "donationPromptCta" to "देणगी द्या",
        "dismiss" to "बंद करा",
    )

private val teluguTranslations = json(
    "adoptU" to "Adopt-U",
    "login" to "లాగిన్",
    "register" to "నమోదు చేసుకోండి",
    "logout" to "సెషన్‌ను ముగించండి",
    "profile" to "ప్రొఫైల్",
    "myPets" to "నా పెంపుడు జంతువులు",
    "addPet" to "పెంపుడు జంతువును జోడించండి",
    "save" to "సేవ్ చేయండి",
    "cancel" to "రద్దు చేయండి",
    "search" to "శోధించండి",
    "shelters" to "ఆశ్రయాలు",
    "photographers" to "ఫోటోగ్రాఫర్‌లు",
    "temporalHomes" to "తాత్కాలిక నివాసాలు",
    "findTemporalHomes" to "తాత్కాలిక నివాసాలను కనుగొనండి",
    "sterilizationLocations" to "స్టెరిలైజేషన్ ప్రదేశాలు",
    "donate" to "విరాళం ఇవ్వండి",
    "countryLabel" to "దేశం",
    "selectCountryToSearch" to "ఒక దేశాన్ని ఎంచుకోండి",
    "selectCountryFirst" to "ఈ ఫిల్టర్‌లను ఉపయోగించడానికి ఒక దేశాన్ని ఎంచుకోండి",
    "state" to "రాష్ట్రం",
    "city" to "నగరం",
    "zipCode" to "పిన్ కోడ్",
    "neighborhood" to "ప్రాంతం",
    "name" to "పేరు",
    "email" to "ఇమెయిల్",
    "phone" to "ఫోన్",
    "address" to "చిరునామా",
    "description" to "వివరణ",
    "animalShelters" to "జంతు ఆశ్రయాలు",
    "sheltersDescription" to "దత్తత తీసుకోవడానికి పెంపుడు జంతువులను కనుగొనగలిగే లేదా ఆశ్రయంతో ముందస్తు ఒప్పందం ద్వారా వాటిని వదిలివేయగలిగే జంతు ఆశ్రయాలను కనుగొనండి",
    "photographerDescription" to "పెంపుడు జంతువుల ఫోటో సెషన్‌లను అందించే వృత్తిపరమైన ఫోటోగ్రాఫర్‌లు",
    "sterilizationLocationsDescription" to "జంతువులను స్టెరిలైజేషన్ కోసం తీసుకెళ్లగలిగే ప్రదేశాలను కనుగొనండి.",
    "privacyPolicy" to "గోప్యతా విధానం",
    "privacyLastUpdated" to "చివరిగా నవీకరించబడింది: ఏప్రిల్ 2025",
    "privacyHighlight" to "Adopt-Uలో, మేము కనీస డేటా సేకరణను నమ్ముతాము. దత్తత తీసుకునేవారికి మరియు రక్షకులకు మధ్య సంభాషణను సాధ్యం చేయడానికి మేము మీ ఇమెయిల్ చిరునామాను మాత్రమే సేకరిస్తాము. మేము మీ సమాచారాన్ని ప్రకటనల కోసం ఉపయోగించము, మరియు మార్కెటింగ్ ప్రయోజనాల కోసం మీ డేటాను మేము ఎప్పటికీ మూడవ పక్షాలతో పంచుకోము.",
    "privacyInfoCollectedH2" to "మేము సేకరించే సమాచారం",
    "privacyAccountRegH3" to "ఖాతా నమోదు",
    "privacyAccountRegIntro" to "Adopt-Uలో ఖాతాను సృష్టించడానికి, మేము ఈ కింది సమాచారాన్ని సేకరిస్తాము:",
    "privacyEmailLabel" to "ఇమెయిల్ చిరునామా: ",
    "privacyEmailDesc" to "ఖాతా ధృవీకరణ, పాస్‌వర్డ్ రికవరీ మరియు దత్తత తీసుకునేవారికి, రక్షకులకు మధ్య సంభాషణ కోసం అవసరం",
    "privacyDisplayNameLabel" to "ప్రదర్శన పేరు: ",
    "privacyDisplayNameDesc" to "ప్లాట్‌ఫారమ్‌లో మిమ్మల్ని గుర్తించడానికి మీరు ఎంచుకున్న పేరు",
    "privacyLangPrefLabel" to "భాషా ప్రాధాన్యత: ",
    "privacyLangPrefDesc" to "మీకు ఇష్టమైన భాషలో ప్లాట్‌ఫారమ్‌ను అందించడానికి",
    "privacyNoFullNameEtc" to "మేము మీ పూర్తి పేరు, ఫోన్ నంబర్, భౌతిక చిరునామా, గుర్తింపు పత్రాలు, చెల్లింపు సమాచారం లేదా పైన పేర్కొన్న దానికి మించి ఏ ఇతర వ్యక్తిగత డేటాను సేకరించము.",
    "privacyOptionalProfileH3" to "ఐచ్ఛిక ప్రొఫైల్ సమాచారం",
    "privacyOptionalProfileIntro" to "మీ పాత్రను బట్టి, మీరు ఐచ్ఛికంగా అందించవచ్చు:",
    "privacyAdopterLabel" to "దత్తత తీసుకునేవారిగా: ",
    "privacyAdopterDesc" to "అదనపు సమాచారం అవసరం లేదు లేదా సేకరించబడదు",
    "privacyRescuerLabel" to "రక్షకుడిగా: ",
    "privacyRescuerDesc" to "మీ వద్ద దత్తత కోసం ఉన్న పెంపుడు జంతువుల స్థాన సమాచారం",
    "privacyPhotographerLabel" to "ఫోటోగ్రాఫర్‌గా: ",
    "privacyPhotographerDesc" to "సేవా స్థానం మరియు సంప్రదింపు ప్రాధాన్యతలు",
    "privacyTemporalHomeLabel" to "తాత్కాలిక నివాసంగా: ",
    "privacyTemporalHomeDesc" to "మీరు తాత్కాలిక నివాసం అందించగల ప్రదేశం",
    "privacyLocationUsage" to "అన్ని స్థాన సమాచారం దత్తత తీసుకునేవారిని సమీపంలోని రక్షకులు మరియు సేవా ప్రదాతలతో సరిపోల్చడానికి మాత్రమే ఉపయోగించబడుతుంది.",
    "privacyPetInfoH3" to "పెంపుడు జంతువు సమాచారం",
    "privacyPetInfoIntro" to "రక్షకులు పెంపుడు జంతువుల గురించి సమాచారాన్ని పోస్ట్ చేయవచ్చు, వాటిలో:",
    "privacyPetPhotos" to "పెంపుడు జంతువు ఫోటోలు",
    "privacyPetDescBreedAge" to "వివరణ, జాతి, వయస్సు మరియు లక్షణాలు",
    "privacyPetHealth" to "ఆరోగ్యం మరియు టీకా స్థితి",
    "privacyPetLocation" to "పెంపుడు జంతువు అందుబాటులో ఉన్న ప్రదేశం",
    "privacyPetInfoNotVerified" to "ఈ సమాచారం రక్షకులచే అందించబడుతుంది మరియు Adopt-U ద్వారా స్వతంత్రంగా ధృవీకరించబడదు.",
    "privacyHowWeUseH2" to "మేము మీ సమాచారాన్ని ఎలా ఉపయోగిస్తాము",
    "privacyPrimaryPurposeH3" to "ప్రాథమిక ఉద్దేశ్యం: దత్తత సంబంధాలను సులభతరం చేయడం",
    "privacyEmailUsedFor" to "మీ ఇమెయిల్ చిరునామా కేవలం ఈ కింది ప్రయోజనాల కోసం ఉపయోగించబడుతుంది:",
    "privacyUseVerification" to "మీరు ఖాతాను సృష్టించినప్పుడు మీకు ధృవీకరణ లింక్‌ను పంపడం",
    "privacyUseRescuerContact" to "మీకు ఆసక్తి ఉన్న పెంపుడు జంతువుల గురించి రక్షకులు మిమ్మల్ని సంప్రదించడానికి అనుమతించడం",
    "privacyUseContactRescuer" to "వారి జాబితా చేసిన పెంపుడు జంతువుల గురించి మీరు రక్షకులను సంప్రదించడానికి అనుమతించడం",
    "privacyUseNotifications" to "మీకు ముఖ్యమైన ఖాతా మరియు భద్రతా నోటిఫికేషన్‌లను పంపడం",
    "privacyOnceConnected" to "మీరు మా ప్లాట్‌ఫారమ్ ద్వారా మరొక వినియోగదారుతో అనుసంధానమైన తర్వాత, తదుపరి అన్ని సంభాషణలు నేరుగా మీ మరియు ఆ వ్యక్తి మధ్య జరుగుతాయి. మీ సంభాషణల కంటెంట్‌ను మేము పర్యవేక్షించము, నిల్వ చేయము లేదా యాక్సెస్ కలిగి ఉండము.",
    "privacyNeverDoH3" to "మేము ఎప్పటికీ చేయని పనులు",
    "privacyCommitIntro" to "మేము ఈ కింది వాటికి గంభీరంగా కట్టుబడి ఉన్నాము:",
    "privacyNoAdsLabel" to "ప్రకటనలు లేవు: ",
    "privacyNoAdsDesc" to "మీ కార్యకలాపాల ఆధారంగా లక్ష్యిత లేదా లక్ష్యితం కాని ప్రకటనలను మేము ఎప్పటికీ ప్రదర్శించము",
    "privacyNoMarketingLabel" to "మార్కెటింగ్ లేదు: ",
    "privacyNoMarketingDesc" to "మార్కెటింగ్ లేదా ప్రచార ప్రయోజనాల కోసం మీ ఇమెయిల్‌ను మేము మూడవ పక్షాలతో ఎప్పటికీ పంచుకోము",
    "privacyNoSellingLabel" to "డేటా అమ్మకం లేదు: ",
    "privacyNoSellingDesc" to "మీ వ్యక్తిగత సమాచారాన్ని ఏ మూడవ పక్షానికి మేము ఎప్పటికీ అమ్మము, అద్దెకు ఇవ్వము లేదా బదిలీ చేయము",
    "privacyNoProfilingLabel" to "ప్రొఫైలింగ్ లేదు: ",
    "privacyNoProfilingDesc" to "ప్రాథమిక ఖాతా నిర్వహణకు మించిన ఏ ప్రయోజనం కోసం మీ డేటాతో ప్రొఫైల్‌లను రూపొందించడానికి మేము ఉపయోగించము",
    "privacyNoTrackingLabel" to "ట్రాకింగ్ లేదు: ",
    "privacyNoTrackingDesc" to "మేము ట్రాకింగ్ పిక్సెల్‌లు, ప్రకటనల కోసం కుకీలు లేదా ఏవైనా ఇలాంటి ట్రాకింగ్ సాంకేతికతలను ఉపయోగించము",
    "privacyDataStorageH2" to "డేటా నిల్వ మరియు భద్రత",
    "privacyDataStoredSecurely" to "మీ డేటా పరిశ్రమ-ప్రామాణిక ఎన్‌క్రిప్షన్‌ను ఉపయోగించి సురక్షితంగా నిల్వ చేయబడుతుంది. అనధికార యాక్సెస్, మార్పు, బహిర్గతం లేదా నాశనం నుండి మీ వ్యక్తిగత సమాచారాన్ని రక్షించడానికి మేము తగిన సాంకేతిక మరియు సంస్థాగత చర్యలను అమలు చేస్తాము.",
    "privacyDataRetention" to "మీ ఖాతా చురుకుగా ఉన్నంత కాలం మేము మీ ఖాతా సమాచారాన్ని నిలుపుకుంటాము. మమ్మల్ని సంప్రదించడం ద్వారా మీరు ఎప్పుడైనా మీ ఖాతా మరియు అనుబంధిత డేటాను తొలగించమని అభ్యర్థించవచ్చు.",
    "privacyYourRightsH2" to "మీ హక్కులు",
    "privacyRightsIntro" to "మీ వ్యక్తిగత డేటాకు సంబంధించి మీకు ఈ కింది హక్కులు ఉన్నాయి:",
    "privacyRightAccess" to "యాక్సెస్: మేము మీ గురించి కలిగి ఉన్న అన్ని వ్యక్తిగత డేటా కాపీని మీరు అభ్యర్థించవచ్చు",
    "privacyRightCorrection" to "సవరణ: ఏదైనా తప్పు సమాచారాన్ని సరిదిద్దమని మీరు మమ్మల్ని అభ్యర్థించవచ్చు",
    "privacyRightDeletion" to "తొలగింపు: మీ ఖాతా మరియు అనుబంధిత అన్ని డేటాను తొలగించమని మీరు మమ్మల్ని అభ్యర్థించవచ్చు",
    "privacyRightPortability" to "పోర్టబిలిటీ: సాధారణంగా ఉపయోగించే ఫార్మాట్‌లో మీ డేటాను అందించమని మీరు మమ్మల్ని అభ్యర్థించవచ్చు",
    "privacyExerciseRights" to "ఈ హక్కులలో దేనినైనా వినియోగించుకోవడానికి, దయచేసి adopt-u@adopt-u.org వద్ద మమ్మల్ని సంప్రదించండి.",
    "privacyCookiesH2" to "కుకీలు",
    "privacyCookiesText" to "Adopt-U సెషన్ నిర్వహణ మరియు భాషా ప్రాధాన్యతలతో సహా ప్లాట్‌ఫారమ్ కార్యాచరణకు అవసరమైన కనీస కుకీలను ఉపయోగిస్తుంది. మేము ప్రకటన కుకీలను లేదా ట్రాకింగ్ కుకీలను ఉపయోగించము.",
    "privacyThirdPartyH2" to "మూడవ పక్ష సేవలు",
    "privacyThirdPartyIntro" to "మీ డేటాను ప్రాసెస్ చేయగల ఈ కింది మూడవ పక్ష సేవలను మేము ఉపయోగిస్తాము:",
    "privacyAws" to "AWS (Amazon Web Services): డేటా నిల్వ మరియు ఇమెయిల్ డెలివరీ కోసం. వారి డేటా ప్రాసెసింగ్‌కు వారి గోప్యతా విధానాలు వర్తిస్తాయి.",
    "privacyWebauthn" to "WebAuthn/FIDO2: సురక్షిత పాస్‌కీ ప్రామాణీకరణ కోసం. దీనిలో మీ బ్రౌజర్‌లో క్రిప్టోగ్రాఫిక్ కార్యకలాపాలు ఉంటాయి.",
    "privacyThirdPartySelected" to "డేటా గోప్యత పట్ల మా నిబద్ధతను పంచుకునే సేవా ప్రదాతలను మేము జాగ్రత్తగా ఎంచుకున్నాము.",
    "privacyChildrensH2" to "పిల్లల గోప్యత",
    "privacyChildrensText" to "Adopt-U 18 సంవత్సరాల కంటే తక్కువ వయస్సు గల వినియోగదారుల కోసం ఉద్దేశించబడలేదు. మేము తెలిసి మైనర్ల నుండి సమాచారాన్ని సేకరించము. ఒక మైనర్ ఖాతాను సృష్టించారని మీరు నమ్మితే, దయచేసి దానిని తొలగించడానికి మమ్మల్ని సంప్రదించండి.",
    "privacyChangesH2" to "ఈ విధానానికి మార్పులు",
    "privacyChangesText" to "మా పద్ధతులు లేదా చట్టపరమైన అవసరాలలో మార్పులను ప్రతిబింబించడానికి మేము ఎప్పటికప్పుడు ఈ గోప్యతా విధానాన్ని నవీకరించవచ్చు. ముఖ్యమైన మార్పుల గురించి మేము ఇమెయిల్ ద్వారా లేదా ప్లాట్‌ఫారమ్‌లో ప్రముఖమైన నోటీసు ద్వారా వినియోగదారులకు తెలియజేస్తాము.",
    "privacyContactH2" to "మమ్మల్ని సంప్రదించండి",
    "privacyContactText" to "ఈ గోప్యతా విధానం గురించి మీకు ఏవైనా ప్రశ్నలు లేదా ఆందోళనలు ఉంటే, లేదా మీ డేటా హక్కులను వినియోగించుకోవడానికి, దయచేసి adopt-u@adopt-u.org వద్ద మమ్మల్ని సంప్రదించండి.",
    "termsConditions" to "నిబంధనలు మరియు షరతులు",
    "installApp" to "యాప్‌ను ఇన్‌స్టాల్ చేయండి",
    "termsAcceptanceH2" to "నిబంధనల అంగీకారం",
    "termsAcceptanceText" to "Adopt-Uను యాక్సెస్ చేయడం మరియు ఉపయోగించడం ద్వారా, మీరు ఈ ఒప్పందం యొక్క నిబంధనలు మరియు నిబంధనలచే కట్టుబడి ఉండటానికి అంగీకరిస్తారు మరియు ఒప్పుకుంటారు. మీరు ఈ నిబంధనలతో ఏకీభవించకపోతే, దయచేసి మా ప్లాట్‌ఫారమ్‌ను ఉపయోగించవద్దు.",
    "termsNatureH2" to "మా సేవ యొక్క స్వభావం",
    "termsNatureText1" to "Adopt-U అనేది పెంపుడు జంతువుల రక్షకులు మరియు సంభావ్య దత్తత తీసుకునేవారి మధ్య సంబంధాన్ని సులభతరం చేసే ఒక అనుసంధాన ప్లాట్‌ఫారమ్. మేము పెంపుడు జంతువుల దుకాణం, బ్రోకర్, ఆశ్రయం లేదా దత్తత సంస్థ కాదు. మా ప్లాట్‌ఫారమ్‌లో జాబితా చేయబడిన ఏ పెంపుడు జంతువులనైనా మేము స్వంతం చేసుకోము, సంరక్షణలో ఉంచుకోము లేదా మధ్యవర్తిగా నిర్వహించము.",
    "termsNatureText2" to "మా పాత్ర పార్టీల మధ్య సంభాషణ మార్గాన్ని అందించడానికి మాత్రమే పరిమితం చేయబడింది. దత్తత చర్చలు, ఒప్పందాలు లేదా జంతువుల భౌతిక బదిలీలో మేము పాల్గొనము.",
    "termsUserRespH2" to "వినియోగదారు బాధ్యతలు",
    "termsForRescuersH3" to "రక్షకుల కోసం:",
    "termsRescuerResp1" to "మీ సంరక్షణలో ఉన్న పెంపుడు జంతువుల గురించి ఖచ్చితమైన, నిజాయితీ గల సమాచారాన్ని అందించండి",
    "termsRescuerResp2" to "జాబితా చేయడానికి ముందు పెంపుడు జంతువులు ఆరోగ్యంగా, టీకాలు వేయబడి మరియు తగినంతగా పరీక్షించబడి ఉండేలా చూసుకోండి",
    "termsRescuerResp3" to "సంభావ్య దత్తత తీసుకునేవారి బాధ్యతాయుతమైన స్క్రీనింగ్ నిర్వహించండి",
    "termsRescuerResp4" to "పెంపుడు జంతువులను వారి కొత్త ఇళ్లకు సురక్షితంగా బదిలీ చేయడానికి ఏర్పాట్లు చేయండి",
    "termsRescuerResp5" to "అందించిన అన్ని సమాచారం యొక్క ఖచ్చితత్వానికి బాధ్యత వహించండి",
    "termsRescuerResp6" to "పెంపుడు జంతువుల దత్తతకు సంబంధించిన అన్ని స్థానిక చట్టాలు మరియు నిబంధనలను పాటించండి",
    "termsRescuerResp7" to "వాణిజ్య పెంపుడు జంతువుల అమ్మకం లేదా సంతానోత్పత్తి ప్రయోజనాల కోసం ప్లాట్‌ఫారమ్‌ను ఎప్పటికీ ఉపయోగించవద్దు",
    "termsForAdoptersH3" to "దత్తత తీసుకునేవారి కోసం:",
    "termsAdopterResp1" to "మీ నివాస పరిస్థితి మరియు పెంపుడు జంతువులతో అనుభవం గురించి ఖచ్చితమైన సమాచారాన్ని అందించండి",
    "termsAdopterResp2" to "సరైన సంరక్షణ అందించే మీ సామర్థ్యాన్ని ప్రదర్శించడానికి సిద్ధంగా ఉండండి",
    "termsAdopterResp3" to "పెంపుడు జంతువు దత్తతను రద్దు చేయడం జంతువుకు గణనీయమైన ఒత్తిడిని కలిగిస్తుందని అర్థం చేసుకోండి",
    "termsAdopterResp4" to "మీ దత్తత అభ్యర్థనను తిరస్కరించే రక్షకుని హక్కును గౌరవించండి",
    "termsPetListingsH2" to "పెంపుడు జంతువుల జాబితాలు మరియు సమాచారం",
    "termsPetListingsText1" to "పెంపుడు జంతువుల జాబితాలలో అందించిన సమాచారం యొక్క ఖచ్చితత్వాన్ని Adopt-U ధృవీకరించదు, ఆమోదించదు లేదా హామీ ఇవ్వదు. జాతి, వయస్సు, ఆరోగ్య స్థితి, స్వభావం మరియు టీకా రికార్డులతో సహా కానీ వాటికే పరిమితం కాని సమాచారం రక్షకులచే అందించబడుతుంది మరియు స్వతంత్రంగా ధృవీకరించబడలేదు.",
    "termsPetListingsText2" to "ఫోటోలు మరియు వివరణలు రక్షకులచే అందించబడతాయి. ఉద్దేశపూర్వక తప్పుడు ప్రాతినిధ్యాన్ని మేము నిషేధించినప్పటికీ, అన్ని జాబితాలు జంతువు యొక్క ప్రస్తుత స్థితిని ఖచ్చితంగా ప్రాతినిధ్యం వహిస్తాయని మేము హామీ ఇవ్వలేము.",
    "termsPetListingsText3" to "ఏదైనా దత్తత ఏర్పాటును ఖరారు చేయడానికి ముందు ప్రశ్నలు అడగాలని, పశువైద్య రికార్డులను అభ్యర్థించాలని మరియు పెంపుడు జంతువులను కలవాలని వినియోగదారులను ప్రోత్సహిస్తారు.",
    "termsAdoptionProcessH2" to "దత్తత ప్రక్రియ",
    "termsAdoptionProcessIntro" to "ఒక రక్షకుడు మరియు దత్తత తీసుకునేవారు దత్తతతో ముందుకు సాగడానికి అంగీకరించిన తర్వాత:",
    "termsAdoptionStep1" to "బదిలీ తేదీ, స్థానం మరియు ఏవైనా అనుబంధిత ఖర్చులతో సహా అన్ని ఏర్పాట్లు పార్టీల మధ్య నేరుగా చర్చించబడతాయి",
    "termsAdoptionStep2" to "Adopt-U ఏ చెల్లింపు లావాదేవీలను సులభతరం చేయదు లేదా హామీ ఇవ్వదు",
    "termsAdoptionStep3" to "ప్లాట్‌ఫారమ్ దత్తత ఒప్పందాలను అందించదు; ఏవైనా ఒప్పంద ఏర్పాట్లకు సంబంధించిన పార్టీలదే పూర్తి బాధ్యత",
    "termsAdoptionStep4" to "అన్ని ఒప్పందాలను వ్రాతపూర్వకంగా నమోదు చేయమని మేము గట్టిగా సిఫార్సు చేస్తున్నాము",
    "termsLiabilityH2" to "బాధ్యతా పరిమితి",
    "termsLiabilityIntro" to "Adopt-U ఈ కింది వాటికి బాధ్యత వహించదు:",
    "termsLiability1" to "రక్షకులు మరియు దత్తత తీసుకునేవారి మధ్య ఏవైనా వివాదాలు, భేదాభిప్రాయాలు లేదా సంఘర్షణలు",
    "termsLiability2" to "పెంపుడు జంతువుల జాబితాలలో అందించిన సమాచారం యొక్క ఖచ్చితత్వం",
    "termsLiability3" to "దత్తతకు ముందు, సమయంలో లేదా తర్వాత ఏదైనా జంతువు యొక్క ఆరోగ్యం, ప్రవర్తన లేదా స్థితి",
    "termsLiability4" to "ప్లాట్‌ఫారమ్ వినియోగం నుండి ఉత్పన్నమయ్యే ఏవైనా ఆర్థిక నష్టాలు, నష్టాలు లేదా గాయాలు",
    "termsLiability5" to "రిటర్న్‌లు, ఆరోగ్య సమస్యలు లేదా అనుకూలత సమస్యలతో సహా కానీ వాటికే పరిమితం కాని ఏదైనా దత్తత ఫలితం",
    "termsLiability6" to "సంప్రదింపు ఏర్పడిన తర్వాత ప్లాట్‌ఫారమ్ వెలుపల వినియోగదారులు తీసుకున్న ఏవైనా చర్యలు",
    "termsLiabilityAck" to "పెంపుడు జంతువును దత్తత తీసుకోవడం ఒక ముఖ్యమైన నిబద్ధత అని మరియు దత్తత తీసుకోవాలనే తమ నిర్ణయానికి పూర్తి బాధ్యత తామే వహిస్తామని వినియోగదారులు అంగీకరిస్తారు.",
    "termsProhibitedH2" to "నిషేధిత కార్యకలాపాలు",
    "termsProhibitedIntro" to "Adopt-Uలో ఈ కింది కార్యకలాపాలు ఖచ్చితంగా నిషేధించబడ్డాయి:",
    "termsProhibited1" to "వాణిజ్య పెంపుడు జంతువుల అమ్మకాలు, సంతానోత్పత్తి కార్యకలాపాలు లేదా పప్పీ మిల్లులు",
    "termsProhibited2" to "మీరు నేరుగా సంరక్షించని పెంపుడు జంతువులను జాబితా చేయడం",
    "termsProhibited3" to "జాబితాలు లేదా ప్రొఫైల్‌లలో తప్పుడు లేదా తప్పుదోవ పట్టించే సమాచారం",
    "termsProhibited4" to "ఇతర వినియోగదారులతో వేధింపులు, వివక్ష లేదా అనుచిత సంప్రదింపు",
    "termsProhibited5" to "పెంపుడు జంతువుల దత్తతకు సంబంధం లేని ప్రయోజనాల కోసం వ్యక్తిగత సమాచారాన్ని సేకరించడానికి ప్లాట్‌ఫారమ్‌ను ఉపయోగించడం",
    "termsProhibited6" to "ఏవైనా చట్టవిరుద్ధ కార్యకలాపాలు లేదా జంతు సంక్షేమ చట్టాల ఉల్లంఘనలు",
    "termsAccountTerminationH2" to "ఖాతా రద్దు",
    "termsAccountTerminationText" to "ఈ నిబంధనలను ఉల్లంఘించే, నిషేధిత కార్యకలాపాలలో పాల్గొనే లేదా ప్లాట్‌ఫారమ్‌ను అప్రతిష్ఠపాలు చేసే ఖాతాలను నిలిపివేయడానికి లేదా రద్దు చేయడానికి Adopt-U హక్కును కలిగి ఉంది. తప్పుడు, తప్పుదోవ పట్టించే లేదా అనుచితమైన కంటెంట్‌ను కలిగి ఉన్న జాబితాలను కూడా మేము తొలగించవచ్చు.",
    "termsModificationsH2" to "నిబంధనలకు మార్పులు",
    "termsModificationsText" to "ఏ సమయంలోనైనా ఈ నిబంధనలను సవరించే హక్కును మేము కలిగి ఉన్నాము. మార్పుల తర్వాత ప్లాట్‌ఫారమ్‌ను కొనసాగించి ఉపయోగించడం సవరించిన నిబంధనల అంగీకారాన్ని సూచిస్తుంది. ముఖ్యమైన మార్పుల గురించి మేము ఇమెయిల్ ద్వారా లేదా ప్లాట్‌ఫారమ్ ప్రకటనల ద్వారా వినియోగదారులకు తెలియజేస్తాము.",
    "termsContactText" to "ఈ నిబంధనలు మరియు షరతుల గురించి మీకు ఏవైనా ప్రశ్నలు ఉంటే, దయచేసి adopt-u@adopt-u.org వద్ద మమ్మల్ని సంప్రదించండి.",
    "language" to "భాష",
    "displayName" to "ప్రదర్శన పేరు",
    "saveProfile" to "ప్రొఫైల్‌ను సేవ్ చేయండి",
    "accountSettings" to "ఖాతా సెట్టింగ్‌లు",
    "yourRoles" to "మీ పాత్రలు:",
    "resources" to "వనరులు",
    "loginWithPasskey" to "పాస్‌కీతో లాగిన్ చేయండి",
    "signInWithPasskey" to "పాస్‌కీతో సైన్ ఇన్ చేయండి",
    "resendVerificationEmail" to "ధృవీకరణ ఇమెయిల్‌ను మళ్లీ పంపండి",
    "usesFido" to "సురక్షితమైన పాస్‌వర్డ్-రహిత ప్రామాణీకరణ కోసం FIDO2 / WebAuthn ఉపయోగిస్తుంది.",
    "loginWithMagicLink" to "ఇమెయిల్ లింక్‌తో లాగిన్ చేయండి",
    "magicLinkDesc" to "మేము మీ ఇమెయిల్‌కు లాగిన్ లింక్‌ను పంపుతాము (5 నిమిషాల పాటు చెల్లుబాటు అవుతుంది).",
    "sendMagicLink" to "లాగిన్ లింక్‌ను పంపండి",
    "loginWithPassword" to "పాస్‌వర్డ్‌తో లాగిన్ చేయండి",
    "signIn" to "సైన్ ఇన్ చేయండి",
    "forgotPassword" to "పాస్‌వర్డ్ మర్చిపోయారా?",
    "registerNewAccount" to "ఖాతాను సృష్టించండి",
    "accountDetails" to "ఖాతా వివరాలు",
    "registrationMethod" to "నమోదు పద్ధతి",
    "selectAdditionalRoles" to "అదనపు పాత్రలను ఎంచుకోండి:",
    "adoptPet" to "పెంపుడు జంతువును దత్తత తీసుకోండి",
    "publishPets" to "దత్తత కోసం పెంపుడు జంతువులను ప్రచురించండి",
    "offerPhotography" to "ఫోటోగ్రఫీ సేవలను అందించండి",
    "photographerSettings" to "ఫోటోగ్రాఫర్ సెట్టింగ్‌లు",
    "sessionFee" to "సెషన్ ఫీజు",
    "provideTemporaryHome" to "పెంపుడు జంతువుల కోసం తాత్కాలిక నివాసం అందించండి",
    "passkeyNoPassword" to "పాస్‌కీలు పాస్‌వర్డ్‌ల కంటే సురక్షితమైనవి మరియు మీ అన్ని పరికరాలలో పనిచేస్తాయి.",
    "registerWithPasskey" to "పాస్‌కీతో నమోదు చేయండి",
    "passkeySettings" to "పాస్‌కీ సెట్టింగ్‌లు",
    "password" to "పాస్‌వర్డ్",
    "confirmPassword" to "పాస్‌వర్డ్‌ను నిర్ధారించండి",
    "passwordRequirements" to "పాస్‌వర్డ్ కనీసం 8 అక్షరాలు కలిగి ఉండాలి మరియు పెద్ద అక్షరం, చిన్న అక్షరం, సంఖ్య మరియు గుర్తును కలిగి ఉండాలి",
    "securityInfo" to "భద్రతా సమాచారం",
    "passkeyMostSecure" to "పాస్‌కీ: అత్యంత సురక్షితం, సిఫార్సు చేయబడింది",
    "passkeyBrowserCompat" to "పాస్‌కీ అన్ని బ్రౌజర్‌లలో పనిచేయకపోవచ్చు",
    "passwordLeastSecure" to "పాస్‌వర్డ్: తక్కువ సురక్షితం, సిఫార్సు చేయబడలేదు",
    "selectLoginMethods" to "ఒకటి లేదా అంతకంటే ఎక్కువ లాగిన్ పద్ధతులను ఎంచుకోండి:",
    "registerPasskeyOption" to "పాస్‌కీ (అత్యంత సురక్షితం, మీ అన్ని పరికరాలలో పనిచేస్తుంది)",
    "registerPasswordOption" to "పాస్‌వర్డ్ (తక్కువ సురక్షితం, బ్యాకప్‌గా మాత్రమే ఉపయోగించండి)",
    "howPasskeysWorkTitle" to "పాస్‌కీ ఎలా పనిచేస్తుంది?",
    "howPasskeysWorkIntro" to "పాస్‌కీ మీ పాస్‌వర్డ్‌ను మీ పరికరం యొక్క అంతర్నిర్మిత భద్రతతో భర్తీ చేస్తుంది — వేలిముద్ర, ముఖ గుర్తింపు లేదా స్క్రీన్ లాక్ పిన్.",
    "howPasskeysWorkSecurity" to "ఇది మీ పరికరంలో మాత్రమే నిల్వ చేయబడుతుంది, ఫిషింగ్ చేయబడదు లేదా డేటా ఉల్లంఘనలో లీక్ కాదు మరియు మీ పరికరాలలో స్వయంచాలకంగా సమకాలీకరించబడుతుంది.",
    "howPasskeysWorkBackup" to "మీరు పాస్‌వర్డ్‌ను కూడా జోడించవచ్చు, కానీ సైన్ ఇన్ చేయడానికి ఒక్క పాస్‌కీ మాత్రమే సరిపోతుంది — పాస్‌వర్డ్ అవసరం లేదు.",
    "gotIt" to "అర్థమైంది",
    "alreadyHaveAccount" to "ఇప్పటికే ఖాతా ఉందా?",
    "all" to "అన్నీ",
    "filterDogs" to "కుక్కలు",
    "filterCats" to "పిల్లులు",
    "filterBirds" to "పక్షులు",
    "filterFish" to "చేపలు",
    "filterAllSexOption" to "అన్ని లింగాలు",
    "filterMaleOption" to "♂ మగ",
    "filterFemaleOption" to "♀ ఆడ",
    "age" to "వయస్సు",
    "rescued" to "రక్షించబడింది",
    "years" to "సంవత్సరాలు",
    "months" to "నెలలు",
    "year" to "సంవత్సరం",
    "month" to "నెల",
    "loginToAdopt" to "దత్తత అభ్యర్థించడానికి లాగిన్ అవ్వండి",
    "requiredMarker" to "(అవసరం)",
    "pageNotFoundTitle" to "పేజీ కనబడలేదు",
    "pageNotFoundHint" to "లింక్ పాతది లేదా తప్పుగా ఉండవచ్చు. పెంపుడు జంతువులు ఇంకా ఇక్కడే ఉన్నాయి.",
    "browsePets" to "పెంపుడు జంతువులను చూడండి",
    "requestsFromRescuers" to "రక్షకుల నుండి అభ్యర్థనలు",
    "signingOut" to "సైన్ అవుట్ అవుతోంది...",
    "adopter" to "దత్తత తీసుకునేవారు",
    "country" to "దేశం",
    "zip" to "పిన్ కోడ్",
    "viewPublic" to "పబ్లిక్ పేజీ చూడండి",
    "banUser" to "వినియోగదారుని నిషేధించండి",
    "banReason" to "కారణం (ఐచ్ఛికం)",
    "myPhotoRequests" to "నా ఫోటో సెషన్ అభ్యర్థనలు",
    "noPhotoRequestsYet" to "మీరు ఇంకా ఫోటో సెషన్ అభ్యర్థించలేదు.",
    "photoRequestsReceived" to "అందిన ఫోటో సెషన్ అభ్యర్థనలు",
    "noPhotoRequestsReceived" to "ఇంకా ఫోటో సెషన్ అభ్యర్థనలు లేవు.",
    "photoStatusPending" to "పెండింగ్",
    "photoStatusApproved" to "ఆమోదించబడింది",
    "photoStatusRejected" to "తిరస్కరించబడింది",
    "photoStatusCancelled" to "రద్దు చేయబడింది",
    "photoStatusCompleted" to "పూర్తయింది",
    "cancelRequest" to "అభ్యర్థనను రద్దు చేయండి",
    "registerConsentPrefix" to "ఖాతా సృష్టించడం ద్వారా మీరు అంగీకరిస్తున్నారు:",
    "registerConsentTerms" to "నిబంధనలు మరియు షరతులు",
    "registerConsentAnd" to "మరియు",
    "registerConsentPrivacy" to "గోప్యతా విధానం",
    "petStatusAvailable" to "అందుబాటులో ఉంది",
    "petStatusAdopted" to "దత్తత తీసుకోబడింది",
    "petStatusDisabled" to "నిలిపివేయబడింది",
    "petStatusPending" to "పెండింగ్‌లో ఉంది",
    "addEditLocation" to "స్థానాన్ని జోడించండి/సవరించండి",
    "addLocation" to "స్థానాన్ని జోడించండి",
    "manage" to "నిర్వహణ",
    "adminPanel" to "అడ్మిన్ ప్యానెల్",
    "adminSterilizationLocations" to "స్టెరిలైజేషన్ ప్రదేశాలను నిర్వహించండి",
    "adopterRequired" to "దత్తత తీసుకునేవారు (అవసరం)",
    "adoptionFee" to "దత్తత ఫీజు",
    "adoptionRequests" to "దత్తత అభ్యర్థనలు",
    "ageMonths" to "వయస్సు (నెలలు)",
    "ageYears" to "వయస్సు (సంవత్సరాలు)",
    "alias" to "మారుపేరు",
    "bird" to "పక్షి",
    "breed" to "జాతి",
    "medicalEventsOverview" to "వైద్య సంఘటనల అవలోకనం",
    "back" to "వెనుకకు",
    "cat" to "పిల్లి",
    "color" to "రంగు",
    "compatibility" to "అనుకూలత",
    "currentPassword" to "ప్రస్తుత పాస్‌వర్డ్",
    "dog" to "కుక్క",
    "emailChange" to "ఇమెయిల్‌ను మార్చండి",
    "emailChangeDesc" to "మీ ఇమెయిల్ చిరునామాను నవీకరించండి. కొత్త ఇమెయిల్‌కు ధృవీకరణ లింక్ పంపబడుతుంది.",
    "energyLevel" to "శక్తి స్థాయి",
    "female" to "ఆడ",
    "findTemporalHome" to "తాత్కాలిక నివాసాన్ని కనుగొనండి",
    "fish" to "చేప",
    "goodWithCats" to "పిల్లులతో బాగా ఉంటుంది",
    "goodWithDogs" to "కుక్కలతో బాగా ఉంటుంది",
    "goodWithKids" to "పిల్లలతో బాగా ఉంటుంది",
    "high" to "అధికం",
    "houseTrained" to "ఇంటి శిక్షణ పొందింది",
    "large" to "పెద్దది",
    "low" to "తక్కువ",
    "male" to "మగ",
    "managePets" to "పెంపుడు జంతువులను నిర్వహించండి",
    "managePetsBtn" to "పెంపుడు జంతువులను జోడించండి లేదా సవరించండి",
    "managePetsDescription" to "అన్ని పెంపుడు జంతువుల పేజీలను నిర్వహించండి. పెంపుడు జంతువులను జోడించండి లేదా తీసివేయండి.",
    "manageShelters" to "ఆశ్రయాలను నిర్వహించండి",
    "manageSheltersBtn" to "ఆశ్రయాలను జోడించండి లేదా సవరించండి",
    "manageSheltersDescription" to "అన్ని ఆశ్రయాల పేజీలను నిర్వహించండి. ఆశ్రయాలను జోడించండి లేదా తీసివేయండి.",
    "manageUsers" to "వినియోగదారులను నిర్వహించండి",
    "medical" to "వైద్యం",
    "medium" to "మధ్యస్థం",
    "backToSearch" to "శోధనకు తిరిగి వెళ్లండి",
    "contactThisHome" to "ఈ తాత్కాలిక నివాసాన్ని సంప్రదించండి",
    "loginAsRescuerToContact" to "ఈ తాత్కాలిక నివాసానికి అభ్యర్థన పంపడానికి రక్షకుడిగా లాగిన్ చేయండి.",
    "memberSince" to "సభ్యుడిగా చేరిన తేదీ",
    "microchipId" to "మైక్రోచిప్ ID",
    "microchipped" to "మైక్రోచిప్ చేయబడింది",
    "myTemporalHome" to "నా తాత్కాలిక నివాసం",
    "newEmail" to "కొత్త ఇమెయిల్",
    "noTemporalHomes" to "తాత్కాలిక నివాసాలు కనుగొనబడలేదు.",
    "requestSentToHome" to "మీ అభ్యర్థన పంపబడింది!",
    "sendRequestBtn" to "అభ్యర్థన పంపండి",
    "temporalHomeNotFound" to "ఈ తాత్కాలిక నివాసం కనుగొనబడలేదు.",
    "viewDetails" to "వివరాలను చూడండి",
    "rescuerDirectory" to "రక్షకులు",
    "noRescuersFound" to "రక్షకులు కనుగొనబడలేదు.",
    "availablePetsCount" to "అందుబాటులో ఉన్న పెంపుడు జంతువులు",
    "availablePets" to "అందుబాటులో ఉన్న పెంపుడు జంతువులు",
    "noAvailablePetsForRescuer" to "ఈ రక్షకునికి ప్రస్తుతం అందుబాటులో ఉన్న పెంపుడు జంతువులు లేవు.",
    "rescuerNotFound" to "ఈ రక్షకుడు కనుగొనబడలేదు.",
    "errorLoadingResults" to "ఫలితాలను లోడ్ చేయడంలో లోపం.",
    "volunteerForThisRescuer" to "ఈ రక్షకుని కోసం వాలంటీర్‌గా చేరండి",
    "volunteerExplanation" to "ఆమోదించబడిన వాలంటీర్లు ఈ రక్షకుని పెంపుడు జంతువుల జాబితాలకు సవరణలను సూచించవచ్చు.",
    "applyToVolunteerBtn" to "వాలంటీర్‌గా దరఖాస్తు చేయండి",
    "loginToVolunteer" to "ఈ రక్షకుని కోసం వాలంటీర్‌గా దరఖాస్తు చేయడానికి లాగిన్ చేయండి.",
    "volunteerApplicationSent" to "మీ వాలంటీర్ దరఖాస్తు పంపబడింది!",
    "myVolunteering" to "నా వాలంటీరింగ్",
    "noVolunteerApplicationsYet" to "మీరు ఇంకా ఏ రక్షకుని కోసం వాలంటీర్‌గా దరఖాస్తు చేయలేదు.",
    "howToVolunteerHint" to "దరఖాస్తు చేయడానికి, డైరెక్టరీ నుండి ఒక రెస్క్యూయర్ పేజీని తెరిచి \"వాలంటీర్‌గా దరఖాస్తు చేయండి\" నొక్కండి.",
    "volunteerStatusPending" to "పెండింగ్‌లో ఉంది",
    "volunteerStatusActive" to "క్రియాశీలం",
    "volunteerStatusRejected" to "తిరస్కరించబడింది",
    "volunteerApplications" to "వాలంటీర్ దరఖాస్తులు",
    "noVolunteerApplications" to "వాలంటీర్ దరఖాస్తులు లేవు.",
    "petEditSuggestions" to "పెంపుడు జంతువు సవరణ సూచనలు",
    "noPetEditSuggestions" to "పెండింగ్‌లో సవరణ సూచనలు లేవు.",
    "suggestedByLabel" to "సూచించినవారు",
    "suggestAnEditTitle" to "సవరణను సూచించండి",
    "suggestAnEditExplanation" to "ఆమోదించబడిన వాలంటీర్‌గా, మీరు ఈ జాబితాకు మార్పులను సూచించవచ్చు. అవి ప్రత్యక్షంగా వెళ్లడానికి ముందు రక్షకుడు సమీక్షిస్తారు.",
    "suggestEditBtn" to "సూచనను పంపండి",
    "editSuggestionSent" to "మీ సూచన సమీక్ష కోసం రక్షకునికి పంపబడింది!",
    "sponsorThisPetTitle" to "ఈ పెంపుడు జంతువును స్పాన్సర్ చేయండి",
    "sponsorExplanation" to "దత్తత తీసుకోలేకపోయినా ఖర్చులతో సహాయం చేయాలనుకుంటున్నారా? డబ్బు లేదా వస్తు రూప సహాయం అందించి రక్షకునితో నేరుగా అనుసంధానం అవ్వండి.",
    "sponsorThisRescuerTitle" to "ఈ రక్షకుడిని స్పాన్సర్ చేయండి",
    "sponsorRescuerExplanation" to "డబ్బు లేదా వస్తు రూప సహాయంతో ఈ రక్షకుని సాధారణ ఖర్చులకు మద్దతు ఇవ్వండి.",
    "sponsorTypeLabel" to "మీరు ఏమి అందించగలరు?",
    "sponsorTypeMoney" to "డబ్బు",
    "sponsorTypeInKind" to "వస్తు రూపంలో (ఆహారం, సామాగ్రి మొదలైనవి)",
    "amountLabel" to "మొత్తం (నెలవారీ)",
    "currencyLabel" to "కరెన్సీ",
    "inKindDescriptionLabel" to "మీరు ఏమి అందించగలరో వివరించండి",
    "messageLabel" to "సందేశం",
    "sendOfferBtn" to "ఆఫర్ పంపండి",
    "sponsorOfferSent" to "మీ ఆఫర్ పంపబడింది! వివరాలను ఏర్పాటు చేయడానికి రక్షకుడు మిమ్మల్ని సంప్రదిస్తారు.",
    "sponsorshipOffersReceived" to "స్పాన్సర్‌షిప్ ఆఫర్‌లు",
    "noSponsorshipOffers" to "ఇంకా స్పాన్సర్‌షిప్ ఆఫర్‌లు లేవు.",
    "generalFundLabel" to "సాధారణ నిధి",
    "offeringLabel" to "అందిస్తున్నది",
    "sponsorshipStatusSent" to "కొత్తది",
    "sponsorshipStatusRead" to "చదవబడింది",
    "markAsReadBtn" to "చదివినట్లు గుర్తించండి",
    "mySponsorshipOffers" to "నా స్పాన్సర్‌షిప్ ఆఫర్‌లు",
    "noSponsorshipOffersYet" to "మీరు ఇంకా ఏ స్పాన్సర్‌షిప్ ఆఫర్‌లను పంపలేదు.",
    "howToSponsorHint" to "స్పాన్సర్ చేయడానికి, ఒక రెస్క్యూయర్ పేజీ (సాధారణ మద్దతు) లేదా ఒక పెంపుడు జంతువు పేజీ (ఆ జంతువు ఖర్చులు) తెరిచి ఆఫర్ పంపండి.",
    "browsePetsLink" to "పెంపుడు జంతువులను చూడండి",
    "yourMessage" to "మీ సందేశం",
    "newPassword" to "కొత్త పాస్‌వర్డ్",
    "passkeyDesc" to "పరికరాలలో సులభమైన లాగిన్ కోసం మీ ఖాతాకు అదనపు పాస్‌కీలను జోడించండి.",
    "passkeyName" to "పాస్‌కీ పేరు",
    "pers" to "వ్యక్తులు",
    "petFoodDescription" to "మీ పెంపుడు జంతువులకు ఏ ఆహారాలు సురక్షితమైనవి, హానికరమైనవి లేదా విషపూరితమైనవో తెలుసుకోండి",
    "petFoodGuide" to "పెంపుడు జంతువుల ఆహార గైడ్",
    "foodInformation" to "ఆహార సమాచారం",
    "petsForAdoption" to "దత్తత కోసం పెంపుడు జంతువులు",
    "chooseCountryPrompt" to "మీ కొత్త ఉత్తమ స్నేహితుడిని కనుగొనండి",
    "chooseCountryPromptHint" to "మీ దగ్గర దత్తత కోసం అందుబాటులో ఉన్న పెంపుడు జంతువులను బ్రౌజ్ చేయడానికి పైన ఒక దేశాన్ని ఎంచుకోండి.",
    "noPetsFound" to "పెంపుడు జంతువులు కనుగొనబడలేదు",
    "noPetsFoundHint" to "వేరే ఫిల్టర్‌ను ప్రయత్నించండి లేదా త్వరలో మళ్లీ తనిఖీ చేయండి — కొత్త పెంపుడు జంతువులు క్రమం తప్పకుండా జోడించబడతాయి.",
    "loadingMorePets" to "మరిన్ని పెంపుడు జంతువులను లోడ్ చేస్తోంది…",
    "photos" to "ఫోటోలు (గరిష్టంగా 12)",
    "profilePhotographerRole" to "ఫోటోగ్రాఫర్ - ఫోటోగ్రఫీ సేవలను అందించండి",
    "profileRescuerRole" to "రక్షకుడు - దత్తత కోసం పెంపుడు జంతువులను ప్రచురించండి",
    "profileTemporalHomeRole" to "తాత్కాలిక నివాసం - పెంపుడు జంతువుల కోసం తాత్కాలిక నివాసం అందించండి",
    "provideShelter" to "ఆశ్రయం - జంతు ఆశ్రయాన్ని నిర్వహించండి",
    "provideSterilization" to "స్టెరిలైజేషన్ సేవ - స్టెరిలైజేషన్ సేవలను అందించండి",
    "requestEmailChange" to "ఇమెయిల్ మార్పును అభ్యర్థించండి",
    "rescueDate" to "రక్షణ తేదీ",
    "rescueLocation" to "రక్షణ స్థానం",
    "savePassword" to "పాస్‌వర్డ్‌ను సేవ్ చేయండి",
    "selectEnergy" to "శక్తిని ఎంచుకోండి",
    "selectSize" to "పరిమాణాన్ని ఎంచుకోండి",
    "sex" to "లింగం",
    "shelterSettings" to "ఆశ్రయ సెట్టింగ్‌లు",
    "size" to "పరిమాణం",
    "small" to "చిన్నది",
    "specialNeeds" to "ప్రత్యేక అవసరాలు",
    "sterilizationSettings" to "స్టెరిలైజేషన్ సెట్టింగ్‌లు",
    "sterilized" to "స్టెరిలైజ్ చేయబడింది",
    "temporalHomeSettings" to "తాత్కాలిక నివాస సెట్టింగ్‌లు",
    "temperament" to "స్వభావం",
    "type" to "రకం *",
    "urgentAdoption" to "అత్యవసర దత్తత అవసరం",
    "vaccinations" to "టీకాలు",
    "vetDisclaimer" to "ఆహార మార్పులు చేయడానికి ముందు మీ పశువైద్యుడిని సంప్రదించండి.",
    "weight" to "బరువు (కిలోలు)",
    "weightLabel" to "బరువు",
    "website" to "వెబ్‌సైట్",
    "securitySettings" to "భద్రతా సెట్టింగ్‌లు",
    "emailVerifiedTitle" to "ఇమెయిల్ విజయవంతంగా ధృవీకరించబడింది!",
    "emailVerifiedMessage" to "మీ ఇమెయిల్ ధృవీకరించబడింది. మీ నమోదు ఇప్పుడు పూర్తయింది.",
    "redirectingIn" to "ఇందులో ప్రధాన పేజీకి దారి మళ్లిస్తోంది ",
    "canAlsoLogin" to "మీరు కూడా ",
    "loginNow" to "ఇప్పుడు లాగిన్ చేయండి",
    "emailVerificationFailed" to "ధృవీకరణ విఫలమైంది",
    "invalidOrExpiredToken" to "ధృవీకరణ లింక్ చెల్లదు లేదా గడువు ముగిసింది.",
    "tryRegisteringAgain" to "దయచేసి మళ్లీ నమోదు చేయడానికి ప్రయత్నించండి.",
    "verificationEmailResent" to "ధృవీకరణ ఇమెయిల్ గడువు ముగిసింది. కొత్త ధృవీకరణ ఇమెయిల్ పంపబడింది. దయచేసి మీ ఇన్‌బాక్స్‌ను తనిఖీ చేయండి.",
    "emailVerificationSent" to "ధృవీకరణ ఇమెయిల్ పంపబడింది. లాగిన్ చేయడానికి ముందు దయచేసి మీ ఇన్‌బాక్స్‌ను తనిఖీ చేయండి.",
    "emailNotVerifiedLogin" to "లాగిన్ చేయడానికి ముందు దయచేసి మీ ఇమెయిల్‌ను ధృవీకరించండి. ధృవీకరణ లింక్ కోసం మీ ఇన్‌బాక్స్‌ను తనిఖీ చేయండి.",
    "invalidOrExpiredMagicLink" to "చెల్లని లేదా గడువు ముగిసిన మ్యాజిక్ లింక్. దయచేసి కొత్తదాన్ని అభ్యర్థించండి.",
    "invalidToken" to "చెల్లని టోకెన్.",
    "accountBanned" to "మీ ఖాతా నిషేధించబడింది.",
    "loginError" to "లాగిన్ లోపం సంభవించింది.",
    "profileUpdated" to "ప్రొఫైల్ నవీకరించబడింది!",
    "displayNameRequired" to "ప్రదర్శన పేరు ఖాళీగా ఉండకూడదు",
    "saving" to "సేవ్ చేస్తోంది...",
    "passwordSet" to "పాస్‌వర్డ్ సెట్ చేయబడింది",
    "noPassword" to "పాస్‌వర్డ్ సెట్ చేయబడలేదు",
    "passwordChangeSuccess" to "పాస్‌వర్డ్ విజయవంతంగా మార్చబడింది!",
    "passwordChangeFailed" to "పాస్‌వర్డ్ మార్చడంలో విఫలమైంది",
    "passwordSetSuccess" to "పాస్‌వర్డ్ విజయవంతంగా సెట్ చేయబడింది!",
    "passwordSetFailed" to "పాస్‌వర్డ్ సెట్ చేయడంలో విఫలమైంది",
    "passwordsDoNotMatch" to "పాస్‌వర్డ్‌లు సరిపోలడం లేదు",
    "currentPasswordRequired" to "ప్రస్తుత పాస్‌వర్డ్ అవసరం",
    "emailChangeSent" to "మీ కొత్త ఇమెయిల్ చిరునామాకు ధృవీకరణ ఇమెయిల్ పంపబడింది!",
    "emailChangeFailed" to "ఇమెయిల్ మార్పును అభ్యర్థించడంలో విఫలమైంది",
    "emailRequired" to "ఇమెయిల్ అవసరం",
    "sendingEllipsis" to "పంపుతోంది...",
    "resetLinkSent" to "పాస్‌వర్డ్ రీసెట్ లింక్ పంపబడింది! మీ ఇమెయిల్‌ను తనిఖీ చేయండి.",
    "magicLinkSent" to "లాగిన్ లింక్ పంపబడింది! మీ ఇమెయిల్‌ను తనిఖీ చేయండి.",
    "magicLinkSendFailed" to "లాగిన్ లింక్ పంపడంలో విఫలమైంది",
    "failedSendResetLink" to "రీసెట్ లింక్ పంపడంలో విఫలమైంది.",
    "invalidOrMissingToken" to "చెల్లని లేదా తప్పిపోయిన టోకెన్.",
    "passwordTooShort" to "పాస్‌వర్డ్ కనీసం 8 అక్షరాలు కలిగి ఉండాలి.",
    "passwordNeedUppercase" to "పాస్‌వర్డ్‌కు కనీసం 1 పెద్ద అక్షరం అవసరం.",
    "passwordNeedLowercase" to "పాస్‌వర్డ్‌కు కనీసం 1 చిన్న అక్షరం అవసరం.",
    "passwordNeedNumber" to "పాస్‌వర్డ్‌కు కనీసం 1 సంఖ్య అవసరం.",
    "passwordNeedSymbol" to "పాస్‌వర్డ్‌కు కనీసం 1 గుర్తు అవసరం.",
    "resettingPassword" to "పాస్‌వర్డ్‌ను రీసెట్ చేస్తోంది...",
    "passwordResetSuccess" to "పాస్‌వర్డ్ విజయవంతంగా రీసెట్ చేయబడింది! మీరు ఇప్పుడు లాగిన్ చేయవచ్చు.",
    "failedResetPassword" to "పాస్‌వర్డ్‌ను రీసెట్ చేయడంలో విఫలమైంది.",
    "loginSuccessRedirecting" to "లాగిన్ విజయవంతమైంది! దారి మళ్లిస్తోంది...",
    "loginFailedExpiredLink" to "లాగిన్ విఫలమైంది. లింక్ చెల్లదు లేదా గడువు ముగిసి ఉండవచ్చు.",
    "loginFailed" to "లాగిన్ విఫలమైంది.",
    "emailChangedSuccess" to "ఇమెయిల్ విజయవంతంగా మార్చబడింది!",
    "failedChangeEmailExpired" to "ఇమెయిల్ మార్చడంలో విఫలమైంది. లింక్ చెల్లదు లేదా గడువు ముగిసి ఉండవచ్చు.",
    "failedChangeEmail" to "ఇమెయిల్ మార్చడంలో విఫలమైంది.",
    "emailVerifiedSuccess" to "ఇమెయిల్ విజయవంతంగా ధృవీకరించబడింది!",
    "failedVerifyEmailExpired" to "ఇమెయిల్‌ను ధృవీకరించడంలో విఫలమైంది. లింక్ చెల్లదు లేదా గడువు ముగిసి ఉండవచ్చు.",
    "failedVerifyEmail" to "ఇమెయిల్‌ను ధృవీకరించడంలో విఫలమైంది.",
    "dropImagesHint" to "చిత్రాలను ఇక్కడ వదలండి లేదా బ్రౌజ్ చేయడానికి క్లిక్ చేయండి",
    "forgotPasswordTitle" to "పాస్‌వర్డ్ మర్చిపోయారా",
    "forgotPasswordIntro" to "మీ ఇమెయిల్‌ను నమోదు చేయండి మరియు మీ పాస్‌వర్డ్‌ను రీసెట్ చేయడానికి మేము మీకు లింక్‌ను పంపుతాము.",
    "sendResetLink" to "రీసెట్ లింక్‌ను పంపండి",
    "backToLogin" to "లాగిన్‌కు తిరిగి వెళ్లండి",
    "resetPasswordTitle" to "పాస్‌వర్డ్‌ను రీసెట్ చేయండి",
    "resetPasswordIntro" to "క్రింద మీ కొత్త పాస్‌వర్డ్‌ను నమోదు చేయండి.",
    "emailLinkLoginTitle" to "ఇమెయిల్ లింక్ లాగిన్",
    "verifying" to "ధృవీకరిస్తోంది...",
    "emailChangeTitle" to "ఇమెయిల్ మార్పు",
    "verifyContactEmailTitle" to "సంప్రదింపు ఇమెయిల్‌ను ధృవీకరించండి",
    "status" to "స్థితి",
    "yes" to "అవును",
    "no" to "లేదు",
    "noDescription" to "వివరణ లేదు.",
    "urgentBadge" to "అత్యవసరం - త్వరలో ఇల్లు కావాలి!",
    "needsNewHome" to "ఈ పెంపుడు జంతువుకు అత్యవసరంగా కొత్త ఇల్లు అవసరం",
    "promotedReasonLabel" to "కారణం",
    "selectReason" to "ఒక కారణాన్ని ఎంచుకోండి",
    "promotedReasonMoving" to "యజమాని మారిపోతున్నారు",
    "promotedReasonComplaints" to "ఫిర్యాదులు / మరిన్ని పెంపుడు జంతువులను ఉంచుకోలేరు",
    "promotedReasonPetConflict" to "మరొక పెంపుడు జంతువుతో సంఘర్షణ",
    "promotedReasonOther" to "ఇతరం",
    "promotedReasonDetailLabel" to "అదనపు వివరాలు (ఐచ్ఛికం)",
    "promotedReasonRequired" to "దయచేసి ఒక కారణాన్ని ఎంచుకోండి",
    "needsNewHomeBadge" to "కొత్త ఇల్లు అవసరం",
    "housingType" to "గృహ రకం",
    "preferNotToSay" to "చెప్పకూడదనుకుంటున్నాను",
    "houseHousing" to "ఇల్లు",
    "apartmentHousing" to "అపార్ట్‌మెంట్",
    "hasYard" to "వెనుక ఖాళీ స్థలం ఉంది",
    "hasOtherPets" to "ఇంట్లో ఇతర పెంపుడు జంతువులు ఉన్నాయి",
    "adoptionExperience" to "దత్తత అనుభవం",
    "firstTimeAdopter" to "మొదటిసారి దత్తత తీసుకునేవారు",
    "experiencedAdopter" to "అనుభవజ్ఞుడైన దత్తత తీసుకునేవారు",
    "noAdoptionRequests" to "దత్తత అభ్యర్థనలు లేవు",
    "noMessage" to "సందేశం లేదు",
    "markUnderReview" to "సమీక్షలో ఉన్నట్లు గుర్తించండి",
    "approve" to "ఆమోదించండి",
    "reject" to "తిరస్కరించండి",
    "reviewNoteLabel" to "ప్రైవేట్ సమీక్షా గమనిక",
    "saveNote" to "గమనికను సేవ్ చేయండి",
    "adoptionStatusPending" to "పెండింగ్‌లో ఉంది",
    "adoptionStatusUnderReview" to "సమీక్షలో ఉంది",
    "adoptionStatusApproved" to "ఆమోదించబడింది",
    "adoptionStatusRejected" to "ఆమోదించబడలేదు",
    "myAdoptionRequests" to "నా దత్తత అభ్యర్థనలు",
    "noAdoptionRequestsYet" to "మీరు ఇంకా ఏ పెంపుడు జంతువును దత్తత తీసుకోవడానికి అభ్యర్థించలేదు.",
    "medicalHistory" to "వైద్య చరిత్ర",
    "medicalSchedule" to "టీకా & నట్టల నివారణ షెడ్యూల్",
    "recordType" to "రకం",
    "vaccination" to "టీకా",
    "deworming" to "నట్టల నివారణ",
    "recordName" to "పేరు",
    "dateGiven" to "ఇచ్చిన తేదీ",
    "nextDueDate" to "తదుపరి గడువు (ఐచ్ఛికం)",
    "notesOptional" to "గమనికలు (ఐచ్ఛికం)",
    "addRecord" to "రికార్డును జోడించండి",
    "noMedicalRecords" to "ఇంకా టీకా లేదా నట్టల నివారణ రికార్డులు లేవు.",
    "nextDueLabel" to "తదుపరి గడువు",
    "givenLabel" to "ఇవ్వబడింది",
    "overdue" to "గడువు మించింది",
    "dueSoon" to "త్వరలో గడువు",
    "upToDate" to "తాజాగా ఉంది",
    "recordNameRequired" to "పేరు అవసరం",
    "dateGivenRequired" to "ఇచ్చిన తేదీ అవసరం",
    "failedToAddRecord" to "రికార్డును జోడించడంలో విఫలమైంది",
    "confirmDeleteRecord" to "ఈ రికార్డును తొలగించాలా?",
    "maxCapacity" to "ఒకేసారి గరిష్ట పెంపుడు జంతువులు (ఐచ్ఛికం)",
    "placePetHere" to "ఇక్కడ ఒక పెంపుడు జంతువును ఉంచండి",
    "noAvailablePetsToPlace" to "ప్రస్తుతం ఉంచడానికి మీ వద్ద అందుబాటులో ఉన్న పెంపుడు జంతువులు లేవు.",
    "selectPet" to "పెంపుడు జంతువును ఎంచుకోండి",
    "startPlacementBtn" to "ప్లేస్‌మెంట్‌ను ప్రారంభించండి",
    "placementStarted" to "ఫోస్టర్ ప్లేస్‌మెంట్ ప్రారంభమైంది!",
    "petsCurrentlyWithMe" to "ప్రస్తుతం నా వద్ద ఉన్న పెంపుడు జంతువులు",
    "noPetsCurrentlyFostered" to "ప్రస్తుతం మీ వద్ద ఉంచబడిన పెంపుడు జంతువులు లేవు.",
    "sinceLabel" to "నుండి",
    "fosterPlacement" to "ఫోస్టర్ ప్లేస్‌మెంట్",
    "endPlacement" to "ప్లేస్‌మెంట్‌ను ముగించండి",
    "currentlyWithLabel" to "ప్రస్తుతం వీరి వద్ద",
    "notCurrentlyFostered" to "ప్రస్తుతం ఫోస్టర్ కేర్‌లో లేదు.",
    "confirmEndPlacement" to "ఈ ఫోస్టర్ ప్లేస్‌మెంట్‌ను ముగించాలా? పెంపుడు జంతువు మళ్లీ మీ వద్దకు వచ్చినట్లు నమోదు చేయబడుతుంది.",
    "aTemporalHome" to "ఒక తాత్కాలిక నివాసం",
    "viewsLabel" to "వీక్షణలు",
    "inquiriesLabel" to "విచారణలు",
    "conversionLabel" to "మార్పిడి",
    "messageOptional" to "సందేశం (ఐచ్ఛికం)",
    "requestAdoption" to "దత్తతను అభ్యర్థించండి",
    "editPet" to "పెంపుడు జంతువును సవరించండి",
    "adoptionRequestSubmitted" to "దత్తత అభ్యర్థన సమర్పించబడింది!",
    "failedSubmitRequest" to "అభ్యర్థనను సమర్పించడంలో విఫలమైంది",
    "noPhotosYet" to "ఇంకా ఫోటోలు లేవు.",
    "rabbit" to "కుందేలు",
    "safeFoods" to "సురక్షిత ఆహారాలు",
    "harmfulFoods" to "హానికరమైన ఆహారాలు",
    "cannotEatFoods" to "తినకూడని ఆహారాలు",
    "legendSafe" to "సురక్షితం",
    "legendHarmful" to "హానికరం",
    "legendToxic" to "విషపూరితం",
    "food_dog_safe_chicken_name" to "చికెన్",
    "food_dog_safe_chicken_detail" to "వండినది, ఎముకలు లేనిది",
    "food_dog_safe_chicken_desc" to "తక్కువ కొవ్వు ప్రోటీన్, జీర్ణం చేసుకోవడం సులభం",
    "food_dog_safe_rice_name" to "అన్నం",
    "food_dog_safe_rice_detail" to "తెలుపు లేదా బ్రౌన్",
    "food_dog_safe_rice_desc" to "కార్బోహైడ్రేట్ల మంచి మూలం",
    "food_dog_safe_carrots_name" to "క్యారెట్లు",
    "food_dog_safe_carrots_detail" to "పచ్చి లేదా వండినది",
    "food_dog_safe_carrots_desc" to "తక్కువ కేలరీలు, అధిక ఫైబర్",
    "food_dog_safe_apples_name" to "ఆపిల్స్",
    "food_dog_safe_apples_detail" to "గింజలు లేకుండా",
    "food_dog_safe_apples_desc" to "విటమిన్లు అధికంగా ఉంటాయి",
    "food_dog_safe_peanutbutter_name" to "పీనట్ బటర్",
    "food_dog_safe_peanutbutter_detail" to "ఉప్పు లేనిది, జైలిటాల్ లేనిది",
    "food_dog_safe_peanutbutter_desc" to "మంచి ప్రోటీన్ మూలం",
    "food_dog_harmful_grapes_name" to "ద్రాక్ష",
    "food_dog_harmful_grapes_detail" to "అన్ని రకాలు",
    "food_dog_harmful_grapes_desc" to "మూత్రపిండాల వైఫల్యానికి కారణం కావచ్చు",
    "food_dog_harmful_onions_name" to "ఉల్లిపాయలు",
    "food_dog_harmful_onions_detail" to "అన్ని రూపాలు",
    "food_dog_harmful_onions_desc" to "ఎర్ర రక్త కణాలను దెబ్బతీస్తుంది",
    "food_dog_harmful_chocolate_name" to "చాక్లెట్",
    "food_dog_harmful_chocolate_detail" to "అన్ని రకాలు",
    "food_dog_harmful_chocolate_desc" to "కుక్కలకు విషపూరితం",
    "food_dog_harmful_garlic_name" to "వెల్లుల్లి",
    "food_dog_harmful_garlic_detail" to "అన్ని రూపాలు",
    "food_dog_harmful_garlic_desc" to "రక్తహీనతకు కారణమవుతుంది",
    "food_dog_cannot_xylitol_name" to "జైలిటాల్",
    "food_dog_cannot_xylitol_detail" to "కృత్రిమ స్వీటెనర్",
    "food_dog_cannot_xylitol_desc" to "అత్యంత విషపూరితం",
    "food_dog_cannot_macadamia_name" to "మెకడామియా గింజలు",
    "food_dog_cannot_macadamia_detail" to "అన్ని రూపాలు",
    "food_dog_cannot_macadamia_desc" to "బలహీనతకు కారణమవుతుంది",
    "food_dog_cannot_avocado_name" to "అవకాడో",
    "food_dog_cannot_avocado_detail" to "గింజ, తొక్క, అన్నీ",
    "food_dog_cannot_avocado_desc" to "పెర్సిన్ కలిగి ఉంటుంది",
    "food_dog_cannot_alcohol_name" to "ఆల్కహాల్",
    "food_dog_cannot_alcohol_detail" to "అన్ని రూపాలు",
    "food_dog_cannot_alcohol_desc" to "అత్యంత విషపూరితం",
    "food_cat_safe_cookedfish_name" to "వండిన చేప",
    "food_cat_safe_cookedfish_detail" to "ఎముకలు లేకుండా",
    "food_cat_safe_cookedfish_desc" to "అధిక ప్రోటీన్",
    "food_cat_safe_chicken_name" to "చికెన్",
    "food_cat_safe_chicken_detail" to "వండినది, సాదా",
    "food_cat_safe_chicken_desc" to "మంచి ప్రోటీన్ మూలం",
    "food_cat_safe_pumpkin_name" to "గుమ్మడికాయ",
    "food_cat_safe_pumpkin_detail" to "సాదా, వండినది",
    "food_cat_safe_pumpkin_desc" to "జీర్ణక్రియకు సహాయపడుతుంది",
    "food_cat_safe_eggs_name" to "గుడ్లు",
    "food_cat_safe_eggs_detail" to "వండినవి",
    "food_cat_safe_eggs_desc" to "పూర్తి ప్రోటీన్",
    "food_cat_harmful_raweggs_name" to "పచ్చి గుడ్లు",
    "food_cat_harmful_raweggs_detail" to "అవిడిన్‌తో",
    "food_cat_harmful_raweggs_desc" to "బయోటిన్‌కు ఆటంకం కలిగిస్తుంది",
    "food_cat_harmful_rawfish_name" to "పచ్చి చేప",
    "food_cat_harmful_rawfish_detail" to "థయామినేస్ కలిగి ఉంటుంది",
    "food_cat_harmful_rawfish_desc" to "B విటమిన్లను విచ్ఛిన్నం చేస్తుంది",
    "food_cat_harmful_dogfood_name" to "కుక్క ఆహారం",
    "food_cat_harmful_dogfood_detail" to "ఏదైనా",
    "food_cat_harmful_dogfood_desc" to "టౌరిన్ లోపిస్తుంది",
    "food_cat_harmful_milk_name" to "పాలు",
    "food_cat_harmful_milk_detail" to "చాలా పిల్లులకు",
    "food_cat_harmful_milk_desc" to "లాక్టోస్ అసహనం",
    "food_cat_cannot_chocolate_name" to "చాక్లెట్",
    "food_cat_cannot_chocolate_detail" to "అన్ని రకాలు",
    "food_cat_cannot_chocolate_desc" to "థియోబ్రోమిన్ విషపూరితం",
    "food_cat_cannot_onionsgarlic_name" to "ఉల్లిపాయలు/వెల్లుల్లి",
    "food_cat_cannot_onionsgarlic_detail" to "అన్ని రూపాలు",
    "food_cat_cannot_onionsgarlic_desc" to "ఎర్ర రక్త కణాలను దెబ్బతీస్తుంది",
    "food_cat_cannot_grapesraisins_name" to "ద్రాక్ష/ఎండు ద్రాక్ష",
    "food_cat_cannot_grapesraisins_detail" to "అన్ని రకాలు",
    "food_cat_cannot_grapesraisins_desc" to "మూత్రపిండాల నష్టం",
    "food_cat_cannot_alcohol_name" to "ఆల్కహాల్",
    "food_cat_cannot_alcohol_detail" to "అన్ని రూపాలు",
    "food_cat_cannot_alcohol_desc" to "చాలా విషపూరితం",
    "food_bird_safe_seeds_name" to "గింజలు",
    "food_bird_safe_seeds_detail" to "వివిధ రకాల మిశ్రమం",
    "food_bird_safe_seeds_desc" to "మంచి కొవ్వు మూలం",
    "food_bird_safe_fruits_name" to "పండ్లు",
    "food_bird_safe_fruits_detail" to "చాలా రకాలు",
    "food_bird_safe_fruits_desc" to "విటమిన్లు మరియు ఖనిజాలు",
    "food_bird_safe_vegetables_name" to "కూరగాయలు",
    "food_bird_safe_vegetables_detail" to "ఆకుకూరలు",
    "food_bird_safe_vegetables_desc" to "అవసరమైన పోషకాలు",
    "food_bird_safe_pellets_name" to "పెల్లెట్లు",
    "food_bird_safe_pellets_detail" to "నాణ్యమైన ఫార్ములా",
    "food_bird_safe_pellets_desc" to "పూర్తి పోషణ",
    "food_bird_harmful_avocado_name" to "అవకాడో",
    "food_bird_harmful_avocado_detail" to "అన్ని భాగాలు",
    "food_bird_harmful_avocado_desc" to "పెర్సిన్ విషపూరితం",
    "food_bird_harmful_fruitpits_name" to "పండ్ల గింజలు",
    "food_bird_harmful_fruitpits_detail" to "ఆపిల్, పీచ్",
    "food_bird_harmful_fruitpits_desc" to "సైనైడ్ ఆనవాళ్లు",
    "food_bird_harmful_salt_name" to "ఉప్పు",
    "food_bird_harmful_salt_detail" to "ఏ రూపంలోనైనా",
    "food_bird_harmful_salt_desc" to "పక్షులకు విషపూరితం",
    "food_bird_harmful_caffeine_name" to "కెఫిన్",
    "food_bird_harmful_caffeine_detail" to "కాఫీ, టీ",
    "food_bird_harmful_caffeine_desc" to "గుండె సమస్యలు",
    "food_bird_cannot_chocolate_name" to "చాక్లెట్",
    "food_bird_cannot_chocolate_detail" to "అన్ని రకాలు",
    "food_bird_cannot_chocolate_desc" to "థియోబ్రోమిన్ విషపూరితం",
    "food_bird_cannot_onions_name" to "ఉల్లిపాయలు",
    "food_bird_cannot_onions_detail" to "అన్ని రూపాలు",
    "food_bird_cannot_onions_desc" to "ఎర్ర రక్త కణాలను దెబ్బతీస్తుంది",
    "food_bird_cannot_garlic_name" to "వెల్లుల్లి",
    "food_bird_cannot_garlic_detail" to "అన్ని రూపాలు",
    "food_bird_cannot_garlic_desc" to "విషపూరితం",
    "food_bird_cannot_mushrooms_name" to "పుట్టగొడుగులు",
    "food_bird_cannot_mushrooms_detail" to "అడవి రకాలు",
    "food_bird_cannot_mushrooms_desc" to "సాధ్యమైన విషాలు",
    "food_fish_safe_flakes_name" to "ఫ్లేక్స్",
    "food_fish_safe_flakes_detail" to "నాణ్యమైన బ్రాండ్లు",
    "food_fish_safe_flakes_desc" to "పూర్తి చేపల ఆహారం",
    "food_fish_safe_pellets_name" to "పెల్లెట్లు",
    "food_fish_safe_pellets_detail" to "తేలియాడేవి/మునిగేవి",
    "food_fish_safe_pellets_desc" to "సమతుల్య పోషణ",
    "food_fish_safe_frozenfood_name" to "ఘనీభవించిన ఆహారం",
    "food_fish_safe_frozenfood_detail" to "బ్లడ్‌వార్మ్స్, బ్రైన్",
    "food_fish_safe_frozenfood_desc" to "ప్రోటీన్ మూలం",
    "food_fish_safe_vegetables_name" to "కూరగాయలు",
    "food_fish_safe_vegetables_detail" to "ఉడకబెట్టిన గుమ్మడికాయ",
    "food_fish_safe_vegetables_desc" to "ఫైబర్ మూలం",
    "food_fish_harmful_bread_name" to "బ్రెడ్",
    "food_fish_harmful_bread_detail" to "ఏదైనా రకం",
    "food_fish_harmful_bread_desc" to "పోషణ లేదు, కడుపు నింపేస్తుంది",
    "food_fish_harmful_humanfood_name" to "మనుషుల ఆహారం",
    "food_fish_harmful_humanfood_detail" to "వండిన భోజనం",
    "food_fish_harmful_humanfood_desc" to "తప్పు పోషణ",
    "food_fish_harmful_livefeed_name" to "సజీవ ఆహారం",
    "food_fish_harmful_livefeed_detail" to "అడవిలో పట్టుకున్న కీటకాలు",
    "food_fish_harmful_livefeed_desc" to "సాధ్యమైన పరాన్నజీవులు",
    "food_fish_cannot_landinsect_name" to "భూమి కీటకం",
    "food_fish_cannot_landinsect_detail" to "పాకే పురుగులు",
    "food_fish_cannot_landinsect_desc" to "సహజ ఆహారం కాదు",
    "food_fish_cannot_mammalmeat_name" to "క్షీరద మాంసం",
    "food_fish_cannot_mammalmeat_detail" to "చికెన్, గొడ్డు మాంసం",
    "food_fish_cannot_mammalmeat_desc" to "చాలా చేపలకు సరిపడదు",
    "food_fish_cannot_dairy_name" to "పాల ఉత్పత్తులు",
    "food_fish_cannot_dairy_detail" to "ఏదైనా",
    "food_fish_cannot_dairy_desc" to "జీర్ణించుకోలేవు",
    "food_fish_cannot_breadcrumbs_name" to "బ్రెడ్ క్రంబ్స్",
    "food_fish_cannot_breadcrumbs_detail" to "చాలా వాటిలో వెల్లుల్లి ఉంటుంది",
    "food_fish_cannot_breadcrumbs_desc" to "హానికరమైన సంకలనాలు",
    "food_rabbit_safe_hay_name" to "గడ్డి",
    "food_rabbit_safe_hay_detail" to "టిమోతీ గడ్డి",
    "food_rabbit_safe_hay_desc" to "ఆహారంలో 80%",
    "food_rabbit_safe_leafygreens_name" to "ఆకుకూరలు",
    "food_rabbit_safe_leafygreens_detail" to "రొమేన్, కొత్తిమీర",
    "food_rabbit_safe_leafygreens_desc" to "రోజువారీ విటమిన్లు",
    "food_rabbit_safe_pellets_name" to "పెల్లెట్లు",
    "food_rabbit_safe_pellets_detail" to "టిమోతీ ఆధారిత",
    "food_rabbit_safe_pellets_desc" to "సమతుల్య పోషణ",
    "food_rabbit_safe_carrots_name" to "క్యారెట్లు",
    "food_rabbit_safe_carrots_detail" to "మితంగా",
    "food_rabbit_safe_carrots_desc" to "ట్రీట్",
    "food_rabbit_harmful_icebergletttuce_name" to "ఐస్‌బర్గ్ లెట్యూస్",
    "food_rabbit_harmful_icebergletttuce_detail" to "పోషణ లేదు",
    "food_rabbit_harmful_icebergletttuce_desc" to "జీర్ణాశయ స్తబ్దతకు కారణం కావచ్చు",
    "food_rabbit_harmful_beans_name" to "బీన్స్",
    "food_rabbit_harmful_beans_detail" to "అన్ని పప్పు దినుసులు",
    "food_rabbit_harmful_beans_desc" to "గ్యాస్, జీర్ణ సమస్యలు",
    "food_rabbit_harmful_corn_name" to "మొక్కజొన్న",
    "food_rabbit_harmful_corn_detail" to "ఏ రూపంలోనైనా",
    "food_rabbit_harmful_corn_desc" to "జీర్ణించుకోలేదు",
    "food_rabbit_harmful_potatoes_name" to "బంగాళదుంపలు",
    "food_rabbit_harmful_potatoes_detail" to "ఏ రూపంలోనైనా",
    "food_rabbit_harmful_potatoes_desc" to "తప్పు పోషకాలు",
    "food_rabbit_cannot_chocolate_name" to "చాక్లెట్",
    "food_rabbit_cannot_chocolate_detail" to "అన్ని రకాలు",
    "food_rabbit_cannot_chocolate_desc" to "థియోబ్రోమిన్ విషపూరితం",
    "food_rabbit_cannot_onions_name" to "ఉల్లిపాయలు",
    "food_rabbit_cannot_onions_detail" to "అన్ని రూపాలు",
    "food_rabbit_cannot_onions_desc" to "చాలా విషపూరితం",
    "food_rabbit_cannot_garlic_name" to "వెల్లుల్లి",
    "food_rabbit_cannot_garlic_detail" to "అన్ని రూపాలు",
    "food_rabbit_cannot_garlic_desc" to "విషపూరితం",
    "food_rabbit_cannot_avocado_name" to "అవకాడో",
    "food_rabbit_cannot_avocado_detail" to "అన్ని భాగాలు",
    "food_rabbit_cannot_avocado_desc" to "పెర్సిన్ కలిగి ఉంటుంది",
    "primary" to "ప్రాథమిక",
    "passkeyRegisteredSuccess" to "పాస్‌కీ విజయవంతంగా నమోదు చేయబడింది!",
    "passkeyRegistrationFailed" to "పాస్‌కీ నమోదు విఫలమైంది",
    "passkeyNameRequired" to "పాస్‌కీ పేరు అవసరం",
    "registeringPasskey" to "పాస్‌కీని నమోదు చేస్తోంది...",
    "passkeyRegistered" to "పాస్‌కీ నమోదు చేయబడింది",
    "noPasskey" to "పాస్‌కీ నమోదు చేయబడలేదు",
    "noPets" to "ఇంకా పెంపుడు జంతువులు లేవు.",
    "photographerCountryStateRequired" to "దయచేసి ఫోటోగ్రాఫర్ సేవల కోసం దేశం మరియు రాష్ట్రాన్ని పూరించండి",
    "temporalHomeAliasCountryCityRequired" to "దయచేసి తాత్కాలిక నివాసం కోసం మారుపేరు, దేశం మరియు నగరాన్ని పూరించండి",
    "shelterNameCountryCityAddressRequired" to "దయచేసి ఆశ్రయం కోసం పేరు, దేశం, నగరం మరియు చిరునామాను పూరించండి",
    "sterilizationNameCountryCityAddressRequired" to "దయచేసి స్టెరిలైజేషన్ ప్రదేశం కోసం పేరు, దేశం, నగరం మరియు చిరునామాను పూరించండి",
    "country.afghanistan" to "ఆఫ్ఘనిస్తాన్",
    "country.albania" to "అల్బేనియా",
    "country.algeria" to "అల్జీరియా",
    "country.argentina" to "అర్జెంటీనా",
    "country.armenia" to "అర్మేనియా",
    "country.australia" to "ఆస్ట్రేలియా",
    "country.austria" to "ఆస్ట్రియా",
    "country.azerbaijan" to "అజర్‌బైజాన్",
    "country.bangladesh" to "బంగ్లాదేశ్",
    "country.belarus" to "బెలారస్",
    "country.belgium" to "బెల్జియం",
    "country.bolivia" to "బొలీవియా",
    "country.bosnia" to "బోస్నియా అండ్ హెర్జెగోవినా",
    "country.brazil" to "బ్రెజిల్",
    "country.bulgaria" to "బల్గేరియా",
    "country.cambodia" to "కంబోడియా",
    "country.cameroon" to "కామెరూన్",
    "country.canada" to "కెనడా",
    "country.chile" to "చిలీ",
    "country.china" to "చైనా",
    "country.colombia" to "కొలంబియా",
    "country.costaRica" to "కోస్టా రికా",
    "country.croatia" to "క్రొయేషియా",
    "country.cuba" to "క్యూబా",
    "country.czechia" to "చెక్ రిపబ్లిక్",
    "country.denmark" to "డెన్మార్క్",
    "country.dominicanRepublic" to "డొమినికన్ రిపబ్లిక్",
    "country.ecuador" to "ఈక్వెడార్",
    "country.egypt" to "ఈజిప్ట్",
    "country.elSalvador" to "ఎల్ సాల్వడార్",
    "country.estonia" to "ఎస్టోనియా",
    "country.ethiopia" to "ఇథియోపియా",
    "country.finland" to "ఫిన్లాండ్",
    "country.france" to "ఫ్రాన్స్",
    "country.georgia" to "జార్జియా",
    "country.germany" to "జర్మనీ",
    "country.ghana" to "ఘనా",
    "country.greece" to "గ్రీస్",
    "country.guatemala" to "గ్వాటెమాల",
    "country.haiti" to "హైతీ",
    "country.honduras" to "హోండురాస్",
    "country.hungary" to "హంగరీ",
    "country.iceland" to "ఐస్‌లాండ్",
    "country.india" to "భారతదేశం",
    "country.indonesia" to "ఇండోనేషియా",
    "country.iran" to "ఇరాన్",
    "country.iraq" to "ఇరాక్",
    "country.ireland" to "ఐర్లాండ్",
    "country.israel" to "ఇజ్రాయెల్",
    "country.italy" to "ఇటలీ",
    "country.jamaica" to "జమైకా",
    "country.japan" to "జపాన్",
    "country.jordan" to "జోర్డాన్",
    "country.kazakhstan" to "కజాఖ్‌స్తాన్",
    "country.kenya" to "కెన్యా",
    "country.kuwait" to "కువైట్",
    "country.latvia" to "లాట్వియా",
    "country.lebanon" to "లెబనాన్",
    "country.libya" to "లిబియా",
    "country.lithuania" to "లిథువేనియా",
    "country.luxembourg" to "లక్సెంబర్గ్",
    "country.malaysia" to "మలేషియా",
    "country.mexico" to "మెక్సికో",
    "country.moldova" to "మోల్డోవా",
    "country.mongolia" to "మంగోలియా",
    "country.montenegro" to "మోంటెనెగ్రో",
    "country.morocco" to "మొరాకో",
    "country.myanmar" to "మయన్మార్",
    "country.nepal" to "నేపాల్",
    "country.netherlands" to "నెదర్లాండ్స్",
    "country.newZealand" to "న్యూజిలాండ్",
    "country.nicaragua" to "నికరాగువా",
    "country.nigeria" to "నైజీరియా",
    "country.northKorea" to "ఉత్తర కొరియా",
    "country.norway" to "నార్వే",
    "country.pakistan" to "పాకిస్తాన్",
    "country.panama" to "పనామా",
    "country.paraguay" to "పరాగ్వే",
    "country.peru" to "పెరూ",
    "country.philippines" to "ఫిలిప్పీన్స్",
    "country.poland" to "పోలాండ్",
    "country.portugal" to "పోర్చుగల్",
    "country.puertoRico" to "ప్యూర్టో రికో",
    "country.qatar" to "ఖతార్",
    "country.romania" to "రొమేనియా",
    "country.russia" to "రష్యా",
    "country.saudiArabia" to "సౌదీ అరేబియా",
    "country.serbia" to "సెర్బియా",
    "country.singapore" to "సింగపూర్",
    "country.slovakia" to "స్లోవేకియా",
    "country.slovenia" to "స్లోవేనియా",
    "country.southAfrica" to "దక్షిణ ఆఫ్రికా",
    "country.southKorea" to "దక్షిణ కొరియా",
    "country.spain" to "స్పెయిన్",
    "country.sriLanka" to "శ్రీలంక",
    "country.sudan" to "సూడాన్",
    "country.sweden" to "స్వీడన్",
    "country.switzerland" to "స్విట్జర్లాండ్",
    "country.syria" to "సిరియా",
    "country.taiwan" to "తైవాన్",
    "country.thailand" to "థాయ్‌లాండ్",
    "country.tunisia" to "ట్యునీషియా",
    "country.turkey" to "టర్కీ",
    "country.ukraine" to "ఉక్రెయిన్",
    "country.uae" to "యునైటెడ్ అరబ్ ఎమిరేట్స్",
    "country.uk" to "యునైటెడ్ కింగ్‌డమ్",
    "country.usa" to "అమెరికా సంయుక్త రాష్ట్రాలు",
    "country.uruguay" to "ఉరుగ్వే",
    "country.uzbekistan" to "ఉజ్బెకిస్తాన్",
    "country.venezuela" to "వెనిజులా",
    "country.vietnam" to "వియత్నాం",
    "country.yemen" to "యెమెన్",
    "loading" to "లోడ్ అవుతోంది...",
    "countryRequired" to "దయచేసి శోధించడానికి ఒక దేశాన్ని ఎంచుకోండి",
    "noSheltersFound" to "ఇంకా ఆశ్రయాలు కనుగొనబడలేదు. తర్వాత మళ్లీ తనిఖీ చేయండి!",
    "visitWebsite" to "వెబ్‌సైట్‌ను సందర్శించండి",
    "fiscalId" to "పన్ను గుర్తింపు సంఖ్య",
    "donationInformation" to "విరాళ సమాచారం",
    "bankName" to "బ్యాంకు",
    "accountHolder" to "ఖాతాదారు",
    "accountNumber" to "ఖాతా నంబర్",
    "currency" to "కరెన్సీ",
    "errorLoadingShelters" to "ఆశ్రయాలను లోడ్ చేయడంలో లోపం",
    "pleaseSelectCountry" to "దయచేసి ఒక దేశాన్ని ఎంచుకోండి",
    "actions" to "చర్యలు",
    "addShelter" to "ఆశ్రయాన్ని జోడించండి",
    "addEditShelter" to "ఆశ్రయాన్ని జోడించండి / సవరించండి",
    "allStates" to "అన్ని రాష్ట్రాలు",
    "confirmDelete" to "ఈ స్థానాన్ని తొలగించాలనుకుంటున్నారా?",
    "confirmDeleteShelter" to "ఈ ఆశ్రయాన్ని తొలగించాలనుకుంటున్నారా?",
    "confirmUnbanUser" to "ఈ వినియోగదారుని నిషేధం ఎత్తివేయాలా?",
    "confirmResetUserCredentials" to "ఇది {email} యొక్క ప్రస్తుత పాస్‌వర్డ్ మరియు పాస్‌కీలను చెల్లనివిగా చేసి, వారికి రీసెట్ లింక్‌ను ఇమెయిల్ చేస్తుంది. కొనసాగించాలా?",
    "confirmDeactivateUser" to "ఈ వినియోగదారుని నిష్క్రియం చేయాలా? వారిని తర్వాత మళ్లీ యాక్టివేట్ చేయవచ్చు.",
    "confirmDeactivatePet" to "ఈ పెంపుడు జంతువును నిష్క్రియం చేయాలా? ఇది పబ్లిక్ జాబితాల నుండి దాచబడుతుంది మరియు తర్వాత మళ్లీ యాక్టివేట్ చేయవచ్చు.",
    "confirmBlockRescuer" to "ఈ రక్షకుడు మీకు మరిన్ని అభ్యర్థనలు పంపకుండా బ్లాక్ చేయాలా?",
    "confirmDeletePet" to "ఈ పెంపుడు జంతువును తొలగించాలా?",
    "confirmDeleteStorage" to "ఈ స్టోరేజ్‌ని తొలగించాలా?",
    "contact" to "సంప్రదించండి",
    "delete" to "తొలగించండి",
    "edit" to "సవరించండి",
    "errorDeletingShelter" to "ఆశ్రయాన్ని తొలగించడంలో లోపం",
    "errorLoadingLocations" to "స్థానాలను లోడ్ చేయడంలో లోపం",
    "errorLoadingShelter" to "ఆశ్రయాన్ని లోడ్ చేయడంలో లోపం",
    "errorSaving" to "సేవ్ చేయడంలో లోపం",
    "errorSavingShelter" to "ఆశ్రయాన్ని సేవ్ చేయడంలో లోపం",
    "location" to "స్థానం",
    "noLocationsFound" to "స్థానాలు కనుగొనబడలేదు",
    "selectCountry" to "ఒక దేశాన్ని ఎంచుకోండి",
    "selectCountryToFilter" to "ఫిల్టర్ చేయడానికి దయచేసి ఒక దేశాన్ని ఎంచుకోండి",
    "shelterAdded" to "ఆశ్రయం విజయవంతంగా జోడించబడింది",
    "shelterDeleted" to "ఆశ్రయం విజయవంతంగా తొలగించబడింది",
    "shelterUpdated" to "ఆశ్రయం విజయవంతంగా నవీకరించబడింది",
    "updateShelter" to "ఆశ్రయాన్ని నవీకరించండి",
    "errorLoadingPhotographers" to "ఫోటోగ్రాఫర్‌లను లోడ్ చేయడంలో లోపం",
    "noPhotographersAvailable" to "ఫోటోగ్రాఫర్‌లు అందుబాటులో లేరు",
    "requestPhotoSession" to "ఫోటో సెషన్‌ను అభ్యర్థించండి",
    "selectPhotographer" to "ఎంచుకోండి",
    "photographersSelected" to "{count} ఎంపిక చేయబడ్డాయి",
    "requestSelectedPhotographers" to "ఎంపిక చేసిన ఫోటోగ్రాఫర్‌లను అభ్యర్థించండి",
    "clearSelection" to "ఎంపికను తీసివేయండి",
    "requestToMultiple" to "వీరికి అభ్యర్థించండి",
    "messageRequired" to "దయచేసి ఫోటోగ్రాఫర్‌ల కోసం సందేశం రాయండి.",
    "requestsSentSuccessfully" to "అభ్యర్థనలు విజయవంతంగా పంపబడ్డాయి",
    "message" to "సందేశం",
    "sendRequest" to "అభ్యర్థనను పంపండి",
    "requestSentSuccessfully" to "అభ్యర్థన విజయవంతంగా పంపబడింది!",
    "requestTo" to "వీరికి అభ్యర్థన",
    "enterMessage" to "{name} కోసం ఒక సందేశాన్ని నమోదు చేయండి...",
    "reportUrgent" to "అత్యవసరం",
    "topRescuers" to "టాప్ రక్షకులు",
    "urgentRescuerSettings" to "అత్యవసర రక్షకుడు సెట్టింగ్‌లు",
    "urgentRescueAlerts" to "అత్యవసర రక్షణ హెచ్చరికలు",
    "reportUrgentTitle" to "ప్రమాదంలో ఉన్న పెంపుడు జంతువును నివేదించండి",
    "reportUrgentIntro" to "మీరు గాయపడిన, దుర్వినియోగం చేయబడిన, ఆకలితో ఉన్న లేదా వదిలేయబడిన పెంపుడు జంతువును కనుగొంటే మరియు తక్షణ సహాయం అవసరమైతే, అది ఎక్కడ ఉందో మరియు ఏమి తప్పు జరిగిందో మాకు తెలియజేయండి - మేము వెంటనే సమీపంలోని అత్యవసర రక్షకులను హెచ్చరిస్తాము.",
    "dangerType" to "ఏమి తప్పు జరిగింది?",
    "dangerInjured" to "గాయపడింది",
    "dangerAbused" to "దుర్వినియోగం చేయబడుతోంది / దురుసుగా చూస్తున్నారు",
    "dangerStarving" to "ఆకలితో ఉంది",
    "dangerTooYoung" to "ఒంటరిగా బతకడానికి చాలా చిన్నది",
    "dangerOther" to "ఇతర అత్యవసర పరిస్థితి",
    "useMyLocation" to "నా ప్రస్తుత స్థానాన్ని ఉపయోగించండి",
    "confirmMyLocation" to "నా ప్రస్తుత స్థానాన్ని నిర్ధారించండి",
    "locationPermissionHint" to "మీ బ్రౌజర్ మీ స్థానాన్ని పంచుకోవడానికి అనుమతి అడుగుతుంది; ఇది మీ సమీపంలోని నివేదికల గురించి తెలియజేయడానికి మాత్రమే ఉపయోగించబడుతుంది.",
    "phoneOptional" to "మీ ఫోన్ (ఐచ్ఛికం)",
    "submitReport" to "నివేదికను సమర్పించండి",
    "urgentRescuerSettingsTitle" to "అత్యవసర రక్షకుడు సెట్టింగ్‌లు",
    "urgentRescuerSettingsIntro" to "మీ కవరేజ్ ప్రాంతం సమీపంలో ప్రమాదంలో ఉన్న పెంపుడు జంతువు నివేదించబడినప్పుడు మీకు అలర్ట్ వస్తుంది. ముందుగా అంగీకరించినవారు కేసును తీసుకుంటారు.",
    "urgentRescuerActive" to "నేను అత్యవసర నివేదికలకు స్పందించడానికి అందుబాటులో ఉన్నాను",
    "coverageAreaMode" to "కవరేజ్ ప్రాంతం",
    "coverageArea" to "కవరేజ్ ప్రాంతం",
    "coverageAreaHint" to "మీ ప్రస్తుత స్థానాన్ని నిర్ధారించండి, పిన్‌ను లాగండి, లేదా క్రింద చిరునామాను టైప్ చేయండి - మ్యాప్ మరియు ఫీల్డ్‌లు సమకాలీకరించబడి ఉంటాయి.",
    "useMyLocationAndRadius" to "నా స్థానం + ఒక వ్యాసార్థాన్ని ఉపయోగించండి",
    "pickAZone" to "బదులుగా ఒక దేశం/రాష్ట్రం/నగరాన్ని ఎంచుకోండి",
    "radiusKm" to "వ్యాసార్థం (కిమీ)",
    "urgentRescueAlertsTitle" to "అత్యవసర రక్షణ హెచ్చరికలు",
    "urgentRescuerLeaderboardTitle" to "టాప్ అత్యవసర రక్షకులు (గత 12 నెలలు)",
    "locating" to "గుర్తిస్తోంది...",
    "geolocationUnsupported" to "మీ బ్రౌజర్ జియోలొకేషన్‌కు మద్దతు ఇవ్వదు",
    "locationCaptured" to "స్థానం సంగ్రహించబడింది",
    "locationDenied" to "మీ స్థానాన్ని పొందలేకపోయాము - దయచేసి స్థాన యాక్సెస్‌ను అనుమతించండి లేదా బదులుగా ఒక నగరాన్ని నమోదు చేయండి",
    "street" to "వీధి",
    "exteriorNumber" to "ఇంటి నంబర్",
    "referenceNotes" to "సూచన గమనికలు (ఉదా. ఇంటి రంగు, సమీప గుర్తు)",
    "concreteAddressHint" to "వీలైతే వీధి మరియు నంబర్‌ను జోడించండి - ఇది రక్షకుడు టాక్సీ తీసుకోవడానికి లేదా మ్యాప్స్ యాప్‌ను నేరుగా ఆ ప్రదేశానికి తెరవడానికి వీలు కల్పిస్తుంది.",
    "descriptionRequired" to "దయచేసి పరిస్థితిని వివరించండి",
    "locationRequired" to "దయచేసి ఒక స్థానాన్ని అందించండి (మీ ప్రస్తుత స్థానాన్ని ఉపయోగించండి లేదా ఒక దేశం మరియు నగరాన్ని నమోదు చేయండి)",
    "submittingEllipsis" to "సమర్పిస్తోంది...",
    "reportSubmitted" to "నివేదిక సమర్పించబడింది. ధన్యవాదాలు - సమీపంలోని రక్షకులకు ఇప్పుడు అలర్ట్ చేయబడుతోంది.",
    "reportSubmitFailed" to "నివేదికను సమర్పించడంలో విఫలమైంది",
    "phoneRequired" to "దయచేసి ఒక ఫోన్ నంబర్‌ను నమోదు చేయండి",
    "savingEllipsis" to "సేవ్ చేస్తోంది...",
    "saved" to "సేవ్ చేయబడింది",
    "saveFailed" to "సేవ్ చేయడంలో విఫలమైంది",
    "noUrgentPages" to "ప్రస్తుతం పెండింగ్‌లో అత్యవసర హెచ్చరికలు లేవు",
    "accept" to "అంగీకరించండి",
    "alreadyAccepted" to "ఎవరైనా ఇప్పటికే ఈ నివేదికను అంగీకరించారు",
    "noLeaderboardData" to "గత 12 నెలల్లో ఆమోదించబడిన రక్షణలు ఇంకా లేవు",
    "rescueAcceptedTitle" to "రక్షణ అంగీకరించబడింది",
    "rescueAcceptedMessage" to "ధన్యవాదాలు - మీరు దానిని చూసుకుంటున్నారు. వీలైనంత త్వరగా నివేదించిన స్థానానికి వెళ్లండి.",
    "rescueAcceptFailedTitle" to "అంగీకరించలేకపోయాము",
    "rescueAcceptFailedMessage" to "ఈ నివేదిక ఇప్పటికే మరొక రక్షకునిచే అంగీకరించబడింది, లేదా లింక్ చెల్లదు లేదా గడువు ముగిసింది.",
    "share" to "పంచుకోండి",
    "shareViaFacebook" to "Facebookలో షేర్ చేయండి",
    "shareViaX" to "Xలో షేర్ చేయండి",
    "shareViaWhatsapp" to "WhatsAppలో షేర్ చేయండి",
    "shareViaEmail" to "ఇమెయిల్ ద్వారా షేర్ చేయండి",
    "copyLink" to "లింక్ కాపీ చేయండి",
    "linkCopied" to "లింక్ కాపీ చేయబడింది!",
    "reportLostFoundTitle" to "కోల్పోయిన లేదా దొరికిన పెంపుడు జంతువును నివేదించండి",
    "reportLostFoundIntro" to "మీ పెంపుడు జంతువును కోల్పోయారా, లేదా వీధి జంతువును కనుగొన్నారా? ఒక నివేదికను దాఖలు చేయండి మరియు మేము దానిని సమీపంలోని ఇతర నివేదికలతో పోల్చి చూస్తాము.",
    "browseLostFound" to "ఇప్పటికే ఉన్న కోల్పోయిన & దొరికిన నివేదికలను బ్రౌజ్ చేయండి",
    "kindLost" to "నేను నా పెంపుడు జంతువును కోల్పోయాను",
    "kindFound" to "నేను ఒక పెంపుడు జంతువును కనుగొన్నాను",
    "petType" to "పెంపుడు జంతువు రకం",
    "petTypeUnknown" to "ఖచ్చితంగా తెలియదు",
    "lostFoundBrowseTitle" to "కోల్పోయిన & దొరికిన పెంపుడు జంతువులు",
    "showing" to "చూపిస్తోంది",
    "kindLostPlural" to "కోల్పోయిన పెంపుడు జంతువులు",
    "kindFoundPlural" to "దొరికిన పెంపుడు జంతువులు",
    "reportResolvedTitle" to "నివేదిక పరిష్కరించబడింది",
    "reportResolvedMessage" to "అప్‌డేట్ కోసం ధన్యవాదాలు - ఈ నివేదిక ఇప్పుడు మూసివేయబడింది.",
    "reportResolveFailedTitle" to "పరిష్కరించలేకపోయాము",
    "reportResolveFailedMessage" to "ఈ నివేదిక ఇప్పటికే పరిష్కరించబడింది, లేదా లింక్ చెల్లదు లేదా గడువు ముగిసింది.",
    "reportLostFound" to "కోల్పోయినవి/\u200Bదొరికినవి",
    "noLostFoundReportsFound" to "కోల్పోయిన & దొరికిన నివేదికలు కనుగొనబడలేదు",
    "errorLoadingReports" to "నివేదికలను లోడ్ చేయడంలో లోపం",
    "yourEmail" to "మీ ఇమెయిల్",
    "contactReporter" to "నివేదించినవారిని సంప్రదించండి",
    "contactFormIncomplete" to "దయచేసి మీ ఇమెయిల్ మరియు ఒక సందేశాన్ని నమోదు చేయండి",
    "contactSent" to "సందేశం పంపబడింది",
    "contactFailed" to "సందేశాన్ని పంపడంలో విఫలమైంది",
    "lostFoundReportSubmitted" to "నివేదిక సమర్పించబడింది. ధన్యవాదాలు - మేము దానిని సమీపంలోని ఇతర నివేదికలతో పోల్చి చూస్తాము.",
    "video" to "వీడియో (ఐచ్ఛికం)",
    "removeVideo" to "వీడియోను తీసివేయండి",
    "saveThisSearch" to "ఈ శోధనను సేవ్ చేయండి",
    "savedSearchSaved" to "శోధన సేవ్ చేయబడింది! కొత్త మ్యాచ్ జాబితా చేయబడినప్పుడు మేము మీకు ఇమెయిల్ చేస్తాము.",
    "savedSearchFailed" to "శోధనను సేవ్ చేయడంలో విఫలమైంది",
    "favorite" to "ఇష్టమైనది",
    "addToFavorites" to "ఇష్టమైనవాటికి జోడించండి",
    "removeFromFavorites" to "ఇష్టమైనవాటి నుండి తీసివేయండి",
    "myFavorites" to "నా ఇష్టమైనవి",
    "noFavoritesYet" to "ఇంకా ఇష్టమైనవి లేవు",
    "mySavedSearches" to "నా సేవ్ చేసిన శోధనలు",
    "noSavedSearchesYet" to "ఇంకా సేవ్ చేసిన శోధనలు లేవు",
    "donationPromptText" to "ఈరోజు ఒక పెంపుడు జంతువుకు సహాయం చేసినందుకు ధన్యవాదాలు. మీకు వీలైతే, అందరికీ Adopt-U ఉచితంగా ఉంచడానికి ఒక చిన్న విరాళాన్ని పరిగణించండి.",
    "donationPromptCta" to "విరాళం ఇవ్వండి",
    "dismiss" to "తీసివేయండి",
)


@JsExport
@JsName("i18n")
object I18n {
    var currentLang: String = "en"
    private val loadedLangs = mutableSetOf<String>()

    private val translations = mapOf(
        "en" to englishTranslations,
        "es" to spanishTranslations,
        "fr" to frenchTranslations,
        "pt" to portugueseTranslations,
        "zh" to chineseTranslations,
        "ar" to arabicTranslations,
        "ru" to russianTranslations,
        "ko" to koreanTranslations,
        "ja" to japaneseTranslations,
        "sw" to swahiliTranslations,
        "ha" to hausaTranslations,
        "tr" to turkishTranslations,
        "ro" to romanianTranslations,
        "hi" to hindiTranslations,
        "bn" to bengaliTranslations,
        "mr" to marathiTranslations,
        "te" to teluguTranslations
    )

    val langEmojis = mapOf(
        "en" to "\uD83C\uDDFA\uD83C\uDDF8",
        "es" to "\uD83C\uDDEA\uD83C\uDDF8",
        "fr" to "\uD83C\uDDEB\uD83C\uDDF7",
        "pt" to "\uD83C\uDDE7\uD83C\uDDF7",
        "zh" to "\uD83C\uDDE8\uD83C\uDDF3",
        "ar" to "\uD83C\uDDF8\uD83C\uDDE6",
        "ru" to "\uD83C\uDDF7\uD83C\uDDFA",
        "ko" to "\uD83C\uDDF0\uD83C\uDDF7",
        "ja" to "\uD83C\uDDEF\uD83C\uDDF5",
        "sw" to "\uD83C\uDDF9\uD83C\uDDFF",
        "ha" to "\uD83C\uDDF3\uD83C\uDDEC",
        "tr" to "\uD83C\uDDF9\uD83C\uDDF7",
        "ro" to "\uD83C\uDDF7\uD83C\uDDF4",
        "hi" to "\uD83C\uDDEE\uD83C\uDDF3",
        "bn" to "\uD83C\uDDE7\uD83C\uDDE9",
        "mr" to "\uD83C\uDDEE\uD83C\uDDF3",
        "te" to "\uD83C\uDDEE\uD83C\uDDF3"
    )

    fun loadLang(lang: String): Promise<Unit> {
        currentLang = lang
        document.documentElement?.setAttribute("lang", lang)
        document.documentElement?.setAttribute("dir", if (lang == "ar") "rtl" else "ltr")
        return Promise.resolve(Unit)
    }

    fun t(key: String): String {
        val langTranslations = translations[currentLang] ?: englishTranslations
        val value = langTranslations[key]
        return value?.toString() ?: key
    }

    private val countryKeyByEnglishName: Map<String, String> = mapOf(
        "afghanistan" to "country.afghanistan",
        "albania" to "country.albania",
        "algeria" to "country.algeria",
        "argentina" to "country.argentina",
        "armenia" to "country.armenia",
        "australia" to "country.australia",
        "austria" to "country.austria",
        "azerbaijan" to "country.azerbaijan",
        "bangladesh" to "country.bangladesh",
        "belarus" to "country.belarus",
        "belgium" to "country.belgium",
        "bolivia" to "country.bolivia",
        "bosnia and herzegovina" to "country.bosnia",
        "brazil" to "country.brazil",
        "bulgaria" to "country.bulgaria",
        "cambodia" to "country.cambodia",
        "cameroon" to "country.cameroon",
        "canada" to "country.canada",
        "chile" to "country.chile",
        "china" to "country.china",
        "colombia" to "country.colombia",
        "costa rica" to "country.costaRica",
        "croatia" to "country.croatia",
        "cuba" to "country.cuba",
        "czech republic" to "country.czechia",
        "denmark" to "country.denmark",
        "dominican republic" to "country.dominicanRepublic",
        "ecuador" to "country.ecuador",
        "egypt" to "country.egypt",
        "el salvador" to "country.elSalvador",
        "estonia" to "country.estonia",
        "ethiopia" to "country.ethiopia",
        "finland" to "country.finland",
        "france" to "country.france",
        "georgia" to "country.georgia",
        "germany" to "country.germany",
        "ghana" to "country.ghana",
        "greece" to "country.greece",
        "guatemala" to "country.guatemala",
        "haiti" to "country.haiti",
        "honduras" to "country.honduras",
        "hungary" to "country.hungary",
        "iceland" to "country.iceland",
        "india" to "country.india",
        "indonesia" to "country.indonesia",
        "iran" to "country.iran",
        "iraq" to "country.iraq",
        "ireland" to "country.ireland",
        "israel" to "country.israel",
        "italy" to "country.italy",
        "jamaica" to "country.jamaica",
        "japan" to "country.japan",
        "jordan" to "country.jordan",
        "kazakhstan" to "country.kazakhstan",
        "kenya" to "country.kenya",
        "kuwait" to "country.kuwait",
        "latvia" to "country.latvia",
        "lebanon" to "country.lebanon",
        "libya" to "country.libya",
        "lithuania" to "country.lithuania",
        "luxembourg" to "country.luxembourg",
        "malaysia" to "country.malaysia",
        "mexico" to "country.mexico",
        "moldova" to "country.moldova",
        "mongolia" to "country.mongolia",
        "montenegro" to "country.montenegro",
        "morocco" to "country.morocco",
        "myanmar" to "country.myanmar",
        "nepal" to "country.nepal",
        "netherlands" to "country.netherlands",
        "new zealand" to "country.newZealand",
        "nicaragua" to "country.nicaragua",
        "nigeria" to "country.nigeria",
        "north korea" to "country.northKorea",
        "norway" to "country.norway",
        "pakistan" to "country.pakistan",
        "panama" to "country.panama",
        "paraguay" to "country.paraguay",
        "peru" to "country.peru",
        "philippines" to "country.philippines",
        "poland" to "country.poland",
        "portugal" to "country.portugal",
        "puerto rico" to "country.puertoRico",
        "qatar" to "country.qatar",
        "romania" to "country.romania",
        "russia" to "country.russia",
        "saudi arabia" to "country.saudiArabia",
        "serbia" to "country.serbia",
        "singapore" to "country.singapore",
        "slovakia" to "country.slovakia",
        "slovenia" to "country.slovenia",
        "south africa" to "country.southAfrica",
        "south korea" to "country.southKorea",
        "spain" to "country.spain",
        "sri lanka" to "country.sriLanka",
        "sudan" to "country.sudan",
        "sweden" to "country.sweden",
        "switzerland" to "country.switzerland",
        "syria" to "country.syria",
        "taiwan" to "country.taiwan",
        "thailand" to "country.thailand",
        "tunisia" to "country.tunisia",
        "turkey" to "country.turkey",
        "ukraine" to "country.ukraine",
        "united arab emirates" to "country.uae",
        "united kingdom" to "country.uk",
        "united states" to "country.usa",
        "uruguay" to "country.uruguay",
        "uzbekistan" to "country.uzbekistan",
        "venezuela" to "country.venezuela",
        "vietnam" to "country.vietnam",
        "yemen" to "country.yemen"
    )

    fun translateCountry(name: String?): String? {
        if (name.isNullOrBlank()) return name
        val key = countryKeyByEnglishName[name.trim().lowercase()] ?: return name
        return t(key)
    }

    fun updatePage() {
        val elements = document.querySelectorAll("[data-i18n]")
        for (i in 0 until elements.length) {
            val el = elements.item(i)?.unsafeCast<Element>() ?: continue
            val key = el.getAttribute("data-i18n") ?: continue
            el.textContent = t(key)
        }
        val labelled = document.querySelectorAll("[data-i18n-aria-label]")
        for (i in 0 until labelled.length) {
            val el = labelled.item(i)?.unsafeCast<Element>() ?: continue
            val key = el.getAttribute("data-i18n-aria-label") ?: continue
            el.setAttribute("aria-label", t(key))
        }
        updateTitle()
    }

    // Derive the tab title from the page's translated <h1>. Pages that render several outcome
    // headings (e.g. success/error panels, all but one hidden) use the first one that is not
    // inside a .hidden container; call this again after toggling which panel is visible.
    fun updateTitle() {
        val headings = document.querySelectorAll("main h1[data-i18n]")
        if (headings.length == 0) return
        var chosen: Element? = null
        for (i in 0 until headings.length) {
            val el = headings.item(i)?.unsafeCast<Element>() ?: continue
            if (el.closest(".hidden") == null) { chosen = el; break }
        }
        if (chosen == null && headings.length == 1) chosen = headings.item(0)?.unsafeCast<Element>()
        val text = chosen?.textContent?.trim().orEmpty()
        if (text.isNotEmpty()) document.title = "$text - Adopt-U"
    }

    fun formatDate(value: dynamic): String = js("new Date(value)").toLocaleDateString(currentLang).unsafeCast<String>()

    fun formatDateOnly(value: dynamic): String = js("new Date(value)").toLocaleDateString(currentLang, js("({timeZone: 'UTC'})")).unsafeCast<String>()

    fun updateActiveLangOption() {
        val options = document.querySelectorAll(".lang-option")
        for (i in 0 until options.length) {
            val el = options.item(i)?.unsafeCast<Element>() ?: continue
            val langAttr = el.getAttribute("data-lang") ?: continue
            if (langAttr == currentLang) {
                el.classList.add("active")
                // Update the dropdown button text with the flag
                val btn = document.querySelector(".lang-dropbtn")?.unsafeCast<HTMLElement>()
                btn?.textContent = getFlag(langAttr)
            } else {
                el.classList.remove("active")
            }
            // Add click handler to change language
            el.addEventListener("click", { _ ->
                val lang = el.getAttribute("data-lang") ?: return@addEventListener
                window.localStorage.setItem("preferredLanguage", lang)
                ApiClientModule.me().then<Unit> { user ->
                    if (user?.authenticated == true) ApiClientModule.updateLanguage(lang)
                }.catch<Unit> { }
                loadLang(lang).then {
                    updatePage()
                    updateActiveLangOption()
                }
            })
        }
    }

    fun getFlag(lang: String): String = langEmojis[lang] ?: "\uD83C\uDDFA\uD83C\uDDF8"
}
